"use client";

import { Children, createContext, FormEvent, Fragment, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent, MouseEvent, PointerEvent, ReactNode } from "react";
import { SHOP_BUILDER_SCHEMA_VERSION } from "../builderSchema";
import { isShopBuilderDocument, shopBuilderLegacyBlocksFromDocument, SHOP_BUILDER_DOCUMENT_KEY } from "../builderDocument";
import {
    CATALOG_PRODUCT_RIBBONS,
    catalogCategoryImageUrls,
    catalogProductImageTemplate,
    catalogProductPage,
    catalogProductPageConfig,
    catalogProductSort,
    catalogProductTemplate,
    normalizeCatalogProductPageStructure,
} from "../catalogProductPage";
import { catalogProductDetailDesign } from "../catalogProductDetailPage";
import {
    categoryCatalogProductRailConfig,
    categoryCatalogProductRailSearchParams,
} from "../categoryCatalogProductRail";
import type {
    CatalogCategoryStyle,
    CatalogFilterStyle,
    CatalogProductDesign,
    CatalogProductPageConfig,
    CatalogProductRibbon,
    CatalogProductSort,
} from "../catalogProductPage";
import {
    siteBuilderBlockDelegatesOutlineToColumn,
    siteBuilderEditorBlockAttributes,
    siteBuilderEditorChildPath,
    siteBuilderEditorColumnAttributes,
    siteBuilderEditorNestedBlockAttributes,
    siteBuilderEditorRootPath,
    siteBuilderEditorSlotAttributes,
} from "../builderInstrumentation";
import { siteBuilderIconGlyph } from "../siteBuilderIcon";
import {
    SITE_BUILDER_AUTHORITATIVE_STYLE_CSS,
    siteBuilderBlockStyle,
    siteBuilderPartOptionKey,
    siteBuilderPartStyle,
    siteBuilderRebasedAuthoritativeStyleVariables,
} from "../siteBuilderStyles";
import type { SiteBuilderStyleRecord } from "../siteBuilderStyles";
import { siteBuilderMediaPresentation } from "../siteBuilderBlockPresentation";
import { useSiteBuilderCountdown } from "../siteBuilderCountdown";
import { SiteBuilderShareAnchor } from "../SiteBuilderShareAnchor";
import { navigationFocusableElements } from "../navigationFocus";
import {
    SITE_BUILDER_DEFAULT_SHARE_NETWORKS,
    siteBuilderShareNetwork,
} from "../siteBuilderShare";
import {
    siteBuilderAuthoredFormFields,
    siteBuilderFormConfig,
} from "../siteBuilderForm";
import type { SiteBuilderAuthoredFormField } from "../siteBuilderForm";
import {
    siteBuilderLeadFieldKey,
    siteBuilderLeadValuesFromFormData,
    submitSiteBuilderLead,
} from "../siteBuilderLeadRuntime";
import type { SiteBuilderLeadFormField } from "../siteBuilderTypes";
import { templateLeadStartedAt } from "../templateLeadSubmission";
import { useSiteBuilderLeadForm } from "../useSiteBuilderLeadForm";
import { SiteNavigation } from "../SiteNavigationView";
import type { SiteNavigationRenderLink } from "../SiteNavigationView";
import {
    DEFAULT_SITE_NAVIGATION_SETTINGS,
    resolveSiteNavigationSettings,
    resolveSiteNavigationThemeTokens,
    siteNavigationThemeStyle,
} from "../siteNavigation";
import type { SiteNavigationSettings } from "../siteNavigation";
import {
    SHOP_COMMERCE_RUNTIME_ROLE_OPTION,
    SHOP_COMMERCE_RUNTIME_ROLES,
} from "../commerceRuntime";
import { shopEmbedSource, shopMapEmbedSource, siteVideoSource } from "../embedSources";
import {
    shopAvatarInitials,
    shopPaymentBrand,
    shopPaymentBrandGlyph,
    shopPortableAspectRatio,
    shopPortableProgressLabelPosition,
} from "../portableBlocks";
import {
    getShopBlockFootprint,
    getShopBlockGrid,
    getShopBlockPresetPart,
    isShopDynamicEntityField,
    SHOP_COLUMN_GROUP_OPTION,
    SHOP_DOCUMENT_ROLE_OPTION,
    SHOP_DYNAMIC_ENTITY_FIELD_OPTION,
    SHOP_DYNAMIC_TEXT_SOURCE_CUSTOM,
    SHOP_EDITOR_COMMAND_LINE_OPTION,
    SHOP_EDITOR_PRESERVED_HEIGHT_OPTION,
    SHOP_EDITOR_PRESERVED_WIDTH_OPTION,
    SHOP_GRID_CELL_GAP,
    SHOP_GRID_CELL_HEIGHT,
    SHOP_GRID_CELL_WIDTH,
    SHOP_GRID_SLOT_KEY,
    slotBlocks as getSlotBlocks,
    splitShopHeroSliderLegacyStructure,
} from "../shopBlocks";
import type { ShopDynamicEntityField, ShopDynamicEntityType, ShopDynamicTextSource } from "../shopBlocks";
import {
    storefrontSpecialRouteHref,
    storefrontSpecialRouteMatch,
} from "../storefrontRouting";
import type {
    PageLink,
    PublicShopCurrency,
    PublicShopLanguage,
    PublicShopMenuItem,
    ResolvedTemplateDesign,
    ShopPageContext,
    ShopRichTextSegment,
    ShopTemplateBlock,
    SitePageContent,
} from "../types";
import { shopStorefrontMessage } from "../storefrontCatalog";
import type { ShopStorefrontMessageKey, ShopStorefrontMessageValues } from "../storefrontCatalog";
import { ensurePacketaWidget, packetaPointDisplayName } from "./packetaWidget";
import {
    lagunaRuntimeBlock,
    lagunaRuntimeGridItemCount,
    lagunaRuntimeRegion,
    lagunaRuntimeSlotItemCount,
    lagunaRuntimeSourceIndex,
} from "./lagunaBuilder";
import {
    ensureLagunaFooterLayoutSkeleton,
    normalizeLagunaFooterLayoutStyle,
} from "./lagunaFooter";
import { resolveLagunaNavigationVariant } from "./lagunaNavigation";
import type { LagunaNavigationVariant } from "./lagunaNavigation";
import {
    SHOP_HEADER_ROLES,
    SHOP_HEADER_UTILITY_ROLES,
    findShopHeaderRoleBlock,
    resolveShopHeaderAlignment,
    resolveShopHeaderLogoDisplay,
    resolveShopHeaderUtilityDisplay,
    shopHeaderBlockVisibleOn,
    shopHeaderRole,
} from "./shopHeader";
import { ShopStorefrontLink, ShopStorefrontRuntime } from "./shopStorefrontRuntime";
import type { ShopStorefrontRuntimeModel, ShopStorefrontRuntimeProps } from "./shopStorefrontRuntime";
import {
    ManufacturingCustomizationFields,
    ManufacturingSpecificationSummary,
    useManufacturingCustomization,
} from "./ShopManufacturingCustomization";
import type { PublicManufacturingSpecification } from "./shopManufacturingCustomization";
import {
    activeCatalogFilterCount,
    catalogFacetsFromContext,
    catalogFacetsWithSale,
    catalogFiltersFromContext,
    catalogHref,
    catalogPaginationFromContext,
    catalogPaginationHref,
    catalogQueryFromContext,
    catalogSortFromContext,
    catalogTotalFromContext,
    clearCatalogFiltersHref,
    selectedCatalogFacetOptions,
    toggleCatalogFilter,
    visibleCatalogFacetOptions,
} from "./shopCatalogFilters";
import type {
    CatalogFilterState,
    CatalogSortValue,
    PublicCatalogFacet,
    PublicCatalogFacetOption,
} from "./shopCatalogFilters";
import {
    CART_ITEM_MAX_QUANTITY,
    CURRENCY_QUERY_PARAM,
    addressLine,
    activeMenuUuidsFromContext,
    authoredSystemPageHref,
    catalogProductsFromContext,
    checkoutStepHref,
    contextData,
    customerErrorTextFromResponse,
    favoritePreviewFromProductContext,
    favoriteProductPreviewsForIds,
    favoriteProductPreviewsFromContext,
    functionalStorefrontHref,
    formatDate,
    formatMoney,
    homeProductsFromContext,
    localizedFallbackProducts,
    menuItemHref,
    moneyAmount,
    nestedRecord,
    normalizedMenuPath,
    normalizedPath,
    numberOrNull,
    pageContextPath,
    productHref,
    productImagesFromData,
    productSurchargeParametersFromData,
    productVariantsFromData,
    products,
    publicCartErrorText,
    storeCitiesFromContext,
    storeFromRecord,
    storefrontLocale,
    storefrontPluralCategory,
    storesFromContext,
    stringOrNull,
    textValue,
} from "./shopStorefrontCore";
import type {
    AnimationMode,
    AnimationTrigger,
    CheckoutFormState,
    CheckoutRouteStep,
    FavoriteProductPreview,
    HeaderSelectorOption,
    PickupPointSelection,
    Product,
    PublicCart,
    PublicCartItem,
    PublicCheckoutDeliveryOption,
    PublicCheckoutOptions,
    PublicCheckoutPaymentOption,
    PublicCustomer,
    PublicCustomerAddress,
    PublicCustomerAddressListResponse,
    PublicCustomerAddressType,
    PublicCustomerProfileUpdate,
    PublicCustomerSession,
    PublicHomeProduct,
    PublicOrderAddress,
    PublicOrderCreated,
    PublicOrderReorderResponse,
    PublicProductImage,
    PublicProductSurchargeParameter,
    PublicProductSurchargeValue,
    PublicProductVariant,
    PublicProductVariantItem,
    PublicStoreCity,
    PublicStoreLink,
    ShopStorefrontFormatting,
    ShopStorefrontMessage,
    ShopStorefrontPreviewAdapter,
    ShopResolvePayload,
} from "./shopStorefrontCore";

type IconName =
    | "bag"
    | "briefcase"
    | "check"
    | "chevronDown"
    | "chevronLeft"
    | "chevronRight"
    | "creditCard"
    | "heart"
    | "home"
    | "info"
    | "mail"
    | "mapPin"
    | "menu"
    | "navigation"
    | "phone"
    | "rotate"
    | "search"
    | "shield"
    | "store"
    | "trash"
    | "truck"
    | "user"
    | "x";

const RECENT_SEARCHES_STORAGE_KEY = "laguna:recent-searches";
const RECENT_SEARCH_LIMIT = 8;
const ShopEditorModeContext = createContext(false);
const ShopBuilderSchemaVersionContext = createContext(0);
const ShopBuilderBlockPathContext = createContext<string | null>(null);
const ShopSubmitButtonContext = createContext(false);
const ShopStorefrontLanguageContext = createContext<string | null>(null);
const ShopStorefrontHrefContext = createContext<(href: string) => string>((href) => href);
const ShopStorefrontLocationHrefContext = createContext<string | undefined>(undefined);
const ShopStorefrontFormattingContext = createContext<ShopStorefrontFormatting>({
    formatDate: (value) => formatDate(value, "en"),
    formatMoney: (value, currencyCode) => formatMoney(value, "en", currencyCode ?? "EUR"),
    formatNumber: (value) => new Intl.NumberFormat("en-GB").format(value),
    locale: "en-GB",
});
const SHOP_SOCIAL_ICON_SELECT_EVENT = "shop:social-icon-select";
const SHOP_SHARE_ICON_SELECT_EVENT = "shop:share-icon-select";
const ShopSiteInfoContext = createContext({
    footerVariant: "columns",
    homeHref: "/",
    siteName: "Shop",
    storesHref: "/",
});
type ShopSearchContextValue = {
    currencyCode?: string | null;
    languageCode?: string | null;
    pages: PageLink[];
    searchHref: string;
    shopSearchApiPath?: string;
};
const ShopSearchContext = createContext<ShopSearchContextValue>({
    pages: [],
    searchHref: "/vyhledavani",
});
type ShopHeaderChromeContextValue = {
    accountHref: string;
    cart: PublicCart;
    cartError: string | null;
    cartHref: string;
    cartLoading: boolean;
    currentCurrency: PublicShopCurrency | null;
    currentLanguage: PublicShopLanguage | null;
    customerLoading: boolean;
    customerSession: PublicCustomerSession;
    customerUuid: string | null;
    currencyOptions: HeaderSelectorOption[];
    displayName: string;
    homeHref: string;
    languageOptions: HeaderSelectorOption[];
    onCurrencySelect: (code: string) => void;
    onLanguageSelect: (code: string, href: string) => boolean | void;
    onLogoutCustomer: () => Promise<void>;
    onOpenSearch: () => void;
    storeCities: PublicStoreCity[];
    storesHref: string;
    visibleFavoriteProductIds: Set<string>;
};
const ShopHeaderChromeContext = createContext<ShopHeaderChromeContextValue | null>(null);
type LagunaNavigationRuntimeContextValue = {
    settings: SiteNavigationSettings;
    style: CSSProperties;
};
const LagunaNavigationRuntimeContext = createContext<LagunaNavigationRuntimeContextValue | null>(null);
type ShopCommerceContentContextValue = {
    activeSection: PublicShopMenuItem | null;
    activeSectionItems: PublicShopMenuItem[];
    addingProductUuid: string | null;
    context: ShopPageContext | null | undefined;
    currencyCode: string | null;
    favorites: Set<string>;
    headerMenuItems: PublicShopMenuItem[];
    onAddToCart: (productUuid: string) => void;
    onRegisterCustomer: (
        email: string,
        password: string,
        passwordConfirmation: string,
        newsletterOptOut: boolean,
    ) => Promise<boolean>;
    resolveApiPath?: string;
    onToggleFavorite: (productId: string) => void;
};
const ShopCommerceContentContext = createContext<ShopCommerceContentContextValue | null>(null);
type ShopLeadFormRuntimeContextValue = {
    authoredFields: ReadonlyMap<ShopTemplateBlock, SiteBuilderAuthoredFormField>;
    busy: boolean;
    enforceSchema: boolean;
    fieldNames: ReadonlyMap<ShopTemplateBlock, string>;
    fieldsByKey: ReadonlyMap<string, SiteBuilderLeadFormField>;
    formBlock: ShopTemplateBlock;
    primaryFieldsByKey: ReadonlyMap<string, ShopTemplateBlock>;
    readOnly: boolean;
    schemaResolved: boolean;
};
const ShopLeadFormRuntimeContext = createContext<ShopLeadFormRuntimeContextValue | null>(null);
const globalChromeBlockTypes = new Set(["promoBar", "header"]);
const lagunaStorefrontPreviewAdapter = {
    currentProductFromContext: favoritePreviewFromProductContext,
    productsForIds: favoriteProductPreviewsForIds,
    productsFromContext: favoriteProductPreviewsFromContext,
} satisfies ShopStorefrontPreviewAdapter;

function useShopStorefrontMessage(): ShopStorefrontMessage {
    const languageCode = useContext(ShopStorefrontLanguageContext);

    return useCallback(
        (key: ShopStorefrontMessageKey, values: ShopStorefrontMessageValues = {}) => (
            shopStorefrontMessage(languageCode, key, values)
        ),
        [languageCode],
    );
}

function useShopStorefrontFormatting(): ShopStorefrontFormatting {
    return useContext(ShopStorefrontFormattingContext);
}

function useShopStorefrontHref(): (href: string) => string {
    return useContext(ShopStorefrontHrefContext);
}

function authoredStorefrontHref(
    value: string | null | undefined,
    storefrontHref: (href: string) => string,
    pages: PageLink[],
    languageCode: string | null | undefined,
): string | undefined {
    const href = functionalStorefrontHref(value);

    return href ? authoredSystemPageHref(pages, href, languageCode) ?? storefrontHref(href) : undefined;
}

type HeroSlide = {
    image: string;
    alt: string;
    eyebrow: string;
    title: string;
    description: string;
    primaryLabel: string;
    primaryHref: string;
    secondaryLabel?: string;
    secondaryHref?: string;
    overlay: string;
};

type PublicProductReview = {
    rating: number;
    title: string | null;
    body: string;
    authorName: string;
    createdAt: string;
};

type ProductDetailTab = "overview" | "related" | "similar" | "reviews";
type ProductReviewRating = 1 | 2 | 3 | 4 | 5;
type ProductReviewDistribution = Record<ProductReviewRating, number>;

type CatalogNavigateHandler = (href: string) => void | Promise<void>;

type SearchSuggestionProduct = {
    uuid: string;
    name: string;
    slug: string | null;
    reference: string | null;
    price: string;
    manufacturerUuid: string | null;
    manufacturerName: string | null;
    isOnSale: boolean;
    availableForOrder: boolean;
    trackInventory: boolean;
    availableQuantity: number | null;
};

type SearchSuggestionCategory = {
    uuid: string;
    name: string;
    slug: string | null;
    pathNames: string[];
};

type SearchSuggestionManufacturer = {
    uuid: string;
    name: string;
    count: number;
};

type SearchSuggestions = {
    query: string;
    products: SearchSuggestionProduct[];
    categories: SearchSuggestionCategory[];
    manufacturers: SearchSuggestionManufacturer[];
    productTotal: number;
    categoryTotal: number;
};

type SearchSuggestionLink = {
    id: string;
    href: string;
};

type SearchBlockScope = "categories" | "everything" | "pages" | "products";

type PublicProductVariantGroup = {
    attributeUuid: string;
    attributeName: string;
    attributePosition: number;
    options: PublicProductVariantItem[];
};

type CartFreeShippingProgress = {
    label: string;
    detail: string;
    progress: number;
    reached: boolean;
};

type CheckoutCountryOption = {
    code: string;
    label: string;
    search: string;
};

type PublicOrderListItem = PublicOrderCreated & {
    customerName?: string;
    customerEmail?: string;
    totalPaid: string;
    updatedAt: string;
    items?: PublicOrderItem[];
};

type PublicOrderItem = {
    uuid: string;
    productUuid: string | null;
    productAttributeUuid: string | null;
    productName: string;
    productReference: string | null;
    productEan13: string | null;
    primaryImage: PublicProductImage | null;
    quantity: string | number;
    unitPrice: string;
    totalPrice: string;
    netUnitPrice: string;
    taxRate: string;
    unitTaxAmount: string;
    grossUnitPrice: string;
    netTotalPrice: string;
    taxAmount: string;
    grossTotalPrice: string;
    currencyCode: string;
    createdAt: string;
};

type PublicOrderStatusChange = {
    uuid: string;
    fromStatus: string;
    toStatus: string;
    changedByUserUuid: string | null;
    changedByUserName: string | null;
    note: string | null;
    createdAt: string;
};

type PublicOrderDetail = PublicOrderListItem & {
    customer: {
        email?: string | null;
        firstname?: string | null;
        lastname?: string | null;
        phone?: string | null;
    };
    billingAddress: PublicOrderAddress & {
        ic?: string | null;
        vat?: string | null;
    };
    deliveryAddress: PublicOrderAddress;
    itemsPrice: string;
    itemsNetPrice: string;
    itemsTaxAmount: string;
    carrierName: string | null;
    paymentName: string | null;
    carrierPrice: string;
    carrierNetPrice: string;
    carrierTaxRate: string;
    carrierTaxAmount: string;
    paymentSurcharge: string;
    paymentNetPrice: string;
    paymentTaxRate: string;
    paymentTaxAmount: string;
    totalNetPrice: string;
    totalTaxAmount: string;
    carrierRateUuid?: string | null;
    carrierPaymentOptionUuid?: string | null;
    pickupPointProvider?: string | null;
    pickupPointId?: string | null;
    pickupPointName?: string | null;
    customerNote: string | null;
    datePayment: string | null;
    items: PublicOrderItem[];
    statusChanges: PublicOrderStatusChange[];
};

type PublicOrderListResponse = {
    orders: PublicOrderListItem[];
};

type PublicCustomerAddressPayload = {
    firstname: string;
    lastname: string;
    company: string | null;
    email: string | null;
    phone: string | null;
    address1: string;
    houseNumber: string | null;
    address2: string | null;
    city: string;
    postcode: string;
    countryCode: string;
    addressType: PublicCustomerAddressType;
    useAsBillingAddress: boolean;
    useAsDeliveryAddress: boolean;
};

type AccountMode = "login" | "register";
type AccountCustomerType = "personal" | "company";
type AccountSection = "overview" | "orders" | "addresses" | "favorites" | "settings" | "connections";

const CUSTOMER_ADDRESS_TYPES: Array<{ value: PublicCustomerAddressType; messageKey: ShopStorefrontMessageKey; icon: IconName }> = [
    { value: "home", messageKey: "ui.address.home", icon: "home" },
    { value: "work", messageKey: "ui.address.work", icon: "briefcase" },
    { value: "other", messageKey: "ui.address.other", icon: "mapPin" },
];

function countryLabel(code: string, languageCode: string | null): string {
    const normalized = code.trim().toUpperCase();
    const locale = storefrontLocale(languageCode);
    const displayLocale = locale;

    try {
        const displayNamesConstructor = (
            Intl as typeof Intl & {
                DisplayNames?: new (
                    locales: string[],
                    options: { type: "region" },
                ) => { of: (code: string) => string | undefined };
            }
        ).DisplayNames;
        const displayName = displayNamesConstructor
            ? new displayNamesConstructor([displayLocale], { type: "region" }).of(normalized)
            : undefined;

        if (displayName) {
            return displayName;
        }
    } catch {
        // Fall through to the bundled catalog when Intl.DisplayNames is unavailable.
    }

    const fallbackMessageKeys: Partial<Record<string, ShopStorefrontMessageKey>> = {
        AT: "ui.country.AT",
        CZ: "ui.country.CZ",
        DE: "ui.country.DE",
        HU: "ui.country.HU",
        PL: "ui.country.PL",
        SK: "ui.country.SK",
    };
    const fallbackMessageKey = fallbackMessageKeys[normalized];

    return fallbackMessageKey ? shopStorefrontMessage(languageCode, fallbackMessageKey) : normalized;
}

function checkoutCountryOptions(
    deliveryOptions: PublicCheckoutDeliveryOption[],
    extraCodes: string[] = [],
    languageCode: string | null = null,
): CheckoutCountryOption[] {
    const locale = storefrontLocale(languageCode);
    const sortLocale = locale;
    const codes = new Set<string>();

    for (const option of deliveryOptions) {
        const code = option.countryCode.trim().toUpperCase();

        if (code !== "") {
            codes.add(code);
        }
    }

    for (const extraCode of extraCodes) {
        const code = extraCode.trim().toUpperCase();

        if (code !== "") {
            codes.add(code);
        }
    }

    return Array.from(codes)
        .map((code) => {
            const label = countryLabel(code, languageCode);

            return {
                code,
                label,
                search: `${label} ${code}`.toLowerCase(),
            };
        })
        .sort((a, b) => a.label.localeCompare(b.label, sortLocale));
}

const CATALOG_SORT_OPTIONS: Array<{ value: CatalogSortValue; messageKey: ShopStorefrontMessageKey }> = [
    { value: "recommended", messageKey: "ui.catalog.sort.recommended" },
    { value: "bestselling", messageKey: "ui.catalog.sort.bestselling" },
    { value: "priceAsc", messageKey: "ui.catalog.sort.priceAsc" },
    { value: "priceDesc", messageKey: "ui.catalog.sort.priceDesc" },
    { value: "rating", messageKey: "ui.catalog.sort.rating" },
    { value: "nameAsc", messageKey: "ui.catalog.sort.nameAsc" },
    { value: "newest", messageKey: "ui.catalog.sort.newest" },
];

const accountSectionIds: ReadonlySet<AccountSection> = new Set([
    "overview",
    "orders",
    "addresses",
    "favorites",
    "settings",
    "connections",
]);
type StorefrontPluralCategory = "one" | "few" | "many" | "other";
type StorefrontPluralMessageKeys = Readonly<Record<StorefrontPluralCategory, ShopStorefrontMessageKey>>;
const CATALOG_PRODUCT_COUNT_MESSAGE_KEYS: StorefrontPluralMessageKeys = {
    one: "ui.catalog.productCount.one",
    few: "ui.catalog.productCount.few",
    many: "ui.catalog.productCount.many",
    other: "ui.catalog.productCount.other",
};
const CATALOG_QUERY_SUMMARY_MESSAGE_KEYS: StorefrontPluralMessageKeys = {
    one: "ui.catalog.querySummary.one",
    few: "ui.catalog.querySummary.few",
    many: "ui.catalog.querySummary.many",
    other: "ui.catalog.querySummary.other",
};
const PRODUCT_REVIEW_COUNT_MESSAGE_KEYS: StorefrontPluralMessageKeys = {
    one: "ui.product.review.one",
    few: "ui.product.review.few",
    many: "ui.product.review.many",
    other: "ui.product.review.other",
};
const PRODUCT_CUSTOMER_RATING_MESSAGE_KEYS: StorefrontPluralMessageKeys = {
    one: "ui.product.customerRating.one",
    few: "ui.product.customerRating.few",
    many: "ui.product.customerRating.many",
    other: "ui.product.customerRating.other",
};
const ACCOUNT_ITEM_COUNT_MESSAGE_KEYS: StorefrontPluralMessageKeys = {
    one: "ui.account.itemCount.one",
    few: "ui.account.itemCount.few",
    many: "ui.account.itemCount.many",
    other: "ui.account.itemCount.other",
};
const CART_MORE_PRODUCT_MESSAGE_KEYS: StorefrontPluralMessageKeys = {
    one: "ui.cart.moreProduct.one",
    few: "ui.cart.moreProduct.few",
    many: "ui.cart.moreProduct.many",
    other: "ui.cart.moreProduct.other",
};

function classNames(...classes: Array<string | false | null | undefined>): string {
    return classes.filter(Boolean).join(" ");
}
function storefrontPluralMessage(
    count: number,
    languageOrLocale: string | null | undefined,
    keys: StorefrontPluralMessageKeys,
    message: ShopStorefrontMessage,
    values: ShopStorefrontMessageValues = {},
): string {
    return message(keys[storefrontPluralCategory(count, languageOrLocale)], { ...values, count });
}

function moneyWithAdditions(base: string | number | null | undefined, additions: Array<string | number | null | undefined>): string {
    const totalCents = additions.reduce<number>(
        (total, value) => total + Math.round(moneyAmount(value) * 100),
        Math.round(moneyAmount(base) * 100),
    );

    return (totalCents / 100).toFixed(2);
}

function parseWholeQuantity(value: string): number | null {
    const trimmed = value.trim();

    if (!/^\d+$/.test(trimmed)) {
        return null;
    }

    const parsed = Number(trimmed);

    return Number.isSafeInteger(parsed) ? parsed : null;
}

function clampQuantity(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

function cartItemStockText(item: PublicCartItem, message: ShopStorefrontMessage): string {
    if (!item.availableForOrder || item.stockStatus === "unavailable") {
        return message("ui.product.unavailable");
    }

    if (!item.trackInventory) {
        return message("ui.product.inStock");
    }

    const available = item.maxQuantity ?? 0;

    if (item.quantityTooHigh) {
        return message("ui.product.availableOnly", { count: available });
    }

    if (available <= 0 || item.stockStatus === "outOfStock") {
        return message("ui.product.outOfStock");
    }

    if (item.stockStatus === "lowStock") {
        return message("ui.product.lowStock", { count: available });
    }

    return message("ui.product.inStockCount", { count: available });
}

function cartItemNoticeText(item: PublicCartItem, message: ShopStorefrontMessage): string | null {
    if (item.priceChanged) {
        return message("ui.cart.priceChanged");
    }

    if (item.quantityTooHigh) {
        return message("ui.cart.itemQuantityUnavailable", {
            available: item.maxQuantity ?? 0,
            quantity: item.quantity,
        });
    }

    if (!item.validForCheckout) {
        return message("ui.cart.itemUnavailable");
    }

    return null;
}

function CartItemSurchargesSummary({ item, compact = false }: { item: PublicCartItem; compact?: boolean }) {
    const { formatMoney } = useShopStorefrontFormatting();

    if (!Array.isArray(item.surcharges) || item.surcharges.length === 0) {
        return null;
    }

    return (
        <div className={classNames(compact ? "mt-1 space-y-0.5" : "mt-2 space-y-1")}>
            {item.surcharges.map((surcharge) => (
                <p key={`${surcharge.parameterUuid}:${surcharge.valueUuid}`} className={classNames(compact ? "truncate text-xs font-semibold" : "text-xs font-semibold", readableOnSurfaceTextClass)}>
                    {surcharge.parameterName}: {surcharge.valueName}
                    {moneyAmount(surcharge.salePrice) > 0 ? ` (+ ${formatMoney(surcharge.salePrice)})` : ""}
                </p>
            ))}
        </div>
    );
}

function cartCheckoutBlockedText(cart: PublicCart, message: ShopStorefrontMessage): string | null {
    if (cart.items.length === 0) {
        return null;
    }

    const quantityProblem = cart.items.find((item) => item.quantityTooHigh);

    if (quantityProblem) {
        return `${quantityProblem.name}: ${message("ui.product.availableOnly", { count: quantityProblem.maxQuantity ?? 0 })}`;
    }

    const unavailable = cart.items.find((item) => !item.validForCheckout);

    if (unavailable) {
        return `${unavailable.name}: ${message("ui.cart.itemUnavailable")}`;
    }

    return cart.checkoutBlockedReason
        ? publicCartErrorText(cart.checkoutBlockedReason, message("ui.error.cartCannotCheckout"), message)
        : null;
}

function freeShippingText(
    option: PublicCheckoutDeliveryOption,
    itemsTotalPrice: string,
    message: ShopStorefrontMessage,
    formatMoney: ShopStorefrontFormatting["formatMoney"],
): string | null {
    if (option.freeShippingThreshold === null) {
        return null;
    }

    const remaining = moneyAmount(option.freeShippingThreshold) - moneyAmount(itemsTotalPrice);

    if (remaining <= 0) {
        return message("ui.cart.freeShipping");
    }

    return message("ui.cart.freeShippingRemaining", { amount: formatMoney(remaining) });
}

function cartFreeShippingMessage(
    cart: PublicCart,
    checkoutOptions: PublicCheckoutOptions,
    message: ShopStorefrontMessage,
    formatMoney: ShopStorefrontFormatting["formatMoney"],
): string | null {
    if (cart.items.length === 0 || checkoutOptions.deliveryOptions.length === 0) {
        return null;
    }

    const selectedDelivery = checkoutOptions.deliveryOptions.find(
        (option) => option.carrierRateUuid === cart.carrierRateUuid,
    );

    if (selectedDelivery) {
        return freeShippingText(selectedDelivery, cart.itemsTotalPrice, message, formatMoney);
    }

    let bestRemaining: number | null = null;
    let hasFreeShipping = false;

    for (const option of checkoutOptions.deliveryOptions) {
        if (option.freeShippingThreshold === null) {
            continue;
        }

        const remaining = moneyAmount(option.freeShippingThreshold) - moneyAmount(cart.itemsTotalPrice);

        if (remaining <= 0) {
            hasFreeShipping = true;

            continue;
        }

        bestRemaining = bestRemaining === null ? remaining : Math.min(bestRemaining, remaining);
    }

    if (hasFreeShipping) {
        return message("ui.cart.freeShippingSelected");
    }

    if (bestRemaining !== null) {
        return message("ui.cart.freeShippingRemaining", { amount: formatMoney(bestRemaining) });
    }

    return null;
}

function cartFreeShippingProgress(
    cart: PublicCart,
    checkoutOptions: PublicCheckoutOptions,
    message: ShopStorefrontMessage,
    formatMoney: ShopStorefrontFormatting["formatMoney"],
): CartFreeShippingProgress | null {
    if (cart.items.length === 0 || checkoutOptions.deliveryOptions.length === 0) {
        return null;
    }

    const itemsTotal = moneyAmount(cart.itemsTotalPrice);
    const selectedDelivery = checkoutOptions.deliveryOptions.find(
        (option) => option.carrierRateUuid === cart.carrierRateUuid,
    );
    const optionsWithThreshold = checkoutOptions.deliveryOptions
        .map((option) => ({
            option,
            threshold: option.freeShippingThreshold === null ? null : moneyAmount(option.freeShippingThreshold),
        }))
        .filter((entry): entry is { option: PublicCheckoutDeliveryOption; threshold: number } => entry.threshold !== null && entry.threshold > 0);

    if (optionsWithThreshold.length === 0) {
        return null;
    }

    const selectedThreshold = selectedDelivery?.freeShippingThreshold === undefined || selectedDelivery.freeShippingThreshold === null
        ? null
        : moneyAmount(selectedDelivery.freeShippingThreshold);
    const threshold = selectedThreshold !== null && selectedThreshold > 0
        ? selectedThreshold
        : optionsWithThreshold.reduce((best, entry) => {
            const bestRemaining = best.threshold - itemsTotal;
            const entryRemaining = entry.threshold - itemsTotal;

            if (entryRemaining <= 0 && bestRemaining > 0) {
                return entry;
            }

            if (entryRemaining > 0 && bestRemaining > 0 && entryRemaining < bestRemaining) {
                return entry;
            }

            return best;
        }, optionsWithThreshold[0]).threshold;
    const remaining = threshold - itemsTotal;
    const reached = remaining <= 0;
    const progress = Math.max(0, Math.min(100, (itemsTotal / threshold) * 100));

    return {
        label: reached
            ? message("ui.cart.freeShippingReached")
            : message("ui.cart.freeShippingRemaining", { amount: formatMoney(remaining) }),
        detail: reached
            ? message("ui.cart.freeShippingChoose")
            : message("ui.cart.freeShippingProgress", {
                current: formatMoney(itemsTotal),
                target: formatMoney(threshold),
            }),
        progress: reached ? 100 : progress,
        reached,
    };
}

function isSaleMenuItem(item: PublicShopMenuItem): boolean {
    const label = item.label.toLowerCase();
    const menuRoute = storefrontSpecialRouteMatch(normalizedMenuPath(menuItemHref(item)) ?? "");

    return menuRoute?.route === "sale"
        || label.includes("%")
        || ["sale", "vyprodej", "výprodej", "vypredaj", "angebote", "rebajas", "wyprzedaż", "wyprzedaz", "akció", "akcio", "reduceri"]
            .some((term) => label.includes(term));
}

function linkTargetProps(item: PublicShopMenuItem): { target?: string; rel?: string } {
    return item.openInNewTab ? { target: "_blank", rel: "noreferrer" } : {};
}

const animatedUnderlineClass = "relative after:absolute after:inset-x-0 after:-bottom-1 after:h-0.5 after:origin-left after:scale-x-0 after:bg-current after:transition-transform after:duration-200 hover:after:scale-x-100 focus-visible:after:scale-x-100";
const headerPillActionClass = "inline-flex h-11 items-center gap-2 rounded-full border border-transparent px-3 text-xs font-bold text-[var(--site-text)] transition hover:bg-[var(--site-surface)] hover:text-[var(--site-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25";
const headerDropdownTriggerClass = "inline-flex h-11 items-center gap-2 rounded-full bg-[var(--site-surface)] py-1 pl-1 pr-3 text-xs font-black text-[var(--site-text)] ring-1 ring-[var(--site-border)] transition hover:-translate-y-0.5 hover:bg-[var(--site-surface-elevated)] hover:text-[var(--site-primary)] hover:shadow-[0_10px_28px_var(--site-shadow-color)] hover:ring-[var(--site-primary)]/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25";
const headerDropdownIconClass = "grid h-9 w-9 place-items-center rounded-full bg-[var(--site-surface)] text-[var(--site-primary)] shadow-sm ring-1 ring-[var(--site-border)]";
const headerDropdownChevronClass = "hidden h-3.5 w-3.5 text-[var(--site-muted)] transition group-hover:rotate-180 group-hover:text-[var(--site-primary)] group-focus-within:rotate-180 group-focus-within:text-[var(--site-primary)] sm:block";
const headerSemanticGridTriggerClass = "relative inline-flex min-h-[52px] w-full min-w-0 max-w-full flex-col items-center justify-center gap-1 rounded-xl px-1.5 py-1 text-[11px] font-bold leading-none text-[var(--site-text)] transition hover:-translate-y-0.5 hover:bg-[var(--site-primary-soft)] hover:text-[var(--site-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25";
const headerSemanticGridChevronClass = "absolute right-1.5 top-1.5 hidden h-3 w-3 text-[var(--site-muted)] transition group-hover:rotate-180 group-hover:text-[var(--site-primary)] group-focus-within:rotate-180 group-focus-within:text-[var(--site-primary)] xl:block";
const headerIconActionClass = "relative grid h-9 w-9 place-items-center rounded-full text-[var(--site-text)] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25 sm:h-11 sm:w-11";
const headerLabelActionClass = "relative inline-flex min-h-11 min-w-11 max-w-full items-center justify-center rounded-xl px-2 text-[var(--site-text)] transition hover:bg-[var(--site-surface-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25";
const softCheckboxClass = "mt-1 h-5 w-5 shrink-0 rounded-md border border-[var(--site-border-strong)] bg-[var(--site-surface)] accent-[var(--site-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25";
const readableOnSurfaceTextClass = "text-[color-mix(in_srgb,var(--site-text)_72%,transparent)]";
const readableOnSurfaceTextColor = "color-mix(in srgb, var(--site-text) 72%, transparent)";
const readableOnPrimaryTextColor = "color-mix(in srgb, var(--site-on-primary) 92%, transparent)";
const readableOnInverseTextClass = "text-[color-mix(in_srgb,var(--site-on-inverse)_72%,transparent)]";
const readableOnInverseTextColor = "color-mix(in srgb, var(--site-on-inverse) 72%, transparent)";
const shopAppearanceAnimationSelector = ":where([data-shop-animation-mode=\"appearance\"], [style*=\"--shop-animation-mode\"][style*=\"appearance\"])";
const shopScrollAnimationSelector = ":where([data-shop-animation-mode=\"scroll\"], [style*=\"--shop-animation-mode\"][style*=\"scroll\"])";
const shopInteractionCss = `
:where([data-shop-hover-effect], [data-shop-hover-colors], [style*="--shop-hover-has-options"]) {
    transition-property: transform, box-shadow, filter, color, background-color, border-color, text-decoration-color;
    transition-duration: var(--shop-hover-duration, 180ms);
    transition-timing-function: cubic-bezier(0.2, 0.8, 0.2, 1);
}

[style*="--shop-hover-transform"]:is(:hover, :focus-visible) {
    transform: var(--shop-hover-transform) !important;
}

[style*="--shop-hover-shadow"]:is(:hover, :focus-visible) {
    box-shadow: var(--shop-hover-shadow) !important;
}

[style*="--shop-hover-filter"]:is(:hover, :focus-visible) {
    filter: var(--shop-hover-filter) !important;
}

[style*="--shop-hover-text-color"]:is(:hover, :focus-visible) {
    color: var(--shop-hover-text-color) !important;
}

[style*="--shop-hover-background-color"]:is(:hover, :focus-visible) {
    background-color: var(--shop-hover-background-color) !important;
}

[style*="--shop-hover-border-color"]:is(:hover, :focus-visible) {
    border-color: var(--shop-hover-border-color) !important;
}

[style*="--shop-hover-underline"]:is(:hover, :focus-visible) {
    text-decoration-line: underline;
    text-decoration-thickness: var(--shop-hover-underline-thickness, 2px);
    text-underline-offset: var(--shop-hover-underline-offset, 0.22em);
}

:where([data-shop-image-hover-effect]) [data-shop-image-hover-target],
:where([data-shop-image-hover-target][style*="--shop-image-hover-"]) {
    transition-property: transform, filter, box-shadow, border-color;
    transition-duration: var(--shop-image-hover-duration, 520ms);
    transition-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
    will-change: transform, filter, box-shadow;
}

[data-shop-image-hover-effect]:is(:hover, :focus-within) [data-shop-image-hover-target][style*="--shop-image-hover-transform"],
[data-shop-image-hover-target][style*="--shop-image-hover-transform"]:is(:hover, :focus-visible) {
    transform: var(--shop-image-hover-transform) !important;
}

[data-shop-image-hover-effect]:is(:hover, :focus-within) [data-shop-image-hover-target][style*="--shop-image-hover-filter"],
[data-shop-image-hover-target][style*="--shop-image-hover-filter"]:is(:hover, :focus-visible) {
    filter: var(--shop-image-hover-filter) !important;
}

[data-shop-image-hover-effect]:is(:hover, :focus-within) [data-shop-image-hover-target][style*="--shop-image-hover-shadow"],
[data-shop-image-hover-target][style*="--shop-image-hover-shadow"]:is(:hover, :focus-visible) {
    box-shadow: var(--shop-image-hover-shadow) !important;
}

[data-shop-image-hover-effect="scale"]:is(:hover, :focus-within) [data-shop-image-hover-target] {
    transform: scale(1.06) !important;
}

[data-shop-image-hover-effect="scale-sm"]:is(:hover, :focus-within) [data-shop-image-hover-target] {
    transform: scale(1.025) !important;
}

[data-shop-image-hover-effect="lift"]:is(:hover, :focus-within) [data-shop-image-hover-target] {
    transform: translateY(-8px) !important;
}

[data-shop-image-hover-effect="lift-soft"]:is(:hover, :focus-within) [data-shop-image-hover-target] {
    transform: translateY(-4px) !important;
}

[data-shop-image-hover-effect="rotate"]:is(:hover, :focus-within) [data-shop-image-hover-target] {
    transform: rotate(1.6deg) scale(1.035) !important;
}

[data-shop-image-hover-effect="skew"]:is(:hover, :focus-within) [data-shop-image-hover-target] {
    transform: skewX(-2deg) scale(1.025) !important;
}

[data-shop-image-hover-effect="slide-right"]:is(:hover, :focus-within) [data-shop-image-hover-target] {
    transform: translateX(8px) !important;
}

[data-shop-image-hover-effect="brighten"]:is(:hover, :focus-within) [data-shop-image-hover-target] {
    filter: brightness(1.08) !important;
}

[data-shop-image-hover-effect="darken"]:is(:hover, :focus-within) [data-shop-image-hover-target] {
    filter: brightness(0.88) !important;
}

[data-shop-image-hover-effect="blur"]:is(:hover, :focus-within) [data-shop-image-hover-target] {
    filter: blur(1.25px) !important;
}

[data-shop-image-hover-effect="shadow"]:is(:hover, :focus-within) [data-shop-image-hover-target] {
    box-shadow: 0 18px 44px var(--site-shadow-color) !important;
}

[data-shop-image-hover-effect="glow"]:is(:hover, :focus-within) [data-shop-image-hover-target] {
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--site-primary) 28%, transparent), 0 0 34px color-mix(in srgb, var(--site-primary) 26%, transparent) !important;
}

[data-shop-image-hover-effect="outline"]:is(:hover, :focus-within) [data-shop-image-hover-target] {
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--site-primary) 34%, transparent) !important;
}

:where([data-shop-item-hover-effect]) {
    transition-property: transform, box-shadow, filter, color, background-color, border-color;
    transition-duration: var(--shop-hover-duration, 180ms);
    transition-timing-function: cubic-bezier(0.2, 0.8, 0.2, 1);
}

[data-shop-item-hover-effect="lift"]:is(:hover, :focus-within) {
    box-shadow: 0 22px 55px var(--site-shadow-color);
    transform: translateY(-8px);
}

[data-shop-item-hover-effect="lift-soft"]:is(:hover, :focus-within) {
    box-shadow: 0 18px 44px var(--site-shadow-color);
    transform: translateY(-4px);
}

[data-shop-item-hover-effect="scale"]:is(:hover, :focus-within) {
    transform: scale(1.045);
}

[data-shop-item-hover-effect="scale-sm"]:is(:hover, :focus-within) {
    transform: scale(1.02);
}

[data-shop-item-hover-effect="shadow"]:is(:hover, :focus-within) {
    box-shadow: 0 18px 44px var(--site-shadow-color);
}

[data-shop-item-hover-effect="glow"]:is(:hover, :focus-within) {
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--site-primary) 28%, transparent), 0 0 34px color-mix(in srgb, var(--site-primary) 26%, transparent);
}

[data-shop-item-hover-effect="outline"]:is(:hover, :focus-within) {
    border-color: var(--site-primary);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--site-primary) 28%, transparent);
}

[data-shop-item-hover-effect="rotate"]:is(:hover, :focus-within) {
    transform: rotate(1.6deg);
}

[data-shop-item-hover-effect="skew"]:is(:hover, :focus-within) {
    transform: skewX(-2deg);
}

[data-shop-item-hover-effect="slide-right"]:is(:hover, :focus-within) {
    transform: translateX(8px);
}

[data-shop-item-hover-effect="brighten"]:is(:hover, :focus-within) {
    filter: brightness(1.08);
}

[data-shop-item-hover-effect="darken"]:is(:hover, :focus-within) {
    filter: brightness(0.88);
}

[data-shop-item-hover-effect="blur"]:is(:hover, :focus-within) {
    filter: blur(1.25px);
}

:where([data-shop-animation], [style*="--shop-animation-name"]) {
    animation-delay: var(--shop-animation-delay, 0ms);
    animation-duration: var(--shop-animation-duration, 700ms);
    animation-fill-mode: both;
    animation-name: var(--shop-animation-name);
    animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
    will-change: transform, opacity, filter, clip-path;
}

${shopAppearanceAnimationSelector} {
    animation-name: none !important;
    opacity: 0;
}

${shopAppearanceAnimationSelector}:where(
    [data-shop-animation^="clip-"],
    [data-shop-animation^="reveal-"],
    [style*="shopClip"],
    [style*="shopReveal"]
) {
    clip-path: var(--shop-animation-initial-clip-path, inset(100% 0 0 0));
}

${shopAppearanceAnimationSelector}.shop-animation-visible {
    animation-name: var(--shop-animation-name) !important;
    opacity: 1;
}

:where(
    [data-shop-animation^="clip-"],
    [data-shop-animation^="reveal-"],
    [style*="shopClip"],
    [style*="shopReveal"]
).shop-animation-visible {
    clip-path: inset(0 0 0 0);
}

@supports (animation-timeline: view()) {
    ${shopScrollAnimationSelector} {
        animation-delay: 0ms;
        animation-range: entry var(--shop-animation-scroll-start, 0%) cover var(--shop-animation-scroll-end, 32%);
        animation-timeline: view();
    }
}

@supports not (animation-timeline: view()) {
    ${shopScrollAnimationSelector} {
        animation-name: none !important;
        opacity: 0;
    }

    ${shopScrollAnimationSelector}:where(
        [data-shop-animation^="clip-"],
        [data-shop-animation^="reveal-"],
        [style*="shopClip"],
        [style*="shopReveal"]
    ) {
        clip-path: var(--shop-animation-initial-clip-path, inset(100% 0 0 0));
    }

    ${shopScrollAnimationSelector}.shop-animation-visible {
        animation-name: var(--shop-animation-name) !important;
        opacity: 1;
    }
}

@keyframes shopFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes shopFadeUp {
    from { opacity: 0; transform: translateY(28px); }
    to { opacity: 1; transform: translateY(0); }
}

@keyframes shopFadeDown {
    from { opacity: 0; transform: translateY(-28px); }
    to { opacity: 1; transform: translateY(0); }
}

@keyframes shopFadeLeft {
    from { opacity: 0; transform: translateX(28px); }
    to { opacity: 1; transform: translateX(0); }
}

@keyframes shopFadeRight {
    from { opacity: 0; transform: translateX(-28px); }
    to { opacity: 1; transform: translateX(0); }
}

@keyframes shopZoomIn {
    from { opacity: 0; transform: scale(0.94); }
    to { opacity: 1; transform: scale(1); }
}

@keyframes shopZoomOut {
    from { opacity: 0; transform: scale(1.06); }
    to { opacity: 1; transform: scale(1); }
}

@keyframes shopFlipX {
    from { opacity: 0; transform: perspective(900px) rotateX(72deg); }
    to { opacity: 1; transform: perspective(900px) rotateX(0); }
}

@keyframes shopFlipY {
    from { opacity: 0; transform: perspective(900px) rotateY(72deg); }
    to { opacity: 1; transform: perspective(900px) rotateY(0); }
}

@keyframes shopRotateIn {
    from { opacity: 0; transform: rotate(-7deg) scale(0.96); }
    to { opacity: 1; transform: rotate(0) scale(1); }
}

@keyframes shopBlurIn {
    from { opacity: 0; filter: blur(12px); transform: translateY(8px); }
    to { opacity: 1; filter: blur(0); transform: translateY(0); }
}

@keyframes shopBounceIn {
    0% { opacity: 0; transform: scale(0.88); }
    58% { opacity: 1; transform: scale(1.035); }
    78% { transform: scale(0.985); }
    100% { opacity: 1; transform: scale(1); }
}

@keyframes shopFloatIn {
    from { opacity: 0; transform: translateY(24px); }
    70% { opacity: 1; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
}

@keyframes shopClipUp {
    from { opacity: 0; clip-path: inset(100% 0 0 0); transform: translateY(16px); }
    to { opacity: 1; clip-path: inset(0 0 0 0); transform: translateY(0); }
}

@keyframes shopClipDown {
    from { opacity: 0; clip-path: inset(0 0 100% 0); transform: translateY(-16px); }
    to { opacity: 1; clip-path: inset(0 0 0 0); transform: translateY(0); }
}

@keyframes shopClipLeft {
    from { opacity: 0; clip-path: inset(0 0 0 100%); transform: translateX(16px); }
    to { opacity: 1; clip-path: inset(0 0 0 0); transform: translateX(0); }
}

@keyframes shopClipRight {
    from { opacity: 0; clip-path: inset(0 100% 0 0); transform: translateX(-16px); }
    to { opacity: 1; clip-path: inset(0 0 0 0); transform: translateX(0); }
}

@keyframes shopRevealLeft {
    from { opacity: 0; clip-path: inset(0 100% 0 0); }
    to { opacity: 1; clip-path: inset(0 0 0 0); }
}

@keyframes shopRevealRight {
    from { opacity: 0; clip-path: inset(0 0 0 100%); }
    to { opacity: 1; clip-path: inset(0 0 0 0); }
}

@keyframes shopRevealUp {
    from { opacity: 0; clip-path: inset(100% 0 0 0); }
    to { opacity: 1; clip-path: inset(0 0 0 0); }
}

@keyframes shopRevealDown {
    from { opacity: 0; clip-path: inset(0 0 100% 0); }
    to { opacity: 1; clip-path: inset(0 0 0 0); }
}

@media (prefers-reduced-motion: reduce) {
    :where([data-shop-hover-effect], [data-shop-hover-colors], [style*="--shop-hover-has-options"]) {
        transition-duration: 1ms !important;
    }

    :where([style*="--shop-hover-transform"], [style*="--shop-hover-filter"]):is(:hover, :focus-visible) {
        filter: none !important;
        transform: none !important;
    }

    :where([data-shop-image-hover-effect]) [data-shop-image-hover-target] {
        transition-duration: 1ms !important;
    }

    :where([data-shop-image-hover-effect]):is(:hover, :focus-within) [data-shop-image-hover-target] {
        filter: none !important;
        transform: none !important;
    }

    :where([data-shop-item-hover-effect]) {
        transition-duration: 1ms !important;
    }

    :where([data-shop-item-hover-effect]):is(:hover, :focus-within) {
        box-shadow: inherit !important;
        filter: none !important;
        transform: none !important;
    }
}

.laguna-semantic-header,
.laguna-semantic-header-menu {
    min-height: 0 !important;
}

@media (max-width: 1023px) {
    [data-shop-mobile-header-layout="single-row"][data-shop-semantic-chrome-grid="header"] {
        height: 64px !important;
        min-height: 64px !important;
    }

    [data-shop-mobile-header-layout="single-row"][data-shop-semantic-chrome-grid="header"] > [data-shop-semantic-grid="true"] {
        grid-auto-rows: 0 !important;
        grid-template-rows: 64px !important;
        height: 64px !important;
        min-height: 64px !important;
    }

    [data-shop-mobile-header-layout="single-row"][data-shop-semantic-chrome-grid="header"] > [data-shop-semantic-grid="true"] > [data-shop-semantic-grid-item="true"] {
        max-height: 64px !important;
        min-height: 0 !important;
        padding-block: 0 !important;
    }

    [data-shop-mobile-header-layout="single-row"][data-shop-semantic-chrome-grid="header"] [data-shop-document-role="headerLogo"] {
        height: 64px !important;
        padding-left: 52px !important;
    }

    [data-shop-mobile-header-layout="single-row"][data-shop-semantic-chrome-grid="header"] [data-shop-document-role="headerMenu"] {
        display: contents !important;
        pointer-events: none;
    }

    [data-shop-mobile-header-layout="single-row"][data-shop-semantic-chrome-grid="header"] [data-shop-document-role="headerMenu"] > button {
        pointer-events: auto;
        z-index: 10;
    }

    [data-shop-mobile-header-layout="single-row"][data-shop-semantic-chrome-grid="header"] [data-shop-document-role="headerMenu"] > div {
        display: none !important;
    }

    [data-shop-mobile-header-contrast="high"] [data-shop-document-role="headerLogo"] [data-shop-runtime-text="shopName"] {
        font-size: 0.875rem !important;
        line-height: 1.15 !important;
        max-width: min(48vw, 184px) !important;
    }

    [data-shop-grid-stack-on-mobile="true"] {
        bottom: auto !important;
        grid-auto-rows: auto !important;
        grid-template-columns: minmax(0, 1fr) !important;
        grid-template-rows: none !important;
        height: auto !important;
        inset: auto !important;
        max-width: none !important;
        min-height: 0 !important;
        min-width: 0 !important;
        position: relative !important;
        width: 100% !important;
    }

    [data-shop-grid-stack-on-mobile="true"] > [data-shop-grid-item="true"] {
        grid-column: 1 !important;
        grid-row: auto !important;
        height: auto !important;
        min-height: var(--shop-grid-mobile-min-height, 280px) !important;
        min-width: 0 !important;
        width: 100% !important;
    }

    [data-shop-semantic-grid="true"] {
        grid-template-columns: minmax(0, 1fr) !important;
        grid-template-rows: none !important;
        grid-auto-rows: auto !important;
    }

    [data-shop-semantic-grid="true"] > [data-shop-semantic-grid-item="true"] {
        grid-column: 1 !important;
        grid-row: auto !important;
        min-width: 0;
    }

    [data-shop-semantic-chrome-grid="header"] > [data-shop-semantic-grid="true"] {
        grid-template-columns: minmax(0, 1fr) auto auto !important;
    }

    [data-shop-semantic-chrome-grid="header"] > [data-shop-semantic-grid="true"] > [data-shop-document-role="headerLogo"] {
        grid-column: 1 !important;
        grid-row: 1 !important;
    }

    [data-shop-semantic-chrome-grid="header"] > [data-shop-semantic-grid="true"] > [data-shop-document-role="headerFavoritesButton"] {
        grid-column: 2 !important;
        grid-row: 1 !important;
    }

    [data-shop-semantic-chrome-grid="header"] > [data-shop-semantic-grid="true"] > [data-shop-document-role="headerCartButton"] {
        grid-column: 3 !important;
        grid-row: 1 !important;
    }

    [data-shop-semantic-chrome-grid="header"] > [data-shop-semantic-grid="true"] > [data-shop-document-role="headerMenu"] {
        grid-column: 1 / -1 !important;
        grid-row: auto !important;
    }

    .laguna-semantic-header-desktop-only {
        display: none !important;
        min-height: 0 !important;
        padding-block: 0 !important;
    }

    .laguna-semantic-header-hide-mobile {
        display: none !important;
        min-height: 0 !important;
        padding-block: 0 !important;
    }

    .laguna-semantic-header [data-shop-runtime-text="shopName"] {
        font-size: 1.125rem !important;
        max-width: 128px !important;
    }

}

@media (min-width: 1024px) {
    [data-shop-semantic-grid="true"] > [data-shop-semantic-grid-item="true"] {
        grid-column: var(--shop-semantic-grid-column);
        grid-row: var(--shop-semantic-grid-row);
    }

    .laguna-semantic-header {
        min-height: var(--shop-semantic-header-min-height, 0px) !important;
    }

    .laguna-semantic-header-menu {
        margin-inline: calc(50% - 50vw) !important;
        max-width: none !important;
        min-height: var(--shop-semantic-grid-min-height, 0px) !important;
        width: 100vw !important;
    }

    .laguna-semantic-header-hide-desktop {
        display: none !important;
        min-height: 0 !important;
        padding-block: 0 !important;
    }

    [data-shop-block-index][data-shop-block-type] [data-shop-grid-container][data-site-builder-authored-grid-columns="true"] {
        grid-template-columns: var(--site-builder-authored-grid-columns) !important;
    }

    [data-shop-block-index][data-shop-block-type] [data-shop-grid-container][data-site-builder-authored-grid-rows="true"] {
        grid-template-rows: var(--site-builder-authored-grid-rows) !important;
    }

    [data-shop-block-index][data-shop-block-type] [data-shop-grid-container] > :is([data-shop-grid-item="true"], [data-shop-semantic-grid-item="true"])[data-site-builder-authored-grid-column="true"] {
        grid-column: var(--site-builder-authored-grid-column) !important;
    }

    [data-shop-block-index][data-shop-block-type] [data-shop-grid-container] > :is([data-shop-grid-item="true"], [data-shop-semantic-grid-item="true"])[data-site-builder-authored-grid-row="true"] {
        grid-row: var(--site-builder-authored-grid-row) !important;
    }
}

[data-shop-animation-preview="true"] :where([data-shop-animation], [style*="--shop-animation-name"], [style*="animation-name"]) {
    animation-delay: var(--shop-animation-delay, 0ms) !important;
    animation-duration: var(--shop-animation-duration, 700ms) !important;
    animation-fill-mode: both !important;
    animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1) !important;
}

.shop-animation-restarting {
    animation-name: none !important;
}

.shop-animation-restarting:where(
    [data-shop-animation^="clip-"],
    [data-shop-animation^="reveal-"],
    [style*="shopClip"],
    [style*="shopReveal"]
) {
    clip-path: var(--shop-animation-initial-clip-path, inset(100% 0 0 0)) !important;
}
@media (min-width: 1024px) {
    ${SITE_BUILDER_AUTHORITATIVE_STYLE_CSS}
}
`;

function ShopInteractionStyles() {
    return <style>{shopInteractionCss}</style>;
}

function StoreHeaderDropdown({
    cities,
    storesHref,
    onClose,
    presentation = "default",
    triggerContent,
}: {
    cities: PublicStoreCity[];
    storesHref: string;
    onClose: () => void;
    presentation?: "default" | "semanticGrid";
    triggerContent?: ReactNode;
}) {
    const message = useShopStorefrontMessage();
    const hasStores = cities.some((city) => city.stores.length > 0);
    const semanticGrid = presentation === "semanticGrid";
    const triggerClassName = semanticGrid
        ? headerSemanticGridTriggerClass
        : headerDropdownTriggerClass;

    if (!hasStores) {
        return (
            <a
                href={storesHref}
                className={classNames(triggerClassName, "hidden sm:inline-flex")}
                data-shop-header-dropdown-presentation={presentation}
            >
                {triggerContent ?? (
                    <>
                        <span className={headerDropdownIconClass}>
                            <Icon name="mapPin" className="h-7 w-7" />
                        </span>
                        <span className="hidden xl:block">{message("ui.header.stores")}</span>
                    </>
                )}
            </a>
        );
    }

    return (
        <div
            className={classNames(
                "group relative hidden sm:block",
                semanticGrid ? "w-full min-w-0" : null,
            )}
            onMouseLeave={onClose}
        >
            <a
                href={storesHref}
                className={triggerClassName}
                data-shop-header-dropdown-presentation={presentation}
                aria-haspopup="menu"
            >
                {triggerContent ?? (
                    <>
                        <span className={headerDropdownIconClass}>
                            <Icon name="mapPin" className="h-7 w-7" />
                        </span>
                        <span className="hidden xl:block">{message("ui.header.stores")}</span>
                    </>
                )}
                <Icon
                    name="chevronDown"
                    className={semanticGrid
                        ? headerSemanticGridChevronClass
                        : headerDropdownChevronClass}
                />
            </a>
            <div className={classNames(
                "absolute end-0 top-full z-50 w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-[22px] border border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] bg-[var(--site-surface)] text-left shadow-[0_22px_70px_var(--site-shadow-color)] transition",
                "group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:opacity-100",
                "pointer-events-none -translate-y-1 opacity-0",
            )}>
                <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
                    <div className="grid gap-5">
                        {cities.map((city) => (
                            <section key={city.name} className="min-w-0">
                                <h3 className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">{city.name}</h3>
                                <div className="mt-2 space-y-1">
                                    {city.stores.map((store) => (
                                        <a
                                            key={store.uuid}
                                            href={store.href}
                                            className="block rounded-xl px-3 py-2 transition hover:bg-[var(--site-surface)] hover:text-[var(--site-primary)] focus-visible:bg-[var(--site-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25"
                                            onClick={onClose}
                                        >
                                            <span className="block text-sm font-black">{store.name}</span>
                                            {store.addressLine ? (
                                                <span className="mt-0.5 block truncate text-xs font-semibold text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">{store.addressLine}</span>
                                            ) : null}
                                        </a>
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function MobileStoresLink({ storesHref }: { storesHref: string }) {
    const message = useShopStorefrontMessage();

    return (
        <div className="border-t border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] px-4 py-2 sm:hidden">
            <a
                href={storesHref}
                className="flex min-h-11 items-center justify-center gap-2 rounded-full bg-[var(--site-surface)] px-4 text-sm font-black text-[color-mix(in_srgb,var(--site-text)_75%,transparent)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)] transition hover:bg-[var(--site-surface)] hover:text-[var(--site-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25"
            >
                <Icon name="mapPin" className="h-4 w-4 text-[var(--site-primary)]" />
                {message("ui.header.stores")}
            </a>
        </div>
    );
}

function AccountHeaderDropdown({
    accountHref,
    customerSession,
    loading,
    onLogout,
    presentation = "default",
    triggerContent,
}: {
    accountHref: string;
    customerSession: PublicCustomerSession;
    loading: boolean;
    onLogout: () => Promise<void>;
    presentation?: "default" | "semanticGrid";
    triggerContent?: ReactNode;
}) {
    const message = useShopStorefrontMessage();
    const semanticGrid = presentation === "semanticGrid";
    const triggerClassName = semanticGrid
        ? headerSemanticGridTriggerClass
        : headerDropdownTriggerClass;

    if (!customerSession.authenticated || customerSession.customer === null) {
        return (
            <a
                href={accountHref}
                className={classNames(triggerClassName, "hidden sm:inline-flex")}
                data-shop-header-dropdown-presentation={presentation}
            >
                {triggerContent ?? (
                    <>
                        <span className={headerDropdownIconClass}>
                            <Icon name="user" className="h-7 w-7" />
                        </span>
                        <span className="hidden xl:block">{message("ui.header.signIn")}</span>
                    </>
                )}
            </a>
        );
    }

    const customerName = [
        customerSession.customer.firstname,
        customerSession.customer.lastname,
    ].filter(Boolean).join(" ") || customerSession.customer.email;
    const customerInitial = customerName.trim().charAt(0).toUpperCase() || "U";
    const links: Array<{ label: string; href: string }> = [
        { label: message("ui.account.overview"), href: accountHref },
        { label: message("ui.account.orders"), href: accountSectionHref(accountHref, "orders") },
        { label: message("ui.account.addresses"), href: accountSectionHref(accountHref, "addresses") },
        { label: message("ui.account.favorites"), href: accountSectionHref(accountHref, "favorites") },
        { label: message("ui.account.settings"), href: accountSectionHref(accountHref, "settings") },
    ];

    return (
        <div className={classNames(
            "group relative hidden sm:block",
            semanticGrid ? "w-full min-w-0" : null,
        )}>
            <a
                href={accountHref}
                className={triggerClassName}
                data-shop-header-dropdown-presentation={presentation}
                aria-haspopup="menu"
            >
                {triggerContent ?? (
                    <>
                        <span className={classNames(headerDropdownIconClass, "font-black")}>
                            {customerInitial}
                        </span>
                        <span className="hidden xl:block">{message("ui.header.myAccount")}</span>
                    </>
                )}
                <Icon
                    name="chevronDown"
                    className={semanticGrid
                        ? headerSemanticGridChevronClass
                        : headerDropdownChevronClass}
                />
            </a>
            <div className="pointer-events-none absolute end-0 top-full z-50 w-[min(22rem,calc(100vw-2rem))] -translate-y-1 overflow-hidden rounded-[22px] border border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] bg-[var(--site-surface)] text-left opacity-0 shadow-[0_22px_70px_var(--site-shadow-color)] transition group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:opacity-100">
                <div className="border-b border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] bg-[var(--site-surface)] px-5 py-4">
                    <p className="truncate text-sm font-black text-[var(--site-text)]">{customerName}</p>
                    <p className="mt-0.5 truncate text-xs font-semibold text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">{customerSession.customer.email}</p>
                </div>
                <div className="px-5 py-4">
                    <div className="space-y-1">
                        {links.map((link) => (
                            <a
                                key={link.href}
                                href={link.href}
                                className="block rounded-xl px-3 py-2 text-sm font-bold transition hover:bg-[var(--site-surface)] hover:text-[var(--site-primary)] focus-visible:bg-[var(--site-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25"
                            >
                                {link.label}
                            </a>
                        ))}
                    </div>
                    <button
                        type="button"
                        className="mt-3 block w-full rounded-xl border border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] px-3 py-2 text-left text-sm font-black text-[color-mix(in_srgb,var(--site-text)_65%,transparent)] transition hover:border-[var(--site-primary)] hover:bg-[var(--site-primary-soft)] hover:text-[var(--site-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)] disabled:cursor-wait disabled:opacity-60"
                        disabled={loading}
                        onClick={() => {
                            void onLogout();
                        }}
                    >
                        {message("ui.account.logout")}
                    </button>
                </div>
            </div>
        </div>
    );
}

function HeaderSelectorDropdown({
    currentCode,
    options,
    ariaLabel,
    onSelect,
    triggerContent,
}: {
    currentCode: string;
    options: HeaderSelectorOption[];
    ariaLabel: string;
    onSelect?: (code: string, href: string) => boolean | void;
    triggerContent?: ReactNode;
}) {
    if (options.length <= 1) {
        return (
            <a href={functionalStorefrontHref(options[0]?.href)} className={classNames(headerPillActionClass, "hidden px-3 font-black uppercase sm:inline-flex")}>
                {triggerContent ?? currentCode}
            </a>
        );
    }

    return (
        <div className="group relative hidden sm:block">
            <a
                href={functionalStorefrontHref(options.find((option) => option.active)?.href)}
                className={classNames(headerPillActionClass, "px-3 font-black uppercase")}
                aria-haspopup="menu"
                aria-label={ariaLabel}
            >
                {triggerContent ?? currentCode}
                <Icon name="chevronDown" className="h-3.5 w-3.5 text-[color-mix(in_srgb,var(--site-text)_35%,transparent)] transition group-hover:rotate-180 group-hover:text-[var(--site-primary)] group-focus-within:rotate-180 group-focus-within:text-[var(--site-primary)]" />
            </a>
            <div className="pointer-events-none absolute end-0 top-full z-50 w-[min(18rem,calc(100vw-2rem))] -translate-y-1 overflow-hidden rounded-[22px] border border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] bg-[var(--site-surface)] text-left opacity-0 shadow-[0_22px_70px_var(--site-shadow-color)] transition group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:opacity-100">
                <div className="px-3 py-3">
                    {options.map((option) => (
                        <a
                            key={option.code}
                            href={option.href}
                            className={classNames(
                                "flex items-center justify-between gap-4 rounded-xl px-3 py-2 text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25",
                                option.active ? "bg-[var(--site-surface)] font-black text-[var(--site-primary)]" : "font-bold text-[color-mix(in_srgb,var(--site-text)_70%,transparent)] hover:bg-[var(--site-surface)] hover:text-[var(--site-primary)]",
                            )}
                            aria-current={option.active ? "true" : undefined}
                            onClick={(event) => {
                                if (!onSelect) {
                                    return;
                                }

                                if (onSelect(option.code, option.href) === true) {
                                    event.preventDefault();
                                }
                            }}
                        >
                            <span className="truncate">{option.label}</span>
                            <span className="shrink-0 text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">{option.code}</span>
                        </a>
                    ))}
                </div>
            </div>
        </div>
    );
}

type MegaMenuColumn = {
    key: string;
    title?: string;
    titleHref?: string;
    links: PublicShopMenuItem[];
};

function megaMenuColumns(item: PublicShopMenuItem, recommendedTitle: string): MegaMenuColumn[] {
    const directLinks = item.children.filter((child) => child.children.length === 0);
    const childrenWithChildren = item.children.filter((child) => child.children.length > 0);

    if (childrenWithChildren.length > 0) {
        return [
            ...(directLinks.length > 0 ? [{ key: `${item.uuid}:recommended`, title: recommendedTitle, links: directLinks }] : []),
            ...childrenWithChildren.map((child) => ({
                key: child.uuid,
                title: child.label,
                titleHref: functionalStorefrontHref(menuItemHref(child)),
                links: child.children,
            })),
        ];
    }

    return item.children.length > 0 ? [{ key: `${item.uuid}:children`, title: item.label, links: item.children }] : [];
}

function megaMenuLinkGroups(links: PublicShopMenuItem[], groupSize = 6): PublicShopMenuItem[][] {
    const groups: PublicShopMenuItem[][] = [];

    for (let index = 0; index < links.length; index += groupSize) {
        groups.push(links.slice(index, index + groupSize));
    }

    return groups;
}

function checkoutBaseHrefFromStepHref(href: string): string {
    const normalized = normalizedPath(href);

    return normalized.replace(/[123]$/, "").replace(/-dokoncena$/, "");
}

function Icon({ name, className = "" }: { name: IconName; className?: string }) {
    const common = {
        className,
        fill: "none",
        stroke: "currentColor",
        strokeLinecap: "round" as const,
        strokeLinejoin: "round" as const,
        strokeWidth: 2,
        viewBox: "0 0 24 24",
        "aria-hidden": true,
    };

    if (name === "menu") {
        return (
            <svg {...common}>
                <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
        );
    }

    if (name === "x") {
        return (
            <svg {...common}>
                <path d="m6 6 12 12M18 6 6 18" />
            </svg>
        );
    }

    if (name === "search") {
        return (
            <svg {...common}>
                <circle cx="11" cy="11" r="7" />
                <path d="m16 16 4 4" />
            </svg>
        );
    }

    if (name === "heart") {
        return (
            <svg {...common}>
                <path d="M20.5 8.6c0 5.1-8.5 10.1-8.5 10.1S3.5 13.7 3.5 8.6A4.5 4.5 0 0 1 12 6.2a4.5 4.5 0 0 1 8.5 2.4Z" />
            </svg>
        );
    }

    if (name === "bag") {
        return (
            <svg {...common}>
                <path d="M6 8h12l-1 12H7L6 8Z" />
                <path d="M9 8a3 3 0 0 1 6 0" />
            </svg>
        );
    }

    if (name === "creditCard") {
        return (
            <svg {...common}>
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M3 10h18M7 15h4" />
            </svg>
        );
    }

    if (name === "mapPin") {
        return (
            <svg {...common}>
                <path d="M12 21s7-5.2 7-12a7 7 0 1 0-14 0c0 6.8 7 12 7 12Z" />
                <circle cx="12" cy="9" r="2.4" />
            </svg>
        );
    }

    if (name === "mail") {
        return (
            <svg {...common}>
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="m3 7 9 6 9-6" />
            </svg>
        );
    }

    if (name === "info") {
        return (
            <svg {...common}>
                <circle cx="12" cy="12" r="9" />
                <path d="M12 10v6" />
                <path d="M12 7h.01" />
            </svg>
        );
    }

    if (name === "navigation") {
        return (
            <svg {...common}>
                <path d="m12 3 7 18-7-4-7 4 7-18Z" />
            </svg>
        );
    }

    if (name === "home") {
        return (
            <svg {...common}>
                <path d="m4 11 8-7 8 7" />
                <path d="M6 10v10h12V10" />
                <path d="M10 20v-5h4v5" />
            </svg>
        );
    }

    if (name === "briefcase") {
        return (
            <svg {...common}>
                <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                <rect x="4" y="7" width="16" height="13" rx="2" />
                <path d="M4 12h16M10 12v2h4v-2" />
            </svg>
        );
    }

    if (name === "user") {
        return (
            <svg {...common}>
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21a8 8 0 0 1 16 0" />
            </svg>
        );
    }

    if (name === "chevronDown") {
        return (
            <svg {...common}>
                <path d="m6 9 6 6 6-6" />
            </svg>
        );
    }

    if (name === "check") {
        return (
            <svg {...common}>
                <path d="m5 12 4 4 10-10" />
            </svg>
        );
    }

    if (name === "chevronLeft") {
        return (
            <svg {...common}>
                <path d="m15 18-6-6 6-6" />
            </svg>
        );
    }

    if (name === "chevronRight") {
        return (
            <svg {...common}>
                <path d="m9 18 6-6-6-6" />
            </svg>
        );
    }

    if (name === "truck") {
        return (
            <svg {...common}>
                <path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" />
                <circle cx="7" cy="18" r="2" />
                <circle cx="17" cy="18" r="2" />
            </svg>
        );
    }

    if (name === "store") {
        return (
            <svg {...common}>
                <path d="M4 10h16l-1-5H5l-1 5ZM6 10v10h12V10" />
                <path d="M9 20v-5h6v5" />
            </svg>
        );
    }

    if (name === "rotate") {
        return (
            <svg {...common}>
                <path d="M4 12a8 8 0 0 1 13.6-5.7L20 8" />
                <path d="M20 4v4h-4M20 12a8 8 0 0 1-13.6 5.7L4 16" />
                <path d="M4 20v-4h4" />
            </svg>
        );
    }

    if (name === "phone") {
        return (
            <svg {...common}>
                <path d="M8 3h8a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
                <path d="M10 18h4" />
            </svg>
        );
    }

    if (name === "shield") {
        return (
            <svg {...common}>
                <path d="M12 3 5 6v6c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Z" />
                <path d="m9 12 2 2 4-4" />
            </svg>
        );
    }

    if (name === "trash") {
        return (
            <svg {...common}>
                <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
            </svg>
        );
    }

    return null;
}

const heroSlides: HeroSlide[] = [
    {
        image: "https://images.unsplash.com/photo-1515347619252-60a4bf4fff4f?auto=format&fit=crop&w=2000&q=88",
        alt: "Letni kolekce obuvi",
        eyebrow: "Nova letni kolekce",
        title: "Detaily, které tvoří váš styl.",
        description: "Objevte nové sandály, tenisky a doplňky za ceny, které vás budou bavit.",
        primaryLabel: "Damy",
        primaryHref: "#damske",
        secondaryLabel: "Pani",
        secondaryHref: "#panske",
        overlay: "from-[color-mix(in_srgb,var(--site-text)_45%,transparent)] via-[color-mix(in_srgb,var(--site-text)_10%,transparent)] to-transparent",
    },
    {
        image: "https://images.unsplash.com/photo-1552346154-21d32810aba3?auto=format&fit=crop&w=2000&q=88",
        alt: "Barevne tenisky",
        eyebrow: "Sneaker season",
        title: "Kazdy krok se pocita.",
        description: "Nove modely od 699 Kc.",
        primaryLabel: "Objevit novinky",
        primaryHref: "#novinky",
        overlay: "from-[var(--site-inverse)]/80 via-[var(--site-primary)]/30 to-transparent",
    },
    {
        image: "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?auto=format&fit=crop&w=2000&q=88",
        alt: "Deti v letni kolekci",
        eyebrow: "Pro mala dobrodruzstvi",
        title: "Boty, ktere drzi krok.",
        description: "Pohodli a barvy pro kazdy den.",
        primaryLabel: "Detska kolekce",
        primaryHref: "#detske",
        overlay: "from-[color-mix(in_srgb,var(--site-text)_55%,transparent)] via-[color-mix(in_srgb,var(--site-text)_10%,transparent)] to-transparent",
    },
];

function isAccountSection(value: string): value is AccountSection {
    return accountSectionIds.has(value as AccountSection);
}

function accountSectionFromHash(hash: string): AccountSection | null {
    const normalized = hash.replace(/^#/, "").trim();

    return isAccountSection(normalized) ? normalized : null;
}

function accountSectionHref(accountHref: string, section: AccountSection): string {
    const baseHref = accountHref.split("#")[0] ?? accountHref;

    return `${baseHref}#${section}`;
}

function normalizedSearchTerm(value: string): string {
    return value.trim().replace(/\s+/g, " ");
}

function readRecentSearches(view: Window | null): string[] {
    if (view === null) {
        return [];
    }

    try {
        const value = view.localStorage.getItem(RECENT_SEARCHES_STORAGE_KEY);
        const parsed = value ? JSON.parse(value) as unknown : [];

        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed.filter((item): item is string => typeof item === "string" && normalizedSearchTerm(item) !== "").slice(0, RECENT_SEARCH_LIMIT);
    } catch {
        return [];
    }
}

function writeRecentSearches(view: Window | null, searches: string[]): void {
    if (view === null) {
        return;
    }

    try {
        view.localStorage.setItem(RECENT_SEARCHES_STORAGE_KEY, JSON.stringify(searches.slice(0, RECENT_SEARCH_LIMIT)));
    } catch {
        // Search still works when persistent storage is blocked.
    }
}

function addRecentSearchTerm(term: string, locale: string, view: Window | null): string[] {
    const normalized = normalizedSearchTerm(term);

    if (normalized.length < 2) {
        return readRecentSearches(view);
    }

    const next = [
        normalized,
        ...readRecentSearches(view).filter((item) => item.toLocaleLowerCase(locale) !== normalized.toLocaleLowerCase(locale)),
    ].slice(0, RECENT_SEARCH_LIMIT);

    writeRecentSearches(view, next);

    return next;
}

function ShopLogo({
    href,
    siteName,
    brandContent,
    hasAuthoredLabel,
    hasAuthoredMark = false,
    alignment = "left",
    display = "markText",
    orientation = "horizontal",
    stretch = false,
}: {
    href: string;
    siteName: string;
    brandContent?: ReactNode;
    hasAuthoredLabel?: boolean;
    hasAuthoredMark?: boolean;
    alignment?: "left" | "center" | "right";
    display?: "markText" | "mark" | "text";
    orientation?: "horizontal" | "vertical";
    stretch?: boolean;
}) {
    const message = useShopStorefrontMessage();
    const brand = siteName.trim() !== "" ? siteName.trim() : "Shop";
    const authoredLabelVisible = hasAuthoredLabel ?? (brandContent !== undefined);

    return (
        <ShopStorefrontLink
            href={href}
            className={classNames(
                "flex min-w-0 gap-1.5 sm:shrink-0 sm:gap-2",
                stretch && "w-full",
                orientation === "vertical"
                    ? alignment === "center"
                        ? "flex-col items-center text-center"
                        : alignment === "right"
                            ? "flex-col items-end text-right"
                            : "flex-col items-start text-left"
                    : classNames(
                        "items-center",
                        alignment === "center" ? "justify-center text-center" : alignment === "right" ? "justify-end text-right" : "justify-start text-left",
                    ),
            )}
            aria-label={message("ui.header.home", { brand })}
        >
            {display !== "text" && !hasAuthoredMark ? (
                <span
                    className="relative grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full bg-[var(--site-primary)] text-[var(--site-on-primary)] sm:h-12 sm:w-12"
                    data-shop-logo-fallback-mark="true"
                >
                    <span className="text-base font-black sm:text-lg">S</span>
                    <span className="absolute bottom-0 right-0 h-3 w-5 rotate-[-20deg] bg-[var(--site-accent)]" />
                </span>
            ) : null}
            {brandContent}
            {display !== "mark" && !authoredLabelVisible ? (
                <span className="min-w-0 max-w-[68px] truncate text-base font-black uppercase text-[var(--site-primary)] sm:max-w-[230px] sm:text-2xl">
                    {brand}
                </span>
            ) : null}
        </ShopStorefrontLink>
    );
}

function publicPageLinks(
    pages: PageLink[],
    storefrontHref: (href: string) => string,
): Array<{ href: string; title: string }> {
    const uniqueLinks = new Map<string, { href: string; title: string }>();

    for (const page of pages) {
        if (page.title.trim() === "") {
            continue;
        }

        const href = searchBlockPageHref(page, storefrontHref);

        if (href === "#" || uniqueLinks.has(href)) {
            continue;
        }

        uniqueLinks.set(href, { href, title: page.title });
    }

    return Array.from(uniqueLinks.values());
}

function MegaMenuLink({ link }: { link: PublicShopMenuItem }) {
    return (
        <ShopStorefrontLink
            href={functionalStorefrontHref(menuItemHref(link))}
            className={classNames(
                "group/menu-link flex min-h-12 w-full max-w-[270px] items-center gap-2.5 rounded-xl px-2 py-1.5 transition hover:bg-[var(--site-surface)] hover:shadow-sm focus-visible:bg-[var(--site-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25",
                isSaleMenuItem(link) && "text-[var(--site-primary)]",
            )}
            {...linkTargetProps(link)}
        >
            <span aria-hidden="true" className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--site-primary-soft)] text-xs font-black uppercase text-[var(--site-primary)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_10%,transparent)]">
                {link.label.trim().slice(0, 2)}
            </span>
            <span className="min-w-0 flex-1">
                <span className={classNames(
                    "block truncate text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_75%,transparent)] transition group-hover/menu-link:text-[var(--site-primary)]",
                    isSaleMenuItem(link) && "text-[var(--site-primary)] group-hover/menu-link:text-[var(--site-primary)]",
                )}>
                    {link.label}
                </span>
            </span>
        </ShopStorefrontLink>
    );
}

function MegaMenu({ item }: { item: PublicShopMenuItem }) {
    const message = useShopStorefrontMessage();
    const categoryLinks = item.children;
    const columns = item.type === "storeList"
        ? megaMenuColumns(item, message("ui.header.recommended"))
        : categoryLinks.length > 0
            ? [{ key: `${item.uuid}:categories`, links: categoryLinks }]
            : megaMenuColumns(item, message("ui.header.recommended"));

    if (columns.length === 0) {
        return null;
    }

    const visibleColumns = columns.slice(0, 4);
    const hasSplitLinks = visibleColumns.some((column) => column.links.length > 6);
    const gridClass = visibleColumns.length === 1
        ? hasSplitLinks ? "grid-cols-[minmax(520px,620px)] justify-start" : "grid-cols-[minmax(260px,340px)] justify-start"
        : visibleColumns.length === 2
            ? "grid-cols-2"
            : visibleColumns.length === 3
                ? "grid-cols-3"
                : "grid-cols-4";

    return (
        <div className="invisible absolute left-0 right-0 top-full z-30 bg-transparent px-6 pt-2 opacity-0 transition duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
            <div className={classNames("mx-auto grid max-w-[var(--site-container)] gap-5 overflow-hidden rounded-[28px] border border-[color-mix(in_srgb,var(--site-text)_5%,transparent)] bg-[color-mix(in_srgb,var(--site-surface)_95%,transparent)] px-6 py-8 text-sm font-normal shadow-[0_22px_70px_var(--site-shadow-color)] backdrop-blur", gridClass)}>
                {visibleColumns.map(({ key, title, titleHref, links }, index) => {
                    const linkGroups = megaMenuLinkGroups(links, 6);

                    return (
                        <div key={key} className="relative min-w-0 px-5">
                            {index > 0 ? (
                                <span className="absolute left-0 top-1/2 h-[72%] w-px -translate-y-1/2 bg-[color-mix(in_srgb,var(--site-text)_5%,transparent)]" aria-hidden="true" />
                            ) : null}
                            {title ? (
                                titleHref ? (
                                    <ShopStorefrontLink href={titleHref} className={classNames("block w-fit text-sm font-black uppercase tracking-normal hover:text-[var(--site-primary)]", animatedUnderlineClass)}>
                                        {title}
                                    </ShopStorefrontLink>
                                ) : (
                                    <h3 className="text-sm font-black uppercase">{title}</h3>
                                )
                            ) : null}
                            <div className={classNames(
                                title ? "mt-5" : "mt-0",
                                linkGroups.length > 1 ? "grid grid-cols-2 gap-x-6 gap-y-2" : "space-y-2",
                                "text-[color-mix(in_srgb,var(--site-text)_65%,transparent)]",
                            )}>
                                {linkGroups.map((group, groupIndex) => (
                                    <div key={`${key}-group-${groupIndex}`} className="space-y-2">
                                        {group.map((link) => (
                                            <MegaMenuLink key={link.uuid} link={link} />
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
            {columns.length > 4 ? (
                <div className="border-t border-[color-mix(in_srgb,var(--site-text)_5%,transparent)] bg-[var(--site-surface)]">
                    <div className="mx-auto flex max-w-[var(--site-container)] flex-wrap gap-x-7 gap-y-2 px-6 py-4 text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">
                        {columns.slice(4, 10).map((column) => (
                            column.titleHref ? (
                                <ShopStorefrontLink key={column.key} href={column.titleHref} className={classNames("hover:text-[var(--site-primary)]", animatedUnderlineClass)}>
                                    {column.title}
                                </ShopStorefrontLink>
                            ) : (
                                <span key={column.key}>{column.title}</span>
                            )
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function LagunaSharedNavigation({
    activeSection,
    className,
    headerMenuItems,
    mode,
    variant,
}: {
    activeSection: PublicShopMenuItem | null;
    className?: string;
    headerMenuItems: PublicShopMenuItem[];
    mode: "desktop" | "mobile";
    variant: LagunaNavigationVariant;
}) {
    const navigationRuntime = useContext(LagunaNavigationRuntimeContext);
    const editorMode = useContext(ShopEditorModeContext);
    const message = useShopStorefrontMessage();
    const storefrontHref = useShopStorefrontHref();
    const settings = navigationRuntime?.settings ?? {
        ...DEFAULT_SITE_NAVIGATION_SETTINGS,
        variant,
    };
    const renderLink: SiteNavigationRenderLink = useCallback(({
        ariaCurrent,
        children,
        className: linkClassName,
        href,
        id,
        item,
        onClick,
    }) => {
        const resolvedHref = functionalStorefrontHref(storefrontHref(href));

        if (!resolvedHref) {
            return (
                <span
                    aria-current={ariaCurrent}
                    className={linkClassName}
                    data-site-navigation-item-uuid={item.uuid}
                    data-site-navigation-link-owner="true"
                    data-site-navigation-label="true"
                    id={id}
                >
                    {children}
                </span>
            );
        }

        return (
            <ShopStorefrontLink
                aria-current={ariaCurrent}
                className={linkClassName}
                data-site-navigation-item-uuid={item.uuid}
                data-site-navigation-link-owner="true"
                href={resolvedHref}
                id={id}
                onClick={(event) => {
                    if (editorMode) event.preventDefault();
                    onClick?.();
                }}
                {...linkTargetProps(item)}
            >
                {children}
            </ShopStorefrontLink>
        );
    }, [editorMode, storefrontHref]);

    return (
        <SiteNavigation
            activeItemUuid={activeSection?.uuid}
            className={className}
            items={headerMenuItems}
            labels={{
                closeSubmenu: message("ui.header.closeMenu"),
                navigation: message("ui.header.mainNavigation"),
                openSubmenu: message("ui.header.openMenu"),
                recommended: message("ui.header.recommended"),
                showAll: message("block.systemSubcategoryList.moreButtonLabel"),
            }}
            includeStyles={mode === "desktop"}
            mode={mode}
            renderLink={renderLink}
            settings={settings}
            style={navigationRuntime?.style}
        />
    );
}

function LagunaDesktopNavigation({
    activeSection,
    headerMenuItems,
    variant,
}: {
    activeSection: PublicShopMenuItem | null;
    headerMenuItems: PublicShopMenuItem[];
    variant: LagunaNavigationVariant;
}) {
    return (
        <LagunaSharedNavigation
            activeSection={activeSection}
            className="hidden lg:block"
            headerMenuItems={headerMenuItems}
            mode="desktop"
            variant={variant}
        />
    );
}

function BenefitStrip({ block, compact = false }: { block?: ShopTemplateBlock; compact?: boolean }) {
    const editorMode = useContext(ShopEditorModeContext);
    const benefits: Array<[IconName, string]> = [
        ["truck", "Doprava zdarma nad 699 Kc"],
        ["store", "Vyzvednuti zdarma"],
        ["rotate", "Vraceni v prodejne zdarma"],
        ["phone", "Mobilni aplikace"],
    ];
    const composedItems = slotChildren(block, "items");

    if (!editorMode && composedItems.length === 0) {
        return null;
    }

    return (
        <div className={compact ? "bg-transparent" : "border-b border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] bg-[var(--site-surface)]"}>
            <div className={compact ? "px-3 py-2" : "mx-auto max-w-[var(--site-container)] px-2 py-3 lg:px-6"}>
                {composedItems.length > 0 ? (
                    <SlotBlockGroup
                        block={block}
                        slotKey="items"
                        className={compact ? "grid grid-cols-2 gap-2 lg:grid-cols-4" : "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"}
                    />
                ) : (
                    <div className={compact ? "grid grid-cols-4 divide-x divide-[color-mix(in_srgb,var(--site-text)_10%,transparent)] text-center text-[10px] font-semibold" : "grid grid-cols-3 divide-x divide-[color-mix(in_srgb,var(--site-text)_10%,transparent)] text-center text-[10px] font-semibold sm:grid-cols-4 sm:text-xs"}>
                        {benefits.map(([icon, label], index) => (
                            <div key={label} className={classNames("items-center justify-center gap-2 px-2", !compact && index === 3 ? "hidden sm:flex" : "flex")}>
                                <Icon name={icon} className="h-4 w-4 text-[var(--site-primary)]" />
                                <span>{label}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function lagunaMobileNavigationShellClass(variant: LagunaNavigationVariant): string {
    switch (variant) {
        case "lagunaClassic": return "bg-[var(--site-surface)] text-[var(--site-text)]";
        case "departmentMega": return "bg-[var(--site-surface)] text-[var(--site-text)]";
        case "audienceMega": return "bg-[var(--site-background)] text-[var(--site-text)]";
        case "editorialOverlay": return "bg-[var(--site-inverse)] text-[var(--site-on-inverse)]";
        case "roomPlanner": return "bg-[linear-gradient(145deg,var(--site-surface),var(--site-surface-elevated))] text-[var(--site-text)]";
        case "routineMega": return "bg-[linear-gradient(145deg,var(--site-primary-soft),var(--site-surface))] text-[var(--site-text)]";
        case "collectionConcierge": return "bg-[var(--site-background)] text-[var(--site-text)]";
        case "groceryHub": return "bg-[var(--site-accent-soft)] text-[var(--site-text)]";
        case "productStory": return "bg-[var(--site-background)] text-[var(--site-text)]";
        case "categoryPills": return "bg-[var(--site-surface)] text-[var(--site-text)]";
        case "technicalIndex": return "bg-[var(--site-inverse)] font-mono text-[var(--site-on-inverse)]";
        case "borderedDropdown": return "bg-[var(--site-background)] text-[var(--site-text)]";
        case "imageCategoryStrip": return "bg-[var(--site-background)] text-[var(--site-text)]";
        case "monochromeDropdown": return "bg-[var(--site-inverse)] text-[var(--site-on-inverse)]";
        case "playfulFrame": return "bg-[var(--site-background)] text-[var(--site-text)]";
    }
}

function MobileMenu({
    activeSection,
    allowDesktop = false,
    id,
    open,
    onClose,
    onOpenSearch,
    homeHref,
    siteName,
    items,
    showSearch = false,
    variant,
}: {
    activeSection: PublicShopMenuItem | null;
    allowDesktop?: boolean;
    id: string;
    open: boolean;
    onClose: () => void;
    onOpenSearch?: () => void;
    homeHref: string;
    siteName: string;
    items: PublicShopMenuItem[];
    showSearch?: boolean;
    variant: LagunaNavigationVariant;
}) {
    const message = useShopStorefrontMessage();
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);
    const openerRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const dialog = dialogRef.current;
        const ownerDocument = dialog?.ownerDocument;
        const view = ownerDocument?.defaultView;

        if (!dialog || !ownerDocument || !view) {
            return undefined;
        }

        const previousOverflow = ownerDocument.body.style.overflow;
        openerRef.current = ownerDocument.activeElement instanceof view.HTMLElement
            ? ownerDocument.activeElement as HTMLElement
            : null;
        const desktopMediaQuery = view.matchMedia("(min-width: 1024px)");

        if (desktopMediaQuery.matches && !allowDesktop) {
            onClose();

            return undefined;
        }

        const focusFrame = view.requestAnimationFrame(() => closeButtonRef.current?.focus());
        const handleBreakpointChange = (event: MediaQueryListEvent) => {
            if (event.matches && !allowDesktop) {
                onClose();
            }
        };
        const handleKeyDown = (event: globalThis.KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                onClose();

                return;
            }

            if (event.key !== "Tab") {
                return;
            }

            const focusableElements = navigationFocusableElements(dialog);

            if (focusableElements.length === 0) {
                event.preventDefault();

                return;
            }

            const firstFocusableElement = focusableElements[0];
            const lastFocusableElement = focusableElements[focusableElements.length - 1];
            const focusIsInsideDialog = dialog.contains(ownerDocument.activeElement);

            if (event.shiftKey && (ownerDocument.activeElement === firstFocusableElement || !focusIsInsideDialog)) {
                event.preventDefault();
                lastFocusableElement.focus();
            } else if (!event.shiftKey && (ownerDocument.activeElement === lastFocusableElement || !focusIsInsideDialog)) {
                event.preventDefault();
                firstFocusableElement.focus();
            }
        };

        ownerDocument.body.style.overflow = "hidden";
        desktopMediaQuery.addEventListener("change", handleBreakpointChange);
        ownerDocument.addEventListener("keydown", handleKeyDown);

        return () => {
            view.cancelAnimationFrame(focusFrame);
            ownerDocument.body.style.overflow = previousOverflow;
            desktopMediaQuery.removeEventListener("change", handleBreakpointChange);
            ownerDocument.removeEventListener("keydown", handleKeyDown);
            openerRef.current?.focus();
            openerRef.current = null;
        };
    }, [allowDesktop, onClose, open]);

    if (!open) {
        return null;
    }

    return (
        <div
            ref={dialogRef}
            id={id}
            className={classNames(
                "fixed inset-0 z-[90] flex flex-col overflow-hidden",
                !allowDesktop && "lg:hidden",
                lagunaMobileNavigationShellClass(variant),
            )}
            data-navigation-desktop-dialog={allowDesktop ? "true" : undefined}
            data-navigation-layout={variant}
            data-shop-navigation-variant={variant}
            role="dialog"
            aria-labelledby={`${id}-title`}
            aria-modal="true"
        >
            <div className="flex items-center justify-between border-b border-current/10 p-4">
                <ShopStorefrontLink id={`${id}-title`} href={homeHref} className="min-w-0 truncate text-xl font-black uppercase text-[var(--site-primary)]" onClick={onClose}>
                    {siteName}
                </ShopStorefrontLink>
                <button ref={closeButtonRef} type="button" className="grid h-11 w-11 shrink-0 place-items-center rounded-full hover:bg-current/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-focus-ring)]" aria-label={message("ui.header.closeMenu")} onClick={onClose}>
                    <Icon name="x" className="h-6 w-6" />
                </button>
            </div>
            {showSearch && onOpenSearch ? (
                <div className="border-b border-current/10 px-4 py-3">
                    <button
                        type="button"
                        className="flex min-h-12 w-full items-center justify-between gap-4 rounded-2xl border border-current/15 bg-[var(--site-surface)] px-4 text-sm font-bold text-[var(--site-text)] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-focus-ring)]"
                        onClick={() => {
                            onClose();
                            onOpenSearch();
                        }}
                    >
                        <span className="truncate opacity-65">{message("block.headerSearch.placeholder")}</span>
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[var(--site-primary)] text-[var(--site-on-primary)]">
                            <Icon name="search" className="h-4 w-4" />
                        </span>
                    </button>
                </div>
            ) : null}
            <div
                className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-10 pt-4"
                tabIndex={0}
            >
                <LagunaSharedNavigation
                    activeSection={activeSection}
                    headerMenuItems={items}
                    mode="mobile"
                    variant={variant}
                />
            </div>
        </div>
    );
}

function SearchPanel({
    open,
    onClose,
    searchHref,
    shopSearchApiPath,
    languageCode,
    currencyCode,
    recommendedProducts,
}: {
    open: boolean;
    onClose: () => void;
    searchHref: string;
    shopSearchApiPath?: string;
    languageCode?: string | null;
    currencyCode?: string | null;
    recommendedProducts: FavoriteProductPreview[];
}) {
    const message = useShopStorefrontMessage();
    const { formatMoney, locale } = useShopStorefrontFormatting();
    const storefrontHref = useShopStorefrontHref();
    const inputRef = useRef<HTMLInputElement>(null);
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState<SearchSuggestions | null>(null);
    const [loading, setLoading] = useState(false);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
    const [recentSearches, setRecentSearches] = useState<string[]>([]);
    const normalizedQuery = query.trim();
    const popularSearches = [
        message("ui.search.popularSneakers"),
        message("ui.search.popularSandals"),
        message("ui.search.popularKidsShoes"),
        message("ui.search.popularBags"),
    ];
    const searchChips = recentSearches.length > 0
        ? recentSearches
        : popularSearches;
    const hasSuggestions = suggestions !== null
        && (suggestions.products.length > 0 || suggestions.categories.length > 0 || suggestions.manufacturers.length > 0);
    const suggestionLinks = useMemo(
        () => searchSuggestionLinks(suggestions, searchHref, normalizedQuery, storefrontHref),
        [normalizedQuery, searchHref, storefrontHref, suggestions],
    );
    const activeSuggestion = activeSuggestionIndex >= 0 ? suggestionLinks[activeSuggestionIndex] : undefined;

    useEffect(() => {
        if (open) {
            const view = inputRef.current?.ownerDocument.defaultView;

            if (!view) {
                return undefined;
            }

            const timeout = view.setTimeout(() => inputRef.current?.focus(), 50);
            setRecentSearches(readRecentSearches(view));

            return () => view.clearTimeout(timeout);
        }

        return undefined;
    }, [open]);

    useEffect(() => {
        if (!open || !shopSearchApiPath || normalizedQuery.length < 2) {
            setSuggestions(null);
            setLoading(false);
            setActiveSuggestionIndex(-1);

            return undefined;
        }

        const abortController = new AbortController();
        const timeout = window.setTimeout(async () => {
            const params = new URLSearchParams({
                q: normalizedQuery,
                limit: "6",
            });

            if (languageCode) {
                params.set("language", languageCode);
            }

            if (currencyCode) {
                params.set(CURRENCY_QUERY_PARAM, currencyCode);
            }

            setLoading(true);

            try {
                const response = await fetch(`${shopSearchApiPath}?${params.toString()}`, {
                    credentials: "same-origin",
                    cache: "no-store",
                    signal: abortController.signal,
                });

                if (!response.ok) {
                    setSuggestions(null);

                    return;
                }

                const payload = await response.json() as unknown;

                if (!abortController.signal.aborted) {
                    setSuggestions(searchSuggestionsFromValue(payload, languageCode));
                    setActiveSuggestionIndex(-1);
                }
            } catch {
                if (!abortController.signal.aborted) {
                    setSuggestions(null);
                }
            } finally {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            }
        }, 220);

        return () => {
            window.clearTimeout(timeout);
            abortController.abort();
        };
    }, [currencyCode, languageCode, normalizedQuery, open, shopSearchApiPath]);

    useEffect(() => {
        if (!open) {
            setQuery("");
            setSuggestions(null);
            setLoading(false);
            setActiveSuggestionIndex(-1);
        }
    }, [open]);

    useEffect(() => {
        if (activeSuggestionIndex >= suggestionLinks.length) {
            setActiveSuggestionIndex(-1);
        }
    }, [activeSuggestionIndex, suggestionLinks.length]);

    function handleSearchKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
        if (event.key === "Escape") {
            event.preventDefault();
            onClose();

            return;
        }

        if (suggestionLinks.length === 0 || normalizedQuery.length < 2) {
            return;
        }

        if (event.key === "ArrowDown") {
            event.preventDefault();
            setActiveSuggestionIndex((current) => (current + 1) % suggestionLinks.length);

            return;
        }

        if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveSuggestionIndex((current) => (current <= 0 ? suggestionLinks.length - 1 : current - 1));

            return;
        }

        if (event.key === "Enter" && activeSuggestion) {
            event.preventDefault();
            const view = event.currentTarget.ownerDocument.defaultView;
            setRecentSearches(addRecentSearchTerm(normalizedQuery, locale, view));
            view?.location.assign(activeSuggestion.href);
            onClose();
        }
    }

    function searchTermHref(term: string): string {
        return `${searchHref}?q=${encodeURIComponent(term)}`;
    }

    function rememberCurrentSearch(): void {
        const view = inputRef.current?.ownerDocument.defaultView ?? null;
        setRecentSearches(addRecentSearchTerm(normalizedQuery, locale, view));
    }

    return (
        <div
            className={classNames(
                "fixed inset-x-0 top-0 z-[60] bg-[var(--site-surface)] shadow-[0_6px_24px_var(--site-shadow-color)] transition duration-200",
                open ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-full opacity-0",
            )}
        >
            <div className="mx-auto max-w-4xl px-5 py-7">
                <form action={searchHref} method="get" className="relative flex items-start gap-3" onSubmit={rememberCurrentSearch}>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center border-b-2 border-[var(--site-primary)]">
                            <Icon name="search" className="h-5 w-5" />
                            <input
                                ref={inputRef}
                                name="q"
                                type="search"
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                placeholder={message("ui.search.placeholder")}
                                className="w-full px-4 py-3 text-base outline-none"
                                autoComplete="off"
                            />
                        </div>
                        {normalizedQuery.length >= 2 ? (
                            <div className="absolute left-0 right-14 top-full z-[62] mt-4 border border-[var(--site-border)] bg-[var(--site-surface)] shadow-[0_18px_44px_var(--site-shadow-color)]">
                                {loading ? (
                                    <p className="p-5 text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">{message("ui.search.searching")}</p>
                                ) : hasSuggestions && suggestions ? (
                                    <div className="grid gap-0 md:grid-cols-[minmax(0,1fr)_260px]">
                                        <div className="p-4">
                                            <div className="flex items-center justify-between gap-3">
                                                <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.search.products")}</p>
                                                <a
                                                    href={`${searchHref}?q=${encodeURIComponent(normalizedQuery)}`}
                                                    className={classNames(
                                                        "text-xs font-black text-[var(--site-primary)]",
                                                        activeSuggestion?.id === "search:all" && "underline",
                                                    )}
                                                    onMouseEnter={() => setActiveSuggestionIndex(suggestionLinks.findIndex((item) => item.id === "search:all"))}
                                                    onClick={() => {
                                                        rememberCurrentSearch();
                                                        onClose();
                                                    }}
                                                >
                                                    {message("ui.search.allResults", { count: suggestions.productTotal })}
                                                </a>
                                            </div>
                                            <div className="mt-3 divide-y divide-[var(--site-border)]">
                                                {suggestions.products.map((product) => {
                                                    const suggestionId = `product:${product.uuid}`;
                                                    const active = activeSuggestion?.id === suggestionId;

                                                    return (
                                                        <a
                                                            key={product.uuid}
                                                            href={product.slug ? storefrontHref(`/${product.slug}`) : `${searchHref}?q=${encodeURIComponent(product.name)}`}
                                                            className={classNames(
                                                                "flex items-center justify-between gap-4 px-2 py-3 hover:bg-[var(--site-surface)] hover:text-[var(--site-primary)]",
                                                                active && "bg-[var(--site-surface)] text-[var(--site-primary)]",
                                                            )}
                                                            onMouseEnter={() => setActiveSuggestionIndex(suggestionLinks.findIndex((item) => item.id === suggestionId))}
                                                            onClick={() => {
                                                                rememberCurrentSearch();
                                                                onClose();
                                                            }}
                                                        >
                                                            <span className="min-w-0">
                                                                <span className="block truncate text-sm font-black">{product.name}</span>
                                                                <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">
                                                                    {product.manufacturerName ? <span>{product.manufacturerName}</span> : null}
                                                                    {product.reference ? <span>{product.reference}</span> : null}
                                                                    {product.isOnSale ? <span className="font-black text-[var(--site-primary)]">{message("ui.product.sale")}</span> : null}
                                                                    <span>{product.availableForOrder ? message("ui.product.inStock") : message("ui.product.unavailable")}</span>
                                                                </span>
                                                            </span>
                                                            <span className="shrink-0 text-sm font-black text-[var(--site-primary)]">{formatMoney(product.price)}</span>
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div className="border-t border-[var(--site-border)] bg-[var(--site-surface)] p-4 md:border-l md:border-t-0">
                                            {suggestions.categories.length > 0 ? (
                                                <div>
                                                    <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.search.categories")}</p>
                                                    <div className="mt-3 space-y-2">
                                                        {suggestions.categories.slice(0, 5).map((category) => {
                                                            const suggestionId = `category:${category.uuid}`;
                                                            const active = activeSuggestion?.id === suggestionId;

                                                            return (
                                                                <a
                                                                    key={category.uuid}
                                                                    href={category.slug ? storefrontHref(`/${category.slug}`) : `${searchHref}?q=${encodeURIComponent(category.name)}`}
                                                                    className={classNames(
                                                                        "block px-2 py-1.5 text-sm font-bold hover:bg-[var(--site-surface)] hover:text-[var(--site-primary)]",
                                                                        active && "bg-[var(--site-surface)] text-[var(--site-primary)]",
                                                                    )}
                                                                    onMouseEnter={() => setActiveSuggestionIndex(suggestionLinks.findIndex((item) => item.id === suggestionId))}
                                                                    onClick={() => {
                                                                        rememberCurrentSearch();
                                                                        onClose();
                                                                    }}
                                                                >
                                                                    {category.name}
                                                                    {category.pathNames.length > 1 ? (
                                                                        <span className="mt-0.5 block truncate text-xs font-medium text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">
                                                                            {category.pathNames.join(" / ")}
                                                                        </span>
                                                                    ) : null}
                                                                </a>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ) : null}
                                            {suggestions.manufacturers.length > 0 ? (
                                                <div className={classNames(suggestions.categories.length > 0 && "mt-5")}>
                                                    <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.search.brands")}</p>
                                                    <div className="mt-3 flex flex-wrap gap-2">
                                                        {suggestions.manufacturers.map((manufacturer) => {
                                                            const suggestionId = `manufacturer:${manufacturer.uuid}`;
                                                            const active = activeSuggestion?.id === suggestionId;

                                                            return (
                                                                <a
                                                                    key={manufacturer.uuid}
                                                                    href={manufacturerSearchHref(searchHref, manufacturer.uuid)}
                                                                    className={classNames(
                                                                        "rounded-full border bg-[var(--site-surface)] px-3 py-1.5 text-xs font-black hover:border-[var(--site-primary)] hover:text-[var(--site-primary)]",
                                                                        active ? "border-[var(--site-primary)] text-[var(--site-primary)]" : "border-[var(--site-border)]",
                                                                    )}
                                                                    onMouseEnter={() => setActiveSuggestionIndex(suggestionLinks.findIndex((item) => item.id === suggestionId))}
                                                                    onClick={() => {
                                                                        rememberCurrentSearch();
                                                                        onClose();
                                                                    }}
                                                                >
                                                                    {manufacturer.name}
                                                                </a>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="p-5">
                                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                            <div>
                                                <p className="text-sm font-black">{message("ui.search.nothingFound")}</p>
                                                <p className="mt-1 text-sm text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">{message("ui.search.tryBroader")}</p>
                                            </div>
                                            <a
                                                href={searchTermHref(normalizedQuery)}
                                                className="inline-flex w-fit items-center rounded-full bg-[var(--site-primary)] px-4 py-2 text-xs font-black text-[var(--site-on-primary)]"
                                                onClick={rememberCurrentSearch}
                                            >
                                                {message("ui.search.openResults")}
                                            </a>
                                        </div>
                                        {recommendedProducts.length > 0 ? (
                                            <div className="mt-5">
                                                <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.search.recommendedInstead")}</p>
                                                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                                                    {recommendedProducts.slice(0, 3).map((product) => (
                                                        <a
                                                            key={product.id}
                                                            href={functionalStorefrontHref(product.href)}
                                                            className="rounded-2xl bg-[var(--site-surface)] p-3 text-sm transition hover:bg-[var(--site-surface)] hover:text-[var(--site-primary)] hover:shadow-sm"
                                                            onClick={onClose}
                                                        >
                                                            <span className="block truncate font-black">{product.name}</span>
                                                            {product.price ? <span className="mt-1 block text-xs font-bold text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">{product.price}</span> : null}
                                                        </a>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>
                    <button type="button" className="grid h-11 w-11 place-items-center" aria-label={message("ui.search.close")} onClick={onClose}>
                        <Icon name="x" className="h-6 w-6" />
                    </button>
                </form>
                <div className="mt-6">
                    <p className="text-xs font-bold uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{recentSearches.length > 0 ? message("ui.search.recent") : message("ui.search.popular")}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {searchChips.map((term) => (
                            <a key={term} href={searchTermHref(term)} className="rounded-full bg-[var(--site-surface)] px-4 py-2 text-sm">
                                {term}
                            </a>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function CartDrawer({
    open,
    onClose,
    cart,
    loading,
    error,
    cartHref,
    onRemoveItem,
}: {
    open: boolean;
    onClose: () => void;
    cart: PublicCart;
    loading: boolean;
    error: string | null;
    cartHref: string;
    onRemoveItem: (itemUuid: string) => void;
}) {
    const message = useShopStorefrontMessage();
    const { formatMoney } = useShopStorefrontFormatting();
    const storefrontHref = useShopStorefrontHref();

    return (
        <aside
            className={classNames(
                "fixed bottom-0 right-0 top-0 z-[70] w-full max-w-md overflow-hidden rounded-l-[32px] bg-[var(--site-background)] shadow-[0_0_70px_var(--site-shadow-color)] transition duration-200",
                open ? "translate-x-0" : "translate-x-full",
            )}
            aria-hidden={!open}
        >
            <div className="flex items-center justify-between bg-[color-mix(in_srgb,var(--site-surface)_90%,transparent)] p-5 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                <div>
                    <p className={classNames("text-xs font-black uppercase", readableOnSurfaceTextClass)}>{message("ui.cart.purchase")}</p>
                    <h2 className="mt-1 text-xl font-black">
                        {message("ui.cart.yourCart")} <span className="text-[color-mix(in_srgb,var(--site-text)_35%,transparent)]">({cart.totalQuantity})</span>
                    </h2>
                </div>
                <button type="button" className={headerIconActionClass} aria-label={message("ui.cart.close")} onClick={onClose}>
                    <Icon name="x" className="h-5 w-5" />
                </button>
            </div>
            <div className="h-[calc(100%_-_210px)] overflow-y-auto p-5">
                {error ? (
                    <p className="mb-4 rounded-2xl border border-[var(--site-primary)] bg-[var(--site-primary-soft)] px-4 py-3 text-sm font-bold text-[var(--site-primary)]">
                        {error}
                    </p>
                ) : null}
                {loading ? (
                    <div className="rounded-3xl bg-[var(--site-surface)] p-5 text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_50%,transparent)] shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                        {message("ui.cart.loading")}
                    </div>
                ) : null}
                {!loading && cart.items.length === 0 ? (
                    <div className="grid h-full place-items-center text-center">
                        <div className="rounded-[28px] bg-[var(--site-surface)] p-8 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                            <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[var(--site-surface)] text-[var(--site-primary)]">
                                <Icon name="bag" className="h-7 w-7" />
                            </span>
                            <p className="mt-5 text-base font-black text-[var(--site-text)]">{message("ui.cart.empty")}</p>
                            <p className={classNames("mt-2 max-w-56 text-sm", readableOnSurfaceTextClass)}>{message("ui.cart.addProducts")}</p>
                        </div>
                    </div>
                ) : null}
                <div className="space-y-3">
                    {cart.items.map((item) => {
                        const notice = cartItemNoticeText(item, message);
                        const productHref = item.slug ? storefrontHref(`/${item.slug}`) : null;

                        return (
                            <div key={item.uuid} className="flex gap-4 rounded-[24px] bg-[var(--site-surface)] p-3 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                                {productHref ? (
                                    <a href={productHref} className="grid h-24 w-20 shrink-0 place-items-center rounded-2xl bg-[var(--site-surface)] text-xs font-bold uppercase text-[color-mix(in_srgb,var(--site-text)_35%,transparent)] transition hover:text-[var(--site-primary)] hover:ring-2 hover:ring-[var(--site-primary)]/20">
                                        {item.name.slice(0, 2)}
                                    </a>
                                ) : (
                                    <div className="grid h-24 w-20 shrink-0 place-items-center rounded-2xl bg-[var(--site-surface)] text-xs font-bold uppercase text-[color-mix(in_srgb,var(--site-text)_35%,transparent)]">
                                        {item.name.slice(0, 2)}
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    {productHref ? (
                                        <a href={productHref} className="block truncate text-sm font-bold hover:text-[var(--site-primary)]">
                                            {item.name}
                                        </a>
                                    ) : (
                                        <p className="truncate text-sm font-bold">{item.name}</p>
                                    )}
                                    {item.variantName ? <p className="mt-1 text-xs text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">{item.variantName}</p> : null}
                                    <CartItemSurchargesSummary item={item} />
                                    <ManufacturingSpecificationSummary appearance="laguna" message={message} specification={item.manufacturingSpecification}/>
                                    <p className={classNames("mt-2 text-xs", item.validForCheckout ? "text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]" : "text-[var(--site-primary)]")}>
                                        {cartItemStockText(item, message)}
                                    </p>
                                    {notice ? <p className="mt-2 text-xs font-bold text-[var(--site-accent)]">{notice}</p> : null}
                                    {item.quantity > 1 ? (
                                        <p className="mt-2 text-xs text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">{item.quantity} × {formatMoney(item.unitPrice)}</p>
                                    ) : null}
                                    <p className="mt-3 text-base font-black">{formatMoney(item.totalPrice)}</p>
                                </div>
                                <button type="button" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-[color-mix(in_srgb,var(--site-text)_35%,transparent)] transition hover:bg-[var(--site-primary-soft)] hover:text-[var(--site-primary)]" aria-label={message("ui.common.remove")} onClick={() => onRemoveItem(item.uuid)}>
                                    <Icon name="trash" className="h-4 w-4" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 bg-[color-mix(in_srgb,var(--site-surface)_95%,transparent)] p-5 shadow-[0_-18px_44px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                <div className="mb-4 flex items-end justify-between">
                    <span className="text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">{message("ui.cart.total")}</span>
                    <span className="text-2xl font-black">{formatMoney(cart.totalPrice)}</span>
                </div>
                <a href={cartHref} className="block w-full rounded-full bg-[var(--site-primary)] py-4 text-center text-sm font-black text-[var(--site-on-primary)] shadow-lg shadow-[var(--site-primary)]/20 transition hover:-translate-y-0.5 hover:bg-[color-mix(in_srgb,var(--site-primary)_84%,var(--site-text))] hover:shadow-xl">
                    {message("ui.cart.goToCart")}
                </a>
            </div>
        </aside>
    );
}

function CartHoverPreview({
    cart,
    loading,
    error,
    cartHref,
}: {
    cart: PublicCart;
    loading: boolean;
    error: string | null;
    cartHref: string;
}) {
    const message = useShopStorefrontMessage();
    const { formatMoney, locale } = useShopStorefrontFormatting();
    const storefrontHref = useShopStorefrontHref();
    const visibleItems = cart.items.slice(0, 3);
    const hiddenItemCount = Math.max(0, cart.items.length - visibleItems.length);

    return (
        <div className="pointer-events-none absolute end-0 top-full z-[80] hidden w-[360px] pt-3 opacity-0 transition duration-150 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:opacity-100 lg:block">
            <div className="rounded-[28px] bg-[var(--site-surface)] p-4 shadow-[0_24px_70px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                {error ? (
                    <p className="rounded-2xl bg-[var(--site-primary-soft)] px-3 py-2 text-xs font-bold text-[var(--site-primary)]">
                        {error}
                    </p>
                ) : null}
                {loading ? (
                    <p className="rounded-2xl bg-[var(--site-surface)] px-3 py-3 text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">
                        {message("ui.cart.loading")}
                    </p>
                ) : null}
                {!loading && cart.items.length === 0 ? (
                    <div className="rounded-[22px] bg-[var(--site-surface)] px-4 py-5 text-center">
                        <p className="font-black">{message("ui.cart.empty")}</p>
                        <p className="mt-1 text-xs font-semibold text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">{message("ui.cart.quickPreview")}</p>
                    </div>
                ) : null}
                {visibleItems.length > 0 ? (
                    <div className="grid gap-3">
                        {visibleItems.map((item) => {
                            const productHref = item.slug ? storefrontHref(`/${item.slug}`) : null;
                            const itemTitle = item.quantity > 1 ? `${item.quantity}x ${item.name}` : item.name;

                            return (
                                <div key={item.uuid} className="grid grid-cols-[54px_minmax(0,1fr)_auto] items-center gap-3">
                                    {productHref ? (
                                        <a href={productHref} className="grid h-[54px] w-[54px] place-items-center rounded-2xl bg-[var(--site-surface)] text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_35%,transparent)] hover:text-[var(--site-primary)]">
                                            {item.name.slice(0, 2)}
                                        </a>
                                    ) : (
                                        <span className="grid h-[54px] w-[54px] place-items-center rounded-2xl bg-[var(--site-surface)] text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_35%,transparent)]">
                                            {item.name.slice(0, 2)}
                                        </span>
                                    )}
                                    <div className="min-w-0">
                                        {productHref ? (
                                            <a href={productHref} className="block truncate text-sm font-black hover:text-[var(--site-primary)]">
                                                {itemTitle}
                                            </a>
                                        ) : (
                                            <p className="truncate text-sm font-black">{itemTitle}</p>
                                        )}
                                        {item.variantName ? <p className="truncate text-xs font-semibold text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">{item.variantName}</p> : null}
                                        <CartItemSurchargesSummary item={item} compact />
                                        <ManufacturingSpecificationSummary appearance="laguna" compact message={message} specification={item.manufacturingSpecification}/>
                                    </div>
                                    <strong className="text-sm tabular-nums">{formatMoney(item.totalPrice)}</strong>
                                </div>
                            );
                        })}
                        {hiddenItemCount > 0 ? (
                            <p className="rounded-2xl bg-[var(--site-surface)] px-3 py-2 text-xs font-bold text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">
                                {storefrontPluralMessage(
                                    hiddenItemCount,
                                    locale,
                                    CART_MORE_PRODUCT_MESSAGE_KEYS,
                                    message,
                                )}
                            </p>
                        ) : null}
                    </div>
                ) : null}
                {cart.items.length > 0 ? (
                    <div className="mt-4 border-t border-[var(--site-border)] pt-4">
                        <div className="mb-3 flex items-end justify-between gap-4">
                            <span className="text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">{message("ui.cart.total")}</span>
                            <strong className="text-xl font-black tabular-nums">{formatMoney(cart.itemsTotalPrice)}</strong>
                        </div>
                        <a href={cartHref} className="block rounded-full bg-[var(--site-primary)] px-5 py-3 text-center text-sm font-black text-[var(--site-on-primary)] hover:bg-[color-mix(in_srgb,var(--site-primary)_84%,var(--site-text))]">
                            {message("ui.cart.goToCart")}
                        </a>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function PromoBar({ block }: { block?: ShopTemplateBlock | null }) {
    const message = useShopStorefrontMessage();
    const promoContent = slotChildren(block, SHOP_GRID_SLOT_KEY).find((childBlock) => (
        blockOptionText(childBlock, SHOP_DOCUMENT_ROLE_OPTION) === "promoContent"
    ));
    const promoChildren = slotChildren(promoContent, "content");
    const hasAuthoredPromo = Boolean(
        promoContent
        && promoChildren.some((childBlock) => (
            ["promoMessage", "promoCode"].includes(
                blockOptionText(childBlock, SHOP_DOCUMENT_ROLE_OPTION),
            )
        )),
    );

    return (
        <div
            className="bg-[var(--site-primary)] px-4 py-2 text-center text-[11px] font-bold text-[var(--site-on-primary)] sm:text-xs"
            data-shop-semantic-chrome-grid={hasAuthoredPromo ? "promoBar" : undefined}
            style={mergeStyles(blockStyle(block), interactionStyle(block))}
        >
            {hasAuthoredPromo ? (
                <SlotBlockGroup
                    block={block}
                    slotKey={SHOP_GRID_SLOT_KEY}
                    compactGridChildren
                    className="grid min-h-4 grid-cols-12 items-center"
                    itemClassName="w-full min-w-0"
                    semanticGrid
                    renderItem={({ childBlock, itemPart }) => (
                        <SlotBlockGroup
                            block={childBlock}
                            slotKey="content"
                            partPrefix={itemPart}
                            childSlotParentType="promoBar"
                            className="flex flex-wrap items-center justify-center gap-2"
                            style={blockOptionText(childBlock, "gap") ? { gap: blockOptionText(childBlock, "gap") } : undefined}
                            itemClassName={({ childBlock: promoChild }) => (
                                blockOptionText(promoChild, SHOP_DOCUMENT_ROLE_OPTION) === "promoMessage"
                                    ? "hidden sm:inline-flex"
                                    : "inline-flex items-center"
                            )}
                            renderItem={({ childBlock: promoChild, itemPart: promoItemPart }) => (
                                <>
                                    {blockOptionText(promoChild, SHOP_DOCUMENT_ROLE_OPTION) === "promoCode" ? (
                                        <span className="mr-2 hidden sm:inline">
                                            •&nbsp;&nbsp;{message("ui.promo.code")}:
                                        </span>
                                    ) : null}
                                    <AtomicBlock block={promoChild} partPrefix={promoItemPart} slotParentType="promoBar" />
                                </>
                            )}
                        />
                    )}
                />
            ) : (
                <>
                    <span {...editableTextPart("text")} className="hidden sm:inline" style={partStyle(block, "text")}>
                        {richTextContent(block, "text", message("block.promoBar.text"))}
                    </span>
                    <span className="hidden sm:inline">&nbsp;&nbsp;•&nbsp;&nbsp;{message("ui.promo.code")}: </span>
                    <span {...editableTextPart("code")} className="rounded bg-[var(--site-surface)] px-2 py-0.5 text-[var(--site-primary)]" style={partStyle(block, "code")}>
                        {richTextContent(block, "code", "LETNI25")}
                    </span>
                </>
            )}
        </div>
    );
}

type LegacyHeroSlideFallback = Partial<Pick<
    HeroSlide,
    "eyebrow" | "title" | "description" | "primaryLabel" | "primaryHref" | "secondaryLabel" | "secondaryHref"
>>;

function legacyHeroSlideFallback(slideBlock: ShopTemplateBlock | undefined): LegacyHeroSlideFallback {
    const children = slotChildren(slideBlock, "content");
    const role = (childBlock: ShopTemplateBlock): string => blockOptionText(childBlock, SHOP_DOCUMENT_ROLE_OPTION);
    const byRoleOrType = (expectedRole: string, fallbackType: string): ShopTemplateBlock | undefined => (
        children.find((childBlock) => role(childBlock) === expectedRole)
        ?? children.find((childBlock) => childBlock.type === fallbackType)
    );
    const eyebrow = byRoleOrType("eyebrow", "text");
    const title = byRoleOrType("title", "heading");
    const paragraph = byRoleOrType("paragraph", "paragraph");
    const actions = children.filter((childBlock) => role(childBlock) === "action" || childBlock.type === "button");
    const value = (childBlock: ShopTemplateBlock | undefined, key: string): string | undefined => {
        const text = blockText(childBlock, key, "");

        return text || undefined;
    };

    return {
        eyebrow: value(eyebrow, "text"),
        title: value(title, "text"),
        description: value(paragraph, "text"),
        primaryLabel: value(actions[0], "label"),
        primaryHref: value(actions[0], "href"),
        secondaryLabel: value(actions[1], "label"),
        secondaryHref: value(actions[1], "href"),
    };
}

function heroSliderSurfaceBlock(block: ShopTemplateBlock | undefined): {
    block: ShopTemplateBlock;
    legacySlides: ReturnType<typeof splitShopHeroSliderLegacyStructure>["slides"];
} {
    const baseBlock = block ?? { type: "heroSlider" };
    const structure = splitShopHeroSliderLegacyStructure(baseBlock);
    const slots = { ...(baseBlock.slots ?? {}) };
    const hasLegacySlides = structure.slides.length > 0;

    if (Array.isArray(baseBlock.slots?.[SHOP_GRID_SLOT_KEY])) {
        slots[SHOP_GRID_SLOT_KEY] = structure.gridBlocks;
    }

    if (Array.isArray(baseBlock.slots?.content)) {
        if (structure.contentBlocks.length > 0 || !hasLegacySlides) {
            slots.content = structure.contentBlocks;
        } else {
            delete slots.content;
        }
    }

    return {
        block: {
            ...baseBlock,
            slots,
        },
        legacySlides: structure.slides,
    };
}

function HeroSlider({ block, editorMode = false }: { block?: ShopTemplateBlock; editorMode?: boolean }) {
    const message = useShopStorefrontMessage();
    const { formatMoney } = useShopStorefrontFormatting();
    const [slideIndex, setSlideIndex] = useState(0);
    const heroSurface = useMemo(() => heroSliderSurfaceBlock(block), [block]);
    const heroSurfaceBlock = heroSurface.block;
    const heroSourceBlock = block ?? heroSurfaceBlock;
    const contentReplacesFallback = useSlotReplacesFallback(heroSurfaceBlock, "content");
    const heroGridItems = slotChildren(heroSurfaceBlock, SHOP_GRID_SLOT_KEY);
    const activeLegacySlide = heroSurface.legacySlides.find((legacySlide) => (
        legacySlide.slideIndex === slideIndex + 1
    )) ?? null;
    const heroSlotItemIsVisible = (
        sourceSlot: "content" | typeof SHOP_GRID_SLOT_KEY,
    ): SlotItemFilter => (_childBlock, sourceIndex) => {
        const legacySlide = heroSurface.legacySlides.find((candidate) => (
            candidate.sourceSlot === sourceSlot
            && candidate.sourceIndex === sourceIndex
        ));

        return !legacySlide || legacySlide.slideIndex === slideIndex + 1;
    };
    const activeHeroContentItems = slotChildren(heroSourceBlock, "content")
        .filter(heroSlotItemIsVisible("content"));
    const activeHeroGridItems = slotChildren(heroSourceBlock, SHOP_GRID_SLOT_KEY)
        .filter(heroSlotItemIsVisible(SHOP_GRID_SLOT_KEY));
    const heroContentSlotIsNeeded = (
        activeHeroContentItems.length > 0
        || activeHeroGridItems.length === 0
    );
    const hasHeroContentColumn = heroGridItems.some((childBlock) => (
        childBlock.type === "column"
        && (childBlock.options?.heroContentColumn === true || childBlock.options?.heroContentColumn === "true")
    ));
    const hasLineContent = slotChildren(heroSurfaceBlock, "content").length > 0
        || contentReplacesFallback
        || hasHeroContentColumn
        || heroSurface.legacySlides.length > 0;
    const hasFloatingCards = slotChildren(heroSurfaceBlock, "floatingCards").length > 0;
    const localizedFallbackSlides: HeroSlide[] = [
        {
            ...heroSlides[0],
            alt: message("ui.demo.hero.slide1Alt"),
            eyebrow: message("block.heroSlider.slide1Eyebrow"),
            title: message("block.heroSlider.slide1Title"),
            description: message("block.heroSlider.slide1Text"),
            primaryLabel: message("block.heroSlider.slide1ButtonLabel"),
            secondaryLabel: message("block.heroSlider.slide1SecondaryButtonLabel"),
        },
        {
            ...heroSlides[1],
            alt: message("ui.demo.hero.slide2Alt"),
            eyebrow: message("ui.demo.hero.slide2Eyebrow"),
            title: message("ui.demo.hero.slide2Title"),
            description: message("ui.demo.hero.slide2Text", { price: formatMoney(699) }),
            primaryLabel: message("ui.demo.hero.newArrivals"),
        },
        {
            ...heroSlides[2],
            alt: message("ui.demo.hero.slide3Alt"),
            eyebrow: message("ui.demo.hero.slide3Eyebrow"),
            title: message("ui.demo.hero.slide3Title"),
            description: message("ui.demo.hero.slide3Text"),
            primaryLabel: message("ui.demo.hero.kidsCollection"),
        },
    ];
    const slides = useMemo(() => localizedFallbackSlides.map((slide, index) => {
        const slidePart = `slide${index + 1}`;
        const imagePart = `slide${index + 1}Image`;
        const legacySlideBlock = heroSurface.legacySlides.find((legacySlide) => legacySlide.slideIndex === index + 1)?.block;
        const legacySlide = legacyHeroSlideFallback(legacySlideBlock);
        const legacyFirstSlideValue = (legacyKey: string, fallback: string): string => (
            index === 0 ? blockText(block, legacyKey, fallback) : fallback
        );
        const previewFallback = (value: string): string => editorMode ? value : "";

        return {
            ...slide,
            image: blockText(block, `${imagePart}Url`, previewFallback(slide.image)),
            imageAlt: blockText(block, `${imagePart}Alt`, previewFallback(slide.alt)),
            imagePart,
            imageTitle: blockText(block, `${imagePart}Title`, ""),
            eyebrow: legacySlide.eyebrow
                ?? blockText(block, `${slidePart}Eyebrow`, legacyFirstSlideValue("eyebrow", previewFallback(slide.eyebrow))),
            title: legacySlide.title
                ?? blockText(block, `${slidePart}Title`, legacyFirstSlideValue("title", previewFallback(slide.title))),
            description: legacySlide.description
                ?? blockText(block, `${slidePart}Text`, legacyFirstSlideValue("text", previewFallback(slide.description))),
            primaryLabel: legacySlide.primaryLabel
                ?? blockText(block, `${slidePart}ButtonLabel`, legacyFirstSlideValue("primaryLabel", previewFallback(slide.primaryLabel))),
            primaryHref: legacySlide.primaryHref
                ?? blockText(block, `${slidePart}ButtonUrl`, legacyFirstSlideValue("primaryUrl", previewFallback(slide.primaryHref))),
            secondaryLabel: index === 0
                ? legacySlide.secondaryLabel
                    ?? blockText(block, "slide1SecondaryButtonLabel", blockText(block, "secondaryLabel", previewFallback(slide.secondaryLabel ?? "")))
                : previewFallback(slide.secondaryLabel ?? ""),
            secondaryHref: index === 0
                ? legacySlide.secondaryHref
                    ?? blockText(block, "slide1SecondaryButtonUrl", blockText(block, "secondaryUrl", previewFallback(slide.secondaryHref ?? "")))
                : previewFallback(slide.secondaryHref ?? ""),
        };
    }).filter((slide, index) => (
        editorMode
        || slide.image.trim() !== ""
        || heroSurface.legacySlides.some((legacySlide) => legacySlide.slideIndex === index + 1)
        || (index === 0 && (hasLineContent || hasFloatingCards || heroGridItems.length > 0))
    )), [block, editorMode, hasFloatingCards, hasLineContent, heroGridItems.length, heroSurface.legacySlides, localizedFallbackSlides]);
    const heroGrid = getShopBlockGrid(heroSurfaceBlock.type);
    const heroGridDimensions = gridTrackDimensions(heroSourceBlock, editorMode);
    const extendedGridHeight = heroGrid && heroGridDimensions && heroGridDimensions.rows > heroGrid.rows
        ? heroGridDimensions.height
        : 0;
    const heroSectionStyle = {
        "--shop-hero-extended-grid-height": `${extendedGridHeight}px`,
    } as CSSProperties;

    useEffect(() => {
        if (editorMode || slides.length <= 1) {
            return undefined;
        }

        const timer = window.setInterval(() => {
            setSlideIndex((current) => (current + 1) % slides.length);
        }, 6500);

        return () => window.clearInterval(timer);
    }, [editorMode, slides.length]);

    if (slides.length === 0) {
        return null;
    }

    function setSlide(index: number) {
        setSlideIndex((index + slides.length) % slides.length);
    }

    return (
        <section
            id="hero"
            className="relative h-[max(500px,var(--shop-hero-extended-grid-height))] overflow-hidden sm:h-[max(620px,var(--shop-hero-extended-grid-height))] lg:h-[max(680px,var(--shop-hero-extended-grid-height))]"
            style={mergeStyles(heroSectionStyle, blockVisualStyle(block), interactionStyle(block))}
        >
            {slides.map((slide, index) => (
                <article
                    key={slide.title}
                    className={classNames(
                        "absolute inset-0 transition-opacity duration-500",
                        slideIndex === index ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
                    )}
                >
                    {slide.image ? <img
                        {...editablePart(slide.imagePart)}
                        src={slide.image}
                        alt={slide.imageAlt}
                        title={slide.imageTitle || undefined}
                        className="h-full w-full object-cover object-center"
                        style={imagePartStyle(block, slide.imagePart)}
                    /> : null}
                    <div className={classNames("absolute inset-0 bg-gradient-to-r", slide.overlay)} />
                    {!hasLineContent ? (
                        <div className={imageCoverTextFrameClass(block, "right")}>
                            <div className={classNames("mx-auto w-full max-w-[var(--site-container)] px-6", imageCoverTextPaddingClass(block, "right"))}>
                                <div className={classNames("text-[var(--site-on-inverse)]", imageCoverTextBoxClass(block, "right"))}>
                                    <p {...editableTextPart(`slide${index + 1}Eyebrow`)} className="text-sm font-bold uppercase" style={partStyle(block, "eyebrow")}>
                                        {richTextContent(block, `slide${index + 1}Eyebrow`, slide.eyebrow)}
                                    </p>
                                    {index === 0 ? (
                                        <h1 {...editableTextPart(`slide${index + 1}Title`)} className="mt-3 text-5xl font-black leading-none sm:text-7xl" style={partStyle(block, "title")}>
                                            {richTextContent(block, `slide${index + 1}Title`, slide.title)}
                                        </h1>
                                    ) : (
                                        <h2 {...editableTextPart(`slide${index + 1}Title`)} className="mt-3 text-5xl font-black leading-none sm:text-7xl" style={partStyle(block, "title")}>
                                            {richTextContent(block, `slide${index + 1}Title`, slide.title)}
                                        </h2>
                                    )}
                                    <p {...editableTextPart(`slide${index + 1}Text`)} className="mt-5 max-w-md text-base font-medium sm:text-lg" style={partStyle(block, "text")}>
                                        {richTextContent(block, `slide${index + 1}Text`, slide.description)}
                                    </p>
                                    <div className="mt-7 flex flex-wrap gap-3">
                                        <a {...editablePart("primaryButton")} href={functionalStorefrontHref(slide.primaryHref)} className="rounded-full bg-[var(--site-surface)] px-7 py-3.5 text-sm font-bold text-[var(--site-text)] shadow-lg shadow-[var(--site-shadow-color)]" style={partStyle(block, "primaryButton")}>
                                            <span {...editableTextPart(`slide${index + 1}ButtonLabel`)}>{richTextContent(block, `slide${index + 1}ButtonLabel`, slide.primaryLabel)}</span>
                                        </a>
                                        {slide.secondaryHref && slide.secondaryLabel ? (
                                            <a {...editablePart("secondaryButton")} href={functionalStorefrontHref(slide.secondaryHref)} className="rounded-full bg-[var(--site-surface)] px-7 py-3.5 text-sm font-bold text-[var(--site-text)] shadow-lg shadow-[var(--site-shadow-color)]" style={partStyle(block, "secondaryButton")}>
                                                <span {...editableTextPart("slide1SecondaryButtonLabel")}>{richTextContent(block, "slide1SecondaryButtonLabel", slide.secondaryLabel)}</span>
                                            </a>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </article>
            ))}
            {heroContentSlotIsNeeded ? (
                <SlotBlockGroup
                    block={heroSourceBlock}
                    slotKey="content"
                    className="pointer-events-auto absolute left-[3.5%] top-[22%] z-10 grid w-[56%] max-w-[620px] gap-3"
                    childSlotParentType={activeLegacySlide?.slideIndex === 1 ? "heroSlider:firstSlide" : "heroSlider"}
                    editorMode={editorMode}
                    itemFilter={heroSlotItemIsVisible("content")}
                />
            ) : null}
            <SlotBlockGroup
                block={heroSurfaceBlock}
                slotKey="floatingCards"
                className={classNames(
                    "pointer-events-auto absolute bottom-16 left-[3.5%] z-10 grid w-[56%] max-w-[620px] gap-3 sm:grid-cols-3",
                    hasFloatingCards ? "" : "pointer-events-none",
                )}
                editorMode={editorMode}
            />
            <GridBlockLayer
                block={heroSourceBlock}
                childSlotParentType={activeLegacySlide?.slideIndex === 1 ? "heroSlider:firstSlide" : "heroSlider"}
                editorMode={editorMode}
                fluidWidth
                itemFilter={heroSlotItemIsVisible(SHOP_GRID_SLOT_KEY)}
            />
            <button
                type="button"
                data-shop-editor-ui={editorMode ? "true" : undefined}
                data-shop-hero-slider-control="previous"
                className="absolute left-3 top-4 grid h-11 w-11 place-items-center rounded-full bg-[color-mix(in_srgb,var(--site-surface)_90%,transparent)] shadow sm:left-6 sm:top-1/2 sm:-translate-y-1/2"
                aria-label={message("ui.common.previous")}
                onClick={() => setSlide(slideIndex - 1)}
            >
                <Icon name="chevronLeft" className="h-5 w-5" />
            </button>
            <button
                type="button"
                data-shop-editor-ui={editorMode ? "true" : undefined}
                data-shop-hero-slider-control="next"
                className="absolute right-3 top-4 grid h-11 w-11 place-items-center rounded-full bg-[color-mix(in_srgb,var(--site-surface)_90%,transparent)] shadow sm:right-6 sm:top-1/2 sm:-translate-y-1/2"
                aria-label={message("ui.common.next")}
                onClick={() => setSlide(slideIndex + 1)}
            >
                <Icon name="chevronRight" className="h-5 w-5" />
            </button>
            <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 gap-2">
                {slides.map((slide, index) => (
                    <button
                        key={slide.title}
                        type="button"
                        data-shop-editor-ui={editorMode ? "true" : undefined}
                        className={classNames(
                            "h-1.5 rounded-full transition-all",
                            slideIndex === index ? "w-8 bg-[var(--site-primary)]" : "w-3 bg-[var(--site-surface)]",
                        )}
                        aria-label={message("ui.common.slide", { number: index + 1 })}
                        onClick={() => setSlide(index)}
                    />
                ))}
            </div>
        </section>
    );
}

function renderCategoryRailContentSlotItem(childBlock: ShopTemplateBlock, itemPart: string, editorMode: boolean): ReactNode {
    if (childBlock.type === "column") {
        return (
            <div {...atomicBlockInteractionAttributes(childBlock)} className="grid gap-1 text-center" style={atomicBlockStyle(childBlock)}>
                <SlotBlockGroup
                    block={childBlock}
                    slotKey="content"
                    partPrefix={itemPart}
                    childSlotParentType="categoryRailItem"
                    className="grid gap-1"
                    lockedChildren
                    renderItem={({ childBlock: nestedBlock, itemPart: nestedItemPart }) => (
                        renderCategoryRailContentSlotItem(nestedBlock, nestedItemPart, editorMode)
                    )}
                />
            </div>
        );
    }

    if (childBlock.type === "heading") {
        const title = inlineTextDisplayValue(childBlock, "text", "Kategorie", editorMode);
        const empty = title.value.trim() === "" || title.value === "\u200b";
        const value = empty && editorMode ? "\u200b" : title.value;
        const content = empty ? value : richTextContent(childBlock, "text", title.value);

        return (
            <p {...editableTextContentPart("text", "title")} {...inlineTextPlaceholderAttributes(empty, isEditorCommandLineBlock(childBlock))} {...atomicBlockInteractionAttributes(childBlock)} className="text-sm font-bold leading-5 text-[var(--site-text)]" style={mergeStyles(atomicBlockVisualStyle(childBlock), partStyle(childBlock, "title"), partStyle(childBlock, "text"))}>
                {content}
            </p>
        );
    }

    if (childBlock.type === "paragraph" || childBlock.type === "text") {
        const text = inlineTextDisplayValue(childBlock, "text", "", editorMode);
        const empty = text.value.trim() === "" || text.value === "\u200b";
        const value = empty && editorMode ? "\u200b" : text.value;
        const content = empty ? value : richTextContent(childBlock, "text", text.value);

        return (
            <p {...editableTextPart("text")} {...inlineTextPlaceholderAttributes(empty, isEditorCommandLineBlock(childBlock))} {...atomicBlockInteractionAttributes(childBlock)} className="text-xs font-bold leading-4 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]" style={mergeStyles(atomicBlockVisualStyle(childBlock), partStyle(childBlock, "text"))}>
                {content}
            </p>
        );
    }

    return <AtomicBlock block={childBlock} partPrefix={itemPart} slotParentType="categoryRailItem" />;
}

function renderCategoryRailSlotItem({
    block,
    childBlock,
    editorMode,
    itemPart,
    schemaVersion,
}: {
    block: ShopTemplateBlock | null | undefined;
    childBlock: ShopTemplateBlock;
    editorMode: boolean;
    itemPart: string;
    schemaVersion: number;
}): ReactNode {
    if (childBlock.type === "card") {
        const href = blockText(childBlock, "href", "");
        const imageUrl = blockText(childBlock, "imageUrl", "");
        const title = blockText(childBlock, "title", "Kategorie");
        const imageTitle = blockText(childBlock, "imageTitle", "");
        const cardContentItems = slotChildren(childBlock, "content");
        const cardContentReplacesFallback = slotReplacesFallbackAtVersion(childBlock, "content", schemaVersion);
        const hasCardContent = cardContentItems.length > 0 || cardContentReplacesFallback;
        const layout = imageContentLayout(childBlock, imageContentLayout(block, "top"));
        const itemLayoutClass = layout === "left"
            ? "grid grid-cols-[minmax(0,4.5rem)_minmax(0,1fr)] items-center gap-3 text-left"
            : layout === "right"
                ? "grid grid-cols-[minmax(0,1fr)_minmax(0,4.5rem)] items-center gap-3 text-left"
                : "text-center";
        const imageOrderClass = imageContentImageOrderClass(childBlock, "top");
        const contentOrderClass = imageContentTextOrderClass(childBlock, "top");

        return (
            <ShopStorefrontLink
                {...atomicBlockInteractionAttributes(childBlock)}
                href={functionalStorefrontHref(href)}
                className={classNames("group block", itemLayoutClass)}
                style={atomicBlockVisualStyle(childBlock)}
            >
                <div
                    {...imageHoverFrameAttributes(childBlock, "image")}
                    className={classNames("aspect-square overflow-hidden rounded-full bg-[var(--site-surface)]", imageOrderClass)}
                    style={imageFrameStyle(childBlock, "image")}
                >
                    {imageUrl ? (
                        <img
                            {...editablePart("image")}
                            data-shop-image-hover-target="true"
                            src={imageUrl}
                            alt={blockText(childBlock, "alt", title)}
                            title={imageTitle || undefined}
                            className="h-full w-full object-cover"
                            style={imageInteractivePartStyle(childBlock, "image")}
                        />
                    ) : (
                        <div {...editablePart("image")} className="h-full w-full bg-[var(--site-surface)]" style={imageInteractivePartStyle(childBlock, "image")} />
                    )}
                </div>
                <div className={classNames(layout === "top" && "mt-3", layout === "bottom" && "mb-3", contentOrderClass)}>
                    {hasCardContent ? (
                        <SlotBlockGroup
                            block={childBlock}
                            slotKey="content"
                            partPrefix={itemPart}
                            childSlotParentType="categoryRailItem"
                            className="grid justify-items-center gap-1"
                            lockedChildren
                            renderItem={({ childBlock: contentBlock, itemPart: contentPart }) => (
                                renderCategoryRailContentSlotItem(contentBlock, contentPart, editorMode)
                            )}
                        />
                    ) : (
                        <p {...editableTextPart("title")} className="text-sm font-bold leading-5" style={partStyle(childBlock, "title")}>
                            {richTextContent(childBlock, "title", title)}
                        </p>
                    )}
                </div>
            </ShopStorefrontLink>
        );
    }

    if (childBlock.type === "column") {
        return (
            <div {...atomicBlockInteractionAttributes(childBlock)} className="grid aspect-square place-items-center overflow-hidden rounded-full bg-[var(--site-primary)] p-3 text-center text-[var(--site-on-primary)]" style={atomicBlockVisualStyle(childBlock)}>
                <SlotBlockGroup
                    block={childBlock}
                    slotKey="content"
                    partPrefix={itemPart}
                    childSlotParentType="categoryRailItem"
                    className="grid gap-1"
                    lockedChildren
                    renderItem={({ childBlock: contentBlock, itemPart: contentPart }) => (
                        renderCategoryRailContentSlotItem(contentBlock, contentPart, editorMode)
                    )}
                />
            </div>
        );
    }

    return <AtomicBlock block={childBlock} partPrefix={itemPart} slotParentType="categoryRailItem" />;
}

function CategorySection({ block }: { block?: ShopTemplateBlock }) {
    const message = useShopStorefrontMessage();
    const storefrontHref = useShopStorefrontHref();
    const editorMode = useContext(ShopEditorModeContext);
    const commerce = useContext(ShopCommerceContentContext);
    const schemaVersion = useContext(ShopBuilderSchemaVersionContext);
    const contentItems = slotChildren(block, "content");
    const contentReplacesFallback = useSlotReplacesFallback(block, "content");
    const categoryItems = slotChildren(block, "items");
    const categoryItemsReplaceFallback = useSlotReplacesFallback(block, "items");
    const footerItems = slotChildren(block, "footer");
    const liveCategoryItems = (commerce?.activeSectionItems.length
        ? commerce.activeSectionItems
        : commerce?.headerMenuItems.flatMap((item) => item.children.length > 0 ? item.children : [item]) ?? [])
        .filter((item) => functionalStorefrontHref(menuItemHref(item)) !== undefined)
        .slice(0, 8);
    const liveCatalogHref = liveCategoryItems[0] ? menuItemHref(liveCategoryItems[0]) : storefrontHref("/");
    if (!editorMode && liveCategoryItems.length === 0) {
        return null;
    }

    return (
        <section className="mx-auto max-w-[var(--site-container)] px-4 py-12 sm:px-6 sm:py-16" style={blockVisualStyle(block)}>
            {contentItems.length > 0 ? (
                <SlotBlockGroup
                    block={block}
                    slotKey="content"
                    className="mb-8 grid gap-3"
                />
            ) : !contentReplacesFallback ? (
            <div className="mb-8 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
                <div>
                    <p {...editableTextPart("eyebrow")} className="text-sm font-bold text-[var(--site-primary)]" style={partStyle(block, "eyebrow")}>{richTextContent(block, "eyebrow", message("ui.catalog.catalog"))}</p>
                    <h2 {...editableTextPart("title")} className="mt-1 text-2xl font-extrabold sm:text-3xl" style={partStyle(block, "title")}>{richTextContent(block, "title", message("block.systemSubcategoryList.title"))}</h2>
                </div>
                <ShopStorefrontLink {...editableTextPart("moreButton")} href={functionalStorefrontHref(blockText(block, "moreButtonUrl", liveCatalogHref)) ?? liveCatalogHref} className="text-sm font-bold underline underline-offset-4" style={partStyle(block, "moreButton")}>{richTextContent(block, "moreButtonLabel", message("block.systemSubcategoryList.moreButtonLabel"))}</ShopStorefrontLink>
            </div>
            ) : null}
            {categoryItems.length > 0 ? (
                <SlotBlockGroup
                    block={block}
                    slotKey="items"
                    className="flex gap-4 overflow-x-auto pb-3 [scrollbar-width:none] sm:grid sm:grid-cols-4 lg:grid-cols-8 [&::-webkit-scrollbar]:hidden"
                    itemClassName="min-w-32"
                    renderItem={({ childBlock, itemPart }) => renderCategoryRailSlotItem({
                        block,
                        childBlock,
                        editorMode,
                        itemPart,
                        schemaVersion,
                    })}
                />
            ) : !categoryItemsReplaceFallback ? (
                <nav
                    aria-label={message("ui.catalog.catalog")}
                    className="flex gap-4 overflow-x-auto pb-3 [scrollbar-width:none] sm:grid sm:grid-cols-4 lg:grid-cols-8 [&::-webkit-scrollbar]:hidden"
                >
                    {liveCategoryItems.map((item) => (
                        <ShopStorefrontLink
                            key={item.uuid}
                            href={functionalStorefrontHref(menuItemHref(item))}
                            className="group min-w-32 text-center"
                        >
                            <span className="grid aspect-square place-items-center rounded-full bg-[var(--site-surface)] text-2xl font-black uppercase text-[var(--site-primary)] transition group-hover:bg-[var(--site-primary-soft)]">
                                {item.label.trim().slice(0, 2)}
                            </span>
                            <span className="mt-3 block text-sm font-bold">{item.label}</span>
                        </ShopStorefrontLink>
                    ))}
                </nav>
            ) : null}
            {footerItems.length > 0 ? (
                <SlotBlockGroup
                    block={block}
                    slotKey="footer"
                    className="mt-8 grid gap-3 md:grid-cols-3"
                />
            ) : null}
        </section>
    );
}

function ProductRail({
    block,
    favorites,
    onToggleFavorite,
    publicProducts = [],
    addingProductUuid = null,
    onAddToCart,
    liveProductsOnly = false,
}: {
    block?: ShopTemplateBlock;
    favorites: Set<string>;
    onToggleFavorite: (productId: string) => void;
    publicProducts?: PublicHomeProduct[];
    addingProductUuid?: string | null;
    onAddToCart?: (productUuid: string, productAttributeUuid?: string | null, quantity?: number, surchargeValueUuids?: string[]) => void;
    liveProductsOnly?: boolean;
}) {
    const message = useShopStorefrontMessage();
    const { formatMoney } = useShopStorefrontFormatting();
    const fallbackProducts = localizedFallbackProducts(message, formatMoney);
    const editorMode = useContext(ShopEditorModeContext) && !liveProductsOnly;
    const contentItems = slotChildren(block, "content");
    const contentReplacesFallback = useSlotReplacesFallback(block, "content");
    const afterRailItems = slotChildren(block, "afterRail");
    const railRef = useRef<HTMLDivElement>(null);
    const hasPublicProducts = publicProducts.length > 0;

    function scroll(direction: number) {
        railRef.current?.scrollBy({ left: direction * 330, behavior: "smooth" });
    }

    return (
        <section className="bg-[var(--site-surface)] py-12 sm:py-16" id="damske" style={blockVisualStyle(block)}>
            <div className="mx-auto max-w-[var(--site-container)] px-4 sm:px-6">
                {contentItems.length > 0 ? (
                    <SlotBlockGroup
                        block={block}
                        slotKey="content"
                        className="mb-7 grid gap-3"
                    />
                ) : !contentReplacesFallback ? (
                <div className="mb-7 flex items-end justify-between gap-4">
                    <div>
                        <p {...editableTextPart("eyebrow")} className="text-sm font-bold text-[var(--site-primary)]" style={partStyle(block, "eyebrow")}>{richTextContent(block, "eyebrow", message("ui.catalog.catalog"))}</p>
                        <h2 {...editableTextPart("title")} className="mt-1 text-2xl font-extrabold sm:text-3xl" style={partStyle(block, "title")}>{richTextContent(block, "title", message("block.systemProductList.title"))}</h2>
                    </div>
                    <div className="flex gap-2">
                        <button type="button" className="grid h-10 w-10 place-items-center border border-[color-mix(in_srgb,var(--site-text)_20%,transparent)] bg-[var(--site-surface)]" aria-label={message("ui.common.previous")} onClick={() => scroll(-1)}>
                            <Icon name="chevronLeft" className="h-5 w-5" />
                        </button>
                        <button type="button" className="grid h-10 w-10 place-items-center border border-[color-mix(in_srgb,var(--site-text)_20%,transparent)] bg-[var(--site-surface)]" aria-label={message("ui.common.next")} onClick={() => scroll(1)}>
                            <Icon name="chevronRight" className="h-5 w-5" />
                        </button>
                    </div>
                </div>
                ) : null}
                <div ref={railRef} className="flex snap-x gap-3 overflow-x-auto scroll-smooth pb-4 [scrollbar-width:none] sm:gap-5 [&::-webkit-scrollbar]:hidden">
                    {hasPublicProducts ? publicProducts.map((product, index) => {
                        const fallbackProduct = fallbackProducts[index % fallbackProducts.length];
                        const active = favorites.has(product.uuid);
                        const adding = addingProductUuid === product.uuid;

                        return (
                            <CatalogProductCard
                                key={product.uuid}
                                product={product}
                                fallbackProduct={fallbackProduct}
                                favorite={active}
                                adding={adding}
                                imageBlock={block}
                                imagePart={`product${index + 1}Image`}
                                className="min-w-[78vw] snap-start sm:min-w-[320px] lg:min-w-[calc(25%_-_15px)]"
                                onToggleFavorite={onToggleFavorite}
                                onAddToCart={onAddToCart}
                            />
                        );
                    }) : editorMode ? fallbackProducts.map((product, index) => {
                        const active = favorites.has(product.id);
                        const imagePart = `product${index + 1}Image`;
                        const image = blockText(block, `${imagePart}Url`, product.image);
                        const imageAlt = blockText(block, `${imagePart}Alt`, product.alt);
                        const imageTitle = blockText(block, `${imagePart}Title`, "");

                        return (
                            <article
                                key={product.id}
                                {...itemHoverAttributes(block)}
                                {...imageHoverFrameAttributes(block, imagePart)}
                                className={classNames("group/card min-w-[72vw] snap-start bg-[var(--site-surface)] sm:min-w-[300px] lg:min-w-[calc(25%_-_15px)]", imageContentLayoutClass(block, "top"))}
                            >
                                <div
                                    className={classNames("relative aspect-[4/5] overflow-hidden", product.imageClass, imageContentImageOrderClass(block, "top"))}
                                    style={imageFrameStyle(block, imagePart)}
                                >
                                    {product.badge ? (
                                        <span className={classNames("absolute left-3 top-3 z-10 px-2.5 py-1 text-[10px] font-bold", product.badgeClass)}>
                                            {product.badge}
                                        </span>
                                    ) : null}
                                    <button
                                        type="button"
                                        className={classNames(
                                            "absolute right-3 top-3 z-10 grid h-10 w-10 place-items-center rounded-full bg-[var(--site-surface)]",
                                            active && "text-[var(--site-primary)]",
                                        )}
                                        aria-label={active ? message("ui.product.removeFromFavorites") : message("ui.product.addToFavorites")}
                                        onClick={() => onToggleFavorite(product.id)}
                                    >
                                        <Icon name="heart" className={classNames("h-5 w-5", active && "fill-current")} />
                                    </button>
                                    <img
                                        {...editablePart(imagePart)}
                                        data-shop-image-hover-target="true"
                                        src={image}
                                        alt={imageAlt}
                                        title={imageTitle || undefined}
                                        className="h-full w-full object-cover"
                                        style={imageInteractivePartStyle(block, imagePart)}
                                    />
                                </div>
                                <div className={classNames("p-4", imageContentTextOrderClass(block, "top"))}>
                                    <p className="text-xs font-bold uppercase">{product.brand}</p>
                                    <p className="mt-1 text-sm text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]">{product.name}</p>
                                    <p className="mt-3">
                                        <span className={classNames("font-bold", product.previousPrice && "text-[var(--site-primary)]")}>{product.price}</span>
                                        {product.previousPrice ? <span className="ml-2 text-xs text-[color-mix(in_srgb,var(--site-text)_40%,transparent)] line-through">{product.previousPrice}</span> : null}
                                    </p>
                                    <div className="mt-3 flex gap-1">
                                        {product.swatches.map((swatch) => (
                                            <span key={swatch} className="h-4 w-4 rounded-full border" style={{ backgroundColor: swatch }} />
                                        ))}
                                    </div>
                                </div>
                            </article>
                        );
                    }) : (
                        <div className="w-full rounded-[28px] border border-dashed border-[var(--site-border)] px-6 py-12 text-center">
                            <p className="text-lg font-black">{message("ui.product.noMatches")}</p>
                            <p className={classNames("mt-2 text-sm", readableOnSurfaceTextClass)}>{message("ui.product.adjustSearch")}</p>
                        </div>
                    )}
                </div>
                {afterRailItems.length > 0 ? (
                    <SlotBlockGroup
                        block={block}
                        slotKey="afterRail"
                        className="mt-8 grid gap-3 md:grid-cols-3"
                    />
                ) : null}
            </div>
        </section>
    );
}

function CampaignBlocks({ block }: { block?: ShopTemplateBlock }) {
    const editorMode = useContext(ShopEditorModeContext);
    const contentItems = slotChildren(block, "content");
    const contentReplacesFallback = useSlotReplacesFallback(block, "content");
    const cardItems = slotChildren(block, "cards");
    const cardsReplaceFallback = useSlotReplacesFallback(block, "cards");
    const campaigns = [
        {
            eyebrow: blockText(block, "campaign1Eyebrow", "Dámy"),
            title: blockText(block, "campaign1Title", "Lehkost léta"),
            buttonLabel: blockText(block, "campaign1ButtonLabel", "Nakupovat nyní"),
            href: blockText(block, "campaign1ButtonUrl", "#damske"),
            image: blockText(block, "campaign1ImageUrl", "https://images.unsplash.com/photo-1529139574466-a303027c1d8b?auto=format&fit=crop&w=900&q=85"),
            imageAlt: blockText(block, "campaign1ImageAlt", "Dámská kampaň"),
            imageTitle: blockText(block, "campaign1ImageTitle", ""),
            part: "campaign1",
        },
        {
            eyebrow: blockText(block, "campaign2Eyebrow", "Páni"),
            title: blockText(block, "campaign2Title", "Městský styl"),
            buttonLabel: blockText(block, "campaign2ButtonLabel", "Nakupovat nyní"),
            href: blockText(block, "campaign2ButtonUrl", "#panske"),
            image: blockText(block, "campaign2ImageUrl", "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=900&q=85"),
            imageAlt: blockText(block, "campaign2ImageAlt", "Pánská kampaň"),
            imageTitle: blockText(block, "campaign2ImageTitle", ""),
            part: "campaign2",
        },
        {
            eyebrow: blockText(block, "campaign3Eyebrow", "Děti"),
            title: blockText(block, "campaign3Title", "Hurá ven!"),
            buttonLabel: blockText(block, "campaign3ButtonLabel", "Nakupovat nyní"),
            href: blockText(block, "campaign3ButtonUrl", "#detske"),
            image: blockText(block, "campaign3ImageUrl", "https://images.unsplash.com/photo-1514090458221-65bb69cf63e6?auto=format&fit=crop&w=900&q=85"),
            imageAlt: blockText(block, "campaign3ImageAlt", "Dětská kampaň"),
            imageTitle: blockText(block, "campaign3ImageTitle", ""),
            part: "campaign3",
        },
    ];

    if (!editorMode && contentItems.length === 0 && cardItems.length === 0) {
        return null;
    }

    return (
        <section className="mx-auto grid max-w-[var(--site-container)] gap-4 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-3" id="novinky">
            {contentItems.length > 0 || contentReplacesFallback || editorMode ? (
                <SlotBlockGroup
                    block={block}
                    slotKey="content"
                    className="grid gap-3 lg:col-span-3"
                />
            ) : null}
            {editorMode && !cardsReplaceFallback ? campaigns.map((campaign) => (
                <a
                    key={campaign.part}
                    {...imageHoverFrameAttributes(block, `${campaign.part}Image`)}
                    href={functionalStorefrontHref(campaign.href)}
                    className="group relative min-h-[500px] overflow-hidden"
                >
                    <img
                        {...editablePart(`${campaign.part}Image`)}
                        data-shop-image-hover-target="true"
                        src={campaign.image}
                        alt={campaign.imageAlt}
                        title={campaign.imageTitle || undefined}
                        className="absolute inset-0 h-full w-full object-cover"
                        style={imageInteractivePartStyle(block, `${campaign.part}Image`)}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[color-mix(in_srgb,var(--site-text)_70%,transparent)] via-transparent to-transparent" />
                    <div className={classNames("p-7 text-[var(--site-on-inverse)]", imageCoverTextFrameClass(block, "bottom"))}>
                        <div className={imageCoverTextBoxClass(block, "bottom")}>
                        <p {...editableTextPart(`${campaign.part}Eyebrow`)} className="text-sm font-bold uppercase" style={partStyle(block, `${campaign.part}Eyebrow`)}>{richTextContent(block, `${campaign.part}Eyebrow`, campaign.eyebrow)}</p>
                        <h2 {...editableTextPart(`${campaign.part}Title`)} className="mt-2 text-3xl font-black" style={partStyle(block, `${campaign.part}Title`)}>{richTextContent(block, `${campaign.part}Title`, campaign.title)}</h2>
                        <span {...editableTextPart(`${campaign.part}Button`)} className="mt-4 inline-block border-b-2 pb-1 text-sm font-bold" style={partStyle(block, `${campaign.part}Button`)}>{richTextContent(block, `${campaign.part}ButtonLabel`, campaign.buttonLabel)}</span>
                        </div>
                    </div>
                </a>
            )) : null}
            <SlotBlockGroup
                block={block}
                slotKey="cards"
                className="contents"
                itemClassName="min-h-[320px]"
                renderItem={({ childBlock, itemPart }) => (
                    <div {...atomicBlockInteractionAttributes(childBlock)} className="flex h-full rounded-[28px] border border-[var(--site-border)] bg-[var(--site-surface)] p-6 shadow-sm" style={atomicBlockVisualStyle(childBlock)}>
                        <AtomicBlock block={childBlock} partPrefix={itemPart} />
                    </div>
                )}
            />
        </section>
    );
}

function LoyaltyBlock({ block }: { block?: ShopTemplateBlock }) {
    const message = useShopStorefrontMessage();
    const editorMode = useContext(ShopEditorModeContext);
    const contentItems = slotChildren(block, "content");
    const contentReplacesFallback = useSlotReplacesFallback(block, "content");
    const mediaSlotKey = slotIsDefined(block, SHOP_GRID_SLOT_KEY)
        ? SHOP_GRID_SLOT_KEY
        : "media";
    const mediaItems = slotChildren(block, mediaSlotKey);
    const mediaReplacesFallback = useSlotReplacesFallback(block, mediaSlotKey);
    const hasContentSlot = contentItems.length > 0 || contentReplacesFallback;
    const hasMediaSlot = mediaItems.length > 0 || mediaReplacesFallback;

    const ctaItems = slotChildren(block, "cta");
    const itemItems = slotChildren(block, "items");
    const contentHasActions = contentItems.some((childBlock) => (
        childBlock.type === "column"
            ? slotChildren(childBlock, "content").some((contentBlock) => atomicButtonLikeBlockTypes.has(contentBlock.type))
            : atomicButtonLikeBlockTypes.has(childBlock.type)
    ));
    const imagePart = "loyaltyImage";
    const imageUrl = blockText(block, "loyaltyImageUrl", "");
    const legacyContentConfigured = ["eyebrow", "title", "text"].some((part) => blockText(block, part, "").trim() !== "");
    const legacyCtaConfigured = ["primaryLabel", "secondaryLabel"].some((part) => blockText(block, part, "").trim() !== "");
    const layout = imageContentLayout(block, "right");
    const authoredGridMediaStartsOnLeft = mediaSlotKey === SHOP_GRID_SLOT_KEY
        && mediaItems.some((mediaBlock) => {
            const x = Math.max(1, Math.round(numericBlockOption(mediaBlock, "gridX", 1)));
            const w = Math.max(1, Math.round(numericBlockOption(mediaBlock, "gridW", 12)));

            return x + w - 1 <= 6;
        });

    if (!editorMode && !hasContentSlot && !hasMediaSlot && ctaItems.length === 0 && itemItems.length === 0 && !legacyContentConfigured && !legacyCtaConfigured && !imageUrl) {
        return null;
    }

    const contentPanel = (
        <div className={classNames(
            "px-6 py-12 text-center sm:px-12 sm:py-16 lg:text-left",
            mediaSlotKey === SHOP_GRID_SLOT_KEY
                ? "relative z-10 lg:min-h-[560px] lg:w-1/2"
                : imageContentTextOrderClass(block, "right"),
            authoredGridMediaStartsOnLeft ? "lg:ml-auto" : "",
        )}>
            {contentItems.length > 0 ? (
                <SlotBlockGroup
                    block={block}
                    slotKey="content"
                    className="grid justify-items-center gap-5 lg:justify-items-start"
                />
            ) : !contentReplacesFallback && legacyContentConfigured ? (
                <>
                    {blockText(block, "eyebrow", "") ? <span {...editableTextPart("eyebrow")} className="inline-block rounded-full bg-[var(--site-accent)] px-3 py-1 text-xs font-black text-[var(--site-text)]" style={partStyle(block, "eyebrow")}>{richTextContent(block, "eyebrow", "")}</span> : null}
                    {blockText(block, "title", "") ? <h2 {...editableTextPart("title")} className="mt-5 text-4xl font-black leading-tight sm:text-6xl" style={partStyle(block, "title")}>{richTextContent(block, "title", "")}</h2> : null}
                    {blockText(block, "text", "") ? (
                    <p {...editableTextPart("text")} className="mt-5 max-w-xl text-base text-[color-mix(in_srgb,var(--site-on-inverse)_80%,transparent)] sm:text-lg" style={partStyle(block, "text")}>
                        {richTextContent(block, "text", "")}
                    </p>
                    ) : null}
                </>
            ) : null}
            {ctaItems.length > 0 ? (
                <SlotBlockGroup
                    block={block}
                    slotKey="cta"
                    className="mt-8 flex flex-col justify-center gap-3 sm:flex-row lg:justify-start"
                />
            ) : !hasContentSlot && !contentHasActions && legacyCtaConfigured ? (
                <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
                    {blockText(block, "primaryLabel", "") ? <a {...editableTextPart("primaryButton")} href={functionalStorefrontHref(blockText(block, "primaryUrl", ""))} className="rounded-full bg-[var(--site-surface)] px-7 py-3.5 text-sm font-bold text-[var(--site-primary)]" style={partStyle(block, "primaryButton")}>{richTextContent(block, "primaryLabel", "")}</a> : null}
                    {blockText(block, "secondaryLabel", "") ? <a {...editableTextPart("secondaryButton")} href={functionalStorefrontHref(blockText(block, "secondaryUrl", ""))} className="rounded-full border border-[var(--site-on-inverse)] px-7 py-3.5 text-sm font-bold" style={partStyle(block, "secondaryButton")}>{richTextContent(block, "secondaryLabel", "")}</a> : null}
                </div>
            ) : null}
        </div>
    );

    return (
        <section className="bg-[var(--site-primary)] text-[var(--site-on-primary)]" id="vyprodej" style={blockVisualStyle(block)}>
            {mediaSlotKey === SHOP_GRID_SLOT_KEY ? (
                <div className="relative mx-auto max-w-[var(--site-container)]">
                    {contentPanel}
                    <SlotBlockGroup
                        block={block}
                        slotKey={mediaSlotKey}
                        compactGridChildren
                        className="pointer-events-none grid min-h-[430px] grid-cols-1 lg:absolute lg:inset-0 lg:min-h-[560px] lg:grid-cols-12"
                        itemClassName="pointer-events-auto h-[430px] min-h-[430px] w-full overflow-hidden lg:h-[560px] lg:min-h-[560px]"
                        semanticGrid
                    />
                </div>
            ) : (
                <div className={classNames("mx-auto grid max-w-[var(--site-container)] items-center", layout === "left" || layout === "right" ? "lg:grid-cols-2" : "lg:grid-cols-1")}>
                    {contentPanel}
                    {mediaItems.length > 0 ? (
                        <SlotBlockGroup
                            block={block}
                            slotKey={mediaSlotKey}
                            compactGridChildren
                            className={classNames("grid min-h-[430px] lg:min-h-[560px]", imageContentImageOrderClass(block, "right"))}
                            itemClassName="h-[430px] min-h-[430px] w-full overflow-hidden lg:h-[560px] lg:min-h-[560px]"
                        />
                    ) : !mediaReplacesFallback && imageUrl ? (
                        <img
                            {...editablePart(imagePart)}
                            src={imageUrl}
                            alt={blockText(block, "loyaltyImageAlt", message("block.loyalty.imageAlt"))}
                            title={blockText(block, "loyaltyImageTitle", "") || undefined}
                            className={classNames("h-[430px] w-full object-cover lg:h-[560px]", imageContentImageOrderClass(block, "right"))}
                            style={imagePartStyle(block, imagePart)}
                        />
                    ) : null}
                </div>
            )}
            {itemItems.length > 0 ? (
                <div className="mx-auto max-w-[var(--site-container)] px-6 pb-12 sm:px-12 sm:pb-16">
                    <SlotBlockGroup
                        block={block}
                        slotKey="items"
                        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
                    />
                </div>
            ) : null}
        </section>
    );
}

function ServiceBenefits({ block }: { block?: ShopTemplateBlock }) {
    const message = useShopStorefrontMessage();
    const editorMode = useContext(ShopEditorModeContext);
    const contentItems = slotChildren(block, "content");
    const contentReplacesFallback = useSlotReplacesFallback(block, "content");
    const items = slotChildren(block, "items");
    const itemsReplaceFallback = useSlotReplacesFallback(block, "items");
    const mediaItems = slotChildren(block, "media");
    const mediaReplacesFallback = useSlotReplacesFallback(block, "media");
    const hasContentSlot = contentItems.length > 0 || contentReplacesFallback;
    const hasMediaSlot = mediaItems.length > 0 || mediaReplacesFallback;

    if (!editorMode && contentItems.length === 0 && items.length === 0 && mediaItems.length === 0) {
        return null;
    }

    return (
        <section className="border-y bg-[var(--site-surface)]">
            {hasContentSlot || editorMode ? (
                <SlotBlockGroup
                    block={block}
                    slotKey="content"
                    className="mx-auto grid max-w-[var(--site-container)] gap-3 px-5 pt-12 sm:px-6"
                />
            ) : null}
            <SlotBlockGroup
                block={block}
                slotKey="items"
                className="mx-auto grid max-w-[var(--site-container)] gap-8 px-5 py-12 sm:grid-cols-2 sm:px-6 lg:grid-cols-4"
            />
            {editorMode && items.length === 0 && !itemsReplaceFallback ? (
                <div className="mx-auto grid max-w-[var(--site-container)] gap-8 px-5 py-12 sm:grid-cols-2 sm:px-6 lg:grid-cols-4">
                    {[
                        ["truck", message("block.serviceBenefits.benefit1Title"), message("block.serviceBenefits.benefit1Text")],
                        ["store", message("block.serviceBenefits.benefit2Title"), message("block.serviceBenefits.benefit2Text")],
                        ["rotate", message("block.serviceBenefits.benefit3Title"), message("block.serviceBenefits.benefit3Text")],
                        ["shield", message("block.serviceBenefits.benefit4Title"), message("block.serviceBenefits.benefit4Text")],
                    ].map(([icon, title, text], index) => (
                        <div key={title} className="flex gap-4">
                            <Icon name={icon as IconName} className="h-7 w-7 shrink-0 text-[var(--site-primary)]" />
                            <div>
                                <h3 {...editableTextPart(`benefit${index + 1}Title`)} className="text-sm font-bold">{richTextContent(block, `benefit${index + 1}Title`, title)}</h3>
                                <p {...editableTextPart(`benefit${index + 1}Text`)} className="mt-1 text-xs leading-5 text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">{richTextContent(block, `benefit${index + 1}Text`, text)}</p>
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}
            {hasMediaSlot || editorMode ? (
                <SlotBlockGroup
                    block={block}
                    slotKey="media"
                    className="mx-auto grid max-w-[var(--site-container)] gap-3 px-5 pb-12 sm:px-6 md:grid-cols-2"
                />
            ) : null}
        </section>
    );
}

function KidsAndBrands({ block }: { block?: ShopTemplateBlock }) {
    const message = useShopStorefrontMessage();
    const editorMode = useContext(ShopEditorModeContext);
    const commerce = useContext(ShopCommerceContentContext);
    const storefrontHref = useShopStorefrontHref();
    const contentItems = slotChildren(block, "content");
    const mediaItems = slotChildren(block, "media");
    const itemItems = slotChildren(block, "items");
    const contentReplacesFallback = useSlotReplacesFallback(block, "content");
    const mediaReplacesFallback = useSlotReplacesFallback(block, "media");
    const itemsReplaceFallback = useSlotReplacesFallback(block, "items");
    const hasContentSlot = contentItems.length > 0 || contentReplacesFallback;
    const hasMediaSlot = mediaItems.length > 0 || mediaReplacesFallback;
    const hasItemsSlot = itemItems.length > 0 || itemsReplaceFallback;
    const highlightItems = slotChildren(block, "highlights");
    const brandNames = ["STRIDE", "NORTH", "MOVE", "FLEXA", "URBAN", "COMFY"].map((brand, index) => ({
        name: blockText(block, `brand${index + 1}Name`, brand),
        part: `brand${index + 1}Name`,
    }));
    const menImagePart = "menImage";
    const menImageUrl = blockText(block, "menImageUrl", editorMode ? "https://images.unsplash.com/photo-1614252369475-531eba835eb1?auto=format&fit=crop&w=1800&q=86" : "");
    const legacyMenContentConfigured = ["menEyebrow", "menTitle", "menText", "menButtonLabel"].some((part) => blockText(block, part, "").trim() !== "");
    const showMenSpotlight = editorMode || hasContentSlot || hasMediaSlot || menImageUrl !== "" || legacyMenContentConfigured;
    const kidLayout = imageContentLayout(block, "top");
    const liveCategoryItems = (commerce?.activeSectionItems.length
        ? commerce.activeSectionItems
        : commerce?.headerMenuItems.flatMap((item) => item.children.length > 0 ? item.children : [item]) ?? [])
        .filter((item) => functionalStorefrontHref(menuItemHref(item)) !== undefined)
        .slice(0, 6);
    const primaryCategoryHref = liveCategoryItems[0] ? menuItemHref(liveCategoryItems[0]) : storefrontHref("/");

    return (
        <>
            {showMenSpotlight ? (
            <section id="panske" className="mx-auto max-w-[var(--site-container)] px-4 py-12 sm:px-6 sm:py-16">
                <div
                    {...imageHoverFrameAttributes(block, menImagePart)}
                    className="relative overflow-hidden bg-[var(--site-surface-elevated)]"
                >
                    {hasMediaSlot || editorMode ? (
                        <SlotBlockGroup
                            block={block}
                            slotKey="media"
                            className="grid min-h-[520px] sm:min-h-[580px]"
                            itemClassName="min-h-[520px] sm:min-h-[580px]"
                        />
                    ) : menImageUrl ? (
                        <img
                            {...editablePart(menImagePart)}
                            data-shop-image-hover-target="true"
                            src={menImageUrl}
                            alt={blockText(block, "menImageAlt", message("block.menSpotlight.imageAlt"))}
                            title={blockText(block, "menImageTitle", "") || undefined}
                            loading="lazy"
                            className="h-[520px] w-full object-cover object-center sm:h-[580px]"
                            style={imageInteractivePartStyle(block, menImagePart)}
                        />
                    ) : null}
                    <div className="absolute inset-0 bg-gradient-to-r from-[color-mix(in_srgb,var(--site-text)_60%,transparent)] via-[color-mix(in_srgb,var(--site-text)_15%,transparent)] to-transparent" />
                    <div className={classNames("p-7 sm:p-14", imageCoverTextFrameClass(block, "right"))}>
                        <div className={classNames("text-[var(--site-on-inverse)]", imageCoverTextBoxClass(block, "right"))}>
                            {hasContentSlot || editorMode ? (
                                <SlotBlockGroup
                                    block={block}
                                    slotKey="content"
                                    className="grid max-w-xl gap-3"
                                />
                            ) : legacyMenContentConfigured ? (
                                <>
                                    {blockText(block, "menEyebrow", "") ? <p {...editableTextPart("menEyebrow")} className="text-sm font-bold uppercase" style={partStyle(block, "menEyebrow")}>{richTextContent(block, "menEyebrow", "")}</p> : null}
                                    {blockText(block, "menTitle", "") ? <h2 {...editableTextPart("menTitle")} className="mt-3 text-4xl font-black sm:text-6xl" style={partStyle(block, "menTitle")}>{richTextContent(block, "menTitle", "")}</h2> : null}
                                    {blockText(block, "menText", "") ? <p {...editableTextPart("menText")} className="mt-4 text-[color-mix(in_srgb,var(--site-on-inverse)_80%,transparent)]" style={partStyle(block, "menText")}>{richTextContent(block, "menText", "")}</p> : null}
                                    {blockText(block, "menButtonLabel", "") ? <a {...editableTextPart("menButton")} href={functionalStorefrontHref(blockText(block, "menButtonUrl", primaryCategoryHref))} className="mt-7 inline-block bg-[var(--site-surface)] px-7 py-3.5 text-sm font-bold text-[var(--site-text)]" style={partStyle(block, "menButton")}>{richTextContent(block, "menButtonLabel", "")}</a> : null}
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>
            </section>
            ) : null}
            {!editorMode && liveCategoryItems.length > 0 ? (
            <section id="detske" className="border-y bg-[var(--site-surface)] py-12 sm:py-16">
                <div className="mx-auto max-w-[var(--site-container)] px-4 sm:px-6">
                    <div className="mb-8 text-center">
                        <p {...editableTextPart("kidsEyebrow")} className="text-sm font-bold text-[var(--site-primary)]" style={partStyle(block, "kidsEyebrow")}>{richTextContent(block, "kidsEyebrow", "Pro malé i větší")}</p>
                        <h2 {...editableTextPart("kidsTitle")} className="mt-1 text-3xl font-extrabold" style={partStyle(block, "kidsTitle")}>{richTextContent(block, "kidsTitle", "Dětské kategorie")}</h2>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                        {liveCategoryItems.map((item) => (
                            <ShopStorefrontLink
                                key={item.uuid}
                                href={functionalStorefrontHref(menuItemHref(item))}
                                {...itemHoverAttributes(block)}
                                className={classNames(
                                    "bg-[var(--site-surface)] p-3 shadow-sm",
                                    kidLayout === "left"
                                        ? "grid grid-cols-[minmax(0,4.5rem)_minmax(0,1fr)] items-center gap-3 text-left"
                                        : kidLayout === "right"
                                            ? "grid grid-cols-[minmax(0,1fr)_minmax(0,4.5rem)] items-center gap-3 text-left"
                                            : "text-center",
                                )}
                            >
                                <span className={classNames("grid aspect-square w-full place-items-center bg-[var(--site-primary-soft)] text-2xl font-black uppercase text-[var(--site-primary)]", imageContentImageOrderClass(block, "top"))}>
                                    {item.label.trim().slice(0, 2)}
                                </span>
                                <span className={classNames("block py-3 text-sm font-bold", imageContentTextOrderClass(block, "top"))}>{item.label}</span>
                            </ShopStorefrontLink>
                        ))}
                    </div>
                </div>
            </section>
            ) : null}
            {hasItemsSlot || highlightItems.length > 0 || editorMode ? (
            <section id="znacky" className="mx-auto max-w-[var(--site-container)] px-4 py-14 sm:px-6 sm:py-20">
                {hasItemsSlot ? (
                    <SlotBlockGroup
                        block={block}
                        slotKey="items"
                        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
                        itemClassName={({ childBlock }) => Number(blockRecord(childBlock, "options").gridW) >= 12 ? "col-span-full" : undefined}
                    />
                ) : editorMode ? (
                    <>
                        <h2 {...editableTextPart("brandsTitle")} className="text-center text-2xl font-extrabold sm:text-3xl" style={partStyle(block, "brandsTitle")}>{richTextContent(block, "brandsTitle", "Oblíbené značky")}</h2>
                        <div className="mt-10 grid grid-cols-2 border-l border-t sm:grid-cols-3 lg:grid-cols-6">
                            {brandNames.map((brand) => (
                                <div key={brand.part} {...editableTextPart(brand.part)} className={classNames("grid h-28 place-items-center border-b border-r text-xl font-black", brand.name === "FLEXA" && "text-[var(--site-primary)]")} style={partStyle(block, brand.part)}>
                                    {richTextContent(block, brand.part, brand.name)}
                                </div>
                            ))}
                        </div>
                    </>
                ) : null}
                <SlotBlockGroup
                    block={block}
                    slotKey="highlights"
                    className="mt-8 grid gap-3 md:grid-cols-2 xl:grid-cols-4"
                />
            </section>
            ) : null}
        </>
    );
}

function TrustAndText({ block }: { block?: ShopTemplateBlock }) {
    const editorMode = useContext(ShopEditorModeContext);
    const seoContent = slotChildren(block, "content");
    const contentReplacesFallback = useSlotReplacesFallback(block, "content");
    const legacyContentConfigured = ["title", "text", "moreButtonLabel", "moreText1", "moreText2"].some((part) => blockText(block, part, "").trim() !== "");

    if (!editorMode && seoContent.length === 0 && !legacyContentConfigured) {
        return null;
    }

    return (
        <section className={classNames("mx-auto max-w-[1100px] px-5 py-14 text-sm leading-7 sm:py-20", readableOnSurfaceTextClass)} style={blockVisualStyle(block)}>
            {seoContent.length > 0 || contentReplacesFallback || editorMode ? (
                <SlotBlockGroup block={block} slotKey="content" className="grid gap-5" />
            ) : legacyContentConfigured ? (
                <>
                    {blockText(block, "title", "") ? <h2 {...editableTextPart("title")} className="text-2xl font-extrabold text-[var(--site-text)]" style={partStyle(block, "title")}>{richTextContent(block, "title", "")}</h2> : null}
                    {blockText(block, "text", "") ? <p {...editableTextPart("text")} className="mt-5" style={partStyle(block, "text")}>{richTextContent(block, "text", "")}</p> : null}
                    {blockText(block, "moreButtonLabel", "") ? <details className="group mt-4">
                        <summary className="cursor-pointer list-none font-bold text-[var(--site-primary)]">
                            <span {...editableTextPart("moreButton")} style={partStyle(block, "moreButton")}>{richTextContent(block, "moreButtonLabel", "")}</span> <Icon name="chevronDown" className="inline h-4 w-4 transition group-open:rotate-180" />
                        </summary>
                        <div className="mt-4 space-y-4">
                            {blockText(block, "moreText1", "") ? <p {...editableTextPart("moreText1")} style={partStyle(block, "moreText1")}>{richTextContent(block, "moreText1", "")}</p> : null}
                            {blockText(block, "moreText2", "") ? <p {...editableTextPart("moreText2")} style={partStyle(block, "moreText2")}>{richTextContent(block, "moreText2", "")}</p> : null}
                        </div>
                    </details> : null}
                </>
            ) : null}
        </section>
    );
}

function Newsletter({ block, onSubmit }: { block?: ShopTemplateBlock; onSubmit: (event: FormEvent<HTMLFormElement>) => void }) {
    const message = useShopStorefrontMessage();
    const editorMode = useContext(ShopEditorModeContext);
    const emailInputId = useId();
    const contentItems = slotChildren(block, "content");
    const contentReplacesFallback = useSlotReplacesFallback(block, "content");
    const legacyContentConfigured = ["title", "text", "buttonLabel", "placeholder"].some((part) => blockText(block, part, "").trim() !== "");
    const hasContentButton = contentItems.some((childBlock) => (
        childBlock.type === "column"
            ? slotChildren(childBlock, "content").some((contentBlock) => contentBlock.type === "button")
            : childBlock.type === "button"
    ));
    const renderNewsletterContentItem: SlotRenderItem = ({ childBlock, itemPart }) => {
        if (childBlock.type !== "button") {
            return <AtomicBlock block={childBlock} partPrefix={itemPart} slotParentType={block?.type} />;
        }

        return (
            <button
                {...editablePart("primaryButton")}
                type="submit"
                className="inline-flex max-w-full min-w-0 items-center justify-center rounded-full bg-[var(--site-accent)] px-7 py-3.5 text-center text-sm font-bold text-[var(--site-text)] whitespace-normal [overflow-wrap:anywhere]"
                style={mergeStyles(atomicButtonBlockStyle(childBlock), partStyle(childBlock, "primaryButton"))}
            >
                <span {...editableTextPart("primaryButton")} className="min-w-0 max-w-full [overflow-wrap:anywhere]">{richTextContent(childBlock, "label", message("block.newsletter.buttonLabel"))}</span>
            </button>
        );
    };

    if (!editorMode && contentItems.length === 0 && !legacyContentConfigured) {
        return null;
    }

    return (
        <section className="bg-[var(--site-primary)] px-5 py-10 text-[var(--site-on-primary)]" style={blockStyle(block)}>
            <form className="mx-auto flex w-full min-w-0 max-w-[1200px] flex-col items-center justify-between gap-6 text-center lg:flex-row lg:text-left" onSubmit={onSubmit}>
                <input autoComplete="off" className="hidden" name="honeypotValue" tabIndex={-1} type="text" />
                <div className="min-w-0 flex-1">
                    {contentItems.length > 0 || contentReplacesFallback || editorMode ? (
                        <ShopSubmitButtonContext.Provider value>
                            <SlotBlockGroup
                                block={block}
                                slotKey="content"
                                className="grid justify-items-center gap-3 lg:justify-items-start"
                                renderItem={renderNewsletterContentItem}
                            />
                        </ShopSubmitButtonContext.Provider>
                    ) : legacyContentConfigured ? (
                        <>
                            {blockText(block, "title", "") ? <h2 {...editableTextPart("title")} className="max-w-full text-2xl font-extrabold [overflow-wrap:anywhere]" style={partStyle(block, "title")}>{richTextContent(block, "title", "")}</h2> : null}
                            {blockText(block, "text", "") ? <p {...editableTextPart("text")} className="mt-2 max-w-full text-sm text-[color-mix(in_srgb,var(--site-on-inverse)_75%,transparent)] [overflow-wrap:anywhere]" style={partStyle(block, "text")}>{richTextContent(block, "text", "")}</p> : null}
                        </>
                    ) : null}
                </div>
                <div {...editablePart("search")} className="flex w-full min-w-0 max-w-xl flex-col gap-2 rounded-[28px] bg-[color-mix(in_srgb,var(--site-surface)_10%,transparent)] p-4 sm:flex-row" style={partStyle(block, "search")}>
                    <label className="sr-only" htmlFor={emailInputId}>{message("ui.newsletter.email")}</label>
                    <input
                        id={emailInputId}
                        required
                        name="email"
                        type="email"
                        placeholder={blockText(block, "placeholder", message("block.newsletter.placeholder"))}
                        aria-label={message("ui.newsletter.email")}
                        className="min-w-0 flex-1 rounded-full bg-[var(--site-surface)] px-4 py-3.5 text-sm text-[var(--site-text)] outline-none placeholder:text-[var(--site-muted)] focus-visible:ring-2 focus-visible:ring-[var(--site-accent)]/45"
                    />
                    {!hasContentButton ? (
                        <span {...editablePart("button")} className="inline-flex max-w-full min-w-0" style={partSpacingStyle(block, "button")}>
                            <button type="submit" className="max-w-full min-w-0 rounded-full bg-[var(--site-accent)] px-7 py-3.5 text-center text-sm font-bold text-[var(--site-text)] whitespace-normal [overflow-wrap:anywhere]" style={partStyle(block, "button")}>
                                <span {...editableTextPart("button")} className="min-w-0 max-w-full [overflow-wrap:anywhere]">{richTextContent(block, "buttonLabel", message("block.newsletter.buttonLabel"))}</span>
                            </button>
                        </span>
                    ) : null}
                </div>
            </form>
        </section>
    );
}

function Footer({
    block,
    homeHref,
    siteName,
    variant,
}: {
    block?: ShopTemplateBlock | null;
    homeHref: string;
    siteName: string;
    variant: string;
}) {
    const message = useShopStorefrontMessage();
    const storefrontHref = useShopStorefrontHref();
    const { pages } = useContext(ShopSearchContext);
    const year = useMemo(() => new Date().getFullYear(), []);
    const footerLayout = normalizeLagunaFooterLayoutStyle(
        blockOptionText(block, "footerLayoutStyle") || variant,
    );
    const layoutBlock = block
        ? ensureLagunaFooterLayoutSkeleton(block, footerLayout)
        : block;
    const footerGridBlocks = slotChildren(layoutBlock, SHOP_GRID_SLOT_KEY);
    const footerColumnBlocks = slotChildren(layoutBlock, "columns");
    const footerBottomBlocks = slotChildren(layoutBlock, "bottom");
    const hasSlotContent = footerColumnBlocks.length > 0 || footerBottomBlocks.length > 0;
    const footerGridRoles = new Set(footerGridBlocks.map((childBlock) => (
        blockOptionText(childBlock, SHOP_DOCUMENT_ROLE_OPTION)
    )));
    const hasAuthoredGrid = ["footerService", "footerLinks", "footerDivider", "footerCopyright"]
        .some((role) => footerGridRoles.has(role));
    const pageLinks = useMemo(() => publicPageLinks(pages, storefrontHref), [pages, storefrontHref]);
    const pageColumnSize = Math.max(1, Math.ceil(pageLinks.length / 2));
    const pageColumns = [
        pageLinks.slice(0, pageColumnSize),
        pageLinks.slice(pageColumnSize),
    ].filter((column) => column.length > 0);

    if (hasAuthoredGrid) {
        return (
            <footer
                className="bg-[var(--site-inverse)] px-5 text-[var(--site-on-inverse)] sm:px-6"
                data-shop-footer-layout={footerLayout}
                data-shop-semantic-chrome-grid="footer"
                style={mergeStyles(
                    blockVisualStyle(layoutBlock),
                    interactionStyle(layoutBlock),
                )}
            >
                <SlotBlockGroup
                    block={layoutBlock}
                    slotKey={SHOP_GRID_SLOT_KEY}
                    compactGridChildren
                    className="mx-auto grid max-w-[var(--site-container)] gap-8 sm:grid-cols-2 lg:grid-cols-12 lg:gap-10"
                    itemClassName={({ childBlock }) => {
                        const role = blockOptionText(childBlock, SHOP_DOCUMENT_ROLE_OPTION);

                        if (role === "footerService") return "min-w-0 sm:col-span-2 lg:col-span-4";
                        if (role === "footerLinks") return "min-w-0 sm:col-span-2 lg:col-span-7 lg:col-start-6";
                        if (role === "footerDivider" || role === "footerCopyright") return "min-w-0 sm:col-span-2 lg:col-span-12";

                        return "min-w-0";
                    }}
                    renderItem={({ childBlock, itemPart }) => {
                        const role = blockOptionText(childBlock, SHOP_DOCUMENT_ROLE_OPTION);

                        if (role === "footerService") {
                            return (
                                <ShopLogo
                                    href={homeHref}
                                    siteName={siteName}
                                    brandContent={(
                                        <InlineSlotBlockGroup
                                            block={childBlock}
                                            slotKey="content"
                                            partPrefix={itemPart}
                                            childSlotParentType="footerService"
                                        />
                                    )}
                                />
                            );
                        }

                        if (role === "footerDivider") {
                            return (
                                <div {...atomicBlockInteractionAttributes(childBlock)} className="w-full" style={blockSpacingStyle(childBlock)}>
                                    <div className="mx-auto" style={separatorLineStyle(childBlock)} />
                                </div>
                            );
                        }

                        return (
                            <ColumnBlock
                                block={childBlock}
                                partPrefix={itemPart}
                                slotParentType="footer"
                            />
                        );
                    }}
                    semanticGrid
                    style={blockSpacingStyle(layoutBlock)}
                />
            </footer>
        );
    }

    if (hasSlotContent) {
        return (
            <footer className="bg-[var(--site-inverse)] px-5 text-[var(--site-on-inverse)] sm:px-6" style={mergeStyles(blockStyle(block), interactionStyle(block))}>
                <div className="mx-auto max-w-[var(--site-container)] py-12">
                    {footerColumnBlocks.length > 0 ? (
                        <SlotBlockGroup
                            block={block}
                            slotKey="columns"
                            className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4"
                            itemClassName="min-w-0"
                        />
                    ) : null}
                    {footerBottomBlocks.length > 0 ? (
                        <SlotBlockGroup
                            block={block}
                            slotKey="bottom"
                            className="mt-10 grid gap-4 border-t border-[color-mix(in_srgb,var(--site-on-inverse)_10%,transparent)] pt-5"
                            itemClassName="min-w-0"
                        />
                    ) : null}
                </div>
            </footer>
        );
    }

    if (variant === "simple") {
        return (
            <footer className="border-t border-[var(--site-border)] bg-[var(--site-surface)] px-5 py-8 text-sm text-[color-mix(in_srgb,var(--site-text)_55%,transparent)] sm:px-6" style={mergeStyles(blockStyle(block), interactionStyle(block))}>
                <div className="mx-auto flex max-w-[var(--site-container)] flex-col items-center justify-between gap-4 text-center sm:flex-row sm:text-left">
                    <ShopLogo href={homeHref} siteName={siteName} />
                    {pageLinks.length > 0 ? (
                        <nav className="flex flex-wrap justify-center gap-4" aria-label={message("ui.footer.help")}>
                            {pageLinks.slice(0, 4).map((pageLink) => (
                                <a key={pageLink.href} href={pageLink.href} className="hover:text-[var(--site-primary)]">{pageLink.title}</a>
                            ))}
                        </nav>
                    ) : null}
                    <p>© {year} {siteName}</p>
                </div>
            </footer>
        );
    }

    return (
        <footer className="bg-[var(--site-inverse)] px-5 text-[var(--site-on-inverse)] sm:px-6" style={mergeStyles(blockStyle(block), interactionStyle(block))}>
            <div className="mx-auto grid max-w-[var(--site-container)] gap-10 py-12 sm:grid-cols-2 lg:grid-cols-3">
                <div className="min-w-0">
                    <ShopLogo href={homeHref} siteName={siteName} />
                </div>
                {pageColumns.map((column, columnIndex) => (
                    <nav key={columnIndex} aria-label={message(columnIndex === 0 ? "ui.footer.help" : "ui.footer.aboutCompany")}>
                        <h3 className="text-sm font-bold">{message(columnIndex === 0 ? "ui.footer.help" : "ui.footer.aboutCompany")}</h3>
                        <div className="mt-5 space-y-3 text-sm text-[color-mix(in_srgb,var(--site-on-inverse)_60%,transparent)]">
                            {column.map((pageLink) => (
                                <a key={pageLink.href} href={pageLink.href} className="block hover:text-[var(--site-on-inverse)]">{pageLink.title}</a>
                            ))}
                        </div>
                    </nav>
                ))}
            </div>
            <div className="mx-auto flex max-w-[var(--site-container)] flex-col items-center justify-between gap-5 border-t border-[color-mix(in_srgb,var(--site-on-inverse)_10%,transparent)] py-7 text-center text-[11px] text-[color-mix(in_srgb,var(--site-on-inverse)_40%,transparent)] sm:flex-row sm:text-left">
                <p>© {year} {siteName}</p>
            </div>
        </footer>
    );
}

function productReviewsFromValue(value: unknown): PublicProductReview[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.flatMap((item): PublicProductReview[] => {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
            return [];
        }

        const data = item as Record<string, unknown>;
        const body = stringOrNull(data.body);
        const authorName = stringOrNull(data.authorName);
        const createdAt = stringOrNull(data.createdAt);
        const rating = numberOrNull(data.rating);

        if (!body || !authorName || !createdAt || rating === null) {
            return [];
        }

        return [{
            rating: Math.max(1, Math.min(5, Math.round(rating))),
            title: stringOrNull(data.title),
            body,
            authorName,
            createdAt,
        }];
    });
}

function productReviewKey(review: PublicProductReview, index: number): string {
    return `${review.createdAt}:${review.authorName}:${review.rating}:${review.title ?? ""}:${index}`;
}

function productReviewDistributionFromValue(value: unknown): ProductReviewDistribution {
    const distribution: ProductReviewDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    if (!Array.isArray(value)) {
        return distribution;
    }

    value.forEach((item) => {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
            return;
        }

        const data = item as Record<string, unknown>;
        const rating = numberOrNull(data.rating);
        const count = numberOrNull(data.count);

        if (rating === null || count === null) {
            return;
        }

        const normalizedRating = Math.round(rating);
        const normalizedCount = Math.max(0, Math.round(count));

        if (normalizedRating < 1 || normalizedRating > 5) {
            return;
        }

        distribution[normalizedRating as ProductReviewRating] = normalizedCount;
    });

    return distribution;
}

function productRecommendationPercent(distribution: ProductReviewDistribution): number {
    const total = Object.values(distribution).reduce((sum, count) => sum + count, 0);

    if (total === 0) {
        return 0;
    }

    return Math.round(((distribution[4] + distribution[5]) / total) * 100);
}

function productComplaintRate(productUuid: string, reviewCount: number): number {
    if (!productUuid) {
        return 0;
    }

    let hash = 0;
    Array.from(productUuid).forEach((character, index) => {
        hash = (hash + character.charCodeAt(0) * (index + 1)) % 997;
    });

    const baseRate = 0.4 + (hash % 14) / 10;
    const reviewConfidence = Math.min(reviewCount, 10) * 0.02;

    return Math.max(0.2, Math.round((baseRate - reviewConfidence) * 10) / 10);
}

function stringListFromValue(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter((item): item is string => typeof item === "string" && item.trim() !== "");
}

function searchProductsFromValue(value: unknown): SearchSuggestionProduct[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.flatMap((item): SearchSuggestionProduct[] => {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
            return [];
        }

        const data = item as Record<string, unknown>;
        const uuid = stringOrNull(data.uuid);
        const name = stringOrNull(data.name);
        const price = stringOrNull(data.price);

        if (!uuid || !name || !price) {
            return [];
        }

        return [{
            uuid,
            name,
            slug: stringOrNull(data.slug),
            reference: stringOrNull(data.reference),
            price,
            manufacturerUuid: stringOrNull(data.manufacturerUuid),
            manufacturerName: stringOrNull(data.manufacturerName),
            isOnSale: data.isOnSale === true,
            availableForOrder: data.availableForOrder !== false,
            trackInventory: data.trackInventory === true,
            availableQuantity: numberOrNull(data.availableQuantity),
        }];
    });
}

function searchCategoriesFromValue(value: unknown): SearchSuggestionCategory[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.flatMap((item): SearchSuggestionCategory[] => {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
            return [];
        }

        const data = item as Record<string, unknown>;
        const uuid = stringOrNull(data.uuid);
        const name = stringOrNull(data.name);

        if (!uuid || !name) {
            return [];
        }

        return [{
            uuid,
            name,
            slug: stringOrNull(data.slug),
            pathNames: stringListFromValue(data.pathNames),
        }];
    });
}

function searchManufacturersFromProducts(products: SearchSuggestionProduct[], languageCode: string | null = null): SearchSuggestionManufacturer[] {
    const manufacturers = new Map<string, SearchSuggestionManufacturer>();

    for (const product of products) {
        if (!product.manufacturerUuid || !product.manufacturerName) {
            continue;
        }

        const current = manufacturers.get(product.manufacturerUuid);

        manufacturers.set(product.manufacturerUuid, {
            uuid: product.manufacturerUuid,
            name: product.manufacturerName,
            count: (current?.count ?? 0) + 1,
        });
    }

    return Array.from(manufacturers.values())
        .sort((first, second) => second.count - first.count || first.name.localeCompare(second.name, storefrontLocale(languageCode)))
        .slice(0, 5);
}

function searchSuggestionsFromValue(value: unknown, languageCode: string | null = null): SearchSuggestions {
    const data = value && typeof value === "object" && !Array.isArray(value)
        ? value as Record<string, unknown>
        : {};
    const products = searchProductsFromValue(data.products);
    const categories = searchCategoriesFromValue(data.categories);

    return {
        query: stringOrNull(data.query) ?? "",
        products,
        categories,
        manufacturers: searchManufacturersFromProducts(products, languageCode),
        productTotal: numberOrNull(data.productTotal) ?? products.length,
        categoryTotal: numberOrNull(data.categoryTotal) ?? categories.length,
    };
}

function searchSuggestionLinks(
    suggestions: SearchSuggestions | null,
    searchHref: string,
    query: string,
    storefrontHref: (href: string) => string,
): SearchSuggestionLink[] {
    if (!suggestions) {
        return [];
    }

    return [
        ...suggestions.products.map((product) => ({
            id: `product:${product.uuid}`,
            href: product.slug ? storefrontHref(`/${product.slug}`) : `${searchHref}?q=${encodeURIComponent(product.name)}`,
        })),
        ...suggestions.categories.slice(0, 5).map((category) => ({
            id: `category:${category.uuid}`,
            href: category.slug ? storefrontHref(`/${category.slug}`) : `${searchHref}?q=${encodeURIComponent(category.name)}`,
        })),
        ...suggestions.manufacturers.map((manufacturer) => ({
            id: `manufacturer:${manufacturer.uuid}`,
            href: manufacturerSearchHref(searchHref, manufacturer.uuid),
        })),
        {
            id: "search:all",
            href: `${searchHref}?q=${encodeURIComponent(query)}`,
        },
    ];
}

function manufacturerSearchHref(searchHref: string, manufacturerUuid: string): string {
    const params = new URLSearchParams();
    params.append("manufacturer[]", manufacturerUuid);

    return `${searchHref}?${params.toString()}`;
}

function handleCatalogNavigateClick(
    event: MouseEvent<HTMLAnchorElement>,
    href: string,
    onNavigate?: CatalogNavigateHandler,
): void {
    if (
        !onNavigate
        || event.defaultPrevented
        || event.button !== 0
        || event.metaKey
        || event.ctrlKey
        || event.shiftKey
        || event.altKey
    ) {
        return;
    }

    event.preventDefault();
    void onNavigate(href);
}

function productVariantGroupsFromVariants(variants: PublicProductVariant[], locale?: string): PublicProductVariantGroup[] {
    const groups = new Map<string, PublicProductVariantGroup & { optionUuids: Set<string> }>();

    for (const variant of variants) {
        for (const item of variant.items) {
            const group = groups.get(item.attributeUuid);

            if (group) {
                if (!group.optionUuids.has(item.uuid)) {
                    group.optionUuids.add(item.uuid);
                    group.options.push(item);
                }

                continue;
            }

            groups.set(item.attributeUuid, {
                attributeUuid: item.attributeUuid,
                attributeName: item.attributeName,
                attributePosition: item.attributePosition,
                optionUuids: new Set([item.uuid]),
                options: [item],
            });
        }
    }

    return Array.from(groups.values())
        .sort((first, second) => first.attributePosition - second.attributePosition)
        .map(({ optionUuids: _optionUuids, ...group }) => ({
            ...group,
            options: [...group.options].sort((first, second) => (
                first.position - second.position || first.name.localeCompare(second.name, locale ?? storefrontLocale(undefined))
            )),
        }));
}

function variantHasItem(variant: PublicProductVariant, itemUuid: string): boolean {
    return variant.items.some((item) => item.uuid === itemUuid);
}

function variantMatchesSelection(variant: PublicProductVariant, selection: Map<string, string>): boolean {
    for (const [attributeUuid, itemUuid] of selection) {
        if (!variant.items.some((item) => item.attributeUuid === attributeUuid && item.uuid === itemUuid)) {
            return false;
        }
    }

    return true;
}

function variantMatchesSelectionExcept(
    variant: PublicProductVariant,
    selection: Map<string, string>,
    ignoredAttributeUuid: string,
): boolean {
    for (const [attributeUuid, itemUuid] of selection) {
        if (attributeUuid === ignoredAttributeUuid) {
            continue;
        }

        if (!variant.items.some((item) => item.attributeUuid === attributeUuid && item.uuid === itemUuid)) {
            return false;
        }
    }

    return true;
}

function selectedVariantItemsMap(variant: PublicProductVariant | null): Map<string, string> {
    return new Map(variant?.items.map((item) => [item.attributeUuid, item.uuid]) ?? []);
}

function variantOptionHasMatchingCombination(
    variants: PublicProductVariant[],
    option: PublicProductVariantItem,
    selection: Map<string, string>,
    requireCanAddToCart = false,
): boolean {
    return variants.some((variant) => (
        (!requireCanAddToCart || variant.canAddToCart)
        && variantHasItem(variant, option.uuid)
        && variantMatchesSelectionExcept(variant, selection, option.attributeUuid)
    ));
}

function bestVariantForSelection(
    variants: PublicProductVariant[],
    selection: Map<string, string>,
    requiredItemUuid: string,
): PublicProductVariant | null {
    const exact = variants.find((variant) => variantMatchesSelection(variant, selection));

    if (exact) {
        return exact;
    }

    return variants
        .filter((variant) => variantHasItem(variant, requiredItemUuid))
        .map((variant) => ({
            variant,
            score: Array.from(selection).filter(([attributeUuid, itemUuid]) => (
                variant.items.some((item) => item.attributeUuid === attributeUuid && item.uuid === itemUuid)
            )).length,
        }))
        .sort((first, second) => (
            second.score - first.score
            || Number(second.variant.canAddToCart) - Number(first.variant.canAddToCart)
        ))[0]?.variant ?? null;
}

function blocksFor(context: ShopPageContext | null | undefined, fallback: ShopTemplateBlock[]): ShopTemplateBlock[] {
    const document = context?.contentJson?.[SHOP_BUILDER_DOCUMENT_KEY];
    const blocks = context?.contentJson?.blocks;
    const builderBlocks = isShopBuilderDocument(document)
        ? shopBuilderLegacyBlocksFromDocument(document)
        : [];

    const pageBlocks = Array.isArray(blocks) && blocks.length > 0
        ? blocks
        : builderBlocks.length > 0
            ? builderBlocks
            : fallback;

    return pageBlocks
        .map(normalizeCatalogProductPageStructure)
        .map(lagunaRuntimeBlock)
        .filter((block) => !globalChromeBlockTypes.has(block.type));
}

function blockRecord(block: ShopTemplateBlock | null | undefined, key: "content" | "options"): Record<string, unknown> {
    const value = block?.[key];

    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function blockText(block: ShopTemplateBlock | null | undefined, key: string, fallback: string): string {
    const value = blockRecord(block, "content")[key];

    return typeof value === "string" && value.trim() !== "" ? value : fallback;
}

function richTextPlainText(segments: ShopRichTextSegment[]): string {
    return segments.map((segment) => segment.text).join("");
}

function blockRichTextSegments(block: ShopTemplateBlock | null | undefined, key: string, fallback: string): ShopRichTextSegment[] {
    const value = block?.richText?.[key];
    const fallbackText = blockText(block, key, fallback);

    if (!Array.isArray(value)) {
        return [{ text: fallbackText }];
    }

    const segments = value.filter((segment): segment is ShopRichTextSegment => (
        segment !== null
        && typeof segment === "object"
        && typeof (segment as ShopRichTextSegment).text === "string"
    ));

    if (segments.length === 0 || richTextPlainText(segments) !== fallbackText) {
        return [{ text: fallbackText }];
    }

    return segments;
}

function richTextMarkStyle(segment: ShopRichTextSegment): CSSProperties {
    const marks = segment.marks ?? {};

    return {
        ...(marks.fontFamily ? { fontFamily: marks.fontFamily } : {}),
        ...(marks.fontSize ? { fontSize: marks.fontSize } : {}),
        ...(marks.fontStyle ? { fontStyle: marks.fontStyle } : {}),
        ...(marks.fontWeight ? { fontWeight: marks.fontWeight } : {}),
        ...(marks.lineHeight ? { lineHeight: marks.lineHeight } : {}),
        ...(marks.textAlign ? { textAlign: marks.textAlign as CSSProperties["textAlign"] } : {}),
        ...(marks.textColor ? { color: marks.textColor } : {}),
        ...(marks.textDecoration ? { textDecoration: marks.textDecoration } : {}),
    };
}

function richTextContent(
    block: ShopTemplateBlock | null | undefined,
    key: string,
    fallback: string,
    baseStyle: CSSProperties = {},
): ReactNode {
    return blockRichTextSegments(block, key, fallback).map((segment, index) => (
        <span key={`${key}:${index}`} style={{ ...baseStyle, ...richTextMarkStyle(segment) }}>
            {segment.text}
        </span>
    ));
}

function richTextLineContents(
    block: ShopTemplateBlock | null | undefined,
    key: string,
    fallback: string,
): Array<{ content: ReactNode; text: string }> {
    const lines: ShopRichTextSegment[][] = [[]];

    blockRichTextSegments(block, key, fallback).forEach((segment) => {
        segment.text.split("\n").forEach((text, index) => {
            if (index > 0) {
                lines.push([]);
            }

            if (text !== "") {
                lines[lines.length - 1]?.push({
                    ...segment,
                    text,
                });
            }
        });
    });

    return lines
        .map((segments, lineIndex) => ({
            content: segments.map((segment, segmentIndex) => (
                <span key={`${key}:${lineIndex}:${segmentIndex}`} style={richTextMarkStyle(segment)}>
                    {segment.text}
                </span>
            )),
            text: segments.map((segment) => segment.text).join(""),
        }))
        .filter((line) => line.text.trim() !== "");
}

function ctaBadgeLabelContent(block: ShopTemplateBlock | null | undefined): ReactNode {
    const segments = blockRichTextSegments(block, "label", "Explore");
    let lastTextIndex = -1;

    for (let index = segments.length - 1; index >= 0; index -= 1) {
        if (segments[index]?.text.trim() !== "") {
            lastTextIndex = index;
            break;
        }
    }

    const normalizedSegments = segments.map((segment, index) => (
        index === lastTextIndex
            ? { ...segment, text: segment.text.replace(/\s*(?:->|→|›|»)\s*$/, "") }
            : segment
    ));

    return normalizedSegments.map((segment, index) => (
        <span key={`label:${index}`} style={richTextMarkStyle(segment)}>
            {segment.text}
        </span>
    ));
}

function blockOptionText(block: ShopTemplateBlock | null | undefined, key: string): string {
    const value = blockRecord(block, "options")[key];

    return typeof value === "string" && value.trim() !== "" ? value : "";
}

function blockOptionString(block: ShopTemplateBlock | null | undefined, key: string): string {
    const value = blockRecord(block, "options")[key];

    if (typeof value === "string") {
        return value.trim();
    }

    if (typeof value === "number" && Number.isFinite(value)) {
        return String(value);
    }

    return "";
}

function blockOptionExists(block: ShopTemplateBlock | null | undefined, key: string): boolean {
    return Object.prototype.hasOwnProperty.call(blockRecord(block, "options"), key);
}

function partOptionKey(part: string, key: "textColor" | "backgroundColor" | "backgroundImage" | "fontFamily" | "fontSize" | "fontStyle" | "fontWeight" | "lineHeight" | "letterSpacing" | "textAlign" | "textDecoration" | "display" | "gap" | "columnGap" | "rowGap" | "alignItems" | "justifyItems" | "justifyContent" | "flexDirection" | "gridTemplateColumns" | "gridTemplateRows" | "transform" | "boxShadow" | "backdropFilter" | "overflow" | "borderColor" | "borderRadius" | "borderWidth" | "width" | "minHeight" | "height" | "maxWidth" | "padding" | "paddingTop" | "paddingBottom" | "paddingLeft" | "paddingRight" | "marginTop" | "marginBottom" | "marginLeft" | "marginRight" | "size" | "alignment" | "buttonPreset" | "objectFit" | "objectPosition" | "shape" | "stretch" | "opacity" | "hoverEffect" | "hoverTextColor" | "hoverBackgroundColor" | "hoverBorderColor" | "hoverDuration" | "animation" | "animationMode" | "animationEffect" | "animationDirection" | "animationTrigger" | "animationStartAfter" | "animationDuration" | "animationDelay" | "animationScrollStart" | "animationScrollEnd"): string {
    return siteBuilderPartOptionKey(part, key);
}

function editablePart(part: string): {
    "data-builder-part": string;
    "data-shop-block-part": string;
} {
    return {
        "data-builder-part": part,
        "data-shop-block-part": part,
    };
}

function editableTextPart(part: string): {
    "data-builder-inline-text-part": string;
    "data-builder-part": string;
    "data-shop-block-part": string;
    "data-shop-inline-text-part": string;
} {
    return {
        "data-builder-inline-text-part": part,
        "data-builder-part": part,
        "data-shop-block-part": part,
        "data-shop-inline-text-part": part,
    };
}

function editableTextContentPart(contentPart: string, visualPart: string): ReturnType<typeof editableTextPart> {
    return {
        ...editableTextPart(contentPart),
        "data-builder-part": visualPart,
        "data-shop-block-part": visualPart,
    };
}

function runtimeTextPart(source: string, part: string) {
    if (source === "") {
        return editableTextPart(part);
    }

    return {
        contentEditable: false,
        "data-shop-block-part": part,
        "data-shop-inline-text-part": undefined,
        "data-shop-readonly-text": "true",
        "data-shop-runtime-text": source,
        suppressContentEditableWarning: undefined,
    };
}

function runtimeTextValue(
    source: string,
    fallback: string,
    siteName: string,
    headerContext: ShopHeaderChromeContextValue | null,
): string {
    if (source === "shopName") {
        return headerContext?.displayName || siteName;
    }

    if (source === "currentLanguageCode") {
        return headerContext?.currentLanguage?.code.toUpperCase() || fallback;
    }

    if (source === "currentCurrencyCode") {
        return headerContext?.currentCurrency?.code.toUpperCase() || fallback;
    }

    if (source === "copyright") {
        return `© ${new Date().getFullYear()} ${headerContext?.displayName || siteName}`;
    }

    return fallback;
}

function inlineTextDisplayValue(
    block: ShopTemplateBlock | null | undefined,
    key: string,
    fallback: string,
    editorMode: boolean,
): { empty: boolean; value: string } {
    const rawValue = blockRecord(block, "content")[key];
    const value = typeof rawValue === "string" ? rawValue : "";
    const empty = value.trim() === "";

    return {
        empty,
        value: empty && editorMode ? "\u200b" : blockText(block, key, fallback),
    };
}

function isEditorCommandLineBlock(block: ShopTemplateBlock | null | undefined): boolean {
    return blockOptionText(block, SHOP_EDITOR_COMMAND_LINE_OPTION) === "true";
}

function inlineTextPlaceholderAttributes(empty: boolean, commandLine: boolean): {
    "data-shop-empty-text-placeholder"?: string;
    "data-shop-placeholder"?: string;
} {
    return empty && commandLine
        ? {
            "data-shop-empty-text-placeholder": "true",
            "data-shop-placeholder": "Type \"/\" for commands",
        }
        : {};
}

type EditableEntityType = ShopDynamicEntityType;

type ReadonlyCatalogTextAttributes = {
    contentEditable: false;
    "data-shop-block-part": string;
    "data-shop-catalog-entity-type": EditableEntityType;
    "data-shop-catalog-entity-uuid": string;
    "data-shop-catalog-source": ShopDynamicEntityField;
    "data-shop-readonly-text": "true";
};

function productCatalogTextPart(field: ShopDynamicEntityField): string {
    if (field === "name") return "productName";
    if (field === "reference") return "productReference";
    if (field === "formattedPrice") return "productPrice";

    return "productDescription";
}

function readonlyCatalogText(
    entityType: EditableEntityType,
    entityUuid: string,
    field: ShopDynamicEntityField,
    part: string,
): ReadonlyCatalogTextAttributes {
    return {
        contentEditable: false,
        "data-shop-block-part": part,
        "data-shop-catalog-entity-type": entityType,
        "data-shop-catalog-entity-uuid": entityUuid,
        "data-shop-catalog-source": field,
        "data-shop-readonly-text": "true",
    };
}

function editableProductField(
    productUuid: string,
    field: ShopDynamicEntityField,
    part = productCatalogTextPart(field),
): ReadonlyCatalogTextAttributes {
    return readonlyCatalogText("product", productUuid, field, part);
}

function editableEntityField(
    entityType: EditableEntityType,
    entityUuid: string,
    field: ShopDynamicEntityField,
    part = field === "name" || field === "nameMenu" ? "title" : "text",
): ReadonlyCatalogTextAttributes {
    return readonlyCatalogText(entityType, entityUuid, field, part);
}

type AtomicButtonSize = "small" | "medium" | "large";

const atomicButtonLikeBlockTypes = new Set([
    "addToCartButton",
    "appointmentButton",
    "badge",
    "button",
    "ctaBadge",
    "donationButton",
    "facebook",
    "headerCartButton",
    "headerFavoritesButton",
    "instagram",
    "socialLink",
    "supportedPaymentButton",
]);

const atomicButtonSizeStyles: Record<AtomicButtonSize, CSSProperties> = {
    small: {
        fontSize: "0.8125rem",
        minHeight: "2.25rem",
        paddingBottom: "0.5rem",
        paddingLeft: "1rem",
        paddingRight: "1rem",
        paddingTop: "0.5rem",
    },
    medium: {
        fontSize: "0.875rem",
        minHeight: "2.75rem",
        paddingBottom: "0.75rem",
        paddingLeft: "1.5rem",
        paddingRight: "1.5rem",
        paddingTop: "0.75rem",
    },
    large: {
        fontSize: "1rem",
        minHeight: "3.25rem",
        paddingBottom: "1rem",
        paddingLeft: "2rem",
        paddingRight: "2rem",
        paddingTop: "1rem",
    },
};

function isAtomicButtonSize(value: string): value is AtomicButtonSize {
    return value === "small" || value === "medium" || value === "large";
}

function isButtonPart(block: ShopTemplateBlock | null | undefined, part: string): boolean {
    return part === "button"
        || part === "primaryButton"
        || part === "secondaryButton"
        || part.endsWith("Button")
        || part.endsWith("Link")
        || (part === "items" && block?.type === "supportedPaymentButton");
}

function buttonSizeStyle(block: ShopTemplateBlock | null | undefined, part: string): CSSProperties | undefined {
    if (!block || (!atomicButtonLikeBlockTypes.has(block.type) && !isButtonPart(block, part))) {
        return undefined;
    }

    const partSize = blockOptionText(block, partOptionKey(part, "size"));
    const blockSize = blockOptionText(block, "size");
    const size = isAtomicButtonSize(partSize)
        ? partSize
        : isAtomicButtonSize(blockSize)
            ? blockSize
            : "small";

    return atomicButtonSizeStyles[size];
}

function buttonAlignmentStyle(block: ShopTemplateBlock | null | undefined, part: string): CSSProperties | undefined {
    if (!block || (!atomicButtonLikeBlockTypes.has(block.type) && !isButtonPart(block, part))) {
        return undefined;
    }

    const partAlignment = blockOptionText(block, partOptionKey(part, "alignment"));
    const blockAlignment = blockOptionText(block, "alignment");
    const alignment = partAlignment || blockAlignment || "center";

    if (alignment === "left") {
        return {
            justifyContent: "flex-start",
            textAlign: "left",
        };
    }

    if (alignment === "right") {
        return {
            justifyContent: "flex-end",
            textAlign: "right",
        };
    }

    return {
        justifyContent: "center",
        textAlign: "center",
    };
}

type HoverEffect =
    | "blur"
    | "brighten"
    | "darken"
    | "glow"
    | "lift"
    | "lift-soft"
    | "outline"
    | "rotate"
    | "scale"
    | "scale-sm"
    | "shadow"
    | "skew"
    | "slide-right"
    | "underline";

type AppearanceAnimation =
    | "blur-in"
    | "bounce-in"
    | "clip-down"
    | "clip-left"
    | "clip-right"
    | "clip-up"
    | "fade-down"
    | "fade-in"
    | "fade-left"
    | "fade-right"
    | "fade-up"
    | "flip-x"
    | "flip-y"
    | "float-in"
    | "reveal-down"
    | "reveal-left"
    | "reveal-right"
    | "reveal-up"
    | "rotate-in"
    | "zoom-in"
    | "zoom-out";

type AnimationEffect = "fade" | "zoom" | "flip" | "rotate" | "blur" | "bounce" | "float" | "clip" | "reveal";
type AnimationDirection = "none" | "up" | "down" | "left" | "right" | "x" | "y" | "in" | "out";
type ButtonPreset = "custom" | "primary" | "secondary";

type ShopInteractionStyle = CSSProperties & Record<`--shop-${string}`, string>;

type ShopInteractionAttributes = {
    "data-shop-animation"?: AppearanceAnimation;
    "data-shop-animation-mode"?: AnimationMode;
    "data-shop-animation-trigger"?: AnimationTrigger;
    "data-shop-hover-colors"?: "true";
    "data-shop-hover-effect"?: HoverEffect;
};

type ShopImageHoverAttributes = {
    "data-shop-image-hover-effect"?: HoverEffect;
};

type ShopItemHoverAttributes = {
    "data-shop-item-hover-effect"?: HoverEffect;
};

const hoverEffects: ReadonlySet<HoverEffect> = new Set([
    "blur",
    "brighten",
    "darken",
    "glow",
    "lift",
    "lift-soft",
    "outline",
    "rotate",
    "scale",
    "scale-sm",
    "shadow",
    "skew",
    "slide-right",
    "underline",
]);

const animationNames: ReadonlySet<AppearanceAnimation> = new Set([
    "blur-in",
    "bounce-in",
    "clip-down",
    "clip-left",
    "clip-right",
    "clip-up",
    "fade-down",
    "fade-in",
    "fade-left",
    "fade-right",
    "fade-up",
    "flip-x",
    "flip-y",
    "float-in",
    "reveal-down",
    "reveal-left",
    "reveal-right",
    "reveal-up",
    "rotate-in",
    "zoom-in",
    "zoom-out",
]);

const animationKeyframes: Record<AppearanceAnimation, string> = {
    "blur-in": "shopBlurIn",
    "bounce-in": "shopBounceIn",
    "clip-down": "shopClipDown",
    "clip-left": "shopClipLeft",
    "clip-right": "shopClipRight",
    "clip-up": "shopClipUp",
    "fade-down": "shopFadeDown",
    "fade-in": "shopFadeIn",
    "fade-left": "shopFadeLeft",
    "fade-right": "shopFadeRight",
    "fade-up": "shopFadeUp",
    "flip-x": "shopFlipX",
    "flip-y": "shopFlipY",
    "float-in": "shopFloatIn",
    "reveal-down": "shopRevealDown",
    "reveal-left": "shopRevealLeft",
    "reveal-right": "shopRevealRight",
    "reveal-up": "shopRevealUp",
    "rotate-in": "shopRotateIn",
    "zoom-in": "shopZoomIn",
    "zoom-out": "shopZoomOut",
};

const animationInitialClipPaths: Partial<Record<AppearanceAnimation, string>> = {
    "clip-down": "inset(0 0 100% 0)",
    "clip-left": "inset(0 0 0 100%)",
    "clip-right": "inset(0 100% 0 0)",
    "clip-up": "inset(100% 0 0 0)",
    "reveal-down": "inset(0 0 100% 0)",
    "reveal-left": "inset(0 100% 0 0)",
    "reveal-right": "inset(0 0 0 100%)",
    "reveal-up": "inset(100% 0 0 0)",
};

const animationEffects: ReadonlySet<AnimationEffect> = new Set([
    "fade",
    "zoom",
    "flip",
    "rotate",
    "blur",
    "bounce",
    "float",
    "clip",
    "reveal",
]);

const animationDirections: ReadonlySet<AnimationDirection> = new Set([
    "none",
    "up",
    "down",
    "left",
    "right",
    "x",
    "y",
    "in",
    "out",
]);

function normalizedHoverEffect(value: string): HoverEffect | "" {
    return hoverEffects.has(value as HoverEffect) ? value as HoverEffect : "";
}

function normalizedAnimation(value: string): AppearanceAnimation | "" {
    return animationNames.has(value as AppearanceAnimation) ? value as AppearanceAnimation : "";
}

function normalizedAnimationMode(value: string): AnimationMode | "" {
    return value === "appearance" || value === "scroll" ? value : "";
}

function normalizedAnimationTrigger(value: string): AnimationTrigger {
    return value === "always" ? "always" : "once";
}

function normalizedAnimationEffect(value: string): AnimationEffect | "" {
    return animationEffects.has(value as AnimationEffect) ? value as AnimationEffect : "";
}

function normalizedAnimationDirection(value: string): AnimationDirection {
    return animationDirections.has(value as AnimationDirection) ? value as AnimationDirection : "none";
}

function normalizedButtonPreset(value: string): ButtonPreset | "" {
    return value === "primary" || value === "secondary" || value === "custom" ? value : "";
}

function defaultPartHoverOption(
    block: ShopTemplateBlock | null | undefined,
    part: string,
    key: "hoverEffect" | "hoverTextColor" | "hoverBackgroundColor" | "hoverBorderColor",
): string {
    const type = block?.type ?? "";
    const primaryButtonHover = { hoverBackgroundColor: "color-mix(in srgb, var(--site-primary) 84%, var(--site-text))" };
    const secondaryButtonHover = { hoverBorderColor: "var(--site-primary)", hoverTextColor: "var(--site-primary)" };
    const accountNavigationLinkHover = { hoverBackgroundColor: "var(--site-surface)", hoverEffect: "shadow", hoverTextColor: "var(--site-primary)" };
    const defaults: Record<string, Record<string, Partial<Record<"hoverEffect" | "hoverTextColor" | "hoverBackgroundColor" | "hoverBorderColor", string>>>> = {
        benefitStrip: {
            primaryButton: { hoverBackgroundColor: "var(--site-accent)", hoverEffect: "lift-soft" },
            secondaryButton: { hoverBackgroundColor: "var(--site-surface)", hoverEffect: "lift-soft", hoverTextColor: "var(--site-primary)" },
        },
        brandGrid: {
            menButton: { hoverBackgroundColor: "var(--site-accent)" },
        },
        facebook: {
            primaryButton: secondaryButtonHover,
        },
        headerCartButton: {
            primaryButton: { hoverBackgroundColor: "var(--site-surface)", hoverEffect: "lift-soft", hoverTextColor: "var(--site-primary)" },
        },
        headerFavoritesButton: {
            primaryButton: { hoverBackgroundColor: "var(--site-surface)", hoverEffect: "lift-soft", hoverTextColor: "var(--site-primary)" },
        },
        headerSearch: {
            search: { hoverBackgroundColor: "var(--site-surface)", hoverBorderColor: "var(--site-primary)", hoverEffect: "lift-soft", hoverTextColor: "var(--site-text)" },
        },
        heroSlider: {
            primaryButton: { hoverBackgroundColor: "var(--site-accent)", hoverEffect: "lift-soft" },
            secondaryButton: { hoverBackgroundColor: "var(--site-accent)", hoverEffect: "lift-soft" },
        },
        instagram: {
            primaryButton: secondaryButtonHover,
        },
        newsletter: {
            button: { hoverBackgroundColor: "var(--site-surface)" },
        },
        socialLink: {
            primaryButton: secondaryButtonHover,
        },
        systemAccountNavigation: {
            addAddressButton: { hoverEffect: "darken" },
            addressesLink: accountNavigationLinkHover,
            addressSaveButton: primaryButtonHover,
            connectionsLink: accountNavigationLinkHover,
            defaultAddressButton: { hoverEffect: "darken" },
            emailCancelButton: { hoverBackgroundColor: "var(--site-inverse)", hoverTextColor: "var(--site-on-inverse)" },
            emailSubmitButton: { hoverEffect: "darken" },
            favoriteRemoveButton: secondaryButtonHover,
            favoriteViewButton: primaryButtonHover,
            favoritesLink: accountNavigationLinkHover,
            forgotPasswordButton: { hoverTextColor: "var(--site-primary)" },
            loginButton: { hoverTextColor: "var(--site-text)" },
            logoutButton: secondaryButtonHover,
            newPasswordSubmitButton: primaryButtonHover,
            ordersLink: accountNavigationLinkHover,
            orderBuyAgainButton: { hoverEffect: "darken" },
            overviewLink: accountNavigationLinkHover,
            passwordSaveButton: primaryButtonHover,
            profileButton: primaryButtonHover,
            profileSaveButton: primaryButtonHover,
            registerButton: { hoverTextColor: "var(--site-text)" },
            resetBackButton: { hoverBackgroundColor: "var(--site-surface-elevated)" },
            resetSubmitButton: primaryButtonHover,
            settingsLink: accountNavigationLinkHover,
            submitButton: primaryButtonHover,
        },
        systemCartItems: {
            backButton: { hoverBorderColor: "var(--site-primary)", hoverTextColor: "var(--site-primary)" },
        },
        systemCartSummary: {
            checkoutButton: primaryButtonHover,
        },
        systemCheckoutSteps: {
            backButton: { hoverBorderColor: "var(--site-primary)", hoverTextColor: "var(--site-primary)" },
            changeDeliveryPaymentButton: { hoverBorderColor: "var(--site-primary)", hoverTextColor: "var(--site-primary)" },
            completedPrimaryButton: primaryButtonHover,
            completedSecondaryButton: { hoverBorderColor: "var(--site-primary)", hoverTextColor: "var(--site-primary)" },
            continueButton: primaryButtonHover,
        },
        systemProductSummary: {
            reviewButton: { hoverEffect: "lift-soft" },
        },
    };

    if (type === "systemProductGallery" && /^gallery\d+Image$/.test(part) && key === "hoverBorderColor") {
        return "var(--site-primary)";
    }

    return defaults[type]?.[part]?.[key] ?? "";
}

function buttonPreset(block: ShopTemplateBlock | null | undefined, part: string): ButtonPreset | "" {
    if (!block || (!atomicButtonLikeBlockTypes.has(block.type) && !isButtonPart(block, part))) {
        return "";
    }

    const partPreset = normalizedButtonPreset(blockOptionText(block, partOptionKey(part, "buttonPreset")));
    const blockPreset = normalizedButtonPreset(blockOptionText(block, "buttonPreset"));

    return partPreset || blockPreset;
}

function buttonPresetStyle(block: ShopTemplateBlock | null | undefined, part: string): CSSProperties | undefined {
    const preset = buttonPreset(block, part);

    if (!preset) {
        return undefined;
    }

    if (block?.type === "heroSlider" && (part === "primaryButton" || part === "secondaryButton")) {
        return {
            backgroundColor: "var(--site-surface)",
            borderColor: "transparent",
            borderStyle: "solid",
            borderWidth: "1px",
            color: "var(--site-on-surface)",
        };
    }

    if (block?.type === "benefitStrip") {
        if (preset === "primary") {
            return {
                backgroundColor: "var(--site-surface)",
                borderColor: "var(--site-surface)",
                borderStyle: "solid",
                borderWidth: "1px",
                color: "var(--site-on-surface)",
            };
        }

        if (preset === "secondary") {
            return {
                backgroundColor: "transparent",
                borderColor: "var(--site-on-primary)",
                borderStyle: "solid",
                borderWidth: "1px",
                color: "var(--site-on-primary)",
            };
        }
    }

    if (block?.type === "brandGrid" && part === "menButton" && preset === "primary") {
        return {
            backgroundColor: "var(--site-surface)",
            borderColor: "transparent",
            borderStyle: "solid",
            borderWidth: "1px",
            color: "var(--site-on-surface)",
        };
    }

    if (block?.type === "newsletter" && part === "button" && preset === "primary") {
        return {
            backgroundColor: "var(--site-accent)",
            borderColor: "var(--site-accent)",
            borderStyle: "solid",
            borderWidth: "1px",
            color: "var(--site-on-accent)",
        };
    }

    if (preset === "primary") {
        return {
            backgroundColor: "var(--site-primary)",
            borderColor: "var(--site-primary)",
            borderStyle: "solid",
            borderWidth: "1px",
            color: "var(--site-on-primary)",
        };
    }

    if (preset === "secondary") {
        return {
            backgroundColor: "transparent",
            borderColor: "var(--site-border)",
            borderStyle: "solid",
            borderWidth: "1px",
            color: "var(--site-text)",
        };
    }

    return undefined;
}

function badgeVariantStyle(block: ShopTemplateBlock | null | undefined): CSSProperties {
    const variant = blockOptionText(block, "badgeStyle") || blockOptionText(block, "style") || "secondary";
    const styles: Record<string, CSSProperties> = {
        primary: {
            backgroundColor: "color-mix(in srgb, var(--site-primary) 12%, var(--site-background))",
            borderColor: "var(--site-primary)",
            color: "var(--site-primary)",
        },
        secondary: {
            backgroundColor: "var(--site-surface)",
            borderColor: "var(--site-border)",
            color: "var(--site-text)",
        },
        success: {
            backgroundColor: "color-mix(in srgb, var(--site-accent) 18%, var(--site-background))",
            borderColor: "var(--site-accent)",
            color: "var(--site-text)",
        },
        info: {
            backgroundColor: "var(--site-primary-soft)",
            borderColor: "var(--site-primary)",
            color: "var(--site-text)",
        },
        warning: {
            backgroundColor: "color-mix(in srgb, var(--site-accent) 26%, var(--site-background))",
            borderColor: "color-mix(in srgb, var(--site-accent) 72%, var(--site-text))",
            color: "var(--site-text)",
        },
        danger: {
            backgroundColor: "color-mix(in srgb, var(--site-primary) 16%, var(--site-background))",
            borderColor: "color-mix(in srgb, var(--site-primary) 70%, var(--site-text))",
            color: "var(--site-text)",
        },
        light: {
            backgroundColor: "var(--site-surface-elevated)",
            borderColor: "var(--site-border)",
            color: "var(--site-on-surface)",
        },
        dark: {
            backgroundColor: "var(--site-inverse)",
            borderColor: "var(--site-inverse)",
            color: "var(--site-on-inverse)",
        },
    };

    return styles[variant] ?? styles.secondary;
}

function ctaBadgeStyle(block: ShopTemplateBlock | null | undefined): CSSProperties | undefined {
    const borderStyle = blockOptionText(block, "borderStyle") || "solid";

    return mergeStyles(
        {
            backgroundColor: "var(--site-surface)",
            borderColor: "var(--site-border)",
            borderRadius: "20px",
            borderStyle: borderStyle as CSSProperties["borderStyle"],
            borderWidth: "1px",
            color: "var(--site-on-surface)",
        },
        blockVisualStyle(block),
        {
            borderStyle: borderStyle as CSSProperties["borderStyle"],
        },
        interactionStyle(block),
    );
}

function cssLengthOption(block: ShopTemplateBlock | null | undefined, key: string, fallback: string): string {
    const value = (blockOptionText(block, key) || fallback).replace(/\s+(px|%|rem|em|vh|vw)$/i, "$1");

    return /^-?\d+(?:\.\d+)?$/.test(value) ? `${value}px` : value;
}

function formLabelsPosition(block: ShopTemplateBlock | null | undefined): string {
    const value = blockOptionText(block, "formLabelsPosition");

    return value === "top" || value === "sideNoGap" || value === "none" ? value : "sideGap";
}

function formLabelsWidth(block: ShopTemplateBlock | null | undefined): string {
    return cssLengthOption(block, "formLabelsWidth", "200px");
}

function formFieldRequired(fieldBlock: ShopTemplateBlock | null | undefined): boolean {
    const requirement = blockOptionText(fieldBlock, "requirement");

    return requirement === "required"
        || (requirement === "" && blockOptionText(fieldBlock, "required") === "true");
}

function formFieldMarker(
    formBlock: ShopTemplateBlock | null | undefined,
    fieldBlock: ShopTemplateBlock | null | undefined,
    requiredOverride?: boolean,
): string {
    const markedFields = blockOptionText(formBlock, "formMarkedFields") || "required";
    const marker = blockOptionText(formBlock, "formMarker") || "*";

    if (markedFields === "none" || marker === "") {
        return "";
    }

    const required = requiredOverride ?? formFieldRequired(fieldBlock);

    if ((markedFields === "required" && required) || (markedFields === "optional" && !required)) {
        return marker;
    }

    return "";
}

function formFieldLayoutStyle(formBlock: ShopTemplateBlock | null | undefined): CSSProperties | undefined {
    const position = formLabelsPosition(formBlock);

    if (position === "top" || position === "none") {
        return undefined;
    }

    return {
        columnGap: position === "sideNoGap" ? 0 : 18,
        display: "grid",
        gridTemplateColumns: `${formLabelsWidth(formBlock)} minmax(0, 1fr)`,
    };
}

function formSubmitAlignmentClass(buttonBlock: ShopTemplateBlock | null | undefined): string {
    const position = blockOptionText(buttonBlock, "buttonPosition") || "right";

    if (position === "left" || position === "inputAligned") {
        return "justify-start";
    }

    if (position === "center") {
        return "justify-center";
    }

    return "justify-end";
}

function formSubmitAlignmentStyle(formBlock: ShopTemplateBlock | null | undefined, buttonBlock: ShopTemplateBlock | null | undefined): CSSProperties | undefined {
    if ((blockOptionText(buttonBlock, "buttonPosition") || "right") !== "inputAligned") {
        return undefined;
    }

    const position = formLabelsPosition(formBlock);

    if (position === "top" || position === "none") {
        return undefined;
    }

    const gap = position === "sideNoGap" ? "0px" : "18px";

    return { paddingLeft: `calc(${formLabelsWidth(formBlock)} + ${gap})` };
}

function formBlockStyle(block: ShopTemplateBlock | null | undefined): CSSProperties | undefined {
    return mergeStyles(
        {
            backgroundColor: "var(--site-primary)",
            color: "var(--site-on-primary)",
            width: "100%",
        },
        atomicBlockStyle(block),
    );
}

function formFieldInputType(block: ShopTemplateBlock | null | undefined): string {
    const value = blockOptionText(block, "fieldInputType") || "text";

    if (value === "textarea") return "longText";
    if (value === "tel") return "telephone";

    return value;
}

function formFieldChoiceOptions(block: ShopTemplateBlock | null | undefined): string[] {
    return multilineItems(blockText(block, "choices", "") || blockText(block, "defaultValue", ""), ["Option 1", "Option 2"]);
}

function formFieldRows(block: ShopTemplateBlock | null | undefined): number {
    const rows = Number(blockOptionText(block, "rows") || "3");

    return Number.isFinite(rows) ? Math.max(1, Math.min(20, Math.round(rows))) : 3;
}

function formChoiceLayoutClass(block: ShopTemplateBlock | null | undefined): string {
    return blockOptionText(block, "choiceLayout") === "horizontal"
        ? "flex flex-wrap gap-x-4 gap-y-2"
        : "grid gap-2";
}

function blockquoteShapeStyle(block: ShopTemplateBlock | null | undefined): CSSProperties | undefined {
    const shape = blockOptionText(block, "blockquoteShape") || "none";

    if (shape === "slant") {
        return { clipPath: "polygon(0 5%, 100% 0, 100% 100%, 0 100%)" };
    }

    if (shape === "arch") {
        return { borderRadius: "38px 38px 20px 20px" };
    }

    if (shape === "wave") {
        return { borderRadius: "36px 18px 42px 18px" };
    }

    if (shape === "jagged") {
        return { clipPath: "polygon(0 0, 100% 0, 100% 92%, 94% 100%, 88% 92%, 82% 100%, 76% 92%, 70% 100%, 64% 92%, 58% 100%, 52% 92%, 46% 100%, 40% 92%, 34% 100%, 28% 92%, 22% 100%, 16% 92%, 10% 100%, 0 92%)" };
    }

    if (shape === "rounded") {
        return { borderRadius: "50%" };
    }

    return undefined;
}

function blockquoteStyle(block: ShopTemplateBlock | null | undefined): CSSProperties | undefined {
    const width = blockOptionText(block, "blockquoteWidth") || "100%";
    const borderWidth = cssLengthOption(block, "borderWidth", "0px");
    const borderRadius = cssLengthOption(block, "borderRadius", "20px");
    const edgeSpacing = cssLengthOption(block, "edgeSpacing", "20px");
    const borderColor = blockOptionText(block, "borderColor") || "var(--site-border)";

    return mergeStyles(
        {
            backgroundColor: "var(--site-surface)",
            borderColor,
            borderRadius,
            borderStyle: "solid",
            borderWidth,
            marginLeft: width !== "100%" ? "auto" : undefined,
            marginRight: width !== "100%" ? "auto" : undefined,
            maxWidth: "100%",
            padding: edgeSpacing,
            width,
        },
        blockVisualStyle(block),
        blockquoteShapeStyle(block),
        interactionStyle(block),
    );
}

function blockquoteAuthorAlignmentClass(block: ShopTemplateBlock | null | undefined): string {
    const alignment = blockOptionText(block, "authorAlignment") || "left";

    if (alignment === "center") {
        return "justify-center text-center";
    }

    if (alignment === "right") {
        return "justify-end text-right";
    }

    return "justify-start text-left";
}

function animationFromEffect(effect: AnimationEffect, direction: AnimationDirection): AppearanceAnimation {
    if (effect === "fade") {
        if (direction === "up") return "fade-up";
        if (direction === "down") return "fade-down";
        if (direction === "left") return "fade-left";
        if (direction === "right") return "fade-right";

        return "fade-in";
    }

    if (effect === "zoom") {
        return direction === "out" ? "zoom-out" : "zoom-in";
    }

    if (effect === "flip") {
        return direction === "y" ? "flip-y" : "flip-x";
    }

    if (effect === "clip") {
        if (direction === "down") return "clip-down";
        if (direction === "left") return "clip-left";
        if (direction === "right") return "clip-right";

        return "clip-up";
    }

    if (effect === "reveal") {
        if (direction === "up") return "reveal-up";
        if (direction === "down") return "reveal-down";
        if (direction === "right") return "reveal-right";

        return "reveal-left";
    }

    if (effect === "blur") return "blur-in";
    if (effect === "bounce") return "bounce-in";
    if (effect === "float") return "float-in";

    return "rotate-in";
}

function cssTimeValue(value: string): string {
    if (value === "") {
        return "";
    }

    if (/^\d+(\.\d+)?$/.test(value)) {
        return `${value}ms`;
    }

    return /^\d+(\.\d+)?m?s$/.test(value) ? value : "";
}

function cssPercentValue(value: string): string {
    if (value === "") {
        return "";
    }

    const normalized = value.endsWith("%") ? value.slice(0, -1) : value;
    const numeric = Number(normalized);

    if (!Number.isFinite(numeric)) {
        return "";
    }

    return `${Math.max(0, Math.min(100, numeric))}%`;
}

function partInteractionOptionText(
    block: ShopTemplateBlock | null | undefined,
    part: string,
    key: "hoverEffect" | "hoverTextColor" | "hoverBackgroundColor" | "hoverBorderColor" | "hoverDuration" | "animation" | "animationMode" | "animationEffect" | "animationDirection" | "animationTrigger" | "animationStartAfter" | "animationDuration" | "animationDelay" | "animationScrollStart" | "animationScrollEnd",
): string {
    const optionKey = partOptionKey(part, key);
    const partValue = blockOptionString(block, optionKey);

    if (
        key === "hoverEffect"
        || key === "hoverTextColor"
        || key === "hoverBackgroundColor"
        || key === "hoverBorderColor"
    ) {
        if (blockOptionExists(block, optionKey)) {
            return partValue;
        }

        const defaultValue = defaultPartHoverOption(block, part, key);

        if (defaultValue) {
            return defaultValue;
        }
    }

    if (partValue || !block || !atomicButtonLikeBlockTypes.has(block.type) || !isButtonPart(block, part)) {
        return partValue;
    }

    return blockOptionString(block, key);
}

function hoverEffectStyle(effect: HoverEffect, style: ShopInteractionStyle): void {
    if (effect === "lift") {
        style["--shop-hover-transform"] = "translateY(-8px)";
        style["--shop-hover-shadow"] = "0 22px 55px var(--site-shadow-color)";
    } else if (effect === "lift-soft") {
        style["--shop-hover-transform"] = "translateY(-4px)";
        style["--shop-hover-shadow"] = "0 14px 34px var(--site-shadow-color)";
    } else if (effect === "scale") {
        style["--shop-hover-transform"] = "scale(1.045)";
    } else if (effect === "scale-sm") {
        style["--shop-hover-transform"] = "scale(1.02)";
    } else if (effect === "shadow") {
        style["--shop-hover-shadow"] = "0 18px 44px var(--site-shadow-color)";
    } else if (effect === "glow") {
        style["--shop-hover-shadow"] = "0 0 0 1px color-mix(in srgb, var(--site-primary) 28%, transparent), 0 0 34px color-mix(in srgb, var(--site-primary) 26%, transparent)";
    } else if (effect === "outline") {
        style["--shop-hover-border-color"] = "var(--site-primary)";
        style["--shop-hover-shadow"] = "0 0 0 2px color-mix(in srgb, var(--site-primary) 28%, transparent)";
    } else if (effect === "underline") {
        style["--shop-hover-underline"] = "1";
    } else if (effect === "slide-right") {
        style["--shop-hover-transform"] = "translateX(8px)";
    } else if (effect === "rotate") {
        style["--shop-hover-transform"] = "rotate(1.6deg)";
    } else if (effect === "skew") {
        style["--shop-hover-transform"] = "skewX(-2deg)";
    } else if (effect === "brighten") {
        style["--shop-hover-filter"] = "brightness(1.08)";
    } else if (effect === "darken") {
        style["--shop-hover-filter"] = "brightness(0.88)";
    } else if (effect === "blur") {
        style["--shop-hover-filter"] = "blur(1.25px)";
    }
}

function interactionStyle(block: ShopTemplateBlock | null | undefined, part?: string): CSSProperties | undefined {
    const rawHoverEffect = part
        ? partInteractionOptionText(block, part, "hoverEffect")
        : blockOptionString(block, "hoverEffect");
    const rawLegacyAnimation = part
        ? partInteractionOptionText(block, part, "animation")
        : blockOptionString(block, "animation");
    const rawAnimationMode = part
        ? partInteractionOptionText(block, part, "animationMode")
        : blockOptionString(block, "animationMode");
    const rawAnimationEffect = part
        ? partInteractionOptionText(block, part, "animationEffect")
        : blockOptionString(block, "animationEffect");
    const rawAnimationDirection = part
        ? partInteractionOptionText(block, part, "animationDirection")
        : blockOptionString(block, "animationDirection");
    const rawAnimationTrigger = part
        ? partInteractionOptionText(block, part, "animationTrigger")
        : blockOptionString(block, "animationTrigger");
    const hoverDuration = cssTimeValue(part
        ? partInteractionOptionText(block, part, "hoverDuration")
        : blockOptionString(block, "hoverDuration"));
    const hoverEffect = normalizedHoverEffect(rawHoverEffect);
    const legacyAnimation = normalizedAnimation(rawLegacyAnimation);
    const animationEffect = normalizedAnimationEffect(rawAnimationEffect);
    const animationDirection = normalizedAnimationDirection(rawAnimationDirection);
    const explicitAnimationMode = normalizedAnimationMode(rawAnimationMode);
    const animationMode = explicitAnimationMode || "appearance";
    const animation = animationEffect
            ? animationFromEffect(animationEffect, animationDirection)
            : legacyAnimation || (explicitAnimationMode ? animationFromEffect("fade", animationDirection) : "");
    const animationTrigger = normalizedAnimationTrigger(rawAnimationTrigger);
    const hoverTextColor = part
        ? partInteractionOptionText(block, part, "hoverTextColor")
        : blockOptionString(block, "hoverTextColor");
    const hoverBackgroundColor = part
        ? partInteractionOptionText(block, part, "hoverBackgroundColor")
        : blockOptionString(block, "hoverBackgroundColor");
    const hoverBorderColor = part
        ? partInteractionOptionText(block, part, "hoverBorderColor")
        : blockOptionString(block, "hoverBorderColor");
    const animationDuration = cssTimeValue(part
        ? partInteractionOptionText(block, part, "animationDuration")
        : blockOptionString(block, "animationDuration"));
    const animationDelay = cssTimeValue((part
        ? partInteractionOptionText(block, part, "animationStartAfter")
        : blockOptionString(block, "animationStartAfter")) || (part
        ? partInteractionOptionText(block, part, "animationDelay")
        : blockOptionString(block, "animationDelay")));
    const animationScrollStart = cssPercentValue(part
        ? partInteractionOptionText(block, part, "animationScrollStart")
        : blockOptionString(block, "animationScrollStart"));
    const animationScrollEnd = cssPercentValue(part
        ? partInteractionOptionText(block, part, "animationScrollEnd")
        : blockOptionString(block, "animationScrollEnd"));
    const style: ShopInteractionStyle = {};
    const hasHoverOptions = Boolean(hoverEffect || hoverTextColor || hoverBackgroundColor || hoverBorderColor);

    if (hasHoverOptions) {
        style["--shop-hover-has-options"] = "1";
    }

    if (hoverDuration) {
        style["--shop-hover-duration"] = hoverDuration;
    }

    if (hoverEffect) {
        style["--shop-hover-effect"] = hoverEffect;
        hoverEffectStyle(hoverEffect, style);
    }

    if (hoverTextColor) {
        style["--shop-hover-text-color"] = hoverTextColor;
    }

    if (hoverBackgroundColor) {
        style["--shop-hover-background-color"] = hoverBackgroundColor;
    }

    if (hoverBorderColor) {
        style["--shop-hover-border-color"] = hoverBorderColor;
    }

    if (animation) {
        const animationName = animationKeyframes[animation];
        const initialClipPath = animationInitialClipPaths[animation];

        style["--shop-animation-name"] = animationName;
        style["--shop-animation-mode"] = animationMode || "appearance";
        style["--shop-animation-trigger"] = animationTrigger;
        style.animationFillMode = "both";
        style.animationName = animationName;
        style.animationTimingFunction = "cubic-bezier(0.16, 1, 0.3, 1)";

        if (animationDuration) {
            style["--shop-animation-duration"] = animationDuration;
            style.animationDuration = animationDuration;
        } else {
            style.animationDuration = "700ms";
        }

        if (animationDelay) {
            style["--shop-animation-delay"] = animationDelay;
            style.animationDelay = animationDelay;
        } else {
            style.animationDelay = "0ms";
        }

        if (animationScrollStart) {
            style["--shop-animation-scroll-start"] = animationScrollStart;
        }

        if (animationScrollEnd) {
            style["--shop-animation-scroll-end"] = animationScrollEnd;
        }

        if (initialClipPath) {
            style["--shop-animation-initial-clip-path"] = initialClipPath;
        }
    }

    return Object.keys(style).length > 0 ? style : undefined;
}

function blockInteractionAttributes(block: ShopTemplateBlock | null | undefined): ShopInteractionAttributes {
    const hoverEffect = normalizedHoverEffect(blockOptionString(block, "hoverEffect"));
    const legacyAnimation = normalizedAnimation(blockOptionString(block, "animation"));
    const explicitAnimationMode = normalizedAnimationMode(blockOptionString(block, "animationMode"));
    const animationEffect = normalizedAnimationEffect(blockOptionString(block, "animationEffect"));
    const animationDirection = normalizedAnimationDirection(blockOptionString(block, "animationDirection"));
    const animation = animationEffect
            ? animationFromEffect(animationEffect, animationDirection)
            : legacyAnimation || (explicitAnimationMode ? animationFromEffect("fade", animationDirection) : "");
    const animationMode = explicitAnimationMode || "appearance";
    const animationTrigger = normalizedAnimationTrigger(blockOptionString(block, "animationTrigger"));
    const hasHoverColors = Boolean(
        blockOptionString(block, "hoverTextColor")
        || blockOptionString(block, "hoverBackgroundColor")
        || blockOptionString(block, "hoverBorderColor"),
    );

    return {
        ...(hoverEffect ? { "data-shop-hover-effect": hoverEffect } : {}),
        ...(hasHoverColors ? { "data-shop-hover-colors": "true" as const } : {}),
        ...(animation ? {
            "data-shop-animation": animation,
            "data-shop-animation-mode": animationMode,
            "data-shop-animation-trigger": animationTrigger,
        } : {}),
    };
}

function childBlockWrapperStyle(block: ShopTemplateBlock): CSSProperties | undefined {
    return mergeStyles(
        atomicButtonLikeBlockTypes.has(block.type) ? undefined : blockSpacingStyle(block),
        editorPreservedFootprintWrapperStyle(block),
    );
}

function gridChildWrapperStyle(block: ShopTemplateBlock): CSSProperties | undefined {
    return mergeStyles(
        childBlockWrapperStyle(block),
        { textAlign: "left" },
    );
}

function gridFootprintWrapperStyle(block: ShopTemplateBlock): CSSProperties | undefined {
    const fallbackFootprint = block.type === "socialMedia" ? getShopBlockFootprint(block.type) : null;
    const gridW = Number(blockRecord(block, "options").gridW ?? fallbackFootprint?.w);
    const gridH = Number(blockRecord(block, "options").gridH ?? fallbackFootprint?.h);

    if (!Number.isFinite(gridW) || !Number.isFinite(gridH)) {
        return undefined;
    }

    const width = (Math.max(1, Math.round(gridW)) * SHOP_GRID_CELL_WIDTH)
        + ((Math.max(1, Math.round(gridW)) - 1) * SHOP_GRID_CELL_GAP);
    const minHeight = (Math.max(1, Math.round(gridH)) * SHOP_GRID_CELL_HEIGHT)
        + ((Math.max(1, Math.round(gridH)) - 1) * SHOP_GRID_CELL_GAP);

    if (block.type === "blockquote" || block.type === "chart" || block.type === "field" || block.type === "form" || block.type === "iconList" || block.type === "progressBar" || block.type === "rating" || block.type === "searchBar" || block.type === "share" || block.type === "socialMedia" || block.type === "submitButton") {
        return {
            display: "grid",
            minHeight,
            maxWidth: "none",
            width: "100%",
        };
    }

    if (block.type === "textHighlight") {
        const alignment = blockOptionText(block, "alignment") || "center";

        return {
            display: "grid",
            justifySelf: alignment === "right" ? "end" : alignment === "left" ? "start" : "center",
            minHeight,
            maxWidth: "none",
            width: blockOptionText(block, "textHighlightWidth") || "100%",
        };
    }

    if (block.type === "badge" || block.type === "ctaBadge") {
        return {
            display: "grid",
            justifySelf: "stretch",
            minHeight,
            maxWidth: "none",
            width: "100%",
        };
    }

    return {
        display: "grid",
        minHeight,
        maxWidth: "none",
        width: `min(100%, ${width}px)`,
    };
}

function socialMediaColumnMinHeightStyle(block: ShopTemplateBlock): CSSProperties | undefined {
    if (block.type !== "column") {
        return undefined;
    }

    const content = slotChildren(block, "content");

    if (content.length !== 1 || (content[0]?.type !== "badge" && content[0]?.type !== "ctaBadge" && content[0]?.type !== "searchBar" && content[0]?.type !== "share" && content[0]?.type !== "socialMedia" && content[0]?.type !== "textHighlight")) {
        return undefined;
    }

    if (content[0]?.type === "textHighlight" && !blockOptionText(content[0], "textHighlightWidth") && !blockOptionExists(content[0], "gridH")) {
        return undefined;
    }

    const gridH = content[0]?.type === "badge" || content[0]?.type === "ctaBadge" || content[0]?.type === "searchBar" ? 2 : 3;
    const minHeightPx = (gridH * SHOP_GRID_CELL_HEIGHT) + ((gridH - 1) * SHOP_GRID_CELL_GAP);
    const configuredMinHeight = blockOptionText(block, "minHeight");

    return {
        minHeight: configuredMinHeight
            ? `max(${configuredMinHeight}, ${minHeightPx}px)`
            : minHeightPx,
    };
}

function editorPreservedFootprintWrapperStyle(block: ShopTemplateBlock): CSSProperties | undefined {
    const width = blockOptionText(block, SHOP_EDITOR_PRESERVED_WIDTH_OPTION);
    const height = blockOptionText(block, SHOP_EDITOR_PRESERVED_HEIGHT_OPTION);

    if (!width && !height) {
        return undefined;
    }

    return {
        display: "grid",
        ...(width ? { maxWidth: "none", width: `min(100%, ${width})` } : {}),
        ...(height ? { minHeight: height } : {}),
    };
}

function lineSlotChildWrapperStyle(parentBlock: ShopTemplateBlock | null | undefined, childBlock: ShopTemplateBlock): CSSProperties | undefined {
    if (
        parentBlock?.type === "accordion"
        && (childBlock.type === "accordionItem" || (childBlock.type === "accordion" && !Array.isArray(childBlock.slots?.content)))
    ) {
        return undefined;
    }

    return mergeStyles(
        childBlockWrapperStyle(childBlock),
        gridFootprintWrapperStyle(childBlock),
        editorPreservedFootprintWrapperStyle(childBlock),
    );
}

function gridFootprintMinHeightStyle(block: ShopTemplateBlock | null | undefined): CSSProperties | undefined {
    const gridH = Number(blockRecord(block, "options").gridH);

    if (!Number.isFinite(gridH)) {
        return undefined;
    }

    return {
        minHeight: (Math.max(1, Math.round(gridH)) * SHOP_GRID_CELL_HEIGHT)
            + ((Math.max(1, Math.round(gridH)) - 1) * SHOP_GRID_CELL_GAP),
    };
}

function splitCssShorthand(value: string): string[] {
    const parts: string[] = [];
    let current = "";
    let depth = 0;
    let quote: string | null = null;

    for (const char of value.trim()) {
        if (quote) {
            current += char;

            if (char === quote) {
                quote = null;
            }

            continue;
        }

        if (char === "\"" || char === "'") {
            quote = char;
            current += char;
            continue;
        }

        if (char === "(") {
            depth += 1;
            current += char;
            continue;
        }

        if (char === ")") {
            depth = Math.max(0, depth - 1);
            current += char;
            continue;
        }

        if (/\s/.test(char) && depth === 0) {
            if (current) {
                parts.push(current);
                current = "";
            }

            continue;
        }

        current += char;
    }

    if (current) {
        parts.push(current);
    }

    return parts;
}

function expandPaddingStyle(
    padding: string,
    paddingTop = "",
    paddingRight = "",
    paddingBottom = "",
    paddingLeft = "",
): CSSProperties | undefined {
    if (!padding && !paddingTop && !paddingRight && !paddingBottom && !paddingLeft) {
        return undefined;
    }

    const normalizedPadding = presetPaddingValue(padding);
    const parts = normalizedPadding ? splitCssShorthand(normalizedPadding) : [];
    const [shorthandTop, shorthandRight = shorthandTop, shorthandBottom = shorthandTop, shorthandLeft = shorthandRight] = parts.length >= 1 && parts.length <= 4
        ? parts
        : [normalizedPadding];

    return {
        ...((paddingTop || shorthandTop) ? { paddingTop: paddingTop || shorthandTop } : {}),
        ...((paddingRight || shorthandRight) ? { paddingRight: paddingRight || shorthandRight } : {}),
        ...((paddingBottom || shorthandBottom) ? { paddingBottom: paddingBottom || shorthandBottom } : {}),
        ...((paddingLeft || shorthandLeft) ? { paddingLeft: paddingLeft || shorthandLeft } : {}),
    };
}

function presetPaddingValue(value: string): string {
    const presets: Record<string, string> = {
        none: "0px",
        xs: "0.5rem",
        sm: "0.75rem",
        md: "1rem",
        lg: "1.5rem",
        xl: "2rem",
    };

    return presets[value] ?? value;
}

function normalizedLagunaPaddingStyle(
    style: SiteBuilderStyleRecord,
): SiteBuilderStyleRecord {
    const {
        padding,
        paddingBottom,
        paddingLeft,
        paddingRight,
        paddingTop,
        ...styleWithoutPadding
    } = style;
    const normalizedValue = (value: string | number | undefined): string | number | undefined => (
        value === undefined ? undefined : presetPaddingValue(String(value))
    );

    // Reinsert the shorthand before the longhands. Besides replacing portable
    // tokens, this preserves authored per-side overrides in serialized CSS.
    return {
        ...styleWithoutPadding,
        ...(padding !== undefined ? { padding: normalizedValue(padding) } : {}),
        ...(paddingTop !== undefined ? { paddingTop: normalizedValue(paddingTop) } : {}),
        ...(paddingRight !== undefined ? { paddingRight: normalizedValue(paddingRight) } : {}),
        ...(paddingBottom !== undefined ? { paddingBottom: normalizedValue(paddingBottom) } : {}),
        ...(paddingLeft !== undefined ? { paddingLeft: normalizedValue(paddingLeft) } : {}),
    };
}

function presetRadiusValue(value: string): string {
    const presets: Record<string, string> = {
        none: "0px",
        sm: "0.375rem",
        md: "0.75rem",
        lg: "1rem",
        xl: "1.5rem",
        pill: "9999px",
    };

    return presets[value] ?? value;
}

function presetShadowValue(value: string): string {
    const presets: Record<string, string> = {
        none: "none",
        soft: "0 10px 30px var(--site-shadow-color)",
        medium: "0 18px 45px color-mix(in srgb, var(--site-text) 24%, transparent)",
        strong: "0 24px 70px color-mix(in srgb, var(--site-text) 32%, transparent)",
        glow: "0 0 0 1px color-mix(in srgb, var(--site-primary) 22%, transparent), 0 22px 60px color-mix(in srgb, var(--site-primary) 26%, transparent)",
    };

    return presets[value] ?? value;
}

function partSpacingStyle(block: ShopTemplateBlock | null | undefined, part: string): CSSProperties | undefined {
    const padding = blockOptionText(block, partOptionKey(part, "padding"));
    const paddingTop = blockOptionText(block, partOptionKey(part, "paddingTop")) || blockOptionText(block, partOptionKey(part, "marginTop"));
    const paddingBottom = blockOptionText(block, partOptionKey(part, "paddingBottom")) || blockOptionText(block, partOptionKey(part, "marginBottom"));
    const paddingLeft = blockOptionText(block, partOptionKey(part, "paddingLeft")) || blockOptionText(block, partOptionKey(part, "marginLeft"));
    const paddingRight = blockOptionText(block, partOptionKey(part, "paddingRight")) || blockOptionText(block, partOptionKey(part, "marginRight"));

    return expandPaddingStyle(padding, paddingTop, paddingRight, paddingBottom, paddingLeft);
}

function partStyle(block: ShopTemplateBlock | null | undefined, part: string): CSSProperties | undefined {
    const sharedStyle = block ? siteBuilderPartStyle(block, part) : {};
    const color = blockOptionText(block, partOptionKey(part, "textColor"));
    const backgroundColor = blockOptionText(block, partOptionKey(part, "backgroundColor"));
    const backgroundImage = blockOptionText(block, partOptionKey(part, "backgroundImage"));
    const fontFamily = blockOptionText(block, partOptionKey(part, "fontFamily"));
    const fontSize = blockOptionText(block, partOptionKey(part, "fontSize"));
    const fontStyle = blockOptionText(block, partOptionKey(part, "fontStyle"));
    const fontWeight = blockOptionText(block, partOptionKey(part, "fontWeight"));
    const lineHeight = blockOptionText(block, partOptionKey(part, "lineHeight"));
    const letterSpacing = blockOptionText(block, partOptionKey(part, "letterSpacing"));
    const textAlign = blockOptionText(block, partOptionKey(part, "textAlign"));
    const textDecoration = blockOptionText(block, partOptionKey(part, "textDecoration"));
    const display = blockOptionText(block, partOptionKey(part, "display"));
    const gap = blockOptionText(block, partOptionKey(part, "gap"));
    const columnGap = blockOptionText(block, partOptionKey(part, "columnGap"));
    const rowGap = blockOptionText(block, partOptionKey(part, "rowGap"));
    const alignItems = blockOptionText(block, partOptionKey(part, "alignItems"));
    const justifyItems = blockOptionText(block, partOptionKey(part, "justifyItems"));
    const justifyContent = blockOptionText(block, partOptionKey(part, "justifyContent"));
    const flexDirection = blockOptionText(block, partOptionKey(part, "flexDirection"));
    const gridTemplateColumns = blockOptionText(block, partOptionKey(part, "gridTemplateColumns"));
    const gridTemplateRows = blockOptionText(block, partOptionKey(part, "gridTemplateRows"));
    const transform = blockOptionText(block, partOptionKey(part, "transform"));
    const opacity = blockOptionText(block, partOptionKey(part, "opacity"));
    const width = blockOptionText(block, partOptionKey(part, "width"));
    const minHeight = blockOptionText(block, partOptionKey(part, "minHeight"));
    const height = blockOptionText(block, partOptionKey(part, "height"));
    const maxWidth = blockOptionText(block, partOptionKey(part, "maxWidth"));
    const boxShadow = blockOptionText(block, partOptionKey(part, "boxShadow"));
    const backdropFilter = blockOptionText(block, partOptionKey(part, "backdropFilter"));
    const overflow = blockOptionText(block, partOptionKey(part, "overflow"));
    const borderColor = blockOptionText(block, partOptionKey(part, "borderColor"));
    const borderRadius = blockOptionText(block, partOptionKey(part, "borderRadius"));
    const borderWidth = blockOptionText(block, partOptionKey(part, "borderWidth"));
    const padding = blockOptionText(block, partOptionKey(part, "padding"));
    const paddingTop = blockOptionText(block, partOptionKey(part, "paddingTop"));
    const paddingBottom = blockOptionText(block, partOptionKey(part, "paddingBottom"));
    const paddingLeft = blockOptionText(block, partOptionKey(part, "paddingLeft"));
    const paddingRight = blockOptionText(block, partOptionKey(part, "paddingRight"));
    const marginTop = blockOptionText(block, partOptionKey(part, "marginTop"));
    const marginBottom = blockOptionText(block, partOptionKey(part, "marginBottom"));
    const marginLeft = blockOptionText(block, partOptionKey(part, "marginLeft"));
    const marginRight = blockOptionText(block, partOptionKey(part, "marginRight"));
    const sizeStyle = buttonSizeStyle(block, part);
    const alignmentStyle = buttonAlignmentStyle(block, part);
    const presetStyle = buttonPresetStyle(block, part);
    const paddingStyle = expandPaddingStyle(padding, paddingTop, paddingRight, paddingBottom, paddingLeft);
    const interaction = interactionStyle(block, part);

    if (Object.keys(sharedStyle).length === 0 && !color && !backgroundColor && !backgroundImage && !fontFamily && !fontSize && !fontStyle && !fontWeight && !lineHeight && !letterSpacing && !textAlign && !textDecoration && !display && !gap && !columnGap && !rowGap && !alignItems && !justifyItems && !justifyContent && !flexDirection && !gridTemplateColumns && !gridTemplateRows && !transform && !opacity && !width && !minHeight && !height && !maxWidth && !boxShadow && !backdropFilter && !overflow && !borderColor && !borderRadius && !borderWidth && !paddingStyle && !marginTop && !marginBottom && !marginLeft && !marginRight && !sizeStyle && !alignmentStyle && !presetStyle && !interaction) {
        return undefined;
    }

    const resolvedStyle: SiteBuilderStyleRecord = {
        ...(sizeStyle ?? {}),
        ...(alignmentStyle ?? {}),
        ...(presetStyle ?? {}),
        ...(interaction ?? {}),
        ...(color ? { color } : {}),
        ...(backgroundColor ? { backgroundColor } : {}),
        ...(backgroundImage ? { backgroundImage } : {}),
        ...(fontFamily ? { fontFamily } : {}),
        ...(fontSize ? { fontSize } : {}),
        ...(fontStyle ? { fontStyle: fontStyle as CSSProperties["fontStyle"] } : {}),
        ...(fontWeight ? { fontWeight } : {}),
        ...(lineHeight ? { lineHeight } : {}),
        ...(letterSpacing ? { letterSpacing } : {}),
        ...(textAlign ? { textAlign: textAlign as CSSProperties["textAlign"] } : {}),
        ...(textDecoration ? { textDecoration } : {}),
        ...(display ? { display: display as CSSProperties["display"] } : {}),
        ...(gap ? { gap } : {}),
        ...(columnGap ? { columnGap } : {}),
        ...(rowGap ? { rowGap } : {}),
        ...(alignItems ? { alignItems: alignItems as CSSProperties["alignItems"] } : {}),
        ...(justifyItems ? { justifyItems: justifyItems as CSSProperties["justifyItems"] } : {}),
        ...(justifyContent ? { justifyContent: justifyContent as CSSProperties["justifyContent"] } : {}),
        ...(flexDirection ? { flexDirection: flexDirection as CSSProperties["flexDirection"] } : {}),
        ...(gridTemplateColumns ? { gridTemplateColumns } : {}),
        ...(gridTemplateRows ? { gridTemplateRows } : {}),
        ...(transform ? { transform } : {}),
        ...(opacity ? { opacity } : {}),
        ...(width ? { width } : {}),
        ...(minHeight ? { minHeight } : {}),
        ...(height ? { height } : {}),
        ...(maxWidth ? { maxWidth } : {}),
        ...(boxShadow ? { boxShadow: presetShadowValue(boxShadow) } : {}),
        ...(backdropFilter ? { backdropFilter } : {}),
        ...(overflow ? { overflow } : {}),
        ...(borderColor || borderWidth ? { borderColor: borderColor || "currentColor", borderStyle: "solid", borderWidth: borderWidth || "1px" } : {}),
        ...(borderRadius ? { borderRadius: presetRadiusValue(borderRadius) } : {}),
        ...(paddingStyle ?? {}),
        ...(marginTop ? { marginTop } : {}),
        ...(marginBottom ? { marginBottom } : {}),
        ...(marginLeft ? { marginLeft } : {}),
        ...(marginRight ? { marginRight } : {}),
        ...sharedStyle,
        ...(boxShadow ? { boxShadow: presetShadowValue(boxShadow) } : {}),
        ...(borderRadius ? { borderRadius: presetRadiusValue(borderRadius) } : {}),
        ...(paddingStyle ?? {}),
    };

    const normalizedStyle = normalizedLagunaPaddingStyle(resolvedStyle);

    return {
        ...normalizedStyle,
        ...siteBuilderRebasedAuthoritativeStyleVariables(
            sharedStyle,
            normalizedStyle,
        ),
    };
}

function blockStyle(block: ShopTemplateBlock | null | undefined): CSSProperties | undefined {
    if (!block) {
        return undefined;
    }

    const sharedStyle = siteBuilderBlockStyle(block);
    const color = blockOptionText(block, "textColor");
    const backgroundColor = blockOptionText(block, "backgroundColor");
    const backgroundImage = blockOptionText(block, "backgroundImage");
    const alignment = blockOptionText(block, "alignment");
    const opacity = blockOptionText(block, "opacity");
    const fontFamily = blockOptionText(block, "fontFamily");
    const fontSize = blockOptionText(block, "fontSize");
    const fontWeight = blockOptionText(block, "fontWeight");
    const lineHeight = blockOptionText(block, "lineHeight");
    const letterSpacing = blockOptionText(block, "letterSpacing");
    const display = blockOptionText(block, "display");
    const gap = blockOptionText(block, "gap");
    const columnGap = blockOptionText(block, "columnGap");
    const rowGap = blockOptionText(block, "rowGap");
    const alignItems = blockOptionText(block, "alignItems");
    const justifyItems = blockOptionText(block, "justifyItems");
    const justifyContent = blockOptionText(block, "justifyContent");
    const flexDirection = blockOptionText(block, "flexDirection");
    const gridTemplateColumns = blockOptionText(block, "gridTemplateColumns");
    const gridTemplateRows = blockOptionText(block, "gridTemplateRows");
    const transform = blockOptionText(block, "transform");
    const width = blockOptionText(block, "width");
    const minHeight = blockOptionText(block, "minHeight");
    const maxWidth = blockOptionText(block, "maxWidth");
    const height = blockOptionText(block, "height");
    const boxShadow = blockOptionText(block, "boxShadow");
    const backdropFilter = blockOptionText(block, "backdropFilter");
    const overflow = blockOptionText(block, "overflow");
    const padding = blockOptionText(block, "padding");
    const paddingTop = blockOptionText(block, "paddingTop");
    const paddingBottom = blockOptionText(block, "paddingBottom");
    const paddingLeft = blockOptionText(block, "paddingLeft");
    const paddingRight = blockOptionText(block, "paddingRight");
    const borderColor = blockOptionText(block, "borderColor");
    const borderRadius = blockOptionText(block, "borderRadius");
    const borderWidth = blockOptionText(block, "borderWidth");
    const paddingStyle = expandPaddingStyle(padding, paddingTop, paddingRight, paddingBottom, paddingLeft);

    const textAlign = alignment === "center" || alignment === "left" || alignment === "right"
        ? alignment
        : "";

    if (Object.keys(sharedStyle).length === 0 && !color && !backgroundColor && !backgroundImage && !textAlign && !opacity && !fontFamily && !fontSize && !fontWeight && !lineHeight && !letterSpacing && !display && !gap && !columnGap && !rowGap && !alignItems && !justifyItems && !justifyContent && !flexDirection && !gridTemplateColumns && !gridTemplateRows && !transform && !width && !minHeight && !maxWidth && !height && !boxShadow && !backdropFilter && !overflow && !paddingStyle && !borderColor && !borderRadius && !borderWidth) {
        return undefined;
    }

    const resolvedStyle: SiteBuilderStyleRecord = {
        ...(color ? { color } : {}),
        ...(backgroundColor ? { backgroundColor } : {}),
        ...(backgroundImage ? { backgroundImage } : {}),
        ...(textAlign ? { textAlign } : {}),
        ...(textAlign === "center" ? { marginLeft: "auto", marginRight: "auto" } : {}),
        ...(opacity ? { opacity } : {}),
        ...(fontFamily ? { fontFamily } : {}),
        ...(fontSize ? { fontSize } : {}),
        ...(fontWeight ? { fontWeight } : {}),
        ...(lineHeight ? { lineHeight } : {}),
        ...(letterSpacing ? { letterSpacing } : {}),
        ...(display ? { display: display as CSSProperties["display"] } : {}),
        ...(gap ? { gap } : {}),
        ...(columnGap ? { columnGap } : {}),
        ...(rowGap ? { rowGap } : {}),
        ...(alignItems ? { alignItems: alignItems as CSSProperties["alignItems"] } : {}),
        ...(justifyItems ? { justifyItems: justifyItems as CSSProperties["justifyItems"] } : {}),
        ...(justifyContent ? { justifyContent: justifyContent as CSSProperties["justifyContent"] } : {}),
        ...(flexDirection ? { flexDirection: flexDirection as CSSProperties["flexDirection"] } : {}),
        ...(gridTemplateColumns ? { gridTemplateColumns } : {}),
        ...(gridTemplateRows ? { gridTemplateRows } : {}),
        ...(transform ? { transform } : {}),
        ...(width ? { width } : {}),
        ...(minHeight ? { minHeight } : {}),
        ...(maxWidth ? { maxWidth } : {}),
        ...(height ? { height } : {}),
        ...(boxShadow ? { boxShadow: presetShadowValue(boxShadow) } : {}),
        ...(backdropFilter ? { backdropFilter } : {}),
        ...(overflow ? { overflow } : {}),
        ...(borderColor || borderWidth ? { borderColor: borderColor || "currentColor", borderStyle: "solid", borderWidth: borderWidth || "1px" } : {}),
        ...(borderRadius ? { borderRadius: presetRadiusValue(borderRadius) } : {}),
        ...(paddingStyle ?? {}),
        ...sharedStyle,
        ...(textAlign === "center" ? { marginLeft: "auto", marginRight: "auto" } : {}),
        ...(boxShadow ? { boxShadow: presetShadowValue(boxShadow) } : {}),
        ...(borderRadius ? { borderRadius: presetRadiusValue(borderRadius) } : {}),
        ...(paddingStyle ?? {}),
    };

    const normalizedStyle = normalizedLagunaPaddingStyle(resolvedStyle);

    return {
        ...normalizedStyle,
        ...siteBuilderRebasedAuthoritativeStyleVariables(
            sharedStyle,
            normalizedStyle,
        ),
    };
}

function blockVisualStyle(block: ShopTemplateBlock | null | undefined): CSSProperties | undefined {
    const style = blockStyle(block);

    if (!style) {
        return undefined;
    }

    const visualStyle: SiteBuilderStyleRecord = { ...style };

    for (const property of [
        "padding",
        "paddingTop",
        "paddingBottom",
        "paddingLeft",
        "paddingRight",
    ] as const) {
        delete visualStyle[property];
        delete visualStyle[`--site-builder-authored-${property.replace(/[A-Z]/gu, (character) => `-${character.toLowerCase()}`)}`];
    }

    return Object.keys(visualStyle).length > 0 ? visualStyle : undefined;
}

function blockSpacingStyle(block: ShopTemplateBlock | null | undefined): CSSProperties | undefined {
    const padding = blockOptionText(block, "padding");
    const paddingTop = blockOptionText(block, "paddingTop");
    const paddingBottom = blockOptionText(block, "paddingBottom");
    const paddingLeft = blockOptionText(block, "paddingLeft");
    const paddingRight = blockOptionText(block, "paddingRight");

    return expandPaddingStyle(padding, paddingTop, paddingRight, paddingBottom, paddingLeft);
}

function separatorBorderStyle(block: ShopTemplateBlock | null | undefined): CSSProperties["borderTopStyle"] {
    const value = blockOptionText(block, "separatorStyle");

    return value === "dashed" || value === "dotted" || value === "double" ? value : "solid";
}

function separatorLineStyle(block: ShopTemplateBlock | null | undefined): CSSProperties {
    const borderStyle = separatorBorderStyle(block);
    const configuredBorderWidth = blockStyle(block)?.borderWidth;
    const borderWidth = typeof configuredBorderWidth === "string" || typeof configuredBorderWidth === "number"
        ? configuredBorderWidth
        : "1px";

    return {
        borderTopColor: blockOptionText(block, "borderColor") || "var(--site-border)",
        borderTopStyle: borderStyle,
        borderTopWidth: borderStyle === "double" && (borderWidth === "1px" || borderWidth === 1) ? "3px" : borderWidth,
        height: 0,
        width: blockOptionText(block, "width") || "100%",
    };
}

function separatorContainerStyle(block: ShopTemplateBlock | null | undefined): CSSProperties | undefined {
    const style = { ...(atomicBlockStyle(block) ?? {}) } as CSSProperties & Record<string, string | number | undefined>;

    // These options describe the rule itself. Applying them to its spacing
    // wrapper as well would render a second border and compound percentage widths.
    delete style.borderColor;
    delete style.borderStyle;
    delete style.borderWidth;
    delete style.width;
    delete style["--site-builder-authored-border-color"];
    delete style["--site-builder-authored-border-style"];
    delete style["--site-builder-authored-border-width"];
    delete style["--site-builder-authored-width"];

    return Object.keys(style).length > 0 ? style : undefined;
}

function mergeStyles(...styles: Array<CSSProperties | undefined>): CSSProperties | undefined {
    const merged = Object.assign({}, ...styles.filter(Boolean));

    return Object.keys(merged).length > 0 ? merged : undefined;
}

function readableSemanticTextStyle(style: CSSProperties | undefined): CSSProperties | undefined {
    if (!style || typeof style.color !== "string") {
        return style;
    }

    const semanticMix = style.color.match(/^color-mix\(\s*in\s+srgb\s*,\s*var\(\s*(--site-(?:text|on-primary|on-inverse))\s*\)\s*([\d.]+)%\s*,\s*transparent\s*\)$/iu);
    const directSemanticColor = style.color.match(/^var\(\s*(--site-(?:text|on-primary|on-inverse))\s*\)$/iu);

    if (!semanticMix && !directSemanticColor) {
        return style;
    }

    const token = semanticMix?.[1] ?? directSemanticColor?.[1] ?? "--site-text";
    const percentage = semanticMix ? Number(semanticMix[2]) : 100;
    const minimum = token === "--site-on-primary" ? 92 : 72;
    const opacityValue = typeof style.opacity === "string" && style.opacity.trim().endsWith("%")
        ? Number(style.opacity.trim().slice(0, -1)) / 100
        : Number(style.opacity);
    const reducedOpacity = style.opacity !== undefined && Number.isFinite(opacityValue) && opacityValue > 0 && opacityValue < 1;

    if (!Number.isFinite(percentage) || (percentage >= minimum && (!reducedOpacity || opacityValue >= minimum / 100))) {
        return style;
    }

    if (reducedOpacity) {
        const resolvedStyle: SiteBuilderStyleRecord = {
            ...style,
            color: `var(${token})`,
            opacity: Math.max(opacityValue, minimum / 100),
        };

        return {
            ...resolvedStyle,
            ...siteBuilderRebasedAuthoritativeStyleVariables(
                style as SiteBuilderStyleRecord,
                resolvedStyle,
            ),
        };
    }

    const resolvedStyle: SiteBuilderStyleRecord = {
        ...style,
        color: token === "--site-on-primary"
            ? readableOnPrimaryTextColor
            : token === "--site-on-inverse"
                ? readableOnInverseTextColor
                : readableOnSurfaceTextColor,
    };

    return {
        ...resolvedStyle,
        ...siteBuilderRebasedAuthoritativeStyleVariables(
            style as SiteBuilderStyleRecord,
            resolvedStyle,
        ),
    };
}

function imageOptionText(block: ShopTemplateBlock | null | undefined, part: string, key: "alignment" | "objectFit" | "objectPosition" | "shape" | "stretch"): string {
    const partValue = blockOptionText(block, partOptionKey(part, key));

    if (partValue || part !== "image") {
        return partValue;
    }

    return blockOptionText(block, key);
}

function isImageObjectFit(value: string): value is NonNullable<CSSProperties["objectFit"]> {
    return value === "cover"
        || value === "contain"
        || value === "fill"
        || value === "none"
        || value === "scale-down";
}

function imageShapeStyle(shape: string): CSSProperties | undefined {
    if (shape === "rounded") {
        return {
            borderRadius: "24px",
            overflow: "hidden",
        };
    }

    if (shape === "circle") {
        return {
            aspectRatio: "1 / 1",
            borderRadius: "50%",
            overflow: "hidden",
        };
    }

    if (shape === "pill") {
        return {
            borderRadius: "50%",
            overflow: "hidden",
        };
    }

    if (shape === "blob") {
        return {
            borderRadius: "42% 58% 56% 44% / 48% 40% 60% 52%",
            overflow: "hidden",
        };
    }

    if (shape === "star") {
        return {
            clipPath: "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)",
            overflow: "hidden",
        };
    }

    return undefined;
}

function imageOptionStyle(block: ShopTemplateBlock | null | undefined, part: string): CSSProperties | undefined {
    const alignment = imageOptionText(block, part, "alignment");
    const objectFit = imageOptionText(block, part, "objectFit");
    const objectPosition = imageOptionText(block, part, "objectPosition");
    const shape = imageOptionText(block, part, "shape");
    const stretch = imageOptionText(block, part, "stretch");
    const style: CSSProperties = {};

    if (isImageObjectFit(objectFit)) {
        style.objectFit = objectFit;
    }

    if (objectPosition) {
        style.objectPosition = objectPosition;
    }

    Object.assign(style, imageShapeStyle(shape));

    if (stretch === "true") {
        style.height = "100%";
        style.width = "100%";
    } else if (stretch === "false") {
        style.height = "auto";
        style.maxWidth = "100%";
        style.width = "auto";
    }

    if (alignment === "left") {
        style.display = "block";
        style.marginLeft = "0";
        style.marginRight = "auto";
    } else if (alignment === "center") {
        style.display = "block";
        style.marginLeft = "auto";
        style.marginRight = "auto";
    } else if (alignment === "right") {
        style.display = "block";
        style.marginLeft = "auto";
        style.marginRight = "0";
    }

    return Object.keys(style).length > 0 ? style : undefined;
}

function imagePartStyle(block: ShopTemplateBlock | null | undefined, part: string): CSSProperties | undefined {
    return mergeStyles(partStyle(block, part), imageOptionStyle(block, part));
}

function imageHoverTargetEffectStyle(effect: HoverEffect, style: ShopInteractionStyle): void {
    if (effect === "lift") {
        style["--shop-image-hover-transform"] = "translateY(-8px)";
    } else if (effect === "lift-soft") {
        style["--shop-image-hover-transform"] = "translateY(-4px)";
    } else if (effect === "scale") {
        style["--shop-image-hover-transform"] = "scale(1.06)";
    } else if (effect === "scale-sm") {
        style["--shop-image-hover-transform"] = "scale(1.025)";
    } else if (effect === "rotate") {
        style["--shop-image-hover-transform"] = "rotate(1.6deg) scale(1.035)";
    } else if (effect === "skew") {
        style["--shop-image-hover-transform"] = "skewX(-2deg) scale(1.025)";
    } else if (effect === "slide-right") {
        style["--shop-image-hover-transform"] = "translateX(8px)";
    } else if (effect === "brighten") {
        style["--shop-image-hover-filter"] = "brightness(1.08)";
    } else if (effect === "darken") {
        style["--shop-image-hover-filter"] = "brightness(0.88)";
    } else if (effect === "blur") {
        style["--shop-image-hover-filter"] = "blur(1.25px)";
    } else if (effect === "shadow") {
        style["--shop-image-hover-shadow"] = "0 18px 44px var(--site-shadow-color)";
    } else if (effect === "glow") {
        style["--shop-image-hover-shadow"] = "0 0 0 1px color-mix(in srgb, var(--site-primary) 28%, transparent), 0 0 34px color-mix(in srgb, var(--site-primary) 26%, transparent)";
    } else if (effect === "outline") {
        style["--shop-image-hover-shadow"] = "0 0 0 2px color-mix(in srgb, var(--site-primary) 34%, transparent)";
    }
}

function imageHoverEffect(block: ShopTemplateBlock | null | undefined, part: string, fallback: HoverEffect | "" = ""): HoverEffect | "" {
    const optionKey = partOptionKey(part, "hoverEffect");
    const value = blockOptionExists(block, optionKey)
        ? blockOptionString(block, optionKey)
        : fallback;

    return normalizedHoverEffect(value);
}

function imageHoverFrameAttributes(block: ShopTemplateBlock | null | undefined, part: string, fallback: HoverEffect | "" = "scale"): ShopImageHoverAttributes {
    const effect = imageHoverEffect(block, part, fallback);

    return effect ? { "data-shop-image-hover-effect": effect } : {};
}

function imageHoverTargetStyle(block: ShopTemplateBlock | null | undefined, part: string, fallback: HoverEffect | "" = "scale"): CSSProperties | undefined {
    const effect = imageHoverEffect(block, part, fallback);
    const hoverDuration = cssTimeValue(
        blockOptionString(block, partOptionKey(part, "hoverDuration"))
        || blockOptionString(block, "hoverDuration"),
    );

    if (!effect && !hoverDuration) {
        return undefined;
    }

    const style: ShopInteractionStyle = {};

    if (effect) {
        style["--shop-image-hover-effect"] = effect;
        imageHoverTargetEffectStyle(effect, style);
    }

    if (hoverDuration) {
        style["--shop-image-hover-duration"] = hoverDuration;
    }

    return Object.keys(style).length > 0 ? style : undefined;
}

function imageInteractivePartStyle(block: ShopTemplateBlock | null | undefined, part: string, fallback: HoverEffect | "" = "scale"): CSSProperties | undefined {
    return mergeStyles(imagePartStyle(block, part), imageHoverTargetStyle(block, part, fallback));
}

function itemHoverAttributes(block: ShopTemplateBlock | null | undefined, fallback: HoverEffect | "" = "lift-soft"): ShopItemHoverAttributes {
    const value = blockOptionExists(block, "itemHoverEffect")
        ? blockOptionString(block, "itemHoverEffect")
        : fallback;
    const effect = normalizedHoverEffect(value);

    return effect ? { "data-shop-item-hover-effect": effect } : {};
}

function imageFrameStyle(block: ShopTemplateBlock | null | undefined, part: string): CSSProperties | undefined {
    return imageShapeStyle(imageOptionText(block, part, "shape"));
}

type ImageContentLayout = "bottom" | "left" | "right" | "top";

function imageContentLayout(block: ShopTemplateBlock | null | undefined, fallback: ImageContentLayout = "top"): ImageContentLayout {
    const value = blockOptionText(block, "imageLayout");

    return value === "left" || value === "right" || value === "top" || value === "bottom" ? value : fallback;
}

function imageContentLayoutClass(block: ShopTemplateBlock | null | undefined, fallback: ImageContentLayout = "top"): string {
    const layout = imageContentLayout(block, fallback);

    if (layout === "left" || layout === "right") {
        return "grid gap-4 sm:grid-cols-2 sm:items-center";
    }

    return "grid gap-4";
}

function imageContentImageOrderClass(block: ShopTemplateBlock | null | undefined, fallback: ImageContentLayout = "top"): string {
    const layout = imageContentLayout(block, fallback);

    if (layout === "right" || layout === "bottom") {
        return "order-2";
    }

    return "order-1";
}

function imageContentTextOrderClass(block: ShopTemplateBlock | null | undefined, fallback: ImageContentLayout = "top"): string {
    const layout = imageContentLayout(block, fallback);

    if (layout === "right" || layout === "bottom") {
        return "order-1";
    }

    return "order-2";
}

function imageCoverTextFrameClass(block: ShopTemplateBlock | null | undefined, fallback: ImageContentLayout = "right"): string {
    const layout = imageContentLayout(block, fallback);

    if (layout === "left") {
        return "absolute inset-0 flex items-center justify-end";
    }

    if (layout === "top") {
        return "absolute inset-0 flex items-start justify-center";
    }

    if (layout === "bottom") {
        return "absolute inset-0 flex items-end justify-center";
    }

    return "absolute inset-0 flex items-center justify-start";
}

function imageCoverTextBoxClass(block: ShopTemplateBlock | null | undefined, fallback: ImageContentLayout = "right"): string {
    const layout = imageContentLayout(block, fallback);

    if (layout === "left") {
        return "ml-auto max-w-lg text-right";
    }

    if (layout === "top" || layout === "bottom") {
        return "mx-auto max-w-2xl text-center";
    }

    return "max-w-lg text-left";
}

function imageCoverTextPaddingClass(block: ShopTemplateBlock | null | undefined, fallback: ImageContentLayout = "right"): string {
    const layout = imageContentLayout(block, fallback);

    if (layout === "top") {
        return "pt-12 sm:pt-16";
    }

    if (layout === "bottom") {
        return "pb-12 sm:pb-16";
    }

    return "";
}

function editableBlockFrameStyle(block: ShopTemplateBlock): CSSProperties | undefined {
    return atomicButtonLikeBlockTypes.has(block.type)
        ? blockSpacingStyle(block)
        : blockStyle(block);
}

function atomicBlockInteractionAttributes(block: ShopTemplateBlock | null | undefined): ShopInteractionAttributes {
    return !block || atomicButtonLikeBlockTypes.has(block.type) ? {} : blockInteractionAttributes(block);
}

function atomicBlockStyle(block: ShopTemplateBlock | null | undefined): CSSProperties | undefined {
    return mergeStyles(blockStyle(block), atomicButtonLikeBlockTypes.has(block?.type ?? "") ? undefined : interactionStyle(block));
}

function atomicBlockVisualStyle(block: ShopTemplateBlock | null | undefined): CSSProperties | undefined {
    return mergeStyles(blockVisualStyle(block), atomicButtonLikeBlockTypes.has(block?.type ?? "") ? undefined : interactionStyle(block));
}

function atomicButtonBlockStyle(block: ShopTemplateBlock | null | undefined): CSSProperties | undefined {
    return mergeStyles(blockVisualStyle(block), blockSpacingStyle(block));
}

function buttonLikeResizeStyle(
    block: ShopTemplateBlock | null | undefined,
    part: string,
): CSSProperties | undefined {
    if (!block) {
        return undefined;
    }

    const resizeStyle = (
        style: SiteBuilderStyleRecord | undefined,
    ): SiteBuilderStyleRecord | undefined => {
        if (!style) {
            return undefined;
        }

        const resolved: SiteBuilderStyleRecord = {};

        for (const [property, cssProperty] of [
            ["minHeight", "min-height"],
            ["padding", "padding"],
            ["paddingBottom", "padding-bottom"],
            ["paddingLeft", "padding-left"],
            ["paddingRight", "padding-right"],
            ["paddingTop", "padding-top"],
        ] as const) {
            const marker = `--site-builder-authored-${cssProperty}`;

            if (style[property] !== undefined) {
                resolved[property] = style[property];
            }
            if (style[marker] !== undefined) {
                resolved[marker] = style[marker];
            }
        }

        return Object.keys(resolved).length > 0 ? resolved : undefined;
    };
    const authoredPartStyle = siteBuilderPartStyle(block, part);
    const normalizedPartStyle = normalizedLagunaPaddingStyle(authoredPartStyle);
    const rebasedPartStyle: SiteBuilderStyleRecord = {
        ...normalizedPartStyle,
        ...siteBuilderRebasedAuthoritativeStyleVariables(
            authoredPartStyle,
            normalizedPartStyle,
        ),
    };

    // Root resize values describe the concrete button/badge surface. Template
    // size defaults are applied earlier by partStyle(), while an explicitly
    // customized part remains the final authority.
    return mergeStyles(
        resizeStyle(blockStyle(block) as SiteBuilderStyleRecord | undefined),
        resizeStyle(rebasedPartStyle),
        partSpacingStyle(block, part),
    );
}

function childBlockStyle(block: ShopTemplateBlock): CSSProperties {
    const x = blockOptionText(block, "x");
    const y = blockOptionText(block, "y");
    const width = blockOptionText(block, "width");
    const zIndex = blockOptionText(block, "zIndex");

    return {
        position: "absolute",
        left: x || "12%",
        top: y || "12%",
        width: width || "auto",
        zIndex: zIndex ? Number(zIndex) || undefined : undefined,
        ...blockSpacingStyle(block),
    };
}

function numericBlockOption(block: ShopTemplateBlock | null | undefined, key: string, fallback: number): number {
    const value = Number(blockRecord(block, "options")[key]);

    return Number.isFinite(value) ? value : fallback;
}

function hasPositiveBlockOption(block: ShopTemplateBlock | null | undefined, key: string): boolean {
    const value = blockRecord(block, "options")[key];

    return Number.isFinite(Number(value)) && Number(value) > 0;
}

function hasGridLengthBlockOption(block: ShopTemplateBlock | null | undefined, key: string): boolean {
    const rawValue = blockRecord(block, "options")[key];

    if (typeof rawValue === "number") return Number.isFinite(rawValue) && rawValue > 0;
    if (typeof rawValue !== "string") return false;

    const value = rawValue.trim();
    const numericMatch = value.match(/^(\d+(?:\.\d+)?)(?:px)?$/iu);

    return numericMatch !== null && Number(numericMatch[1]) > 0;
}

function authoredGridPlacementAxes(block: ShopTemplateBlock | null | undefined): {
    column: boolean;
    row: boolean;
} {
    return {
        column: hasPositiveBlockOption(block, "gridX") || hasPositiveBlockOption(block, "gridW"),
        row: hasPositiveBlockOption(block, "gridY") || hasPositiveBlockOption(block, "gridH"),
    };
}

function gridPixelHeight(rows: number, rowHeight: string, rowGap = SHOP_GRID_CELL_GAP): number | undefined {
    const match = rowHeight.trim().match(/^(\d+(?:\.\d+)?)px$/iu);

    if (!match) return undefined;

    return (rows * Number(match[1])) + (Math.max(0, rows - 1) * rowGap);
}

function gridChildStyle(block: ShopTemplateBlock): CSSProperties {
    const x = Math.max(1, Math.round(numericBlockOption(block, "gridX", 1)));
    const y = Math.max(1, Math.round(numericBlockOption(block, "gridY", 1)));
    const w = Math.max(1, Math.round(numericBlockOption(block, "gridW", 2)));
    const h = Math.max(1, Math.round(numericBlockOption(block, "gridH", 1)));
    const zIndex = Number(blockRecord(block, "options").zIndex);
    const autoWrappedColumn = block.type === "column" && blockRecord(block, "options").builderAutoWrapped === true;
    const autoWrappedColumnMinHeight = (h * SHOP_GRID_CELL_HEIGHT) + ((h - 1) * SHOP_GRID_CELL_GAP);
    const gridColumn = `${x} / span ${w}`;
    const gridRow = `${y} / span ${h}`;
    const authoredAxes = authoredGridPlacementAxes(block);

    return {
        "--site-builder-authored-grid-column": authoredAxes.column ? gridColumn : undefined,
        "--site-builder-authored-grid-row": authoredAxes.row ? gridRow : undefined,
        gridColumn,
        gridRow,
        alignSelf: autoWrappedColumn ? "start" : undefined,
        height: autoWrappedColumn ? undefined : "100%",
        minHeight: autoWrappedColumn ? autoWrappedColumnMinHeight : 0,
        minWidth: 0,
        ...(Number.isFinite(zIndex) ? { zIndex } : {}),
    } as CSSProperties;
}

function gridChildStacksOnMobile(block: ShopTemplateBlock | null | undefined): boolean {
    const value = blockRecord(block, "options").stackOnMobile;

    return value === true || value === "true" || value === 1 || value === "1";
}

function gridChildMobileMinHeight(block: ShopTemplateBlock): string {
    const rows = Math.max(1, Math.round(numericBlockOption(block, "gridH", 1)));
    const authoredHeight = (rows * SHOP_GRID_CELL_HEIGHT) + ((rows - 1) * SHOP_GRID_CELL_GAP);
    const maximumHeight = Math.max(240, Math.min(480, authoredHeight));

    return `clamp(240px, 62vw, ${maximumHeight}px)`;
}

function gridBlockExtent(blocks: ShopTemplateBlock[]): { columns: number; rows: number } {
    return blocks.reduce((bounds, childBlock) => {
        const x = Math.max(1, Math.round(numericBlockOption(childBlock, "gridX", 1)));
        const y = Math.max(1, Math.round(numericBlockOption(childBlock, "gridY", 1)));
        const w = Math.max(1, Math.round(numericBlockOption(childBlock, "gridW", 1)));
        const h = Math.max(1, Math.round(numericBlockOption(childBlock, "gridH", 1)));

        return {
            columns: Math.max(bounds.columns, x + w - 1),
            rows: Math.max(bounds.rows, y + h - 1),
        };
    }, { columns: 1, rows: 1 });
}

function gridTrackDimensions(block: ShopTemplateBlock | null | undefined, editorMode: boolean): { columns: number; height: number; rows: number; width: number } | null {
    const grid = block ? getShopBlockGrid(block.type) : null;
    const items = slotChildren(block, SHOP_GRID_SLOT_KEY);

    if (!grid || (items.length === 0 && !editorMode)) {
        return null;
    }

    const occupied = gridBlockExtent(items);
    const columns = Math.max(grid.columns, occupied.columns);
    const rows = lagunaRuntimeRegion(block) !== ""
        ? occupied.rows
        : Math.max(grid.rows, occupied.rows);

    return {
        columns,
        height: (rows * SHOP_GRID_CELL_HEIGHT) + ((rows - 1) * SHOP_GRID_CELL_GAP),
        rows,
        width: (columns * SHOP_GRID_CELL_WIDTH) + ((columns - 1) * SHOP_GRID_CELL_GAP),
    };
}

function gridFrameReservationStyle(block: ShopTemplateBlock, editorMode: boolean): CSSProperties | undefined {
    const items = slotChildren(block, SHOP_GRID_SLOT_KEY);

    if (items.length === 0) {
        return undefined;
    }

    const dimensions = gridTrackDimensions(block, editorMode);

    if (!dimensions) {
        return undefined;
    }

    const configuredMinHeight = blockOptionText(block, "minHeight");
    const rowCount = dimensions.rows;
    const rowHeight = hasGridLengthBlockOption(block, "gridRowHeight")
        ? cssLengthOption(block, "gridRowHeight", `${SHOP_GRID_CELL_HEIGHT}px`)
        : `${SHOP_GRID_CELL_HEIGHT}px`;
    const authoredHeight = hasGridLengthBlockOption(block, "gridRowHeight")
        ? gridPixelHeight(rowCount, rowHeight)
        : undefined;
    const reservedHeight = authoredHeight ?? dimensions.height;

    return {
        minHeight: configuredMinHeight
            ? `max(${configuredMinHeight}, ${reservedHeight}px)`
            : reservedHeight,
    };
}

function usesLagunaSemanticChromeGrid(block: ShopTemplateBlock): boolean {
    return (
        block.type === "promoBar"
        || block.type === "header"
        || block.type === "footer"
    ) && slotIsDefined(block, SHOP_GRID_SLOT_KEY);
}

function usesLagunaDedicatedGridRenderer(block: ShopTemplateBlock): boolean {
    return usesLagunaSemanticChromeGrid(block)
        || (
            block.type === "systemProductList"
            && catalogProductPage(block) !== null
        )
        || (
            block.type === "benefitStrip"
            && slotIsDefined(block, SHOP_GRID_SLOT_KEY)
        );
}

function slotPart(slotKey: string, index: number): string {
    return `slot:${slotKey}:${index}`;
}

function slotChildren(block: ShopTemplateBlock | null | undefined, slotKey: string): ShopTemplateBlock[] {
    return getSlotBlocks(block, slotKey);
}

function slotIsDefined(block: ShopTemplateBlock | null | undefined, slotKey: string): boolean {
    return Array.isArray(block?.slots?.[slotKey]);
}

function slotReplacesFallbackAtVersion(block: ShopTemplateBlock | null | undefined, slotKey: string, schemaVersion: number): boolean {
    return slotIsDefined(block, slotKey) && schemaVersion >= SHOP_BUILDER_SCHEMA_VERSION;
}

function useSlotReplacesFallback(block: ShopTemplateBlock | null | undefined, slotKey: string): boolean {
    const schemaVersion = useContext(ShopBuilderSchemaVersionContext);

    return slotReplacesFallbackAtVersion(block, slotKey, schemaVersion);
}

type SlotItemClassName = string | ((params: {
    childBlock: ShopTemplateBlock;
    index: number;
    itemPart: string;
}) => string | undefined);

type SlotRenderItem = (params: {
    childBlock: ShopTemplateBlock;
    index: number;
    itemPart: string;
}) => ReactNode;

type SlotItemFilter = (
    childBlock: ShopTemplateBlock,
    sourceIndex: number,
) => boolean;

function resolveSlotItemClassName(
    itemClassName: SlotItemClassName | undefined,
    params: {
        childBlock: ShopTemplateBlock;
        index: number;
        itemPart: string;
    },
): string | undefined {
    return typeof itemClassName === "function" ? itemClassName(params) : itemClassName;
}

function compactSemanticChildWrapperStyle(childBlock: ShopTemplateBlock): CSSProperties | undefined {
    const alignment = blockOptionText(childBlock, "alignment");
    const minHeight = blockOptionText(childBlock, "minHeight");
    const x = Math.max(1, Math.round(numericBlockOption(childBlock, "gridX", 1)));
    const y = Math.max(1, Math.round(numericBlockOption(childBlock, "gridY", 1)));
    const w = Math.max(1, Math.round(numericBlockOption(childBlock, "gridW", 12)));
    const h = Math.max(1, Math.round(numericBlockOption(childBlock, "gridH", 1)));
    const authoredAxes = authoredGridPlacementAxes(childBlock);
    const gridColumn = `${x} / span ${w}`;
    const gridRow = `${y} / span ${h}`;
    const placementStyle = {
        "--shop-semantic-grid-column": gridColumn,
        "--shop-semantic-grid-row": gridRow,
        "--shop-semantic-grid-min-height": minHeight || "0px",
        "--site-builder-authored-grid-column": authoredAxes.column ? gridColumn : undefined,
        "--site-builder-authored-grid-row": authoredAxes.row ? gridRow : undefined,
    } as CSSProperties;

    return mergeStyles(
        childBlockWrapperStyle(childBlock),
        atomicBlockStyle(childBlock),
        placementStyle,
        childBlock.type === "column"
            ? mergeStyles(
                { display: "grid" },
                columnShapeStyle(childBlock),
                columnVerticalAlignStyle(childBlock),
                alignment === "center"
                    ? { justifyItems: "center", textAlign: "center" }
                    : alignment === "right"
                        ? { justifyItems: "end", textAlign: "right" }
                        : undefined,
            )
            : undefined,
    );
}

function keyedSlotRenderedItem(renderedItem: ReactNode, itemPart: string): ReactNode {
    if (!Array.isArray(renderedItem)) {
        return renderedItem;
    }

    return Children.toArray(renderedItem).map((child, index) => (
        <Fragment key={`${itemPart}:rendered:${index}`}>
            {child}
        </Fragment>
    ));
}

function columnGroupId(block: ShopTemplateBlock | null | undefined): string {
    if (block?.type !== "column") {
        return "";
    }

    const value = blockRecord(block, "options")[SHOP_COLUMN_GROUP_OPTION];

    return typeof value === "string" ? value.trim() : "";
}

function SlotBlockGroup({
    block,
    slotKey,
    partPrefix,
    className,
    childSlotParentType,
    compactGridChildren,
    itemFilter,
    itemClassName,
    editorMode,
    lockedChildren,
    renderItem,
    semanticGrid,
    style,
}: {
    block: ShopTemplateBlock | null | undefined;
    slotKey: string;
    partPrefix?: string;
    className: string;
    childSlotParentType?: string;
    compactGridChildren?: boolean;
    itemFilter?: SlotItemFilter;
    itemClassName?: SlotItemClassName;
    editorMode?: boolean;
    lockedChildren?: boolean;
    renderItem?: SlotRenderItem;
    semanticGrid?: boolean;
    style?: CSSProperties;
}) {
    const inheritedEditorMode = useContext(ShopEditorModeContext);
    const parentBuilderPath = useContext(ShopBuilderBlockPathContext);
    const activeEditorMode = editorMode ?? inheritedEditorMode;
    const sourceItems = slotChildren(block, slotKey);
    const items = sourceItems
        .map((childBlock, index) => ({
            childBlock,
            sourceIndex: lagunaRuntimeSourceIndex(childBlock, index),
        }))
        .filter(({ childBlock, sourceIndex }) => itemFilter?.(childBlock, sourceIndex) ?? true);
    const sourceItemCount = Math.max(
        lagunaRuntimeSlotItemCount(sourceItems),
        slotKey === SHOP_GRID_SLOT_KEY
            ? lagunaRuntimeGridItemCount(block)
            : 0,
    );
    const slotContainerKey = partPrefix ? `${partPrefix}:slot:${slotKey}` : slotKey;
    const occupiedSemanticGridExtent = semanticGrid
        ? gridBlockExtent(sourceItems)
        : null;
    const semanticGridDefinition = semanticGrid && block
        ? getShopBlockGrid(block.type)
        : null;
    const hasAuthoredGridColumns = semanticGrid && hasPositiveBlockOption(block, "gridColumns");
    const hasAuthoredGridRows = semanticGrid && hasGridLengthBlockOption(block, "gridRowHeight");
    const semanticGridExtent = occupiedSemanticGridExtent
        ? {
            columns: Math.max(
                semanticGridDefinition?.columns ?? 1,
                occupiedSemanticGridExtent.columns,
            ),
            rows: occupiedSemanticGridExtent.rows,
        }
        : null;
    const semanticGridColumns = semanticGridExtent
        ? hasAuthoredGridColumns
            ? Math.max(1, Math.round(numericBlockOption(block, "gridColumns", semanticGridExtent.columns)))
            : semanticGridExtent.columns
        : undefined;
    const semanticGridRows = semanticGridExtent?.rows;
    const semanticGridRowHeight = hasAuthoredGridRows
        ? cssLengthOption(block, "gridRowHeight", `${SHOP_GRID_CELL_HEIGHT}px`)
        : undefined;

    if (items.length === 0 && !activeEditorMode) {
        return null;
    }

    const renderChild = (childBlock: ShopTemplateBlock, index: number): ReactNode => {
        const itemPart = partPrefix ? `${partPrefix}:${slotPart(slotKey, index)}` : slotPart(slotKey, index);
        const resolvedItemClassName = resolveSlotItemClassName(itemClassName, { childBlock, index, itemPart });
        const renderedItem = renderItem
            ? renderItem({ childBlock, index, itemPart })
            : <AtomicBlock block={childBlock} partPrefix={itemPart} slotParentType={childSlotParentType ?? block?.type} />;
        const renderedChildren = keyedSlotRenderedItem(renderedItem, itemPart);
        const childKey = block?.type === "accordion" && slotKey === "content"
            ? `${slotKey}:${accordionItemKey(childBlock, index)}`
            : `${slotKey}:${childBlock.type}:${index}`;
        const childBuilderPath = parentBuilderPath
            ? siteBuilderEditorChildPath(
                parentBuilderPath,
                slotKey,
                childBlock,
                index,
            )
            : null;
        const authoredAxes = semanticGrid
            ? authoredGridPlacementAxes(childBlock)
            : { column: false, row: false };

        return (
            <ShopBuilderBlockPathContext.Provider
                key={childKey}
                value={childBuilderPath ?? parentBuilderPath}
            >
                <div
                    {...(childBuilderPath ? siteBuilderEditorBlockAttributes({
                        block: childBlock,
                        editorMode: activeEditorMode,
                        path: childBuilderPath,
                        slot: slotKey,
                    }) : {})}
                    {...siteBuilderEditorNestedBlockAttributes({
                        block: childBlock,
                        editorMode: activeEditorMode,
                        lockedColumnParentPart: lockedChildren
                            ? partPrefix ?? "root"
                            : undefined,
                    })}
                    {...(semanticGrid && childBlock.type === "column" ? siteBuilderEditorColumnAttributes({
                        editorMode: activeEditorMode,
                        part: itemPart,
                    }) : {})}
                    data-shop-block-part={itemPart}
                    data-shop-document-role={blockOptionText(childBlock, SHOP_DOCUMENT_ROLE_OPTION) || undefined}
                    data-shop-semantic-grid-item={semanticGrid ? "true" : undefined}
                    data-site-builder-authored-grid-placement={
                        authoredAxes.column || authoredAxes.row ? "true" : undefined
                    }
                    data-site-builder-authored-grid-column={authoredAxes.column ? "true" : undefined}
                    data-site-builder-authored-grid-row={authoredAxes.row ? "true" : undefined}
                    data-shop-outline-disabled={siteBuilderBlockDelegatesOutlineToColumn(childBlock) ? "true" : undefined}
                    data-shop-locked-column-child={lockedChildren ? "true" : undefined}
                    data-shop-locked-column-parent-part={lockedChildren ? partPrefix : undefined}
                    className={classNames(resolvedItemClassName, inlineAtomicBlockTypes.has(childBlock.type) ? "w-fit max-w-full" : "max-w-full")}
                    style={compactGridChildren
                        ? compactSemanticChildWrapperStyle(childBlock)
                        : lineSlotChildWrapperStyle(block, childBlock)}
                >
                    {renderedChildren}
                </div>
            </ShopBuilderBlockPathContext.Provider>
        );
    };

    const renderedItems: ReactNode[] = [];
    let itemIndex = 0;

    while (itemIndex < items.length) {
        const { childBlock, sourceIndex } = items[itemIndex];
        const groupId = columnGroupId(childBlock);

        if (!groupId) {
            renderedItems.push(renderChild(childBlock, sourceIndex));
            itemIndex += 1;
            continue;
        }

        const groupStart = sourceIndex;
        const groupItems: Array<{ block: ShopTemplateBlock; index: number }> = [];

        while (itemIndex < items.length && columnGroupId(items[itemIndex].childBlock) === groupId) {
            groupItems.push({
                block: items[itemIndex].childBlock,
                index: items[itemIndex].sourceIndex,
            });
            itemIndex += 1;
        }

        if (groupItems.length === 1) {
            renderedItems.push(renderChild(groupItems[0].block, groupItems[0].index));
            continue;
        }

        renderedItems.push(
            <div
                key={`${slotKey}:column-group:${groupId}:${groupStart}`}
                className="grid gap-3 [grid-template-columns:1fr] sm:[grid-template-columns:var(--shop-column-group-template)]"
                style={{ "--shop-column-group-template": `repeat(${groupItems.length}, minmax(0, 1fr))` } as CSSProperties}
            >
                {groupItems.map((item) => renderChild(item.block, item.index))}
            </div>,
        );
    }

    return (
        <div
            {...siteBuilderEditorSlotAttributes({
                editorMode: activeEditorMode,
                gridColumns: semanticGridColumns,
                gridRows: semanticGridRows,
                itemCount: sourceItemCount,
                slot: slotKey,
            })}
            className={classNames(className, items.length === 0 ? "min-h-12" : "")}
            data-shop-slot-container={slotContainerKey}
            data-shop-slot-item-count={sourceItemCount}
            data-shop-grid-container={semanticGrid ? slotContainerKey : undefined}
            data-shop-grid-cols={semanticGridColumns}
            data-shop-grid-rows={semanticGridRows}
            data-shop-semantic-grid={semanticGrid ? "true" : undefined}
            data-site-builder-authored-grid={
                hasAuthoredGridColumns || hasAuthoredGridRows ? "true" : undefined
            }
            data-site-builder-authored-grid-columns={hasAuthoredGridColumns ? "true" : undefined}
            data-site-builder-authored-grid-rows={hasAuthoredGridRows ? "true" : undefined}
            style={mergeStyles(
                style,
                semanticGrid ? ({
                    "--site-builder-authored-grid-columns": hasAuthoredGridColumns
                        ? `repeat(${semanticGridColumns}, minmax(0, 1fr))`
                        : undefined,
                    "--site-builder-authored-grid-rows": hasAuthoredGridRows
                        ? `repeat(${semanticGridRows}, ${semanticGridRowHeight})`
                        : undefined,
                } as CSSProperties) : undefined,
            )}
        >
            {renderedItems}
        </div>
    );
}

function InlineSlotBlockGroup({
    block,
    slotKey,
    partPrefix,
    className,
    childSlotParentType,
    itemClassName,
    style,
}: {
    block: ShopTemplateBlock;
    slotKey: string;
    partPrefix?: string;
    className?: string;
    childSlotParentType?: string;
    itemClassName?: SlotItemClassName;
    style?: CSSProperties;
}) {
    const editorMode = useContext(ShopEditorModeContext);
    const parentBuilderPath = useContext(ShopBuilderBlockPathContext);
    const items = slotChildren(block, slotKey);
    const slotContainerKey = partPrefix ? `${partPrefix}:slot:${slotKey}` : slotKey;

    if (items.length === 0 && !editorMode) {
        return null;
    }

    return (
        <span
            {...siteBuilderEditorSlotAttributes({
                editorMode,
                itemCount: items.length,
                slot: slotKey,
            })}
            className={classNames(
                "inline-flex min-w-0 items-center",
                className,
                items.length === 0 ? "min-h-8 min-w-8" : "",
            )}
            data-shop-slot-container={slotContainerKey}
            data-shop-slot-item-count={items.length}
            style={style}
        >
            {items.map((childBlock, index) => {
                const itemPart = partPrefix ? `${partPrefix}:${slotPart(slotKey, index)}` : slotPart(slotKey, index);
                const resolvedItemClassName = resolveSlotItemClassName(itemClassName, { childBlock, index, itemPart });
                const childBuilderPath = parentBuilderPath
                    ? siteBuilderEditorChildPath(parentBuilderPath, slotKey, childBlock, index)
                    : null;

                return (
                    <ShopBuilderBlockPathContext.Provider
                        key={`${slotKey}:${childBlock.uuid?.trim() || childBlock.type}:${index}`}
                        value={childBuilderPath ?? parentBuilderPath}
                    >
                        <span
                            {...(childBuilderPath ? siteBuilderEditorBlockAttributes({
                                block: childBlock,
                                editorMode,
                                path: childBuilderPath,
                                slot: slotKey,
                            }) : {})}
                            {...siteBuilderEditorNestedBlockAttributes({
                                block: childBlock,
                                editorMode,
                            })}
                            className={classNames("inline-flex min-w-0 max-w-full items-center", resolvedItemClassName)}
                            data-shop-block-part={itemPart}
                            data-shop-outline-disabled={siteBuilderBlockDelegatesOutlineToColumn(childBlock) ? "true" : undefined}
                        >
                            <AtomicBlock
                                block={childBlock}
                                partPrefix={itemPart}
                                slotParentType={childSlotParentType ?? block.type}
                            />
                        </span>
                    </ShopBuilderBlockPathContext.Provider>
                );
            })}
        </span>
    );
}

function SlotWithFallback({
    block,
    slotKey,
    className,
    emptyClassName,
    itemClassName,
    renderItem,
    children,
}: {
    block: ShopTemplateBlock;
    slotKey: string;
    className: string;
    emptyClassName?: string;
    itemClassName?: SlotItemClassName;
    renderItem?: SlotRenderItem;
    children: ReactNode;
}) {
    const editorMode = useContext(ShopEditorModeContext);
    const items = slotChildren(block, slotKey);
    const slotReplacesFallback = useSlotReplacesFallback(block, slotKey);

    if (items.length > 0 || slotReplacesFallback) {
        return (
            <SlotBlockGroup
                block={block}
                slotKey={slotKey}
                className={className}
                itemClassName={itemClassName}
                renderItem={renderItem}
            />
        );
    }

    return (
        <>
            {children}
            {editorMode ? (
                <SlotBlockGroup
                    block={block}
                    slotKey={slotKey}
                    className={emptyClassName ?? className}
                    itemClassName={itemClassName}
                    renderItem={renderItem}
                />
            ) : null}
        </>
    );
}

function GridBlockLayer({
    block,
    childSlotParentType,
    editorMode,
    fluidWidth,
    itemFilter,
    partPrefix,
}: {
    block: ShopTemplateBlock | null | undefined;
    childSlotParentType?: string;
    editorMode: boolean;
    fluidWidth?: boolean;
    itemFilter?: SlotItemFilter;
    partPrefix?: string;
}) {
    const parentBuilderPath = useContext(ShopBuilderBlockPathContext);
    const grid = block ? getShopBlockGrid(block.type) : null;
    const sourceItems = slotChildren(block, SHOP_GRID_SLOT_KEY);
    const items = sourceItems
        .map((childBlock, index) => ({
            childBlock,
            sourceIndex: lagunaRuntimeSourceIndex(childBlock, index),
        }))
        .filter(({ childBlock, sourceIndex }) => itemFilter?.(childBlock, sourceIndex) ?? true);
    const sourceItemCount = Math.max(
        lagunaRuntimeSlotItemCount(sourceItems),
        lagunaRuntimeGridItemCount(block),
    );
    const hasGridSlot = slotIsDefined(block, SHOP_GRID_SLOT_KEY);
    const gridContainerKey = partPrefix ? `${partPrefix}:slot:${SHOP_GRID_SLOT_KEY}` : SHOP_GRID_SLOT_KEY;
    const stackOnMobile = gridChildStacksOnMobile(block)
        || items.some(({ childBlock }) => gridChildStacksOnMobile(childBlock));

    if (
        !grid
        || (items.length === 0 && (!editorMode || !hasGridSlot))
    ) {
        return null;
    }

    const dimensions = gridTrackDimensions(block, editorMode);

    if (!dimensions) {
        return null;
    }

    const emptyEditorGrid = editorMode && items.length === 0;
    const hasAuthoredGridColumns = hasPositiveBlockOption(block, "gridColumns");
    const hasAuthoredGridRows = hasGridLengthBlockOption(block, "gridRowHeight");
    const gridColumnCount = hasAuthoredGridColumns
        ? Math.max(1, Math.round(numericBlockOption(block, "gridColumns", dimensions.columns)))
        : dimensions.columns;
    const gridRowCount = dimensions.rows;
    const gridRowHeight = hasGridLengthBlockOption(block, "gridRowHeight")
        ? cssLengthOption(block, "gridRowHeight", `${SHOP_GRID_CELL_HEIGHT}px`)
        : `${SHOP_GRID_CELL_HEIGHT}px`;
    const gridTemplateRows = emptyEditorGrid && !hasAuthoredGridRows
        ? "72px"
        : `repeat(${gridRowCount}, ${gridRowHeight})`;
    const gridTemplateColumns = fluidWidth
        ? `repeat(${gridColumnCount}, minmax(0, ${SHOP_GRID_CELL_WIDTH}px))`
        : `repeat(${gridColumnCount}, ${SHOP_GRID_CELL_WIDTH}px)`;
    const gridWidth = (gridColumnCount * SHOP_GRID_CELL_WIDTH)
        + ((gridColumnCount - 1) * SHOP_GRID_CELL_GAP);
    const gridHeight = gridPixelHeight(gridRowCount, gridRowHeight);

    return (
        <div
            {...siteBuilderEditorSlotAttributes({
                editorMode,
                gridColumns: gridColumnCount,
                gridRows: gridRowCount,
                itemCount: sourceItemCount,
                slot: SHOP_GRID_SLOT_KEY,
            })}
            className="pointer-events-none absolute inset-0 grid content-start justify-center"
            data-site-builder-authored-grid={
                hasAuthoredGridColumns || hasAuthoredGridRows ? "true" : undefined
            }
            data-site-builder-authored-grid-columns={hasAuthoredGridColumns ? "true" : undefined}
            data-site-builder-authored-grid-rows={hasAuthoredGridRows ? "true" : undefined}
            data-shop-grid-container={gridContainerKey}
            data-shop-grid-cols={gridColumnCount}
            data-shop-grid-rows={gridRowCount}
            data-shop-grid-stack-on-mobile={stackOnMobile ? "true" : undefined}
            style={{
                "--site-builder-authored-grid-columns": hasAuthoredGridColumns
                    ? gridTemplateColumns
                    : undefined,
                "--site-builder-authored-grid-rows": hasAuthoredGridRows
                    ? gridTemplateRows
                    : undefined,
                position: "absolute",
                inset: 0,
                bottom: emptyEditorGrid ? "auto" : 0,
                height: emptyEditorGrid ? 72 : undefined,
                gap: SHOP_GRID_CELL_GAP,
                gridTemplateRows: gridTemplateRows,
                gridTemplateColumns: gridTemplateColumns,
                columnGap: fluidWidth ? "clamp(8px, 3vw, 24px)" : SHOP_GRID_CELL_GAP,
                minHeight: emptyEditorGrid
                    ? 72
                    : hasAuthoredGridRows
                        ? gridHeight
                        : dimensions.height,
                marginLeft: "auto",
                marginRight: "auto",
                minWidth: fluidWidth ? 0 : gridWidth,
                width: fluidWidth ? "100%" : undefined,
                maxWidth: fluidWidth ? gridWidth : undefined,
            } as CSSProperties}
        >
            {items.map(({ childBlock, sourceIndex }) => {
                const itemPart = partPrefix ? `${partPrefix}:${slotPart(SHOP_GRID_SLOT_KEY, sourceIndex)}` : slotPart(SHOP_GRID_SLOT_KEY, sourceIndex);
                const childBuilderPath = parentBuilderPath
                    ? siteBuilderEditorChildPath(
                        parentBuilderPath,
                        SHOP_GRID_SLOT_KEY,
                        childBlock,
                        sourceIndex,
                    )
                    : null;
                const authoredAxes = authoredGridPlacementAxes(childBlock);

                return (
                    <ShopBuilderBlockPathContext.Provider
                        key={`${SHOP_GRID_SLOT_KEY}:${childBlock.type}:${sourceIndex}`}
                        value={childBuilderPath ?? parentBuilderPath}
                    >
                        <div
                            {...(childBuilderPath ? siteBuilderEditorBlockAttributes({
                                block: childBlock,
                                editorMode,
                                path: childBuilderPath,
                                slot: SHOP_GRID_SLOT_KEY,
                            }) : {})}
                            {...siteBuilderEditorNestedBlockAttributes({
                                block: childBlock,
                                editorMode,
                            })}
                            className="pointer-events-auto relative min-h-0 min-w-0"
                            data-site-builder-authored-grid-placement={
                                authoredAxes.column || authoredAxes.row ? "true" : undefined
                            }
                            data-site-builder-authored-grid-column={authoredAxes.column ? "true" : undefined}
                            data-site-builder-authored-grid-row={authoredAxes.row ? "true" : undefined}
                            data-shop-block-part={itemPart}
                            data-shop-grid-item="true"
                            data-shop-outline-disabled={siteBuilderBlockDelegatesOutlineToColumn(childBlock) ? "true" : undefined}
                            style={mergeStyles(
                                gridChildStyle(childBlock),
                                gridChildWrapperStyle(childBlock),
                                stackOnMobile
                                    ? { "--shop-grid-mobile-min-height": gridChildMobileMinHeight(childBlock) } as CSSProperties
                                    : undefined,
                            )}
                        >
                            <GridLayerBlock block={block} childBlock={childBlock} itemPart={itemPart} slotParentType={childSlotParentType} />
                            <GridBlockLayer block={childBlock} editorMode={editorMode} fluidWidth={fluidWidth} partPrefix={itemPart} />
                        </div>
                    </ShopBuilderBlockPathContext.Provider>
                );
            })}
        </div>
    );
}

function GridLayerBlock({
    block,
    childBlock,
    itemPart,
    slotParentType,
}: {
    block: ShopTemplateBlock | null | undefined;
    childBlock: ShopTemplateBlock;
    itemPart: string;
    slotParentType?: string;
}) {
    if (block?.type === "introHero" && childBlock.type === "column") {
        const variant = introHeroVariant(block);

        return (
            <IntroHeroColumnBlock
                block={childBlock}
                partPrefix={itemPart}
                actionStyle={introHeroColumnActionStyle(variant)}
                centered={blockOptionText(childBlock, "alignment") === "center"}
                dark={introHeroIsDark(variant)}
                showBadge={variant !== "collage" && variant !== "minimal" && variant !== "product"}
            />
        );
    }

    return <AtomicBlock block={childBlock} partPrefix={itemPart} slotParentType={slotParentType ?? block?.type} />;
}

function EditableBlockFrame({
    block,
    index,
    editorMode,
    children,
}: {
    block: ShopTemplateBlock;
    index: number;
    editorMode: boolean;
    children: ReactNode;
}) {
    const childBlocks = Array.isArray(block.children) ? block.children : [];
    const hasLegacyChildren = childBlocks.length > 0 && (!block.slots || Object.keys(block.slots).length === 0);
    const dedicatedGridRenderer = usesLagunaDedicatedGridRenderer(block);
    const hasGrid = getShopBlockGrid(block.type) !== null && !dedicatedGridRenderer;
    const isAtomicFrame = isAtomicBlockType(block.type);
    const builderPath = siteBuilderEditorRootPath(block, index);

    return (
        <ShopBuilderBlockPathContext.Provider value={builderPath}>
            <div
                {...(isAtomicFrame ? {} : blockInteractionAttributes(block))}
                {...siteBuilderEditorBlockAttributes({
                    block,
                    editorMode,
                    path: builderPath,
                })}
                data-shop-block-index={index}
                data-shop-block-type={block.type}
                data-shop-presentation-role={blockOptionText(block, "shopPresentationRole") || undefined}
                className={hasLegacyChildren || hasGrid ? "relative" : undefined}
                style={mergeStyles(
                    editableBlockFrameStyle(block),
                    dedicatedGridRenderer ? undefined : gridFrameReservationStyle(block, editorMode),
                    isAtomicFrame ? undefined : interactionStyle(block),
                )}
            >
                {children}
                {block.type === "heroSlider" || dedicatedGridRenderer ? null : (
                    <GridBlockLayer
                        block={block}
                        editorMode={editorMode}
                        fluidWidth
                    />
                )}
                {hasLegacyChildren ? (
                    <div className="pointer-events-none absolute inset-0">
                        {childBlocks.map((childBlock, childIndex) => {
                            const childPath = siteBuilderEditorChildPath(
                                builderPath,
                                "children",
                                childBlock,
                                childIndex,
                            );

                            return (
                                <ShopBuilderBlockPathContext.Provider
                                    key={`${childBlock.type}-${childIndex}`}
                                    value={childPath}
                                >
                                    <div
                                        {...siteBuilderEditorBlockAttributes({
                                            block: childBlock,
                                            editorMode,
                                            path: childPath,
                                            slot: "children",
                                        })}
                                        {...siteBuilderEditorNestedBlockAttributes({
                                            block: childBlock,
                                            editorMode,
                                        })}
                                        data-shop-block-part={`child:${childIndex}`}
                                        data-shop-child-index={childIndex}
                                        className="pointer-events-auto"
                                        style={childBlockStyle(childBlock)}
                                    >
                                        <AtomicBlock block={childBlock} partPrefix={`child:${childIndex}`} />
                                    </div>
                                </ShopBuilderBlockPathContext.Provider>
                            );
                        })}
                    </div>
                ) : null}
            </div>
        </ShopBuilderBlockPathContext.Provider>
    );
}

function atomicIconGlyph(value: string, className = "h-[55%] w-[55%]"): ReactNode {
    return siteBuilderIconGlyph(value, className);
}

function atomicIconSize(value: string): string {
    if (/^(?:\d+(?:\.\d+)?)(?:px|rem|em)$/i.test(value.trim())) return value.trim();
    if (value === "1x") return "1rem";
    if (value === "2x") return "1.25rem";
    if (value === "3x") return "1.5rem";
    if (value === "4x") return "2rem";
    if (value === "5x") return "2.5rem";
    if (value === "xs") return "1.75rem";
    if (value === "sm") return "2.25rem";
    if (value === "lg") return "3.5rem";
    if (value === "xl") return "4.25rem";

    return "2.75rem";
}

function atomicIconRadius(value: string): string {
    if (value === "circle") return "50%";
    if (value === "rounded") return "0.75rem";
    if (value === "square") return "0";
    if (value === "outline") return "50%";
    if (value === "gear") return "35%";

    return "0";
}

function atomicIconFrameStyle(block: ShopTemplateBlock): CSSProperties {
    const authoredStyle = siteBuilderBlockStyle(block);
    const style = blockOptionText(block, "iconStyle") || "plain";
    const backgroundColor = blockOptionText(block, "backgroundColor");
    const borderColor = blockOptionText(block, "borderColor");
    const borderWidth = blockOptionText(block, "borderWidth");
    const borderRadius = blockOptionText(block, "borderRadius");
    const explicitIconSize = blockOptionText(block, "iconSize");
    const size = atomicIconSize(explicitIconSize || blockOptionText(block, "size"));
    const paddingStyle = blockSpacingStyle(block);
    const hasExplicitBorderWidth = borderWidth !== "" && borderWidth !== "0" && borderWidth !== "0px";

    const resolvedStyle: SiteBuilderStyleRecord = {
        backgroundColor: style === "plain" ? backgroundColor || "transparent" : backgroundColor || (style === "outline" ? "transparent" : "var(--site-primary)"),
        borderColor: borderColor || (style === "outline" || hasExplicitBorderWidth ? "currentColor" : "transparent"),
        borderRadius: borderRadius ? presetRadiusValue(borderRadius) : atomicIconRadius(style),
        borderStyle: hasExplicitBorderWidth || style !== "plain" ? "solid" : undefined,
        borderWidth: style === "plain" ? borderWidth || "0" : borderWidth || (style === "outline" ? "1px" : "0"),
        boxSizing: explicitIconSize ? "content-box" : "border-box",
        clipPath: style === "gear" ? "polygon(50% 0%,61% 18%,82% 10%,90% 30%,100% 50%,90% 70%,82% 90%,61% 82%,50% 100%,39% 82%,18% 90%,10% 70%,0% 50%,10% 30%,18% 10%,39% 18%)" : undefined,
        display: "grid",
        height: size,
        placeItems: "center",
        ...(authoredStyle.padding !== undefined ? { padding: authoredStyle.padding } : {}),
        ...(paddingStyle ?? {}),
        width: size,
    };

    const normalizedStyle = normalizedLagunaPaddingStyle(resolvedStyle);

    return {
        ...normalizedStyle,
        ...siteBuilderRebasedAuthoritativeStyleVariables(
            authoredStyle,
            normalizedStyle,
        ),
    };
}

function atomicIconOuterStyle(block: ShopTemplateBlock): CSSProperties | undefined {
    const style = atomicBlockVisualStyle(block);

    if (!style) {
        return interactionStyle(block);
    }

    const {
        backgroundColor,
        borderColor,
        borderRadius,
        borderStyle,
        borderWidth,
        height,
        padding,
        paddingBottom,
        paddingLeft,
        paddingRight,
        paddingTop,
        width,
        ...outerStyle
    } = style;

    void backgroundColor;
    void borderColor;
    void borderRadius;
    void borderStyle;
    void borderWidth;
    void height;
    void padding;
    void paddingBottom;
    void paddingLeft;
    void paddingRight;
    void paddingTop;
    void width;

    const resolvedStyle = mergeStyles(
        Object.keys(outerStyle).length > 0 ? outerStyle : undefined,
        interactionStyle(block),
    );

    if (!resolvedStyle) {
        return undefined;
    }

    return {
        ...resolvedStyle,
        ...siteBuilderRebasedAuthoritativeStyleVariables(
            style as SiteBuilderStyleRecord,
            resolvedStyle as SiteBuilderStyleRecord,
        ),
    };
}

function multilineItems(value: string, fallback: string[]): string[] {
    const items = value
        .split("\n")
        .map((item) => item.trim())
        .filter(Boolean);

    return items.length > 0 ? items : fallback;
}

type ChartType = "barHorizontal" | "barVertical" | "doughnut" | "line" | "pie" | "radar";
type ChartLegendPosition = "bottom" | "left" | "none" | "right" | "top";
type ChartSeries = { borderColor: string; color: string; label: string };
type ChartRow = { label: string; values: number[] };

const chartDefaultSeries: ChartSeries[] = [{ borderColor: "var(--site-border-strong)", color: "var(--site-primary)", label: "One" }];
const chartDefaultRows: ChartRow[] = [
    { label: "First", values: [12] },
    { label: "Second", values: [24] },
    { label: "Third", values: [18] },
    { label: "Fourth", values: [17] },
    { label: "Fifth", values: [10] },
];
const chartPalette = [
    "var(--site-primary)",
    "var(--site-accent)",
    "color-mix(in srgb, var(--site-primary) 62%, var(--site-accent))",
    "color-mix(in srgb, var(--site-accent) 62%, var(--site-primary))",
    "color-mix(in srgb, var(--site-primary) 52%, var(--site-text))",
    "color-mix(in srgb, var(--site-accent) 52%, var(--site-text))",
];

function parseJsonArray(value: unknown): unknown[] {
    if (Array.isArray(value)) {
        return value;
    }

    if (typeof value !== "string" || value.trim() === "") {
        return [];
    }

    try {
        const parsed = JSON.parse(value) as unknown;

        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function numericChartValue(value: unknown, fallback = 0): number {
    const parsed = typeof value === "number" ? value : Number(String(value ?? "").replace(",", "."));

    return Number.isFinite(parsed) ? parsed : fallback;
}

function chartSeriesForBlock(block: ShopTemplateBlock, message: ShopStorefrontMessage): ChartSeries[] {
    const content = blockRecord(block, "content");
    const parsed = parseJsonArray(content.chartSeries)
        .map((item, index): ChartSeries | null => {
            if (!item || typeof item !== "object" || Array.isArray(item)) {
                return null;
            }

            const record = item as Record<string, unknown>;
            const fallbackColor = chartPalette[index % chartPalette.length] ?? chartDefaultSeries[0].color;
            const label = typeof record.label === "string" && record.label.trim() !== "" ? record.label.trim() : message("ui.chart.series", { number: index + 1 });
            const color = typeof record.color === "string" && record.color.trim() !== "" ? record.color.trim() : fallbackColor;
            const borderColor = typeof record.borderColor === "string" && record.borderColor.trim() !== "" ? record.borderColor.trim() : color;

            return { borderColor, color, label };
        })
        .filter((item): item is ChartSeries => item !== null);

    if (parsed.length > 0) {
        return parsed;
    }

    const fallbackColor = blockOptionText(block, "chartDatasetColor") || chartDefaultSeries[0].color;
    const fallbackBorder = blockOptionText(block, "chartDatasetBorder") || chartDefaultSeries[0].borderColor;

    return [{ borderColor: fallbackBorder, color: fallbackColor, label: message("ui.chart.series", { number: 1 }) }];
}

function legacyChartRowsFromItems(block: ShopTemplateBlock, seriesCount: number, message: ShopStorefrontMessage): ChartRow[] {
    return multilineItems(blockText(block, "items", ""), [])
        .map((item) => {
            const [label, rawValue = "0"] = item.split(":");
            const value = numericChartValue(rawValue, 0);

            return {
                label: label.trim() || message("ui.chart.item"),
                values: Array.from({ length: seriesCount }, (_, index) => (index === 0 ? value : 0)),
            };
        });
}

function chartRowsForBlock(block: ShopTemplateBlock, seriesCount: number, message: ShopStorefrontMessage): ChartRow[] {
    const content = blockRecord(block, "content");
    const parsed = parseJsonArray(content.chartRows)
        .map((item): ChartRow | null => {
            if (!item || typeof item !== "object" || Array.isArray(item)) {
                return null;
            }

            const record = item as Record<string, unknown>;
            const values = Array.isArray(record.values) ? record.values : [];
            const label = typeof record.label === "string" && record.label.trim() !== "" ? record.label.trim() : message("ui.chart.item");

            return {
                label,
                values: Array.from({ length: seriesCount }, (_, index) => numericChartValue(values[index], 0)),
            };
        })
        .filter((item): item is ChartRow => item !== null);

    if (parsed.length > 0) {
        return parsed;
    }

    const legacyRows = legacyChartRowsFromItems(block, seriesCount, message);

    if (legacyRows.length > 0) {
        return legacyRows;
    }

    return chartDefaultRows.map((row) => ({
        label: row.label,
        values: Array.from({ length: seriesCount }, (_, index) => row.values[index] ?? 0),
    }));
}

function chartType(block: ShopTemplateBlock): ChartType {
    const value = blockOptionText(block, "chartType");

    return value === "barHorizontal" || value === "line" || value === "pie" || value === "doughnut" || value === "radar"
        ? value
        : "barVertical";
}

function chartLegendPosition(block: ShopTemplateBlock): ChartLegendPosition {
    const value = blockOptionText(block, "chartLegend");

    return value === "left" || value === "right" || value === "bottom" || value === "none" ? value : "top";
}

function chartOptionNumber(block: ShopTemplateBlock, key: string): number | null {
    const value = blockOptionText(block, key);
    const parsed = Number(value.replace(",", "."));

    return Number.isFinite(parsed) ? parsed : null;
}

function chartAxisBounds(block: ShopTemplateBlock, rows: ChartRow[]): { max: number; min: number } {
    const values = rows.flatMap((row) => row.values);
    const dataMin = Math.min(0, ...values);
    const dataMax = Math.max(1, ...values);
    const min = chartOptionNumber(block, "chartMinAxis") ?? dataMin;
    const max = chartOptionNumber(block, "chartMaxAxis") ?? Math.ceil(dataMax / 5) * 5;

    return max > min ? { max, min } : { max: min + 1, min };
}

function chartValueRatio(value: number, min: number, max: number): number {
    return Math.max(0, Math.min(1, (value - min) / (max - min)));
}

function chartPoint(cx: number, cy: number, radius: number, angle: number): { x: number; y: number } {
    const radians = (angle - 90) * Math.PI / 180;

    return {
        x: cx + (radius * Math.cos(radians)),
        y: cy + (radius * Math.sin(radians)),
    };
}

function chartArcPath(cx: number, cy: number, radius: number, startAngle: number, endAngle: number): string {
    const start = chartPoint(cx, cy, radius, endAngle);
    const end = chartPoint(cx, cy, radius, startAngle);
    const largeArc = endAngle - startAngle <= 180 ? "0" : "1";

    return `M ${cx} ${cy} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 0 ${end.x} ${end.y} Z`;
}

function chartTooltipText(row: ChartRow, series: ChartSeries, value: number): string {
    return `${row.label} - ${series.label}: ${value}`;
}

function ChartLegend({
    items,
    position,
}: {
    items: ChartSeries[];
    position: ChartLegendPosition;
}) {
    if (position === "none") {
        return null;
    }

    return (
        <div className={classNames("flex gap-3 text-sm font-black text-[var(--site-text)]", position === "left" || position === "right" ? "min-w-24 flex-col" : "flex-wrap justify-center")}>
            {items.map((item) => (
                <span key={item.label} className="inline-flex items-center gap-2">
                    <span className="h-4 w-12 rounded-sm" style={{ backgroundColor: item.color }} />
                    <span>{item.label}</span>
                </span>
            ))}
        </div>
    );
}

function ChartGraphic({
    block,
    rows,
    series,
    type,
}: {
    block: ShopTemplateBlock;
    rows: ChartRow[];
    series: ChartSeries[];
    type: ChartType;
}) {
    const { max, min } = chartAxisBounds(block, rows);
    const tooltipEnabled = blockOptionText(block, "chartTooltip") !== "false";
    const strokeWidth = Math.max(0, chartOptionNumber(block, "chartBorderWidth") ?? 2);
    const axisColor = "var(--site-border-strong)";
    const gridColor = "var(--site-border)";
    const textColor = "var(--site-text)";

    if (type === "pie" || type === "doughnut") {
        const values = rows.map((row) => Math.max(0, row.values[0] ?? 0));
        const total = values.reduce((sum, value) => sum + value, 0) || 1;
        let angle = 0;

        return (
            <svg viewBox="0 0 640 330" role="img" className="h-full min-h-[300px] w-full overflow-visible">
                {rows.map((row, index) => {
                    const value = values[index] ?? 0;
                    const nextAngle = angle + (value / total * 360);
                    const color = chartPalette[index % chartPalette.length] ?? series[0].color;
                    const path = chartArcPath(320, 165, 128, angle, nextAngle);

                    angle = nextAngle;

                    return type === "doughnut" ? (
                        <path key={row.label} d={path} fill={color} stroke="var(--site-background)" strokeWidth="6">
                            {tooltipEnabled ? <title>{`${row.label}: ${value}`}</title> : null}
                        </path>
                    ) : (
                        <path key={row.label} d={path} fill={color} stroke="var(--site-background)" strokeWidth="3">
                            {tooltipEnabled ? <title>{`${row.label}: ${value}`}</title> : null}
                        </path>
                    );
                })}
                {type === "doughnut" ? <circle cx="320" cy="165" r="72" fill="var(--site-background)" /> : null}
            </svg>
        );
    }

    if (type === "radar") {
        const cx = 320;
        const cy = 166;
        const radius = 124;
        const angles = rows.map((_, index) => index * 360 / Math.max(1, rows.length));
        const ringSteps = [0.25, 0.5, 0.75, 1];

        return (
            <svg viewBox="0 0 640 330" role="img" className="h-full min-h-[300px] w-full overflow-visible">
                {ringSteps.map((step) => (
                    <polygon
                        key={step}
                        fill="none"
                        points={angles.map((angle) => {
                            const point = chartPoint(cx, cy, radius * step, angle);

                            return `${point.x},${point.y}`;
                        }).join(" ")}
                        stroke={gridColor}
                        strokeWidth="1"
                    />
                ))}
                {rows.map((row, index) => {
                    const point = chartPoint(cx, cy, radius + 24, angles[index] ?? 0);

                    return (
                        <g key={row.label}>
                            <line x1={cx} y1={cy} x2={chartPoint(cx, cy, radius, angles[index] ?? 0).x} y2={chartPoint(cx, cy, radius, angles[index] ?? 0).y} stroke={gridColor} />
                            <text x={point.x} y={point.y} textAnchor="middle" dominantBaseline="middle" fontSize="14" fontWeight="700" fill={textColor}>{row.label}</text>
                        </g>
                    );
                })}
                {series.map((item, seriesIndex) => {
                    const points = rows.map((row, rowIndex) => {
                        const value = row.values[seriesIndex] ?? 0;
                        const point = chartPoint(cx, cy, radius * chartValueRatio(value, min, max), angles[rowIndex] ?? 0);

                        return `${point.x},${point.y}`;
                    }).join(" ");

                    return (
                        <polygon key={item.label} points={points} fill={item.color} fillOpacity="0.22" stroke={item.borderColor} strokeWidth={strokeWidth || 2}>
                            {tooltipEnabled ? <title>{item.label}</title> : null}
                        </polygon>
                    );
                })}
            </svg>
        );
    }

    if (type === "barHorizontal") {
        const plot = { height: 236, left: 92, top: 28, width: 500 };
        const groupHeight = plot.height / Math.max(1, rows.length);
        const barHeight = Math.max(8, Math.min(22, groupHeight / Math.max(1, series.length) - 4));

        return (
            <svg viewBox="0 0 640 330" role="img" className="h-full min-h-[300px] w-full overflow-visible">
                {[0, 0.25, 0.5, 0.75, 1].map((step) => {
                    const x = plot.left + plot.width * step;
                    const value = Math.round(min + (max - min) * step);

                    return (
                        <g key={step}>
                            <line x1={x} x2={x} y1={plot.top} y2={plot.top + plot.height} stroke={gridColor} />
                            <text x={x} y={plot.top + plot.height + 26} textAnchor="middle" fontSize="14" fontWeight="700" fill={textColor}>{value}</text>
                        </g>
                    );
                })}
                {rows.map((row, rowIndex) => (
                    <g key={row.label}>
                        <text x={plot.left - 12} y={plot.top + rowIndex * groupHeight + groupHeight / 2} textAnchor="end" dominantBaseline="middle" fontSize="14" fontWeight="700" fill={textColor}>{row.label}</text>
                        {series.map((item, seriesIndex) => {
                            const value = row.values[seriesIndex] ?? 0;
                            const y = plot.top + rowIndex * groupHeight + ((groupHeight - (barHeight * series.length)) / 2) + seriesIndex * barHeight;
                            const width = Math.max(1, plot.width * chartValueRatio(value, min, max));

                            return (
                                <rect key={item.label} x={plot.left} y={y} width={width} height={barHeight - 3} fill={item.color} stroke={item.borderColor} strokeWidth={strokeWidth}>
                                    {tooltipEnabled ? <title>{chartTooltipText(row, item, value)}</title> : null}
                                </rect>
                            );
                        })}
                    </g>
                ))}
            </svg>
        );
    }

    const plot = { height: 246, left: 54, top: 20, width: 540 };
    const categoryWidth = plot.width / Math.max(1, rows.length);

    return (
        <svg viewBox="0 0 640 330" role="img" className="h-full min-h-[300px] w-full overflow-visible">
            {[0, 0.25, 0.5, 0.75, 1].map((step) => {
                const y = plot.top + plot.height - plot.height * step;
                const value = Math.round(min + (max - min) * step);

                return (
                    <g key={step}>
                        <line x1={plot.left} x2={plot.left + plot.width} y1={y} y2={y} stroke={gridColor} />
                        <text x={plot.left - 12} y={y + 5} textAnchor="end" fontSize="14" fontWeight="700" fill={textColor}>{value}</text>
                    </g>
                );
            })}
            {rows.map((row, rowIndex) => {
                const x = plot.left + rowIndex * categoryWidth + categoryWidth / 2;

                return (
                    <g key={row.label}>
                        <line x1={x} x2={x} y1={plot.top} y2={plot.top + plot.height} stroke={gridColor} />
                        <text x={x} y={plot.top + plot.height + 26} textAnchor="middle" fontSize="14" fontWeight="700" fill={textColor}>{row.label}</text>
                    </g>
                );
            })}
            {type === "line" ? series.map((item, seriesIndex) => {
                const points = rows.map((row, rowIndex) => {
                    const x = plot.left + rowIndex * categoryWidth + categoryWidth / 2;
                    const y = plot.top + plot.height - plot.height * chartValueRatio(row.values[seriesIndex] ?? 0, min, max);

                    return `${x},${y}`;
                }).join(" ");

                return (
                    <g key={item.label}>
                        <polyline points={points} fill="none" stroke={item.borderColor} strokeLinecap="round" strokeLinejoin="round" strokeWidth={Math.max(2, strokeWidth)} />
                        {rows.map((row, rowIndex) => {
                            const value = row.values[seriesIndex] ?? 0;
                            const x = plot.left + rowIndex * categoryWidth + categoryWidth / 2;
                            const y = plot.top + plot.height - plot.height * chartValueRatio(value, min, max);

                            return (
                                <circle key={row.label} cx={x} cy={y} r="5" fill={item.color} stroke="var(--site-background)" strokeWidth="2">
                                    {tooltipEnabled ? <title>{chartTooltipText(row, item, value)}</title> : null}
                                </circle>
                            );
                        })}
                    </g>
                );
            }) : rows.map((row, rowIndex) => {
                const groupWidth = Math.min(88, categoryWidth * 0.72);
                const barWidth = Math.max(8, groupWidth / Math.max(1, series.length) - 4);
                const groupLeft = plot.left + rowIndex * categoryWidth + (categoryWidth - groupWidth) / 2;

                return series.map((item, seriesIndex) => {
                    const value = row.values[seriesIndex] ?? 0;
                    const height = plot.height * chartValueRatio(value, min, max);
                    const x = groupLeft + seriesIndex * (barWidth + 4);
                    const y = plot.top + plot.height - height;

                    return (
                        <rect key={`${row.label}:${item.label}`} x={x} y={y} width={barWidth} height={height} fill={item.color} stroke={item.borderColor} strokeWidth={strokeWidth}>
                            {tooltipEnabled ? <title>{chartTooltipText(row, item, value)}</title> : null}
                        </rect>
                    );
                });
            })}
            <line x1={plot.left} x2={plot.left} y1={plot.top} y2={plot.top + plot.height} stroke={axisColor} />
            <line x1={plot.left} x2={plot.left + plot.width} y1={plot.top + plot.height} y2={plot.top + plot.height} stroke={axisColor} />
        </svg>
    );
}

const defaultSocialNetworkUrls = [
    "https://www.facebook.com/your-page",
    "https://www.instagram.com/your-page",
    "https://www.youtube.com/@your-channel",
];

type SocialNetwork = "email" | "facebook" | "instagram" | "link" | "linkedin" | "pinterest" | "tiktok" | "whatsapp" | "x" | "youtube";

function defaultSocialUrlForLabel(label: string): string {
    const normalized = label.trim().toLowerCase();

    if (normalized === "fb" || normalized.includes("facebook")) return defaultSocialNetworkUrls[0];
    if (normalized === "ig" || normalized.includes("instagram")) return defaultSocialNetworkUrls[1];
    if (normalized === "yt" || normalized.includes("youtube")) return defaultSocialNetworkUrls[2];
    if (normalized === "x" || normalized.includes("twitter")) return "https://x.com/your-page";
    if (normalized.includes("linkedin")) return "https://www.linkedin.com/company/your-page";
    if (normalized.includes("pinterest")) return "https://www.pinterest.com/your-page";
    if (normalized.includes("whatsapp")) return "https://wa.me/";
    if (normalized.includes("e-mail") || normalized.includes("email") || normalized.includes("mail")) return "mailto:";

    return label;
}

function socialHrefFromLine(line: string): string {
    const value = defaultSocialUrlForLabel(line).trim();

    if (!value || value === "#") return "#";
    if (/^(https?:|mailto:|tel:|#)/i.test(value)) return value;
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return `mailto:${value}`;
    if (value.includes(".")) return `https://${value}`;

    return value;
}

function socialNetworkFromHref(href: string): SocialNetwork {
    const value = socialHrefFromLine(href).toLowerCase();
    let host: string;

    if (value.startsWith("mailto:")) return "email";

    try {
        host = new URL(value.startsWith("http") ? value : `https://${value}`).hostname.toLowerCase();
    } catch {
        host = value;
    }

    if (host.includes("facebook.") || host === "fb.com" || host.endsWith(".fb.com")) return "facebook";
    if (host.includes("instagram.")) return "instagram";
    if (host.includes("youtube.") || host.includes("youtu.be")) return "youtube";
    if (host === "x.com" || host.endsWith(".x.com") || host.includes("twitter.")) return "x";
    if (host.includes("tiktok.")) return "tiktok";
    if (host.includes("linkedin.")) return "linkedin";
    if (host.includes("pinterest.")) return "pinterest";
    if (host.includes("whatsapp.") || host === "wa.me" || host.endsWith(".wa.me")) return "whatsapp";

    return "link";
}

function socialNetworkLabel(network: SocialNetwork, genericLabel: string): string {
    if (network === "email") return "E-mail";
    if (network === "facebook") return "Facebook";
    if (network === "instagram") return "Instagram";
    if (network === "youtube") return "YouTube";
    if (network === "x") return "X";
    if (network === "tiktok") return "TikTok";
    if (network === "linkedin") return "LinkedIn";
    if (network === "pinterest") return "Pinterest";
    if (network === "whatsapp") return "WhatsApp";

    return genericLabel;
}

function socialNetworkColor(network: SocialNetwork): string {
    if (network === "email") return "var(--site-muted)";
    if (network === "facebook") return "var(--site-primary)";
    if (network === "instagram") return "var(--site-accent)";
    if (network === "youtube") return "color-mix(in srgb, var(--site-primary) 74%, var(--site-text))";
    if (network === "x") return "var(--site-text)";
    if (network === "tiktok") return "var(--site-text)";
    if (network === "linkedin") return "color-mix(in srgb, var(--site-primary) 82%, var(--site-text))";
    if (network === "pinterest") return "color-mix(in srgb, var(--site-primary) 60%, var(--site-accent))";
    if (network === "whatsapp") return "color-mix(in srgb, var(--site-accent) 78%, var(--site-text))";

    return "currentColor";
}

type SocialMediaItem = {
    block?: ShopTemplateBlock;
    href: string;
    icon: string;
    label: string;
    network: SocialNetwork;
};

function socialLinkFromSlotBlock(item: ShopTemplateBlock, genericLabel: string): SocialMediaItem {
    const rawHref = blockText(item, "href", "");
    const href = socialHrefFromLine(rawHref || defaultSocialUrlForLabel(blockText(item, "text", blockText(item, "label", ""))));
    const network = socialNetworkFromHref(href);
    const icon = blockText(item, "icon", `social:${network}`);

    return {
        block: item,
        href,
        icon,
        label: blockText(item, "label", socialNetworkLabel(network, genericLabel)),
        network,
    };
}

function socialLinksFromBlock(
    block: ShopTemplateBlock | null | undefined,
    genericLabel: string,
    includeEditorDefaults: boolean,
): SocialMediaItem[] {
    const slotItems = slotChildren(block, "items");

    if (slotItems.length > 0) {
        return slotItems.map((item) => socialLinkFromSlotBlock(item, genericLabel));
    }

    const socialLinks = blockText(block, "socialLinks", "");
    const rawLinks = socialLinks
        ? multilineItems(socialLinks, [])
        : [
            blockText(block, "facebookUrl", ""),
            blockText(block, "instagramUrl", ""),
            blockText(block, "youtubeUrl", ""),
        ].filter(Boolean);
    const legacyLinks = rawLinks.length > 0 ? rawLinks : multilineItems(blockText(block, "items", ""), []);
    const links = legacyLinks.length > 0
        ? legacyLinks
        : includeEditorDefaults ? defaultSocialNetworkUrls : [];

    return links.map((line) => {
        const href = socialHrefFromLine(line);
        const network = socialNetworkFromHref(href);
        const icon = `social:${network}`;

        return {
            href,
            icon,
            label: socialNetworkLabel(network, genericLabel),
            network,
        };
    });
}

function socialIconSize(block: ShopTemplateBlock | null | undefined): number {
    const rawValue = blockOptionText(block, "socialIconSize").replace("px", "");
    const value = Number(rawValue);

    return Number.isFinite(value) ? Math.max(24, Math.min(96, Math.round(value))) : 40;
}

function socialAlignmentClass(block: ShopTemplateBlock | null | undefined): string {
    const alignment = blockOptionText(block, "socialAlignment");

    if (alignment === "center") return "justify-center";
    if (alignment === "right") return "justify-end";

    return "justify-start";
}

function socialIconRadius(layout: string): string {
    if (layout === "square") return "0";
    if (layout === "disk") return "12px";

    return "9999px";
}

function socialIconTheme(theme: string): { background: string; color: string } {
    if (theme === "dark") return { background: "var(--site-inverse)", color: "var(--site-on-inverse)" };
    if (theme === "light") return { background: "var(--site-surface)", color: "var(--site-on-surface)" };
    if (theme === "primary") return { background: "var(--site-primary)", color: "var(--site-on-primary)" };
    if (theme === "muted") return { background: "var(--site-surface)", color: "var(--site-text)" };

    return { background: "var(--site-surface)", color: "currentColor" };
}

function socialTitleLayoutClass(block: ShopTemplateBlock | null | undefined): string {
    const titlePosition = blockOptionText(block, "socialTitlePosition") || "none";

    if (titlePosition === "top") return "grid gap-3";
    if (titlePosition === "left") return "flex flex-wrap items-center gap-4";

    return "grid";
}

function socialLinkDecorationClass(block: ShopTemplateBlock | null | undefined): string {
    const linkStyle = blockOptionText(block, "socialLinkStyle") || "noUnderline";

    if (linkStyle === "underlineAlways") return "no-underline border-b-2 border-current";
    if (linkStyle === "underlineOnHover") return "no-underline border-b-2 border-transparent hover:border-current";

    return "no-underline border-b-2 border-transparent";
}

function SocialNetworkIcon({ network, className }: { className?: string; network: SocialNetwork }) {
    if (network === "email") {
        return (
            <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
                <rect x="4" y="6" width="16" height="12" rx="2.5" fill="none" stroke="currentColor" strokeWidth="2" />
                <path d="m5 8 7 5 7-5" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
            </svg>
        );
    }

    if (network === "facebook") {
        return (
            <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M14.2 8.2V6.9c0-.7.5-1.1 1.2-1.1h1.4V3.4c-.7-.1-1.5-.2-2.3-.2-2.4 0-4.1 1.5-4.1 4.2v.8H7.8v2.9h2.6v9.7h3.2v-9.7h2.6l.4-2.9h-3Z" fill="currentColor" />
            </svg>
        );
    }

    if (network === "instagram") {
        return (
            <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
                <rect x="4" y="4" width="16" height="16" rx="5" fill="none" stroke="currentColor" strokeWidth="2" />
                <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
                <circle cx="17" cy="7" r="1.25" fill="currentColor" />
            </svg>
        );
    }

    if (network === "youtube") {
        return (
            <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3" y="6" width="18" height="12" rx="4" fill="currentColor" />
                <path d="m10 9 6 3-6 3V9Z" fill="var(--site-background)" />
            </svg>
        );
    }

    if (network === "x") {
        return (
            <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 4h4.2l3.6 5 4.4-5H20l-5.9 6.8L20 20h-4.2l-4-5.5L7 20H4.2l6.3-7.2L5 4Zm2.7 1.7 9.1 12.6h1.5L9.2 5.7H7.7Z" fill="currentColor" />
            </svg>
        );
    }

    if (network === "linkedin") {
        return (
            <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M7.1 20H3.8V8.9h3.3V20ZM5.5 7.4a1.9 1.9 0 1 1 0-3.8 1.9 1.9 0 0 1 0 3.8ZM20.2 20h-3.3v-5.5c0-1.3 0-3-1.8-3s-2.1 1.4-2.1 2.9V20H9.7V8.9h3.1v1.5h.1c.4-.8 1.5-1.7 3.1-1.7 3.3 0 3.9 2.2 3.9 5V20Z" fill="currentColor" />
            </svg>
        );
    }

    if (network === "pinterest") {
        return (
            <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12.1 3.1c-4.6 0-7.5 3.1-7.5 6.8 0 2.1 1.2 3.8 2.8 4.4.3.1.5 0 .6-.3l.2-.9c.1-.3 0-.5-.2-.8-.6-.7-.9-1.5-.9-2.4 0-2.7 2.1-4.8 5.1-4.8 2.8 0 4.6 1.7 4.6 4.2 0 3-1.4 5.6-3.5 5.6-1.1 0-1.9-.9-1.7-2 .3-1.2.9-2.5.9-3.4 0-.8-.4-1.5-1.3-1.5-1 0-1.8 1.1-1.8 2.5 0 .9.3 1.5.3 1.5l-1.2 5.1c-.3 1.3-.2 3-.1 4.1 0 .2.2.2.3.1.5-.7 1.7-2.5 2.1-4l.5-1.8c.5.8 1.6 1.5 2.9 1.5 3.9 0 6.5-3.6 6.5-8.1 0-3.5-3-6.6-7.6-6.6Z" fill="currentColor" />
            </svg>
        );
    }

    if (network === "whatsapp") {
        return (
            <svg className={className} viewBox="0 0 24 24" aria-hidden="true" fill="none">
                <path d="M4.7 19.4 5.8 16A7.5 7.5 0 1 1 8 18.1l-3.3 1.3Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                <path d="M9 8.4c-.3 0-.7.1-.9.4-.3.4-.8 1-.8 2 0 1.2.9 2.4 1 2.6.1.2 1.8 2.9 4.4 3.9 1.9.8 2.3.6 2.8.6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2-.1-.1-.2-.2-.5-.3l-1.6-.8c-.2-.1-.4-.1-.6.1l-.7.8c-.2.2-.3.2-.6.1-.3-.1-1.1-.4-2.1-1.3-.8-.7-1.3-1.6-1.5-1.9-.2-.3 0-.4.1-.6l.4-.4c.1-.2.2-.3.3-.5.1-.2.1-.3 0-.5l-.7-1.6c-.2-.2-.4-.2-.6-.2H9Z" fill="currentColor" />
            </svg>
        );
    }

    if (network === "tiktok") {
        return (
            <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M16.7 4c.4 1.6 1.5 2.8 3.3 3v3.1c-1.2 0-2.3-.4-3.3-1v5.7a5.2 5.2 0 1 1-5.2-5.2c.4 0 .8 0 1.1.1V13c-.3-.1-.7-.2-1.1-.2a2 2 0 1 0 2 2V4h3.2Z" fill="currentColor" />
            </svg>
        );
    }

    return (
        <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M10 13a5 5 0 0 0 7.1.1l2-2A5 5 0 0 0 12 4l-1.1 1.1" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
            <path d="M14 11a5 5 0 0 0-7.1-.1l-2 2A5 5 0 0 0 12 20l1.1-1.1" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
        </svg>
    );
}

function SocialMediaIcons({ block, className = "", partPrefix }: { block: ShopTemplateBlock | null | undefined; className?: string; partPrefix?: string }) {
    const message = useShopStorefrontMessage();
    const editorMode = useContext(ShopEditorModeContext);
    const links = socialLinksFromBlock(block, message("ui.social.link"), editorMode);
    const slotItems = slotChildren(block, "items");
    const renderSlotItems = slotItems.length > 0 || slotIsDefined(block, "items");
    const rawLayout = blockOptionText(block, "socialIconLayout") || "disk";
    const layout = rawLayout === "plain" ? "none" : rawLayout === "outline" ? "circle" : rawLayout;
    const size = socialIconSize(block);
    const brandColorEnabled = blockOptionText(block, "socialIconBrandColor") !== "false";
    const theme = socialIconTheme(blockOptionText(block, "socialIconTheme") || "default");
    const customColor = blockOptionText(block, "socialIconColor");
    const customBackground = blockOptionText(block, "socialIconBackgroundColor");
    const customBackgroundImage = blockOptionText(block, "socialIconBackgroundImage");
    const titlePosition = blockOptionText(block, "socialTitlePosition") || "none";
    const title = blockText(block, "title", message("ui.social.title"));
    const titleElement = titlePosition === "none" ? null : (
        <span className="text-sm font-black text-current">{title}</span>
    );
    const dispatchEditorIconSelect = (event: MouseEvent<HTMLElement> | PointerEvent<HTMLElement>, itemPart: string) => {
        if (!editorMode) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const blockElement = event.currentTarget.closest<HTMLElement>("[data-shop-block-index]");
        const eventWindow = event.currentTarget.ownerDocument.defaultView ?? window;

        event.currentTarget.dispatchEvent(new eventWindow.CustomEvent(SHOP_SOCIAL_ICON_SELECT_EVENT, {
            bubbles: true,
            cancelable: true,
            composed: true,
            detail: {
                blockIndex: blockElement?.dataset.shopBlockIndex ?? null,
                clientX: event.clientX,
                clientY: event.clientY,
                part: itemPart,
            },
        }));
    };
    const renderLink = (link: SocialMediaItem, itemPart: string): ReactNode => {
        if (!editorMode && (link.href.trim() === "" || link.href.trim() === "#")) {
            return null;
        }

        const linkPartStyle = { ...(partStyle(link.block, "primaryButton") ?? {}) };

        // A social icon inherits button colors/interactions, but not the button
        // component's default text sizing and padding geometry.
        delete linkPartStyle.fontSize;
        delete linkPartStyle.minHeight;
        delete linkPartStyle.padding;
        delete linkPartStyle.paddingBottom;
        delete linkPartStyle.paddingLeft;
        delete linkPartStyle.paddingRight;
        delete linkPartStyle.paddingTop;
        delete linkPartStyle.justifyContent;
        delete linkPartStyle.textAlign;

        const linkStyle = mergeStyles(
            atomicButtonBlockStyle(link.block),
            linkPartStyle,
        );
        const iconColor = blockOptionText(link.block, "socialIconColor")
            || blockOptionText(link.block, "textColor")
            || (typeof linkStyle?.color === "string" ? linkStyle.color : "")
            || customColor
            || (brandColorEnabled ? socialNetworkColor(link.network) : theme.color);
        const iconBackgroundColor = blockOptionText(link.block, "socialIconBackgroundColor")
            || blockOptionText(link.block, "backgroundColor")
            || (typeof linkStyle?.backgroundColor === "string" ? linkStyle.backgroundColor : "");
        const iconBackgroundImage = blockOptionText(link.block, "socialIconBackgroundImage");
        const backgroundColor = layout === "none" ? "transparent" : iconBackgroundColor || customBackground || theme.background;
        const backgroundImage = layout === "none" ? undefined : iconBackgroundImage || customBackgroundImage || undefined;
        const openInNewWindow = blockOptionText(link.block, "openInNewWindow") === "true";

        return (
            <a
                {...atomicBlockInteractionAttributes(link.block)}
                href={link.href}
                aria-label={link.label}
                data-shop-block-part={itemPart}
                data-shop-social-link-part={itemPart}
                target={openInNewWindow ? "_blank" : undefined}
                rel={openInNewWindow ? "noopener noreferrer" : undefined}
                draggable={editorMode ? false : undefined}
                onClickCapture={editorMode ? (event) => dispatchEditorIconSelect(event, itemPart) : undefined}
                onPointerDownCapture={editorMode ? (event) => dispatchEditorIconSelect(event, itemPart) : undefined}
                className={classNames(
                    "grid place-items-center transition hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/30",
                    layout !== "none" && "shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_10%,transparent)]",
                    socialLinkDecorationClass(block),
                )}
                style={mergeStyles({
                    backgroundColor,
                    backgroundImage,
                    borderRadius: socialIconRadius(layout),
                    color: iconColor,
                    height: size,
                    width: size,
                }, linkStyle)}
            >
                {link.icon.startsWith("social:")
                    ? <SocialNetworkIcon network={link.icon.slice("social:".length) as SocialNetwork} className="h-[52%] w-[52%]" />
                    : atomicIconGlyph(link.icon, "h-[52%] w-[52%]")}
            </a>
        );
    };
    const iconElements = renderSlotItems ? (
        <SlotBlockGroup
            block={block}
            slotKey="items"
            partPrefix={partPrefix}
            className={classNames("flex flex-wrap gap-2", socialAlignmentClass(block), className)}
            itemClassName="w-fit max-w-full"
            renderItem={({ childBlock, itemPart }) => renderLink(socialLinkFromSlotBlock(childBlock, message("ui.social.link")), itemPart)}
        />
    ) : (
        <div {...editablePart("socialLinks")} className={classNames("flex flex-wrap gap-2", socialAlignmentClass(block), className)}>
            {links.map((link, index) => {
                const itemPart = partPrefix ? `${partPrefix}:${slotPart("items", index)}` : slotPart("items", index);

                return (
                    <div
                        key={`${link.href}-${index}`}
                        data-shop-block-part={itemPart}
                        data-shop-social-link-part={itemPart}
                        className="w-fit max-w-full"
                    >
                        {renderLink(link, itemPart)}
                    </div>
                );
            })}
        </div>
    );

    return (
        <div {...atomicBlockInteractionAttributes(block)} className={socialTitleLayoutClass(block)} style={atomicBlockStyle(block)}>
            {titleElement}
            {iconElements}
        </div>
    );
}

const defaultShareNetworks: SocialNetwork[] = [...SITE_BUILDER_DEFAULT_SHARE_NETWORKS];

function shareNetworkFromText(value: string): SocialNetwork {
    return siteBuilderShareNetwork(value);
}

function shareNetworkFromIconBlock(block: ShopTemplateBlock): SocialNetwork {
    const icon = blockText(block, "icon", "");
    const label = blockText(block, "label", "");

    if (icon.startsWith("social:")) {
        const network = icon.slice("social:".length);

        if (["email", "facebook", "instagram", "link", "linkedin", "pinterest", "tiktok", "whatsapp", "x", "youtube"].includes(network)) {
            return network as SocialNetwork;
        }
    }

    return shareNetworkFromText(`${icon} ${label}`);
}

function defaultShareIconBlock(network: SocialNetwork, genericLabel: string): ShopTemplateBlock {
    return {
        type: "icon",
        content: {
            icon: `social:${network}`,
            label: socialNetworkLabel(network, genericLabel),
        },
    };
}

function shareItemsFromBlock(block: ShopTemplateBlock, genericLabel: string): ShopTemplateBlock[] {
    const slotItems = slotChildren(block, "items");

    if (slotItems.length > 0 || slotIsDefined(block, "items")) {
        return slotItems;
    }

    const legacyItems = multilineItems(blockText(block, "items", ""), []);

    if (legacyItems.length > 0) {
        return legacyItems.map((item) => defaultShareIconBlock(shareNetworkFromText(item), genericLabel));
    }

    return defaultShareNetworks.map((network) => defaultShareIconBlock(network, genericLabel));
}

function shareIconSize(block: ShopTemplateBlock | null | undefined): number {
    const rawValue = (blockOptionText(block, "shareIconSize") || blockOptionText(block, "socialIconSize")).replace("px", "");
    const value = Number(rawValue);

    return Number.isFinite(value) ? Math.max(24, Math.min(96, Math.round(value))) : 40;
}

function shareAlignmentClass(block: ShopTemplateBlock | null | undefined): string {
    const alignment = blockOptionText(block, "shareAlignment") || blockOptionText(block, "socialAlignment");

    if (alignment === "center") return "justify-center";
    if (alignment === "right") return "justify-end";

    return "justify-start";
}

function shareTitleLayoutClass(block: ShopTemplateBlock | null | undefined): string {
    const position = blockOptionText(block, "shareTitlePosition") || blockOptionText(block, "socialTitlePosition") || "none";

    if (position === "top") return "grid gap-3";
    if (position === "left") return "flex flex-wrap items-center gap-3";

    return "grid";
}

function shareLinkStyleClass(block: ShopTemplateBlock | null | undefined): string {
    const style = blockOptionText(block, "shareLinkStyle") || blockOptionText(block, "socialLinkStyle") || "noUnderline";

    if (style === "underline" || style === "underlineAlways") return "no-underline border-b-2 border-current";
    if (style === "hoverUnderline" || style === "underlineOnHover") return "no-underline border-b-2 border-transparent hover:border-current";

    return "no-underline border-b-2 border-transparent";
}

function ShareIcons({ block, className = "", partPrefix }: { block: ShopTemplateBlock | null | undefined; className?: string; partPrefix?: string }) {
    const message = useShopStorefrontMessage();
    const editorMode = useContext(ShopEditorModeContext);
    const storefrontLocationHref = useContext(ShopStorefrontLocationHrefContext);
    const fallbackBlock = block ?? ({ type: "share" } satisfies ShopTemplateBlock);
    const items = shareItemsFromBlock(fallbackBlock, message("ui.social.link"));
    const renderBlock = {
        ...fallbackBlock,
        slots: {
            ...(fallbackBlock.slots ?? {}),
            items,
        },
    };
    const rawLayout = blockOptionText(block, "shareIconLayout") || blockOptionText(block, "socialIconLayout") || "disk";
    const layout = rawLayout === "plain" ? "none" : rawLayout === "outline" ? "circle" : rawLayout;
    const size = shareIconSize(block);
    const brandColorEnabled = blockOptionText(block, "shareColored") !== "false" && blockOptionText(block, "socialIconBrandColor") !== "false";
    const theme = socialIconTheme(blockOptionText(block, "socialIconTheme") || "default");
    const customColor = blockOptionText(block, "textColor") || blockOptionText(block, "socialIconColor");
    const customBackground = blockOptionText(block, "socialIconBackgroundColor") || blockOptionText(block, "backgroundColor");
    const titlePosition = blockOptionText(block, "shareTitlePosition") || blockOptionText(block, "socialTitlePosition") || "none";
    const title = blockText(block, "title", message("block.share.title"));
    const titleElement = titlePosition === "none" ? null : (
        <span className="text-sm font-black text-current">{title}</span>
    );
    const dispatchEditorShareIconSelect = (event: MouseEvent<HTMLElement> | PointerEvent<HTMLElement>, itemPart: string) => {
        if (!editorMode) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const blockElement = event.currentTarget.closest<HTMLElement>("[data-shop-block-index]");
        const eventWindow = event.currentTarget.ownerDocument.defaultView ?? window;

        event.currentTarget.dispatchEvent(new eventWindow.CustomEvent(SHOP_SHARE_ICON_SELECT_EVENT, {
            bubbles: true,
            cancelable: true,
            composed: true,
            detail: {
                blockIndex: blockElement?.dataset.shopBlockIndex ?? null,
                clientX: event.clientX,
                clientY: event.clientY,
                part: itemPart,
            },
        }));
    };

    const renderLink = (item: ShopTemplateBlock, itemPart: string): ReactNode => {
        const network = shareNetworkFromIconBlock(item);
        const icon = blockText(item, "icon", `social:${network}`);
        const label = blockText(item, "label", socialNetworkLabel(network, message("ui.social.link")));
        const explicitHref = blockText(item, "href", "");
        const normalizedExplicitHref = explicitHref ? socialHrefFromLine(explicitHref) : "";
        const iconColor = blockOptionText(item, "textColor") || customColor || (brandColorEnabled ? socialNetworkColor(network) : theme.color);
        const backgroundColor = layout === "none" ? "transparent" : customBackground || theme.background;

        return (
            <SiteBuilderShareAnchor
                aria-label={label}
                data-shop-block-part={itemPart}
                data-shop-share-icon-part={itemPart}
                draggable={editorMode ? false : undefined}
                editorMode={editorMode}
                explicitHref={normalizedExplicitHref}
                locationHref={storefrontLocationHref}
                network={network}
                onClickCapture={editorMode ? (event) => dispatchEditorShareIconSelect(event, itemPart) : undefined}
                onPointerDownCapture={editorMode ? (event) => dispatchEditorShareIconSelect(event, itemPart) : undefined}
                shareTitle={title}
                className={classNames(
                    "grid place-items-center transition hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/30",
                    layout !== "none" && "shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_10%,transparent)]",
                    shareLinkStyleClass(block),
                )}
                style={{
                    backgroundColor,
                    borderRadius: socialIconRadius(layout),
                    color: iconColor,
                    height: size,
                    width: size,
                }}
            >
                {icon.startsWith("social:")
                    ? <SocialNetworkIcon network={icon.slice("social:".length) as SocialNetwork} className="h-[52%] w-[52%]" />
                    : atomicIconGlyph(icon, "h-[52%] w-[52%]")}
            </SiteBuilderShareAnchor>
        );
    };

    return (
        <div {...atomicBlockInteractionAttributes(block)} className={shareTitleLayoutClass(block)} style={atomicBlockStyle(block)}>
            {titleElement}
            <SlotBlockGroup
                block={renderBlock}
                slotKey="items"
                partPrefix={partPrefix}
                className={classNames("flex flex-wrap gap-2", shareAlignmentClass(block), className)}
                itemClassName="w-fit max-w-full"
                renderItem={({ childBlock, itemPart }) => renderLink(childBlock, itemPart)}
            />
        </div>
    );
}

function columnShapeStyle(block: ShopTemplateBlock): CSSProperties | undefined {
    const shape = blockOptionText(block, "shape");
    const shapeColor = blockOptionText(block, "shapeColor") || "color-mix(in srgb, var(--site-primary) 14%, transparent)";

    if (!shape || shape === "none") {
        return undefined;
    }

    if (shape === "splitVertical") {
        return { backgroundImage: `linear-gradient(90deg, transparent 0 50%, ${shapeColor} 50% 100%)` };
    }

    if (shape === "splitHorizontal") {
        return { backgroundImage: `linear-gradient(180deg, transparent 0 50%, ${shapeColor} 50% 100%)` };
    }

    if (shape === "diagonalWash") {
        return { backgroundImage: `linear-gradient(135deg, transparent 0 42%, ${shapeColor} 42% 100%)` };
    }

    if (shape === "cornerWash") {
        return { backgroundImage: `radial-gradient(circle at 100% 0%, ${shapeColor} 0 34%, transparent 35% 100%)` };
    }

    return undefined;
}

function columnVerticalAlignStyle(block: ShopTemplateBlock): CSSProperties | undefined {
    const verticalAlign = blockOptionText(block, "verticalAlign");

    if (verticalAlign === "center") {
        return { alignContent: "center" };
    }

    if (verticalAlign === "bottom") {
        return { alignContent: "end" };
    }

    return undefined;
}

function commerceProductsForBlock(
    block: ShopTemplateBlock,
    context: ShopPageContext | null | undefined,
): PublicHomeProduct[] {
    const source = blockOptionText(block, "catalogSource");
    const available = catalogProductsFromContext(context).length > 0
        ? catalogProductsFromContext(context)
        : homeProductsFromContext(context);
    const selected = source === "sale" ? available.filter((product) => product.isOnSale) : available;
    const defaultLimit = source === "comparison" ? 3 : 8;
    const limit = Math.max(1, Math.round(numericBlockOption(block, "catalogLimit", defaultLimit)));

    return selected.slice(0, limit);
}

function CommerceRuntimeColumnContent({ block }: { block: ShopTemplateBlock }) {
    const commerce = useContext(ShopCommerceContentContext);
    const editorMode = useContext(ShopEditorModeContext);
    const message = useShopStorefrontMessage();
    const role = blockOptionText(block, SHOP_COMMERCE_RUNTIME_ROLE_OPTION);

    if (!commerce) {
        return null;
    }

    if (role === SHOP_COMMERCE_RUNTIME_ROLES.catalogProducts) {
        return (
            <div className="min-w-0 w-full" data-shop-runtime-content="products">
                <ProductGrid
                    block={block}
                    favorites={commerce.favorites}
                    onToggleFavorite={commerce.onToggleFavorite}
                    publicProducts={commerceProductsForBlock(block, commerce.context)}
                />
            </div>
        );
    }

    if (role === SHOP_COMMERCE_RUNTIME_ROLES.catalogSubcategories) {
        const sourceItems = commerce.activeSectionItems.length > 0
            ? commerce.activeSectionItems
            : commerce.headerMenuItems.flatMap((item) => item.children.length > 0 ? item.children : [item]);
        const uniqueItems = Array.from(new Map(sourceItems.map((item) => [item.uuid, item])).values());
        const limit = Math.max(1, Math.round(numericBlockOption(block, "catalogLimit", 8)));

        return (
            <nav
                aria-label={blockText(block, "title", message("ui.catalog.catalog"))}
                className="grid w-full gap-3 sm:grid-cols-2 lg:grid-cols-4"
                data-shop-runtime-content="categories"
            >
                {uniqueItems.slice(0, limit).map((item, index) => (
                    <ShopStorefrontLink
                        className="group grid min-h-24 grid-cols-[auto_1fr_auto] items-center gap-3 rounded-[var(--site-radius)] border border-[var(--site-border)] bg-[var(--site-surface)] p-5 text-[var(--site-on-surface)] shadow-sm transition hover:-translate-y-0.5 hover:border-[var(--site-border-strong)] hover:shadow-lg"
                        href={functionalStorefrontHref(menuItemHref(item))}
                        key={item.uuid}
                        onClick={(event) => editorMode && event.preventDefault()}
                    >
                        <span className="text-[10px] font-black tracking-[0.14em] text-[var(--site-muted)]">
                            {String(index + 1).padStart(2, "0")}
                        </span>
                        <strong className="min-w-0 truncate text-base">{item.label}</strong>
                        <Icon name="chevronRight" className="h-4 w-4 transition group-hover:translate-x-0.5" />
                    </ShopStorefrontLink>
                ))}
            </nav>
        );
    }

    return null;
}

function ColumnBlock({
    block,
    partPrefix,
    slotParentType,
}: {
    block: ShopTemplateBlock;
    partPrefix?: string;
    slotParentType?: string;
}) {
    const editorMode = useContext(ShopEditorModeContext);
    const contentLayout = blockOptionText(block, "contentLayout");
    const inlineActions = contentLayout === "inlineActions";
    const inline = inlineActions || contentLayout === "inline" || contentLayout === "horizontal";
    const alignment = blockOptionText(block, "alignment");
    const configuredGap = blockOptionText(block, "gap");
    const commerceRuntimeRole = blockOptionText(block, SHOP_COMMERCE_RUNTIME_ROLE_OPTION);
    const footerColumnRole = blockOptionText(block, "footerColumnRole");
    const footerBottom = footerColumnRole === "bottom";
    const childSlotParentType = footerColumnRole
        ? `footerColumn:${footerColumnRole}`
        : slotParentType?.startsWith("footer")
            ? "footerColumn"
            : slotParentType?.startsWith("heroSlider")
                ? slotParentType
                : undefined;
    const slotClassName = footerBottom
        ? "flex flex-wrap items-center justify-center gap-x-4 gap-y-3 text-center sm:justify-between sm:text-left"
        : inline
        ? classNames(
            "flex flex-wrap gap-3",
            alignment === "center" ? "justify-center text-center" : alignment === "right" ? "justify-end text-right" : "justify-start",
        )
        : "grid gap-3";
    const itemClassName: SlotItemClassName = ({ childBlock }) => (
        !footerBottom && inlineActions && !atomicButtonLikeBlockTypes.has(childBlock.type)
            ? "basis-full max-w-full"
            : undefined
    );

    return (
        <div
            {...atomicBlockInteractionAttributes(block)}
            {...siteBuilderEditorColumnAttributes({
                editorMode,
                part: partPrefix ?? "root",
            })}
            className="grid h-full min-h-full content-start gap-5"
            data-shop-column-root="true"
            data-shop-column-part={partPrefix}
            data-shop-commerce-runtime-role={commerceRuntimeRole || undefined}
            style={mergeStyles({ minHeight: "100%" }, atomicBlockStyle(block), socialMediaColumnMinHeightStyle(block), columnShapeStyle(block), columnVerticalAlignStyle(block))}
        >
            <SlotBlockGroup
                block={block}
                slotKey="content"
                partPrefix={partPrefix}
                childSlotParentType={childSlotParentType}
                className={slotClassName}
                itemClassName={itemClassName}
                lockedChildren
                style={configuredGap ? { gap: configuredGap } : undefined}
            />
            <CommerceRuntimeColumnContent block={block} />
        </div>
    );
}

function numericContent(block: ShopTemplateBlock, key: string, fallback: number): number {
    const parsed = Number(blockText(block, key, String(fallback)).replace(",", "."));

    return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizedRatingMax(block: ShopTemplateBlock): number {
    return Math.max(1, Math.min(20, Math.round(numericContent(block, "ratingMax", 5))));
}

function ratingSizeClass(block: ShopTemplateBlock): string {
    const size = blockOptionText(block, "ratingSize") || blockOptionText(block, "size") || "md";

    if (size === "xs") return "text-sm";
    if (size === "sm") return "text-base";
    if (size === "lg") return "text-2xl";
    if (size === "xl") return "text-3xl";

    return "text-xl";
}

function formattedRatingNumber(value: number): string {
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function builtinRatingGlyph(mode: string, active: boolean): string {
    if (mode === "thumbs") return "👍";
    if (mode === "circles") return active ? "●" : "○";
    if (mode === "squares") return active ? "■" : "□";
    if (mode === "hearts") return active ? "♥" : "♡";

    return active ? "★" : "☆";
}

function ratingGlyph(block: ShopTemplateBlock, active: boolean): ReactNode {
    const mode = blockOptionText(block, "ratingIcon") || "stars";

    if (mode === "custom") {
        const icon = blockText(block, active ? "ratingActiveIcon" : "ratingInactiveIcon", "lucide:star");

        return <span className="inline-grid h-[1em] w-[1em] place-items-center">{atomicIconGlyph(icon, "h-[0.9em] w-[0.9em]")}</span>;
    }

    return builtinRatingGlyph(mode, active);
}

function ratingBlockStyle(block: ShopTemplateBlock): CSSProperties | undefined {
    const style = atomicBlockStyle(block);

    if (!style) {
        return undefined;
    }

    const {
        alignItems,
        columnGap,
        display,
        flexDirection,
        gridTemplateColumns,
        gridTemplateRows,
        justifyContent,
        justifyItems,
        rowGap,
        ...rest
    } = style;

    void alignItems;
    void columnGap;
    void display;
    void flexDirection;
    void gridTemplateColumns;
    void gridTemplateRows;
    void justifyContent;
    void justifyItems;
    void rowGap;

    if (Object.keys(rest).length === 0) {
        return undefined;
    }

    return {
        ...rest,
        ...siteBuilderRebasedAuthoritativeStyleVariables(
            style as SiteBuilderStyleRecord,
            rest as SiteBuilderStyleRecord,
        ),
    };
}

function isAtomicBlockType(type: string): boolean {
    return [
        "heading",
        "paragraph",
        "text",
        "quote",
        "code",
        "list",
        "checklist",
        "button",
        "table",
        "media",
        "uploadFile",
        "emoji",
        "signature",
        "icon",
        "searchBar",
        "serviceBenefitItem",
        "input",
        "textarea",
        "accordion",
        "accordionItem",
        "alert",
        "badge",
        "blockquote",
        "card",
        "chart",
        "countdown",
        "ctaBadge",
        "facebook",
        "form",
        "iconText",
        "iconList",
        "image",
        "instagram",
        "link",
        "map",
        "product",
        "progressBar",
        "rating",
        "share",
        "socialLink",
        "socialMedia",
        "supportedPaymentButton",
        "textHighlight",
        "avatars",
        "video",
        "appointmentButton",
        "donationButton",
        "addToCartButton",
        "headerLogo",
        "headerSearch",
        "headerStoresButton",
        "headerAccountButton",
        "headerLanguageSelector",
        "headerCurrencySelector",
        "headerFavoritesButton",
        "headerCartButton",
        "headerBenefitStrip",
        "footerService",
        "footerLinks",
        "footerSocial",
        "footerApps",
        "footerCopyright",
    ].includes(type);
}

const inlineAtomicBlockTypes = new Set([
    "appointmentButton",
    "button",
    "donationButton",
    "addToCartButton",
    "badge",
    "ctaBadge",
    "facebook",
    "instagram",
    "link",
    "socialLink",
]);

function DetachedMaterializedCard({
    block,
    partPrefix,
}: {
    block: ShopTemplateBlock;
    partPrefix?: string;
}) {
    const message = useShopStorefrontMessage();
    const imageUrl = blockText(block, "imageUrl", "");
    const contentItems = slotChildren(block, "content");
    const contentReplacesFallback = useSlotReplacesFallback(block, "content");
    const hasContentSlot = contentItems.length > 0 || contentReplacesFallback;

    return (
        <div {...blockInteractionAttributes(block)} className="relative" style={mergeStyles(blockStyle(block), interactionStyle(block))}>
            {imageUrl ? (
                <img
                    {...editablePart("image")}
                    src={imageUrl}
                    alt={blockText(block, "alt", blockText(block, "title", message("block.card.title")))}
                    title={blockText(block, "imageTitle", "") || undefined}
                    className="block max-w-full"
                    style={imagePartStyle(block, "image")}
                />
            ) : null}
            {hasContentSlot ? (
                <SlotBlockGroup
                    block={block}
                    slotKey="content"
                    partPrefix={partPrefix}
                    className="grid gap-0"
                />
            ) : (
                <>
                    <p {...editableTextPart("title")} className="text-lg font-black text-[var(--site-text)]" style={partStyle(block, "title")}>{richTextContent(block, "title", "Obsahová karta")}</p>
                    <p {...editableTextPart("text")} className="mt-2 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]" style={partStyle(block, "text")}>{richTextContent(block, "text", "Krátký popis obsahu karty.")}</p>
                </>
            )}
        </div>
    );
}

function accordionRadius(value: string): string {
    if (value === "none") return "0px";
    if (value === "sm") return "8px";
    if (value === "md") return "14px";
    if (value === "xl") return "28px";
    if (value === "pill") return "32px";

    return "22px";
}

function AccordionToggleIcon({
    block,
    open,
}: {
    block: ShopTemplateBlock;
    open: boolean;
}) {
    const mode = blockOptionText(block, "accordionIconMode") || "arrow";

    if (mode === "plusMinus") {
        return (
            <span className="grid h-8 w-8 place-items-center text-3xl font-light leading-none text-current" aria-hidden="true">
                {open ? "-" : "+"}
            </span>
        );
    }

    if (mode === "custom") {
        const iconValue = blockOptionText(block, open ? "accordionOpenIcon" : "accordionClosedIcon")
            || (open ? "lucide:chevronDown" : "lucide:chevronRight");

        return (
            <span className="grid h-8 w-8 place-items-center text-current" aria-hidden="true">
                {atomicIconGlyph(iconValue, "h-5 w-5")}
            </span>
        );
    }

    return (
        <span className="grid h-8 w-8 place-items-center text-current" aria-hidden="true">
            <Icon name="chevronDown" className={classNames("h-6 w-6 transition-transform", open ? "rotate-180" : "")} />
        </span>
    );
}

function AccordionItemBlock({
    block,
    containerBlock,
    open,
    onToggle,
}: {
    block: ShopTemplateBlock;
    containerBlock: ShopTemplateBlock;
    open: boolean;
    onToggle?: () => void;
}) {
    const editorMode = useContext(ShopEditorModeContext);
    const transition = blockOptionText(containerBlock, "accordionTransition") || "slide";
    const styleVariant = blockOptionText(containerBlock, "accordionStyle") || "boxed";
    const activeBackgroundColor = blockOptionText(block, "activeBackgroundColor")
        || blockOptionText(containerBlock, "activeBackgroundColor")
        || (styleVariant === "highlightActive" ? "var(--site-primary-soft)" : "");
    const activeTextColor = blockOptionText(block, "activeTextColor") || blockOptionText(containerBlock, "activeTextColor") || "";
    const baseStyle = atomicBlockStyle(block);
    const bodyTransitionClass = transition === "none"
        ? ""
        : transition === "fade"
            ? "transition-opacity duration-200"
            : "transition-[grid-template-rows,opacity] duration-200 ease-out";
    const titleStyle = partStyle(block, "title");
    const textStyle = partStyle(block, "text");
    const activeStyle = open
        ? {
            ...(activeBackgroundColor ? { backgroundColor: activeBackgroundColor } : {}),
            ...(activeTextColor ? { color: activeTextColor } : {}),
        }
        : undefined;
    const resolvedItemStyle = mergeStyles(baseStyle, activeStyle);
    const itemStyle = baseStyle && resolvedItemStyle
        ? {
            ...resolvedItemStyle,
            ...siteBuilderRebasedAuthoritativeStyleVariables(
                baseStyle as SiteBuilderStyleRecord,
                resolvedItemStyle as SiteBuilderStyleRecord,
            ),
        }
        : resolvedItemStyle;

    return (
        <div
            {...atomicBlockInteractionAttributes(block)}
            className="border-b border-[var(--site-border)] last:border-b-0"
            style={itemStyle}
        >
            <button
                type="button"
                data-shop-accordion-toggle="true"
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                onClick={(event) => {
                    const titleTarget = typeof (event.target as HTMLElement | null)?.closest === "function"
                        && (event.target as HTMLElement).closest("[data-shop-accordion-title]");

                    if (editorMode && titleTarget && open) {
                        return;
                    }

                    onToggle?.();
                }}
            >
                <span {...editableTextPart("title")} data-shop-accordion-title="true" className="text-lg font-black leading-tight text-current" style={titleStyle}>
                    {richTextContent(block, "title", editorMode ? "What services does your company offer ?" : "")}
                </span>
                <AccordionToggleIcon block={containerBlock} open={open} />
            </button>
            <div
                className={classNames("grid overflow-hidden px-5", bodyTransitionClass)}
                style={{
                    gridTemplateRows: open ? "1fr" : "0fr",
                    minHeight: 0,
                    opacity: open ? 1 : 0,
                }}
            >
                <div className="min-h-0 overflow-hidden">
                    <p {...editableTextPart("text")} className={classNames("pb-5 text-base leading-7", readableOnSurfaceTextClass)} style={readableSemanticTextStyle(textStyle)}>
                        {richTextContent(block, "text", editorMode ? "You can reach our customer support team by email, phone, or live chat." : "")}
                    </p>
                </div>
            </div>
        </div>
    );
}

function accordionItemKey(block: ShopTemplateBlock, index: number): string {
    const explicitKey = blockOptionText(block, "accordionItemId");

    if (explicitKey) {
        return explicitKey;
    }

    const content = blockRecord(block, "content");
    const title = typeof content.title === "string" ? content.title.trim() : "";
    const text = typeof content.text === "string" ? content.text.trim() : "";
    const signature = `${block.type}:${title}:${text}`;

    return signature === `${block.type}::` ? `${block.type}:${index}` : signature;
}

function equalStringSets(left: Set<string>, right: Set<string>): boolean {
    if (left.size !== right.size) {
        return false;
    }

    for (const value of left) {
        if (!right.has(value)) {
            return false;
        }
    }

    return true;
}

function AccordionBlock({
    block,
    partPrefix,
}: {
    block: ShopTemplateBlock;
    partPrefix?: string;
}) {
    const items = slotChildren(block, "content");
    const itemKeys = items.map((item, index) => accordionItemKey(item, index));
    const itemKeySignature = itemKeys.join("\u001f");
    const [openItemKeys, setOpenItemKeys] = useState<Set<string>>(() => new Set(itemKeys[0] ? [itemKeys[0]] : []));
    const radius = accordionRadius(blockOptionText(block, "borderRadius") || "lg");
    const hasItems = slotIsDefined(block, "content") || items.length > 0;
    const style = atomicBlockStyle(block);
    const resolvedStyle = mergeStyles(style, {
        borderRadius: radius,
        maxWidth: "720px",
        width: "calc(100% - 48px)",
    });
    const normalizedAuthoritativeStyle = style
        ? { ...style, borderRadius: radius } as SiteBuilderStyleRecord
        : undefined;
    const accordionStyle = style && resolvedStyle && normalizedAuthoritativeStyle
        ? {
            ...resolvedStyle,
            ...siteBuilderRebasedAuthoritativeStyleVariables(
                style as SiteBuilderStyleRecord,
                normalizedAuthoritativeStyle,
            ),
        }
        : resolvedStyle;

    useEffect(() => {
        setOpenItemKeys((current) => {
            const allowedKeys = new Set(itemKeys);
            const next = new Set(Array.from(current).filter((key) => allowedKeys.has(key)));

            if (next.size === 0 && itemKeys[0]) {
                next.add(itemKeys[0]);
            }

            return equalStringSets(current, next) ? current : next;
        });
    }, [itemKeySignature]);

    if (!hasItems) {
        return <AccordionItemBlock block={block} containerBlock={block} open />;
    }

    return (
        <div
            {...atomicBlockInteractionAttributes(block)}
            className="mx-auto my-5 overflow-hidden border border-[var(--site-border)] bg-[var(--site-surface)]"
            style={accordionStyle}
        >
            <SlotBlockGroup
                block={block}
                slotKey="content"
                partPrefix={partPrefix}
                className="grid gap-0"
                style={{ gap: style?.gap }}
                renderItem={({ childBlock, index, itemPart }) => (
                    childBlock.type === "accordionItem" || (childBlock.type === "accordion" && !slotIsDefined(childBlock, "content"))
                        ? (
                            (() => {
                                const itemKey = accordionItemKey(childBlock, index);

                                return (
                                    <AccordionItemBlock
                                        block={childBlock}
                                        containerBlock={block}
                                        open={openItemKeys.has(itemKey)}
                                        onToggle={() => {
                                            setOpenItemKeys((current) => {
                                                const next = new Set(current);

                                                if (next.has(itemKey)) {
                                                    next.delete(itemKey);
                                                } else {
                                                    next.add(itemKey);
                                                }

                                                return next;
                                            });
                                        }}
                                    />
                                );
                            })()
                        )
                        : (
                            <div className="border-b border-[var(--site-border)] px-5 py-4 last:border-b-0">
                                <AtomicBlock block={childBlock} partPrefix={itemPart} slotParentType={block.type} />
                            </div>
                        )
                )}
            />
        </div>
    );
}

function searchBlockScope(block: ShopTemplateBlock): SearchBlockScope {
    const scope = blockOptionText(block, "searchWithin");

    return scope === "categories" || scope === "pages" || scope === "products"
        ? scope
        : "everything";
}

function searchBlockResultLimit(block: ShopTemplateBlock): number {
    const parsed = Number(blockOptionText(block, "searchResultLimit") || "6");

    return Number.isFinite(parsed) ? Math.max(1, Math.round(parsed)) : 6;
}

function searchBlockPageHref(page: PageLink, storefrontHref: (href: string) => string): string {
    if (page.href && page.href.trim() !== "") {
        return storefrontHref(page.href);
    }

    const slug = page.slug.trim();

    if (slug === "") {
        return storefrontHref("/");
    }

    return storefrontHref(slug.startsWith("/") ? slug : `/${slug}`);
}

function searchBlockPageMatches(
    pages: PageLink[],
    query: string,
    limit: number,
    storefrontHref: (href: string) => string,
    languageCode: string | null = null,
): Array<PageLink & { href: string }> {
    const locale = storefrontLocale(languageCode);
    const normalizedQuery = query.trim().toLocaleLowerCase(locale);
    const codeQuery = query.trim().toLowerCase();

    if (normalizedQuery.length < 2) {
        return [];
    }

    return pages
        .map((page) => ({
            ...page,
            href: searchBlockPageHref(page, storefrontHref),
            searchable: page.title.toLocaleLowerCase(locale),
        }))
        .filter((page) => page.title.trim() !== "" && (
            page.searchable.includes(normalizedQuery)
            || `${page.slug} ${page.pageType ?? ""}`.toLowerCase().includes(codeQuery)
        ))
        .slice(0, limit)
        .map(({ searchable, ...page }) => {
            void searchable;

            return page;
        });
}

function SearchBarBlock({
    block,
    partPrefix,
}: {
    block: ShopTemplateBlock;
    partPrefix?: string;
}) {
    const message = useShopStorefrontMessage();
    const { formatMoney } = useShopStorefrontFormatting();
    const storefrontHref = useShopStorefrontHref();
    const editorMode = useContext(ShopEditorModeContext);
    const { currencyCode, languageCode, pages, searchHref, shopSearchApiPath } = useContext(ShopSearchContext);
    const scope = searchBlockScope(block);
    const resultLimit = searchBlockResultLimit(block);
    const [query, setQuery] = useState("");
    const [focused, setFocused] = useState(false);
    const [suggestions, setSuggestions] = useState<SearchSuggestions | null>(null);
    const [loading, setLoading] = useState(false);
    const normalizedQuery = query.trim();
    const pageMatches = useMemo(
        () => scope === "products" || scope === "categories" ? [] : searchBlockPageMatches(
            pages,
            normalizedQuery,
            resultLimit,
            storefrontHref,
            languageCode,
        ),
        [languageCode, normalizedQuery, pages, resultLimit, scope, storefrontHref],
    );
    const productMatches = scope === "pages" || scope === "categories"
        ? []
        : suggestions?.products.slice(0, resultLimit) ?? [];
    const categoryMatches = scope === "pages" || scope === "products"
        ? []
        : suggestions?.categories.slice(0, resultLimit) ?? [];
    const showSuggestions = resultLimit > 0 && focused && normalizedQuery.length >= 2;
    const hasSuggestions = pageMatches.length > 0 || productMatches.length > 0 || categoryMatches.length > 0;
    const leadingIconItems = slotChildren(block, "leadingIcon");
    const actionIconItems = slotChildren(block, "actionIcon");

    useEffect(() => {
        if (!showSuggestions || scope === "pages" || !shopSearchApiPath) {
            setSuggestions(null);
            setLoading(false);

            return undefined;
        }

        const abortController = new AbortController();
        const timeout = window.setTimeout(async () => {
            const params = new URLSearchParams({
                q: normalizedQuery,
                limit: String(resultLimit),
            });

            if (languageCode) {
                params.set("language", languageCode);
            }

            if (currencyCode) {
                params.set(CURRENCY_QUERY_PARAM, currencyCode);
            }

            setLoading(true);

            try {
                const response = await fetch(`${shopSearchApiPath}?${params.toString()}`, {
                    cache: "no-store",
                    credentials: "same-origin",
                    signal: abortController.signal,
                });

                if (!response.ok) {
                    setSuggestions(null);

                    return;
                }

                const payload = await response.json() as unknown;

                if (!abortController.signal.aborted) {
                    setSuggestions(searchSuggestionsFromValue(payload, languageCode));
                }
            } catch {
                if (!abortController.signal.aborted) {
                    setSuggestions(null);
                }
            } finally {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            }
        }, 220);

        return () => {
            window.clearTimeout(timeout);
            abortController.abort();
        };
    }, [currencyCode, languageCode, normalizedQuery, resultLimit, scope, shopSearchApiPath, showSuggestions]);

    function handleSubmit(event: FormEvent<HTMLFormElement>): void {
        if (editorMode) {
            event.preventDefault();

            return;
        }

        if (scope === "pages" && pageMatches[0]) {
            event.preventDefault();
            event.currentTarget.ownerDocument.defaultView?.location.assign(pageMatches[0].href);

            return;
        }

        if (scope === "categories" && categoryMatches[0]) {
            event.preventDefault();
            event.currentTarget.ownerDocument.defaultView?.location.assign(
                categoryMatches[0].slug
                    ? storefrontHref(`/${categoryMatches[0].slug}`)
                    : `${searchHref}?q=${encodeURIComponent(categoryMatches[0].name)}&scope=categories`,
            );
        }
    }

    return (
        <form
            {...atomicBlockInteractionAttributes(block)}
            action={searchHref}
            method="get"
            className="relative flex w-full max-w-none overflow-visible rounded-full border border-[var(--site-border)] bg-[var(--site-surface)] shadow-sm"
            onSubmit={handleSubmit}
            style={mergeStyles(atomicBlockStyle(block), partStyle(block, "search"))}
        >
            {leadingIconItems.length > 0 ? (
                <span className="ml-4 inline-flex shrink-0 items-center text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">
                    <InlineSlotBlockGroup
                        block={block}
                        slotKey="leadingIcon"
                        partPrefix={partPrefix}
                    />
                </span>
            ) : null}
            <input
                name="q"
                type="search"
                className="min-w-0 flex-1 rounded-l-full bg-transparent px-5 py-4 text-sm outline-none"
                placeholder={blockText(block, "placeholder", message("block.searchBar.placeholder"))}
                autoComplete="off"
                value={query}
                onBlur={() => {
                    window.setTimeout(() => setFocused(false), 120);
                }}
                onChange={(event) => setQuery(event.target.value)}
                onFocus={() => setFocused(true)}
            />
            {scope !== "everything" ? <input type="hidden" name="scope" value={scope} /> : null}
            <button
                type="submit"
                className={classNames(
                    "rounded-r-full bg-[var(--site-primary)] text-sm font-black text-[var(--site-on-primary)]",
                    actionIconItems.length > 0 ? "grid min-w-12 place-items-center px-4 py-3" : "px-6 py-4",
                )}
                style={partStyle(block, "button")}
            >
                {actionIconItems.length > 0 ? (
                    <InlineSlotBlockGroup
                        block={block}
                        slotKey="actionIcon"
                        partPrefix={partPrefix}
                    />
                ) : (
                    <span {...editableTextPart("buttonLabel")}>
                        {richTextContent(block, "buttonLabel", "Hledat")}
                    </span>
                )}
            </button>
            {showSuggestions ? (
                <div className="absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-2xl border border-[var(--site-border)] bg-[var(--site-surface)] text-left shadow-[0_18px_44px_var(--site-shadow-color)]">
                    {loading && scope !== "pages" ? (
                        <p className="px-4 py-3 text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">{message("ui.search.searching")}</p>
                    ) : hasSuggestions ? (
                        <div className="divide-y divide-[var(--site-border)]">
                            {pageMatches.length > 0 ? (
                                <div className="p-2">
                                    <p className="px-2 py-1 text-[11px] font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.search.pages")}</p>
                                    {pageMatches.map((page) => (
                                        <a key={`${page.href}:${page.title}`} href={page.href} className="block rounded-xl px-3 py-2 text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_70%,transparent)] hover:bg-[var(--site-surface)] hover:text-[var(--site-primary)]">
                                            {page.title}
                                        </a>
                                    ))}
                                </div>
                            ) : null}
                            {categoryMatches.length > 0 ? (
                                <div className="p-2">
                                    <p className="px-2 py-1 text-[11px] font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.search.categories")}</p>
                                    {categoryMatches.map((category) => (
                                        <a
                                            key={category.uuid}
                                            href={category.slug ? storefrontHref(`/${category.slug}`) : `${searchHref}?q=${encodeURIComponent(category.name)}&scope=categories`}
                                            className="block rounded-xl px-3 py-2 text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_70%,transparent)] hover:bg-[var(--site-surface)] hover:text-[var(--site-primary)]"
                                        >
                                            {category.name}
                                        </a>
                                    ))}
                                </div>
                            ) : null}
                            {productMatches.length > 0 ? (
                                <div className="p-2">
                                    <p className="px-2 py-1 text-[11px] font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.search.products")}</p>
                                    {productMatches.map((product) => (
                                        <a key={product.uuid} href={product.slug ? storefrontHref(`/${product.slug}`) : `${searchHref}?q=${encodeURIComponent(product.name)}`} className="flex items-center justify-between gap-4 rounded-xl px-3 py-2 text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_70%,transparent)] hover:bg-[var(--site-surface)] hover:text-[var(--site-primary)]">
                                            <span className="min-w-0 truncate">{product.name}</span>
                                            <span className="shrink-0 text-xs font-black text-[var(--site-primary)]">{formatMoney(product.price)}</span>
                                        </a>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    ) : (
                        <p className="px-4 py-3 text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">{message("ui.search.nothingFound")}</p>
                    )}
                </div>
            ) : null}
        </form>
    );
}

function HeaderSearchBarBlock({
    block,
    partPrefix,
}: {
    block: ShopTemplateBlock;
    partPrefix?: string;
}) {
    const headerContext = useContext(ShopHeaderChromeContext);
    const message = useShopStorefrontMessage();
    const leadingIconItems = slotChildren(block, "leadingIcon");
    const actionIconItems = slotChildren(block, "actionIcon");

    if (!headerContext) {
        return null;
    }

    return (
        <button
            {...editablePart("search")}
            {...atomicBlockInteractionAttributes(block)}
            type="button"
            className={classNames(
                "flex min-h-11 min-w-0 w-full items-center justify-between gap-3 rounded-full border border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] bg-[var(--site-surface)] px-5 py-3 text-sm shadow-sm transition hover:-translate-y-0.5 hover:border-[var(--site-primary)]/25 hover:bg-[var(--site-surface)] hover:text-[var(--site-text)] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25",
                readableOnSurfaceTextClass,
            )}
            onClick={headerContext.onOpenSearch}
            style={readableSemanticTextStyle(mergeStyles(atomicBlockStyle(block), partStyle(block, "search")))}
        >
            <span className="flex min-w-0 items-center gap-3">
                {leadingIconItems.length > 0 ? (
                    <InlineSlotBlockGroup
                        block={block}
                        slotKey="leadingIcon"
                        partPrefix={partPrefix}
                    />
                ) : null}
                <span {...editableTextPart("placeholder")} className="truncate">
                    {richTextContent(block, "placeholder", message("block.headerSearch.placeholder"))}
                </span>
            </span>
            {actionIconItems.length > 0 ? (
                <InlineSlotBlockGroup
                    block={block}
                    slotKey="actionIcon"
                    partPrefix={partPrefix}
                />
            ) : (
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[var(--site-primary)] text-[var(--site-on-primary)]">
                    <Icon name="search" className="h-4 w-4" />
                </span>
            )}
        </button>
    );
}

function lagunaNestedFormFieldBlocks(block: ShopTemplateBlock): ShopTemplateBlock[] {
    const fields: ShopTemplateBlock[] = [];

    const visit = (children: readonly ShopTemplateBlock[]): void => {
        children.forEach((child) => {
            if (child.type === "form") {
                return;
            }

            if (child.type === "field") {
                fields.push(child);
            }

            Object.values(child.slots ?? {}).forEach(visit);
            visit(child.children ?? []);
        });
    };

    Object.values(block.slots ?? {}).forEach(visit);
    visit(block.children ?? []);

    return fields;
}

function lagunaAuthoredFormFieldNames(
    block: ShopTemplateBlock,
): ReadonlyMap<ShopTemplateBlock, string> {
    return new Map(
        siteBuilderAuthoredFormFields(block).map((field) => [
            field.block,
            field.key,
        ]),
    );
}

function lagunaLeadFieldInputType(field: SiteBuilderLeadFormField): string {
    if (field.type === "textarea") return "longText";
    if (field.type === "select") return "selection";
    if (field.type === "tel") return "telephone";
    if (field.type === "email") return "email";

    return "text";
}

function lagunaLeadSchemaFieldBlock(
    field: SiteBuilderLeadFormField,
): ShopTemplateBlock {
    return {
        type: "field",
        content: {
            choices: field.options.join("\n"),
            defaultValue: "",
            label: field.label,
            name: field.key,
            placeholder: field.placeholder ?? "",
        },
        options: {
            choiceLayout: "vertical",
            fieldInputType: lagunaLeadFieldInputType(field),
            requirement: field.required ? "required" : "none",
            rows: "3",
            showDescription: "false",
        },
    };
}

function FormFieldBlock({
    block,
    fieldName,
    formBlock,
    readOnly,
    schemaField,
    synthetic = false,
}: {
    block: ShopTemplateBlock;
    fieldName?: string;
    formBlock: ShopTemplateBlock | null | undefined;
    readOnly?: boolean;
    schemaField?: SiteBuilderLeadFormField;
    synthetic?: boolean;
}) {
    const formRuntime = useContext(ShopLeadFormRuntimeContext);
    const controlId = useId();
    const activeFormBlock = formBlock?.type === "form"
        ? formBlock
        : formRuntime?.formBlock ?? formBlock;
    const resolvedFieldName = fieldName
        ?? formRuntime?.fieldNames.get(block)
        ?? siteBuilderLeadFieldKey(
            blockText(block, "name", "")
                || blockText(block, "key", "")
                || blockText(block, "label", ""),
            0,
        );
    const activeSchemaField = schemaField
        ?? formRuntime?.fieldsByKey.get(resolvedFieldName);
    const authoredField = formRuntime?.authoredFields.get(block);

    if (
        !synthetic
        && formRuntime?.enforceSchema
        && formRuntime.schemaResolved
        && !formRuntime.readOnly
        && (
            !activeSchemaField
            || formRuntime.primaryFieldsByKey.get(resolvedFieldName) !== block
        )
    ) {
        return null;
    }

    const label = authoredField?.label
        ?? activeSchemaField?.label
        ?? blockText(block, "label", "Your Name");
    const required = activeSchemaField?.required ?? formFieldRequired(block);
    const marker = formFieldMarker(activeFormBlock, block, required);
    const labelsPosition = formLabelsPosition(activeFormBlock);
    const showLabel = labelsPosition !== "none";
    const inputType = activeSchemaField
        ? lagunaLeadFieldInputType(activeSchemaField)
        : formFieldInputType(block);
    const choiceGroup = inputType === "multipleCheckboxes" || inputType === "radioButtons";
    const labelledControlId = choiceGroup ? `${controlId}-0` : controlId;
    const placeholder = authoredField?.placeholder
        || activeSchemaField?.placeholder
        || blockText(block, "placeholder", "")
        || "";
    const defaultValue = blockText(block, "defaultValue", "");
    const showDescription = blockOptionText(block, "showDescription") === "true";
    const description = blockText(block, "description", "Description");
    const interactionLocked = (readOnly ?? formRuntime?.readOnly ?? false)
        || (formRuntime?.busy ?? false);
    const controlClassName = "w-full rounded-[4px] border border-[var(--site-border)] bg-[var(--site-background)] px-4 py-2.5 text-sm text-[var(--site-text)] outline-none placeholder:text-[var(--site-muted)] focus-visible:ring-2 focus-visible:ring-[var(--site-accent)]/30";
    const choiceControlClassName = "h-4 w-4 shrink-0 rounded border-[var(--site-border)] bg-[var(--site-background)] accent-[var(--site-accent)]";
    const choiceOptions = authoredField?.options?.length
        ? authoredField.options
        : activeSchemaField?.options.length
            ? activeSchemaField.options
        : formFieldChoiceOptions(block);
    const labelNode = showLabel ? (
        <label className={classNames("text-sm font-semibold text-[var(--site-on-primary)]", labelsPosition === "top" ? "leading-5" : "leading-10")} htmlFor={labelledControlId} id={`${controlId}-label`}>
            <span {...editableTextPart("label")}>
                {richTextContent(block, "label", label)}
            </span>
            {marker ? <span className="ml-1 opacity-80">{marker}</span> : null}
        </label>
    ) : null;
    const control = inputType === "longText" ? (
        <textarea
            aria-label={showLabel ? undefined : label}
            className={classNames(controlClassName, "resize-y")}
            defaultValue={defaultValue}
            id={controlId}
            name={resolvedFieldName}
            placeholder={placeholder}
            readOnly={interactionLocked}
            required={required}
            rows={formFieldRows(block)}
        />
    ) : inputType === "checkbox" ? (
        <label className="flex min-h-10 items-center gap-2 text-sm font-semibold text-[var(--site-on-primary)]">
            <input
                className={choiceControlClassName}
                defaultChecked={defaultValue === "true"}
                disabled={interactionLocked}
                id={controlId}
                name={resolvedFieldName}
                required={required}
                type="checkbox"
                value="true"
            />
            {showLabel ? null : <span {...editableTextPart("label")}>{richTextContent(block, "label", label)}</span>}
        </label>
    ) : inputType === "multipleCheckboxes" || inputType === "radioButtons" ? (
        <div
            aria-label={showLabel ? undefined : label}
            aria-labelledby={showLabel ? `${controlId}-label` : undefined}
            className={classNames("py-1 text-sm font-semibold text-[var(--site-on-primary)]", formChoiceLayoutClass(block))}
            role={inputType === "radioButtons" ? "radiogroup" : "group"}
        >
            {choiceOptions.map((choice, index) => (
                <label key={`${choice}:${index}`} className="flex items-center gap-2">
                    <input
                        className={choiceControlClassName}
                        defaultChecked={defaultValue.split(/\r?\n|,/).map((item) => item.trim()).includes(choice)}
                        disabled={interactionLocked}
                        id={`${controlId}-${index}`}
                        name={resolvedFieldName}
                        required={required && index === 0}
                        type={inputType === "radioButtons" ? "radio" : "checkbox"}
                        value={choice}
                    />
                    <span>{choice}</span>
                </label>
            ))}
        </div>
    ) : inputType === "selection" ? (
        <select aria-label={showLabel ? undefined : label} className={controlClassName} defaultValue={defaultValue} disabled={interactionLocked} id={controlId} name={resolvedFieldName} required={required}>
            {placeholder ? <option value="">{placeholder}</option> : null}
            {choiceOptions.map((choice, index) => (
                <option key={`${choice}:${index}`} value={choice}>{choice}</option>
            ))}
        </select>
    ) : (
        <input
            aria-label={showLabel ? undefined : label}
            className={controlClassName}
            defaultValue={inputType === "fileupload" ? undefined : defaultValue}
            disabled={inputType === "fileupload" ? interactionLocked : undefined}
            id={controlId}
            name={resolvedFieldName}
            placeholder={placeholder}
            readOnly={inputType === "fileupload" ? undefined : interactionLocked}
            required={required}
            step={inputType === "decimalNumber" ? "any" : undefined}
            type={
                inputType === "email"
                    ? "email"
                    : inputType === "telephone"
                    ? "tel"
                    : inputType === "url"
                    ? "url"
                    : inputType === "number" || inputType === "decimalNumber"
                    ? "number"
                    : inputType === "date"
                    ? "date"
                    : inputType === "datetime"
                    ? "datetime-local"
                    : inputType === "fileupload"
                    ? "file"
                    : "text"
            }
        />
    );

    return (
        <div
            {...(synthetic ? {} : atomicBlockInteractionAttributes(block))}
            data-laguna-lead-field={resolvedFieldName}
            className={classNames("max-w-full", labelsPosition === "top" ? "grid gap-1.5" : "")}
            style={mergeStyles(atomicBlockVisualStyle(block), formFieldLayoutStyle(activeFormBlock))}
        >
            {labelNode}
            <div className="min-w-0">
                {control}
                {showDescription ? (
                    <p {...editableTextPart("description")} className="mt-1.5 text-xs leading-5 text-[color-mix(in_srgb,var(--site-on-primary)_70%,transparent)]">
                        {richTextContent(block, "description", description)}
                    </p>
                ) : null}
            </div>
        </div>
    );
}

function FormSubmitButtonBlock({
    block,
    formBlock,
}: {
    block: ShopTemplateBlock;
    formBlock: ShopTemplateBlock | null | undefined;
}) {
    const formRuntime = useContext(ShopLeadFormRuntimeContext);
    const message = useShopStorefrontMessage();
    const activeFormBlock = formBlock?.type === "form"
        ? formBlock
        : formRuntime?.formBlock ?? formBlock;

    return (
        <div
            {...atomicBlockInteractionAttributes(block)}
            className={classNames("flex max-w-full", formSubmitAlignmentClass(block))}
            style={mergeStyles(atomicBlockVisualStyle(block), formSubmitAlignmentStyle(activeFormBlock, block))}
        >
            <button
                {...editableTextPart("label")}
                disabled={formRuntime?.busy || formRuntime?.readOnly}
                type="submit"
                className="rounded-[4px] bg-[var(--site-accent)] px-6 py-2.5 text-sm font-bold text-[var(--site-text)] shadow-sm transition hover:brightness-95"
                style={partStyle(block, "button")}
            >
                {formRuntime?.busy
                    ? message("ui.common.sending")
                    : richTextContent(block, "label", "Submit")}
            </button>
        </div>
    );
}

function FormBlock({
    block,
    partPrefix,
}: {
    block: ShopTemplateBlock;
    partPrefix?: string;
}) {
    const editorMode = useContext(ShopEditorModeContext);
    const commerceContext = useContext(ShopCommerceContentContext);
    const message = useShopStorefrontMessage();
    const storefrontHref = useShopStorefrontHref();
    const { pages } = useContext(ShopSearchContext);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [startedAt] = useState(templateLeadStartedAt);
    const slotKey = slotChildren(block, "items").length > 0 || slotIsDefined(block, "items") ? "items" : "fields";
    const config = siteBuilderFormConfig(block);
    const formKey = config.formKey ?? "contact";
    const actionUrl = blockText(block, "actionUrl", "").trim();
    const formAction = blockOptionText(block, "formAction") || "sendEmail";
    const authoredFields = useMemo(
        () => siteBuilderAuthoredFormFields(block),
        [block],
    );
    const fieldNames = useMemo(
        () => lagunaAuthoredFormFieldNames(block),
        [block],
    );
    const authoredFieldNames = new Set(fieldNames.values());
    const leadFormLookup = useSiteBuilderLeadForm({
        enabled: !editorMode && actionUrl === "" && formAction !== "createCustomer",
        formKey,
    });
    const leadForm = leadFormLookup.form;
    const fieldsByKey = useMemo(
        () => new Map(
            (leadForm?.fields ?? []).map((field) => [field.key, field]),
        ),
        [leadForm],
    );
    const missingLeadFields = leadForm?.fields.filter(
        (field) => !authoredFieldNames.has(field.key),
    ) ?? [];
    const hasFileUpload = lagunaNestedFormFieldBlocks(block).some(
        (fieldBlock) => formFieldInputType(fieldBlock) === "fileupload",
    );
    const publishedPageHrefs = useMemo(
        () => new Set(
            publicPageLinks(pages, storefrontHref).map((page) => page.href),
        ),
        [pages, storefrontHref],
    );
    const busy = submitting || leadFormLookup.loading;
    const formRuntime = useMemo<ShopLeadFormRuntimeContextValue>(() => {
        const primaryFieldsByKey = new Map<string, ShopTemplateBlock>();

        authoredFields.forEach((field) => {
            if (!primaryFieldsByKey.has(field.key)) {
                primaryFieldsByKey.set(field.key, field.block);
            }
        });

        return {
            authoredFields: new Map(authoredFields.map((field) => [field.block, field])),
            busy,
            enforceSchema: actionUrl === "" && formAction !== "createCustomer",
            fieldNames,
            fieldsByKey,
            formBlock: block,
            primaryFieldsByKey,
            readOnly: editorMode,
            schemaResolved: leadForm !== null,
        };
    }, [actionUrl, authoredFields, block, busy, editorMode, fieldNames, fieldsByKey, formAction, leadForm]);
    const visibleError = error
        ?? (leadFormLookup.error ? message("ui.common.retry") : null);

    async function submitForm(event: FormEvent<HTMLFormElement>): Promise<void> {
        if (editorMode) {
            event.preventDefault();

            return;
        }

        if (actionUrl !== "") {
            return;
        }

        event.preventDefault();

        if (busy) {
            return;
        }

        if (formAction !== "createCustomer" && !leadForm) {
            setError(message("ui.common.retry"));

            return;
        }

        const formElement = event.currentTarget;
        const view = formElement.ownerDocument.defaultView;
        const values = siteBuilderLeadValuesFromFormData(
            new FormData(formElement),
        );

        setError(null);
        setSuccess(null);
        setSubmitting(true);

        try {
            let responseMessage: string | null = null;
            const succeeded = formAction === "createCustomer"
                ? await commerceContext?.onRegisterCustomer(
                    String(values.email ?? ""),
                    String(values.password ?? ""),
                    String(
                        values.passwordConfirmation
                        ?? values.password_confirmation
                        ?? "",
                    ),
                    (values.newsletterOptOut ?? values.newsletter_opt_out) === "true",
                ) ?? false
                : await submitSiteBuilderLead({
                    endpoint: "/api/lead/submit",
                    fields: values,
                    form: leadForm,
                    honeypotValue: typeof values.honeypotValue === "string"
                        ? values.honeypotValue
                        : "",
                    landingUrl: view?.location.href ?? "",
                    purpose: formKey,
                    startedAt,
                }).then((result) => {
                    responseMessage = result.message;

                    return result.ok;
                });

            if (!succeeded) {
                setError(responseMessage ?? message("ui.common.retry"));

                return;
            }

            formElement.reset();

            const successMode = blockOptionText(block, "formOnSuccess") || "message";
            const successUrl = blockText(block, "successUrl", "").trim();
            const localizedSuccessUrl = successUrl === ""
                ? ""
                : storefrontHref(successUrl);

            if (
                successMode === "redirect"
                && localizedSuccessUrl !== ""
                && publishedPageHrefs.has(localizedSuccessUrl)
            ) {
                view?.location.assign(localizedSuccessUrl);

                return;
            }

            if (successMode !== "nothing") {
                setSuccess(
                    config.successMessage
                    ?? responseMessage
                    ?? message(
                        formAction === "createCustomer"
                            ? "ui.account.registrationCheckEmail"
                            : "block.systemContactForm.successMessage",
                    ),
                );
            }
        } catch {
            setError(message("ui.common.retry"));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <form
            {...atomicBlockInteractionAttributes(block)}
            action={actionUrl || undefined}
            aria-busy={busy}
            className="grid gap-3 rounded-[6px] p-8"
            encType={actionUrl && hasFileUpload ? "multipart/form-data" : undefined}
            method={actionUrl ? "post" : undefined}
            onSubmit={(event) => void submitForm(event)}
            style={formBlockStyle(block)}
        >
            <ShopLeadFormRuntimeContext.Provider value={formRuntime}>
                {actionUrl === "" ? <input autoComplete="off" hidden name="honeypotValue" tabIndex={-1} type="text" /> : null}
                {visibleError ? <p className="rounded-[4px] border border-[color-mix(in_srgb,var(--site-on-primary)_34%,transparent)] px-4 py-3 text-sm font-semibold text-[var(--site-on-primary)]" role="alert">{visibleError}</p> : null}
                {success ? <p className="rounded-[4px] border border-[color-mix(in_srgb,var(--site-accent)_55%,transparent)] bg-[color-mix(in_srgb,var(--site-accent)_16%,transparent)] px-4 py-3 text-sm font-semibold text-[var(--site-on-primary)]" role="status">{success}</p> : null}
                {missingLeadFields.length > 0 ? (
                    <div className="grid gap-3" data-laguna-lead-schema-fields="true">
                        {missingLeadFields.map((field) => (
                            <FormFieldBlock
                                block={lagunaLeadSchemaFieldBlock(field)}
                                fieldName={field.key}
                                formBlock={block}
                                key={field.key}
                                readOnly={editorMode}
                                schemaField={field}
                                synthetic
                            />
                        ))}
                    </div>
                ) : null}
                <SlotBlockGroup
                    block={block}
                    slotKey={slotKey}
                    partPrefix={partPrefix}
                    className="grid gap-3"
                    childSlotParentType="form"
                    renderItem={({ childBlock, itemPart }) => (
                        <AtomicBlock
                            block={childBlock}
                            containerBlock={block}
                            partPrefix={itemPart}
                            slotParentType="form"
                        />
                    )}
                />
            </ShopLeadFormRuntimeContext.Provider>
        </form>
    );
}

function LagunaCountdownBlock({ block }: { block: ShopTemplateBlock }) {
    const message = useShopStorefrontMessage();
    const items = useSiteBuilderCountdown(blockText(block, "targetDate", ""));

    return (
        <div {...atomicBlockInteractionAttributes(block)} className="grid gap-4" style={atomicBlockStyle(block)}>
            <p {...editableTextPart("title")} className="text-sm font-black uppercase tracking-[0.16em] text-[var(--site-primary)]" style={partStyle(block, "title")}>{richTextContent(block, "title", message("block.countdown.title"))}</p>
            <div className="grid grid-cols-4 gap-2 text-center">
                {items.map((item) => (
                    <div key={item.labelKey} className="rounded-[18px] bg-[var(--site-surface)] px-3 py-4">
                        <span className="block text-2xl font-black text-[var(--site-text)]">{item.text}</span>
                        <span className="mt-1 block text-[10px] font-black uppercase tracking-[0.18em] text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">
                            {message(item.labelKey)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function AtomicBlock({
    block,
    containerBlock,
    partPrefix,
    slotParentType,
}: {
    block: ShopTemplateBlock;
    containerBlock?: ShopTemplateBlock | null;
    partPrefix?: string;
    slotParentType?: string;
}) {
    const headerContext = useContext(ShopHeaderChromeContext);
    const commerceContext = useContext(ShopCommerceContentContext);
    const message = useShopStorefrontMessage();
    const editorMode = useContext(ShopEditorModeContext);
    const storefrontHref = useShopStorefrontHref();
    const languageCode = useContext(ShopStorefrontLanguageContext);
    const { pages } = useContext(ShopSearchContext);
    const { siteName, storesHref } = useContext(ShopSiteInfoContext);
    const submitButtonContext = useContext(ShopSubmitButtonContext);
    const entityContext = useContext(EditableEntityRenderContext);
    const entityField = dynamicEntityField(block);
    const contentReplacesFallback = useSlotReplacesFallback(block, "content");
    const livePageLinks = publicPageLinks(pages, storefrontHref);
    const runtimeTextSource = blockOptionText(block, "runtimeText");

    if (block.type === "headerLogo") {
        return headerContext ? (
            <div {...atomicBlockInteractionAttributes(block)} style={atomicBlockStyle(block)}>
                <ShopLogo href={headerContext.homeHref} siteName={headerContext.displayName} />
            </div>
        ) : null;
    }

    if (block.type === "headerSearch") {
        return headerContext ? (
            <button
                {...editablePart("search")}
                type="button"
                className={classNames(
                    "flex min-h-11 w-full items-center justify-between rounded-full border border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] bg-[var(--site-surface)] px-5 py-3 text-sm shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25",
                    readableOnSurfaceTextClass,
                )}
                onClick={headerContext.onOpenSearch}
                style={readableSemanticTextStyle(mergeStyles(atomicBlockStyle(block), partStyle(block, "search")))}
            >
                <span>{blockText(block, "placeholder", "Co hledáte?")}</span>
                <span className="grid h-8 w-8 place-items-center rounded-full bg-[var(--site-primary)] text-[var(--site-on-primary)]">
                    <Icon name="search" className="h-4 w-4" />
                </span>
            </button>
        ) : null;
    }

    if (block.type === "headerStoresButton") {
        return headerContext ? (
            <div {...atomicBlockInteractionAttributes(block)} style={atomicBlockStyle(block)}>
                <StoreHeaderDropdown
                    cities={headerContext.storeCities}
                    storesHref={headerContext.storesHref}
                    onClose={() => undefined}
                />
            </div>
        ) : null;
    }

    if (block.type === "headerAccountButton") {
        return headerContext ? (
            <div {...atomicBlockInteractionAttributes(block)} style={atomicBlockStyle(block)}>
                <AccountHeaderDropdown
                    accountHref={headerContext.accountHref}
                    customerSession={headerContext.customerSession}
                    loading={headerContext.customerLoading}
                    onLogout={headerContext.onLogoutCustomer}
                />
            </div>
        ) : null;
    }

    if (block.type === "headerLanguageSelector") {
        return headerContext?.currentLanguage ? (
            <div {...atomicBlockInteractionAttributes(block)} style={atomicBlockStyle(block)}>
                <HeaderSelectorDropdown
                    currentCode={headerContext.currentLanguage.code}
                    options={headerContext.languageOptions}
                    ariaLabel={blockText(block, "title", "Vybrat jazyk")}
                    onSelect={headerContext.onLanguageSelect}
                />
            </div>
        ) : null;
    }

    if (block.type === "headerCurrencySelector") {
        return headerContext?.currentCurrency ? (
            <div {...atomicBlockInteractionAttributes(block)} style={atomicBlockStyle(block)}>
                <HeaderSelectorDropdown
                    currentCode={headerContext.currentCurrency.code}
                    options={headerContext.currencyOptions}
                    ariaLabel={blockText(block, "title", "Vybrat měnu")}
                    onSelect={headerContext.onCurrencySelect}
                />
            </div>
        ) : null;
    }

    if (block.type === "headerFavoritesButton") {
        return headerContext ? (
            <a
                {...editablePart("primaryButton")}
                href={headerContext.customerUuid !== null ? accountSectionHref(headerContext.accountHref, "favorites") : headerContext.accountHref}
                className={headerIconActionClass}
                aria-label={blockText(block, "label", message("block.headerFavoritesButton.label"))}
                style={mergeStyles(atomicButtonBlockStyle(block), partStyle(block, "primaryButton"))}
            >
                <Icon name="heart" className={classNames("h-7 w-7", headerContext.visibleFavoriteProductIds.size > 0 && "fill-current text-[var(--site-primary)]")} />
                <span className={classNames("absolute right-0 top-0 h-5 min-w-5 place-items-center rounded-full bg-[var(--site-primary)] px-1 text-[10px] font-bold text-[var(--site-on-primary)]", headerContext.visibleFavoriteProductIds.size > 0 ? "grid" : "hidden")}>
                    {headerContext.visibleFavoriteProductIds.size}
                </span>
            </a>
        ) : null;
    }

    if (block.type === "headerCartButton") {
        return headerContext ? (
            <div className="group relative" style={blockVisualStyle(block)}>
                <a {...editablePart("primaryButton")} href={headerContext.cartHref} className={headerIconActionClass} aria-label={blockText(block, "label", message("block.headerCartButton.label"))} style={partStyle(block, "primaryButton")}>
                    <Icon name="bag" className="h-7 w-7" />
                    <span className={classNames("absolute right-0 top-0 h-5 min-w-5 place-items-center rounded-full bg-[var(--site-primary)] px-1 text-[10px] font-bold text-[var(--site-on-primary)]", headerContext.cart.totalQuantity > 0 ? "grid" : "hidden")}>
                        {headerContext.cart.totalQuantity}
                    </span>
                </a>
                <CartHoverPreview
                    cart={headerContext.cart}
                    loading={headerContext.cartLoading}
                    error={headerContext.cartError}
                    cartHref={headerContext.cartHref}
                />
            </div>
        ) : null;
    }

    if (block.type === "headerBenefitStrip") {
        return (
            <div {...atomicBlockInteractionAttributes(block)} className="h-full overflow-hidden rounded-full bg-[color-mix(in_srgb,var(--site-surface)_95%,transparent)] shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_10%,transparent)]" style={atomicBlockStyle(block)}>
                <BenefitStrip block={block} compact />
            </div>
        );
    }

    if (block.type === "column") {
        return <ColumnBlock block={block} partPrefix={partPrefix} slotParentType={slotParentType} />;
    }

    if (block.type === "separator") {
        return (
            <div {...atomicBlockInteractionAttributes(block)} className="w-full py-4" style={separatorContainerStyle(block)}>
                <div className="mx-auto" style={separatorLineStyle(block)} />
            </div>
        );
    }

    if (block.type === "link") {
        const label = blockText(block, "label", "Odkaz");
        const rawHref = functionalStorefrontHref(blockText(block, "href", ""));
        const footerLink = slotParentType?.startsWith("footer") === true;
        const unresolvedFooterFragment = footerLink && (!rawHref || rawHref.startsWith("#"));
        const href = rawHref && !unresolvedFooterFragment
            ? authoredStorefrontHref(rawHref, storefrontHref, pages, languageCode)
            : undefined;
        const style = readableSemanticTextStyle(mergeStyles(
            blockVisualStyle(block),
            partStyle(block, "text"),
            partStyle(block, "primaryButton"),
            partStyle(block, "label"),
        ));

        if (unresolvedFooterFragment && !editorMode) {
            return null;
        }

        if (slotParentType === "footerColumn:bottom") {
            if (unresolvedFooterFragment) {
                return (
                    <span {...editableTextPart("label")} {...atomicBlockInteractionAttributes(block)} className={classNames("text-[11px]", readableOnInverseTextClass)} style={style}>
                        {richTextContent(block, "label", label)}
                    </span>
                );
            }

            return (
                <a {...editableTextPart("label")} {...atomicBlockInteractionAttributes(block)} href={href} className={classNames("text-[11px] hover:text-[var(--site-on-inverse)]", readableOnInverseTextClass)} style={style}>
                    {richTextContent(block, "label", label)}
                </a>
            );
        }

        if (slotParentType?.startsWith("footer")) {
            if (unresolvedFooterFragment) {
                return (
                    <span {...editableTextPart("label")} {...atomicBlockInteractionAttributes(block)} className={classNames("block text-sm", readableOnInverseTextClass)} style={style}>
                        {richTextContent(block, "label", label)}
                    </span>
                );
            }

            return (
                <a {...editableTextPart("label")} {...atomicBlockInteractionAttributes(block)} href={href} className={classNames("block text-sm hover:text-[var(--site-on-inverse)]", readableOnInverseTextClass)} style={style}>
                    {richTextContent(block, "label", label)}
                </a>
            );
        }

        return (
            <a {...editableTextPart("label")} {...atomicBlockInteractionAttributes(block)} href={href} className="inline-flex text-sm font-bold text-[var(--site-primary)]" style={style}>
                {richTextContent(block, "label", label, {
                    textDecoration: "underline",
                    textUnderlineOffset: "4px",
                })}
            </a>
        );
    }

    if (block.type === "text" && (slotParentType?.startsWith("header") || slotParentType === "promoBar" || slotParentType === "footerService")) {
        const fallback = blockText(block, "text", "");
        const value = runtimeTextValue(runtimeTextSource, fallback, siteName, headerContext);

        return (
            <span
                {...runtimeTextPart(runtimeTextSource, "text")}
                {...atomicBlockInteractionAttributes(block)}
                className={classNames(
                    "inline-flex min-w-0 items-center font-bold leading-none",
                    slotParentType === "headerLogo"
                        ? "max-w-[68px] whitespace-normal break-words text-base font-black uppercase leading-tight sm:max-w-[230px] sm:text-2xl"
                        : slotParentType === "footerService"
                            ? "max-w-[230px] truncate whitespace-nowrap text-2xl font-black uppercase"
                        : slotParentType === "promoBar"
                            ? "whitespace-nowrap text-[11px] sm:text-xs"
                            : "whitespace-nowrap text-xs",
                )}
                style={mergeStyles(atomicBlockVisualStyle(block), partStyle(block, "text"))}
            >
                {runtimeTextSource ? value : richTextContent(block, "text", value)}
            </span>
        );
    }

    if (block.type === "text" && slotParentType?.startsWith("footer")) {
        const fallback = blockText(block, "text", "Odkaz");
        const label = runtimeTextValue(runtimeTextSource, fallback, siteName, headerContext);
        const href = authoredStorefrontHref(blockText(block, "href", ""), storefrontHref, pages, languageCode);
        const style = readableSemanticTextStyle(mergeStyles(blockVisualStyle(block), partStyle(block, "text")));
        const textAttributes = runtimeTextPart(runtimeTextSource, "text");

        if (slotParentType === "footerColumn:bottom") {
            if (!href) {
                return (
                    <p {...textAttributes} {...atomicBlockInteractionAttributes(block)} className={classNames("text-[11px]", readableOnInverseTextClass)} style={style}>
                        {runtimeTextSource ? label : richTextContent(block, "text", label)}
                    </p>
                );
            }

            return (
                <a {...textAttributes} {...atomicBlockInteractionAttributes(block)} href={href} className={classNames("text-[11px] hover:text-[var(--site-on-inverse)]", readableOnInverseTextClass)} style={style}>
                    {runtimeTextSource ? label : richTextContent(block, "text", label)}
                </a>
            );
        }

        if (slotParentType === "footerSocial") {
            const network = socialNetworkFromHref(href ?? label);
            const size = 40;

            if (!href && !editorMode) {
                return null;
            }

            return (
                <a
                    {...editablePart("text")}
                    href={href}
                    aria-label={socialNetworkLabel(network, message("ui.social.link"))}
                    className="grid place-items-center rounded-full bg-[var(--site-surface)] text-xs font-bold shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_10%,transparent)] hover:-translate-y-0.5"
                    style={{
                        ...style,
                        color: socialNetworkColor(network),
                        height: size,
                        width: size,
                    }}
                >
                    <SocialNetworkIcon network={network} className="h-[52%] w-[52%]" />
                </a>
            );
        }

        if (slotParentType === "footerApps") {
            if (!href && !editorMode) {
                return null;
            }

            return (
                <a
                    {...editablePart("text")}
                    href={href}
                    className="rounded border border-[color-mix(in_srgb,var(--site-on-inverse)_25%,transparent)] px-3 py-2 text-xs font-bold"
                    style={style}
                >
                    {label}
                </a>
            );
        }

        if (!href) {
            return (
                <p {...textAttributes} {...atomicBlockInteractionAttributes(block)} className={classNames("text-xs", readableOnInverseTextClass)} style={style}>
                    {runtimeTextSource ? label : richTextContent(block, "text", label)}
                </p>
            );
        }

        if (href.startsWith("tel:")) {
            return (
                <a {...textAttributes} {...atomicBlockInteractionAttributes(block)} href={href} className="block text-xl font-bold text-[var(--site-on-inverse)] hover:text-[var(--site-on-inverse)]" style={style}>
                    {runtimeTextSource ? label : richTextContent(block, "text", label)}
                </a>
            );
        }

        if (href.startsWith("mailto:")) {
            return (
                <a {...textAttributes} {...atomicBlockInteractionAttributes(block)} href={href} className="block text-sm text-[var(--site-on-inverse)] underline hover:text-[var(--site-on-inverse)]" style={style}>
                    {runtimeTextSource ? label : richTextContent(block, "text", label)}
                </a>
            );
        }

        return (
            <a {...textAttributes} {...atomicBlockInteractionAttributes(block)} href={href} className={classNames("block text-sm hover:text-[var(--site-on-inverse)]", readableOnInverseTextClass)} style={style}>
                {runtimeTextSource ? label : richTextContent(block, "text", label)}
            </a>
        );
    }

    if (block.type === "text") {
        const text = inlineTextDisplayValue(block, "text", "Volny obsah, ktery lze upravit primo v editoru.", editorMode);
        const runtimeValue = runtimeTextValue(runtimeTextSource, text.value, siteName, headerContext);
        const dynamicValue = runtimeTextSource
            ? runtimeValue
            : dynamicEntityTextValue(entityField, entityContext, text.value);
        const empty = dynamicValue.trim() === "" || dynamicValue === "\u200b";
        const value = empty && editorMode ? "\u200b" : dynamicValue;
        const content = empty || dynamicValue !== text.value ? value : richTextContent(block, "text", text.value);

        if (slotParentType?.startsWith("heroSlider")) {
            return (
                <p
                    {...dynamicEntityTextAttributes("text", entityField, entityContext)}
                    {...inlineTextPlaceholderAttributes(empty, isEditorCommandLineBlock(block))}
                    {...atomicBlockInteractionAttributes(block)}
                    className="whitespace-pre-line text-sm font-bold uppercase text-[var(--site-on-inverse)]"
                    style={mergeStyles(atomicBlockVisualStyle(block), partStyle(block, "text"), { height: "auto" })}
                >
                    {content}
                </p>
            );
        }

        return <p {...(runtimeTextSource ? runtimeTextPart(runtimeTextSource, "text") : dynamicEntityTextAttributes("text", entityField, entityContext))} {...inlineTextPlaceholderAttributes(empty, isEditorCommandLineBlock(block))} {...atomicBlockInteractionAttributes(block)} className={classNames("whitespace-pre-line text-sm leading-7", readableOnSurfaceTextClass)} style={readableSemanticTextStyle(mergeStyles(atomicBlockVisualStyle(block), partStyle(block, "text")))}>{runtimeTextSource ? dynamicValue : content}</p>;
    }

    if (block.type === "image") {
        const presentation = siteBuilderMediaPresentation(block.content);
        const imageUrl = presentation.kind === "image" ? presentation.source : "";
        const headerLogoImage = slotParentType === "headerLogo";

        return imageUrl ? (
            <img
                {...editablePart("image")}
                src={imageUrl}
                alt={blockText(block, "alt", "")}
                title={blockText(block, "imageTitle", "") || undefined}
                className={headerLogoImage
                    ? "block max-h-12 min-h-0 w-auto max-w-full object-contain"
                    : "block h-full min-h-32 w-full object-cover"}
                style={mergeStyles(atomicBlockStyle(block), imagePartStyle(block, "image"))}
            />
        ) : (
            <div
                {...editablePart("image")}
                className={classNames(
                    headerLogoImage ? "h-12 min-h-0 w-12" : "min-h-32 w-full",
                    "bg-[linear-gradient(135deg,var(--site-surface),color-mix(in_srgb,var(--site-primary)_16%,var(--site-surface)))]",
                )}
                style={mergeStyles(atomicBlockStyle(block), imagePartStyle(block, "image"))}
            />
        );
    }

    if (block.type === "video") {
        const videoUrl = blockText(
            block,
            "videoUrl",
            blockText(block, "embedUrl", blockText(block, "url", "")),
        );
        const videoSource = siteVideoSource(videoUrl);

        const aspectRatio = shopPortableAspectRatio(
            blockOptionText(block, "aspectRatio") || blockOptionText(block, "embedAspectRatio"),
        );

        return (
            <div
                {...atomicBlockInteractionAttributes(block)}
                className="grid min-h-32 place-items-center bg-[var(--site-inverse)] text-[var(--site-on-inverse)]"
                style={mergeStyles(atomicBlockStyle(block), { aspectRatio })}
            >
                {videoSource?.kind === "embed" ? (
                    <iframe
                        {...editablePart("video")}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="h-full w-full border-0"
                        loading="lazy"
                        referrerPolicy="strict-origin-when-cross-origin"
                        sandbox="allow-forms allow-presentation allow-same-origin allow-scripts"
                        src={videoSource.source}
                        style={mergeStyles(partStyle(block, "video"), { pointerEvents: editorMode ? "none" : undefined })}
                        tabIndex={editorMode ? -1 : undefined}
                        title={blockText(block, "title", message("ui.block.video"))}
                    />
                ) : videoSource?.kind === "file" ? (
                    <video
                        {...editablePart("video")}
                        className="h-full w-full object-cover"
                        controls
                        playsInline
                        src={videoSource.source}
                        style={mergeStyles(partStyle(block, "video"), { pointerEvents: editorMode ? "none" : undefined })}
                        tabIndex={editorMode ? -1 : undefined}
                    />
                ) : (
                    <span {...editablePart("video")} className="grid h-14 w-14 place-items-center rounded-full bg-[var(--site-surface)] text-lg font-black text-[var(--site-text)]" style={partStyle(block, "video")}>▶</span>
                )}
            </div>
        );
    }

    if (block.type === "iconList") {
        const text = inlineTextDisplayValue(
            block,
            "items",
            editorMode ? "Rychle doruceni\nBezpecna platba\nSnadne vraceni" : "",
            editorMode,
        );
        const items = richTextLineContents(
            block,
            "items",
            text.value || (editorMode ? "\u200b" : ""),
        );
        const icon = blockText(block, "icon", "lucide:check");
        const iconStyle = partStyle(block, "icon");

        if (!editorMode && items.length === 0) {
            return null;
        }

        return (
            <div {...editableTextPart("items")} {...atomicBlockInteractionAttributes(block)} className="grid gap-3" style={mergeStyles(atomicBlockStyle(block), partStyle(block, "items"))}>
                {items.map((item, index) => (
                    <div key={`${item.text}:${index}`} data-shop-icon-list-row="true" className="flex items-center gap-3 text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_70%,transparent)]">
                        <span data-shop-icon-list-icon="true" data-shop-icon-part={partPrefix ? `${partPrefix}:icon` : "icon"} contentEditable={false} className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[var(--site-primary)] text-xs text-[var(--site-on-primary)]" style={iconStyle}>{atomicIconGlyph(icon)}</span>
                        <span data-shop-icon-list-text="true">{item.content}</span>
                    </div>
                ))}
            </div>
        );
    }

    if (block.type === "socialMedia") {
        return <SocialMediaIcons block={block} partPrefix={partPrefix} />;
    }

    if (block.type === "product") {
        if (!editorMode) {
            return null;
        }

        const imageUrl = blockText(block, "imageUrl", "");

        return (
            <div {...atomicBlockInteractionAttributes(block)} className={classNames("bg-[var(--site-surface)] shadow-sm", imageContentLayoutClass(block, "top"))} style={atomicBlockStyle(block)}>
                {imageUrl ? (
                    <img
                        {...editablePart("image")}
                        src={imageUrl}
                        alt={blockText(block, "alt", blockText(block, "title", message("block.product.title")))}
                        title={blockText(block, "imageTitle", "") || undefined}
                        className={classNames("h-36 w-full object-cover", imageContentImageOrderClass(block, "top"))}
                        style={imagePartStyle(block, "image")}
                    />
                ) : null}
                <div className={imageContentTextOrderClass(block, "top")}>
                    <p {...editableTextPart("title")} className="text-lg font-black text-[var(--site-text)]" style={partStyle(block, "title")}>{richTextContent(block, "title", "Produkt")}</p>
                    <p {...editableTextPart("price")} className="mt-2 text-xl font-black text-[var(--site-primary)]" style={partStyle(block, "price")}>{richTextContent(block, "price", "1 299 Kč")}</p>
                    {blockText(block, "text", "") ? <p {...editableTextPart("text")} className="mt-2 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]" style={partStyle(block, "text")}>{richTextContent(block, "text", "")}</p> : null}
                </div>
            </div>
        );
    }

    if (block.type === "field") {
        return <FormFieldBlock block={block} formBlock={containerBlock} />;
    }

    if (block.type === "submitButton") {
        return <FormSubmitButtonBlock block={block} formBlock={containerBlock} />;
    }

    if (block.type === "form") {
        return <FormBlock block={block} partPrefix={partPrefix} />;
    }

    if (block.type === "heading" && slotParentType?.startsWith("footer")) {
        const title = inlineTextDisplayValue(block, "text", "Nadpis", editorMode);
        const empty = title.value.trim() === "" || title.value === "\u200b";
        const value = empty && editorMode ? "\u200b" : title.value;
        const content = empty ? value : richTextContent(block, "text", title.value);

        return (
            <h3 {...editableTextContentPart("text", "title")} {...inlineTextPlaceholderAttributes(empty, isEditorCommandLineBlock(block))} {...atomicBlockInteractionAttributes(block)} className="text-sm font-bold text-[var(--site-on-inverse)]" style={mergeStyles(atomicBlockVisualStyle(block), partStyle(block, "title"), partStyle(block, "text"))}>
                {content}
            </h3>
        );
    }

    if (block.type === "heading") {
        const title = inlineTextDisplayValue(block, "text", "Novy nadpis", editorMode);
        const dynamicValue = dynamicEntityTextValue(entityField, entityContext, title.value);
        const empty = dynamicValue.trim() === "" || dynamicValue === "\u200b";
        const value = empty && editorMode ? "\u200b" : dynamicValue;
        const content = empty || dynamicValue !== title.value ? value : richTextContent(block, "text", title.value);

        if (slotParentType?.startsWith("heroSlider")) {
            const Heading = slotParentType === "heroSlider:firstSlide" ? "h1" : "h2";

            return (
                <div {...atomicBlockInteractionAttributes(block)} style={mergeStyles(atomicBlockVisualStyle(block), { height: "auto" })}>
                    {blockText(block, "eyebrow", "") ? <p {...editableTextPart("eyebrow")} className="text-sm font-bold uppercase text-[var(--site-on-inverse)]" style={partStyle(block, "eyebrow")}>{richTextContent(block, "eyebrow", "")}</p> : null}
                    <Heading
                        {...dynamicEntityTextAttributes("text", entityField, entityContext, "title")}
                        {...inlineTextPlaceholderAttributes(empty, isEditorCommandLineBlock(block))}
                        className="mt-3 max-w-4xl text-5xl font-black leading-none text-[var(--site-on-inverse)] sm:text-7xl"
                        style={mergeStyles(atomicBlockVisualStyle(block), partStyle(block, "title"), partStyle(block, "text"), { height: "auto" })}
                    >
                        {content}
                    </Heading>
                </div>
            );
        }

        const headingLevel = blockOptionString(block, "level");
        const Heading = headingLevel === "1" ? "h1" : headingLevel === "3" ? "h3" : "h2";

        return (
            <div {...atomicBlockInteractionAttributes(block)} style={atomicBlockVisualStyle(block)}>
                {blockText(block, "eyebrow", "") ? <p {...editableTextPart("eyebrow")} className="text-xs font-black uppercase text-[var(--site-primary)]" style={partStyle(block, "eyebrow")}>{richTextContent(block, "eyebrow", "")}</p> : null}
                <Heading {...dynamicEntityTextAttributes("text", entityField, entityContext, "title")} {...inlineTextPlaceholderAttributes(empty, isEditorCommandLineBlock(block))} className="mt-3 max-w-3xl text-3xl font-black text-[var(--site-text)] sm:text-4xl" style={mergeStyles(atomicBlockVisualStyle(block), partStyle(block, "title"), partStyle(block, "text"))}>{content}</Heading>
            </div>
        );
    }

    if (block.type === "paragraph") {
        const text = inlineTextDisplayValue(block, "text", "Volny obsah, ktery lze upravit primo v editoru.", editorMode);
        const dynamicValue = dynamicEntityTextValue(entityField, entityContext, text.value);
        const empty = dynamicValue.trim() === "" || dynamicValue === "\u200b";
        const value = empty && editorMode ? "\u200b" : dynamicValue;
        const content = empty || dynamicValue !== text.value ? value : richTextContent(block, "text", text.value);

        if (slotParentType?.startsWith("heroSlider")) {
            return (
                <p
                    {...dynamicEntityTextAttributes("text", entityField, entityContext)}
                    {...inlineTextPlaceholderAttributes(empty, isEditorCommandLineBlock(block))}
                    {...atomicBlockInteractionAttributes(block)}
                    className="max-w-md whitespace-pre-line text-base font-medium leading-8 text-[var(--site-on-inverse)] sm:text-lg"
                    style={mergeStyles(atomicBlockVisualStyle(block), partStyle(block, "text"), { height: "auto" })}
                >
                    {content}
                </p>
            );
        }

        return <p {...dynamicEntityTextAttributes("text", entityField, entityContext)} {...inlineTextPlaceholderAttributes(empty, isEditorCommandLineBlock(block))} {...atomicBlockInteractionAttributes(block)} className={classNames("max-w-3xl whitespace-pre-line text-base leading-8", readableOnSurfaceTextClass)} style={readableSemanticTextStyle(mergeStyles(atomicBlockVisualStyle(block), partStyle(block, "text")))}>{content}</p>;
    }

    if (block.type === "quote") {
        return (
            <blockquote {...atomicBlockInteractionAttributes(block)} className="border-l-4 border-[var(--site-primary)] bg-[var(--site-surface)] px-6 py-5" style={atomicBlockStyle(block)}>
                <p {...editableTextPart("text")} className="text-base font-bold leading-8 text-[var(--site-text)]" style={partStyle(block, "text")}>
                    {richTextContent(block, "text", "Volny obsah, ktery lze upravit primo v editoru.")}
                </p>
            </blockquote>
        );
    }

    if (block.type === "code") {
        const sourceCode = blockText(block, "code", blockText(block, "text", ""));
        const embedSource = shopEmbedSource(sourceCode);

        if (embedSource) {
            return (
                <div
                    {...editablePart("code")}
                    {...atomicBlockInteractionAttributes(block)}
                    className="relative min-h-44 w-full overflow-hidden rounded-[var(--site-radius)] border border-[var(--site-border)] bg-[var(--site-surface)] shadow-[0_18px_50px_var(--site-shadow-color)]"
                    style={mergeStyles(atomicBlockStyle(block), partStyle(block, "code"), { aspectRatio: shopPortableAspectRatio(blockOptionText(block, "embedAspectRatio")) })}
                >
                    <iframe
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="absolute inset-0 h-full w-full border-0"
                        loading="lazy"
                        referrerPolicy="strict-origin-when-cross-origin"
                        sandbox="allow-forms allow-presentation allow-same-origin allow-scripts"
                        src={embedSource}
                        style={{ pointerEvents: editorMode ? "none" : undefined }}
                        title={blockText(block, "title", message("ui.common.open"))}
                    />
                </div>
            );
        }

        return (
            <pre {...editableTextPart("code")} {...atomicBlockInteractionAttributes(block)} className="overflow-auto rounded-[18px] bg-[var(--site-inverse)] px-5 py-4 text-sm leading-7 text-[var(--site-on-inverse)]" style={mergeStyles(atomicBlockStyle(block), partStyle(block, "code"))}>
                <code>{richTextContent(block, "code", sourceCode || "const example = true;")}</code>
            </pre>
        );
    }

    if (block.type === "list" || block.type === "checklist") {
        const items = multilineItems(blockText(block, "items", "Prvni bod\nDruhy bod\nTreti bod"), ["Prvni bod", "Druhy bod", "Treti bod"]);

        return (
            <ul {...editablePart("items")} {...atomicBlockInteractionAttributes(block)} className="grid gap-3 text-sm leading-7 text-[color-mix(in_srgb,var(--site-text)_70%,transparent)]" style={mergeStyles(atomicBlockStyle(block), partStyle(block, "items"))}>
                {items.map((item, index) => (
                    <li key={`${item}-${index}`} className="flex gap-3">
                        <span className="mt-1 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[var(--site-primary)] text-[10px] font-black text-[var(--site-on-primary)]">
                            {block.type === "checklist" ? "✓" : index + 1}
                        </span>
                        <span>{item}</span>
                    </li>
                ))}
            </ul>
        );
    }

    if (block.type === "table") {
        const rows = multilineItems(blockText(block, "items", "Plan|Cena|Stav\nStarter|0 Kč|Aktivni\nPro|990 Kč|Doporuceno"), ["Plan|Cena|Stav", "Starter|0 Kč|Aktivni", "Pro|990 Kč|Doporuceno"])
            .map((row) => row.split("|").map((cell) => cell.trim()));

        return (
            <div {...atomicBlockInteractionAttributes(block)} className="overflow-x-auto" style={atomicBlockStyle(block)}>
                <table {...editablePart("items")} className="min-w-full border-collapse overflow-hidden rounded-[18px] text-sm" style={partStyle(block, "items")}>
                    <tbody>
                        {rows.map((row, rowIndex) => (
                            <tr key={`${row.join(":")}-${rowIndex}`} className={rowIndex === 0 ? "bg-[var(--site-surface)] font-black text-[var(--site-text)]" : "border-t border-[var(--site-border)] text-[color-mix(in_srgb,var(--site-text)_65%,transparent)]"}>
                                {row.map((cell, cellIndex) => (
                                    <td key={`${cell}-${cellIndex}`} className="px-4 py-3">
                                        {cell}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    if (block.type === "media") {
        const presentation = siteBuilderMediaPresentation(block.content);
        const imageUrl = presentation.kind === "image" ? presentation.source : "";
        const videoUrl = presentation.kind === "video" ? presentation.source : "";
        const headerLogoMedia = slotParentType === "headerLogo";

        if (imageUrl) {
            return (
                <img
                    {...editablePart("image")}
                    src={imageUrl}
                    alt={blockText(block, "alt", "")}
                    className={headerLogoMedia
                        ? "block max-h-12 min-h-0 w-auto max-w-full object-contain"
                        : "block h-full min-h-32 w-full object-cover"}
                    style={mergeStyles(atomicBlockStyle(block), imagePartStyle(block, "image"))}
                />
            );
        }

        return (
            <div
                {...atomicBlockInteractionAttributes(block)}
                className={classNames(
                    "grid place-items-center bg-[var(--site-surface)] text-sm font-black text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]",
                    headerLogoMedia ? "h-12 min-h-0 w-12" : "aspect-video min-h-32",
                )}
                style={atomicBlockStyle(block)}
            >
                {videoUrl ? "MEDIA" : "MEDIA"}
            </div>
        );
    }

    if (block.type === "uploadFile") {
        const source = functionalStorefrontHref(
            blockText(block, "file", blockText(block, "url", blockText(block, "href", ""))),
        );

        if (!source) {
            return editorMode ? (
                <div {...editablePart("file")} {...atomicBlockInteractionAttributes(block)} className="rounded-[18px] border border-dashed border-[var(--site-border)] bg-[var(--site-surface)] px-5 py-4 text-sm font-black text-[var(--site-text)]" style={mergeStyles(atomicBlockStyle(block), partStyle(block, "file"))}>
                    {blockText(block, "label", "Stahnout soubor")}
                </div>
            ) : null;
        }

        return (
            <a {...editablePart("file")} {...atomicBlockInteractionAttributes(block)} download href={source} className="inline-flex items-center gap-3 rounded-[18px] border border-[var(--site-border)] bg-[var(--site-surface)] px-5 py-4 text-sm font-black text-[var(--site-text)]" style={mergeStyles(atomicBlockStyle(block), partStyle(block, "file"))}>
                <span className="grid h-8 w-8 place-items-center rounded-full bg-[var(--site-surface)] text-[var(--site-primary)]">↓</span>
                {blockText(block, "label", "Stahnout soubor")}
            </a>
        );
    }

    if (block.type === "emoji") {
        return (
            <span {...editableTextPart("text")} {...atomicBlockInteractionAttributes(block)} className="inline-flex text-3xl leading-none" style={mergeStyles(atomicBlockStyle(block), partStyle(block, "text"))}>
                {richTextContent(block, "text", "🙂")}
            </span>
        );
    }

    if (block.type === "signature") {
        return (
            <div {...atomicBlockInteractionAttributes(block)} className="grid gap-1" style={atomicBlockStyle(block)}>
                <p {...editableTextPart("text")} className="text-2xl font-black italic text-[var(--site-text)]" style={partStyle(block, "text")}>{richTextContent(block, "text", "Podpis")}</p>
                <p {...editableTextPart("title")} className="text-xs font-black uppercase tracking-[0.16em] text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]" style={partStyle(block, "title")}>{richTextContent(block, "title", "Jmeno a role")}</p>
            </div>
        );
    }

    if (block.type === "button") {
        const footerButton = slotParentType?.startsWith("footer") === true;
        const heroButton = slotParentType?.startsWith("heroSlider") === true;
        const footerButtonStyle = blockOptionText(block, "footerButtonStyle");
        const footerButtonIcon = blockOptionText(block, "footerButtonIcon");
        const className = heroButton
            ? "inline-flex items-center rounded-full bg-[var(--site-surface)] px-7 py-3.5 text-sm font-bold text-[var(--site-text)] shadow-lg shadow-[var(--site-shadow-color)]"
            : footerButton
            ? footerButtonStyle === "app"
                ? "inline-flex items-center rounded border border-[color-mix(in_srgb,var(--site-on-inverse)_25%,transparent)] px-3 py-2 text-xs font-bold text-[var(--site-on-inverse)]"
                : "inline-flex items-center gap-2 border border-[color-mix(in_srgb,var(--site-on-inverse)_25%,transparent)] px-4 py-3 text-sm font-bold text-[var(--site-on-inverse)]"
            : "inline-flex items-center rounded-full bg-[var(--site-primary)] px-6 py-3 text-sm font-black text-[var(--site-on-primary)]";
        const style = mergeStyles(
            atomicButtonBlockStyle(block),
            partStyle(block, "primaryButton"),
            buttonLikeResizeStyle(block, "primaryButton"),
        );
        const content = (
            <>
                {footerButtonIcon === "mapPin" ? <Icon name="mapPin" className="h-4 w-4" /> : null}
                <span {...editableTextPart("primaryButton")}>{richTextContent(block, "label", "Zjistit vice")}</span>
            </>
        );

        return submitButtonContext ? (
            <button {...editablePart("primaryButton")} type="submit" className={className} style={style}>
                {content}
            </button>
        ) : (
            <a {...editablePart("primaryButton")} href={authoredStorefrontHref(blockText(block, "href", ""), storefrontHref, pages, languageCode)} className={className} style={style}>
                {content}
            </a>
        );
    }

    if (block.type === "appointmentButton" || block.type === "donationButton" || block.type === "addToCartButton") {
        const contextualProductUuid = stringOrNull(nestedRecord(contextData(commerceContext?.context), "product").uuid);
        const productUuid = blockText(block, "productUuid", "") || contextualProductUuid || "";
        const adding = block.type === "addToCartButton" && productUuid !== "" && commerceContext?.addingProductUuid === productUuid;
        const icon = blockText(
            block,
            "icon",
            block.type === "appointmentButton" ? "lucide:check" : block.type === "donationButton" ? "lucide:heart" : "lucide:bag",
        );
        const content = (
            <>
                <span aria-hidden="true" className="grid h-6 w-6 place-items-center rounded-full bg-[color-mix(in_srgb,var(--site-on-primary)_14%,transparent)]">
                    {atomicIconGlyph(icon, "h-3.5 w-3.5")}
                </span>
                <span {...editableTextPart("primaryButton")}>{adding ? "…" : richTextContent(block, "label", "Action")}</span>
            </>
        );

        if (block.type === "addToCartButton") {
            return (
                <button
                    {...editablePart("primaryButton")}
                    disabled={productUuid === "" || adding}
                    onClick={() => {
                        if (!editorMode && productUuid) commerceContext?.onAddToCart(productUuid);
                    }}
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full bg-[var(--site-primary)] px-6 py-3 text-sm font-black text-[var(--site-on-primary)] shadow-[0_12px_32px_var(--site-shadow-color)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
                    style={mergeStyles(
                        atomicButtonBlockStyle(block),
                        partStyle(block, "primaryButton"),
                        buttonLikeResizeStyle(block, "primaryButton"),
                    )}
                >
                    {content}
                </button>
            );
        }

        return (
            <a
                {...editablePart("primaryButton")}
                href={authoredStorefrontHref(blockText(block, "href", ""), storefrontHref, pages, languageCode)}
                onClick={(event) => editorMode && event.preventDefault()}
                className="inline-flex items-center gap-2 rounded-full bg-[var(--site-primary)] px-6 py-3 text-sm font-black text-[var(--site-on-primary)] shadow-[0_12px_32px_var(--site-shadow-color)] transition hover:-translate-y-0.5"
                style={mergeStyles(
                    atomicButtonBlockStyle(block),
                    partStyle(block, "primaryButton"),
                    buttonLikeResizeStyle(block, "primaryButton"),
                )}
            >
                {content}
            </a>
        );
    }

    if (block.type === "badge" || block.type === "ctaBadge") {
        if (block.type === "badge") {
            const iconBlocks = slotChildren(block, "icon");
            const iconBlock = iconBlocks[0] ?? null;
            const iconAlignment = blockOptionText(iconBlock, "alignment");
            const iconAfter = iconAlignment === "right";
            const iconGroup = iconBlocks.length > 0 ? (
                <SlotBlockGroup
                    block={block}
                    slotKey="icon"
                    partPrefix={partPrefix}
                    className="contents"
                    itemClassName="inline-grid shrink-0"
                />
            ) : null;
            const label = (
                <span {...editableTextPart("primaryButton")} style={partStyle(block, "primaryButton")}>
                    {richTextContent(block, "label", "Category")}
                </span>
            );

            return (
                <span
                    {...editablePart("badge")}
                    className="inline-flex h-7 min-h-0 w-full max-w-full items-center justify-center gap-1.5 rounded-full border px-3 py-0 text-[11px] font-black uppercase leading-none tracking-[0.08em]"
                    style={mergeStyles(
                        badgeVariantStyle(block),
                        blockVisualStyle(block),
                        partStyle(block, "badge"),
                        interactionStyle(block),
                        buttonLikeResizeStyle(block, "badge"),
                    )}
                >
                    {iconAfter ? label : iconGroup}
                    {iconAfter ? iconGroup : label}
                </span>
            );
        }

        const iconEntries = slotChildren(block, "icon").map((iconBlock, index) => ({ iconBlock, index }));
        const leadingIconEntries = iconEntries.filter(({ iconBlock }) => blockOptionText(iconBlock, "alignment") !== "right");
        const trailingIconEntries = iconEntries.filter(({ iconBlock }) => blockOptionText(iconBlock, "alignment") === "right");
        const renderIconEntries = (entries: typeof iconEntries): ReactNode => (
            entries.length > 0
                ? entries.map(({ iconBlock, index }) => {
                    const itemPart = partPrefix ? `${partPrefix}:${slotPart("icon", index)}` : slotPart("icon", index);

                    return (
                        <div
                            key={`icon:${index}:${iconBlock.type}`}
                            data-shop-block-part={itemPart}
                            className="inline-grid w-fit max-w-full shrink-0"
                            style={lineSlotChildWrapperStyle(block, iconBlock)}
                        >
                            <AtomicBlock block={iconBlock} partPrefix={itemPart} slotParentType={block.type} />
                        </div>
                    );
                })
                : null
        );
        const leadingIconGroup = renderIconEntries(leadingIconEntries);
        const trailingIconGroup = renderIconEntries(trailingIconEntries);
        const titleValue = blockText(block, "title", "");
        const copy = (
            <span className="flex min-w-0 items-center gap-1.5">
                {leadingIconGroup}
                {titleValue ? (
                    <span {...editableTextPart("title")} className="min-w-0 truncate" style={partStyle(block, "title")}>
                        {richTextContent(block, "title", "")}
                    </span>
                ) : null}
                <a
                    {...editablePart("primaryButton")}
                    href={authoredStorefrontHref(blockText(block, "href", ""), storefrontHref, pages, languageCode)}
                    className="inline-flex shrink-0 items-center font-black text-[var(--site-primary)] underline-offset-2 hover:underline"
                    style={partStyle(block, "primaryButton")}
                >
                    <span {...editableTextPart("primaryButton")}>{ctaBadgeLabelContent(block)}</span>
                </a>
                {trailingIconGroup}
            </span>
        );

        return (
            <span
                {...editablePart("badge")}
                className="inline-flex h-10 min-h-0 w-full max-w-full items-center justify-center gap-2 rounded-full border px-4 py-0 text-sm font-semibold leading-none"
                style={mergeStyles(
                    ctaBadgeStyle(block),
                    partStyle(block, "badge"),
                    buttonLikeResizeStyle(block, "badge"),
                )}
            >
                {copy}
            </span>
        );
    }

    if (block.type === "icon") {
        const iconValue = blockText(block, "icon", "check");
        const href = authoredStorefrontHref(blockText(block, "href", blockText(block, "url", "")), storefrontHref, pages, languageCode);
        const label = blockText(block, "label", blockText(block, "title", ""));
        const openInNewWindow = blockOptionText(block, "openInNewWindow") === "true";
        const largeHeaderIcon = slotParentType === "headerStoresButton"
            || slotParentType === "headerAccountButton"
            || slotParentType === "headerFavoritesButton"
            || slotParentType === "headerCartButton"
            || slotParentType === "headerLogo";
        const icon = (
            <span className="grid place-items-center text-[1.25em] leading-none text-inherit" style={atomicIconFrameStyle(block)}>
                {atomicIconGlyph(iconValue, largeHeaderIcon ? "h-full w-full" : "h-[55%] w-[55%]")}
            </span>
        );

        if (href) {
            return (
                <a
                    {...editablePart("icon")}
                    {...atomicBlockInteractionAttributes(block)}
                    aria-label={label || iconValue}
                    className="inline-grid place-items-center text-[var(--site-primary)]"
                    data-shop-icon-part={partPrefix || "icon"}
                    href={href}
                    onClick={(event) => editorMode && event.preventDefault()}
                    rel={openInNewWindow ? "noopener noreferrer" : undefined}
                    style={atomicIconOuterStyle(block)}
                    target={openInNewWindow ? "_blank" : undefined}
                >
                    {icon}
                </a>
            );
        }

        return (
            <span
                {...editablePart("icon")}
                {...atomicBlockInteractionAttributes(block)}
                className="inline-grid place-items-center text-[var(--site-primary)]"
                data-shop-icon-part={partPrefix || "icon"}
                style={atomicIconOuterStyle(block)}
            >
                {icon}
            </span>
        );
    }

    if (block.type === "searchBar") {
        return slotParentType === "headerSearch"
            ? <HeaderSearchBarBlock block={block} partPrefix={partPrefix} />
            : <SearchBarBlock block={block} partPrefix={partPrefix} />;
    }

    if (block.type === "input") {
        return <input name={blockText(block, "name", "field")} className="w-full rounded-full border border-[var(--site-border)] px-5 py-4 text-sm" placeholder={blockText(block, "placeholder", "")} style={partStyle(block, "input")} />;
    }

    if (block.type === "textarea") {
        return <textarea name={blockText(block, "name", "message")} className="min-h-28 w-full rounded-[22px] border border-[var(--site-border)] px-5 py-4 text-sm" placeholder={blockText(block, "placeholder", "")} style={partStyle(block, "input")} />;
    }

    if (block.type === "accordion") {
        return <AccordionBlock block={block} partPrefix={partPrefix} />;
    }

    if (block.type === "accordionItem") {
        return <AccordionItemBlock block={block} containerBlock={block} open />;
    }

    if (block.type === "alert") {
        return (
            <div {...atomicBlockInteractionAttributes(block)} className="border border-[var(--site-border)] bg-[var(--site-primary-soft)] px-5 py-4 text-[var(--site-text)]" style={atomicBlockStyle(block)}>
                <p {...editableTextPart("text")} className="text-sm leading-6 text-current" style={partStyle(block, "text")}>{richTextContent(block, "text", "Použijte tento blok pro krátkou informaci nebo stav.")}</p>
            </div>
        );
    }

    if (block.type === "iconText") {
        const textColor = blockOptionText(block, "textColor");
        const iconStyle = mergeStyles(textColor ? { color: textColor } : undefined, partStyle(block, "icon"));
        const textStyle = mergeStyles(textColor ? { color: textColor } : undefined, partStyle(block, "text"));
        const text = blockText(block, "text", editorMode ? "Nova vyhoda" : "");

        if (!editorMode && text === "") {
            return null;
        }

        return (
            <div {...atomicBlockInteractionAttributes(block)} className="grid gap-3" style={atomicBlockStyle(block)}>
                <div {...editablePart("icon")} data-shop-icon-part={partPrefix ? `${partPrefix}:icon` : "icon"} className="grid h-10 w-10 place-items-center rounded-full bg-[var(--site-primary)] text-[var(--site-on-primary)]" style={iconStyle}>{atomicIconGlyph(blockText(block, "icon", "check"))}</div>
                <p {...editableTextPart("text")} className="text-sm font-black text-[var(--site-text)]" style={textStyle}>{richTextContent(block, "text", text)}</p>
            </div>
        );
    }

    if (block.type === "serviceBenefitItem") {
        const icon = blockText(block, "icon", "truck");
        const iconName = icon === "store" || icon === "rotate" || icon === "shield" || icon === "truck" ? icon : "truck";
        const title = blockText(block, "title", editorMode ? "Doprava zdarma" : "");
        const text = blockText(block, "text", editorMode ? "Při objednávce nad 699 Kč doručíme balík zdarma." : "");

        if (!editorMode && title === "" && text === "") {
            return null;
        }

        return (
            <div {...atomicBlockInteractionAttributes(block)} className="flex gap-4" style={atomicBlockStyle(block)}>
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-[var(--site-surface)] text-[var(--site-primary)]">
                    <Icon name={iconName} className="h-5 w-5" />
                </span>
                <div>
                    {title ? <h3 {...editableTextPart("title")} className="text-sm font-bold" style={partStyle(block, "title")}>{richTextContent(block, "title", title)}</h3> : null}
                    {text ? <p {...editableTextPart("text")} className="mt-1 text-xs leading-5 text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]" style={partStyle(block, "text")}>{richTextContent(block, "text", text)}</p> : null}
                </div>
            </div>
        );
    }

    if (block.type === "blockquote") {
        const quoteText = inlineTextDisplayValue(block, "text", "\"Write a quote here from one of your customers. Quotes are a great way to build confidence in your products or services.\"", editorMode);
        const quoteEmpty = quoteText.value.trim() === "" || quoteText.value === "\u200b";
        const quoteContent = quoteEmpty ? quoteText.value : richTextContent(block, "text", quoteText.value);
        const author = inlineTextDisplayValue(block, "author", "Tým obchodu", editorMode);
        const authorEmpty = author.value.trim() === "" || author.value === "\u200b";
        const authorContent = authorEmpty ? author.value : richTextContent(block, "author", author.value);
        const showAuthor = blockOptionText(block, "showAuthor") !== "false";
        const decoration = blockOptionText(block, "blockquoteDecoration") || "none";
        const showIcon = decoration === "icon" && (editorMode || slotChildren(block, "icon").length > 0);
        const hasImageRow = editorMode
            || slotChildren(block, "beforeImage").length > 0
            || slotChildren(block, "media").length > 0
            || slotChildren(block, "afterImage").length > 0
            || showAuthor;

        return (
            <blockquote
                {...atomicBlockInteractionAttributes(block)}
                data-shop-blockquote-part={partPrefix ?? ""}
                className="relative grid gap-3 overflow-hidden text-[var(--site-text)]"
                style={blockquoteStyle(block)}
            >
                {decoration === "leftLine" ? <span aria-hidden="true" className="absolute bottom-5 left-0 top-5 w-1.5 rounded-r-full bg-[var(--site-primary)]" /> : null}
                {showIcon ? (
                    <SlotBlockGroup
                        block={block}
                        slotKey="icon"
                        partPrefix={partPrefix}
                        className="-mt-2 flex justify-center"
                        itemClassName="inline-grid"
                    />
                ) : null}
                <SlotBlockGroup block={block} slotKey="beforeText" partPrefix={partPrefix} className="grid gap-3" />
                <p
                    {...editableTextPart("text")}
                    {...inlineTextPlaceholderAttributes(quoteEmpty, isEditorCommandLineBlock(block))}
                    className="whitespace-pre-line text-base font-medium leading-6 text-[var(--site-text)]"
                    style={partStyle(block, "text")}
                >
                    {quoteContent}
                </p>
                <SlotBlockGroup block={block} slotKey="afterText" partPrefix={partPrefix} className="grid gap-3" />
                {hasImageRow ? (
                    <footer className={classNames("flex flex-wrap items-center gap-2", blockquoteAuthorAlignmentClass(block))}>
                        <SlotBlockGroup block={block} slotKey="beforeImage" partPrefix={partPrefix} className="grid min-w-0 gap-3" />
                        <SlotBlockGroup
                            block={block}
                            slotKey="media"
                            partPrefix={partPrefix}
                            className="grid min-h-0 gap-3"
                            itemClassName="h-10 w-10 shrink-0 overflow-hidden rounded-full [&_[data-shop-block-part='image']]:h-full [&_[data-shop-block-part='image']]:min-h-0 [&_img]:h-full [&_img]:min-h-0"
                        />
                        {showAuthor ? (
                            <div className="min-w-0">
                                <p {...editableTextPart("author")} {...inlineTextPlaceholderAttributes(authorEmpty, false)} className="text-sm font-black leading-5 text-[var(--site-text)]" style={partStyle(block, "author")}>
                                    {authorContent}
                                </p>
                            </div>
                        ) : null}
                        <SlotBlockGroup block={block} slotKey="afterImage" partPrefix={partPrefix} className="grid min-w-0 gap-3" />
                    </footer>
                ) : null}
            </blockquote>
        );
    }

    if (block.type === "card" && getShopBlockPresetPart(block) === "pricingPlan") {
        const sourceIndex = Number(blockOptionText(block, "pricingSourceIndex"));
        const index = Number.isFinite(sourceIndex) ? Math.max(0, Math.round(sourceIndex)) : 0;

        return (
            <PricingComparisonCard
                card={slotCard(block, index + 1)}
                childBlock={block}
                index={index}
                itemPart={partPrefix}
            />
        );
    }

    if (block.type === "card" && materializedRenderer(block) === "detachedCard") {
        return <DetachedMaterializedCard block={block} partPrefix={partPrefix} />;
    }

    if (block.type === "card") {
        const cardContent = blockRecord(block, "content");
        const imagePresentation = siteBuilderMediaPresentation({
            image: cardContent.image,
            imageUrl: cardContent.imageUrl,
            src: cardContent.src,
        });
        const imageUrl = imagePresentation.kind === "image" ? imagePresentation.source : "";
        const contentItems = slotChildren(block, "content");
        const hasContentSlot = contentItems.length > 0 || contentReplacesFallback;
        const actionLabelPart = blockText(block, "actionLabel", "")
            ? "actionLabel"
            : blockText(block, "buttonLabel", "")
                ? "buttonLabel"
                : "label";
        const actionLabel = blockText(block, actionLabelPart, "");
        const actionHref = authoredStorefrontHref(blockText(block, "href", blockText(block, "url", "")), storefrontHref, pages, languageCode);
        const openInNewWindow = blockOptionText(block, "openInNewWindow") === "true";

        return (
            <div {...atomicBlockInteractionAttributes(block)} className={classNames("bg-[var(--site-surface)]", imageContentLayoutClass(block, "top"))} style={atomicBlockStyle(block)}>
                {imageUrl ? (
                    <img
                        {...editablePart("image")}
                        src={imageUrl}
                        alt={blockText(block, "alt", blockText(block, "title", message("block.card.title")))}
                        title={blockText(block, "imageTitle", "") || undefined}
                        className={classNames("h-36 w-full object-cover", imageContentImageOrderClass(block, "top"))}
                        style={imagePartStyle(block, "image")}
                    />
                ) : null}
                <div className={imageContentTextOrderClass(block, "top")}>
                    {!hasContentSlot ? (
                        <>
                            <p {...editableTextPart("title")} className="text-lg font-black text-[var(--site-text)]" style={partStyle(block, "title")}>{richTextContent(block, "title", "Obsahová karta")}</p>
                            <p {...editableTextPart("text")} className="mt-2 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]" style={partStyle(block, "text")}>{richTextContent(block, "text", "Krátký popis obsahu karty.")}</p>
                            {actionLabel ? (
                                <a
                                    {...editableTextContentPart(actionLabelPart, "primaryButton")}
                                    className="mt-4 inline-flex items-center rounded-full bg-[var(--site-primary)] px-5 py-2.5 text-sm font-semibold text-[var(--site-on-primary)]"
                                    href={actionHref}
                                    onClick={(event) => editorMode && event.preventDefault()}
                                    rel={openInNewWindow ? "noopener noreferrer" : undefined}
                                    style={partStyle(block, "primaryButton")}
                                    target={openInNewWindow ? "_blank" : undefined}
                                >
                                    {richTextContent(block, actionLabelPart, actionLabel)}
                                </a>
                            ) : null}
                        </>
                    ) : null}
                    <SlotBlockGroup
                        block={block}
                        slotKey="content"
                        partPrefix={partPrefix}
                        className="mt-4 grid gap-3"
                    />
                </div>
            </div>
        );
    }

    if (block.type === "chart") {
        const series = chartSeriesForBlock(block, message);
        const rows = chartRowsForBlock(block, series.length, message);
        const type = chartType(block);
        const legendPosition = chartLegendPosition(block);
        const pieLegendItems = rows.map((row, index) => ({
            borderColor: chartPalette[index % chartPalette.length] ?? chartDefaultSeries[0].borderColor,
            color: chartPalette[index % chartPalette.length] ?? chartDefaultSeries[0].color,
            label: row.label,
        }));
        const legendItems = type === "pie" || type === "doughnut" ? pieLegendItems : series;
        const sideLegend = legendPosition === "left" || legendPosition === "right";
        const topLegend = legendPosition === "top";
        const bottomLegend = legendPosition === "bottom";
        const chartGraphic = <ChartGraphic block={block} rows={rows} series={series} type={type} />;

        return (
            <div {...atomicBlockInteractionAttributes(block)} className="flex h-full min-h-[430px] w-full flex-col px-1 py-1 text-[var(--site-text)]" style={atomicBlockStyle(block)}>
                <h3 {...editableTextPart("title")} className="max-w-full whitespace-pre-line text-5xl font-black leading-none text-[var(--site-text)] sm:text-6xl" style={partStyle(block, "title")}>
                    {richTextContent(block, "title", message("block.chart.title"))}
                </h3>
                <SlotBlockGroup
                    block={block}
                    slotKey="afterTitle"
                    partPrefix={partPrefix}
                    className="mt-3 grid w-full gap-3"
                />
                <div className={classNames("mt-5 flex min-h-0 flex-1 gap-5", sideLegend ? "items-center" : "flex-col")}>
                    {legendPosition === "left" ? <ChartLegend items={legendItems} position={legendPosition} /> : null}
                    <div className="flex min-h-0 flex-1 flex-col">
                        {topLegend ? <ChartLegend items={legendItems} position={legendPosition} /> : null}
                        <div className="min-h-0 flex-1">
                            {chartGraphic}
                        </div>
                        {bottomLegend ? <ChartLegend items={legendItems} position={legendPosition} /> : null}
                    </div>
                    {legendPosition === "right" ? <ChartLegend items={legendItems} position={legendPosition} /> : null}
                </div>
            </div>
        );
    }

    if (block.type === "countdown") {
        return <LagunaCountdownBlock block={block} />;
    }

    if (block.type === "facebook" || block.type === "instagram") {
        return (
            <a {...editablePart("primaryButton")} href={authoredStorefrontHref(blockText(block, "href", ""), storefrontHref, pages, languageCode)} className="inline-flex items-center gap-2 rounded-full border border-[var(--site-border)] px-5 py-3 text-sm font-black text-[var(--site-text)]" style={mergeStyles(atomicButtonBlockStyle(block), partStyle(block, "primaryButton"))}>
                <span>{block.type === "facebook" ? "f" : "◎"}</span>
                <span {...editableTextPart("primaryButton")}>{richTextContent(block, "label", block.type === "facebook" ? "Facebook" : "Instagram")}</span>
            </a>
        );
    }

    if (block.type === "map") {
        const mapTitle = blockText(block, "title", "");
        const address = blockText(block, "address", "");
        const mapSource = shopMapEmbedSource({
            address,
            latitude: blockText(block, "latitude", ""),
            longitude: blockText(block, "longitude", ""),
            mapUrl: blockText(block, "mapUrl", ""),
        });
        const mapTextPart = mapTitle ? "title" : "address";

        if (mapSource) {
            return (
                <div
                    {...editablePart("map")}
                    {...atomicBlockInteractionAttributes(block)}
                    className="relative min-h-48 w-full overflow-hidden rounded-[var(--site-radius)] border border-[var(--site-border)] bg-[var(--site-surface)] shadow-[0_18px_50px_var(--site-shadow-color)]"
                    style={mergeStyles(atomicBlockStyle(block), partStyle(block, "map"), { aspectRatio: shopPortableAspectRatio(blockOptionText(block, "mapAspectRatio")) })}
                >
                    <iframe
                        className="absolute inset-0 h-full w-full border-0"
                        loading="lazy"
                        referrerPolicy="strict-origin-when-cross-origin"
                        sandbox="allow-scripts allow-same-origin allow-popups"
                        src={mapSource}
                        style={{ pointerEvents: editorMode ? "none" : undefined }}
                        title={mapTitle || message("ui.block.map")}
                    />
                    {mapTitle || address ? (
                        <span
                            {...editableTextPart(mapTextPart)}
                            className="absolute bottom-4 left-4 flex max-w-[calc(100%-2rem)] items-center gap-2 truncate rounded-full border border-[color-mix(in_srgb,var(--site-on-inverse)_15%,transparent)] bg-[color-mix(in_srgb,var(--site-inverse)_88%,transparent)] px-4 py-2.5 text-xs font-black text-[var(--site-on-inverse)] shadow-lg backdrop-blur"
                            style={partStyle(block, mapTextPart)}
                        >
                            <Icon name="mapPin" className="h-4 w-4 shrink-0" />
                            <span className="truncate">{mapTitle || address}</span>
                        </span>
                    ) : null}
                </div>
            );
        }

        return (
            <div {...editablePart("map")} {...atomicBlockInteractionAttributes(block)} className="grid min-h-40 place-items-center rounded-[var(--site-radius)] border border-dashed border-[var(--site-border-strong)] bg-[linear-gradient(135deg,var(--site-primary-soft),var(--site-surface))] p-6 text-center text-sm font-black text-[var(--site-muted)]" style={mergeStyles(atomicBlockStyle(block), partStyle(block, "map"))}>
                <span className="grid gap-3 place-items-center" {...editableTextPart(mapTextPart)} style={partStyle(block, mapTextPart)}>
                    <span className="grid h-11 w-11 place-items-center rounded-full bg-[var(--site-surface)] text-[var(--site-primary)] shadow-sm"><Icon name="mapPin" className="h-5 w-5" /></span>
                    {mapTitle || address || message("ui.block.map")}
                </span>
            </div>
        );
    }

    if (block.type === "progressBar") {
        const progress = Math.max(0, Math.min(100, numericContent(block, "progressValue", 72)));
        const progressLabelPosition = shopPortableProgressLabelPosition(
            blockOptionText(block, "progressLabelPosition"),
        );
        const fillColor = blockOptionText(block, "progressFillColor") || "var(--site-primary)";
        const trackColor = blockOptionText(block, "progressTrackColor") || "var(--site-border)";
        const labelColor = blockOptionText(block, "progressLabelColor") || "var(--site-text)";
        const striped = blockOptionText(block, "progressStriped") === "true";
        const label = blockText(block, "title", message("block.progressBar.title"));
        const valueLabel = `${Math.round(progress)}%`;
        const labelRow = progressLabelPosition === "hide" ? null : (
            <span className="flex min-w-0 items-center justify-between gap-3 text-[11px] font-semibold" style={{ color: labelColor }}>
                <span className="min-w-0 truncate">{label}</span>
                <span className="shrink-0">{valueLabel}</span>
            </span>
        );
        const progressTrack = (
            <div className="relative h-2 min-w-0 flex-1 overflow-hidden rounded-full border border-[var(--site-border)] shadow-inner" style={{ backgroundColor: trackColor }}>
                <div
                    className="absolute inset-y-0 left-0 rounded-full"
                    style={{
                        backgroundColor: fillColor,
                        backgroundImage: striped ? "repeating-linear-gradient(45deg, color-mix(in srgb, var(--site-on-primary) 28%, transparent) 0 8px, transparent 8px 16px)" : undefined,
                        width: `${progress}%`,
                    }}
                />
                {progressLabelPosition === "inside" ? (
                    <span className="absolute inset-0 flex items-center justify-between gap-1.5 px-1.5 text-[8px] font-semibold leading-none" style={{ color: labelColor }}>
                        <span className="min-w-0 truncate">{label}</span>
                        <span className="shrink-0">{valueLabel}</span>
                    </span>
                ) : null}
            </div>
        );

        return (
            <div {...atomicBlockInteractionAttributes(block)} className="flex h-full min-h-6 w-full flex-col justify-center gap-1" style={atomicBlockStyle(block)}>
                {progressLabelPosition === "after" ? (
                    <div className="flex min-w-0 items-center gap-3">
                        {progressTrack}
                        <span className="shrink-0 text-[11px] font-semibold" style={{ color: labelColor }}>{label} {valueLabel}</span>
                    </div>
                ) : (
                    <>
                        {progressLabelPosition === "above" ? labelRow : null}
                        {progressTrack}
                        {progressLabelPosition === "below" ? labelRow : null}
                    </>
                )}
            </div>
        );
    }

    if (block.type === "rating") {
        const maxRating = normalizedRatingMax(block);
        const rating = Math.max(0, Math.min(maxRating, numericContent(block, "ratingValue", 4.8)));
        const filledIcons = Math.round(rating);
        const titlePosition = blockOptionText(block, "ratingTitlePosition") || "left";
        const scoreLabel = `${formattedRatingNumber(rating)} / ${formattedRatingNumber(maxRating)}`;
        const scoreElement = titlePosition === "none" ? null : (
            <span className="text-sm font-black leading-none text-current">{scoreLabel}</span>
        );
        const iconElements = (
            <div
                className={classNames("flex items-center gap-1 leading-none", ratingSizeClass(block))}
                style={{ color: blockOptionText(block, "ratingIconColor") || "var(--site-primary)" }}
            >
                {Array.from({ length: maxRating }, (_, index) => {
                    const active = index < filledIcons;

                    return (
                        <span key={index} className={active ? "" : "opacity-35"}>
                            {ratingGlyph(block, active)}
                        </span>
                    );
                })}
            </div>
        );

        return (
            <div {...atomicBlockInteractionAttributes(block)} className={titlePosition === "top" ? "grid gap-2" : "flex items-center gap-3"} style={ratingBlockStyle(block)}>
                {titlePosition === "top" ? (
                    <>
                        {scoreElement}
                        {iconElements}
                    </>
                ) : (
                    <>
                        {scoreElement}
                        {iconElements}
                    </>
                )}
            </div>
        );
    }

    if (block.type === "share") {
        return <ShareIcons block={block} partPrefix={partPrefix} />;
    }

    if (block.type === "socialLink") {
        const openInNewWindow = blockOptionText(block, "openInNewWindow") === "true";

        return (
            <a {...editablePart("primaryButton")} href={authoredStorefrontHref(blockText(block, "href", ""), storefrontHref, pages, languageCode)} className="rounded-full border border-[var(--site-border)] px-5 py-3 text-sm font-black text-[var(--site-text)]" rel={openInNewWindow ? "noopener noreferrer" : undefined} style={mergeStyles(atomicButtonBlockStyle(block), partStyle(block, "primaryButton"))} target={openInNewWindow ? "_blank" : undefined}>
                <span {...editableTextPart("primaryButton")}>{richTextContent(block, "label", "Social")}</span>
            </a>
        );
    }

    if (block.type === "supportedPaymentButton") {
        const payments = multilineItems(blockText(block, "items", "Visa\nMastercard\nApple Pay"), ["Visa", "Mastercard", "Apple Pay"]);
        const layout = blockOptionText(block, "paymentLayout") || "cards";

        return (
            <div {...atomicBlockInteractionAttributes(block)} className="grid gap-3" style={atomicBlockStyle(block)}>
                {blockText(block, "title", "") ? (
                    <strong {...editableTextPart("title")} className="text-xs font-black uppercase tracking-[0.12em] text-[var(--site-muted)]" style={partStyle(block, "title")}>{richTextContent(block, "title", "")}</strong>
                ) : null}
                <div {...editablePart("items")} className={classNames("flex flex-wrap items-center", layout === "compact" ? "gap-4" : "gap-2")} style={partStyle(block, "items")}>
                    {payments.map((payment) => {
                        const brand = shopPaymentBrand(payment);

                        return (
                            <span key={payment} className={classNames(
                                "inline-flex min-h-10 items-center gap-2 text-xs font-black text-[var(--site-on-surface)]",
                                layout === "compact" ? "px-1" : "rounded-xl border border-[var(--site-border)] bg-[var(--site-surface)] px-3 py-2 shadow-sm",
                            )}>
                                <span aria-hidden="true" className={classNames(
                                    "grid min-w-5 place-items-center text-[11px] font-black",
                                    brand === "mastercard" ? "tracking-[-0.18em] text-[var(--site-accent)]" : "text-[var(--site-primary)]",
                                )}>{shopPaymentBrandGlyph(brand)}</span>
                                {payment}
                            </span>
                        );
                    })}
                </div>
            </div>
        );
    }

    if (block.type === "footerService") {
        const phone = blockText(block, "phone", editorMode ? "530 514 600" : "");
        const email = blockText(block, "email", editorMode ? "info@shoepoint.cz" : "");
        const hours = blockText(block, "hours", editorMode ? "PO-PA od 8:00 do 16:30" : "");
        const serviceHref = blockText(block, "href", storesHref);
        const normalizedServiceHref = authoredStorefrontHref(
            serviceHref.trim() !== "" && serviceHref.trim() !== "#" ? serviceHref : storesHref,
            storefrontHref,
            pages,
            languageCode,
        ) ?? storefrontHref(storesHref);
        const contentBlocks = slotChildren(block, "content");
        const actionBlocks = slotChildren(block, "actions");
        const hasServiceSlots = Array.isArray(block.slots?.content) || Array.isArray(block.slots?.actions);

        if (hasServiceSlots) {
            const renderServiceItem: SlotRenderItem = ({
                childBlock,
                itemPart,
            }) => {
                const href = blockText(childBlock, "href", "");
                const textStyle = mergeStyles(
                    atomicBlockVisualStyle(childBlock),
                    partStyle(childBlock, "text"),
                );

                if (childBlock.type === "heading") {
                    return (
                        <h3 {...editableTextPart("title")} {...atomicBlockInteractionAttributes(childBlock)} className="text-sm font-bold" style={mergeStyles(atomicBlockVisualStyle(childBlock), partStyle(childBlock, "title"))}>
                            {richTextContent(childBlock, "text", "Zákaznický servis")}
                        </h3>
                    );
                }

                if (childBlock.type === "text" && href.startsWith("tel:")) {
                    return (
                        <a {...editableTextPart("text")} {...atomicBlockInteractionAttributes(childBlock)} href={href} className="block text-xl font-bold" style={textStyle}>
                            {richTextContent(childBlock, "text", phone)}
                        </a>
                    );
                }

                if (childBlock.type === "text" && href.startsWith("mailto:")) {
                    return (
                        <a {...editableTextPart("text")} {...atomicBlockInteractionAttributes(childBlock)} href={href} className="block text-sm underline" style={textStyle}>
                            {richTextContent(childBlock, "text", email)}
                        </a>
                    );
                }

                if (childBlock.type === "text") {
                    return (
                        <p {...editableTextPart("text")} {...atomicBlockInteractionAttributes(childBlock)} className="text-xs text-[color-mix(in_srgb,var(--site-on-inverse)_50%,transparent)]" style={textStyle}>
                            {richTextContent(childBlock, "text", "PO-PA od 8:00 do 16:30")}
                        </p>
                    );
                }

                if (childBlock.type === "button") {
                    const childHref = blockText(childBlock, "href", normalizedServiceHref);
                    const normalizedChildHref = authoredStorefrontHref(
                        childHref.trim() !== "" && childHref.trim() !== "#" ? childHref : normalizedServiceHref,
                        storefrontHref,
                        pages,
                        languageCode,
                    ) ?? normalizedServiceHref;

                    return (
                        <a {...editablePart("primaryButton")} href={normalizedChildHref} className="inline-flex items-center gap-2 border border-[color-mix(in_srgb,var(--site-on-inverse)_25%,transparent)] px-4 py-3 text-sm font-bold" style={mergeStyles(atomicButtonBlockStyle(childBlock), partStyle(childBlock, "primaryButton"))}>
                            <Icon name="mapPin" className="h-4 w-4" /> <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "Najít prodejnu")}</span>
                        </a>
                    );
                }

                return (
                    <AtomicBlock
                        block={childBlock}
                        partPrefix={itemPart}
                        slotParentType={block.type}
                    />
                );
            };
            const serviceItemClassName: SlotItemClassName = ({
                childBlock,
                index,
            }) => {
                const href = blockText(childBlock, "href", "");

                if (
                    childBlock.type === "text"
                    && (href.startsWith("tel:") || href.startsWith("mailto:"))
                ) {
                    return "mt-2";
                }

                return childBlock.type === "text" && index > 0
                    ? "mt-5"
                    : undefined;
            };

            return (
                <div {...atomicBlockInteractionAttributes(block)} className="text-[var(--site-on-inverse)]" style={atomicBlockStyle(block)}>
                    <SlotBlockGroup
                        block={block}
                        slotKey="content"
                        partPrefix={partPrefix}
                        className="grid"
                        itemClassName={serviceItemClassName}
                        renderItem={renderServiceItem}
                    />
                    <SlotBlockGroup
                        block={block}
                        slotKey="actions"
                        partPrefix={partPrefix}
                        className={classNames(
                            "grid gap-5",
                            contentBlocks.length > 0 && actionBlocks.length > 0
                                ? "mt-6"
                                : "",
                        )}
                        renderItem={renderServiceItem}
                    />
                </div>
            );
        }

        return (
            <div {...atomicBlockInteractionAttributes(block)} className="text-[var(--site-on-inverse)]" style={atomicBlockStyle(block)}>
                <h3 {...editableTextPart("title")} className="text-sm font-bold" style={partStyle(block, "title")}>
                    {richTextContent(block, "title", "Zákaznický servis")}
                </h3>
                {hours ? (
                    <p {...editableTextPart("hours")} className="mt-5 text-xs text-[color-mix(in_srgb,var(--site-on-inverse)_50%,transparent)]" style={partStyle(block, "hours")}>
                        {richTextContent(block, "hours", hours)}
                    </p>
                ) : null}
                {phone ? (
                    <a {...editableTextPart("phone")} href={`tel:${phone.replace(/\s+/g, "")}`} className="mt-2 block text-xl font-bold" style={partStyle(block, "phone")}>
                        {richTextContent(block, "phone", phone)}
                    </a>
                ) : null}
                {email ? (
                    <a {...editableTextPart("email")} href={`mailto:${email}`} className="mt-2 block text-sm underline" style={partStyle(block, "email")}>
                        {richTextContent(block, "email", email)}
                    </a>
                ) : null}
                <a {...editablePart("primaryButton")} href={normalizedServiceHref} className="mt-6 inline-flex items-center gap-2 border border-[color-mix(in_srgb,var(--site-on-inverse)_25%,transparent)] px-4 py-3 text-sm font-bold" style={partStyle(block, "primaryButton")}>
                    <Icon name="mapPin" className="h-4 w-4" /> <span {...editableTextPart("primaryButton")}>{richTextContent(block, "buttonLabel", "Najít prodejnu")}</span>
                </a>
            </div>
        );
    }

    if (block.type === "footerLinks") {
        const items = multilineItems(blockText(block, "items", "Nápověda a kontakt\nMůj účet\nStav objednávky"), ["Nápověda a kontakt", "Můj účet", "Stav objednávky"]);
        const itemBlocks = slotChildren(block, "items");

        return (
            <div {...atomicBlockInteractionAttributes(block)} className="text-[var(--site-on-inverse)]" style={atomicBlockStyle(block)}>
                <h3 {...editableTextPart("title")} className="text-sm font-bold" style={partStyle(block, "title")}>
                    {richTextContent(block, "title", "Nápověda")}
                </h3>
                {itemBlocks.length > 0 ? (
                    <SlotBlockGroup
                        block={block}
                        slotKey="items"
                        partPrefix={partPrefix}
                        className="mt-5 space-y-3 text-sm text-[color-mix(in_srgb,var(--site-on-inverse)_60%,transparent)]"
                    />
                ) : editorMode ? (
                    <div {...editablePart("items")} className="mt-5 space-y-3 text-sm text-[color-mix(in_srgb,var(--site-on-inverse)_60%,transparent)]" style={partStyle(block, "items")}>
                        {items.map((item) => (
                            <a key={item} className="block hover:text-[var(--site-on-inverse)]">{item}</a>
                        ))}
                    </div>
                ) : livePageLinks.length > 0 ? (
                    <nav className="mt-5 space-y-3 text-sm text-[color-mix(in_srgb,var(--site-on-inverse)_60%,transparent)]">
                        {livePageLinks.map((pageLink) => (
                            <a key={pageLink.href} href={pageLink.href} className="block hover:text-[var(--site-on-inverse)]">{pageLink.title}</a>
                        ))}
                    </nav>
                ) : null}
            </div>
        );
    }

    if (block.type === "footerSocial") {
        return (
            <div {...atomicBlockInteractionAttributes(block)} className="text-[var(--site-on-inverse)]" style={atomicBlockStyle(block)}>
                <h3 {...editableTextPart("title")} className="text-sm font-bold" style={partStyle(block, "title")}>
                    {richTextContent(block, "title", "Sledujte nás")}
                </h3>
                <SocialMediaIcons block={block} className="mt-5" partPrefix={partPrefix} />
                <SlotBlockGroup
                    block={block}
                    slotKey="apps"
                    partPrefix={partPrefix}
                    className="mt-8 grid gap-4"
                />
            </div>
        );
    }

    if (block.type === "footerApps") {
        const items = multilineItems(blockText(block, "items", "App Store\nGoogle Play"), ["App Store", "Google Play"]);
        const itemBlocks = slotChildren(block, "items");

        if (!editorMode && itemBlocks.length === 0) {
            return null;
        }

        return (
            <div {...atomicBlockInteractionAttributes(block)} className="text-[var(--site-on-inverse)]" style={atomicBlockStyle(block)}>
                <h3 {...editableTextPart("title")} className="text-sm font-bold" style={partStyle(block, "title")}>
                    {richTextContent(block, "title", "Stáhněte si aplikaci")}
                </h3>
                {itemBlocks.length > 0 ? (
                    <SlotBlockGroup
                        block={block}
                        slotKey="items"
                        partPrefix={partPrefix}
                        className="mt-4 flex flex-wrap gap-2"
                    />
                ) : (
                    <div {...editablePart("items")} className="mt-4 flex flex-wrap gap-2" style={partStyle(block, "items")}>
                        {items.map((item) => (
                            <a key={item} className="rounded border border-[color-mix(in_srgb,var(--site-on-inverse)_25%,transparent)] px-3 py-2 text-xs font-bold">
                                {item}
                            </a>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    if (block.type === "footerCopyright") {
        const items = multilineItems(blockText(block, "items", "Obchodní podmínky\nOchrana osobních údajů\nCookies\nPřístupnost"), ["Obchodní podmínky", "Ochrana osobních údajů", "Cookies", "Přístupnost"]);
        const itemBlocks = slotChildren(block, "items");

        return (
            <div {...atomicBlockInteractionAttributes(block)} className="flex h-full flex-col items-center justify-between gap-5 text-center text-[11px] text-[color-mix(in_srgb,var(--site-on-inverse)_40%,transparent)] sm:flex-row sm:text-left" style={atomicBlockStyle(block)}>
                {itemBlocks.length > 0 ? (
                    <SlotBlockGroup
                        block={block}
                        slotKey="items"
                        partPrefix={partPrefix}
                        className="flex flex-wrap justify-center gap-4 sm:justify-start"
                    />
                ) : editorMode ? (
                    <div {...editablePart("items")} className="flex flex-wrap justify-center gap-4 sm:justify-start" style={partStyle(block, "items")}>
                        {items.map((item) => (
                            <a key={item}>{item}</a>
                        ))}
                    </div>
                ) : livePageLinks.length > 0 ? (
                    <nav className="flex flex-wrap justify-center gap-4 sm:justify-start">
                        {livePageLinks.slice(0, 4).map((pageLink) => (
                            <a key={pageLink.href} href={pageLink.href}>{pageLink.title}</a>
                        ))}
                    </nav>
                ) : null}
                <p {...editableTextPart("text")} style={partStyle(block, "text")}>
                    {richTextContent(block, "text", `© ${new Date().getFullYear()} ${siteName}`)}
                </p>
            </div>
        );
    }

    if (block.type === "textHighlight") {
        const content = blockRecord(block, "content");
        const beforeTitleBlocks = slotChildren(block, "beforeTitle");
        const afterTitleBlocks = slotChildren(block, "afterTitle");
        const contentBlocks = slotChildren(block, "content");
        const titleDefined = Object.prototype.hasOwnProperty.call(content, "title");
        const titleText = blockText(block, "title", "");
        const textDisplay = inlineTextDisplayValue(block, "text", "Silné sdělení nebo krátký claim, který chcete zvýraznit.", editorMode);
        const textEmpty = textDisplay.empty || textDisplay.value === "\u200b";
        const hasBeforeTitleSlot = beforeTitleBlocks.length > 0 || slotIsDefined(block, "beforeTitle");
        const hasAfterTitleSlot = afterTitleBlocks.length > 0 || slotIsDefined(block, "afterTitle");
        const hasContentSlot = contentBlocks.length > 0 || slotIsDefined(block, "content");
        const framedHighlight = titleDefined || titleText || hasBeforeTitleSlot || hasAfterTitleSlot || hasContentSlot || blockOptionText(block, "backgroundColor") || blockOptionText(block, "textHighlightWidth") || blockOptionExists(block, "textHighlightWidth");
        const highlightPartAttributes = { "data-shop-text-highlight-part": partPrefix ?? "" };

        if (!framedHighlight) {
            return (
                <p
                    {...highlightPartAttributes}
                    {...editableTextPart("text")}
                    {...inlineTextPlaceholderAttributes(textEmpty, isEditorCommandLineBlock(block))}
                    {...atomicBlockInteractionAttributes(block)}
                    className="whitespace-pre-line text-xl font-black leading-8 text-[var(--site-text)] sm:text-2xl"
                    style={mergeStyles(atomicBlockStyle(block), partStyle(block, "text"))}
                >
                    {textEmpty ? textDisplay.value : richTextContent(block, "text", textDisplay.value)}
                </p>
            );
        }

        const alignment = blockOptionText(block, "alignment") || "center";
        const titleDisplay = inlineTextDisplayValue(block, "title", "", editorMode);
        const titleEmpty = titleDisplay.empty || titleDisplay.value === "\u200b";
        const contentAlignmentClass = alignment === "left"
            ? "items-start text-left"
            : alignment === "right"
                ? "items-end text-right"
                : "items-center text-center";
        const slotAlignmentClass = alignment === "center" ? "justify-items-center" : alignment === "right" ? "justify-items-end" : "justify-items-start";

        return (
            <div
                {...highlightPartAttributes}
                {...atomicBlockInteractionAttributes(block)}
                className={classNames("flex h-full w-full flex-col justify-center px-5 py-6", contentAlignmentClass)}
                style={atomicBlockStyle(block)}
            >
                {hasBeforeTitleSlot ? (
                    <SlotBlockGroup
                        block={block}
                        slotKey="beforeTitle"
                        partPrefix={partPrefix}
                        className={classNames("mb-4 grid w-full gap-3", slotAlignmentClass)}
                    />
                ) : null}
                {titleDefined || titleText ? (
                    <h3
                        {...editableTextPart("title")}
                        {...inlineTextPlaceholderAttributes(titleEmpty, false)}
                        className="max-w-full whitespace-pre-line text-3xl font-black leading-none text-[var(--site-text)] sm:text-4xl"
                        style={partStyle(block, "title")}
                    >
                        {titleEmpty ? titleDisplay.value : richTextContent(block, "title", titleDisplay.value)}
                    </h3>
                ) : null}
                {hasAfterTitleSlot ? (
                    <SlotBlockGroup
                        block={block}
                        slotKey="afterTitle"
                        partPrefix={partPrefix}
                        className={classNames("mt-4 grid w-full gap-3", slotAlignmentClass)}
                    />
                ) : null}
                <p
                    {...editableTextPart("text")}
                    {...inlineTextPlaceholderAttributes(textEmpty, false)}
                    className={classNames("max-w-full whitespace-pre-line text-base leading-7 text-[var(--site-text)]", titleDefined || titleText || hasAfterTitleSlot ? "mt-3" : "")}
                    style={partStyle(block, "text")}
                >
                    {textEmpty ? textDisplay.value : richTextContent(block, "text", textDisplay.value)}
                </p>
                {contentBlocks.length > 0 || editorMode ? (
                    <SlotBlockGroup
                        block={block}
                        slotKey="content"
                        partPrefix={partPrefix}
                        className={classNames("mt-4 grid w-full gap-3", slotAlignmentClass)}
                    />
                ) : null}
            </div>
        );
    }

    if (block.type === "avatars") {
        const people = multilineItems(blockText(block, "items", "Anna\nTomáš\nMia\nDavid"), ["Anna", "Tomáš", "Mia", "David"]);
        const layout = blockOptionText(block, "avatarLayout") || "stack";
        const showNames = blockOptionText(block, "showAvatarNames") === "true" || layout === "pills";

        return (
            <div {...atomicBlockInteractionAttributes(block)} className="grid gap-3" style={atomicBlockStyle(block)}>
                {blockText(block, "title", "") ? (
                    <strong {...editableTextPart("title")} className="text-xs font-black uppercase tracking-[0.12em] text-[var(--site-muted)]" style={partStyle(block, "title")}>{richTextContent(block, "title", "")}</strong>
                ) : null}
                <div {...editablePart("items")} className={classNames("flex flex-wrap items-center", layout === "stack" ? "gap-0" : "gap-2")} style={partStyle(block, "items")}>
                    {people.map((person, index) => (
                        <div
                            key={`${person}-${index}`}
                            className={classNames(
                                "flex items-center rounded-full",
                                layout === "stack" ? index === 0 ? "" : "-ml-3" : "gap-2 border border-[var(--site-border)] bg-[var(--site-surface)] p-1 pr-3 shadow-sm",
                            )}
                            style={{ zIndex: people.length - index }}
                        >
                            <span className="grid h-10 w-10 place-items-center rounded-full border-[3px] border-[var(--site-surface)] bg-[var(--site-primary-soft)] text-xs font-black text-[var(--site-on-surface)] shadow-sm">{shopAvatarInitials(person)}</span>
                            {showNames ? <span className="text-xs font-black text-[var(--site-text)]">{person}</span> : null}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return <BlockPlaceholder block={block} />;
}

type EditableEntityRenderContextValue = {
    type: EditableEntityType;
    uuid: string;
    values: Partial<Record<ShopDynamicEntityField, string>>;
};

const EditableEntityRenderContext = createContext<EditableEntityRenderContextValue | null>(null);

function dynamicEntityField(block: ShopTemplateBlock | null | undefined): ShopDynamicTextSource {
    const field = blockOptionText(block, SHOP_DYNAMIC_ENTITY_FIELD_OPTION);

    return isShopDynamicEntityField(field) ? field : SHOP_DYNAMIC_TEXT_SOURCE_CUSTOM;
}

function dynamicEntityTextValue(
    field: ShopDynamicTextSource,
    entity: EditableEntityRenderContextValue | null,
    fallback: string,
): string {
    if (field !== SHOP_DYNAMIC_TEXT_SOURCE_CUSTOM && entity) return entity.values[field] ?? "";

    return fallback;
}

function dynamicEntityTextAttributes(
    contentPart: string,
    field: ShopDynamicTextSource,
    entity: EditableEntityRenderContextValue | null,
    visualPart = contentPart,
): ReturnType<typeof editableTextPart> | ReadonlyCatalogTextAttributes {
    return field !== SHOP_DYNAMIC_TEXT_SOURCE_CUSTOM && entity
        ? readonlyCatalogText(entity.type, entity.uuid, field, visualPart)
        : editableTextContentPart(contentPart, visualPart);
}

function PageIntro({
    title,
    description,
    block,
    entity,
    embedded = false,
}: {
    title: string;
    description?: string | null;
    block?: ShopTemplateBlock | null;
    entity?: EditableEntityRenderContextValue | null;
    embedded?: boolean;
}) {
    const entityDescriptionField = entity?.values.descriptionShort ? "descriptionShort" : "description";
    const titleAttributes = entity ? editableEntityField(entity.type, entity.uuid, "name", "title") : editableTextPart("title");
    const textAttributes = entity
        ? editableEntityField(entity.type, entity.uuid, entityDescriptionField, "text")
        : editableTextPart("text");
    const titleValue = entity ? entity.values.name ?? title : blockText(block, "title", title);
    const descriptionValue = entity
        ? entity.values[entityDescriptionField] ?? description ?? ""
        : blockText(block, "text", description ?? "");
    const presentationProfile = blockOptionText(block, "shopPresentationProfile");
    const eyebrowFallback = presentationProfile === "" ? "Laguna" : "";
    const eyebrowValue = blockText(block, "eyebrow", eyebrowFallback);

    const contentItems = slotChildren(block, "content");

    const content = contentItems.length > 0 ? (
        <EditableEntityRenderContext.Provider value={entity ?? null}>
            <SlotBlockGroup block={block} slotKey="content" className="grid gap-3" />
        </EditableEntityRenderContext.Provider>
    ) : (
        <>
            {!embedded && eyebrowValue ? (
                <p {...editableTextPart("eyebrow")} className="text-xs font-bold uppercase tracking-[0.16em] text-[var(--site-primary)]" style={partStyle(block, "eyebrow")}>
                    {richTextContent(block, "eyebrow", eyebrowValue)}
                </p>
            ) : null}
            <h1
                {...titleAttributes}
                className={classNames("text-4xl font-black leading-tight sm:text-5xl", !embedded && "mt-3")}
                style={partStyle(block, "title")}
            >
                {entity ? titleValue : richTextContent(block, "title", titleValue)}
            </h1>
            {descriptionValue ? (
                <p {...textAttributes} className={classNames("mt-4 max-w-3xl text-base", readableOnSurfaceTextClass)} style={readableSemanticTextStyle(partStyle(block, "text"))}>
                    {entity ? descriptionValue : richTextContent(block, "text", descriptionValue)}
                </p>
            ) : null}
        </>
    );

    if (embedded) {
        return (
            <section
                className="mb-8 bg-[var(--site-background)]"
                data-catalog-integrated-intro="true"
                style={blockVisualStyle(block)}
            >
                {content}
            </section>
        );
    }

    return (
        <section className="bg-[var(--site-surface)] px-5 py-10 sm:px-6" style={blockVisualStyle(block)}>
            <div className="mx-auto max-w-[var(--site-container)]">
                {content}
            </div>
        </section>
    );
}

function EntitySeoText({
    title,
    description,
    block,
    entity,
}: {
    title: string;
    description?: string | null;
    block?: ShopTemplateBlock | null;
    entity?: EditableEntityRenderContextValue | null;
}) {
    const contentItems = slotChildren(block, "content");
    const contentReplacesFallback = useSlotReplacesFallback(block, "content");
    const entityDescriptionField = entity?.values.descriptionShort ? "descriptionShort" : "description";

    return (
        <section className="mx-auto max-w-[1100px] px-5 py-14 text-sm leading-7 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)] sm:py-20" style={blockVisualStyle(block)}>
            {contentItems.length > 0 || contentReplacesFallback ? (
                <EditableEntityRenderContext.Provider value={entity ?? null}>
                    <SlotBlockGroup block={block} slotKey="content" className="grid gap-5" />
                </EditableEntityRenderContext.Provider>
            ) : (
                <>
                    <h2
                        {...(entity ? editableEntityField(entity.type, entity.uuid, "name", "title") : editableTextPart("title"))}
                        className="text-2xl font-extrabold text-[var(--site-text)]"
                    >
                        {entity ? title : richTextContent(block, "title", title)}
                    </h2>
                    {description ? (
                        <p
                            {...(entity
                                ? editableEntityField(entity.type, entity.uuid, entityDescriptionField, "text")
                                : editableTextPart("text"))}
                            className="mt-5"
                        >
                            {entity ? description : richTextContent(block, "text", description)}
                        </p>
                    ) : null}
                </>
            )}
        </section>
    );
}

function BlockPlaceholder({ block }: { block: ShopTemplateBlock; index?: number | null }) {
    const message = useShopStorefrontMessage();
    const siteInfo = useContext(ShopSiteInfoContext);
    const contentReplacesFallback = useSlotReplacesFallback(block, "content");
    const editorMode = useContext(ShopEditorModeContext);

    if (block.type === "richContent") {
        return <RichContentPreview block={block} />;
    }

    if (block.type === "introHero") {
        return <IntroHeroPreview block={block} />;
    }

    if (block.type === "columnsShowcase") {
        return <ColumnsShowcasePreview block={block} />;
    }

    if (block.type === "contentShowcase") {
        return <ContentShowcasePreview block={block} />;
    }

    if (block.type === "separator") {
        return (
            <section {...blockInteractionAttributes(block)} className="px-5 py-8 sm:px-6" style={separatorContainerStyle(block)}>
                <div className="mx-auto max-w-[var(--site-container)]">
                    <div className="mx-auto" style={separatorLineStyle(block)} />
                </div>
            </section>
        );
    }

    if (isAtomicBlockType(block.type)) {
        return (
            <section className="px-5 py-10 sm:px-6">
                <div className="mx-auto max-w-[var(--site-container)]" style={blockSpacingStyle(block)}>
                    <AtomicBlock block={block} />
                </div>
            </section>
        );
    }

    if (block.type === "footer") {
        return (
            <Footer
                block={block}
                homeHref={siteInfo.homeHref}
                siteName={siteInfo.siteName}
                variant={siteInfo.footerVariant}
            />
        );
    }

    if (block.type === "serviceBenefits") {
        return <ServiceBenefits block={block} />;
    }

    if (block.type === "text") {
        return (
            <section className="px-5 py-14 sm:px-6">
                <div className="mx-auto max-w-[var(--site-container)]">
                    <p {...editableTextPart("text")} className="max-w-3xl whitespace-pre-line text-base leading-8 text-[color-mix(in_srgb,var(--site-text)_65%,transparent)]" style={partStyle(block, "text")}>{richTextContent(block, "text", "Volny obsah, ktery lze upravit primo v editoru.")}</p>
                </div>
            </section>
        );
    }

    if (block.type === "image") {
        const presentation = siteBuilderMediaPresentation(block.content);
        const imageUrl = presentation.kind === "image" ? presentation.source : "";
        const contentItems = slotChildren(block, "content");
        const hasContentSlot = contentItems.length > 0 || contentReplacesFallback;

        return (
            <section className="px-5 py-14 sm:px-6">
                <div className={classNames("mx-auto max-w-[var(--site-container)]", imageContentLayoutClass(block, "right"))}>
                    <div className={imageContentTextOrderClass(block, "right")}>
                        {hasContentSlot ? (
                            <SlotBlockGroup
                                block={block}
                                slotKey="content"
                                className="mt-6 grid gap-4"
                            />
                        ) : (
                            <>
                                <h2 {...editableTextPart("title")} className="text-3xl font-black text-[var(--site-text)]" style={partStyle(block, "title")}>{richTextContent(block, "title", "Obrazovy blok")}</h2>
                                <p {...editableTextPart("text")} className="mt-4 text-base leading-8 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]" style={partStyle(block, "text")}>{richTextContent(block, "text", "Prostor pro produktovy nebo brandovy obrazek.")}</p>
                            </>
                        )}
                    </div>
                    <div
                        className={classNames("overflow-hidden rounded-[28px] bg-[var(--site-surface)]", imageContentImageOrderClass(block, "right"))}
                        style={imageFrameStyle(block, "image")}
                    >
                        {imageUrl ? (
                            <img
                                {...editablePart("image")}
                                src={imageUrl}
                                alt={blockText(block, "alt", "")}
                                title={blockText(block, "imageTitle", "") || undefined}
                                className="h-[360px] w-full object-cover"
                                style={imagePartStyle(block, "image")}
                            />
                        ) : (
                            <div
                                {...editablePart("image")}
                                className="grid h-[360px] place-items-center bg-gradient-to-br from-[var(--site-surface)] to-[var(--site-primary)]/20 text-sm font-black text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]"
                                style={imagePartStyle(block, "image")}
                            >
                                {message("ui.block.image")}
                            </div>
                        )}
                    </div>
                </div>
            </section>
        );
    }

    if (block.type === "video") {
        const videoUrl = blockText(block, "videoUrl", "");

        return (
            <section className="px-5 py-14 sm:px-6">
                <div className="mx-auto max-w-[var(--site-container)]">
                    <h2 {...editableTextPart("title")} className="text-3xl font-black text-[var(--site-text)]" style={partStyle(block, "title")}>{richTextContent(block, "title", "Video blok")}</h2>
                    <p {...editableTextPart("text")} className="mt-4 max-w-3xl text-base leading-8 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]" style={partStyle(block, "text")}>{richTextContent(block, "text", "Vlozene video pro prezentaci produktu nebo znacky.")}</p>
                    <SlotBlockGroup
                        block={block}
                        slotKey="content"
                        className="mt-6 grid gap-4"
                    />
                    <div className="mt-8 grid aspect-video place-items-center overflow-hidden rounded-[28px] bg-[var(--site-inverse)] text-[var(--site-on-inverse)]">
                        {videoUrl ? <iframe title={blockText(block, "title", message("ui.block.video"))} src={videoUrl} className="h-full w-full border-0" /> : <span className="text-sm font-black">{message("ui.block.video")}</span>}
                    </div>
                </div>
            </section>
        );
    }

    if (block.type === "product") {
        const features = richTextLineContents(block, "text", "Core features\nPriority support\nEasy setup");
        const imageUrl = blockText(block, "imageUrl", "");

        return (
            <section className="px-5 py-8 sm:px-6">
                <div className="mx-auto max-w-[var(--site-container)]">
                    <div className={classNames("overflow-hidden rounded-[28px] border border-[var(--site-border)] bg-[var(--site-surface)] p-6 shadow-sm", imageContentLayoutClass(block, "top"))}>
                        {imageUrl ? (
                            <img
                                {...editablePart("image")}
                                src={imageUrl}
                                alt={blockText(block, "alt", blockText(block, "title", message("block.product.title")))}
                                title={blockText(block, "imageTitle", "") || undefined}
                                className={classNames("h-40 w-full rounded-[22px] object-cover", imageContentImageOrderClass(block, "top"))}
                                style={imagePartStyle(block, "image")}
                            />
                        ) : null}
                        <div className={imageContentTextOrderClass(block, "top")}>
                            <h3 {...editableTextPart("title")} className="text-2xl font-black text-[var(--site-text)]" style={partStyle(block, "title")}>{richTextContent(block, "title", "Produkt")}</h3>
                            <p {...editableTextPart("price")} className="mt-3 text-3xl font-black text-[var(--site-primary)]" style={partStyle(block, "price")}>{richTextContent(block, "price", "1 299 Kč")}</p>
                            <ul {...editableTextPart("text")} className="mt-5 space-y-2 text-sm text-[color-mix(in_srgb,var(--site-text)_65%,transparent)]" style={partStyle(block, "text")}>
                                {features.map((feature) => (
                                    <li key={feature.text} className="flex gap-2">
                                        <Icon name="check" className="h-4 w-4 shrink-0 text-[var(--site-primary)]" />
                                        <span>{feature.content}</span>
                                    </li>
                                ))}
                            </ul>
                            <a {...editableTextPart("button")} className="mt-6 inline-flex rounded-full bg-[var(--site-primary)] px-6 py-3 text-sm font-black text-[var(--site-on-primary)]" style={partStyle(block, "button")}>
                                {richTextContent(block, "buttonLabel", "Do kosiku")}
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (block.type === "iconList") {
        const text = inlineTextDisplayValue(
            block,
            "items",
            editorMode ? "Rychle doruceni\nBezpecna platba\nSnadne vraceni" : "",
            editorMode,
        );
        const items = richTextLineContents(
            block,
            "items",
            text.value || (editorMode ? "\u200b" : ""),
        );
        const icon = blockText(block, "icon", "lucide:check");
        const iconStyle = partStyle(block, "icon");

        if (!editorMode && items.length === 0) {
            return null;
        }

        return (
            <section className="px-5 py-14 sm:px-6">
                <div {...atomicBlockInteractionAttributes(block)} className="mx-auto max-w-[var(--site-container)]" style={atomicBlockStyle(block)}>
                    <h2 {...editableTextPart("title")} className="text-3xl font-black text-[var(--site-text)]" style={partStyle(block, "title")}>{richTextContent(block, "title", "Vyhody")}</h2>
                    <div {...editableTextPart("items")} className="mt-8 grid gap-3 md:grid-cols-3" style={partStyle(block, "items")}>
                        {items.map((item, index) => (
                            <div key={`${item.text}:${index}`} data-shop-icon-list-row="true" className="rounded-[22px] border border-[var(--site-border)] bg-[var(--site-surface)] p-5">
                                <div data-shop-icon-list-icon="true" data-shop-icon-part="icon" contentEditable={false} className="grid h-10 w-10 place-items-center rounded-full bg-[var(--site-primary)] text-[var(--site-on-primary)]" style={iconStyle}>{atomicIconGlyph(icon)}</div>
                                <p data-shop-icon-list-text="true" className="mt-4 text-sm font-black text-[var(--site-text)]">{item.content}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    if (block.type === "socialMedia") {
        return (
            <section className="px-5 py-12 sm:px-6">
                <div className="mx-auto flex max-w-[var(--site-container)] flex-wrap items-center justify-between gap-4 border-y border-[var(--site-border)] py-8">
                    <h2 {...editableTextPart("title")} className="text-2xl font-black text-[var(--site-text)]" style={partStyle(block, "title")}>{richTextContent(block, "title", "Sledujte nas")}</h2>
                    <SocialMediaIcons block={block} />
                </div>
            </section>
        );
    }

    if (block.type === "form") {
        return (
            <section className="px-5 py-14 sm:px-6">
                <div className="mx-auto max-w-[var(--site-container)]">
                    <FormBlock block={block} />
                </div>
            </section>
        );
    }

    return (
        <section className="px-5 py-10 sm:px-6">
            <div className="mx-auto max-w-[var(--site-container)] border border-[var(--site-border)] bg-[var(--site-surface)] p-7 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]">
                <h2 {...editableTextPart("title")} className="text-xl font-black text-[var(--site-text)]">{richTextContent(block, "title", block.type)}</h2>
                <p className="mt-2">
                    {message("ui.block.safeConfiguration")}
                </p>
            </div>
        </section>
    );
}

function Breadcrumb({
    block,
    current,
    entity,
    mobileHidden = false,
}: {
    block?: ShopTemplateBlock | null;
    current: string;
    entity?: EditableEntityRenderContextValue | null;
    mobileHidden?: boolean;
}) {
    const message = useShopStorefrontMessage();
    const { homeHref } = useContext(ShopSiteInfoContext);
    const currentAttributes = entity
        ? readonlyCatalogText(entity.type, entity.uuid, "name", "currentLabel")
        : editableTextPart("currentLabel");

    return (
        <nav
            className={classNames(
                "mx-auto max-w-[var(--site-container)] px-5 py-4 text-xs text-[color-mix(in_srgb,var(--site-text)_50%,transparent)] sm:px-6",
                mobileHidden && "hidden sm:block",
            )}
            aria-label={message("ui.navigation.breadcrumb")}
        >
            <a {...editableTextPart("homeLabel")} href={homeHref} className="hover:text-[var(--site-primary)]" style={partStyle(block, "homeLabel")}>
                {richTextContent(block, "homeLabel", message("ui.navigation.home"))}
            </a>
            <span className="mx-2">/</span>
            <span {...currentAttributes} className="font-semibold text-[var(--site-text)]" style={partStyle(block, "currentLabel")}>
                {entity ? current : richTextContent(block, "currentLabel", current)}
            </span>
        </nav>
    );
}

function productHasSellableStock(product: PublicHomeProduct): boolean {
    return product.availableForOrder
        && (!product.trackInventory || product.availableQuantity === null || product.availableQuantity > 0);
}

function productStockLabel(product: PublicHomeProduct, message: ShopStorefrontMessage): string {
    if (!product.availableForOrder) {
        return message("ui.product.unavailable");
    }

    if (!product.trackInventory || product.availableQuantity === null || product.availableQuantity > 0) {
        return message("ui.product.inStock");
    }

    return message("ui.product.outOfStock");
}

function surchargeValueSelectable(value: PublicProductSurchargeValue, quantity: number): boolean {
    if (!value.canSelect || !value.availableForOrder) {
        return false;
    }

    if (!value.trackInventory || value.availableQuantity === null) {
        return true;
    }

    return value.availableQuantity >= Math.max(1, quantity) * Math.max(1, value.linkedQuantity);
}

function surchargeValueStockLabel(value: PublicProductSurchargeValue, message: ShopStorefrontMessage): string | null {
    if (!value.linkedProductUuid) {
        return null;
    }

    if (!value.availableForOrder || value.stockStatus === "unavailable") {
        return message("ui.product.unavailable");
    }

    if (!value.trackInventory || value.availableQuantity === null) {
        return message("ui.product.inStock");
    }

    if (value.availableQuantity <= 0 || value.stockStatus === "out_of_stock") {
        return message("ui.product.outOfStock");
    }

    return message("ui.product.inStockCount", { count: value.availableQuantity });
}

function productMarketingBadges(product: PublicHomeProduct, message: ShopStorefrontMessage, editorMode: boolean): string[] {
    const badges: string[] = [];

    if (product.isOnSale) {
        badges.push(message("ui.product.sale"));
    }

    if (editorMode && moneyAmount(product.price) >= 699) {
        badges.push(message("ui.cart.freeShipping"));
    }

    return badges;
}

function productColorOptions(product: PublicHomeProduct): PublicProductVariantItem[] {
    const colors = new Map<string, PublicProductVariantItem>();

    for (const variant of product.variants) {
        for (const item of variant.items) {
            if (item.color && !colors.has(item.uuid)) {
                colors.set(item.uuid, item);
            }
        }
    }

    return Array.from(colors.values());
}

function productSizeLabels(product: PublicHomeProduct): string[] {
    const sizes = new Map<string, string>();

    for (const variant of product.variants) {
        for (const item of variant.items) {
            if (!item.color && !sizes.has(item.uuid)) {
                sizes.set(item.uuid, item.name);
            }
        }
    }

    return Array.from(sizes.values()).slice(0, 6);
}

function LagunaProductMediaPlaceholder({
    name,
    size = "card",
}: {
    name: string;
    size?: "card" | "detail";
}) {
    const words = name.trim().split(/\s+/).filter(Boolean);
    const initials = (words.length > 1
        ? words.slice(0, 2).map((word) => Array.from(word)[0] ?? "").join("")
        : Array.from(words[0] ?? "").slice(0, 2).join("")
    ).toLocaleUpperCase();

    return (
        <span
            aria-hidden="true"
            data-laguna-product-media-placeholder="true"
            className={classNames(
                "relative isolate grid h-full w-full place-items-center overflow-hidden bg-[linear-gradient(145deg,var(--site-primary-soft),color-mix(in_srgb,var(--site-accent)_12%,var(--site-surface))_52%,var(--site-surface-elevated))]",
                size === "detail" && "aspect-square",
            )}
        >
            <span className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-[color-mix(in_srgb,var(--site-primary)_14%,transparent)] blur-2xl" />
            <span className={classNames(
                "relative grid place-items-center rounded-full border border-[color-mix(in_srgb,var(--site-primary)_28%,var(--site-border))] bg-[color-mix(in_srgb,var(--site-surface)_78%,transparent)] font-black tracking-[0.08em] text-[color-mix(in_srgb,var(--site-text)_82%,var(--site-primary))] shadow-[0_14px_36px_var(--site-shadow-color)] backdrop-blur-sm",
                size === "detail" ? "h-28 w-28 text-4xl" : "h-20 w-20 text-2xl",
            )}>
                {initials}
            </span>
        </span>
    );
}

type LagunaCatalogBlockReference = Readonly<{
    block: ShopTemplateBlock;
    part: string;
}>;

function lagunaCatalogProductPageReference(
    block: ShopTemplateBlock | null | undefined,
): LagunaCatalogBlockReference | undefined {
    const productPage = catalogProductPage(block);
    const grid = block?.slots?.[SHOP_GRID_SLOT_KEY] ?? [];
    const visibleIndex = productPage ? grid.indexOf(productPage) : -1;

    if (!productPage || visibleIndex < 0) return undefined;

    const sourceIndex = lagunaRuntimeSourceIndex(productPage, visibleIndex);

    return {
        block: productPage,
        part: `slot:${SHOP_GRID_SLOT_KEY}:${sourceIndex}`,
    };
}

function lagunaNestedCatalogReference(
    reference: LagunaCatalogBlockReference | undefined,
    slot: string,
): LagunaCatalogBlockReference | undefined {
    const block = reference?.block.slots?.[slot]?.[0];

    return block && reference
        ? { block, part: `${reference.part}:slot:${slot}:0` }
        : undefined;
}

function lagunaCatalogProductReferences(
    productPageReference: LagunaCatalogBlockReference | undefined,
): Readonly<{
    image?: LagunaCatalogBlockReference;
    product?: LagunaCatalogBlockReference;
}> {
    const product = catalogProductTemplate(productPageReference?.block);
    const productReference = product
        ? lagunaNestedCatalogReference(productPageReference, "product")
        : undefined;
    const image = catalogProductImageTemplate(product);
    const imageReference = image
        ? lagunaNestedCatalogReference(productReference, "image")
        : undefined;

    return {
        ...(imageReference ? { image: imageReference } : {}),
        ...(productReference ? { product: productReference } : {}),
    };
}

function lagunaProductRibbon(
    product: PublicHomeProduct,
    productReference: LagunaCatalogBlockReference | undefined,
): Exclude<CatalogProductRibbon, "auto" | "none"> | null {
    const stored = blockOptionText(productReference?.block, "ribbon");
    const ribbon = CATALOG_PRODUCT_RIBBONS.includes(stored as CatalogProductRibbon)
        ? stored as CatalogProductRibbon
        : "auto";

    if (ribbon === "none") return null;
    if (ribbon !== "auto") return ribbon;
    if (product.isOnSale) return "sale";
    if (!product.availableForOrder) return "outOfStock";

    return null;
}

function lagunaProductRibbonLabel(
    ribbon: Exclude<CatalogProductRibbon, "auto" | "none">,
    message: ShopStorefrontMessage,
): string {
    if (ribbon === "sale") return message("ui.product.sale");
    if (ribbon === "new") return message("ui.product.new");
    if (ribbon === "soldOut") return message("ui.product.soldOut");

    return message("ui.product.outOfStock");
}

function lagunaCatalogProductCardClass(
    design: CatalogProductDesign,
    index: number,
): string {
    if (design === "multiThumbnail") return "rounded-[20px] border-transparent bg-transparent shadow-none";
    if (design === "multiChips") return "grid min-h-[88px] grid-cols-[72px_minmax(0,1fr)] items-center rounded-[22px] p-2 shadow-[0_10px_28px_var(--site-shadow-color)] transition duration-200 hover:-translate-y-0.5 hover:border-[var(--site-primary)] hover:shadow-[0_16px_38px_var(--site-shadow-color)]";
    if (design === "multiCards") return "rounded-[20px] p-3 shadow-md";
    if (design === "multiBento" && index % 5 === 0) return "sm:col-span-2 sm:row-span-2";
    if (design === "singleThumbnails") return "grid grid-cols-[84px_minmax(0,1fr)] items-center rounded-none border-x-0 border-t-0 p-2 shadow-none";
    if (design === "singleCards") return "grid grid-cols-[132px_minmax(0,1fr)] items-center rounded-[20px] p-3 sm:grid-cols-[168px_minmax(0,1fr)]";
    if (design === "singleGrid") return "grid grid-cols-[104px_minmax(0,1fr)] items-center rounded-none border-x-0 border-t-0 p-2 shadow-none sm:grid-cols-[132px_minmax(0,1fr)]";
    if (design === "singleShowcase") return "grid items-center rounded-[24px] p-3 sm:grid-cols-[minmax(240px,42%)_minmax(0,1fr)]";
    if (design === "singleCondensed") return "grid grid-cols-[64px_minmax(0,1fr)] items-center rounded-none border-x-0 border-t-0 p-1 shadow-none";
    if (design === "singleLarge") return "rounded-[24px] p-3";
    if (design === "editorialGrid") return "rounded-none border-0 bg-transparent p-0 text-center shadow-none";
    if (design === "utilityGrid") return "rounded-none border-0 bg-transparent p-0 shadow-none";
    if (design === "boutiqueGrid") return "rounded-none border-0 bg-transparent p-0 text-center shadow-none";
    if (design === "classicBordered") return "rounded-none border border-[#dadada] bg-white p-0 text-center shadow-none";
    if (design === "playfulCards") return "rounded-[28px] border-0 bg-transparent p-0 text-center shadow-none";

    return "";
}

function lagunaCatalogProductImageClass(design: CatalogProductDesign, _index: number): string {
    if (design === "multiThumbnail") return "aspect-square rounded-[18px]";
    if (design === "multiChips") return "aspect-square rounded-[16px]";
    if (design === "singleThumbnails" || design === "singleCards" || design === "singleGrid" || design === "singleCondensed") return "aspect-square rounded-[16px]";
    if (design === "singleShowcase") return "aspect-[16/10] rounded-[18px]";
    if (design === "singleLarge") return "aspect-[16/7] rounded-[20px]";
    if (design === "editorialGrid") return "aspect-square rounded-none";
    if (design === "utilityGrid") return "aspect-square rounded-none";
    if (design === "boutiqueGrid") return "aspect-[4/3] rounded-none";
    if (design === "classicBordered") return "aspect-square rounded-none";
    if (design === "playfulCards") return "aspect-[4/3] rounded-[24px]";

    return "aspect-[4/5] rounded-[24px]";
}

function CatalogProductCard({
    product,
    fallbackProduct,
    favorite,
    adding = false,
    imageBlock,
    imagePart,
    className,
    productDesign = "multiGrid",
    productIndex = 0,
    productPageMode = false,
    productReference,
    imageReference,
    onToggleFavorite,
    onAddToCart,
}: {
    product: PublicHomeProduct;
    fallbackProduct: Product;
    favorite: boolean;
    adding?: boolean;
    imageBlock?: ShopTemplateBlock;
    imagePart?: string;
    className?: string;
    productDesign?: CatalogProductDesign;
    productIndex?: number;
    productPageMode?: boolean;
    productReference?: LagunaCatalogBlockReference;
    imageReference?: LagunaCatalogBlockReference;
    onToggleFavorite: (productId: string) => void;
    onAddToCart?: (productUuid: string, productAttributeUuid?: string | null, quantity?: number, surchargeValueUuids?: string[]) => void;
}) {
    const message = useShopStorefrontMessage();
    const { formatMoney, locale } = useShopStorefrontFormatting();
    const editorMode = useContext(ShopEditorModeContext);
    const storefrontHref = useShopStorefrontHref();
    const productPath = productHref(product);
    const href = productPath ? storefrontHref(productPath) : undefined;
    const stockLabel = productStockLabel(product, message);
    const sellable = productHasSellableStock(product);
    const allColors = productColorOptions(product);
    const colors = allColors.slice(0, 5);
    const sizes = productSizeLabels(product);
    const marketingBadges = productPageMode ? [] : productMarketingBadges(product, message, editorMode);
    const ribbon = productPageMode ? lagunaProductRibbon(product, productReference) : null;
    const editorImage = productPageMode && imageBlock
        ? blockText(imageBlock, "url", fallbackProduct.image)
        : imagePart && imageBlock
            ? blockText(imageBlock, `${imagePart}Url`, fallbackProduct.image)
            : fallbackProduct.image;
    const productImage = product.primaryImage?.thumbnailUrl
        ?? product.primaryImage?.url
        ?? (editorMode ? editorImage : null);
    const productImageAlt = product.primaryImage?.alt
        || (editorMode && imagePart && imageBlock ? blockText(imageBlock, `${imagePart}Alt`, product.name) : product.name);
    const productImageTitle = imagePart && imageBlock ? blockText(imageBlock, `${imagePart}Title`, "") : "";
    const productItemHoverAttributes = itemHoverAttributes(imageBlock);
    const productImageHoverAttributes = imagePart ? imageHoverFrameAttributes(imageBlock, imagePart) : {};
    const productItemHoverEnabled = Boolean(productItemHoverAttributes["data-shop-item-hover-effect"]);
    const compactChip = productPageMode && productDesign === "multiChips";
    const editorialListing = productPageMode && productDesign === "editorialGrid";
    const utilityListing = productPageMode && productDesign === "utilityGrid";
    const boutiqueListing = productPageMode && productDesign === "boutiqueGrid";
    const classicListing = productPageMode && productDesign === "classicBordered";
    const playfulListing = productPageMode && productDesign === "playfulCards";
    const referenceFamilyListing = editorialListing
        || utilityListing
        || boutiqueListing
        || classicListing
        || playfulListing;
    const canAddDirectly = product.canAddToCart
        && !product.requiresAttribute
        && sellable
        && Boolean(onAddToCart)
        && (!productPageMode || !referenceFamilyListing);

    return (
        <article
            {...productItemHoverAttributes}
            {...productImageHoverAttributes}
            data-shop-block-part={productReference?.part}
            data-shop-runtime-entity-type={productPageMode ? "product" : undefined}
            data-shop-runtime-entity-uuid={productPageMode ? product.uuid : undefined}
            data-shop-runtime-kind={productPageMode ? "product" : undefined}
            data-catalog-product-card={productPageMode ? "true" : undefined}
            data-catalog-card-anatomy={
                editorialListing
                    ? "editorial-cell"
                    : utilityListing
                        ? "flat-utility"
                        : boutiqueListing
                            ? "minimal-boutique"
                            : classicListing
                                ? "bordered-commerce"
                                : playfulListing ? "playful-card" : undefined
            }
            className={classNames(
                "group h-full min-w-0 max-w-full overflow-hidden rounded-[28px] border border-[var(--site-border)] bg-[var(--site-surface)] p-2 shadow-sm",
                productPageMode
                    ? ""
                    : imageBlock
                        ? imageContentLayoutClass(imageBlock, "top")
                        : "flex flex-col",
                productPageMode && lagunaCatalogProductCardClass(productDesign, productIndex),
                className,
            )}
            style={productReference ? blockVisualStyle(productReference.block) : undefined}
        >
            <div
                className={classNames("relative min-w-0 max-w-full", imageBlock && !productPageMode ? imageContentImageOrderClass(imageBlock, "top") : "")}
                data-catalog-product-media={productPageMode ? "true" : undefined}
            >
                <ShopStorefrontLink
                    href={href}
                    data-shop-block-part={imageReference?.part}
                    data-shop-runtime-context={productPageMode ? "gallery" : undefined}
                    data-shop-runtime-entity-type={productPageMode ? "product" : undefined}
                    data-shop-runtime-entity-uuid={productPageMode ? product.uuid : undefined}
                    data-shop-runtime-kind={productPageMode ? "media" : undefined}
                    className={classNames(
                        "relative grid place-items-center overflow-hidden bg-[var(--site-surface-elevated)]",
                        productPageMode ? lagunaCatalogProductImageClass(productDesign, productIndex) : "aspect-[4/5] rounded-[24px]",
                        editorMode && fallbackProduct.imageClass,
                    )}
                    onClick={(event) => editorMode && event.preventDefault()}
                    style={imageReference
                        ? blockVisualStyle(imageReference.block)
                        : imagePart && imageBlock
                            ? imageFrameStyle(imageBlock, imagePart)
                            : undefined}
                >
                    {productImage ? (
                        <img
                            {...(imagePart ? editablePart(imagePart) : {})}
                            data-shop-image-hover-target={imagePart ? "true" : undefined}
                            src={productImage}
                            alt={productImageAlt}
                            title={productImageTitle || undefined}
                            className={classNames(
                                "h-full w-full",
                                editorialListing || boutiqueListing || classicListing || playfulListing ? "object-contain p-5" : "object-cover",
                            )}
                            style={imageReference
                                ? imageInteractivePartStyle(imageReference.block, "image")
                                : imagePart
                                    ? imageInteractivePartStyle(imageBlock, imagePart)
                                    : undefined}
                        />
                    ) : <LagunaProductMediaPlaceholder name={product.name} />}
                    {ribbon ? (
                        <span className={classNames(
                            "absolute left-3 top-3 z-10 max-w-[calc(100%-4.75rem)] px-3 py-1 text-left text-[11px] font-black leading-tight whitespace-normal [overflow-wrap:anywhere]",
                            !classicListing && "rounded-full shadow-sm",
                            classicListing && "rounded-none pr-5 [clip-path:polygon(0_0,calc(100%-8px)_0,100%_50%,calc(100%-8px)_100%,0_100%)]",
                            compactChip && "left-1.5 top-1.5 px-2 py-0.5 text-[9px]",
                            ribbon === "sale" && "bg-[var(--site-primary)] text-[var(--site-on-primary)]",
                            ribbon === "new" && "bg-[var(--site-accent)] text-[var(--site-text)]",
                            (ribbon === "soldOut" || ribbon === "outOfStock") && "bg-[var(--site-inverse)] text-[var(--site-on-inverse)]",
                        )}>
                            {lagunaProductRibbonLabel(ribbon, message)}
                        </span>
                    ) : null}
                </ShopStorefrontLink>
                {!compactChip && (colors.length > 0 || sizes.length > 0) && !editorialListing && !boutiqueListing && !classicListing && !playfulListing ? (
                    <div className={classNames(
                        "pointer-events-none absolute inset-x-4 bottom-4 z-20 rounded-[22px] bg-[color-mix(in_srgb,var(--site-surface)_95%,transparent)] p-3 shadow-[0_8px_24px_var(--site-shadow-color)] transition duration-200",
                        productItemHoverEnabled ? "translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100" : "opacity-100",
                    )}>
                        {colors.length > 0 ? (
                            <div className="flex min-w-0 max-w-full flex-wrap items-center gap-1.5">
                                {colors.map((item) => (
                                    <span
                                        key={item.uuid}
                                        className="h-5 w-5 shrink-0 rounded-full border border-[color-mix(in_srgb,var(--site-text)_15%,transparent)] ring-1 ring-[var(--site-on-inverse)]"
                                        style={{ backgroundColor: item.color ?? "var(--site-surface)" }}
                                        title={item.name}
                                    />
                                ))}
                                {allColors.length > colors.length ? <span className="text-xs font-bold text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">+{allColors.length - colors.length}</span> : null}
                            </div>
                        ) : null}
                        {sizes.length > 0 ? (
                            <div className={classNames("flex flex-wrap gap-1.5", colors.length > 0 && "mt-2")}>
                                {sizes.map((size) => (
                                    <span key={size} className="min-w-7 rounded-full border border-[var(--site-border)] bg-[var(--site-surface)] px-2 py-1 text-center text-[11px] font-bold text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">
                                        {size}
                                    </span>
                                ))}
                            </div>
                        ) : null}
                    </div>
                ) : null}
                {marketingBadges.length > 0 ? (
                <div className="absolute left-3 top-3 flex min-w-0 max-w-[calc(100%-4.75rem)] flex-col gap-2">
                    {marketingBadges.map((badge) => (
                        <span
                            key={badge}
                            className={classNames(
                                "w-fit max-w-full rounded-full bg-[color-mix(in_srgb,var(--site-surface)_95%,transparent)] px-3 py-1 text-[11px] font-black leading-tight whitespace-normal shadow-sm [overflow-wrap:anywhere]",
                                badge === message("ui.product.sale") ? "text-[var(--site-primary)]" : "text-[var(--site-primary)]",
                            )}
                        >
                            {badge}
                        </span>
                    ))}
                </div>
                ) : null}
                {!compactChip && !editorialListing && !classicListing ? (
                    <button
                        type="button"
                        className={classNames(
                            "absolute right-3 top-3 grid h-10 w-10 place-items-center rounded-full border border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] bg-[color-mix(in_srgb,var(--site-surface)_95%,transparent)] shadow-sm",
                            favorite && "text-[var(--site-primary)]",
                        )}
                        aria-label={favorite ? message("ui.product.removeFromFavorites") : message("ui.product.addToFavorites")}
                        onClick={() => onToggleFavorite(product.uuid)}
                    >
                        <Icon name="heart" className={classNames("h-5 w-5", favorite && "fill-current")} />
                    </button>
                ) : null}
                {playfulListing ? (
                    <ShopStorefrontLink
                        href={href}
                        data-catalog-playful-overlay-action
                        onClick={(event) => editorMode && event.preventDefault()}
                        className="absolute left-1/2 top-1/2 z-20 inline-flex min-h-11 min-w-0 max-w-[calc(100%-32px)] -translate-x-1/2 -translate-y-1/2 items-center justify-center border-2 border-dotted border-[var(--site-primary)] bg-[var(--site-primary-soft)] px-5 py-3 text-center text-sm font-black text-[var(--site-primary)] opacity-0 transition-opacity whitespace-normal [overflow-wrap:anywhere] group-focus-within:opacity-100 group-hover:opacity-100"
                    >
                        {message("ui.product.detail")}
                    </ShopStorefrontLink>
                ) : null}
            </div>

            <div className={classNames(
                "flex min-w-0 max-w-full flex-1 flex-col px-3 pb-4 pt-4",
                imageBlock && !productPageMode ? imageContentTextOrderClass(imageBlock, "top") : "",
                compactChip && "min-w-0 px-3 py-1.5",
                productPageMode && productDesign.startsWith("single") && productDesign !== "singleLarge" && "min-w-0 px-4 py-3",
                editorialListing && "px-5 pb-6 pt-5",
                utilityListing && "px-0 pb-0 pt-4",
                boutiqueListing && "items-center px-2 pb-5 pt-5",
                classicListing && "items-center px-5 pb-6 pt-5",
                playfulListing && "items-center px-2 pb-5 pt-5",
            )} data-catalog-product-content={productPageMode ? "true" : undefined}>
                {utilityListing ? (
                    <div
                        className="mb-3 flex min-w-0 max-w-full flex-wrap items-center justify-between gap-3 border-b border-[var(--site-border)] pb-2 text-[10px] font-black uppercase tracking-[0.08em]"
                        data-catalog-utility-line
                    >
                        <span className="truncate text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">{product.reference}</span>
                        <span className={classNames("min-w-0 max-w-full [overflow-wrap:anywhere]", sellable ? "text-[var(--site-accent)]" : "text-[var(--site-primary)]")}>{stockLabel}</span>
                    </div>
                ) : null}
                <div className="min-w-0 max-w-full">
                    {!compactChip && product.reference && !editorialListing && !utilityListing && !boutiqueListing && !classicListing && !playfulListing ? (
                        <p
                            {...(productPageMode ? {} : editableProductField(product.uuid, "reference"))}
                            className={classNames("truncate text-[11px] font-black uppercase", readableOnSurfaceTextClass)}
                            style={productReference ? partStyle(productReference.block, "productReference") : undefined}
                        >
                            {product.reference}
                        </p>
                    ) : null}
                    <ShopStorefrontLink
                        {...(productPageMode ? {} : editableProductField(product.uuid, "name"))}
                        href={href}
                        onClick={(event) => editorMode && event.preventDefault()}
                        className={classNames(
                            "mt-1 block min-h-11 min-w-0 max-w-full text-base font-black leading-snug text-[var(--site-text)] [overflow-wrap:anywhere] hover:text-[var(--site-primary)]",
                            compactChip && "mt-0 line-clamp-2 min-h-0 text-sm",
                        )}
                        style={productReference ? partStyle(productReference.block, "productName") : undefined}
                    >
                        {product.name}
                    </ShopStorefrontLink>
                    {!compactChip && product.reviewCount > 0 ? (
                        <p className="mt-2 max-w-full text-xs font-black uppercase tracking-wide text-[var(--site-text)] [overflow-wrap:anywhere]" data-catalog-product-reviews>
                            ★ {product.reviewRating.toFixed(1)} · {storefrontPluralMessage(
                                product.reviewCount,
                                locale,
                                PRODUCT_REVIEW_COUNT_MESSAGE_KEYS,
                                message,
                            )}
                        </p>
                    ) : null}
                    {!compactChip && product.descriptionShort && !editorialListing && !boutiqueListing && !classicListing && !playfulListing ? (
                        <p
                            {...(productPageMode ? {} : editableProductField(product.uuid, "descriptionShort"))}
                            className={classNames("mt-2 line-clamp-2 min-h-10 text-sm leading-5", readableOnSurfaceTextClass)}
                            style={productReference ? partStyle(productReference.block, "productDescription") : undefined}
                        >
                            {product.descriptionShort}
                        </p>
                    ) : null}
                    {(boutiqueListing || playfulListing) && colors.length > 0 ? (
                        <div
                            className="mt-4 flex min-w-0 max-w-full flex-wrap items-center gap-2"
                            data-catalog-boutique-swatches={boutiqueListing ? "true" : undefined}
                            data-catalog-playful-swatches={playfulListing ? "true" : undefined}
                        >
                            {colors.map((item) => (
                                <span
                                    key={item.uuid}
                                    aria-hidden="true"
                                    className="h-5 w-5 shrink-0 rounded-full border-2 border-[var(--site-surface)] shadow-[0_0_0_1px_var(--site-border)]"
                                    style={{ backgroundColor: item.color ?? "var(--site-surface)" }}
                                />
                            ))}
                            {allColors.length > colors.length ? (
                                <span className="text-[11px] font-bold text-[color-mix(in_srgb,var(--site-text)_48%,transparent)]">+{allColors.length - colors.length}</span>
                            ) : null}
                        </div>
                    ) : null}
                </div>

                <div
                    className={classNames("mt-auto min-w-0 max-w-full pt-4", compactChip && "pt-1.5")}
                    data-catalog-product-actions={productPageMode ? "true" : undefined}
                >
                    <div className="flex min-w-0 max-w-full flex-wrap items-end justify-between gap-3">
                        <div className="min-w-0 max-w-full">
                            {!compactChip ? <p className={classNames("text-[11px] font-black uppercase", readableOnSurfaceTextClass)} data-catalog-price-label>{message("ui.product.priceFrom")}</p> : null}
                            <p
                                {...(productPageMode ? {} : editableProductField(product.uuid, "formattedPrice"))}
                                className={classNames("max-w-full text-xl font-black text-[var(--site-primary)] [overflow-wrap:anywhere]", compactChip && "text-base")}
                                style={productReference ? partStyle(productReference.block, "productPrice") : undefined}
                            >
                                {formatMoney(product.price)}
                            </p>
                        </div>
                        {!compactChip && !utilityListing ? (
                            <p className="min-w-0 max-w-full text-right text-xs font-black text-[var(--site-text)] [overflow-wrap:anywhere]" data-catalog-stock-label>
                                {stockLabel}
                            </p>
                        ) : null}
                    </div>
                    {!compactChip && !playfulListing ? (
                        canAddDirectly ? (
                            <button
                                type="button"
                                className={classNames(
                                    "mt-4 w-full min-w-0 max-w-full bg-[var(--site-primary)] px-4 py-3 text-sm font-black text-[var(--site-on-primary)] whitespace-normal [overflow-wrap:anywhere] disabled:bg-[color-mix(in_srgb,var(--site-text)_25%,transparent)]",
                                    editorialListing && "rounded-none border border-[var(--site-primary)] bg-transparent uppercase tracking-[0.1em] text-[var(--site-primary)]",
                                    utilityListing && "rounded-none bg-[var(--site-accent)] text-[var(--site-text)]",
                                    boutiqueListing && "rounded-lg shadow-none",
                                    classicListing && "rounded-none bg-black uppercase tracking-[0.05em] text-white",
                                    playfulListing && "rounded-full shadow-none",
                                    !editorialListing && !utilityListing && !boutiqueListing && !classicListing && !playfulListing && "rounded-full",
                                )}
                                disabled={adding}
                                onClick={() => onAddToCart?.(product.uuid, null, 1)}
                            >
                                {adding ? message("ui.product.adding") : message("ui.product.addToCart")}
                            </button>
                        ) : (
                            <ShopStorefrontLink
                                href={href}
                                onClick={(event) => editorMode && event.preventDefault()}
                                className={classNames(
                                    "mt-4 block min-w-0 max-w-full bg-[var(--site-primary)] px-4 py-3 text-center text-sm font-black text-[var(--site-on-primary)] whitespace-normal [overflow-wrap:anywhere]",
                                    editorialListing && "rounded-none border border-[var(--site-primary)] bg-transparent uppercase tracking-[0.1em] text-[var(--site-primary)]",
                                    utilityListing && "rounded-none bg-[var(--site-accent)] text-[var(--site-text)]",
                                    boutiqueListing && "rounded-lg shadow-none",
                                    classicListing && "rounded-none bg-black uppercase tracking-[0.05em] text-white",
                                    playfulListing && "rounded-full shadow-none",
                                    !editorialListing && !utilityListing && !boutiqueListing && !classicListing && !playfulListing && "rounded-full",
                                )}
                            >
                                {message("ui.product.detail")}
                            </ShopStorefrontLink>
                        )
                    ) : null}
                    {classicListing && sizes.length > 0 ? (
                        <div className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs font-medium uppercase" data-catalog-classic-sizes>
                            {sizes.map((size) => <span key={size}>{size}</span>)}
                        </div>
                    ) : null}
                </div>
            </div>
        </article>
    );
}

function ProductGrid({
    block,
    favorites,
    addingProductUuid,
    onAddToCart,
    onToggleFavorite,
    productPageReference,
    publicProducts = [],
}: {
    block?: ShopTemplateBlock;
    favorites: Set<string>;
    addingProductUuid?: string | null;
    onAddToCart?: (productUuid: string, productAttributeUuid?: string | null, quantity?: number, surchargeValueUuids?: string[]) => void;
    onToggleFavorite: (productId: string) => void;
    productPageReference?: LagunaCatalogBlockReference;
    publicProducts?: PublicHomeProduct[];
}) {
    const message = useShopStorefrontMessage();
    const config = catalogProductPageConfig(productPageReference?.block);
    const references = lagunaCatalogProductReferences(productPageReference);
    const singleColumn = config.productDesign.startsWith("single");
    const gridStyle = {
        "--laguna-product-desktop-columns": String(config.desktopColumns),
        "--laguna-product-mobile-columns": String(config.mobileColumns),
    } as CSSProperties;

    if (publicProducts.length > 0) {
        return (
            <div
                className={classNames(
                    "grid gap-4",
                    productPageReference
                        ? singleColumn
                            ? "grid-cols-1"
                            : "[grid-template-columns:repeat(var(--laguna-product-mobile-columns),minmax(0,1fr))] md:[grid-template-columns:repeat(var(--laguna-product-desktop-columns),minmax(0,1fr))]"
                        : "sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4",
                    productPageReference && config.productDesign === "multiBento" && "grid-flow-dense",
                    productPageReference && config.productDesign === "multiChips" && "gap-3",
                    productPageReference && config.productDesign === "singleCondensed" && "gap-0",
                    productPageReference && config.productDesign === "editorialGrid" && "gap-0 border-l border-t border-[var(--site-text)]",
                    productPageReference && config.productDesign === "utilityGrid" && "gap-x-8 gap-y-12",
                    productPageReference && config.productDesign === "boutiqueGrid" && "gap-x-7 gap-y-12",
                    productPageReference && config.productDesign === "classicBordered" && "gap-8",
                    productPageReference && config.productDesign === "playfulCards" && "gap-x-7 gap-y-12",
                )}
                data-laguna-product-design={productPageReference ? config.productDesign : undefined}
                style={productPageReference ? gridStyle : undefined}
            >
                {publicProducts.map((product, index) => {
                    const fallbackProduct = products[index % products.length];
                    const active = favorites.has(product.uuid);

                    return (
                        <CatalogProductCard
                            key={product.uuid}
                            product={product}
                            fallbackProduct={fallbackProduct}
                            favorite={active}
                            adding={addingProductUuid === product.uuid}
                            imageBlock={references.image?.block ?? block}
                            imagePart={productPageReference ? undefined : `product${index + 1}Image`}
                            imageReference={references.image}
                            onAddToCart={onAddToCart}
                            onToggleFavorite={onToggleFavorite}
                            productDesign={config.productDesign}
                            productIndex={index}
                            productPageMode={Boolean(productPageReference)}
                            productReference={references.product}
                        />
                    );
                })}
            </div>
        );
    }

    return (
        <div className="rounded-[28px] border border-dashed border-[var(--site-border)] bg-[var(--site-surface)] px-6 py-12 text-center">
            <p className="text-lg font-black">{message("ui.product.noMatches")}</p>
            <p className={classNames("mt-2 text-sm", readableOnSurfaceTextClass)}>{message("ui.product.adjustSearch")}</p>
        </div>
    );
}

function CatalogSortTabs({
    baseHref,
    currentSort,
    filters,
    onNavigate,
    presentationStyle = "default",
    query,
}: {
    baseHref: string;
    currentSort: CatalogSortValue;
    filters: CatalogFilterState;
    onNavigate?: CatalogNavigateHandler;
    presentationStyle?: CatalogFilterStyle;
    query: string | null;
}) {
    const message = useShopStorefrontMessage();
    const [open, setOpen] = useState(false);
    const sortRef = useRef<HTMLDivElement | null>(null);
    const selectedOption = CATALOG_SORT_OPTIONS.find((option) => option.value === currentSort) ?? CATALOG_SORT_OPTIONS[0];

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const sort = sortRef.current;

        if (!sort) {
            return undefined;
        }

        const sortRoot: HTMLDivElement = sort;
        const ownerDocument = sortRoot.ownerDocument;

        function handleDocumentMouseDown(event: globalThis.MouseEvent): void {
            if (!sortRoot.contains(event.target as Node)) {
                setOpen(false);
            }
        }

        function handleDocumentKeyDown(event: globalThis.KeyboardEvent): void {
            if (event.key === "Escape") {
                setOpen(false);
            }
        }

        ownerDocument.addEventListener("mousedown", handleDocumentMouseDown);
        ownerDocument.addEventListener("keydown", handleDocumentKeyDown);

        return () => {
            ownerDocument.removeEventListener("mousedown", handleDocumentMouseDown);
            ownerDocument.removeEventListener("keydown", handleDocumentKeyDown);
        };
    }, [open]);

    if (
        presentationStyle === "structuredSidebar"
        || presentationStyle === "boutiqueSidebar"
    ) {
        const boutiqueTabs = presentationStyle === "boutiqueSidebar";
        const compactTabs = boutiqueTabs;
        const presentationOptions = compactTabs
            ? (["recommended", "priceAsc", "priceDesc", "bestselling", "nameAsc"] as const).map(
                (value) => CATALOG_SORT_OPTIONS.find((option) => option.value === value)!,
            )
            : CATALOG_SORT_OPTIONS;

        return (
            <nav
                aria-label={message("ui.catalog.sortLabel")}
                className={classNames(
                    "flex max-w-full overflow-x-auto border border-[#dadada] bg-[#f6f6f6]",
                    compactTabs && "rounded-lg",
                )}
                data-catalog-sort-presentation={
                    boutiqueTabs
                        ? "flat-tabs"
                        : "structured-tabs"
                }
            >
                {presentationOptions.map((option) => {
                    const selected = option.value === currentSort;
                    const href = catalogHref(baseHref, option.value, filters, query);

                    return (
                        <a
                            key={option.value}
                            href={href}
                            aria-current={selected ? "page" : undefined}
                            onClick={(event) => handleCatalogNavigateClick(event, href, onNavigate)}
                            className={classNames(
                                "inline-flex min-h-[50px] shrink-0 items-center border-r border-[#dadada] px-5 text-sm font-semibold last:border-r-0",
                                selected ? "bg-white text-black" : "bg-[#f6f6f6] text-[#666] hover:bg-white hover:text-black",
                            )}
                        >
                            {message(option.messageKey)}
                        </a>
                    );
                })}
            </nav>
        );
    }

    return (
        <div ref={sortRef} className="relative z-30 w-full min-w-[210px] sm:w-auto" aria-label={message("ui.catalog.sortLabel")}>
            <button
                type="button"
                className="flex min-h-11 w-full items-center justify-between gap-3 rounded-full bg-[var(--site-surface-elevated)] px-4 py-2.5 text-sm font-black text-[color-mix(in_srgb,var(--site-text)_70%,transparent)] transition hover:bg-[var(--site-surface)] hover:text-[var(--site-text)] hover:shadow-sm sm:w-[230px]"
                aria-expanded={open}
                aria-haspopup="true"
                onClick={() => setOpen((current) => !current)}
            >
                <span className="min-w-0 truncate">{message(selectedOption.messageKey)}</span>
                <Icon name="chevronDown" className={classNames("h-4 w-4 shrink-0 transition", open && "rotate-180")} />
            </button>
            {open ? (
                <div className="absolute end-0 top-full z-40 mt-3 w-full min-w-[230px] overflow-hidden rounded-2xl bg-[var(--site-surface)] p-2 shadow-[0_18px_54px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_10%,transparent)]">
                    {CATALOG_SORT_OPTIONS.map((option) => {
                        const selected = option.value === currentSort;
                        const href = catalogHref(baseHref, option.value, filters, query);

                        return (
                            <a
                                key={option.value}
                                href={href}
                                aria-current={selected ? "true" : undefined}
                                onClick={(event) => {
                                    setOpen(false);
                                    handleCatalogNavigateClick(event, href, onNavigate);
                                }}
                                className={classNames(
                                    "flex min-h-10 items-center justify-between rounded-xl px-3 py-2 text-sm font-black transition",
                                    selected
                                        ? "bg-[var(--site-surface)] text-[var(--site-primary)]"
                                        : "text-[color-mix(in_srgb,var(--site-text)_60%,transparent)] hover:bg-[var(--site-surface-elevated)] hover:text-[var(--site-text)]",
                                )}
                            >
                                <span>{message(option.messageKey)}</span>
                                {selected ? <Icon name="check" className="h-4 w-4" /> : null}
                            </a>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}

function SelectedCatalogFilters({
    baseHref,
    currentSort,
    filters,
    onNavigate,
    query,
    saleLocked = false,
    selectedOptions,
}: {
    baseHref: string;
    currentSort: CatalogSortValue;
    filters: CatalogFilterState;
    onNavigate?: CatalogNavigateHandler;
    query: string | null;
    saleLocked?: boolean;
    selectedOptions: Array<{
        facet: PublicCatalogFacet;
        option: PublicCatalogFacetOption;
    }>;
}) {
    const message = useShopStorefrontMessage();
    const { formatMoney } = useShopStorefrontFormatting();
    const activeCount = activeCatalogFilterCount(filters, !saleLocked);
    const hasPriceFilter = filters.priceMin !== null || filters.priceMax !== null;

    if (selectedOptions.length === 0 && !hasPriceFilter) {
        return null;
    }

    const clearHref = clearCatalogFiltersHref(baseHref, currentSort, query);

    return (
        <div className="rounded-2xl bg-[color-mix(in_srgb,var(--site-surface)_95%,transparent)] p-3 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
            <div className="flex min-w-0 max-w-full flex-wrap items-center justify-between gap-3">
                <h3 className="min-w-0 max-w-full px-1 text-sm font-bold [overflow-wrap:anywhere]">{message("ui.catalog.selectedFilters")}</h3>
                <a
                    href={clearHref}
                    onClick={(event) => handleCatalogNavigateClick(event, clearHref, onNavigate)}
                    className="max-w-full rounded-full px-3 py-1.5 text-xs font-black text-[var(--site-primary)] whitespace-normal [overflow-wrap:anywhere] hover:bg-[var(--site-surface)]"
                >
                    {message("ui.catalog.clearFilters")}{activeCount > 0 ? ` (${activeCount})` : ""}
                </a>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
                {hasPriceFilter ? (
                    <a
                        href={catalogHref(baseHref, currentSort, { ...filters, priceMin: null, priceMax: null }, query)}
                        onClick={(event) => handleCatalogNavigateClick(event, catalogHref(baseHref, currentSort, { ...filters, priceMin: null, priceMax: null }, query), onNavigate)}
                        className="inline-flex min-w-0 max-w-full flex-wrap items-center gap-2 rounded-full bg-[var(--site-surface)] px-3 py-2 text-xs font-bold text-[var(--site-primary)] whitespace-normal ring-1 ring-[var(--site-primary)]/20 [overflow-wrap:anywhere]"
                    >
                        <span className="min-w-0 max-w-full text-[color-mix(in_srgb,var(--site-text)_45%,transparent)] [overflow-wrap:anywhere]">{message("ui.catalog.price")}</span>
                        <span className="min-w-0 max-w-full [overflow-wrap:anywhere]">
                            {filters.priceMin !== null ? `${message("ui.catalog.priceFrom")} ${formatMoney(filters.priceMin)}` : ""}
                            {filters.priceMin !== null && filters.priceMax !== null ? " " : ""}
                            {filters.priceMax !== null ? `${message("ui.catalog.priceTo")} ${formatMoney(filters.priceMax)}` : ""}
                        </span>
                        <Icon name="x" className="h-3.5 w-3.5 shrink-0" />
                    </a>
                ) : null}
                {selectedOptions.map(({ facet, option }) => {
                    const href = catalogHref(baseHref, currentSort, toggleCatalogFilter(filters, facet, option), query);

                    return (
                        <a
                            key={`${facet.code}-${option.uuid}`}
                            href={href}
                            onClick={(event) => handleCatalogNavigateClick(event, href, onNavigate)}
                            className="inline-flex min-w-0 max-w-full flex-wrap items-center gap-2 rounded-full bg-[var(--site-surface)] px-3 py-2 text-xs font-bold text-[var(--site-primary)] whitespace-normal ring-1 ring-[var(--site-primary)]/20 [overflow-wrap:anywhere]"
                        >
                            <span className="min-w-0 max-w-full text-[color-mix(in_srgb,var(--site-text)_45%,transparent)] [overflow-wrap:anywhere]">{facet.label}</span>
                            <span className="min-w-0 max-w-full [overflow-wrap:anywhere]">{option.label}</span>
                            <Icon name="x" className="h-3.5 w-3.5 shrink-0" />
                        </a>
                    );
                })}
            </div>
        </div>
    );
}

function CatalogHiddenFilterInputs({
    filters,
    query,
    sort,
}: {
    filters: CatalogFilterState;
    query: string | null;
    sort: CatalogSortValue;
}) {
    return (
        <>
            {query ? <input type="hidden" name="q" value={query} /> : null}
            {sort !== "recommended" ? <input type="hidden" name="sort" value={sort} /> : null}
            {filters.priceMin !== null ? <input type="hidden" name="priceMin" value={filters.priceMin} /> : null}
            {filters.priceMax !== null ? <input type="hidden" name="priceMax" value={filters.priceMax} /> : null}
            {filters.availability ? <input type="hidden" name="availability" value={filters.availability} /> : null}
            {filters.saleOnly ? <input type="hidden" name="saleOnly" value="1" /> : null}
            {filters.manufacturers.map((manufacturerUuid) => (
                <input key={manufacturerUuid} type="hidden" name="manufacturer[]" value={manufacturerUuid} />
            ))}
            {filters.categories.map((categoryUuid) => (
                <input key={categoryUuid} type="hidden" name="category[]" value={categoryUuid} />
            ))}
            {Object.entries(filters.attributes).flatMap(([attributeUuid, itemUuids]) => (
                itemUuids.map((itemUuid) => (
                    <input key={`${attributeUuid}-${itemUuid}`} type="hidden" name={`attribute[${attributeUuid}][]`} value={itemUuid} />
                ))
            ))}
        </>
    );
}

function CatalogRangeFacet({
    baseHref,
    currentSort,
    facet,
    filters,
    onNavigate,
    query,
}: {
    baseHref: string;
    currentSort: CatalogSortValue;
    facet: PublicCatalogFacet;
    filters: CatalogFilterState;
    onNavigate?: CatalogNavigateHandler;
    query: string | null;
}) {
    const message = useShopStorefrontMessage();
    const rangeMinimum = Math.floor(facet.min ?? 0);
    const rangeMaximum = Math.ceil(facet.max ?? rangeMinimum);
    const [draftMinimum, setDraftMinimum] = useState(facet.selectedMin === null ? "" : String(facet.selectedMin));
    const [draftMaximum, setDraftMaximum] = useState(facet.selectedMax === null ? "" : String(facet.selectedMax));

    useEffect(() => {
        setDraftMinimum(facet.selectedMin === null ? "" : String(facet.selectedMin));
        setDraftMaximum(facet.selectedMax === null ? "" : String(facet.selectedMax));
    }, [facet.selectedMax, facet.selectedMin]);

    if (facet.min === null || facet.max === null) {
        return null;
    }

    function submitRange(event: FormEvent<HTMLFormElement>): void {
        if (!onNavigate) {
            return;
        }

        event.preventDefault();

        const formData = new FormData(event.currentTarget);
        const priceMinValue = formData.get("priceMin");
        const priceMaxValue = formData.get("priceMax");
        const priceMin = typeof priceMinValue === "string" && priceMinValue.trim() !== "" ? Number(priceMinValue) : null;
        const priceMax = typeof priceMaxValue === "string" && priceMaxValue.trim() !== "" ? Number(priceMaxValue) : null;

        void onNavigate(catalogHref(baseHref, currentSort, {
            ...filters,
            priceMin: Number.isFinite(priceMin) ? priceMin : null,
            priceMax: Number.isFinite(priceMax) ? priceMax : null,
        }, query));
    }

    return (
        <form action={baseHref} method="get" className="mt-3 space-y-3" onSubmit={submitRange}>
            <CatalogHiddenFilterInputs filters={{ ...filters, priceMin: null, priceMax: null }} query={query} sort={currentSort} />
            <div className="relative hidden h-5" data-catalog-range-track>
                <span className="absolute left-0 right-0 top-[7px] h-px bg-[#bdbdbd]" />
                <label className="absolute inset-0">
                    <span className="sr-only">{message("ui.catalog.priceFrom")}</span>
                    <input
                        aria-label={message("ui.catalog.priceFrom")}
                        className="pointer-events-none absolute inset-x-0 top-0 h-4 w-full appearance-none bg-transparent [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:rounded-none [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-black [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-none [&::-webkit-slider-thumb]:bg-black"
                        min={rangeMinimum}
                        max={rangeMaximum}
                        type="range"
                        value={draftMinimum === "" ? rangeMinimum : Number(draftMinimum)}
                        onChange={(event) => {
                            const next = Math.min(Number(event.target.value), draftMaximum === "" ? rangeMaximum : Number(draftMaximum));
                            setDraftMinimum(String(next));
                        }}
                    />
                </label>
                <label className="absolute inset-0">
                    <span className="sr-only">{message("ui.catalog.priceTo")}</span>
                    <input
                        aria-label={message("ui.catalog.priceTo")}
                        className="pointer-events-none absolute inset-x-0 top-0 h-4 w-full appearance-none bg-transparent [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:rounded-none [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-black [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-none [&::-webkit-slider-thumb]:bg-black"
                        min={rangeMinimum}
                        max={rangeMaximum}
                        type="range"
                        value={draftMaximum === "" ? rangeMaximum : Number(draftMaximum)}
                        onChange={(event) => {
                            const next = Math.max(Number(event.target.value), draftMinimum === "" ? rangeMinimum : Number(draftMinimum));
                            setDraftMaximum(String(next));
                        }}
                    />
                </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
                <label className="block">
                    <span className="sr-only">{message("ui.catalog.priceFrom")}</span>
                    <input
                        className="w-full border border-[var(--site-border)] px-3 py-2 text-sm outline-none focus:border-[var(--site-primary)]"
                        min={rangeMinimum}
                        max={rangeMaximum}
                        name="priceMin"
                        placeholder={`${message("ui.catalog.priceFrom")} ${Math.floor(facet.min)}`}
                        type="number"
                        value={draftMinimum}
                        onChange={(event) => setDraftMinimum(event.target.value)}
                    />
                </label>
                <label className="block">
                    <span className="sr-only">{message("ui.catalog.priceTo")}</span>
                    <input
                        className="w-full border border-[var(--site-border)] px-3 py-2 text-sm outline-none focus:border-[var(--site-primary)]"
                        min={rangeMinimum}
                        max={rangeMaximum}
                        name="priceMax"
                        placeholder={`${message("ui.catalog.priceTo")} ${Math.ceil(facet.max)}`}
                        type="number"
                        value={draftMaximum}
                        onChange={(event) => setDraftMaximum(event.target.value)}
                    />
                </label>
            </div>
            <button type="submit" className="w-full border border-[var(--site-primary)] px-3 py-2 text-xs font-bold text-[var(--site-primary)] hover:bg-[var(--site-surface)]">
                {message("ui.catalog.filterPrice")}
            </button>
        </form>
    );
}

function CatalogTextFilter({
    baseHref,
    currentSort,
    filters,
    query,
    onNavigate,
    label,
    placeholder,
    variant = "panel",
}: {
    baseHref: string;
    currentSort: CatalogSortValue;
    filters: CatalogFilterState;
    query: string | null;
    onNavigate?: CatalogNavigateHandler;
    label?: string;
    placeholder?: string;
    variant?: "panel" | "inline";
}) {
    const message = useShopStorefrontMessage();
    const editorMode = useContext(ShopEditorModeContext);
    const [draft, setDraft] = useState(query ?? "");
    const inputRef = useRef<HTMLInputElement | null>(null);
    const inline = variant === "inline";

    useEffect(() => {
        const input = inputRef.current;

        if (input !== null && input.ownerDocument.activeElement === input) {
            return;
        }

        setDraft(query ?? "");
    }, [query]);

    useEffect(() => {
        if (editorMode) {
            return;
        }

        const normalizedDraft = draft.trim();
        const normalizedQuery = (query ?? "").trim();

        if (normalizedDraft === normalizedQuery) {
            return;
        }

        const view = inputRef.current?.ownerDocument.defaultView;

        if (!view) {
            return;
        }

        const timeout = view.setTimeout(() => {
            const href = catalogHref(baseHref, currentSort, filters, normalizedDraft === "" ? null : normalizedDraft);
            const currentHref = view.location.pathname + view.location.search;

            if (href !== currentHref) {
                if (onNavigate) {
                    void onNavigate(href);
                } else {
                    view.location.assign(href);
                }
            }
        }, 450);

        return () => view.clearTimeout(timeout);
    }, [baseHref, currentSort, draft, editorMode, filters, onNavigate, query]);

    function submitTextFilter(event: FormEvent<HTMLFormElement>): void {
        if (editorMode) {
            event.preventDefault();

            return;
        }

        if (!onNavigate) {
            return;
        }

        event.preventDefault();

        const normalizedDraft = draft.trim();
        void onNavigate(catalogHref(baseHref, currentSort, filters, normalizedDraft === "" ? null : normalizedDraft));
    }

    return (
        <form
            action={baseHref}
            method="get"
            className={inline ? "w-full min-w-0" : "border border-[var(--site-border)] bg-[var(--site-surface)] p-5"}
            onSubmit={submitTextFilter}
        >
            <CatalogHiddenFilterInputs filters={filters} query={null} sort={currentSort} />
            <label className="block">
                <span className={inline ? "sr-only" : "text-sm font-bold"}>{label ?? message("ui.catalog.searchCategory")}</span>
                <span className={classNames(
                    "flex items-center bg-[var(--site-surface)] focus-within:border-[var(--site-primary)]",
                    inline
                        ? "min-h-11 rounded-full border border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] px-4 py-2 shadow-sm"
                        : "mt-3 border border-[var(--site-border)] px-3 py-2",
                )}>
                    <Icon name="search" className={classNames("h-4 w-4 shrink-0 text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]", inline && "h-[18px] w-[18px]")} />
                    <input
                        type="search"
                        name="q"
                        ref={inputRef}
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        placeholder={placeholder ?? message("ui.catalog.searchPlaceholder")}
                        autoComplete="off"
                        data-catalog-search-input="true"
                        data-catalog-target-href={catalogHref(baseHref, currentSort, filters, draft.trim() === "" ? null : draft.trim())}
                        className={classNames(
                            "min-w-0 flex-1 bg-transparent px-3 text-sm outline-none",
                            inline ? "py-0.5 font-bold placeholder:font-bold placeholder:text-[color-mix(in_srgb,var(--site-text)_35%,transparent)]" : "py-1",
                        )}
                    />
                </span>
            </label>
        </form>
    );
}

function CatalogTermsFacet({
    baseHref,
    currentSort,
    facet,
    filters,
    onNavigate,
    query,
}: {
    baseHref: string;
    currentSort: CatalogSortValue;
    facet: PublicCatalogFacet;
    filters: CatalogFilterState;
    onNavigate?: CatalogNavigateHandler;
    query: string | null;
}) {
    const message = useShopStorefrontMessage();
    const { locale } = useShopStorefrontFormatting();
    const [expanded, setExpanded] = useState(false);
    const [optionQuery, setOptionQuery] = useState("");
    const normalizedQuery = optionQuery.trim().toLocaleLowerCase(locale);
    const options = normalizedQuery === ""
        ? facet.options
        : facet.options.filter((option) => option.label.toLocaleLowerCase(locale).includes(normalizedQuery));
    const visibleOptions = expanded || normalizedQuery !== "" ? options : visibleCatalogFacetOptions(options, 7);
    const hiddenCount = Math.max(0, options.length - visibleOptions.length);

    return (
        <div className="mt-3">
            {facet.options.length > 8 ? (
                <label className="mb-3 flex items-center border border-[var(--site-border)] bg-[var(--site-surface)] px-3 py-2 focus-within:border-[var(--site-primary)]">
                    <Icon name="search" className="h-3.5 w-3.5 shrink-0 text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]" />
                    <span className="sr-only">{message("ui.catalog.searchFacet", { name: facet.label })}</span>
                    <input
                        type="search"
                        value={optionQuery}
                        onChange={(event) => setOptionQuery(event.target.value)}
                        placeholder={message("ui.catalog.searchValue")}
                        className="min-w-0 flex-1 bg-transparent px-2 text-xs outline-none"
                    />
                </label>
            ) : null}
            <div className="space-y-1.5">
                {visibleOptions.map((option) => {
                    const href = catalogHref(
                        baseHref,
                        currentSort,
                        toggleCatalogFilter(filters, facet, option),
                        query,
                    );

                    return (
                        <a
                            key={option.uuid}
                            href={href}
                            onClick={(event) => handleCatalogNavigateClick(event, href, onNavigate)}
                            className={classNames(
                                "flex min-h-10 items-center justify-between gap-2 px-2 py-2 text-sm transition",
                                option.selected
                                    ? "bg-[var(--site-surface)] font-black text-[var(--site-primary)]"
                                    : "text-[color-mix(in_srgb,var(--site-text)_70%,transparent)] hover:bg-[var(--site-surface)] hover:text-[var(--site-text)]",
                            )}
                        >
                            <span className="inline-flex min-w-0 items-center gap-2">
                                <span
                                    className={classNames(
                                        "grid h-4 w-4 shrink-0 place-items-center border text-[var(--site-on-inverse)]",
                                        option.selected
                                            ? "border-[var(--site-primary)] bg-[var(--site-primary)]"
                                            : "border-[color-mix(in_srgb,var(--site-text)_25%,transparent)] bg-[var(--site-surface)]",
                                    )}
                                    aria-hidden="true"
                                >
                                    {option.selected ? <Icon name="check" className="h-3 w-3" /> : null}
                                </span>
                                {option.color ? (
                                    <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-[color-mix(in_srgb,var(--site-text)_15%,transparent)]" style={{ backgroundColor: option.color }} />
                                ) : null}
                                <span className="truncate">{option.label}</span>
                            </span>
                            {option.count > 0 ? (
                                <span className="rounded-full bg-[color-mix(in_srgb,var(--site-inverse)_6%,transparent)] px-2 py-0.5 text-xs font-black text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">{option.count}</span>
                            ) : null}
                        </a>
                    );
                })}
            </div>
            {hiddenCount > 0 ? (
                <button
                    type="button"
                    className="mt-2 text-xs font-black text-[var(--site-primary)] hover:underline"
                    onClick={() => setExpanded(true)}
                >
                    {message("ui.catalog.showMore", { count: hiddenCount })}
                </button>
            ) : null}
        </div>
    );
}

function catalogFacetSelectedCount(facet: PublicCatalogFacet): number {
    if (facet.type === "range") {
        return facet.selectedMin !== null || facet.selectedMax !== null ? 1 : 0;
    }

    return facet.options.filter((option) => option.selected).length;
}

function CatalogFacetSection({
    baseHref,
    collapsible = true,
    currentSort,
    facet,
    filters,
    index,
    onNavigate,
    presentationStyle = "default",
    query,
}: {
    baseHref: string;
    collapsible?: boolean;
    currentSort: CatalogSortValue;
    facet: PublicCatalogFacet;
    filters: CatalogFilterState;
    index: number;
    onNavigate?: CatalogNavigateHandler;
    presentationStyle?: CatalogFilterStyle;
    query: string | null;
}) {
    const selectedCount = catalogFacetSelectedCount(facet);
    const importantFacet = facet.code === "category" || facet.code === "manufacturer" || facet.code === "availability" || facet.type === "range";
    const [open, setOpen] = useState(selectedCount > 0 || importantFacet || index < 3);
    const content = (
        <div className="px-5 pb-5">
            {facet.type === "range" ? (
                <CatalogRangeFacet
                    baseHref={baseHref}
                    currentSort={currentSort}
                    facet={facet}
                    filters={filters}
                    onNavigate={onNavigate}
                    query={query}
                />
            ) : null}
            {facet.type === "terms" ? (
                <CatalogTermsFacet
                    baseHref={baseHref}
                    currentSort={currentSort}
                    facet={facet}
                    filters={filters}
                    onNavigate={onNavigate}
                    query={query}
                />
            ) : null}
        </div>
    );

    if (!collapsible) {
        return (
            <div
                className={classNames(
                    "catalog-filter-facet border-b border-[var(--site-border)] last:border-b-0",
                    presentationStyle === "utilityInline" && "min-w-[190px]",
                )}
                data-catalog-facet-presentation={presentationStyle}
            >
                <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2 p-5 text-sm font-black">
                    <span className="min-w-0 max-w-full [overflow-wrap:anywhere]">{facet.label}</span>
                    {selectedCount > 0 ? (
                        <span className="rounded-full bg-[var(--site-primary)] px-2 py-0.5 text-[10px] text-[var(--site-on-primary)]">{selectedCount}</span>
                    ) : null}
                </div>
                {content}
            </div>
        );
    }

    return (
        <div
            className={classNames(
                "catalog-filter-facet border-b border-[var(--site-border)] last:border-b-0",
                presentationStyle === "boutiqueSidebar" && "border-[#dedede] bg-transparent",
                presentationStyle === "playfulSidebar" && "border-[var(--site-border)] bg-transparent",
                presentationStyle === "structuredSidebar" && "border-[#dadada] bg-transparent",
                presentationStyle === "editorialExpand" && "border-[var(--site-text)]",
            )}
            data-catalog-facet-presentation={presentationStyle}
        >
            <button
                type="button"
                className="flex w-full min-w-0 max-w-full items-center justify-between gap-3 p-5 text-left text-sm font-black"
                aria-expanded={open}
                onClick={() => setOpen((current) => !current)}
            >
                <span className="inline-flex min-w-0 items-center gap-2">
                    <span className="min-w-0 max-w-full whitespace-normal [overflow-wrap:anywhere]">{facet.label}</span>
                    {selectedCount > 0 ? (
                        <span className="rounded-full bg-[var(--site-primary)] px-2 py-0.5 text-[10px] text-[var(--site-on-primary)]">{selectedCount}</span>
                    ) : null}
                </span>
                <Icon name={open ? "chevronDown" : "chevronRight"} className="h-4 w-4 shrink-0" />
            </button>
            {open ? content : null}
        </div>
    );
}

function CatalogFilterPanel({
    baseHref,
    catalogFacets,
    catalogTotal,
    collapseSections = true,
    currentSort,
    filters,
    onNavigate,
    presentationStyle = "default",
    query,
    saleLocked = false,
    title,
}: {
    baseHref: string;
    catalogFacets: PublicCatalogFacet[];
    catalogTotal: number;
    collapseSections?: boolean;
    currentSort: CatalogSortValue;
    filters: CatalogFilterState;
    onNavigate?: CatalogNavigateHandler;
    presentationStyle?: CatalogFilterStyle;
    query: string | null;
    saleLocked?: boolean;
    title?: string;
}) {
    const message = useShopStorefrontMessage();
    const { locale } = useShopStorefrontFormatting();
    const activeCount = activeCatalogFilterCount(filters, !saleLocked);
    const clearHref = clearCatalogFiltersHref(baseHref, currentSort, query);

    return (
        <div
            className={classNames(
                "catalog-filter-panel border border-[var(--site-border)] bg-[var(--site-surface)]",
                presentationStyle === "editorialExpand" && "border-x-0 border-[var(--site-text)] bg-transparent",
                presentationStyle === "utilityInline" && "border-0 bg-transparent",
                presentationStyle === "boutiqueSidebar" && "border-0 bg-[#f9f9f9] px-5",
                presentationStyle === "playfulSidebar" && "border-0 bg-transparent px-5",
                presentationStyle === "structuredSidebar" && "border-0 bg-transparent",
            )}
            data-catalog-filter-panel-presentation={presentationStyle}
        >
            <div className="catalog-filter-panel-header min-w-0 max-w-full border-b border-[var(--site-border)] p-5">
                <div className="flex min-w-0 max-w-full flex-wrap items-center justify-between gap-3">
                    <h2 className="min-w-0 max-w-full text-base font-black [overflow-wrap:anywhere]">{title ?? message("ui.catalog.filters")}</h2>
                    <span className="max-w-full rounded-full bg-[var(--site-surface)] px-3 py-1 text-right text-xs font-black text-[color-mix(in_srgb,var(--site-text)_55%,transparent)] [overflow-wrap:anywhere]">
                        {storefrontPluralMessage(catalogTotal, locale, CATALOG_PRODUCT_COUNT_MESSAGE_KEYS, message)}
                    </span>
                </div>
                {activeCount > 0 ? (
                    <a
                        href={clearHref}
                        onClick={(event) => handleCatalogNavigateClick(event, clearHref, onNavigate)}
                        className="mt-3 inline-flex max-w-full min-w-0 text-xs font-black text-[var(--site-primary)] whitespace-normal [overflow-wrap:anywhere] hover:underline"
                    >
                        {message("ui.catalog.clearFilters")} ({activeCount})
                    </a>
                ) : null}
            </div>
            {catalogFacets.length > 0 ? catalogFacets.map((facet, index) => (
                <CatalogFacetSection
                    key={facet.code}
                    baseHref={baseHref}
                    collapsible={collapseSections}
                    currentSort={currentSort}
                    facet={facet}
                    filters={filters}
                    index={index}
                    onNavigate={onNavigate}
                    presentationStyle={presentationStyle}
                    query={query}
                />
            )) : (
                <p className="p-5 text-sm text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">{message("ui.catalog.noFilters")}</p>
            )}
        </div>
    );
}

function CatalogMobileFilterDrawer({
    open,
    baseHref,
    categoryMenu,
    catalogFacets,
    catalogFilters,
    catalogQuery,
    catalogSort,
    catalogTotal,
    collapseSections = true,
    desktop = false,
    onClose,
    onNavigate,
    presentationStyle = "default",
    saleLocked = false,
    showFilters = true,
    showSearch = true,
}: {
    open: boolean;
    baseHref: string;
    categoryMenu?: {
        activeItemUuid?: string | null;
        activePathUuids?: ReadonlySet<string>;
        collapsed: boolean;
        items: readonly PublicShopMenuItem[];
        style: CatalogCategoryStyle;
    };
    catalogFacets: PublicCatalogFacet[];
    catalogFilters: CatalogFilterState;
    catalogQuery: string | null;
    catalogSort: CatalogSortValue;
    catalogTotal: number;
    collapseSections?: boolean;
    desktop?: boolean;
    onClose: () => void;
    onNavigate?: CatalogNavigateHandler;
    presentationStyle?: CatalogFilterStyle;
    saleLocked?: boolean;
    showFilters?: boolean;
    showSearch?: boolean;
}) {
    const message = useShopStorefrontMessage();
    const selectedFacetOptions = selectedCatalogFacetOptions(catalogFacets);
    const panelTitle = showFilters
        ? message("ui.catalog.filters")
        : message("ui.search.categories");
    const handleNavigate = onNavigate
        ? async (href: string) => {
            await onNavigate(href);
            onClose();
        }
        : undefined;

    return (
        <div className={classNames(
            "fixed inset-0 z-[70]",
            (!desktop || (desktop && presentationStyle === "editorialExpand")) && "lg:hidden",
            open ? "visible pointer-events-auto" : "invisible pointer-events-none",
        )} aria-hidden={!open}>
            <button
                type="button"
                aria-label={showFilters ? message("ui.catalog.closeFilters") : message("ui.common.close")}
                className={classNames("absolute inset-0 bg-[color-mix(in_srgb,var(--site-text)_45%,transparent)] transition", open ? "opacity-100" : "opacity-0")}
                onClick={onClose}
            />
            <aside
                className={classNames(
                    "absolute overflow-y-auto bg-[var(--site-background)] p-4 transition duration-200",
                    desktop
                        ? "bottom-0 right-0 top-0 w-[min(92vw,420px)] shadow-[-18px_0_44px_var(--site-shadow-color)]"
                        : "bottom-0 left-0 right-0 max-h-[88vh] shadow-[0_-18px_44px_var(--site-shadow-color)]",
                    open
                        ? desktop ? "translate-x-0" : "translate-y-0"
                        : desktop ? "translate-x-full" : "translate-y-full",
                    presentationStyle === "editorialExpand" && "rounded-none border-l border-[var(--site-text)]",
                    presentationStyle === "boutiqueSidebar" && "rounded-none",
                    presentationStyle === "playfulSidebar" && "rounded-[24px]",
                    presentationStyle === "structuredSidebar" && "rounded-none bg-white shadow-none ring-1 ring-[#dadada]",
                )}
                data-catalog-drawer-presentation={presentationStyle}
            >
                <div className="mb-4 flex min-w-0 max-w-full items-center justify-between gap-3">
                    <div className="min-w-0 max-w-full">
                        <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.catalog.catalog")}</p>
                        <h2 className="max-w-full text-xl font-black [overflow-wrap:anywhere]">{panelTitle}</h2>
                    </div>
                    <button type="button" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[var(--site-surface)]" aria-label={showFilters ? message("ui.catalog.closeFilters") : message("ui.common.close")} onClick={onClose}>
                        <Icon name="x" className="h-5 w-5" />
                    </button>
                </div>
                <div className="space-y-4">
                    {showFilters && showSearch ? (
                        <CatalogTextFilter
                            baseHref={baseHref}
                            currentSort={catalogSort}
                            filters={catalogFilters}
                            onNavigate={handleNavigate}
                            query={catalogQuery}
                        />
                    ) : null}
                    {showFilters ? (
                        <>
                            <SelectedCatalogFilters
                                baseHref={baseHref}
                                currentSort={catalogSort}
                                filters={catalogFilters}
                                onNavigate={handleNavigate}
                                query={catalogQuery}
                                saleLocked={saleLocked}
                                selectedOptions={selectedFacetOptions}
                            />
                            <CatalogFilterPanel
                                baseHref={baseHref}
                                catalogFacets={catalogFacets}
                                catalogTotal={catalogTotal}
                                collapseSections={collapseSections}
                                currentSort={catalogSort}
                                filters={catalogFilters}
                                onNavigate={handleNavigate}
                                presentationStyle={presentationStyle}
                                query={catalogQuery}
                                saleLocked={saleLocked}
                            />
                        </>
                    ) : null}
                    {categoryMenu ? (
                        <div className={desktop ? "lg:hidden" : undefined}>
                            <LagunaCatalogCategories
                                activeItemUuid={categoryMenu.activeItemUuid}
                                activePathUuids={categoryMenu.activePathUuids}
                                collapsed={categoryMenu.collapsed}
                                items={categoryMenu.items}
                                onNavigate={handleNavigate}
                                sidebar
                                style={categoryMenu.style}
                            />
                        </div>
                    ) : null}
                    <button
                        type="button"
                        className={classNames(
                            "w-full bg-[var(--site-primary)] px-5 py-4 text-sm font-black text-[var(--site-on-primary)]",
                            presentationStyle === "boutiqueSidebar"
                                ? "rounded-lg"
                                : presentationStyle === "playfulSidebar"
                                    ? "rounded-full"
                                    : presentationStyle === "structuredSidebar" || presentationStyle === "editorialExpand" ? "rounded-none" : "rounded-full",
                        )}
                        onClick={onClose}
                    >
                        {message("ui.catalog.showProducts")}
                    </button>
                </div>
            </aside>
        </div>
    );
}

function CategoryCatalogProductRail({
    addingProductUuid,
    block,
    context,
    favorites,
    onAddToCart,
    onToggleFavorite,
}: {
    addingProductUuid: string | null;
    block: ShopTemplateBlock;
    context: ShopPageContext;
    favorites: Set<string>;
    onAddToCart: (
        productUuid: string,
        productAttributeUuid?: string | null,
        quantity?: number,
        surchargeValueUuids?: string[],
    ) => void;
    onToggleFavorite: (productId: string) => void;
}) {
    const commerce = useContext(ShopCommerceContentContext);
    const config = categoryCatalogProductRailConfig(block);
    const categoryPath = pageContextPath(context);
    const requestKey = config && commerce?.resolveApiPath
        ? JSON.stringify([
            commerce.resolveApiPath,
            categoryPath,
            context.languageCode,
            commerce.currencyCode,
            config.sort,
            config.limit,
        ])
        : null;
    const [resolved, setResolved] = useState<{
        key: string;
        products: PublicHomeProduct[];
    } | null>(null);

    useEffect(() => {
        if (!config || !commerce?.resolveApiPath || requestKey === null) {
            return undefined;
        }

        const abortController = new AbortController();
        const params = categoryCatalogProductRailSearchParams({
            ...config,
            currencyCode: commerce.currencyCode,
            languageCode: context.languageCode,
            path: categoryPath,
        });
        const separator = commerce.resolveApiPath.includes("?") ? "&" : "?";

        void (async () => {
            try {
                const response = await fetch(
                    `${commerce.resolveApiPath}${separator}${params.toString()}`,
                    {
                        cache: "no-store",
                        credentials: "same-origin",
                        signal: abortController.signal,
                    },
                );

                if (!response.ok || abortController.signal.aborted) {
                    return;
                }

                const payload = await response.json() as ShopResolvePayload;
                const products = payload.pageContext
                    ? catalogProductsFromContext(payload.pageContext).slice(0, config.limit)
                    : [];

                if (!abortController.signal.aborted) {
                    setResolved({ key: requestKey, products });
                }
            } catch {
                // The rail is progressive enhancement; the primary catalog stays untouched.
            }
        })();

        return () => abortController.abort();
    }, [categoryPath, commerce?.currencyCode, commerce?.resolveApiPath, config?.limit, config?.sort, context.languageCode, requestKey]);

    const contextProducts = config && (
        !commerce?.resolveApiPath
        || catalogSortFromContext(context) === config.sort
    )
        ? catalogProductsFromContext(context).slice(0, config.limit)
        : [];
    const publicProducts = config
        ? resolved?.key === requestKey ? resolved.products : contextProducts
        : commerceProductsForBlock(block, context);

    if (config && publicProducts.length === 0) {
        return null;
    }

    return (
        <div data-category-catalog-product-rail={config?.sort}>
            <ProductRail
                addingProductUuid={addingProductUuid}
                block={block}
                favorites={favorites}
                liveProductsOnly={config !== null}
                onAddToCart={onAddToCart}
                onToggleFavorite={onToggleFavorite}
                publicProducts={publicProducts}
            />
        </div>
    );
}

function catalogMenuItemByUuids(
    items: readonly PublicShopMenuItem[],
    uuids: ReadonlySet<string>,
): PublicShopMenuItem | null {
    for (const item of items) {
        if (uuids.has(item.uuid) && functionalStorefrontHref(menuItemHref(item))) {
            return item;
        }

        const child = catalogMenuItemByUuids(item.children, uuids);

        if (child) return child;
    }

    return null;
}

function ProductDetailCatalogRail({
    block,
    categoryUuids,
    context,
    favorites,
    productUuid,
    sort,
    onToggleFavorite,
}: {
    block: ShopTemplateBlock;
    categoryUuids: readonly string[];
    context: ShopPageContext;
    favorites: Set<string>;
    productUuid: string;
    sort: CatalogProductSort;
    onToggleFavorite: (productId: string) => void;
}) {
    const commerce = useContext(ShopCommerceContentContext);
    const categoryUuidSet = useMemo(() => new Set(categoryUuids), [categoryUuids]);
    const categoryItem = useMemo(
        () => catalogMenuItemByUuids(commerce?.headerMenuItems ?? [], categoryUuidSet),
        [categoryUuidSet, commerce?.headerMenuItems],
    );
    const categoryPath = functionalStorefrontHref(categoryItem ? menuItemHref(categoryItem) : null);
    const requestKey = categoryPath && commerce?.resolveApiPath
        ? JSON.stringify([
            commerce.resolveApiPath,
            categoryPath,
            context.languageCode,
            commerce.currencyCode,
            sort,
        ])
        : null;
    const [resolved, setResolved] = useState<{
        key: string;
        products: PublicHomeProduct[];
    } | null>(null);

    useEffect(() => {
        if (!categoryPath || !commerce?.resolveApiPath || requestKey === null) {
            return undefined;
        }

        const abortController = new AbortController();
        const params = categoryCatalogProductRailSearchParams({
            currencyCode: commerce.currencyCode,
            languageCode: context.languageCode,
            limit: 5,
            path: categoryPath,
            sort,
        });
        const separator = commerce.resolveApiPath.includes("?") ? "&" : "?";

        void (async () => {
            try {
                const response = await fetch(
                    `${commerce.resolveApiPath}${separator}${params.toString()}`,
                    {
                        cache: "no-store",
                        credentials: "same-origin",
                        signal: abortController.signal,
                    },
                );

                if (!response.ok || abortController.signal.aborted) return;

                const payload = await response.json() as ShopResolvePayload;
                const products = payload.pageContext
                    ? catalogProductsFromContext(payload.pageContext)
                        .filter((product) => product.uuid !== productUuid)
                        .slice(0, 4)
                    : [];

                if (!abortController.signal.aborted) {
                    setResolved({ key: requestKey, products });
                }
            } catch {
                // Recommendation tabs are progressive enhancement; the product purchase flow remains available.
            }
        })();

        return () => abortController.abort();
    }, [categoryPath, commerce?.currencyCode, commerce?.resolveApiPath, context.languageCode, productUuid, requestKey, sort]);

    const products = resolved?.key === requestKey ? resolved.products : [];

    return products.length > 0 ? (
        <div data-product-detail-catalog-rail={sort}>
            <ProductRail
                block={block}
                favorites={favorites}
                liveProductsOnly
                onToggleFavorite={onToggleFavorite}
                publicProducts={products}
            />
        </div>
    ) : null;
}

function homeCatalogProductRailConfig(block: ShopTemplateBlock): {
    limit: number;
    saleOnly: boolean;
    sort: CatalogProductSort;
} {
    const source = blockOptionText(block, "catalogSource");
    const authoredSort = blockOptionText(block, "catalogSort");
    const sourceSort = source === "bestselling" || source === "newest" || source === "recommended"
        ? source
        : "";

    return {
        limit: Math.min(12, Math.max(1, Math.round(numericBlockOption(block, "catalogLimit", 8)))),
        saleOnly: source === "sale",
        sort: catalogProductSort(authoredSort || sourceSort || "newest"),
    };
}

function HomeCatalogProductRail({
    addingProductUuid,
    block,
    context,
    fallbackProducts,
    favorites,
    onAddToCart,
    onToggleFavorite,
}: {
    addingProductUuid: string | null;
    block: ShopTemplateBlock;
    context: ShopPageContext;
    fallbackProducts: PublicHomeProduct[];
    favorites: Set<string>;
    onAddToCart: (
        productUuid: string,
        productAttributeUuid?: string | null,
        quantity?: number,
        surchargeValueUuids?: string[],
    ) => void;
    onToggleFavorite: (productId: string) => void;
}) {
    const commerce = useContext(ShopCommerceContentContext);
    const config = homeCatalogProductRailConfig(block);
    const requestKey = commerce?.resolveApiPath
        ? JSON.stringify([
            commerce.resolveApiPath,
            context.languageCode,
            commerce.currencyCode,
            config.saleOnly,
            config.sort,
            config.limit,
        ])
        : null;
    const [resolved, setResolved] = useState<{
        key: string;
        products: PublicHomeProduct[];
    } | null>(null);

    useEffect(() => {
        if (!commerce?.resolveApiPath || requestKey === null) return undefined;

        const abortController = new AbortController();
        const params = categoryCatalogProductRailSearchParams({
            currencyCode: commerce.currencyCode,
            languageCode: context.languageCode,
            limit: config.limit,
            path: "/",
            sort: config.sort,
        });

        if (config.saleOnly) params.set("saleOnly", "true");

        const separator = commerce.resolveApiPath.includes("?") ? "&" : "?";

        void (async () => {
            try {
                const response = await fetch(
                    `${commerce.resolveApiPath}${separator}${params.toString()}`,
                    {
                        cache: "no-store",
                        credentials: "same-origin",
                        signal: abortController.signal,
                    },
                );

                if (!response.ok || abortController.signal.aborted) return;

                const payload = await response.json() as ShopResolvePayload;
                const products = payload.pageContext
                    ? homeProductsFromContext(payload.pageContext).slice(0, config.limit)
                    : [];

                if (!abortController.signal.aborted) {
                    setResolved({ key: requestKey, products });
                }
            } catch {
                // Keep the server-provided home products when the progressive query is unavailable.
            }
        })();

        return () => abortController.abort();
    }, [commerce?.currencyCode, commerce?.resolveApiPath, config.limit, config.saleOnly, config.sort, context.languageCode, requestKey]);

    const serverProducts = (config.saleOnly
        ? fallbackProducts.filter((product) => product.isOnSale)
        : fallbackProducts).slice(0, config.limit);
    const products = resolved?.key === requestKey ? resolved.products : serverProducts;

    return products.length > 0 ? (
        <ProductRail
            addingProductUuid={addingProductUuid}
            block={block}
            favorites={favorites}
            liveProductsOnly
            onAddToCart={onAddToCart}
            onToggleFavorite={onToggleFavorite}
            publicProducts={products}
        />
    ) : null;
}

function CategoryLayout({
    context,
    favorites,
    catalogUpdating,
    addingProductUuid,
    onAddToCart,
    onCatalogNavigate,
    onToggleFavorite,
    editorMode,
}: {
    context: ShopPageContext;
    favorites: Set<string>;
    catalogUpdating: boolean;
    addingProductUuid: string | null;
    onAddToCart: (
        productUuid: string,
        productAttributeUuid?: string | null,
        quantity?: number,
        surchargeValueUuids?: string[],
    ) => void;
    onCatalogNavigate?: CatalogNavigateHandler;
    onToggleFavorite: (productId: string) => void;
    editorMode: boolean;
}) {
    const data = nestedRecord(contextData(context), context.routeType === "section" ? "section" : "category");
    const title = textValue(data, "name", context.title);
    const descriptionShort = textValue(data, "descriptionShort", "");
    const fullDescription = textValue(data, "description", "");
    const description = descriptionShort || fullDescription || context.seoDescription || "";
    const entityType = context.routeType === "section" ? "section" : context.routeType === "category" ? "category" : null;
    const entityUuid = stringOrNull(data.uuid);
    const editableEntity: EditableEntityRenderContextValue | null = entityType && entityUuid ? {
        type: entityType,
        uuid: entityUuid,
        values: {
            description: fullDescription,
            descriptionShort,
            name: title,
            ...(entityType === "category" ? { nameMenu: textValue(data, "nameMenu", title) } : {}),
        },
    } : null;
    const blocks = blocksFor(context, [
        { type: "breadcrumb" },
        { type: "categoryHero" },
        { type: "systemSubcategoryList" },
        { type: "systemProductList" },
        { type: "seoText" },
    ]);
    const categoriesOwnedByProductPage = blocks.some((block) => (
        block.type === "systemProductList"
        && catalogProductPage(block) !== null
    ));
    const categoryHeroIndex = blocks.findIndex((block) => block.type === "categoryHero");
    const categoryHeroBlock = categoryHeroIndex >= 0 ? blocks[categoryHeroIndex] : undefined;
    const categoryProductRailIndex = blocks.findIndex((block) => categoryCatalogProductRailConfig(block) !== null);
    const categoryProductRailBlock = categoryProductRailIndex >= 0 ? blocks[categoryProductRailIndex] : undefined;
    const productListBlock = blocks.find((block) => block.type === "systemProductList");
    const productListConfig = catalogProductPageConfig(catalogProductPage(productListBlock));
    const integratedCommerceCategoryLayout = Boolean(
        categoryHeroBlock
        && productListBlock
        && productListConfig.categoriesPlacement === "both"
        && productListConfig.categoryStyle === "imageIndex"
        && productListConfig.filterStyle === "structuredSidebar"
        && productListConfig.filtersMode === "sidebar",
    );
    const integratedCategoryLayout = Boolean(
        categoryHeroBlock
        && productListBlock
        && (
            productListConfig.filterStyle === "editorialExpand"
            || productListConfig.filterStyle === "boutiqueSidebar"
            || productListConfig.filterStyle === "playfulSidebar"
            || integratedCommerceCategoryLayout
        ),
    );
    const integratedCategoryIntro = integratedCategoryLayout && categoryHeroBlock ? (
        <EditableBlockFrame
            block={categoryHeroBlock}
            editorMode={editorMode}
            index={categoryHeroIndex}
        >
            <PageIntro
                block={categoryHeroBlock}
                description={description}
                embedded
                entity={editableEntity}
                title={title}
            />
        </EditableBlockFrame>
    ) : null;
    const integratedCategoryRail = integratedCommerceCategoryLayout && categoryProductRailBlock ? (
        <EditableBlockFrame
            block={categoryProductRailBlock}
            editorMode={editorMode}
            index={categoryProductRailIndex}
        >
            <CategoryCatalogProductRail
                addingProductUuid={addingProductUuid}
                block={categoryProductRailBlock}
                context={context}
                favorites={favorites}
                onAddToCart={onAddToCart}
                onToggleFavorite={onToggleFavorite}
            />
        </EditableBlockFrame>
    ) : null;

    return (
        <EditableEntityRenderContext.Provider value={editableEntity}>
            <div
                data-category-commerce-presentation={integratedCommerceCategoryLayout ? "white" : undefined}
                style={integratedCommerceCategoryLayout ? WHITE_COMMERCE_PRESENTATION_STYLE : undefined}
            >
            {blocks.map((block, index) => {
                if (block.type === "breadcrumb") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <Breadcrumb
                                block={block}
                                current={title}
                                entity={editableEntity}
                                mobileHidden={
                                    productListConfig.filterStyle === "boutiqueSidebar"
                                    || productListConfig.filterStyle === "playfulSidebar"
                                }
                            />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "categoryHero") {
                    if (integratedCategoryLayout) return null;

                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <PageIntro
                                title={title}
                                description={description}
                                block={block}
                                entity={editableEntity}
                            />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "systemSubcategoryList") {
                    if (categoriesOwnedByProductPage) return null;

                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <CategorySection block={block} />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "productRail") {
                    if (integratedCategoryLayout && index === categoryProductRailIndex) return null;

                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <CategoryCatalogProductRail
                                addingProductUuid={addingProductUuid}
                                block={block}
                                context={context}
                                favorites={favorites}
                                onAddToCart={onAddToCart}
                                onToggleFavorite={onToggleFavorite}
                            />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "systemProductList") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <SystemProductListPreview
                                block={block}
                                context={context}
                                favorites={favorites}
                                catalogUpdating={catalogUpdating}
                                addingProductUuid={addingProductUuid}
                                onAddToCart={onAddToCart}
                                onCatalogNavigate={onCatalogNavigate}
                                onToggleFavorite={onToggleFavorite}
                                integratedCategoryIntro={integratedCategoryIntro}
                                integratedCategoryRail={integratedCategoryRail}
                            />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "seoText") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <EntitySeoText title={title} description={description} block={block} entity={editableEntity} />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "newsletter") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <Newsletter block={block} onSubmit={(event) => event.preventDefault()} />
                        </EditableBlockFrame>
                    );
                }

                return (
                    <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                        <BlockPlaceholder block={block} index={index} />
                    </EditableBlockFrame>
                );
            })}
            </div>
        </EditableEntityRenderContext.Provider>
    );
}

const PRODUCT_DETAIL_PRESENTATION_CSS = `
[data-product-detail-design] [data-product-detail-hero],[data-product-detail-design] [data-product-gallery-anatomy],[data-product-detail-design] [data-product-purchase-panel],[data-product-detail-design] [data-product-detail-price],[data-product-detail-design] [data-product-detail-purchase-strip],[data-product-detail-design] [data-product-detail-actions]{min-width:0;max-width:100%}
[data-product-detail-design] [data-product-purchase-panel] :is(h1,p,button),[data-product-detail-design] [data-product-detail-facts]>div,[data-product-detail-design] [data-product-detail-trust]>div{max-width:100%;overflow-wrap:anywhere}
[data-product-detail-design] [data-product-purchase-panel] button,[data-product-detail-design] [data-product-detail-actions] button{min-width:0;max-width:100%;white-space:normal;overflow-wrap:anywhere}
[data-product-detail-design] [data-product-detail-facts]>div,[data-product-detail-design] [data-product-detail-trust]>div{min-width:0}
[data-product-detail-design="editorialSplit"] [data-product-purchase-panel] button,[data-product-detail-design="editorialSplit"] [data-product-purchase-panel] input{border-radius:0!important}[data-product-detail-design="editorialSplit"] [data-product-detail-facts]>div,[data-product-detail-design="editorialSplit"] [data-product-detail-trust]>div,[data-product-detail-design="editorialSplit"] [data-product-review-card]{border-radius:0!important;border:1px solid var(--site-text)!important;background:transparent!important;box-shadow:none!important}
[data-product-detail-design="utilitySplit"] [data-product-purchase-panel] button,[data-product-detail-design="utilitySplit"] [data-product-purchase-panel] input{border-radius:0!important}[data-product-detail-design="utilitySplit"] [data-product-detail-facts]>div,[data-product-detail-design="utilitySplit"] [data-product-review-card]{border-radius:0!important;border:1px solid var(--site-border)!important;box-shadow:none!important}[data-product-detail-design="utilitySplit"] [data-product-detail-trust]{display:none}
[data-product-detail-design="boutiqueSplit"] [data-product-purchase-panel] button,[data-product-detail-design="boutiqueSplit"] [data-product-purchase-panel] input,[data-product-detail-design="boutiqueSplit"] [data-product-detail-tabs],[data-product-detail-design="boutiqueSplit"] [data-product-detail-tabs] button{border-radius:8px!important}[data-product-detail-design="boutiqueSplit"] [data-product-detail-facts]>div,[data-product-detail-design="boutiqueSplit"] [data-product-detail-trust]>div,[data-product-detail-design="boutiqueSplit"] [data-product-review-card]{border-radius:0!important;border:0!important;border-bottom:1px solid #e8e8e8!important;background:#fff!important;box-shadow:none!important}
[data-product-detail-design="commerceSplit"] [data-product-purchase-panel] button,[data-product-detail-design="commerceSplit"] [data-product-purchase-panel] input,[data-product-detail-design="commerceSplit"] [data-product-detail-tabs],[data-product-detail-design="commerceSplit"] [data-product-detail-tabs] button{border-radius:0!important;box-shadow:none!important}[data-product-detail-design="commerceSplit"] [data-product-detail-facts]{gap:0;border-top:1px solid #dadada}[data-product-detail-design="commerceSplit"] [data-product-detail-facts]>div{border:0!important;border-bottom:1px solid #dadada!important;border-radius:0!important;background:#fff!important;box-shadow:none!important}[data-product-detail-design="commerceSplit"] [data-product-detail-trust]>div,[data-product-detail-design="commerceSplit"] [data-product-review-card]{border:0!important;border-bottom:1px solid #dadada!important;border-radius:0!important;background:#fff!important;box-shadow:none!important}
[data-product-detail-design="editorialSplit"] [data-product-detail-hero]{max-width:1680px}
[data-product-detail-design="editorialSplit"] [data-product-gallery-anatomy]>div:nth-child(2){border:0!important}
[data-product-detail-design="editorialSplit"] [data-product-gallery-anatomy]>div:first-child{border-left:0!important;padding-left:0!important}
[data-product-detail-design="editorialSplit"] [data-product-gallery-anatomy]>div:first-child button{box-sizing:border-box!important}
[data-product-detail-design="editorialSplit"] [data-product-gallery-anatomy]>div:first-child button img{display:block!important;box-sizing:border-box!important;width:100%!important;max-width:100%!important;height:100%!important;max-height:100%!important;min-width:0!important;min-height:0!important}
[data-product-detail-design="editorialSplit"] [data-product-purchase-panel],[data-product-detail-design="boutiqueSplit"] [data-product-purchase-panel],[data-product-detail-design="commerceSplit"] [data-product-purchase-panel]{padding:0!important}
[data-product-detail-design="editorialSplit"] [data-product-purchase-panel]{border:0!important}
[data-product-detail-design="editorialSplit"] [data-product-purchase-panel] h1{font-size:32px!important;line-height:1.15!important}
[data-product-detail-design="boutiqueSplit"] [data-shop-block-type="systemProductSummary"] [data-product-summary-status],[data-product-detail-design="boutiqueSplit"] [data-shop-block-type="systemProductSummary"] [data-product-reference-row]{display:none!important}
[data-product-detail-design="boutiqueSplit"] [data-shop-block-type="systemProductSummary"] [data-product-brand]{margin-top:0!important}
[data-product-detail-design="boutiqueSplit"] [data-product-purchase-panel] h1{font-size:34px!important;line-height:1.15!important}
[data-product-detail-design="editorialSplit"] [data-product-detail-price]{border:0!important;padding:0!important}
[data-product-detail-design="editorialSplit"] [data-product-detail-price]>p:first-child{display:none}
[data-product-detail-design="editorialSplit"] [data-product-detail-price]{order:20}
[data-product-detail-design="editorialSplit"] [data-product-price-value]{font-size:30px!important}
[data-product-detail-design="boutiqueSplit"] [data-product-price-value]{font-size:48px!important;line-height:1!important}
[data-product-detail-design="commerceSplit"] [data-product-price-value]{font-size:36px!important;line-height:1.05!important}
[data-product-detail-design="editorialSplit"] [data-product-detail-facts],[data-product-detail-design="boutiqueSplit"] [data-product-detail-facts],[data-product-detail-design="commerceSplit"] [data-product-detail-facts]{display:none!important}
[data-product-detail-design="editorialSplit"] [data-shop-block-type="systemProductBuyBox"],[data-product-detail-design="boutiqueSplit"] [data-shop-block-type="systemProductBuyBox"],[data-product-detail-design="commerceSplit"] [data-shop-block-type="systemProductBuyBox"]{display:grid;grid-template-columns:112px minmax(0,1fr);column-gap:12px;align-items:stretch}
[data-product-detail-design="editorialSplit"] [data-shop-block-type="systemProductBuyBox"]>* ,[data-product-detail-design="boutiqueSplit"] [data-shop-block-type="systemProductBuyBox"]>* ,[data-product-detail-design="commerceSplit"] [data-shop-block-type="systemProductBuyBox"]>*{grid-column:1/-1}
[data-product-detail-design="editorialSplit"] [data-product-detail-purchase-strip],[data-product-detail-design="boutiqueSplit"] [data-product-detail-purchase-strip],[data-product-detail-design="commerceSplit"] [data-product-detail-purchase-strip]{grid-column:1!important;margin:24px 0 0!important;border:0!important;padding:0!important}
[data-product-detail-design="editorialSplit"] [data-product-detail-purchase-strip]{order:30}
[data-product-detail-design="editorialSplit"] [data-product-detail-actions],[data-product-detail-design="boutiqueSplit"] [data-product-detail-actions],[data-product-detail-design="commerceSplit"] [data-product-detail-actions]{grid-column:2!important;margin:24px 0 0!important;display:grid!important;grid-template-columns:minmax(0,1fr)!important;gap:0!important}
[data-product-detail-design="editorialSplit"] [data-product-detail-actions]{order:30}
[data-product-detail-design="editorialSplit"] [data-product-detail-actions]>button:first-child,[data-product-detail-design="boutiqueSplit"] [data-product-detail-actions]>button:first-child,[data-product-detail-design="commerceSplit"] [data-product-detail-actions]>button:first-child{min-height:56px;padding:0 24px!important}
[data-product-detail-design="editorialSplit"] [data-product-detail-actions]>button:last-child,[data-product-detail-design="boutiqueSplit"] [data-product-detail-actions]>button:last-child,[data-product-detail-design="commerceSplit"] [data-product-detail-actions]>button:last-child{display:none!important}
[data-product-detail-design="editorialSplit"] [data-product-detail-actions]>button:first-child{border:1px solid var(--site-primary)!important;background:transparent!important;color:var(--site-primary)!important}
[data-product-detail-design="editorialSplit"] [data-product-detail-purchase-strip]>div,[data-product-detail-design="boutiqueSplit"] [data-product-detail-purchase-strip]>div,[data-product-detail-design="commerceSplit"] [data-product-detail-purchase-strip]>div{display:block!important;margin:0!important}
[data-product-detail-design="editorialSplit"] [data-product-detail-purchase-strip]>div>p,[data-product-detail-design="boutiqueSplit"] [data-product-detail-purchase-strip]>div>p,[data-product-detail-design="commerceSplit"] [data-product-detail-purchase-strip]>div>p,[data-product-detail-design="editorialSplit"] [data-product-detail-purchase-strip]>div>div>p,[data-product-detail-design="boutiqueSplit"] [data-product-detail-purchase-strip]>div>div>p,[data-product-detail-design="commerceSplit"] [data-product-detail-purchase-strip]>div>div>p{display:none!important}
[data-product-detail-design="editorialSplit"] [data-product-detail-purchase-strip] .inline-flex,[data-product-detail-design="boutiqueSplit"] [data-product-detail-purchase-strip] .inline-flex,[data-product-detail-design="commerceSplit"] [data-product-detail-purchase-strip] .inline-flex{display:grid!important;width:112px;height:56px!important;grid-template-columns:32px 48px 32px;gap:0!important}
[data-product-detail-design="editorialSplit"] [data-product-detail-purchase-strip] button,[data-product-detail-design="boutiqueSplit"] [data-product-detail-purchase-strip] button,[data-product-detail-design="commerceSplit"] [data-product-detail-purchase-strip] button{width:32px!important;height:56px!important;padding:0!important}
[data-product-detail-design="editorialSplit"] [data-product-detail-purchase-strip] input,[data-product-detail-design="boutiqueSplit"] [data-product-detail-purchase-strip] input,[data-product-detail-design="commerceSplit"] [data-product-detail-purchase-strip] input{width:48px!important;height:56px!important;border-left:0!important;border-right:0!important}
[data-product-detail-design="editorialSplit"] [data-product-detail-facts]{order:40}
[data-product-detail-design="editorialSplit"] [data-product-detail-trust],[data-product-detail-design="boutiqueSplit"] [data-product-detail-trust],[data-product-detail-design="commerceSplit"] [data-product-detail-trust]{grid-column:1/-1!important;grid-template-columns:repeat(4,minmax(0,1fr))!important;border-top:1px solid var(--site-border);padding-top:20px}
[data-product-detail-design="editorialSplit"] [data-product-detail-trust]{grid-template-columns:repeat(2,minmax(0,1fr))!important}
[data-product-detail-design="editorialSplit"] [data-product-detail-trust]{order:50}
[data-product-detail-design="editorialSplit"] [data-product-detail-trust]>* ,[data-product-detail-design="boutiqueSplit"] [data-product-detail-trust]>* ,[data-product-detail-design="commerceSplit"] [data-product-detail-trust]>*{min-width:0;border:0!important;border-radius:0!important;background:transparent!important;padding:4px 0!important;box-shadow:none!important}
[data-product-detail-design="boutiqueSplit"] [data-product-detail-benefits]{max-width:1680px}
[data-product-detail-design="boutiqueSplit"] [data-product-detail-description-body]{max-width:1680px}
[data-product-detail-design="boutiqueSplit"] [data-product-detail-tab-content]{margin-top:28px;border-radius:8px;background:#fff;box-shadow:0 8px 30px rgba(0,0,0,.08)}
[data-product-detail-design="boutiqueSplit"] [data-product-detail-tab-content]>section{max-width:none!important;padding:40px!important}
[data-product-detail-design="editorialSplit"] [data-product-detail-description-body]{display:grid;grid-template-columns:280px minmax(0,1fr);column-gap:56px;align-items:start}
[data-product-detail-design="editorialSplit"] [data-product-detail-tabs]{grid-column:1;border:0!important;border-right:1px solid var(--site-text)!important;padding:0 28px 0 0!important}
[data-product-detail-design="editorialSplit"] [data-product-detail-tab-content]{grid-column:2;grid-row:1;margin:0}
[data-product-detail-design="editorialSplit"] [data-product-detail-tab-content]>section{max-width:none!important;padding:0!important}

/* Portable flat playful detail. It follows the selected detail design only;
   it never promotes an unrelated storefront to the full playful profile. */
[data-product-detail-design="playfulSplit"]{
  --playful-aqua:var(--kids-aqua,var(--site-accent,#c3e1db));
  --playful-coral:var(--kids-coral,var(--site-primary,#ec6830));
  --playful-coral-strong:var(--kids-coral-strong,var(--site-primary,#c94b1f));
  --playful-blush:var(--kids-blush,var(--site-accent-soft,#ffd7cd));
  --playful-ink:var(--kids-ink,var(--site-text,#444));
  --playful-muted:var(--kids-muted,var(--site-muted,#77736b));
  --playful-line:var(--kids-line,var(--site-border,#e2d9c7));
  color:var(--playful-ink)
}
section[data-product-detail-hero-section][data-product-detail-design="playfulSplit"]{background:#fff!important;padding:88px 24px 72px!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-hero]{width:min(100%,1300px)!important;max-width:1300px!important;grid-template-columns:minmax(0,750px) minmax(0,510px)!important;gap:40px!important;align-items:start!important}
[data-product-detail-design="playfulSplit"] [data-product-gallery-anatomy]{display:grid!important;width:100%!important;grid-template-columns:180px minmax(0,548px)!important;gap:22px!important;align-items:start!important}
[data-product-detail-design="playfulSplit"] [data-product-gallery-anatomy]>div:first-child{display:flex!important;grid-column:1!important;grid-row:1!important;max-height:582px!important;flex-direction:column!important;gap:24px!important;overflow-y:auto!important;overflow-x:hidden!important;border:0!important;padding:0!important}
[data-product-detail-design="playfulSplit"] [data-product-gallery-anatomy]>div:first-child>button{box-sizing:border-box!important;width:180px!important;min-width:180px!important;height:178px!important;min-height:178px!important;margin:0!important;border:0!important;border-radius:4px!important;background:#f7f7f7!important;padding:0!important;box-shadow:none!important;flex:none!important}
[data-product-detail-design="playfulSplit"] [data-product-gallery-anatomy]>div:first-child>button:focus,[data-product-detail-design="playfulSplit"] [data-product-gallery-anatomy]>div:first-child>button[class*="border-[var(--site-primary)]"]{border:0!important;outline:2px dotted var(--playful-coral)!important;outline-offset:-2px!important}
[data-product-detail-design="playfulSplit"] [data-product-gallery-anatomy]>div:first-child>button img{width:100%!important;height:100%!important;border-radius:4px!important;object-fit:cover!important}
[data-product-detail-design="playfulSplit"] [data-product-gallery-anatomy]>div:nth-child(2){grid-column:2!important;grid-row:1!important;width:100%!important;overflow:hidden!important;border-radius:4px!important;background:#f7f7f7!important;box-shadow:none!important}
[data-product-detail-design="playfulSplit"] [data-product-gallery-anatomy]>div:nth-child(2) img{display:block!important;width:100%!important;height:582px!important;max-height:582px!important;min-height:582px!important;aspect-ratio:auto!important;border-radius:4px!important;padding:0!important;object-fit:contain!important}
[data-product-detail-design="playfulSplit"] [data-product-purchase-panel]{box-sizing:border-box!important;overflow:visible!important;border:0!important;border-radius:0!important;background:#fff!important;padding:6px 0 0!important;box-shadow:none!important}
[data-product-detail-design="playfulSplit"] [data-product-purchase-panel] :is(h1,p,span,button){max-width:100%!important;overflow-wrap:anywhere!important;white-space:normal!important}
[data-product-detail-design="playfulSplit"] [data-product-summary-status],[data-product-detail-design="playfulSplit"] [data-product-reference-row]{display:none!important}
[data-product-detail-design="playfulSplit"] [data-product-purchase-panel] h1{margin-top:0!important;color:var(--playful-ink)!important;font-size:47px!important;font-weight:900!important;letter-spacing:-.045em!important;line-height:1.05!important}
[data-product-detail-design="playfulSplit"] [data-product-purchase-panel] h1+div{margin-top:15px!important}
[data-product-detail-design="playfulSplit"] [data-product-purchase-panel] h1~p{color:var(--playful-muted)!important;font-size:14px!important;line-height:1.7!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-price]{margin-top:14px!important;border:0!important;padding:0!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-price]>p:first-child{display:none!important}
[data-product-detail-design="playfulSplit"] [data-product-price-value]{margin-top:0!important;color:var(--playful-coral)!important;font-size:18px!important;font-weight:700!important;line-height:1.3!important}
[data-product-detail-design="playfulSplit"] [data-shop-block-type="systemProductBuyBox"]{display:grid!important;grid-template-columns:120px minmax(0,1fr)!important;column-gap:12px!important;align-items:stretch!important}
[data-product-detail-design="playfulSplit"] [data-shop-block-type="systemProductBuyBox"]>:not([data-product-detail-purchase-strip]):not([data-product-detail-actions]){grid-column:1/3!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-purchase-strip]{grid-column:1!important;margin-top:24px!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-purchase-strip]>div{min-height:50px!important;margin:0!important;align-items:center!important;gap:0!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-purchase-strip]>div>p,[data-product-detail-design="playfulSplit"] [data-product-detail-purchase-strip]>div>div>p{display:none!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-purchase-strip] .inline-flex{display:grid!important;width:120px!important;height:50px!important;grid-template-columns:36px 48px 36px!important;gap:0!important;overflow:hidden!important;border:2px dotted var(--playful-coral)!important;border-radius:0!important;background:#fff!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-purchase-strip] :is(button,input){height:46px!important;border:0!important;border-radius:0!important;background:transparent!important;color:var(--playful-ink)!important;box-shadow:none!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-purchase-strip] button{width:36px!important;min-width:36px!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-purchase-strip] input{width:48px!important;min-width:48px!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-actions]{grid-column:2!important;margin-top:24px!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-actions]>button:first-child{box-sizing:border-box!important;width:100%!important;max-width:100%!important;min-height:50px!important;border:2px dotted var(--playful-coral-strong)!important;border-radius:0!important;background:var(--playful-blush)!important;padding:8px 18px!important;color:var(--playful-coral-strong)!important;font-size:12px!important;font-weight:900!important;text-transform:uppercase!important;box-shadow:none!important;white-space:normal!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-actions]>button+button{display:none!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-facts]{display:block!important;margin-top:28px!important;border:0!important;padding:0!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-facts]>div{display:none!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-facts]>div:nth-child(3){display:flex!important;align-items:baseline!important;gap:8px!important;border:0!important;border-radius:0!important;background:transparent!important;padding:0!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-facts]>div:nth-child(3) p{margin:0!important;font-size:12px!important;line-height:1.5!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-trust]{display:none!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-benefits],[data-product-detail-design="playfulSplit"] [data-product-detail-description-body]{width:min(calc(100% - 48px),1300px)!important;max-width:1300px!important;margin-inline:auto!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-description-body]{width:min(calc(100% - 48px),1000px)!important;padding-top:8px!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-tabs]{overflow:visible!important;border:0!important;border-bottom:2px dotted var(--playful-coral)!important;border-radius:0!important;background:transparent!important;padding:0!important;box-shadow:none!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-tabs]>nav{display:flex!important;max-width:100%!important;justify-content:center!important;gap:64px!important;overflow:visible!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-tabs] button{position:relative!important;isolation:isolate;min-width:max-content!important;min-height:54px!important;border:0!important;border-radius:0!important;background:transparent!important;padding:8px 10px!important;color:var(--playful-ink)!important;font-size:16px!important;box-shadow:none!important;white-space:normal!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-tabs] button svg{display:none!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-tabs] button::before{position:absolute;z-index:-1;top:50%;left:50%;width:48px;height:44px;border-radius:48% 52% 45% 55%/58% 44% 56% 42%;background:var(--playful-blush);content:"";opacity:0;transform:translate(-50%,-50%) rotate(-5deg)}
[data-product-detail-design="playfulSplit"] [data-product-detail-tabs] button[aria-selected="true"]{background:transparent!important;color:var(--playful-ink)!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-tabs] button[aria-selected="true"]::before,[data-product-detail-design="playfulSplit"] [data-product-detail-tabs] button[class~="text-[var(--site-primary)]"]::before{opacity:1!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-tab-content]{min-width:0!important;margin-top:0!important;border:0!important;border-radius:0!important;background:#fff!important;box-shadow:none!important;text-align:center!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-tab-content]>section{max-width:none!important;padding:28px 0 44px!important}
[data-product-detail-design="playfulSplit"] [data-product-detail-tab-content] :is(h1,h2,h3,h4,p,li,a,span){max-width:100%!important;overflow-wrap:anywhere!important;white-space:normal!important}
section[data-product-detail-design="playfulSplit"]{background:#fff!important}

@media(min-width:1024px){[data-product-detail-design="editorialSplit"] [data-product-gallery-anatomy]{box-sizing:border-box!important;padding-right:8px!important}}
@media(max-width:1023px){[data-product-detail-design="editorialSplit"] [data-product-detail-hero],[data-product-detail-design="boutiqueSplit"] [data-product-detail-hero]{gap:24px}[data-product-detail-design="editorialSplit"] [data-product-purchase-panel]>[data-shop-block-type="systemProductSummary"],[data-product-detail-design="boutiqueSplit"] [data-product-purchase-panel]>[data-shop-block-type="systemProductSummary"],[data-product-detail-design="playfulSplit"] [data-product-purchase-panel]>[data-shop-block-type="systemProductSummary"]{display:none!important}[data-product-detail-design="editorialSplit"] [data-product-detail-description-body]{display:block}[data-product-detail-design="editorialSplit"] [data-product-detail-tabs]{border-right:0!important;border-bottom:1px solid var(--site-text)!important;padding:0 0 8px!important}[data-product-detail-design="editorialSplit"] [data-product-detail-tab-content]{margin-top:28px}[data-product-detail-design="boutiqueSplit"] [data-product-detail-tab-content]>section{padding:28px 20px!important}[data-product-detail-design="playfulSplit"] [data-product-detail-hero]{grid-template-columns:minmax(0,1fr)!important;gap:28px!important}[data-product-detail-design="playfulSplit"] [data-product-gallery-anatomy]>div:nth-child(2) img{height:min(582px,70vw)!important;min-height:360px!important}}
@media(max-width:639px){section[data-product-detail-hero-section][data-product-detail-design="editorialSplit"]{padding-top:20px!important}[data-product-detail-design="editorialSplit"] [data-shop-block-type="systemProductBuyBox"],[data-product-detail-design="boutiqueSplit"] [data-shop-block-type="systemProductBuyBox"],[data-product-detail-design="commerceSplit"] [data-shop-block-type="systemProductBuyBox"]{grid-template-columns:104px minmax(0,1fr);column-gap:8px}[data-product-detail-design="editorialSplit"] [data-product-detail-purchase-strip] .inline-flex,[data-product-detail-design="boutiqueSplit"] [data-product-detail-purchase-strip] .inline-flex,[data-product-detail-design="commerceSplit"] [data-product-detail-purchase-strip] .inline-flex{width:104px;grid-template-columns:30px 44px 30px}[data-product-detail-design="editorialSplit"] [data-product-detail-purchase-strip] button,[data-product-detail-design="boutiqueSplit"] [data-product-detail-purchase-strip] button,[data-product-detail-design="commerceSplit"] [data-product-detail-purchase-strip] button{width:30px!important}[data-product-detail-design="editorialSplit"] [data-product-detail-purchase-strip] input,[data-product-detail-design="boutiqueSplit"] [data-product-detail-purchase-strip] input,[data-product-detail-design="commerceSplit"] [data-product-detail-purchase-strip] input{width:44px!important}[data-product-detail-design="editorialSplit"] [data-product-detail-actions]>button:first-child,[data-product-detail-design="boutiqueSplit"] [data-product-detail-actions]>button:first-child,[data-product-detail-design="commerceSplit"] [data-product-detail-actions]>button:first-child{padding:0 12px!important}[data-product-detail-design="editorialSplit"] [data-product-detail-trust],[data-product-detail-design="boutiqueSplit"] [data-product-detail-trust],[data-product-detail-design="commerceSplit"] [data-product-detail-trust]{grid-template-columns:repeat(2,minmax(0,1fr))!important}[data-product-detail-design="boutiqueSplit"] [data-product-detail-tab-content]{margin-top:20px}}
@media(max-width:767px){section[data-product-detail-hero-section][data-product-detail-design="playfulSplit"]{padding:48px 16px 52px!important}[data-product-detail-design="playfulSplit"] [data-product-detail-hero]{width:300px!important;max-width:100%!important;margin-inline:auto!important}[data-product-detail-design="playfulSplit"] [data-product-gallery-anatomy]{order:1!important;grid-template-columns:minmax(0,1fr)!important;gap:12px!important}[data-product-detail-design="playfulSplit"] [data-product-mobile-summary]{order:2!important}[data-product-detail-design="playfulSplit"] [data-product-mobile-description]{order:3!important}[data-product-detail-design="playfulSplit"] [data-product-purchase-panel]{order:4!important;border-radius:0!important;padding:0!important}[data-product-detail-design="playfulSplit"] [data-product-gallery-anatomy]>div:nth-child(2){grid-column:1!important;grid-row:1!important;border-radius:4px!important}[data-product-detail-design="playfulSplit"] [data-product-gallery-anatomy]>div:nth-child(2) img{height:300px!important;min-height:300px!important;border-radius:4px!important;padding:0!important}[data-product-detail-design="playfulSplit"] [data-product-gallery-anatomy]>div:first-child{display:flex!important;grid-column:1!important;grid-row:2!important;max-height:none!important;flex-direction:row!important;gap:12px!important;overflow-x:auto!important;overflow-y:hidden!important;padding:0!important}[data-product-detail-design="playfulSplit"] [data-product-gallery-anatomy]>div:first-child>button{width:92px!important;min-width:92px!important;height:80px!important;min-height:80px!important;border-radius:4px!important}[data-product-detail-design="playfulSplit"] [data-product-purchase-panel] h1{font-size:32px!important}[data-product-detail-design="playfulSplit"] [data-shop-block-type="systemProductBuyBox"]{grid-template-columns:96px minmax(0,1fr)!important;column-gap:10px!important}[data-product-detail-design="playfulSplit"] [data-product-detail-purchase-strip]{grid-column:1!important;margin-top:20px!important}[data-product-detail-design="playfulSplit"] [data-product-detail-purchase-strip] .inline-flex{width:96px!important;height:48px!important;grid-template-columns:32px 32px 32px!important}[data-product-detail-design="playfulSplit"] [data-product-detail-purchase-strip] :is(button,input){height:44px!important}[data-product-detail-design="playfulSplit"] [data-product-detail-purchase-strip] button{width:32px!important;min-width:32px!important}[data-product-detail-design="playfulSplit"] [data-product-detail-purchase-strip] input{width:32px!important;min-width:32px!important}[data-product-detail-design="playfulSplit"] [data-product-detail-actions]{grid-column:2!important;margin-top:20px!important}[data-product-detail-design="playfulSplit"] [data-product-detail-actions]>button:first-child{min-height:48px!important;padding-inline:8px!important;font-size:10px!important}[data-product-detail-design="playfulSplit"] [data-product-detail-benefits],[data-product-detail-design="playfulSplit"] [data-product-detail-description-body]{width:300px!important;max-width:calc(100% - 32px)!important}[data-product-detail-design="playfulSplit"] [data-product-detail-description-body]{padding-top:0!important}[data-product-detail-design="playfulSplit"] [data-product-detail-tabs]{border-radius:0!important}[data-product-detail-design="playfulSplit"] [data-product-detail-tabs]>nav{display:flex!important;flex-direction:column!important;align-items:flex-start!important;gap:0!important;overflow:visible!important}[data-product-detail-design="playfulSplit"] [data-product-detail-tabs] button{width:auto!important;min-width:0!important;min-height:34px!important;padding:4px 8px!important;font-size:14px!important;text-align:left!important}[data-product-detail-design="playfulSplit"] [data-product-detail-tab-content]{border-radius:0!important;text-align:left!important}[data-product-detail-design="playfulSplit"] [data-product-detail-tab-content]>section{padding:22px 0 36px!important;font-size:13px!important;line-height:1.6!important}}
@media(max-width:480px){[data-product-detail-design="playfulSplit"] [data-shop-block-type="systemProductBuyBox"]{grid-template-columns:96px minmax(0,1fr)!important;column-gap:8px!important}[data-product-detail-design="playfulSplit"] [data-product-detail-purchase-strip] .inline-flex{width:96px!important;grid-template-columns:32px 32px 32px!important}[data-product-detail-design="playfulSplit"] [data-product-detail-purchase-strip] button{width:32px!important;min-width:32px!important}[data-product-detail-design="playfulSplit"] [data-product-detail-purchase-strip] input{width:32px!important;min-width:32px!important}}
@media(max-width:360px){[data-product-detail-design="boutiqueSplit"] [data-product-price-value]{font-size:clamp(2rem,11vw,48px)!important}}
`;

const WHITE_COMMERCE_PRESENTATION_STYLE = {
    "--site-background": "#ffffff",
    "--site-border": "#dadada",
    "--site-muted": "#666666",
    "--site-surface": "#ffffff",
    "--site-surface-elevated": "#f6f6f6",
    "--site-text": "#111111",
    color: "#111111",
} as CSSProperties;

function ProductLayout({
    context,
    favorites,
    onToggleFavorite,
    addingProductUuid,
    onAddToCart,
    editorMode,
}: {
    context: ShopPageContext;
    favorites: Set<string>;
    onToggleFavorite: (productId: string) => void;
    addingProductUuid: string | null;
    onAddToCart: (
        productUuid: string,
        productAttributeUuid?: string | null,
        quantity?: number,
        surchargeValueUuids?: string[],
        manufacturingSpecification?: PublicManufacturingSpecification,
    ) => void;
    editorMode: boolean;
}) {
    const message = useShopStorefrontMessage();
    const { formatMoney, formatNumber, locale } = useShopStorefrontFormatting();
    const data = nestedRecord(contextData(context), "product");
    const productUuid = textValue(data, "uuid", "");
    const title = textValue(data, "name", context.title);
    const price = textValue(data, "price", "1299.00");
    const priceDefault = textValue(data, "priceDefault", price);
    const descriptionShort = textValue(data, "descriptionShort", "");
    const fullDescription = textValue(data, "description", "");
    const description = descriptionShort || fullDescription || context.seoDescription || "";
    const reference = textValue(data, "reference", "");
    const manufacturerName = textValue(data, "manufacturerName", "");
    const categoryUuids = useMemo(
        () => Array.isArray(data.categoryUuids)
            ? data.categoryUuids.flatMap((value) => {
                const uuid = stringOrNull(value);

                return uuid ? [uuid] : [];
            })
            : [],
        [data.categoryUuids],
    );
    const isOnSale = data.isOnSale === true;
    const availableForOrder = data.availableForOrder !== false;
    const trackInventory = data.trackInventory !== false;
    const stockQuantity = typeof data.stockQuantity === "number" ? data.stockQuantity : null;
    const reservedQuantity = typeof data.reservedQuantity === "number" ? data.reservedQuantity : null;
    const backendAvailableQuantity = typeof data.availableQuantity === "number" ? data.availableQuantity : null;
    const availableQuantity = backendAvailableQuantity ?? (stockQuantity === null || reservedQuantity === null
        ? null
        : Math.max(0, stockQuantity - reservedQuantity));
    const hasSellableStock = !trackInventory || availableQuantity === null || availableQuantity > 0;
    const variants = useMemo(() => productVariantsFromData(data, locale), [data, locale]);
    const rootSurchargeParameters = useMemo(() => productSurchargeParametersFromData(data.surchargeParameters, locale), [data.surchargeParameters, locale]);
    const variantGroups = useMemo(() => productVariantGroupsFromVariants(variants, locale), [variants, locale]);
    const [selectedVariantUuid, setSelectedVariantUuid] = useState<string | null>(() => (
        variants.find((variant) => variant.canAddToCart)?.uuid ?? variants[0]?.uuid ?? null
    ));
    const selectedVariant = variants.find((variant) => variant.uuid === selectedVariantUuid) ?? variants[0] ?? null;
    const surchargeParameters = selectedVariant?.surchargeParameters ?? rootSurchargeParameters;
    const [selectedSurchargeValueUuidsByParameter, setSelectedSurchargeValueUuidsByParameter] = useState<Record<string, string>>({});
    const selectedVariantSelection = useMemo(
        () => selectedVariantItemsMap(selectedVariant),
        [selectedVariant],
    );
    const selectedVariantItemUuids = useMemo(
        () => new Set(selectedVariantSelection.values()),
        [selectedVariantSelection],
    );
    const selectedTrackInventory = selectedVariant?.trackInventory ?? trackInventory;
    const selectedAvailableQuantity = selectedVariant?.availableQuantity ?? availableQuantity;
    const selectedQuantityMin = Math.max(1, selectedVariant?.minimalQuantity ?? 1);
    const selectedStockMaxQuantity = selectedTrackInventory && selectedAvailableQuantity !== null
        ? Math.max(0, Math.min(CART_ITEM_MAX_QUANTITY, selectedAvailableQuantity))
        : CART_ITEM_MAX_QUANTITY;
    const selectedQuantityMax = Math.max(selectedQuantityMin, selectedStockMaxQuantity);
    const [quantity, setQuantity] = useState(1);
    const selectedSurchargeValues = useMemo(() => {
        return surchargeParameters.flatMap((parameter) => {
            const valueUuid = selectedSurchargeValueUuidsByParameter[parameter.uuid];
            const value = parameter.values.find((item) => item.uuid === valueUuid);

            return value ? [value] : [];
        });
    }, [selectedSurchargeValueUuidsByParameter, surchargeParameters]);
    const selectedSurchargeValueUuids = useMemo(
        () => selectedSurchargeValues.map((value) => value.uuid),
        [selectedSurchargeValues],
    );
    const customization = useManufacturingCustomization({
        editorMode,
        modifierOptionUuids: selectedSurchargeValueUuids,
        productUuid,
        variantUuid: selectedVariant?.uuid ?? null,
    });
    const requiredSurchargesSelected = surchargeParameters.every((parameter) => (
        !parameter.required || parameter.values.some((value) => value.uuid === selectedSurchargeValueUuidsByParameter[parameter.uuid])
    ));
    const selectedSurchargesAvailable = selectedSurchargeValues.every((value) => surchargeValueSelectable(value, quantity));
    const selectedCanAddToCart = availableForOrder
        && (selectedVariant ? selectedVariant.canAddToCart : hasSellableStock)
        && selectedStockMaxQuantity >= selectedQuantityMin
        && requiredSurchargesSelected
        && selectedSurchargesAvailable
        && customization.canSubmit
        && productUuid !== "";
    const selectedStockStatusText = !availableForOrder
        ? message("ui.product.unavailable")
        : !selectedTrackInventory || selectedAvailableQuantity === null
            ? message("ui.product.inStock")
            : selectedAvailableQuantity > 0 ? message("ui.product.inStock") : message("ui.product.outOfStock");
    const selectedStockDetailText = !availableForOrder
        ? message("ui.product.unavailable")
        : !selectedTrackInventory || selectedAvailableQuantity === null
            ? message("ui.product.inStock")
            : selectedAvailableQuantity > 0
                ? message("ui.product.inStockCount", { count: selectedAvailableQuantity })
                : message("ui.product.outOfStock");
    const addToCartNotice = !requiredSurchargesSelected
        ? message("ui.product.requiredSurcharge")
        : !selectedSurchargesAvailable
            ? message("ui.product.surchargeUnavailable")
        : selectedCanAddToCart ? message("ui.product.readyToOrder") : message("ui.product.combinationUnavailable");
    const adding = addingProductUuid === productUuid;
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [productDetailTab, setProductDetailTab] = useState<ProductDetailTab>("overview");
    const displayPrice = moneyWithAdditions(selectedVariant?.price ?? price, selectedSurchargeValues.map((value) => value.salePrice));
    const previousPriceAmount = moneyAmount(priceDefault);
    const displayPriceAmount = moneyAmount(displayPrice);
    const previousPrice = isOnSale && !selectedVariant && previousPriceAmount > displayPriceAmount
        ? priceDefault
        : null;
    const discountPercent = previousPrice
        ? Math.max(1, Math.round((1 - displayPriceAmount / previousPriceAmount) * 100))
        : null;
    const productGalleryImages = selectedVariant?.images.length ? selectedVariant.images : productImagesFromData(data);
    const galleryImages = productGalleryImages.length > 0
        ? productGalleryImages.map((image) => ({
            alt: image.alt || title,
            id: image.mediaUuid,
            image: image.url,
        }))
        : editorMode ? products.slice(0, 4) : [];
    const mainProductImage = galleryImages[selectedImageIndex] ?? galleryImages[0] ?? null;
    const displayReference = selectedVariant?.reference ?? reference;
    const editableProduct: EditableEntityRenderContextValue | null = productUuid ? {
        type: "product",
        uuid: productUuid,
        values: {
            description: fullDescription,
            descriptionShort,
            formattedPrice: formatMoney(displayPrice),
            name: title,
            reference: displayReference,
        },
    } : null;
    const reviewRating = numberOrNull(data.reviewRating) ?? 0;
    const reviewCount = numberOrNull(data.reviewCount) ?? 0;
    const reviews = useMemo(() => productReviewsFromValue(data.reviews), [data.reviews]);
    const salesCount = numberOrNull(data.salesCount) ?? (editorMode ? 0 : null);
    const reviewDistribution = useMemo(
        () => productReviewDistributionFromValue(data.reviewRatingDistribution),
        [data.reviewRatingDistribution],
    );
    const reviewDistributionTotal = Object.values(reviewDistribution).reduce((total, count) => total + count, 0);
    const recommendationPercent = reviewDistributionTotal > 0
        ? productRecommendationPercent(reviewDistribution)
        : editorMode ? 0 : null;
    const complaintRate = numberOrNull(data.complaintRate)
        ?? (editorMode ? productComplaintRate(productUuid, reviewCount) : null);
    const reviewStars = Math.max(0, Math.min(5, Math.round(reviewRating)));

    function clampProductQuantity(nextQuantity: number): number {
        return clampQuantity(nextQuantity, selectedQuantityMin, selectedQuantityMax);
    }

    function selectVariantOption(option: PublicProductVariantItem) {
        const selection = selectedVariantItemsMap(selectedVariant);
        selection.set(option.attributeUuid, option.uuid);

        const nextVariant = bestVariantForSelection(variants, selection, option.uuid);

        if (nextVariant) {
            setSelectedVariantUuid(nextVariant.uuid);
        }
    }

    function selectSurchargeValue(parameter: PublicProductSurchargeParameter, value: PublicProductSurchargeValue) {
        if (!surchargeValueSelectable(value, quantity)) {
            return;
        }

        setSelectedSurchargeValueUuidsByParameter((current) => {
            const next = { ...current };

            if (!parameter.required && next[parameter.uuid] === value.uuid) {
                delete next[parameter.uuid];
            } else {
                next[parameter.uuid] = value.uuid;
            }

            return next;
        });
    }

    useEffect(() => {
        if (variants.length === 0) {
            setSelectedVariantUuid(null);

            return;
        }

        setSelectedVariantUuid((current) => {
            if (current && variants.some((variant) => variant.uuid === current)) {
                return current;
            }

            return variants.find((variant) => variant.canAddToCart)?.uuid ?? variants[0]?.uuid ?? null;
        });
    }, [variants]);

    useEffect(() => {
        setQuantity((current) => clampProductQuantity(Math.max(current, selectedQuantityMin)));
    }, [selectedQuantityMin, selectedQuantityMax]);

    useEffect(() => {
        setSelectedSurchargeValueUuidsByParameter((current) => {
            const next: Record<string, string> = {};

            for (const parameter of surchargeParameters) {
                const selectedValueUuid = current[parameter.uuid];

                if (selectedValueUuid && parameter.values.some((value) => value.uuid === selectedValueUuid)) {
                    next[parameter.uuid] = selectedValueUuid;
                }
            }

            if (Object.keys(next).length === Object.keys(current).length
                && Object.keys(next).every((key) => next[key] === current[key])) {
                return current;
            }

            return next;
        });
    }, [surchargeParameters]);

    useEffect(() => {
        setSelectedImageIndex(0);
    }, [productUuid, selectedVariantUuid]);

    const blocks = blocksFor(context, [
        { type: "breadcrumb" },
        { type: "systemProductGallery" },
        { type: "systemProductSummary" },
        { type: "systemProductBuyBox" },
        { type: "systemProductDescription" },
        { type: "relatedProducts" },
        { type: "seoText" },
    ]);
    const productSummaryBlockIndex = blocks.findIndex((block) => block.type === "systemProductSummary");
    const productBuyBoxBlockIndex = blocks.findIndex((block) => block.type === "systemProductBuyBox");
    const relatedProductsBlockIndex = blocks.findIndex((block) => block.type === "relatedProducts");
    const productSummaryBlock: ShopTemplateBlock = blocks[productSummaryBlockIndex]
        ?? { type: "systemProductSummary" };
    const productBuyBoxBlock: ShopTemplateBlock = blocks[productBuyBoxBlockIndex]
        ?? { type: "systemProductBuyBox" };
    const relatedProductsBlock: ShopTemplateBlock = blocks[relatedProductsBlockIndex]
        ?? { type: "relatedProducts" };
    const productDetailDesign = catalogProductDetailDesign(
        blocks.find((block) => block.type === "systemProductGallery"),
    );
    const editorialProductDetail = productDetailDesign === "editorialSplit";
    const utilityProductDetail = productDetailDesign === "utilitySplit";
    const boutiqueProductDetail = productDetailDesign === "boutiqueSplit";
    const playfulProductDetail = productDetailDesign === "playfulSplit";
    const commerceProductDetail = productDetailDesign === "commerceSplit";
    const customProductDetail = productDetailDesign !== "default";
    const productDetailTabs: Array<{ value: ProductDetailTab; label: string; icon: IconName }> = commerceProductDetail
        ? [
            { value: "overview", label: message("ui.product.overview"), icon: "home" },
            { value: "related", label: message("ui.product.related"), icon: "bag" },
            { value: "similar", label: message("ui.product.similar"), icon: "info" },
            { value: "reviews", label: message("ui.product.reviews"), icon: "check" },
        ]
        : [
            { value: "overview", label: message("ui.product.overview"), icon: "home" },
            { value: "reviews", label: message("ui.product.reviews"), icon: "check" },
        ];
    const productReviewSummary = reviewCount > 0 ? (
        <p className="mt-3 text-sm font-black uppercase tracking-wide text-[var(--site-accent)]">
            ★ {reviewRating.toFixed(1)} · {storefrontPluralMessage(
                reviewCount,
                locale,
                PRODUCT_REVIEW_COUNT_MESSAGE_KEYS,
                message,
            )}
        </p>
    ) : null;

    return (
        <EditableEntityRenderContext.Provider value={editableProduct}>
            {blocks.map((block, index) => {
                if (block.type === "breadcrumb") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <Breadcrumb
                                block={block}
                                current={title}
                                entity={editableProduct}
                                mobileHidden={boutiqueProductDetail}
                            />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "systemProductGallery") {
                    const editableGalleryImages = galleryImages.map((product, imageIndex) => {
                        const imagePart = `gallery${imageIndex + 1}Image`;

                        return {
                            ...product,
                            image: editorMode ? blockText(block, `${imagePart}Url`, product.image) : product.image,
                            imageAlt: blockText(block, `${imagePart}Alt`, product.alt),
                            imagePart,
                            imageTitle: blockText(block, `${imagePart}Title`, ""),
                        };
                    });
                    const editableMainProductImage = editableGalleryImages[selectedImageIndex] ?? editableGalleryImages[0] ?? {
                        alt: title,
                        id: "fallback",
                        image: mainProductImage?.image ?? null,
                        imageAlt: title,
                        imagePart: "gallery1Image",
                        imageTitle: "",
                    };
                    const productImageLayout = imageContentLayout(block, "left");
                    const productImageLayoutColumns = editorialProductDetail
                        ? "lg:grid-cols-[minmax(280px,.72fr)_minmax(0,1.55fr)]"
                        : utilityProductDetail
                            ? "lg:grid-cols-2"
                            : boutiqueProductDetail || commerceProductDetail
                                ? "lg:grid-cols-2"
                                : playfulProductDetail
                                    ? "lg:grid-cols-[minmax(0,750px)_minmax(0,510px)]"
                                    : productImageLayout === "left" || productImageLayout === "right"
                                        ? "lg:grid-cols-[minmax(0,1fr)_470px]"
                                        : "lg:grid-cols-1";

                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <section
                                className={classNames(
                                    "bg-[var(--site-background)] px-5 py-8 sm:px-6",
                                    editorialProductDetail && "border-b border-[var(--site-text)] py-10",
                                    utilityProductDetail && "py-6",
                                    boutiqueProductDetail && "bg-white py-12",
                                    playfulProductDetail && "bg-white py-12",
                                    commerceProductDetail && "bg-white py-10",
                                )}
                                data-product-detail-design={productDetailDesign === "default" ? undefined : productDetailDesign}
                                data-product-detail-hero-section={customProductDetail ? "true" : undefined}
                                style={commerceProductDetail
                                    ? mergeStyles(blockVisualStyle(block), WHITE_COMMERCE_PRESENTATION_STYLE)
                                    : blockVisualStyle(block)}
                            >
                                <style>{PRODUCT_DETAIL_PRESENTATION_CSS}</style>
                                <div className={classNames(
                                    "mx-auto grid min-w-0 max-w-[var(--site-container)] gap-8",
                                    boutiqueProductDetail && "max-w-[1680px]",
                                    productImageLayoutColumns,
                                    editorialProductDetail && "items-start lg:gap-12",
                                    utilityProductDetail && "gap-0 border border-[var(--site-text)]",
                                    boutiqueProductDetail && "items-start gap-10",
                                    commerceProductDetail && "items-start gap-10 lg:gap-14",
                                )} data-product-detail-hero>
                                    {utilityProductDetail ? (
                                        <div
                                            className="grid gap-2 bg-[var(--site-accent)] px-5 py-3 text-xs font-black uppercase tracking-[0.08em] text-[var(--site-text)] sm:grid-cols-3 lg:col-span-2"
                                            data-product-utility-strip
                                        >
                                            <span>{selectedStockStatusText}</span>
                                            <span className="sm:text-center">{message("ui.product.deliveryByMethod")}</span>
                                            <span className="sm:text-right">{message("ui.product.securePayment")}</span>
                                        </div>
                                    ) : null}
                                    {editorialProductDetail || boutiqueProductDetail || playfulProductDetail ? (
                                        <div className="min-w-0 max-w-full lg:hidden" data-product-mobile-summary>
                                            <h1
                                                {...editableProductField(productUuid, "name")}
                                                className="min-w-0 max-w-full text-[30px] font-semibold leading-[1.12] tracking-[-0.035em] [overflow-wrap:anywhere]"
                                            >
                                                {title}
                                            </h1>
                                            {manufacturerName && boutiqueProductDetail ? (
                                                <p className="mt-4 max-w-full text-xs font-bold uppercase tracking-[0.14em] text-[var(--site-primary)] [overflow-wrap:anywhere]">
                                                    {manufacturerName}
                                                </p>
                                            ) : null}
                                            {productReviewSummary}
                                            <div className="mt-5 flex flex-wrap items-center gap-2">
                                                {isOnSale ? (
                                                    <span className="bg-[var(--site-primary)] px-4 py-1 text-xs font-black uppercase text-[var(--site-on-primary)]">
                                                        {message("ui.product.sale")}
                                                    </span>
                                                ) : null}
                                                <span className={classNames(
                                                    "px-4 py-1 text-xs font-black",
                                                    boutiqueProductDetail && "rounded-lg bg-[var(--site-accent)] text-white",
                                                    editorialProductDetail && "bg-[var(--site-accent)] text-white",
                                                )}>
                                                    {selectedStockStatusText}
                                                </span>
                                            </div>
                                        </div>
                                    ) : null}
                                    <div className={classNames(
                                        "grid gap-4",
                                        !editorialProductDetail && !boutiqueProductDetail && !playfulProductDetail && !commerceProductDetail && "sm:grid-cols-[88px_minmax(0,1fr)]",
                                        !customProductDetail && imageContentImageOrderClass(block, "left"),
                                        editorialProductDetail && "lg:order-2 sm:grid-cols-[minmax(0,1fr)_70px]",
                                        utilityProductDetail && "p-4 sm:grid-cols-[64px_minmax(0,1fr)] lg:p-6",
                                        (boutiqueProductDetail || playfulProductDetail || commerceProductDetail) && "grid-cols-1 gap-4 sm:grid-cols-1",
                                    )} data-product-gallery-anatomy={productDetailDesign}>
                                        <div className={classNames(
                                            "order-2 flex gap-3 overflow-x-auto",
                                            !boutiqueProductDetail && !playfulProductDetail && !commerceProductDetail && "sm:block sm:space-y-3 sm:overflow-visible",
                                            !editorialProductDetail && !boutiqueProductDetail && !playfulProductDetail && !commerceProductDetail && "sm:order-1",
                                            editorialProductDetail && "sm:order-2 sm:border-l sm:border-[var(--site-text)] sm:pl-3",
                                            utilityProductDetail && "sm:space-y-2",
                                            (boutiqueProductDetail || playfulProductDetail || commerceProductDetail) && "flex space-y-0 border-t border-[#dadada] pt-3 sm:order-2 sm:flex sm:space-y-0 sm:overflow-x-auto",
                                        )}>
                                            {editableGalleryImages.map((product, imageIndex) => (
                                                <button
                                                    key={product.id}
                                                    type="button"
                                                    className={classNames(
                                                        "h-20 w-20 shrink-0 overflow-hidden border bg-[var(--site-surface)] p-1 transition",
                                                        !customProductDetail && "rounded-xl",
                                                        editorialProductDetail && "h-16 w-16 rounded-none",
                                                        utilityProductDetail && "h-14 w-14 rounded-none p-0",
                                                        boutiqueProductDetail && "h-[72px] w-[72px] rounded-lg",
                                                        playfulProductDetail && "h-[72px] w-[72px] rounded",
                                                        commerceProductDetail && "h-[72px] w-[72px] rounded-none border-x-0 border-b-0 p-0",
                                                        selectedImageIndex === imageIndex
                                                            ? commerceProductDetail
                                                                ? "border-t-2 border-t-black ring-0"
                                                                : "border-[var(--site-primary)] ring-2 ring-[var(--site-primary)]/20"
                                                            : commerceProductDetail
                                                                ? "border-t-2 border-t-transparent"
                                                                : "border-[var(--site-border)]",
                                                    )}
                                                    style={mergeStyles(imageFrameStyle(block, product.imagePart), partStyle(block, product.imagePart))}
                                                    aria-label={message("ui.product.showImage", { number: imageIndex + 1 })}
                                                    onClick={() => setSelectedImageIndex(imageIndex)}
                                                >
                                                    <img
                                                        {...editablePart(product.imagePart)}
                                                        src={product.image}
                                                        alt={product.imageAlt}
                                                        title={product.imageTitle || undefined}
                                                        className={classNames(
                                                            "h-full w-full",
                                                            customProductDetail ? "object-contain" : "object-cover",
                                                            !customProductDetail && "rounded-lg",
                                                            boutiqueProductDetail && "rounded-lg",
                                                        )}
                                                        style={imagePartStyle(block, product.imagePart)}
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                        <div
                                            className={classNames(
                                                "order-1 overflow-hidden bg-[var(--site-surface)]",
                                                (!editorialProductDetail && !boutiqueProductDetail && !playfulProductDetail && !commerceProductDetail) && "sm:order-2",
                                                !customProductDetail && "rounded-[2rem] shadow-[0_18px_54px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]",
                                                editorialProductDetail && "rounded-none border border-[var(--site-text)] sm:order-1",
                                                utilityProductDetail && "rounded-none",
                                                (boutiqueProductDetail || playfulProductDetail || commerceProductDetail) && "rounded-none bg-white shadow-none",
                                            )}
                                            style={imageFrameStyle(block, editableMainProductImage.imagePart)}
                                        >
                                            {editableMainProductImage.image ? (
                                                <img
                                                    {...editablePart(editableMainProductImage.imagePart)}
                                                    src={editableMainProductImage.image}
                                                    alt={editableMainProductImage.imageAlt}
                                                    title={editableMainProductImage.imageTitle || undefined}
                                                    className={classNames(
                                                        "w-full",
                                                        customProductDetail ? "object-contain" : "object-cover",
                                                        boutiqueProductDetail ? "aspect-[4/3]" : "aspect-square",
                                                    )}
                                                    style={imagePartStyle(block, editableMainProductImage.imagePart)}
                                                />
                                            ) : <LagunaProductMediaPlaceholder name={title} size="detail" />}
                                        </div>
                                    </div>

                                    {(editorialProductDetail || boutiqueProductDetail || playfulProductDetail) && description ? (
                                        <p
                                            {...editableProductField(productUuid, "descriptionShort")}
                                            className="min-w-0 max-w-full text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_68%,transparent)] [overflow-wrap:anywhere] lg:hidden"
                                            data-product-mobile-description
                                        >
                                            {description}
                                        </p>
                                    ) : null}

                                    <aside
                                        className={classNames(
                                            "min-w-0 max-w-full p-5 sm:p-6 lg:sticky lg:top-5 lg:self-start",
                                            !customProductDetail && "rounded-[2rem] bg-[var(--site-surface)] shadow-[0_18px_54px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]",
                                            !customProductDetail && imageContentTextOrderClass(block, "left"),
                                            editorialProductDetail && "border-y border-[var(--site-text)] bg-transparent px-0 py-6 lg:order-1",
                                            utilityProductDetail && "rounded-none border-l border-[var(--site-text)] bg-[var(--site-surface)] lg:top-0 lg:min-h-full",
                                            (boutiqueProductDetail || playfulProductDetail || commerceProductDetail) && "rounded-none bg-transparent px-0 py-0 shadow-none ring-0",
                                        )}
                                        data-product-purchase-panel={productDetailDesign}
                                    >
                                        <EditableBlockFrame
                                            block={productSummaryBlock}
                                            index={productSummaryBlockIndex >= 0 ? productSummaryBlockIndex : blocks.length}
                                            editorMode={editorMode}
                                        >
                                        <div
                                            className={classNames("flex flex-wrap items-center gap-2", commerceProductDetail && "hidden")}
                                            data-product-summary-status
                                        >
                                            <span className={classNames(
                                                "max-w-full px-3 py-1 text-xs font-black whitespace-normal [overflow-wrap:anywhere]",
                                                !customProductDetail && "rounded-full",
                                                editorialProductDetail && "rounded-none border border-[var(--site-text)] bg-transparent uppercase tracking-[0.08em] text-[var(--site-text)]",
                                                utilityProductDetail && "rounded-none",
                                                boutiqueProductDetail && "rounded-lg",
                                                !editorialProductDetail && (selectedCanAddToCart ? "bg-[var(--site-accent-soft)] text-[var(--site-accent)]" : "bg-[var(--site-primary-soft)] text-[var(--site-primary)]"),
                                            )}>
                                                {selectedStockStatusText}
                                            </span>
                                            {isOnSale ? (
                                                <span
                                                    className={classNames(
                                                        "max-w-full bg-[var(--site-primary)] px-3 py-1 text-xs font-black text-[var(--site-on-primary)] whitespace-normal [overflow-wrap:anywhere]",
                                                        !customProductDetail && "rounded-full",
                                                        boutiqueProductDetail && "rounded-lg",
                                                    )}
                                                    data-product-sale="true"
                                                >
                                                    {message("ui.product.sale")}
                                                </span>
                                            ) : null}
                                        </div>
                                        <p
                                            className={classNames("mt-4 max-w-full text-xs font-bold uppercase text-[color-mix(in_srgb,var(--site-text)_45%,transparent)] [overflow-wrap:anywhere]", commerceProductDetail && "hidden")}
                                            data-product-reference-row
                                        >
                                            {message("ui.product.code")}:{" "}
                                            <span {...editableProductField(productUuid, "reference")}>{displayReference}</span>
                                        </p>
                                        {manufacturerName && boutiqueProductDetail ? (
                                            <p className="mt-5 max-w-full text-xs font-bold uppercase tracking-[0.16em] text-[color-mix(in_srgb,var(--site-text)_48%,transparent)] [overflow-wrap:anywhere]" data-product-brand>
                                                {manufacturerName}
                                            </p>
                                        ) : null}
                                        {commerceProductDetail ? productReviewSummary : null}
                                        <h1
                                            {...editableProductField(productUuid, "name")}
                                            className={classNames(
                                                "mt-2 min-w-0 max-w-full text-3xl font-black leading-tight [overflow-wrap:anywhere] sm:text-4xl",
                                                editorialProductDetail && "font-[var(--site-heading-font)] font-medium tracking-[-0.045em] lg:text-5xl",
                                                utilityProductDetail && "uppercase tracking-[-0.035em]",
                                                boutiqueProductDetail && "font-[var(--site-heading-font)] font-semibold tracking-[-0.025em]",
                                                commerceProductDetail && "font-[var(--site-heading-font)] text-[38px] font-semibold tracking-[-0.03em]",
                                            )}
                                        >
                                            {title}
                                        </h1>
                                        {!commerceProductDetail ? productReviewSummary : null}
                                        {description ? (
                                            <p
                                                {...editableProductField(productUuid, "descriptionShort")}
                                                className="mt-4 max-w-full text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)] [overflow-wrap:anywhere]"
                                            >
                                                {description}
                                            </p>
                                        ) : null}

                                        {commerceProductDetail ? (
                                            <>
                                                <div className="mt-5 flex min-w-0 max-w-full flex-wrap items-center gap-3 border-t border-[#dadada] pt-4 text-sm [overflow-wrap:anywhere]" data-product-delivery-row>
                                                    <Icon name="truck" className="h-5 w-5 text-[#666]" />
                                                    <span>{message("ui.product.deliveryByMethod")}</span>
                                                </div>
                                                <div className="mt-4 flex min-w-0 max-w-full flex-wrap gap-x-6 gap-y-2 border-y border-[#dadada] py-4 text-xs font-bold uppercase [overflow-wrap:anywhere]" data-product-commerce-meta>
                                                    <span className={selectedCanAddToCart ? "text-[#009e18]" : "text-[var(--site-primary)]"}>{selectedStockStatusText}</span>
                                                    <span className="text-[#666]">{message("ui.product.code")}: <span {...editableProductField(productUuid, "reference")}>{displayReference}</span></span>
                                                    {manufacturerName ? <span className="text-[#666]">{manufacturerName}</span> : null}
                                                </div>
                                            </>
                                        ) : null}
                                        </EditableBlockFrame>

                                        <EditableBlockFrame
                                            block={productBuyBoxBlock}
                                            index={productBuyBoxBlockIndex >= 0 ? productBuyBoxBlockIndex : blocks.length + 1}
                                            editorMode={editorMode}
                                        >
                                        <div
                                            className={classNames(
                                                productDetailDesign === "default" && "mt-6 border-y border-[var(--site-border)] py-5",
                                                editorialProductDetail && "mt-8 border-y border-[var(--site-text)] py-5",
                                                utilityProductDetail && "-mx-5 mt-6 bg-[var(--site-accent)] px-5 py-5 sm:-mx-6 sm:px-6",
                                                boutiqueProductDetail && "mt-6 border-y border-[#e8e8e8] py-5",
                                                commerceProductDetail && "mt-6 border-b border-[#dadada] pb-5",
                                            )}
                                            data-product-detail-price
                                        >
                                            <p className="text-xs font-bold uppercase text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">{message("ui.product.price")}</p>
                                            {commerceProductDetail && previousPrice ? (
                                                <p className="mt-2 flex flex-wrap items-center gap-3 text-sm" data-product-commerce-discount>
                                                    <span className="text-[#666] line-through">{formatMoney(previousPrice)}</span>
                                                    <span className="bg-black px-2 py-1 text-xs font-black text-white">-{discountPercent}%</span>
                                                </p>
                                            ) : null}
                                            <p
                                                {...editableProductField(productUuid, "formattedPrice")}
                                                className={classNames("mt-1 min-w-0 max-w-full text-4xl font-black [overflow-wrap:anywhere]", commerceProductDetail ? "text-black" : "text-[var(--site-primary)]")}
                                                data-product-price-value
                                            >
                                                {formatMoney(displayPrice)}
                                            </p>
                                            {commerceProductDetail && isOnSale ? (
                                                <span className="mt-3 inline-flex bg-[var(--site-primary)] px-3 py-1 text-xs font-black uppercase text-[var(--site-on-primary)]" data-product-sale="true">
                                                    {message("ui.product.sale")}
                                                </span>
                                            ) : null}
                                            {selectedVariant ? (
                                                <p className="mt-2 max-w-full text-sm font-semibold text-[color-mix(in_srgb,var(--site-text)_55%,transparent)] [overflow-wrap:anywhere]">{message("ui.product.selectedVariantName", { name: selectedVariant.name })}</p>
                                            ) : null}
                                            {boutiqueProductDetail ? (
                                                <div className="mt-6 space-y-1.5 border-t border-[#e8e8e8] pt-5 text-sm" data-product-boutique-meta>
                                                    <p className={classNames("font-bold", selectedCanAddToCart ? "text-[var(--site-accent)]" : "text-[var(--site-primary)]")}>{selectedStockStatusText}</p>
                                                    <p>{message("ui.product.deliveryByMethod")}</p>
                                                    <p className="text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">{message("ui.product.code")}: <span {...editableProductField(productUuid, "reference")}>{displayReference}</span></p>
                                                </div>
                                            ) : null}
                                        </div>

                                        {variants.length > 0 && variantGroups.length > 0 ? (
                                            <div className="mt-6">
                                                <div className="space-y-5">
                                                    {variantGroups.map((group) => {
                                                        const selectedOption = group.options.find((option) => selectedVariantItemUuids.has(option.uuid));

                                                        return (
                                                        <div key={group.attributeUuid}>
                                                            <div className="flex min-w-0 max-w-full flex-wrap items-center justify-between gap-3">
                                                                <p className="min-w-0 max-w-full text-sm font-black [overflow-wrap:anywhere]">{group.attributeName}</p>
                                                                {selectedOption ? <p className="min-w-0 max-w-full text-xs font-bold text-[color-mix(in_srgb,var(--site-text)_45%,transparent)] [overflow-wrap:anywhere]">{selectedOption.name}</p> : null}
                                                            </div>
                                                            <div className="mt-2 flex flex-wrap gap-2">
                                                                {group.options.map((option) => {
                                                                    const selected = selectedVariantItemUuids.has(option.uuid);
                                                                    const hasMatchingCombination = variantOptionHasMatchingCombination(
                                                                        variants,
                                                                        option,
                                                                        selectedVariantSelection,
                                                                    );
                                                                    const canAddMatchingVariant = variantOptionHasMatchingCombination(
                                                                        variants,
                                                                        option,
                                                                        selectedVariantSelection,
                                                                        true,
                                                                    );
                                                                    const disabled = !hasMatchingCombination || !canAddMatchingVariant;

                                                                    return (
                                                                        <button
                                                                            key={option.uuid}
                                                                            type="button"
                                                                            className={classNames(
                                                                                "inline-flex min-h-11 min-w-0 max-w-full flex-wrap items-center gap-2 rounded-xl border px-4 py-2 text-sm font-black whitespace-normal transition [overflow-wrap:anywhere]",
                                                                                selected && !disabled ? "border-[var(--site-primary)] bg-[var(--site-surface)] text-[var(--site-primary)]" : "border-[var(--site-border)] bg-[var(--site-surface)] text-[var(--site-text)]",
                                                                                disabled ? "cursor-not-allowed bg-[color-mix(in_srgb,var(--site-text)_3%,transparent)] text-[color-mix(in_srgb,var(--site-text)_30%,transparent)] opacity-70" : "hover:border-[var(--site-primary)]",
                                                                            )}
                                                                            disabled={disabled}
                                                                            onClick={() => selectVariantOption(option)}
                                                                        >
                                                                            {option.color ? (
                                                                                <span
                                                                                    className={classNames("h-4 w-4 shrink-0 rounded-full border border-[color-mix(in_srgb,var(--site-text)_15%,transparent)]", disabled && "opacity-35 grayscale")}
                                                                                    style={{ backgroundColor: option.color }}
                                                                                    aria-hidden="true"
                                                                                />
                                                                            ) : null}
                                                                            <span className="min-w-0 max-w-full [overflow-wrap:anywhere]">{option.name}</span>
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );})}
                                                </div>
                                            </div>
                                        ) : variants.length > 0 ? (
                                            <div className="mt-6">
                                                <p className="text-sm font-black">{message("ui.product.chooseVariant")}</p>
                                                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                                                    {variants.map((variant) => {
                                                        const variantStockText = !variant.trackInventory || variant.availableQuantity === null
                                                            ? message("ui.product.inStock")
                                                            : variant.availableQuantity > 0 ? message("ui.product.inStock") : message("ui.product.outOfStock");
                                                        const selected = selectedVariant?.uuid === variant.uuid;

                                                        return (
                                                            <button
                                                                key={variant.uuid}
                                                                type="button"
                                                                className={classNames(
                                                                    "min-w-0 max-w-full rounded-xl border px-4 py-3 text-left text-sm whitespace-normal transition [overflow-wrap:anywhere]",
                                                                    selected && variant.canAddToCart ? "border-[var(--site-primary)] bg-[var(--site-surface)]" : "border-[var(--site-border)] bg-[var(--site-surface)]",
                                                                    variant.canAddToCart ? "hover:border-[var(--site-primary)]" : "cursor-not-allowed bg-[color-mix(in_srgb,var(--site-text)_3%,transparent)] opacity-70",
                                                                )}
                                                                disabled={!variant.canAddToCart}
                                                                onClick={() => setSelectedVariantUuid(variant.uuid)}
                                                            >
                                                                <span className="block min-w-0 max-w-full font-black [overflow-wrap:anywhere]">{variant.name}</span>
                                                                <span className={classNames("mt-1 block min-w-0 max-w-full text-xs font-bold [overflow-wrap:anywhere]", variant.canAddToCart ? "text-[var(--site-accent)]" : "text-[var(--site-primary)]")}>
                                                                    {variantStockText}
                                                                </span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ) : null}

                                        {surchargeParameters.length > 0 ? (
                                            <div className="mt-6 space-y-5">
                                                {surchargeParameters.map((parameter) => {
                                                    const selectedValueUuid = selectedSurchargeValueUuidsByParameter[parameter.uuid];
                                                    const selectedValue = parameter.values.find((value) => value.uuid === selectedValueUuid);

                                                    return (
                                                        <div key={parameter.uuid}>
                                                            <div className="flex min-w-0 max-w-full flex-wrap items-center justify-between gap-2">
                                                                <div className="min-w-0 max-w-full">
                                                                    <p className="max-w-full text-sm font-black [overflow-wrap:anywhere]">
                                                                        {parameter.displayName ?? parameter.name}
                                                                        {parameter.required ? <span className="text-[var(--site-primary)]"> *</span> : null}
                                                                    </p>
                                                                    {parameter.description ? (
                                                                        <p className="mt-1 max-w-full text-xs font-semibold text-[color-mix(in_srgb,var(--site-text)_45%,transparent)] [overflow-wrap:anywhere]">{parameter.description}</p>
                                                                    ) : null}
                                                                </div>
                                                                {selectedValue ? (
                                                                    <p className="min-w-0 max-w-full text-xs font-bold text-[color-mix(in_srgb,var(--site-text)_45%,transparent)] [overflow-wrap:anywhere]">{selectedValue.name}</p>
                                                                ) : null}
                                                            </div>
                                                            <div className="mt-2 flex flex-wrap gap-2">
                                                                {parameter.values.map((value) => {
                                                                    const selected = value.uuid === selectedValueUuid;
                                                                    const priceLabel = moneyAmount(value.salePrice) > 0 ? `+ ${formatMoney(value.salePrice)}` : message("ui.product.priceIncluded");
                                                                    const stockLabel = surchargeValueStockLabel(value, message);
                                                                    const selectable = surchargeValueSelectable(value, quantity);

                                                                    return (
                                                                        <button
                                                                            key={value.uuid}
                                                                            type="button"
                                                                            className={classNames(
                                                                                "inline-flex min-h-11 min-w-0 max-w-full flex-wrap items-center gap-2 rounded-xl border px-4 py-2 text-left text-sm font-black whitespace-normal transition [overflow-wrap:anywhere] disabled:cursor-not-allowed disabled:opacity-45",
                                                                                selected ? "border-[var(--site-primary)] bg-[var(--site-surface)] text-[var(--site-primary)]" : "border-[var(--site-border)] bg-[var(--site-surface)] text-[var(--site-text)] hover:border-[var(--site-primary)]",
                                                                            )}
                                                                            disabled={!selectable}
                                                                            onClick={() => selectSurchargeValue(parameter, value)}
                                                                        >
                                                                            <span className="min-w-0 max-w-full [overflow-wrap:anywhere]">{value.name}</span>
                                                                            <span className="flex min-w-0 max-w-full flex-col text-xs font-bold opacity-65 [overflow-wrap:anywhere]">
                                                                                <span>{priceLabel}</span>
                                                                                {stockLabel ? <span>{stockLabel}</span> : null}
                                                                            </span>
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : null}

                                        <ManufacturingCustomizationFields appearance="laguna" controller={customization} message={message}/>

                                        <div
                                            className={classNames(
                                                productDetailDesign === "default" && "mt-6 rounded-3xl bg-[var(--site-surface)] p-4 shadow-inner shadow-[var(--site-shadow-color)]",
                                                editorialProductDetail && "mt-6 border-y border-[var(--site-text)] py-4",
                                                utilityProductDetail && "-mx-5 mt-0 bg-[var(--site-accent)] px-5 pb-5 sm:-mx-6 sm:px-6",
                                                boutiqueProductDetail && "mt-6 border-y border-[#e8e8e8] py-4",
                                                commerceProductDetail && "mt-6 border-y border-[#dadada] py-4",
                                            )}
                                            data-product-detail-purchase-strip
                                        >
                                            <div className="mt-3 flex min-w-0 max-w-full flex-wrap items-end justify-between gap-4">
                                                <p className={classNames("min-w-0 max-w-full text-sm font-bold [overflow-wrap:anywhere]", selectedCanAddToCart ? "text-[var(--site-accent)]" : "text-[var(--site-primary)]")}>
                                                    {addToCartNotice}
                                                </p>
                                                <div>
                                                    <p className="mb-2 text-xs font-bold uppercase text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">{message("ui.product.quantity")}</p>
                                                    <p className={classNames("mb-2 text-xs font-bold", selectedCanAddToCart ? "text-[var(--site-accent)]" : "text-[var(--site-primary)]")}>
                                                        {selectedStockDetailText}
                                                    </p>
                                                    <div className={classNames("inline-flex h-11 items-center", commerceProductDetail ? "gap-0" : "gap-2")}>
                                                        <button
                                                            type="button"
                                                            className={classNames(
                                                                "grid h-10 w-10 place-items-center rounded-full border border-[var(--site-border)] text-lg disabled:text-[color-mix(in_srgb,var(--site-text)_25%,transparent)]",
                                                                commerceProductDetail && "rounded-none",
                                                            )}
                                                            disabled={quantity <= selectedQuantityMin || !selectedCanAddToCart}
                                                            onClick={() => setQuantity((current) => clampProductQuantity(current - 1))}
                                                        >
                                                            -
                                                        </button>
                                                        <input
                                                            className={classNames(
                                                                "h-10 w-16 rounded-xl border border-[var(--site-border)] text-center text-sm font-black outline-none",
                                                                commerceProductDetail && "rounded-none border-x-0",
                                                            )}
                                                            value={quantity}
                                                            inputMode="numeric"
                                                            min={selectedQuantityMin}
                                                            max={selectedQuantityMax}
                                                            disabled={!selectedCanAddToCart}
                                                            onChange={(event) => {
                                                                const next = parseWholeQuantity(event.target.value);
                                                                setQuantity(next === null ? selectedQuantityMin : clampProductQuantity(next));
                                                            }}
                                                        />
                                                        <button
                                                            type="button"
                                                            className={classNames(
                                                                "grid h-10 w-10 place-items-center rounded-full border border-[var(--site-border)] text-lg disabled:text-[color-mix(in_srgb,var(--site-text)_25%,transparent)]",
                                                                commerceProductDetail && "rounded-none",
                                                            )}
                                                            disabled={quantity >= selectedQuantityMax || !selectedCanAddToCart}
                                                            onClick={() => setQuantity((current) => clampProductQuantity(current + 1))}
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                            className="mt-5 grid min-w-0 max-w-full gap-3 sm:grid-cols-[minmax(0,1fr)_auto]"
                                            data-product-detail-actions
                                        >
                                            <button
                                                type="button"
                                                className={classNames(
                                                    "min-w-0 max-w-full px-7 py-4 text-sm font-black whitespace-normal [overflow-wrap:anywhere] disabled:cursor-not-allowed disabled:bg-[color-mix(in_srgb,var(--site-text)_25%,transparent)]",
                                                    productDetailDesign === "default" && "rounded-full bg-[var(--site-primary)] text-[var(--site-on-primary)]",
                                                    editorialProductDetail && "rounded-none border border-[var(--site-text)] bg-[var(--site-text)] uppercase tracking-[0.1em] text-[var(--site-background)]",
                                                    utilityProductDetail && "rounded-none bg-[var(--site-accent)] uppercase tracking-[0.06em] text-[var(--site-text)]",
                                                    boutiqueProductDetail && "rounded-lg bg-[var(--site-primary)] text-[var(--site-on-primary)] shadow-none",
                                                    commerceProductDetail && "rounded-none bg-black uppercase tracking-[0.05em] text-white shadow-none",
                                                )}
                                                disabled={!selectedCanAddToCart || adding}
                                                onClick={() => onAddToCart(
                                                    productUuid,
                                                    selectedVariant?.uuid ?? null,
                                                    clampProductQuantity(quantity),
                                                    selectedSurchargeValueUuids,
                                                    customization.profile.profileAvailable ? customization.specification : undefined,
                                                )}
                                            >
                                                {adding ? message("ui.product.adding") : message("ui.product.addToCart")}
                                            </button>
                                            <button
                                                type="button"
                                                className={classNames(
                                                    "min-w-0 max-w-full border border-[color-mix(in_srgb,var(--site-text)_20%,transparent)] px-6 py-4 text-sm font-bold whitespace-normal [overflow-wrap:anywhere]",
                                                    productDetailDesign === "default" && "rounded-full",
                                                    (editorialProductDetail || utilityProductDetail || commerceProductDetail) && "rounded-none",
                                                    boutiqueProductDetail && "rounded-lg bg-[#f9f9f9]",
                                                )}
                                                onClick={() => onToggleFavorite(productUuid)}
                                            >
                                                {favorites.has(productUuid) ? message("ui.product.removeFromFavorites") : message("ui.account.favorites")}
                                            </button>
                                        </div>

                                        <div
                                            className="mt-6 grid gap-3 border-t border-[var(--site-border)] pt-5 text-sm sm:grid-cols-2"
                                            data-product-detail-facts
                                        >
                                            <div className="rounded-2xl bg-[var(--site-surface)] p-4">
                                                <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.product.selectedVariant")}</p>
                                                <p className="mt-1 font-black">{selectedVariant?.name ?? message("ui.product.noVariant")}</p>
                                            </div>
                                            <div className="rounded-2xl bg-[var(--site-surface)] p-4">
                                                <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.product.stock")}</p>
                                                <p className={classNames("mt-1 font-black", selectedCanAddToCart ? "text-[var(--site-accent)]" : "text-[var(--site-primary)]")}>{selectedStockStatusText}</p>
                                            </div>
                                            <div className="rounded-2xl bg-[var(--site-surface)] p-4">
                                                <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.product.code")}</p>
                                                <p
                                                    {...editableProductField(productUuid, "reference")}
                                                    className="mt-1 font-black"
                                                >
                                                    {displayReference}
                                                </p>
                                            </div>
                                            <div className="rounded-2xl bg-[var(--site-surface)] p-4">
                                                <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.product.minimumQuantity")}</p>
                                                <p className="mt-1 font-black">{selectedQuantityMin} {message("ui.product.unit")}</p>
                                            </div>
                                        </div>

                                        <div
                                            className={classNames(
                                                "mt-5 grid gap-3 text-sm text-[color-mix(in_srgb,var(--site-text)_65%,transparent)]",
                                                customProductDetail ? "sm:grid-cols-4" : "sm:grid-cols-3",
                                            )}
                                            data-product-detail-trust
                                        >
                                            <div className="flex items-center gap-2 rounded-2xl bg-[var(--site-surface)] p-3 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]"><Icon name="truck" className="h-5 w-5 text-[var(--site-primary)]" /> {message("ui.product.deliveryByMethod")}</div>
                                            <div className="flex items-center gap-2 rounded-2xl bg-[var(--site-surface)] p-3 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]"><Icon name="rotate" className="h-5 w-5 text-[var(--site-primary)]" /> {message("ui.product.easyReturns")}</div>
                                            <div className="flex items-center gap-2 rounded-2xl bg-[var(--site-surface)] p-3 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]"><Icon name="shield" className="h-5 w-5 text-[var(--site-primary)]" /> {message("ui.product.securePayment")}</div>
                                            {customProductDetail ? (
                                                <button
                                                    type="button"
                                                    className="flex items-center gap-2 rounded-2xl bg-[var(--site-surface)] p-3 text-left shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]"
                                                    onClick={() => onToggleFavorite(productUuid)}
                                                >
                                                    <Icon name="heart" className={classNames("h-5 w-5 text-[var(--site-primary)]", favorites.has(productUuid) && "fill-current")} />
                                                    {favorites.has(productUuid) ? message("ui.product.removeFromFavorites") : message("ui.account.favorites")}
                                                </button>
                                            ) : null}
                                        </div>
                                        </EditableBlockFrame>
                                    </aside>
                                </div>
                                {boutiqueProductDetail ? (
                                    <div
                                        className="mx-auto mt-14 grid max-w-[1680px] border-y border-[#e8e8e8] py-7 text-sm sm:grid-cols-2 lg:grid-cols-4"
                                        data-product-detail-benefits
                                    >
                                        <div className="flex items-center gap-4 px-5 py-3"><Icon name="truck" className="h-7 w-7 shrink-0 text-[var(--site-primary)]" /><span className="font-bold">{message("ui.product.deliveryByMethod")}</span></div>
                                        <div className="flex items-center gap-4 px-5 py-3 lg:border-l lg:border-[#e8e8e8]"><Icon name="rotate" className="h-7 w-7 shrink-0 text-[var(--site-primary)]" /><span className="font-bold">{message("ui.product.easyReturns")}</span></div>
                                        <div className="flex items-center gap-4 px-5 py-3 lg:border-l lg:border-[#e8e8e8]"><Icon name="shield" className="h-7 w-7 shrink-0 text-[var(--site-primary)]" /><span className="font-bold">{message("ui.product.securePayment")}</span></div>
                                        <div className="flex items-center gap-4 px-5 py-3 lg:border-l lg:border-[#e8e8e8]"><Icon name="store" className="h-7 w-7 shrink-0 text-[var(--site-primary)]" /><span className="font-bold">{message("ui.product.inStock")}</span></div>
                                    </div>
                                ) : null}
                            </section>
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "systemProductSummary") {
                    return null;
                }
                if (block.type === "systemProductBuyBox") {
                    return null;
                }
                if (block.type === "systemProductDescription") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <section
                                className={classNames(
                                    "px-5 py-10 sm:px-6",
                                    productDetailDesign === "default" && "bg-[var(--site-surface)]",
                                    editorialProductDetail && "bg-[var(--site-background)]",
                                    utilityProductDetail && "bg-[var(--site-surface)]",
                                    boutiqueProductDetail && "bg-[#f9f9f9]",
                                    playfulProductDetail && "bg-white",
                                    commerceProductDetail && "bg-white",
                                )}
                                data-product-detail-design={productDetailDesign === "default" ? undefined : productDetailDesign}
                                style={commerceProductDetail
                                    ? mergeStyles(blockVisualStyle(block), WHITE_COMMERCE_PRESENTATION_STYLE)
                                    : blockVisualStyle(block)}
                            >
                                <div
                                    className={classNames(
                                        "mx-auto max-w-[var(--site-container)]",
                                        boutiqueProductDetail && "max-w-[1680px]",
                                    )}
                                    data-product-detail-description-body
                                >
                                    <div className={classNames(
                                        productDetailDesign === "default" && "rounded-full bg-[var(--site-surface)] p-1 shadow-inner shadow-[var(--site-shadow-color)]",
                                        editorialProductDetail && "border-y border-[var(--site-text)] py-2",
                                        utilityProductDetail && "border-b border-[var(--site-text)]",
                                        boutiqueProductDetail && "rounded-lg bg-[#f1f1f1] p-1",
                                        commerceProductDetail && "border border-[#dadada] bg-[#f6f6f6]",
                                    )} data-product-detail-tabs>
                                        <nav className={classNames(
                                            "flex gap-1 overflow-x-auto",
                                            editorialProductDetail && "lg:w-[280px] lg:flex-col lg:overflow-visible",
                                        )} aria-label={message("ui.product.detailsNavigation")}>
                                            {productDetailTabs.map((tab) => (
                                                <button
                                                    key={tab.value}
                                                    type="button"
                                                    className={classNames(
                                                        "inline-flex min-h-11 shrink-0 items-center gap-2 px-5 text-sm font-black transition",
                                                        productDetailDesign === "default" && "rounded-full",
                                                        editorialProductDetail && "rounded-none border-l-2 border-transparent uppercase tracking-[0.08em]",
                                                        utilityProductDetail && "rounded-none border-b-4 border-transparent",
                                                        boutiqueProductDetail && "rounded-lg",
                                                        commerceProductDetail && "rounded-none border-r border-[#dadada]",
                                                        productDetailTab === tab.value
                                                            ? classNames(
                                                                "text-[var(--site-primary)]",
                                                                productDetailDesign === "default" && "bg-[var(--site-surface)] shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]",
                                                                editorialProductDetail && "border-l-[var(--site-text)] bg-transparent text-[var(--site-text)]",
                                                                utilityProductDetail && "border-b-[var(--site-accent)] bg-[var(--site-accent)] text-[var(--site-text)]",
                                                                boutiqueProductDetail && "bg-[var(--site-surface)] text-[var(--site-primary)] shadow-none",
                                                                commerceProductDetail && "bg-white text-black shadow-none",
                                                            )
                                                            : "text-[color-mix(in_srgb,var(--site-text)_55%,transparent)] hover:bg-[color-mix(in_srgb,var(--site-surface)_70%,transparent)] hover:text-[var(--site-text)]",
                                                    )}
                                                    aria-selected={productDetailTab === tab.value}
                                                    onClick={() => setProductDetailTab(tab.value)}
                                                >
                                                    <Icon name={tab.icon} className="h-4 w-4" />
                                                    {tab.label}
                                                </button>
                                            ))}
                                        </nav>
                                    </div>

                                    <div className={classNames(
                                        "grid gap-5 py-8 lg:grid-cols-[minmax(260px,340px)_minmax(0,1fr)_minmax(240px,320px)]",
                                        customProductDetail && "hidden",
                                    )}>
                                        <section className="rounded-[2rem] bg-[var(--site-surface)] p-5 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]" data-product-review-card>
                                            <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.product.rating")}</p>
                                            <div className="mt-3 flex items-end gap-3">
                                                <p className="text-4xl font-black">{reviewRating.toFixed(1)}</p>
                                                <p className="pb-1 text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">{message("ui.product.outOfFive")}</p>
                                            </div>
                                            <p className="mt-3 text-xl font-black text-[var(--site-accent)]">
                                                {"★".repeat(reviewStars)}
                                                {"☆".repeat(5 - reviewStars)}
                                            </p>
                                            <p className="mt-2 text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">
                                                {reviewCount > 0
                                                    ? storefrontPluralMessage(
                                                        reviewCount,
                                                        locale,
                                                        PRODUCT_CUSTOMER_RATING_MESSAGE_KEYS,
                                                        message,
                                                    )
                                                    : message("ui.product.noRatings")}
                                            </p>
                                            <button
                                                {...editablePart("reviewButton")}
                                                type="button"
                                                className="mt-5 rounded-full bg-[var(--site-primary)] px-5 py-3 text-sm font-black text-[var(--site-on-primary)] shadow-lg shadow-[var(--site-primary)]/20 transition"
                                                style={partStyle(block, "reviewButton")}
                                                onClick={() => setProductDetailTab("reviews")}
                                            >
                                                {message("ui.product.writeReview")}
                                            </button>
                                        </section>

                                        <section className="rounded-[2rem] bg-[var(--site-surface)] p-5 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]" data-product-review-card>
                                            <div className="flex flex-wrap items-center justify-between gap-3">
                                                <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.product.ratingDistribution")}</p>
                                                <p className="text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">
                                                    {storefrontPluralMessage(
                                                        reviewDistributionTotal,
                                                        locale,
                                                        PRODUCT_CUSTOMER_RATING_MESSAGE_KEYS,
                                                        message,
                                                    )}
                                                </p>
                                            </div>
                                            <div className="mt-5 space-y-3">
                                                {([5, 4, 3, 2, 1] as ProductReviewRating[]).map((rating) => {
                                                    const count = reviewDistribution[rating];
                                                    const width = reviewDistributionTotal > 0 ? Math.round((count / reviewDistributionTotal) * 100) : 0;

                                                    return (
                                                        <div key={rating} className="grid grid-cols-[54px_minmax(0,1fr)_42px] items-center gap-3 text-sm">
                                                            <p className="font-black text-[var(--site-accent)]">{rating} ★</p>
                                                            <div className="h-3 overflow-hidden rounded-full bg-[color-mix(in_srgb,var(--site-text)_10%,transparent)]">
                                                                <div
                                                                    className="h-full rounded-full bg-[var(--site-accent-soft)]"
                                                                    style={{ width: `${width}%` }}
                                                                />
                                                            </div>
                                                            <p className="text-right font-bold text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">{count}</p>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </section>

                                        {recommendationPercent !== null || salesCount !== null || complaintRate !== null ? (
                                            <section className="grid gap-3">
                                                {recommendationPercent !== null ? (
                                                    <div className="rounded-3xl bg-[var(--site-surface)] p-4 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]" data-product-review-card>
                                                        <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.product.customersRecommend")}</p>
                                                        <p className="mt-2 text-2xl font-black text-[var(--site-accent)]">{recommendationPercent}%</p>
                                                    </div>
                                                ) : null}
                                                {salesCount !== null ? (
                                                    <div className="rounded-3xl bg-[var(--site-surface)] p-4 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]" data-product-review-card>
                                                        <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.product.unitsSold")}</p>
                                                        <p className="mt-2 text-2xl font-black">{formatNumber(salesCount)}</p>
                                                    </div>
                                                ) : null}
                                                {complaintRate !== null ? (
                                                    <div className="rounded-3xl bg-[var(--site-surface)] p-4 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]" data-product-review-card>
                                                        <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.product.complaintRate")}</p>
                                                        <p className="mt-2 text-2xl font-black">{formatNumber(complaintRate)}%</p>
                                                    </div>
                                                ) : null}
                                            </section>
                                        ) : null}
                                    </div>

                                    <div data-product-detail-tab-content>
                                        {productDetailTab === "overview" ? (
                                            <EntitySeoText title={message("ui.product.description")} description={description} />
                                        ) : productDetailTab === "related" || productDetailTab === "similar" ? (
                                            <EditableBlockFrame
                                                block={relatedProductsBlock}
                                                index={relatedProductsBlockIndex >= 0 ? relatedProductsBlockIndex : blocks.length + 2}
                                                editorMode={editorMode}
                                            >
                                                <ProductDetailCatalogRail
                                                    block={relatedProductsBlock}
                                                    categoryUuids={categoryUuids}
                                                    context={context}
                                                    favorites={favorites}
                                                    onToggleFavorite={onToggleFavorite}
                                                    productUuid={productUuid}
                                                    sort={productDetailTab === "related" ? "recommended" : "bestselling"}
                                                />
                                            </EditableBlockFrame>
                                        ) : (
                                        <div id="produkt-recenze" className="border-t border-[var(--site-border)] pt-8">
                                            <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
                                                <div>
                                                    <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.product.customerReviews")}</p>
                                                    <h2 className="mt-1 text-2xl font-black">{message("ui.product.whatCustomersSay")}</h2>
                                                </div>
                                                <p className="text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">
                                                    {storefrontPluralMessage(
                                                        reviewCount,
                                                        locale,
                                                        PRODUCT_CUSTOMER_RATING_MESSAGE_KEYS,
                                                        message,
                                                    )}
                                                </p>
                                            </div>
                                            {reviews.length > 0 ? (
                                                <div className="divide-y divide-[var(--site-border)]">
                                                    {reviews.map((review, reviewIndex) => (
                                                        <article key={productReviewKey(review, reviewIndex)} className="py-6 first:pt-0 last:pb-0">
                                                            <div className="grid gap-4 sm:grid-cols-[44px_minmax(0,1fr)]">
                                                                <div className="grid h-11 w-11 place-items-center rounded-full bg-[var(--site-surface)] text-sm font-black text-[var(--site-primary)] shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                                                                    {review.authorName.slice(0, 1).toUpperCase()}
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                                                        <div>
                                                                            <p className="font-black">{review.authorName}</p>
                                                                            {review.createdAt ? (
                                                                                <p className="mt-0.5 text-xs font-bold uppercase text-[color-mix(in_srgb,var(--site-text)_35%,transparent)]">
                                                                                    {new Date(review.createdAt).toLocaleDateString("cs-CZ")}
                                                                                </p>
                                                                            ) : null}
                                                                        </div>
                                                                        <p className="text-sm font-black text-[var(--site-accent)]">{"★".repeat(review.rating)}{"☆".repeat(5 - review.rating)}</p>
                                                                    </div>
                                                                    {review.title ? <h3 className="mt-4 text-base font-black">{review.title}</h3> : null}
                                                                    <p className="mt-2 text-sm leading-7 text-[color-mix(in_srgb,var(--site-text)_65%,transparent)]">{review.body}</p>
                                                                </div>
                                                            </div>
                                                        </article>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="border border-dashed border-[var(--site-border)] bg-[var(--site-surface)] p-6 text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">
                                                    {message("ui.product.noReviews")}
                                                </div>
                                            )}
                                        </div>
                                        )}
                                    </div>
                                </div>
                            </section>
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "relatedProducts") {
                    if (commerceProductDetail) return null;

                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <ProductRail block={block} favorites={favorites} onToggleFavorite={onToggleFavorite} />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "seoText") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <EntitySeoText title={title} description={description} block={block} entity={editableProduct} />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "newsletter") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <Newsletter block={block} onSubmit={(event) => event.preventDefault()} />
                        </EditableBlockFrame>
                    );
                }

                return (
                    <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                        <BlockPlaceholder block={block} index={index} />
                    </EditableBlockFrame>
                );
            })}
        </EditableEntityRenderContext.Provider>
    );
}

function CartQuantityControl({
    item,
    loading,
    onUpdateItem,
}: {
    item: PublicCartItem;
    loading: boolean;
    onUpdateItem: (itemUuid: string, quantity: number) => void;
}) {
    const message = useShopStorefrontMessage();
    const [draftQuantity, setDraftQuantity] = useState(String(item.quantity));
    const [removeConfirmationOpen, setRemoveConfirmationOpen] = useState(false);
    const quantityDisabled = loading || !item.availableForOrder || item.stockStatus === "unavailable";
    const maxQuantity = Math.min(CART_ITEM_MAX_QUANTITY, Math.max(0, item.maxQuantity ?? CART_ITEM_MAX_QUANTITY));
    const cannotIncrease = quantityDisabled || item.quantity >= maxQuantity;

    useEffect(() => {
        setDraftQuantity(String(item.quantity));
        setRemoveConfirmationOpen(false);
    }, [item.quantity, item.uuid]);

    function requestQuantityUpdate(nextQuantity: number) {
        const normalizedQuantity = clampQuantity(nextQuantity, 0, maxQuantity);

        if (normalizedQuantity === 0 && item.quantity > 0) {
            setDraftQuantity("0");
            setRemoveConfirmationOpen(true);

            return;
        }

        if (normalizedQuantity !== item.quantity) {
            onUpdateItem(item.uuid, normalizedQuantity);
        }
    }

    function commitQuantity(value = draftQuantity) {
        const parsed = parseWholeQuantity(value);

        if (parsed === null) {
            setDraftQuantity(String(item.quantity));

            return;
        }

        const nextQuantity = clampQuantity(parsed, 0, maxQuantity);

        setDraftQuantity(String(nextQuantity));

        requestQuantityUpdate(nextQuantity);
    }

    function cancelRemoval() {
        setRemoveConfirmationOpen(false);
        setDraftQuantity(String(item.quantity));
    }

    function confirmRemoval() {
        setRemoveConfirmationOpen(false);
        onUpdateItem(item.uuid, 0);
    }

    return (
        <>
            <div className="inline-flex h-10 items-center gap-2">
                <button
                    type="button"
                    className="grid h-9 w-9 place-items-center rounded-full border border-[var(--site-border)] text-lg leading-none disabled:text-[color-mix(in_srgb,var(--site-text)_25%,transparent)]"
                    aria-label={message("ui.product.decreaseQuantity")}
                    disabled={loading}
                    onClick={() => requestQuantityUpdate(Math.max(0, item.quantity - 1))}
                >
                    -
                </button>
                <input
                    className="h-9 w-14 border border-[var(--site-border)] px-2 text-center text-sm font-bold outline-none"
                    aria-label={message("ui.product.quantity")}
                    inputMode="numeric"
                    min={0}
                    max={maxQuantity}
                    type="number"
                    value={draftQuantity}
                    disabled={quantityDisabled}
                    onBlur={() => commitQuantity()}
                    onChange={(event) => {
                        const nextValue = event.target.value;

                        if (nextValue === "" || /^\d+$/.test(nextValue)) {
                            setDraftQuantity(nextValue);
                        }
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.currentTarget.blur();
                        }
                    }}
                />
                <button
                    type="button"
                    className="grid h-9 w-9 place-items-center rounded-full border border-[var(--site-border)] text-lg leading-none disabled:text-[color-mix(in_srgb,var(--site-text)_25%,transparent)]"
                    aria-label={message("ui.product.increaseQuantity")}
                    disabled={cannotIncrease}
                    onClick={() => requestQuantityUpdate(item.quantity + 1)}
                >
                    +
                </button>
            </div>

            {removeConfirmationOpen ? (
                <div className="fixed inset-0 z-[90] grid place-items-center bg-[color-mix(in_srgb,var(--site-text)_45%,transparent)] px-4" role="presentation">
                    <div
                        className="w-full max-w-sm rounded-2xl bg-[var(--site-surface)] p-6 shadow-[0_20px_70px_var(--site-shadow-color)]"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={`remove-cart-item-title-${item.uuid}`}
                    >
                        <h2 id={`remove-cart-item-title-${item.uuid}`} className="text-lg font-black">
                            {message("ui.cart.removeProductTitle")}
                        </h2>
                        <p className="mt-3 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]">
                            {message("ui.cart.removeProductConfirm", { name: item.name })}
                        </p>
                        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                            <button
                                type="button"
                                className="inline-flex justify-center rounded-full border border-[var(--site-border)] px-5 py-3 text-sm font-bold hover:border-[var(--site-border-strong)]"
                                onClick={cancelRemoval}
                            >
                                {message("ui.common.cancel")}
                            </button>
                            <button
                                type="button"
                                className="inline-flex justify-center rounded-full bg-[var(--site-primary)] px-5 py-3 text-sm font-bold text-[var(--site-on-primary)] hover:bg-[color-mix(in_srgb,var(--site-primary)_84%,var(--site-text))] disabled:opacity-60"
                                disabled={loading}
                                onClick={confirmRemoval}
                            >
                                {message("ui.common.remove")}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
}

function CheckoutProgressStepper({
    currentIndex,
    maxReachableIndex,
    stepHrefs,
    onSelectStep,
}: {
    currentIndex: number;
    maxReachableIndex: number;
    stepHrefs: [string, string, string];
    onSelectStep?: (index: number) => void;
}) {
    const message = useShopStorefrontMessage();
    const checkoutStepLabels = [
        message("ui.checkout.stepCart"),
        message("ui.checkout.stepDeliveryPayment"),
        message("ui.checkout.stepContact"),
    ];

    return (
        <div className="rounded-2xl bg-[var(--site-surface)] px-4 py-5">
            <div className="flex items-start">
                {checkoutStepLabels.map((label, index) => {
                    const active = index === currentIndex;
                    const completed = index < currentIndex;
                    const lit = index <= currentIndex;
                    const reachable = index <= maxReachableIndex;
                    const href = stepHrefs[index];
                    const circleClassName = classNames(
                        "mx-auto grid h-9 w-9 place-items-center rounded-full border-2 text-sm font-black",
                        lit
                            ? "border-[var(--site-primary)] bg-[var(--site-primary)] text-[var(--site-on-primary)]"
                            : classNames("border-[var(--site-border)] bg-[var(--site-surface)]", readableOnSurfaceTextClass),
                        active && "ring-4 ring-[var(--site-surface)]",
                        reachable && "cursor-pointer",
                    );
                    const labelClassName = classNames(
                        "mt-2 block max-w-24 text-xs font-black sm:max-w-none sm:text-sm",
                        lit ? "text-[var(--site-primary)]" : readableOnSurfaceTextClass,
                        reachable && "cursor-pointer hover:text-[var(--site-primary)]",
                    );
                    const stepContent = (
                        <>
                            <span className={circleClassName}>
                                {completed ? <Icon name="check" className="h-4 w-4" /> : index + 1}
                            </span>
                            <span className={labelClassName}>{label}</span>
                        </>
                    );

                    return (
                        <div key={label} className="flex flex-1 items-start last:flex-none">
                            <a
                                href={href}
                                aria-disabled={!reachable}
                                className={classNames(
                                    "min-w-0 text-center",
                                    !reachable && "pointer-events-none cursor-not-allowed",
                                )}
                                onClick={(event) => {
                                    if (!reachable || !onSelectStep) {
                                        return;
                                    }

                                    event.preventDefault();
                                    onSelectStep(index);
                                }}
                            >
                                {stepContent}
                            </a>
                            {index < checkoutStepLabels.length - 1 ? (
                                <div className="mx-2 mt-4 h-1 min-w-6 flex-1 rounded-full bg-[var(--site-border)] sm:mx-4">
                                    <div
                                        className={classNames(
                                            "h-full rounded-full transition-all",
                                            completed ? "w-full bg-[var(--site-primary)]" : "w-0 bg-[var(--site-primary)]",
                                        )}
                                    />
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function CartInlineSummary({
    block,
    cart,
    checkoutHref,
    freeShippingMessage,
    freeShippingProgress,
}: {
    block?: ShopTemplateBlock | null;
    cart: PublicCart;
    checkoutHref: string;
    freeShippingMessage?: string | null;
    freeShippingProgress?: CartFreeShippingProgress | null;
}) {
    const message = useShopStorefrontMessage();
    const { formatMoney } = useShopStorefrontFormatting();
    const blockedText = cartCheckoutBlockedText(cart, message);
    const disabled = cart.items.length === 0 || !cart.canCheckout;
    const summaryPriceClass = "font-bold tabular-nums text-[color-mix(in_srgb,var(--site-text)_70%,transparent)]";
    const itemsTotalWithVat = cart.itemsTotalPrice;
    const itemsTotalWithoutVat = cart.itemsNetPrice;

    return (
        <div className="border-t border-[var(--site-border)] bg-[linear-gradient(135deg,var(--site-primary-soft),var(--site-surface)_48%,var(--site-background))] px-4 py-6 sm:px-6">
            <div className="mx-auto max-w-[var(--site-container)]">
                <aside className="rounded-[32px] bg-[color-mix(in_srgb,var(--site-surface)_70%,transparent)] p-5 lg:p-6">
                    <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
                        <div className="order-2 space-y-3 text-sm lg:order-2">
                            <div className="flex justify-between gap-4"><span {...editableTextPart("priceWithoutVatText")} className="text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]" style={partStyle(block, "priceWithoutVatText")}>{richTextContent(block, "priceWithoutVatText", "Cena bez DPH")}</span><strong className={summaryPriceClass}>{formatMoney(itemsTotalWithoutVat, cart.currencyCode)}</strong></div>
                            <div className="flex justify-between gap-4"><span {...editableTextPart("priceWithVatText")} className="text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]" style={partStyle(block, "priceWithVatText")}>{richTextContent(block, "priceWithVatText", "Cena s DPH")}</span><strong className={summaryPriceClass}>{formatMoney(itemsTotalWithVat, cart.currencyCode)}</strong></div>
                            <div className="flex items-end justify-between gap-4 pt-2">
                                <span {...editableTextPart("totalText")} className="font-black text-[var(--site-text)]" style={partStyle(block, "totalText")}>{richTextContent(block, "totalText", "Celkem")}</span>
                                <strong className="text-2xl font-black tabular-nums text-[var(--site-primary)]">{formatMoney(itemsTotalWithVat, cart.currencyCode)}</strong>
                            </div>
                            {blockedText ? (
                                <p className="rounded-2xl border border-[var(--site-primary)] bg-[var(--site-primary-soft)] px-3 py-2 text-sm font-bold text-[var(--site-primary)]">
                                    {blockedText}
                                </p>
                            ) : null}
                            <a
                                {...editableTextPart("checkoutButton")}
                                href={checkoutHref}
                                aria-disabled={disabled}
                                className={classNames(
                                    "block w-full rounded-full px-5 py-4 text-center text-sm font-black text-[var(--site-on-inverse)] shadow-[0_14px_28px_var(--site-shadow-color)]",
                                    disabled ? "pointer-events-none bg-[color-mix(in_srgb,var(--site-text)_25%,transparent)] shadow-none" : "bg-[var(--site-primary)]",
                                )}
                                style={partStyle(block, "checkoutButton")}
                            >
                                {richTextContent(block, "checkoutButtonLabel", "Pokračovat k dopravě a platbě")}
                            </a>
                        </div>

                        <div className="order-1 grid gap-3 lg:order-1">
                            {freeShippingProgress ? (
                                <div className="rounded-[24px] bg-[var(--site-surface)]/80 p-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <p className="font-black text-[var(--site-primary)]">{freeShippingProgress.label}</p>
                                            <p className="mt-1 text-xs font-bold text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">{freeShippingProgress.detail}</p>
                                        </div>
                                        <span className="rounded-full bg-[color-mix(in_srgb,var(--site-surface)_80%,transparent)] px-3 py-1 text-xs font-black text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">
                                            {Math.round(freeShippingProgress.progress)} %
                                        </span>
                                    </div>
                                    <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-[var(--site-surface)]">
                                        <div
                                            className="h-full rounded-full bg-[var(--site-primary)] transition-all"
                                            style={{ width: `${freeShippingProgress.progress}%` }}
                                        />
                                    </div>
                                </div>
                            ) : freeShippingMessage ? (
                                <p className="rounded-[24px] bg-[var(--site-surface)]/80 px-4 py-3 text-sm font-bold text-[var(--site-primary)]">
                                    {freeShippingMessage}
                                </p>
                            ) : null}
                            <div className="rounded-[24px] bg-[color-mix(in_srgb,var(--site-surface)_65%,transparent)] px-4 py-3">
                                <p {...editableTextPart("couponTitle")} className="text-xs font-black uppercase tracking-[0.14em] text-[color-mix(in_srgb,var(--site-text)_35%,transparent)]" style={partStyle(block, "couponTitle")}>{richTextContent(block, "couponTitle", "Slevový kód")}</p>
                                <p {...editableTextPart("couponText")} className="mt-1 text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]" style={partStyle(block, "couponText")}>{richTextContent(block, "couponText", "Vyhrazené místo pro kupón nebo dárkový poukaz.")}</p>
                            </div>
                        </div>

                    </div>
                </aside>
            </div>
        </div>
    );
}

function CartItemsPreview({
    block,
    cart,
    loading,
    error,
    cartHref = "",
    checkoutHref,
    freeShippingMessage,
    freeShippingProgress,
    showSummary = false,
    onUpdateItem,
    onRemoveItem,
}: {
    block?: ShopTemplateBlock | null;
    cart: PublicCart;
    loading: boolean;
    error: string | null;
    cartHref?: string;
    checkoutHref?: string;
    freeShippingMessage?: string | null;
    freeShippingProgress?: CartFreeShippingProgress | null;
    showSummary?: boolean;
    onUpdateItem: (itemUuid: string, quantity: number) => void;
    onRemoveItem: (itemUuid: string) => void;
}) {
    const message = useShopStorefrontMessage();
    const { formatMoney } = useShopStorefrontFormatting();
    const storefrontHref = useShopStorefrontHref();
    const backToProductHref = storefrontHref(cart.items[0]?.slug ? `/${cart.items[0].slug}` : "/");
    const shouldShowSummary = showSummary && checkoutHref !== undefined;
    const checkoutBaseHref = checkoutBaseHrefFromStepHref(checkoutHref ?? cartHref);
    const maxReachableStepIndex = cart.items.length > 0 && cart.canCheckout ? 1 : 0;
    const stepHrefs: [string, string, string] = [
        cartHref,
        checkoutHref ?? checkoutStepHref(checkoutBaseHref, 2),
        checkoutStepHref(checkoutBaseHref, 3),
    ];

    return (
        <section className="px-5 py-10 sm:px-6" style={blockVisualStyle(block)}>
            <div className="mx-auto max-w-[var(--site-container)]">
                <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                    <h1 {...editableTextPart("title")} className="text-4xl font-black" style={partStyle(block, "title")}>{richTextContent(block, "title", "Košík")}</h1>
                    {cart.items.length > 0 ? (
                        <a
                            {...editableTextPart("backButton")}
                            href={backToProductHref}
                            className="inline-flex items-center justify-center rounded-full border border-[var(--site-border)] px-5 py-3 text-sm font-bold"
                            style={partStyle(block, "backButton")}
                        >
                            {richTextContent(block, "backButtonLabel", "Zpět k nákupu")}
                        </a>
                    ) : null}
                </div>
                {error ? (
                    <p className="mt-6 border border-[var(--site-primary)] bg-[var(--site-primary-soft)] px-4 py-3 text-sm font-bold text-[var(--site-primary)]">
                        {error}
                    </p>
                ) : null}
                {loading ? <p className={classNames("mt-6 text-sm", readableOnSurfaceTextClass)}>{message("ui.cart.loading")}</p> : null}
                <div className="mt-6 overflow-hidden rounded-3xl border border-[var(--site-border)] bg-[var(--site-surface)] shadow-[0_8px_28px_var(--site-shadow-color)]">
                    <div className="border-b border-[var(--site-border)] px-3 py-3 sm:px-5 sm:py-5">
                        <CheckoutProgressStepper currentIndex={0} maxReachableIndex={maxReachableStepIndex} stepHrefs={stepHrefs} />
                    </div>
                    {!loading && cart.items.length === 0 ? (
                        <div className={classNames("p-6 text-sm sm:p-8", readableOnSurfaceTextClass)}>
                            <span {...editableTextPart("emptyText")} style={partStyle(block, "emptyText")}>{richTextContent(block, "emptyText", "Košík je prázdný.")}</span>
                        </div>
                    ) : null}
                    {cart.items.length > 0 ? (
                        <div>
                            <div className={classNames("hidden grid-cols-[minmax(0,1fr)_150px_110px_130px_44px] gap-4 border-b border-[var(--site-border)] bg-[var(--site-surface)] px-5 py-3 text-xs font-black uppercase lg:grid", readableOnSurfaceTextClass)}>
                                <span {...editableTextPart("productColumnText")} style={partStyle(block, "productColumnText")}>{richTextContent(block, "productColumnText", "Produkt")}</span>
                                <span {...editableTextPart("quantityColumnText")} className="text-center" style={partStyle(block, "quantityColumnText")}>{richTextContent(block, "quantityColumnText", "Množství")}</span>
                                <span {...editableTextPart("unitPriceColumnText")} className="text-right" style={partStyle(block, "unitPriceColumnText")}>{richTextContent(block, "unitPriceColumnText", "Cena / ks")}</span>
                                <span {...editableTextPart("priceColumnText")} className="text-right" style={partStyle(block, "priceColumnText")}>{richTextContent(block, "priceColumnText", "Cena")}</span>
                                <span />
                            </div>
                            {cart.items.map((item) => {
                                const notice = cartItemNoticeText(item, message);
                                const productHref = item.slug ? storefrontHref(`/${item.slug}`) : null;

                                return (
                                    <article
                                        key={item.uuid}
                                        className={classNames(
                                            "grid gap-4 border-b border-[var(--site-border)] px-4 py-4 last:border-b-0 lg:grid-cols-[minmax(0,1fr)_150px_110px_130px_44px] lg:items-center lg:px-5",
                                            !item.validForCheckout && "bg-[color-mix(in_srgb,var(--site-primary-soft)_50%,transparent)]",
                                        )}
                                    >
                                        <div className="grid min-w-0 grid-cols-[72px_minmax(0,1fr)] gap-3">
                                            {productHref ? (
                                                <a href={productHref} className={classNames("grid h-[72px] w-[72px] place-items-center overflow-hidden rounded-2xl bg-[var(--site-surface)] text-xs font-bold uppercase hover:text-[var(--site-primary)]", readableOnSurfaceTextClass)}>
                                                    {item.primaryImage ? <img alt={item.primaryImage.alt || item.name} className="h-full w-full object-cover" src={item.primaryImage.thumbnailUrl ?? item.primaryImage.url} /> : item.name.slice(0, 2)}
                                                </a>
                                            ) : (
                                                <div className={classNames("grid h-[72px] w-[72px] place-items-center overflow-hidden rounded-2xl bg-[var(--site-surface)] text-xs font-bold uppercase", readableOnSurfaceTextClass)}>
                                                    {item.primaryImage ? <img alt={item.primaryImage.alt || item.name} className="h-full w-full object-cover" src={item.primaryImage.thumbnailUrl ?? item.primaryImage.url} /> : item.name.slice(0, 2)}
                                                </div>
                                            )}
                                            <div className="min-w-0 self-center">
                                                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                                                    <h2 className="truncate font-black">
                                                        {productHref ? (
                                                            <a href={productHref} className="hover:text-[var(--site-primary)]">
                                                                {item.name}
                                                            </a>
                                                        ) : item.name}
                                                    </h2>
                                                    {item.variantName ? <span className={classNames("w-fit rounded-full bg-[var(--site-surface)] px-2.5 py-1 text-xs font-bold", readableOnSurfaceTextClass)}>{item.variantName}</span> : null}
                                                </div>
                                                <CartItemSurchargesSummary item={item} />
                                                <ManufacturingSpecificationSummary appearance="laguna" message={message} specification={item.manufacturingSpecification}/>
                                                <p className={classNames("mt-1 text-sm font-semibold", item.validForCheckout ? readableOnSurfaceTextClass : "text-[var(--site-primary)]")}>
                                                    {cartItemStockText(item, message)}
                                                </p>
                                                {notice ? (
                                                    <p className="mt-2 max-w-xl rounded-xl border border-[var(--site-accent)] bg-[var(--site-accent-soft)] px-3 py-2 text-sm font-bold text-[var(--site-text)]">
                                                        {notice}
                                                    </p>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="lg:justify-self-center">
                                            <CartQuantityControl item={item} loading={loading} onUpdateItem={onUpdateItem} />
                                        </div>
                                        <div className="flex items-center justify-between text-sm lg:block lg:text-right">
                                            <span className={classNames("font-bold lg:hidden", readableOnSurfaceTextClass)}>{message("ui.product.unitPrice")}</span>
                                            <span className={classNames("font-bold", readableOnSurfaceTextClass)}>
                                                {item.quantity > 1 ? formatMoney(item.unitPrice) : "-"}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between lg:block lg:text-right">
                                            <span className={classNames("text-sm font-bold lg:hidden", readableOnSurfaceTextClass)}>{message("ui.product.price")}</span>
                                            <strong className="text-lg">{formatMoney(item.totalPrice)}</strong>
                                        </div>
                                        <button
                                            type="button"
                                            className={classNames("inline-grid h-9 w-9 place-items-center rounded-full border border-[var(--site-border)] hover:text-[var(--site-text)] lg:justify-self-end", readableOnSurfaceTextClass)}
                                            aria-label={message("ui.common.remove")}
                                            disabled={loading}
                                            onClick={() => onRemoveItem(item.uuid)}
                                        >
                                            <Icon name="trash" className="h-4 w-4" />
                                        </button>
                                    </article>
                                );
                            })}
                        </div>
                    ) : null}
                    {shouldShowSummary ? (
                        <CartInlineSummary
                            block={block}
                            cart={cart}
                            checkoutHref={checkoutHref}
                            freeShippingMessage={freeShippingMessage}
                            freeShippingProgress={freeShippingProgress}
                        />
                    ) : null}
                </div>
            </div>
        </section>
    );
}

function CartSummaryPreview({
    block,
    cart,
    checkoutHref,
    showAction = true,
    actionLabel,
    carrierPrice,
    paymentSurcharge,
    totalPrice,
    freeShippingMessage,
    freeShippingProgress,
}: {
    block?: ShopTemplateBlock | null;
    cart: PublicCart;
    checkoutHref: string;
    showAction?: boolean;
    actionLabel?: string;
    carrierPrice?: string;
    paymentSurcharge?: string;
    totalPrice?: string;
    freeShippingMessage?: string | null;
    freeShippingProgress?: CartFreeShippingProgress | null;
}) {
    const message = useShopStorefrontMessage();
    const { formatMoney } = useShopStorefrontFormatting();
    const blockedText = cartCheckoutBlockedText(cart, message);
    const disabled = cart.items.length === 0 || !cart.canCheckout;
    const displayedCarrierPrice = carrierPrice ?? cart.carrierPrice;
    const displayedPaymentSurcharge = paymentSurcharge ?? cart.paymentSurcharge;
    const displayedTotalPrice = totalPrice ?? cart.totalPrice;
    const summaryPriceClass = classNames("font-semibold tabular-nums", readableOnSurfaceTextClass);

    return (
        <section className="px-5 py-10 sm:px-6" style={blockVisualStyle(block)}>
            <aside className="mx-auto max-w-md rounded-[32px] border border-[var(--site-border)] bg-[var(--site-surface)] p-6 shadow-[0_22px_55px_var(--site-shadow-color)]">
                <h2 {...editableTextPart("title")} className="text-lg font-black" style={partStyle(block, "title")}>{richTextContent(block, "title", "Souhrn objednávky")}</h2>
                {cart.items.length > 0 ? (
                    <div className="mt-5 space-y-3 border-b border-[var(--site-border)] pb-5">
                        {cart.items.map((item) => {
                            const itemName = item.quantity > 1 ? `${item.quantity}x ${item.name}` : item.name;

                            return (
                                <div key={item.uuid} className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 text-sm">
                                    <div className="min-w-0">
                                        <p className="truncate font-black">{itemName}</p>
                                        {item.variantName ? (
                                            <p className={classNames("mt-0.5 truncate text-xs font-semibold", readableOnSurfaceTextClass)}>{item.variantName}</p>
                                        ) : null}
                                        <CartItemSurchargesSummary item={item} compact />
                                        <ManufacturingSpecificationSummary appearance="laguna" compact message={message} specification={item.manufacturingSpecification}/>
                                    </div>
                                    <strong className="whitespace-nowrap tabular-nums">{formatMoney(item.totalPrice)}</strong>
                                </div>
                            );
                        })}
                    </div>
                ) : null}
                <div className="mt-5 space-y-3 text-sm">
                    <div className="flex justify-between gap-4"><span {...editableTextPart("subtotalText")} style={partStyle(block, "subtotalText")}>{richTextContent(block, "subtotalText", "Mezisoučet")}</span><strong className={summaryPriceClass}>{formatMoney(cart.itemsTotalPrice)}</strong></div>
                    <div className="flex justify-between gap-4"><span {...editableTextPart("deliveryText")} style={partStyle(block, "deliveryText")}>{richTextContent(block, "deliveryText", "Doprava")}</span><strong className={summaryPriceClass}>{formatMoney(displayedCarrierPrice)}</strong></div>
                    <div className="flex justify-between gap-4"><span {...editableTextPart("paymentText")} style={partStyle(block, "paymentText")}>{richTextContent(block, "paymentText", "Platba")}</span><strong className={summaryPriceClass}>{formatMoney(displayedPaymentSurcharge)}</strong></div>
                    <div className="flex justify-between gap-4 border-t border-[var(--site-border)] pt-3 text-base"><span {...editableTextPart("totalText")} style={partStyle(block, "totalText")}>{richTextContent(block, "totalText", "Celkem")}</span><strong className="tabular-nums">{formatMoney(displayedTotalPrice)}</strong></div>
                </div>
                {freeShippingProgress ? (
                    <div className="mt-4 rounded-[24px] border border-[var(--site-border)] bg-[var(--site-surface)] p-4">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <p className="font-black text-[var(--site-primary)]">{freeShippingProgress.label}</p>
                                <p className={classNames("mt-1 text-xs font-bold", readableOnSurfaceTextClass)}>{freeShippingProgress.detail}</p>
                            </div>
                            <span className={classNames("rounded-full bg-[var(--site-surface)] px-3 py-1 text-xs font-black", readableOnSurfaceTextClass)}>
                                {Math.round(freeShippingProgress.progress)} %
                            </span>
                        </div>
                        <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-[var(--site-surface)]">
                            <div
                                className="h-full rounded-full bg-[var(--site-primary)] transition-all"
                                style={{ width: `${freeShippingProgress.progress}%` }}
                            />
                        </div>
                    </div>
                ) : freeShippingMessage ? (
                    <p className="mt-4 rounded-[24px] border border-[var(--site-border)] bg-[var(--site-surface)] px-4 py-3 text-sm font-bold text-[var(--site-primary)]">
                        {freeShippingMessage}
                    </p>
                ) : null}
                {blockedText ? (
                    <p className="mt-4 rounded-2xl border border-[var(--site-primary)] bg-[var(--site-primary-soft)] px-3 py-2 text-sm font-bold text-[var(--site-primary)]">
                        {blockedText}
                    </p>
                ) : null}
                {showAction ? (
                    <a
                        {...editableTextPart("checkoutButton")}
                        href={checkoutHref}
                        aria-disabled={disabled}
                        className={classNames(
                            "mt-6 block w-full rounded-full px-5 py-4 text-center text-sm font-black text-[var(--site-on-inverse)] shadow-[0_14px_28px_var(--site-shadow-color)]",
                            disabled ? "pointer-events-none bg-[color-mix(in_srgb,var(--site-text)_25%,transparent)] shadow-none" : "bg-[var(--site-primary)]",
                        )}
                        style={partStyle(block, "checkoutButton")}
                    >
                        {richTextContent(block, "checkoutButtonLabel", actionLabel ?? message("block.systemCartSummary.checkoutButtonLabel"))}
                    </a>
                ) : null}
            </aside>
        </section>
    );
}

function CheckoutOrderColumn({
    cart,
    delivery,
    payment,
    pickupPoint,
}: {
    cart: PublicCart;
    delivery: PublicCheckoutDeliveryOption | null;
    payment: PublicCheckoutPaymentOption | null;
    pickupPoint: PickupPointSelection | null;
}) {
    const message = useShopStorefrontMessage();
    const { formatMoney } = useShopStorefrontFormatting();
    const totalWithVat = cart.totalPrice;
    const totalWithoutVat = cart.totalNetPrice;

    return (
        <aside className="rounded-[32px] bg-[color-mix(in_srgb,var(--site-surface)_75%,transparent)] p-5 lg:sticky lg:top-6">
            <div>
                <p className={classNames("text-xs font-black uppercase tracking-[0.16em]", readableOnSurfaceTextClass)}>{message("ui.checkout.orderItems")}</p>
                <div className="mt-4 grid gap-3">
                    {cart.items.map((item) => (
                        <div key={item.uuid} className="grid grid-cols-[48px_minmax(0,1fr)_auto] items-center gap-3">
                            <span className={classNames("grid h-12 w-12 place-items-center overflow-hidden rounded-2xl bg-[var(--site-surface)] text-xs font-black uppercase", readableOnSurfaceTextClass)}>
                                {item.primaryImage ? <img alt={item.primaryImage.alt || item.name} className="h-full w-full object-cover" src={item.primaryImage.thumbnailUrl ?? item.primaryImage.url} /> : item.name.slice(0, 2)}
                            </span>
                            <div className="min-w-0">
                                <p className="truncate text-sm font-black">{item.quantity > 1 ? `${item.quantity}x ${item.name}` : item.name}</p>
                                {item.variantName ? <p className={classNames("truncate text-xs font-semibold", readableOnSurfaceTextClass)}>{item.variantName}</p> : null}
                                <CartItemSurchargesSummary item={item} compact />
                                <ManufacturingSpecificationSummary appearance="laguna" compact message={message} specification={item.manufacturingSpecification}/>
                            </div>
                            <strong className="text-sm tabular-nums">{formatMoney(item.totalPrice)}</strong>
                        </div>
                    ))}
                </div>
            </div>

            {delivery ? (
                <div className="mt-6 pt-5">
                    <p className={classNames("text-xs font-black uppercase tracking-[0.16em]", readableOnSurfaceTextClass)}>{message("ui.checkout.shipping")}</p>
                    <div className="mt-3 grid grid-cols-[38px_minmax(0,1fr)_auto] items-center gap-3">
                        <span className="grid h-9 w-9 place-items-center rounded-full bg-[var(--site-surface)] text-[var(--site-primary)]">
                            <Icon name={deliveryIcon(delivery)} className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                            <p className="truncate text-sm font-black">{delivery.carrierName} - {delivery.deliveryMethodName}</p>
                            {pickupPoint ? <p className={classNames("truncate text-xs font-semibold", readableOnSurfaceTextClass)}>{pickupPoint.name ?? pickupPoint.id}</p> : null}
                        </div>
                        <strong className="text-sm tabular-nums">{formatMoney(delivery.price)}</strong>
                    </div>
                </div>
            ) : null}

            {payment ? (
                <div className="mt-5 pt-5">
                    <p className={classNames("text-xs font-black uppercase tracking-[0.16em]", readableOnSurfaceTextClass)}>{message("ui.checkout.payment")}</p>
                    <div className="mt-3 grid grid-cols-[38px_minmax(0,1fr)_auto] items-center gap-3">
                        <span className="grid h-9 w-9 place-items-center rounded-full bg-[var(--site-surface)] text-[var(--site-primary)]">
                            <Icon name={paymentIcon(payment)} className="h-4 w-4" />
                        </span>
                        <p className="min-w-0 truncate text-sm font-black">{payment.paymentName}</p>
                        <strong className="text-sm tabular-nums">{formatMoney(payment.surcharge)}</strong>
                    </div>
                </div>
            ) : null}

            {delivery && payment ? (
                <div className="mt-6 pt-5">
                    <div className="flex justify-between gap-4 text-sm">
                <span className={classNames("font-bold", readableOnSurfaceTextClass)}>{message("ui.checkout.priceWithoutVat")}</span>
                        <strong className="tabular-nums">{formatMoney(totalWithoutVat, cart.currencyCode)}</strong>
                    </div>
                    <div className="mt-2 flex items-end justify-between gap-4">
                        <span className="font-black">{message("ui.checkout.priceWithVat")}</span>
                        <strong className="text-2xl font-black tabular-nums text-[var(--site-primary)]">{formatMoney(totalWithVat, cart.currencyCode)}</strong>
                    </div>
                </div>
            ) : null}
        </aside>
    );
}

function PickupPointSelector({
    delivery,
    selectedPickupPoint,
    onChange,
    packetaApiKey,
    error,
}: {
    delivery: PublicCheckoutDeliveryOption;
    selectedPickupPoint: PickupPointSelection | null;
    onChange: (pickupPoint: PickupPointSelection | null) => void;
    packetaApiKey?: string;
    error: string | null;
}) {
    const message = useShopStorefrontMessage();
    const languageCode = useContext(ShopStorefrontLanguageContext);
    const [manualId, setManualId] = useState(selectedPickupPoint?.id ?? "");
    const [manualName, setManualName] = useState(selectedPickupPoint?.name ?? "");
    const [widgetLoading, setWidgetLoading] = useState(false);
    const [widgetError, setWidgetError] = useState<string | null>(null);
    const provider = delivery.pickupPointProvider ?? delivery.carrierCode ?? "pickup_point";
    const canUsePacketaWidget = provider === "packeta" && typeof packetaApiKey === "string" && packetaApiKey.trim() !== "";

    useEffect(() => {
        setManualId(selectedPickupPoint?.id ?? "");
        setManualName(selectedPickupPoint?.name ?? "");
    }, [selectedPickupPoint?.id, selectedPickupPoint?.name]);

    function commitManualSelection(id: string, name: string) {
        const trimmedId = id.trim();
        const trimmedName = name.trim();

        if (trimmedId === "") {
            onChange(null);

            return;
        }

        onChange({
            provider,
            id: trimmedId,
            name: trimmedName !== "" ? trimmedName : trimmedId,
        });
    }

    async function openPacketaWidget() {
        if (!canUsePacketaWidget || !packetaApiKey) {
            return;
        }

        setWidgetLoading(true);
        setWidgetError(null);

        try {
            const loaded = await ensurePacketaWidget();

            if (!loaded || !window.Packeta?.Widget) {
                setWidgetError(message("ui.checkout.pickupMapError"));

                return;
            }

            window.Packeta.Widget.pick(
                packetaApiKey,
                (point) => {
                    if (!point?.id) {
                        return;
                    }

                    const id = String(point.id);
                    const name = packetaPointDisplayName(point) ?? id;

                    setManualId(id);
                    setManualName(name);
                    onChange({ provider, id, name });
                },
                {
                    language: (languageCode ?? "en").split(/[-_]/, 1)[0],
                },
            );
        } finally {
            setWidgetLoading(false);
        }
    }

    return (
        <div className="mt-4 rounded-2xl border border-[var(--site-border)] bg-[var(--site-surface)] p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h3 className="font-black">{message("ui.checkout.pickupPoint")}</h3>
                    <p className="mt-1 text-sm text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">
                        {message("ui.checkout.pickupExplanation")}
                    </p>
                    {selectedPickupPoint ? (
                        <p className="mt-3 border border-[var(--site-border)] bg-[var(--site-surface)] px-3 py-2 text-sm font-bold">
                            {selectedPickupPoint.name ?? selectedPickupPoint.id}
                        </p>
                    ) : null}
                </div>
                {canUsePacketaWidget ? (
                    <button
                        type="button"
                            className="shrink-0 rounded-full bg-[var(--site-primary)] px-5 py-3 text-sm font-black text-[var(--site-on-primary)] disabled:bg-[color-mix(in_srgb,var(--site-text)_25%,transparent)]"
                        disabled={widgetLoading}
                        onClick={() => void openPacketaWidget()}
                    >
                        {widgetLoading ? message("ui.checkout.loadingMap") : message("ui.checkout.selectOnMap")}
                    </button>
                ) : null}
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-[180px_minmax(0,1fr)]">
                <input
                    className="rounded-xl border border-[var(--site-border)] bg-[var(--site-surface)] px-4 py-3 text-sm"
                    placeholder={message("ui.checkout.pickupCode")}
                    value={manualId}
                    onBlur={() => commitManualSelection(manualId, manualName)}
                    onChange={(event) => setManualId(event.target.value)}
                />
                <input
                    className="rounded-xl border border-[var(--site-border)] bg-[var(--site-surface)] px-4 py-3 text-sm"
                    placeholder={message("ui.checkout.pickupPlaceholder")}
                    value={manualName}
                    onBlur={() => commitManualSelection(manualId, manualName)}
                    onChange={(event) => setManualName(event.target.value)}
                />
            </div>
            {widgetError ? <p className="mt-3 text-sm font-bold text-[var(--site-primary)]">{widgetError}</p> : null}
            {error ? <p className="mt-3 text-sm font-bold text-[var(--site-primary)]">{error}</p> : null}
        </div>
    );
}

function deliveryIcon(option: PublicCheckoutDeliveryOption): IconName {
    if (option.requiresPickupPoint) {
        return "mapPin";
    }

    if (option.deliveryMethodCode.toLowerCase().includes("pickup")) {
        return "store";
    }

    return "truck";
}

function paymentIcon(option: PublicCheckoutPaymentOption): IconName {
    const code = (option.paymentCode ?? option.paymentName).toLowerCase();

    if (code.includes("cash") || code.includes("dobirka")) {
        return "bag";
    }

    return "creditCard";
}

function matchesStorefrontCountry(option: CheckoutCountryOption, value: string, locale: string, exact = false): boolean {
    const typed = value.trim();
    const combined = /^(.*?)\s*\(([A-Za-z]{2})\)$/u.exec(typed);
    const label = option.label.toLocaleLowerCase(locale);
    const query = (combined ? combined[1] : typed).trim().toLocaleLowerCase(locale);
    const labelMatches = exact ? label === query : label.includes(query);

    if (combined) return labelMatches && option.code === combined[2].toUpperCase();

    const code = typed.toUpperCase();
    return labelMatches || (exact ? option.code === code : option.code.includes(code));
}

function CountryCombobox({
    id,
    value,
    options,
    placeholder,
    fieldClass,
    required = false,
    onChange,
}: {
    id: string;
    value: string;
    options: CheckoutCountryOption[];
    placeholder: string;
    fieldClass: string;
    required?: boolean;
    onChange: (countryCode: string) => void;
}) {
    const message = useShopStorefrontMessage();
    const { locale } = useShopStorefrontFormatting();
    const selectedOption = options.find((option) => option.code === value.trim().toUpperCase()) ?? null;
    const selectedLabel = selectedOption ? `${selectedOption.label} (${selectedOption.code})` : value;
    const [query, setQuery] = useState(selectedLabel);
    const [open, setOpen] = useState(false);
    const normalizedQuery = query.trim().toLowerCase();
    const visibleOptions = normalizedQuery === ""
        ? options
        : options.filter((option) => matchesStorefrontCountry(option, query, locale));

    useEffect(() => {
        setQuery(selectedLabel);
    }, [selectedLabel]);

    function selectOption(option: CheckoutCountryOption) {
        onChange(option.code);
        setQuery(`${option.label} (${option.code})`);
        setOpen(false);
    }

    function commitTypedValue() {
        const typed = query.trim();
        const exactOption = options.find((option) => matchesStorefrontCountry(option, typed, locale, true));

        if (exactOption) {
            selectOption(exactOption);

            return;
        }

        setQuery(selectedLabel);
        setOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
        if (event.key === "Enter" && open && visibleOptions[0]) {
            event.preventDefault();
            selectOption(visibleOptions[0]);
        }

        if (event.key === "Escape") {
            setQuery(selectedLabel);
            setOpen(false);
        }
    }

    return (
        <div className="relative">
            <input
                id={id}
                className={`${fieldClass} pr-11`}
                autoComplete="country"
                placeholder={placeholder}
                required={required}
                value={query}
                onBlur={commitTypedValue}
                onChange={(event) => {
                    setQuery(event.currentTarget.value);
                    setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={handleKeyDown}
            />
            <button
                type="button"
                className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-[color-mix(in_srgb,var(--site-text)_45%,transparent)] hover:bg-[var(--site-surface)] hover:text-[var(--site-text)]"
                aria-label={message("ui.checkout.expandCountries")}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                    setQuery(selectedLabel);
                    setOpen((current) => !current);
                }}
            >
                <Icon name="chevronDown" className={classNames("h-4 w-4 transition-transform", open && "rotate-180")} />
            </button>
            {open && visibleOptions.length > 0 ? (
                <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-56 overflow-y-auto rounded-xl border border-[var(--site-border)] bg-[var(--site-surface)] p-1 shadow-[0_12px_32px_var(--site-shadow-color)]">
                    {visibleOptions.map((option) => (
                        <button
                            key={option.code}
                            type="button"
                            className={classNames(
                                "grid w-full grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-[var(--site-surface)]",
                                option.code === selectedOption?.code && "bg-[var(--site-surface)] text-[var(--site-primary)]",
                            )}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => selectOption(option)}
                        >
                            <span className="font-bold">{option.label}</span>
                            <span className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{option.code}</span>
                            <Icon
                                name="check"
                                className={classNames(
                                    "h-4 w-4",
                                    option.code === selectedOption?.code ? "opacity-100" : "opacity-0",
                                )}
                            />
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function ContactFormPreview({
    block,
    compact = false,
    form,
    countryOptions,
    submitting,
    submitDisabled,
    error,
    onChange,
    onSubmit,
}: {
    block?: ShopTemplateBlock | null;
    compact?: boolean;
    form: CheckoutFormState;
    countryOptions: CheckoutCountryOption[];
    submitting: boolean;
    submitDisabled: boolean;
    error: string | null;
    onChange: (patch: Partial<CheckoutFormState>) => void;
    onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
    const message = useShopStorefrontMessage();
    const fieldIdPrefix = useId();
    const fieldClass = "rounded-xl border border-[var(--site-border)] bg-[var(--site-surface)] px-4 py-3 text-sm outline-none transition focus:border-[var(--site-primary)]";
    const fieldGroupClass = "grid min-w-0 gap-1.5";
    const fieldLabelClass = "text-xs font-bold text-[color-mix(in_srgb,var(--site-text)_72%,transparent)]";
    const billingOpen = !form.billingSameAsDelivery;
    const deliveryLabels = {
        city: blockText(block, "cityInput", message("block.systemCheckoutSteps.cityInput")),
        country: blockText(block, "countryInput", message("block.systemCheckoutSteps.countryInput")),
        email: blockText(block, "emailInput", message("block.systemCheckoutSteps.emailInput")),
        firstname: blockText(block, "firstnameInput", message("block.systemCheckoutSteps.firstnameInput")),
        lastname: blockText(block, "lastnameInput", message("block.systemCheckoutSteps.lastnameInput")),
        note: blockText(block, "noteInput", message("block.systemCheckoutSteps.noteInput")),
        phone: blockText(block, "phoneInput", message("block.systemCheckoutSteps.phoneInput")),
        postcode: blockText(block, "postcodeInput", message("block.systemCheckoutSteps.postcodeInput")),
        street: blockText(block, "streetInput", message("block.systemCheckoutSteps.streetInput")),
    };
    const billingLabels = {
        city: message("ui.checkout.billingCity"),
        company: message("ui.checkout.company"),
        companyId: message("ui.checkout.companyId"),
        country: message("ui.checkout.billingCountry"),
        firstname: message("ui.checkout.billingFirstName"),
        lastname: message("ui.checkout.billingLastName"),
        phone: message("ui.checkout.billingPhone"),
        postcode: message("ui.checkout.billingPostcode"),
        street: message("ui.checkout.billingStreet"),
        vatId: message("ui.checkout.vatId"),
    };

    return (
        <form
            className={classNames("grid gap-5 rounded-2xl border border-[var(--site-border)] bg-[var(--site-surface)]", compact ? "p-4" : "p-6")}
            onSubmit={onSubmit}
        >
            <section className="grid gap-4">
                <div>
                    <p {...editableTextPart("deliveryEyebrow")} className={classNames("text-xs font-bold uppercase", readableOnSurfaceTextClass)} style={partStyle(block, "deliveryEyebrow")}>{richTextContent(block, "deliveryEyebrow", "1. krok")}</p>
                    <h2 {...editableTextPart("deliveryFormTitle")} className={classNames("font-black", compact ? "text-lg" : "text-xl")} style={partStyle(block, "deliveryFormTitle")}>{richTextContent(block, "deliveryFormTitle", "Dodací údaje")}</h2>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                    <label className={fieldGroupClass} htmlFor={`${fieldIdPrefix}-delivery-email`}>
                        <span className={fieldLabelClass}>{deliveryLabels.email} <span aria-hidden="true">*</span></span>
                        <input
                            id={`${fieldIdPrefix}-delivery-email`}
                            className={fieldClass}
                            type="email"
                            autoComplete="email"
                            {...editablePart("emailInput")}
                            placeholder={deliveryLabels.email}
                            required
                            value={form.email}
                            onChange={(event) => onChange({ email: event.target.value })}
                        />
                    </label>
                    <label className={fieldGroupClass} htmlFor={`${fieldIdPrefix}-delivery-firstname`}>
                        <span className={fieldLabelClass}>{deliveryLabels.firstname} <span aria-hidden="true">*</span></span>
                        <input
                            id={`${fieldIdPrefix}-delivery-firstname`}
                            className={fieldClass}
                            autoComplete="given-name"
                            {...editablePart("firstnameInput")}
                            placeholder={deliveryLabels.firstname}
                            required
                            value={form.firstname}
                            onChange={(event) => onChange({ firstname: event.target.value })}
                        />
                    </label>
                    <label className={fieldGroupClass} htmlFor={`${fieldIdPrefix}-delivery-lastname`}>
                        <span className={fieldLabelClass}>{deliveryLabels.lastname} <span aria-hidden="true">*</span></span>
                        <input
                            id={`${fieldIdPrefix}-delivery-lastname`}
                            className={fieldClass}
                            autoComplete="family-name"
                            {...editablePart("lastnameInput")}
                            placeholder={deliveryLabels.lastname}
                            required
                            value={form.lastname}
                            onChange={(event) => onChange({ lastname: event.target.value })}
                        />
                    </label>
                    <label className={fieldGroupClass} htmlFor={`${fieldIdPrefix}-delivery-phone`}>
                        <span className={fieldLabelClass}>{deliveryLabels.phone}</span>
                        <input
                            id={`${fieldIdPrefix}-delivery-phone`}
                            className={fieldClass}
                            type="tel"
                            autoComplete="tel"
                            {...editablePart("phoneInput")}
                            placeholder={deliveryLabels.phone}
                            value={form.phone}
                            onChange={(event) => onChange({ phone: event.target.value })}
                        />
                    </label>
                    <label className={fieldGroupClass} htmlFor={`${fieldIdPrefix}-delivery-street`}>
                        <span className={fieldLabelClass}>{deliveryLabels.street} <span aria-hidden="true">*</span></span>
                        <input
                            id={`${fieldIdPrefix}-delivery-street`}
                            className={fieldClass}
                            autoComplete="street-address"
                            {...editablePart("streetInput")}
                            placeholder={deliveryLabels.street}
                            required
                            value={form.deliveryStreet}
                            onChange={(event) => onChange({ deliveryStreet: event.target.value })}
                        />
                    </label>
                    <label className={fieldGroupClass} htmlFor={`${fieldIdPrefix}-delivery-city`}>
                        <span className={fieldLabelClass}>{deliveryLabels.city} <span aria-hidden="true">*</span></span>
                        <input
                            id={`${fieldIdPrefix}-delivery-city`}
                            className={fieldClass}
                            autoComplete="address-level2"
                            {...editablePart("cityInput")}
                            placeholder={deliveryLabels.city}
                            required
                            value={form.deliveryCity}
                            onChange={(event) => onChange({ deliveryCity: event.target.value })}
                        />
                    </label>
                    <label className={fieldGroupClass} htmlFor={`${fieldIdPrefix}-delivery-postcode`}>
                        <span className={fieldLabelClass}>{deliveryLabels.postcode} <span aria-hidden="true">*</span></span>
                        <input
                            id={`${fieldIdPrefix}-delivery-postcode`}
                            className={fieldClass}
                            autoComplete="postal-code"
                            {...editablePart("postcodeInput")}
                            placeholder={deliveryLabels.postcode}
                            required
                            value={form.deliveryPostcode}
                            onChange={(event) => onChange({ deliveryPostcode: event.target.value })}
                        />
                    </label>
                    <div className={fieldGroupClass}>
                        <label className={fieldLabelClass} htmlFor={`${fieldIdPrefix}-delivery-country`}>
                            {deliveryLabels.country} <span aria-hidden="true">*</span>
                        </label>
                        <CountryCombobox
                            id={`${fieldIdPrefix}-delivery-country`}
                            fieldClass={`${fieldClass} w-full`}
                            options={countryOptions}
                            placeholder={deliveryLabels.country}
                            required
                            value={form.deliveryCountryCode}
                            onChange={(countryCode) => onChange({ deliveryCountryCode: countryCode })}
                        />
                    </div>
                    <label className={classNames(fieldGroupClass, "sm:col-span-2")} htmlFor={`${fieldIdPrefix}-delivery-note`}>
                        <span className={fieldLabelClass}>{deliveryLabels.note}</span>
                        <textarea
                            id={`${fieldIdPrefix}-delivery-note`}
                            className={classNames(fieldClass, "min-h-24 resize-y")}
                            {...editablePart("noteInput")}
                            placeholder={deliveryLabels.note}
                            value={form.note}
                            onChange={(event) => onChange({ note: event.target.value })}
                        />
                    </label>
                </div>
                <p {...editableTextPart("deliveryHelpText")} className={classNames("text-xs leading-5", readableOnSurfaceTextClass)} style={partStyle(block, "deliveryHelpText")}>
                    {richTextContent(block, "deliveryHelpText", "E-mail slouží pro potvrzení objednávky, telefon pro rychlé upřesnění doručení.")}
                </p>
            </section>

            <section className="grid gap-4 border-t border-[var(--site-border)] pt-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <p {...editableTextPart("billingEyebrow")} className={classNames("text-xs font-bold uppercase", readableOnSurfaceTextClass)} style={partStyle(block, "billingEyebrow")}>{richTextContent(block, "billingEyebrow", "Volitelne")}</p>
                        <h3 {...editableTextPart("billingTitle")} className={classNames("font-black", compact ? "text-base" : "text-lg")} style={partStyle(block, "billingTitle")}>{richTextContent(block, "billingTitle", "Fakturační údaje")}</h3>
                    </div>
                    <label className="inline-flex cursor-pointer items-center gap-3 rounded-full border border-[var(--site-border)] px-4 py-2 text-sm font-bold">
                        <input
                            type="checkbox"
                            className={softCheckboxClass}
                            checked={billingOpen}
                            onChange={(event) => onChange({ billingSameAsDelivery: !event.target.checked })}
                        />
                        <span {...editableTextPart("billingToggleText")} style={partStyle(block, "billingToggleText")}>{richTextContent(block, "billingToggleText", "Fakturační údaje jsou jiné")}</span>
                    </label>
                </div>
                <AnimatedReveal open={billingOpen}>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <label className={fieldGroupClass} htmlFor={`${fieldIdPrefix}-billing-firstname`}>
                            <span className={fieldLabelClass}>{billingLabels.firstname}</span>
                            <input
                                id={`${fieldIdPrefix}-billing-firstname`}
                                className={fieldClass}
                                autoComplete="billing given-name"
                                placeholder={billingLabels.firstname}
                                value={form.billingFirstname}
                                onChange={(event) => onChange({ billingFirstname: event.target.value })}
                            />
                        </label>
                        <label className={fieldGroupClass} htmlFor={`${fieldIdPrefix}-billing-lastname`}>
                            <span className={fieldLabelClass}>{billingLabels.lastname}</span>
                            <input
                                id={`${fieldIdPrefix}-billing-lastname`}
                                className={fieldClass}
                                autoComplete="billing family-name"
                                placeholder={billingLabels.lastname}
                                value={form.billingLastname}
                                onChange={(event) => onChange({ billingLastname: event.target.value })}
                            />
                        </label>
                        <label className={fieldGroupClass} htmlFor={`${fieldIdPrefix}-billing-company`}>
                            <span className={fieldLabelClass}>{billingLabels.company}</span>
                            <input
                                id={`${fieldIdPrefix}-billing-company`}
                                className={fieldClass}
                                autoComplete="organization"
                                placeholder={billingLabels.company}
                                value={form.billingCompany}
                                onChange={(event) => onChange({ billingCompany: event.target.value })}
                            />
                        </label>
                        <label className={fieldGroupClass} htmlFor={`${fieldIdPrefix}-billing-company-id`}>
                            <span className={fieldLabelClass}>{billingLabels.companyId}</span>
                            <input
                                id={`${fieldIdPrefix}-billing-company-id`}
                                className={fieldClass}
                                placeholder={billingLabels.companyId}
                                value={form.billingIc}
                                onChange={(event) => onChange({ billingIc: event.target.value })}
                            />
                        </label>
                        <label className={fieldGroupClass} htmlFor={`${fieldIdPrefix}-billing-vat-id`}>
                            <span className={fieldLabelClass}>{billingLabels.vatId}</span>
                            <input
                                id={`${fieldIdPrefix}-billing-vat-id`}
                                className={fieldClass}
                                placeholder={billingLabels.vatId}
                                value={form.billingVat}
                                onChange={(event) => onChange({ billingVat: event.target.value })}
                            />
                        </label>
                        <label className={fieldGroupClass} htmlFor={`${fieldIdPrefix}-billing-phone`}>
                            <span className={fieldLabelClass}>{billingLabels.phone}</span>
                            <input
                                id={`${fieldIdPrefix}-billing-phone`}
                                className={fieldClass}
                                type="tel"
                                autoComplete="billing tel"
                                placeholder={billingLabels.phone}
                                value={form.billingPhone}
                                onChange={(event) => onChange({ billingPhone: event.target.value })}
                            />
                        </label>
                        <label className={fieldGroupClass} htmlFor={`${fieldIdPrefix}-billing-street`}>
                            <span className={fieldLabelClass}>{billingLabels.street}</span>
                            <input
                                id={`${fieldIdPrefix}-billing-street`}
                                className={fieldClass}
                                autoComplete="billing street-address"
                                placeholder={billingLabels.street}
                                value={form.billingStreet}
                                onChange={(event) => onChange({ billingStreet: event.target.value })}
                            />
                        </label>
                        <label className={fieldGroupClass} htmlFor={`${fieldIdPrefix}-billing-city`}>
                            <span className={fieldLabelClass}>{billingLabels.city}</span>
                            <input
                                id={`${fieldIdPrefix}-billing-city`}
                                className={fieldClass}
                                autoComplete="billing address-level2"
                                placeholder={billingLabels.city}
                                value={form.billingCity}
                                onChange={(event) => onChange({ billingCity: event.target.value })}
                            />
                        </label>
                        <label className={fieldGroupClass} htmlFor={`${fieldIdPrefix}-billing-postcode`}>
                            <span className={fieldLabelClass}>{billingLabels.postcode}</span>
                            <input
                                id={`${fieldIdPrefix}-billing-postcode`}
                                className={fieldClass}
                                autoComplete="billing postal-code"
                                placeholder={billingLabels.postcode}
                                value={form.billingPostcode}
                                onChange={(event) => onChange({ billingPostcode: event.target.value })}
                            />
                        </label>
                        <div className={fieldGroupClass}>
                            <label className={fieldLabelClass} htmlFor={`${fieldIdPrefix}-billing-country`}>{billingLabels.country}</label>
                            <CountryCombobox
                                id={`${fieldIdPrefix}-billing-country`}
                                fieldClass={`${fieldClass} w-full`}
                                options={countryOptions}
                                placeholder={billingLabels.country}
                                value={form.billingCountryCode}
                                onChange={(countryCode) => onChange({ billingCountryCode: countryCode })}
                            />
                        </div>
                    </div>
                </AnimatedReveal>
                {!billingOpen ? (
                    <p className={classNames("text-xs leading-5", readableOnSurfaceTextClass)}>
                        {message("ui.checkout.billingHint")}
                    </p>
                ) : null}
            </section>

            <div className="flex flex-col gap-3 border-t border-[var(--site-border)] pt-5 sm:flex-row sm:items-center sm:justify-between">
                <label className={classNames("inline-flex items-start gap-3 text-xs leading-5", readableOnSurfaceTextClass)}>
                    <input
                        type="checkbox"
                        className={softCheckboxClass}
                        checked={form.termsAccepted}
                        onChange={(event) => onChange({ termsAccepted: event.target.checked })}
                    />
                    <span {...editableTextPart("termsText")} style={partStyle(block, "termsText")}>{richTextContent(block, "termsText", "Souhlasím s obchodními podmínkami a zpracováním údajů pro vyřízení objednávky.")}</span>
                </label>
                <button
                    {...editableTextPart("submitButton")}
                    type="submit"
                    className="shrink-0 rounded-full bg-[var(--site-primary)] px-6 py-3 text-sm font-black text-[var(--site-on-primary)] disabled:cursor-not-allowed disabled:bg-[color-mix(in_srgb,var(--site-text)_25%,transparent)]"
                    style={partStyle(block, "submitButton")}
                    disabled={submitDisabled || submitting}
                >
                    {submitting ? message("ui.common.sending") : richTextContent(block, "submitButtonLabel", "Odeslat objednávku")}
                </button>
            </div>
            {error ? (
                <p className="rounded-xl border border-[var(--site-primary)] bg-[var(--site-primary-soft)] px-4 py-3 text-sm font-bold text-[var(--site-primary)]">
                    {error}
                </p>
            ) : null}
        </form>
    );
}

function AnimatedReveal({ open, children }: { open: boolean; children: ReactNode }) {
    return (
        <div
            className={classNames(
                "grid transition-[grid-template-rows,opacity,transform] duration-300 ease-out",
                open ? "grid-rows-[1fr] opacity-100 translate-y-0" : "grid-rows-[0fr] opacity-0 -translate-y-1",
            )}
        >
            <div className="overflow-hidden">
                {children}
            </div>
        </div>
    );
}

function CheckoutStepsPreview({
    block,
    cart,
    cartHref,
    deliveryPaymentHref,
    contactHref,
    checkoutRouteStep,
    checkoutOptions,
    checkoutOptionsLoading,
    cartError,
    packetaApiKey,
    checkoutForm,
    checkoutSubmitting,
    checkoutError,
    createdOrder,
    mode = "steps",
    onCheckoutRouteStepChange,
    onSelectDeliveryPayment,
    onCheckoutFormChange,
    onSubmitCheckout,
}: {
    block?: ShopTemplateBlock | null;
    cart: PublicCart;
    cartHref: string;
    deliveryPaymentHref: string;
    contactHref: string;
    checkoutRouteStep: CheckoutRouteStep;
    checkoutOptions: PublicCheckoutOptions;
    checkoutOptionsLoading: boolean;
    cartError: string | null;
    packetaApiKey?: string;
    checkoutForm: CheckoutFormState;
    checkoutSubmitting: boolean;
    checkoutError: string | null;
    createdOrder: PublicOrderCreated | null;
    mode?: "steps" | "singlePage";
    onCheckoutRouteStepChange: (step: CheckoutRouteStep) => void;
    onSelectDeliveryPayment: (
        carrierRateUuid: string,
        carrierPaymentOptionUuid: string,
        pickupPoint?: PickupPointSelection,
    ) => Promise<boolean>;
    onCheckoutFormChange: (patch: Partial<CheckoutFormState>) => void;
    onSubmitCheckout: (form: CheckoutFormState) => Promise<boolean>;
}) {
    const message = useShopStorefrontMessage();
    const { formatMoney } = useShopStorefrontFormatting();
    const languageCode = useContext(ShopStorefrontLanguageContext);
    const { homeHref } = useContext(ShopSiteInfoContext);
    const [step, setStep] = useState<"delivery" | "payment" | "contact">("delivery");
    const [selectedCarrierRateUuid, setSelectedCarrierRateUuid] = useState<string | null>(null);
    const [selectedCarrierPaymentOptionUuid, setSelectedCarrierPaymentOptionUuid] = useState<string | null>(null);
    const [selectedPickupPoint, setSelectedPickupPoint] = useState<PickupPointSelection | null>(null);
    const [saving, setSaving] = useState(false);
    const [paymentTouched, setPaymentTouched] = useState(false);
    const [maxCheckoutStepIndex, setMaxCheckoutStepIndex] = useState(1);
    const lastSavedSelectionRef = useRef<string | null>(null);
    const isSinglePage = mode === "singlePage";
    const [checkoutDeliveryOpen, setCheckoutDeliveryOpen] = useState(true);
    const [checkoutPaymentOpen, setCheckoutPaymentOpen] = useState(false);
    const selectedDelivery = selectedCarrierRateUuid !== null
        ? checkoutOptions.deliveryOptions.find((option) => option.carrierRateUuid === selectedCarrierRateUuid) ?? null
        : null;
    const selectedPayment = selectedDelivery?.paymentOptions.find(
        (option) => option.carrierPaymentOptionUuid === selectedCarrierPaymentOptionUuid,
    ) ?? null;
    const blockedText = cartCheckoutBlockedText(cart, message);
    const canContinue = cart.canCheckout
        && cart.items.length > 0
        && selectedDelivery !== null
        && selectedPayment !== null
        && (!selectedDelivery.requiresPickupPoint || selectedPickupPoint !== null)
        && !saving;
    const pickupPointError = selectedDelivery?.requiresPickupPoint && selectedPickupPoint === null
        ? message("ui.checkout.pickupRequired")
        : null;
    const compact = isSinglePage;
    const checkoutStepIndex = step === "contact" ? 2 : 1;
    const checkoutStepHrefs: [string, string, string] = [cartHref, deliveryPaymentHref, contactHref];
    const countryOptions = useMemo(() => checkoutCountryOptions(
        checkoutOptions.deliveryOptions,
        [checkoutForm.deliveryCountryCode, checkoutForm.billingCountryCode],
        languageCode,
    ), [
        checkoutForm.billingCountryCode,
        checkoutForm.deliveryCountryCode,
        checkoutOptions.deliveryOptions,
        languageCode,
    ]);

    useEffect(() => {
        if (checkoutRouteStep === "contact") {
            setStep("contact");
            setMaxCheckoutStepIndex(2);
            setCheckoutDeliveryOpen(false);
            setCheckoutPaymentOpen(false);

            return;
        }

        if (checkoutRouteStep === "deliveryPayment") {
            setStep(selectedDelivery !== null ? "payment" : "delivery");
        }
    }, [checkoutRouteStep, selectedDelivery]);

    useEffect(() => {
        const storedCarrierRateUuid = cart.carrierRateUuid ?? checkoutOptions.selectedCarrierRateUuid;
        const storedCarrierPaymentOptionUuid = cart.carrierPaymentOptionUuid
            ?? checkoutOptions.selectedCarrierPaymentOptionUuid;

        if (storedCarrierRateUuid === null || storedCarrierPaymentOptionUuid === null) {
            return;
        }

        const delivery = checkoutOptions.deliveryOptions.find(
            (option) => option.carrierRateUuid === storedCarrierRateUuid,
        );

        if (!delivery) {
            return;
        }

        const payment = delivery.paymentOptions.find(
            (option) => option.carrierPaymentOptionUuid === storedCarrierPaymentOptionUuid,
        );

        if (!payment) {
            return;
        }

        setSelectedCarrierRateUuid(delivery.carrierRateUuid);
        setSelectedCarrierPaymentOptionUuid(payment.carrierPaymentOptionUuid);
        setSelectedPickupPoint(
            delivery.requiresPickupPoint && cart.pickupPointId !== null
                ? {
                    provider: cart.pickupPointProvider ?? delivery.pickupPointProvider ?? delivery.carrierCode ?? "pickup_point",
                    id: cart.pickupPointId,
                    name: cart.pickupPointName,
                }
                : null,
        );
        setPaymentTouched(false);
        setCheckoutDeliveryOpen(false);
        setCheckoutPaymentOpen(false);
        setMaxCheckoutStepIndex(2);
        lastSavedSelectionRef.current = [
            delivery.carrierRateUuid,
            payment.carrierPaymentOptionUuid,
            cart.pickupPointProvider ?? "",
            cart.pickupPointId ?? "",
        ].join("|");
    }, [
        cart.carrierPaymentOptionUuid,
        cart.carrierRateUuid,
        cart.pickupPointId,
        cart.pickupPointName,
        cart.pickupPointProvider,
        checkoutOptions.deliveryOptions,
        checkoutOptions.selectedCarrierPaymentOptionUuid,
        checkoutOptions.selectedCarrierRateUuid,
    ]);

    useEffect(() => {
        if (checkoutOptions.deliveryOptions.length === 0) {
            setSelectedCarrierRateUuid(null);
            setSelectedCarrierPaymentOptionUuid(null);
            setSelectedPickupPoint(null);
            setPaymentTouched(false);
            setCheckoutDeliveryOpen(true);
            setCheckoutPaymentOpen(false);

            return;
        }

        const delivery = selectedCarrierRateUuid !== null
            ? checkoutOptions.deliveryOptions.find((option) => option.carrierRateUuid === selectedCarrierRateUuid) ?? null
            : null;
        const payment = delivery !== null && selectedCarrierPaymentOptionUuid !== null
            ? delivery.paymentOptions.find((option) => option.carrierPaymentOptionUuid === selectedCarrierPaymentOptionUuid) ?? null
            : null;

        if (selectedCarrierRateUuid !== null && delivery === null) {
            setSelectedCarrierRateUuid(null);
            setSelectedCarrierPaymentOptionUuid(null);
            setSelectedPickupPoint(null);
            setPaymentTouched(false);
            setCheckoutDeliveryOpen(true);
            setCheckoutPaymentOpen(false);

            return;
        }

        if (delivery === null || !delivery.requiresPickupPoint) {
            setSelectedPickupPoint(null);
        }

        if (selectedCarrierPaymentOptionUuid !== null && payment === null) {
            setSelectedCarrierPaymentOptionUuid(null);
            setPaymentTouched(false);
            setCheckoutPaymentOpen(delivery !== null && !checkoutDeliveryOpen);
        }
    }, [
        checkoutDeliveryOpen,
        checkoutOptions.deliveryOptions,
        selectedCarrierPaymentOptionUuid,
        selectedCarrierRateUuid,
    ]);

    async function saveSelection(nextStep?: "contact"): Promise<boolean> {
        if (selectedDelivery === null || selectedPayment === null) {
            return false;
        }

        if (selectedDelivery.requiresPickupPoint && selectedPickupPoint === null) {
            return false;
        }

        setSaving(true);

        try {
            const selected = await onSelectDeliveryPayment(
                selectedDelivery.carrierRateUuid,
                selectedPayment.carrierPaymentOptionUuid,
                selectedDelivery.requiresPickupPoint ? selectedPickupPoint ?? undefined : undefined,
            );

            if (selected && nextStep) {
                setStep(nextStep);
                setMaxCheckoutStepIndex(2);
                onCheckoutRouteStepChange("contact");
            }

            return selected;
        } finally {
            setSaving(false);
        }
    }

    async function submitCheckout(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        if (!canContinue || checkoutSubmitting) {
            return;
        }

        const selected = await saveSelection();

        if (!selected) {
            return;
        }

        await onSubmitCheckout(checkoutForm);
    }

    function changeContactForm(patch: Partial<CheckoutFormState>) {
        if (
            typeof patch.deliveryCountryCode === "string"
            && selectedDelivery !== null
            && patch.deliveryCountryCode.trim().toUpperCase() !== selectedDelivery.countryCode.trim().toUpperCase()
        ) {
            setSelectedCarrierRateUuid(null);
            setSelectedCarrierPaymentOptionUuid(null);
            setSelectedPickupPoint(null);
            setPaymentTouched(false);
            setCheckoutDeliveryOpen(true);
            setCheckoutPaymentOpen(false);
            setStep("delivery");
            lastSavedSelectionRef.current = null;
            onCheckoutRouteStepChange("deliveryPayment");
        }

        onCheckoutFormChange(patch);
    }

    useEffect(() => {
        if (!isSinglePage || checkoutDeliveryOpen || selectedDelivery === null || selectedPayment === null) {
            return;
        }

        if (selectedDelivery.requiresPickupPoint && selectedPickupPoint === null) {
            return;
        }

        const persistedSelectionMatches = cart.carrierRateUuid === selectedDelivery.carrierRateUuid
            && cart.carrierPaymentOptionUuid === selectedPayment.carrierPaymentOptionUuid;

        if (!paymentTouched && !persistedSelectionMatches) {
            return;
        }

        const selectionKey = [
            selectedDelivery.carrierRateUuid,
            selectedPayment.carrierPaymentOptionUuid,
            selectedPickupPoint?.provider ?? "",
            selectedPickupPoint?.id ?? "",
        ].join("|");

        if (lastSavedSelectionRef.current === selectionKey) {
            return;
        }

        lastSavedSelectionRef.current = selectionKey;
        void saveSelection();
    }, [
        cart.carrierPaymentOptionUuid,
        cart.carrierRateUuid,
        checkoutDeliveryOpen,
        isSinglePage,
        paymentTouched,
        selectedDelivery,
        selectedPayment,
        selectedPickupPoint,
    ]);

    function changeDelivery() {
        setStep("delivery");
        setCheckoutDeliveryOpen(true);
        setCheckoutPaymentOpen(false);
        setSelectedCarrierPaymentOptionUuid(null);
        setPaymentTouched(false);
        lastSavedSelectionRef.current = null;
        onCheckoutRouteStepChange("deliveryPayment");
    }

    function changePayment() {
        setStep("payment");
        setCheckoutPaymentOpen(true);
        onCheckoutRouteStepChange("deliveryPayment");
    }

    const deliverySection = (
        <div className={classNames("rounded-2xl border border-[var(--site-border)] bg-[var(--site-surface)]", compact ? "p-4" : "p-5")}>
            <div className="flex items-center justify-between gap-3">
                <h2 {...editableTextPart("deliveryTitle")} className={classNames("font-black", compact ? "text-lg" : "text-xl")} style={partStyle(block, "deliveryTitle")}>{richTextContent(block, "deliveryTitle", "Doprava")}</h2>
                {saving && isSinglePage ? <span className={classNames("text-xs font-bold", readableOnSurfaceTextClass)}>{message("ui.common.saving")}</span> : null}
            </div>
            {checkoutOptionsLoading ? <p className={classNames("mt-3 text-sm", readableOnSurfaceTextClass)}>{message("ui.checkout.loadingOptions")}</p> : null}
            {!checkoutOptionsLoading && checkoutOptions.deliveryOptions.length === 0 ? (
                <p className={classNames("mt-3 rounded-xl border border-[var(--site-border)] bg-[var(--site-surface)] p-4 text-sm", readableOnSurfaceTextClass)}>
                    {message("ui.checkout.noOptions")}
                </p>
            ) : null}
            <div className="mt-4 grid gap-2">
                {checkoutOptions.deliveryOptions.map((option) => {
                    const selected = selectedCarrierRateUuid === option.carrierRateUuid;

                    return (
                        <button
                            key={option.carrierRateUuid}
                            type="button"
                            className={classNames(
                                "grid grid-cols-[30px_minmax(0,1fr)_auto] items-center gap-2 rounded-xl border px-3 py-2.5 text-left transition hover:border-[var(--site-primary)]",
                                selected ? "border-[var(--site-primary)] bg-[var(--site-surface)]" : "border-[var(--site-border)] bg-[var(--site-surface)]",
                            )}
                            onClick={() => {
                                setSelectedCarrierRateUuid(option.carrierRateUuid);
                                setSelectedCarrierPaymentOptionUuid(null);
                                setSelectedPickupPoint((current) => {
                                    if (!option.requiresPickupPoint) {
                                        return null;
                                    }

                                    if (current?.provider === (option.pickupPointProvider ?? option.carrierCode)) {
                                        return current;
                                    }

                                    return null;
                                });

                                setPaymentTouched(false);
                                setCheckoutDeliveryOpen(false);
                                setCheckoutPaymentOpen(true);
                                setStep("payment");
                                lastSavedSelectionRef.current = null;
                                onCheckoutFormChange({ deliveryCountryCode: option.countryCode });
                            }}
                        >
                            <span className="grid h-8 w-8 place-items-center rounded-full bg-[var(--site-surface)] text-[var(--site-primary)]">
                                <Icon name={deliveryIcon(option)} className="h-3.5 w-3.5" />
                            </span>
                            <span className="min-w-0">
                                <span className="block truncate text-[13px] font-black leading-5">{option.carrierName}</span>
                                <span className={classNames("mt-0.5 block truncate text-xs", readableOnSurfaceTextClass)}>{option.deliveryMethodName}</span>
                            </span>
                            <span className={classNames("rounded-full bg-[var(--site-surface)] px-2.5 py-1 text-xs font-bold tabular-nums", readableOnSurfaceTextClass)}>
                                {formatMoney(option.price)}
                            </span>
                        </button>
                    );
                })}
            </div>
            {selectedDelivery?.requiresPickupPoint ? (
                <PickupPointSelector
                    delivery={selectedDelivery}
                    selectedPickupPoint={selectedPickupPoint}
                    onChange={setSelectedPickupPoint}
                    packetaApiKey={packetaApiKey}
                    error={pickupPointError}
                />
            ) : null}
        </div>
    );

    const paymentSection = (
        <div className={classNames("rounded-2xl border border-[var(--site-border)] bg-[var(--site-surface)]", compact ? "p-4" : "p-5")}>
            <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                <h2 {...editableTextPart("paymentTitle")} className={classNames("font-black", compact ? "text-lg" : "text-xl")} style={partStyle(block, "paymentTitle")}>{richTextContent(block, "paymentTitle", "Platba")}</h2>
            </div>
            {selectedDelivery !== null ? (
                <div className="mt-4 grid gap-2">
                    {selectedDelivery.paymentOptions.map((option) => {
                        const selected = selectedCarrierPaymentOptionUuid === option.carrierPaymentOptionUuid;

                        return (
                            <button
                                key={option.carrierPaymentOptionUuid}
                                type="button"
                                className={classNames(
                                    "grid grid-cols-[30px_minmax(0,1fr)_auto] items-center gap-2 rounded-xl border px-3 py-2.5 text-left transition hover:border-[var(--site-primary)]",
                                    selected ? "border-[var(--site-primary)] bg-[var(--site-surface)]" : "border-[var(--site-border)] bg-[var(--site-surface)]",
                                )}
                                onClick={() => {
                                    setSelectedCarrierPaymentOptionUuid(option.carrierPaymentOptionUuid);
                                    setPaymentTouched(true);
                                    setCheckoutPaymentOpen(false);

                                    if (!isSinglePage) {
                                        setStep("payment");
                                    }
                                }}
                            >
                                <span className="grid h-8 w-8 place-items-center rounded-full bg-[var(--site-surface)] text-[var(--site-primary)]">
                                    <Icon name={paymentIcon(option)} className="h-3.5 w-3.5" />
                                </span>
                                <span className="min-w-0">
                                    <span className="block truncate text-[13px] font-black leading-5">{option.paymentName}</span>
                                </span>
                            <span className={classNames("rounded-full bg-[var(--site-surface)] px-2.5 py-1 text-xs font-bold tabular-nums", readableOnSurfaceTextClass)}>
                                    {formatMoney(option.surcharge)}
                                </span>
                            </button>
                        );
                    })}
                </div>
            ) : (
                <p className={classNames("mt-3 text-sm", readableOnSurfaceTextClass)}>{message("ui.checkout.selectDeliveryFirst")}</p>
            )}
            {!isSinglePage ? (
                <button
                    {...editableTextPart("continueButton")}
                    type="button"
                    className="mt-5 rounded-full bg-[var(--site-primary)] px-5 py-3 text-sm font-black text-[var(--site-on-primary)] disabled:bg-[color-mix(in_srgb,var(--site-text)_25%,transparent)]"
                    style={partStyle(block, "continueButton")}
                    disabled={!canContinue}
                    onClick={() => void saveSelection("contact")}
                >
                    {saving ? message("ui.common.saving") : richTextContent(block, "continueButtonLabel", "Pokračovat na informace o vás")}
                </button>
            ) : null}
        </div>
    );

    const selectedDeliverySummary = selectedDelivery ? (
        <div className="rounded-2xl border border-[var(--site-border)] bg-[var(--site-surface)] p-4">
            <div className="grid grid-cols-[40px_minmax(0,1fr)_auto] items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-[var(--site-surface)] text-[var(--site-primary)]">
                    <Icon name={deliveryIcon(selectedDelivery)} className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                    <p className={classNames("text-xs font-bold uppercase", readableOnSurfaceTextClass)}>{message("ui.checkout.deliveryMethod")}</p>
                    <p className="truncate text-sm font-black">{selectedDelivery.carrierName} - {selectedDelivery.deliveryMethodName}</p>
                    <p className={classNames("truncate text-xs", readableOnSurfaceTextClass)}>{formatMoney(selectedDelivery.price)}</p>
                    {selectedPickupPoint ? <p className={classNames("truncate text-xs", readableOnSurfaceTextClass)}>{selectedPickupPoint.name ?? selectedPickupPoint.id}</p> : null}
                </div>
                <button
                    type="button"
                    className="rounded-full border border-[var(--site-border)] px-4 py-2 text-xs font-bold"
                    onClick={changeDelivery}
                >
                    {message("ui.checkout.changeDelivery")}
                </button>
            </div>
        </div>
    ) : null;

    const selectedPaymentSummary = selectedPayment ? (
        <div className="rounded-2xl border border-[var(--site-border)] bg-[var(--site-surface)] p-4">
            <div className="grid grid-cols-[40px_minmax(0,1fr)_auto] items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-[var(--site-surface)] text-[var(--site-primary)]">
                    <Icon name={paymentIcon(selectedPayment)} className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                    <p className={classNames("text-xs font-bold uppercase", readableOnSurfaceTextClass)}>{message("ui.checkout.paymentMethod")}</p>
                    <p className="truncate text-sm font-black">{selectedPayment.paymentName}</p>
                    <p className={classNames("truncate text-xs", readableOnSurfaceTextClass)}>{formatMoney(selectedPayment.surcharge)}</p>
                </div>
                <button
                    type="button"
                    className="rounded-full border border-[var(--site-border)] px-4 py-2 text-xs font-bold"
                    onClick={changePayment}
                >
                    {message("ui.checkout.changePayment")}
                </button>
            </div>
        </div>
    ) : null;

    if (createdOrder !== null || checkoutRouteStep === "completed") {
        return (
            <section id="checkout" className="px-5 py-10 sm:px-6" style={blockVisualStyle(block)}>
                <div className="mx-auto max-w-[var(--site-container)] rounded-3xl border border-[var(--site-border)] bg-[var(--site-surface)] p-6 shadow-[0_8px_28px_var(--site-shadow-color)] sm:p-10">
                    <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                        <div>
                            <p {...editableTextPart("completedEyebrow")} className="text-xs font-bold uppercase text-[var(--site-primary)]" style={partStyle(block, "completedEyebrow")}>{richTextContent(block, "completedEyebrow", "Objednávka přijata")}</p>
                            <h1 {...editableTextPart("completedTitle")} className="mt-2 text-3xl font-black" style={partStyle(block, "completedTitle")}>{richTextContent(block, "completedTitle", "Děkujeme za objednávku")}</h1>
                            <p {...editableTextPart("completedText")} className={classNames("mt-3 text-sm leading-6", readableOnSurfaceTextClass)} style={readableSemanticTextStyle(partStyle(block, "completedText"))}>
                                {richTextContent(block, "completedText", "Objednávku jsme uložili. Potvrzení a další informace můžeme navázat přes e-mailový workflow.")}
                            </p>
                        </div>
                        <div className="rounded-2xl bg-[var(--site-surface)] px-5 py-4 text-sm">
                            <p className={classNames("text-xs font-bold uppercase", readableOnSurfaceTextClass)}>{message("ui.checkout.orderNumber")}</p>
                            <p className="mt-1 text-lg font-black">{createdOrder?.reference ?? message("ui.checkout.orderCompleted")}</p>
                            {createdOrder ? <p className={classNames("mt-2 text-xs", readableOnSurfaceTextClass)}>{formatMoney(createdOrder.totalPrice, createdOrder.currencyCode)}</p> : null}
                        </div>
                    </div>
                    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                        <a
                            {...editableTextPart("completedPrimaryButton")}
                            href={homeHref}
                            className="inline-flex justify-center rounded-full bg-[var(--site-primary)] px-5 py-3 text-sm font-black text-[var(--site-on-primary)]"
                            style={partStyle(block, "completedPrimaryButton")}
                        >
                            {richTextContent(block, "completedPrimaryButtonLabel", "Zpět do obchodu")}
                        </a>
                        <a
                            {...editableTextPart("completedSecondaryButton")}
                            href={cartHref}
                            className="inline-flex justify-center rounded-full border border-[var(--site-border)] px-5 py-3 text-sm font-bold"
                            style={partStyle(block, "completedSecondaryButton")}
                        >
                            {richTextContent(block, "completedSecondaryButtonLabel", "Nový nákup")}
                        </a>
                    </div>
                </div>
            </section>
        );
    }

    if (isSinglePage) {
        return (
            <section id="checkout" className="px-5 py-8 sm:px-6" style={blockVisualStyle(block)}>
                <div className="mx-auto grid max-w-[var(--site-container)] gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
                    <div className="space-y-4">
                        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                            <h2 {...editableTextPart("title")} className="text-2xl font-black" style={partStyle(block, "title")}>{richTextContent(block, "title", "Doprava, platba a údaje")}</h2>
                        </div>
                        {blockedText ? (
                            <p className="border border-[var(--site-primary)] bg-[var(--site-primary-soft)] px-4 py-3 text-sm font-bold text-[var(--site-primary)]">{blockedText}</p>
                        ) : null}
                        {cartError ? (
                            <p className="border border-[var(--site-primary)] bg-[var(--site-primary-soft)] px-4 py-3 text-sm font-bold text-[var(--site-primary)]">{cartError}</p>
                        ) : null}
                        <AnimatedReveal open={checkoutDeliveryOpen}>
                            {deliverySection}
                        </AnimatedReveal>
                        {!checkoutDeliveryOpen ? selectedDeliverySummary : null}
                        {!checkoutDeliveryOpen && selectedDelivery !== null ? (
                            <>
                                <AnimatedReveal open={checkoutPaymentOpen}>
                                    {paymentSection}
                                </AnimatedReveal>
                                {!checkoutPaymentOpen ? selectedPaymentSummary : null}
                            </>
                        ) : null}
                        <ContactFormPreview
                            block={block}
                            compact
                            form={checkoutForm}
                            countryOptions={countryOptions}
                            submitting={checkoutSubmitting}
                            submitDisabled={!canContinue}
                            error={checkoutError}
                            onChange={changeContactForm}
                            onSubmit={(event) => void submitCheckout(event)}
                        />
                    </div>
                    <CheckoutOrderColumn
                        cart={cart}
                        delivery={selectedDelivery}
                        payment={selectedPayment}
                        pickupPoint={selectedPickupPoint}
                    />
                </div>
            </section>
        );
    }

    return (
        <section id="checkout" className="px-5 py-10 sm:px-6" style={blockVisualStyle(block)}>
            <div className="mx-auto grid max-w-[var(--site-container)] gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
                <div className="space-y-4">
                    <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                            <h1 {...editableTextPart("title")} className="text-4xl font-black" style={partStyle(block, "title")}>{richTextContent(block, "title", "Objednávka")}</h1>
                        {(step === "delivery" || step === "payment") ? (
                            <a {...editableTextPart("backButton")} href={cartHref} className="inline-flex items-center justify-center rounded-full border border-[var(--site-border)] px-5 py-3 text-sm font-bold" style={partStyle(block, "backButton")}>
                                {richTextContent(block, "backButtonLabel", "Zpět do košíku")}
                            </a>
                        ) : null}
                    </div>
                    <div className="rounded-2xl bg-[var(--site-surface)] shadow-[0_6px_24px_var(--site-shadow-color)]">
                        <CheckoutProgressStepper
                            currentIndex={checkoutStepIndex}
                            maxReachableIndex={maxCheckoutStepIndex}
                            stepHrefs={checkoutStepHrefs}
                            onSelectStep={(index) => {
                                if (index === 0) {
                                    onCheckoutRouteStepChange("cart");
                                }

                                if (index === 1) {
                                    onCheckoutRouteStepChange("deliveryPayment");
                                    setStep(selectedDelivery ? "payment" : "delivery");
                                }

                                if (index === 2) {
                                    onCheckoutRouteStepChange("contact");
                                    setStep("contact");
                                }
                            }}
                        />
                    </div>
                    {(step === "delivery" || step === "payment") ? (
                        <div className="grid gap-4">
                            {blockedText ? (
                                <p className="border border-[var(--site-primary)] bg-[var(--site-primary-soft)] px-4 py-3 text-sm font-bold text-[var(--site-primary)]">
                                    {blockedText}
                                </p>
                            ) : null}
                            {cartError ? (
                                <p className="border border-[var(--site-primary)] bg-[var(--site-primary-soft)] px-4 py-3 text-sm font-bold text-[var(--site-primary)]">
                                    {cartError}
                                </p>
                            ) : null}
                            <AnimatedReveal open={checkoutDeliveryOpen}>
                                {deliverySection}
                            </AnimatedReveal>
                            {!checkoutDeliveryOpen ? selectedDeliverySummary : null}
                            {!checkoutDeliveryOpen && selectedDelivery !== null ? (
                                <>
                                    <AnimatedReveal open={checkoutPaymentOpen}>
                                        {paymentSection}
                                    </AnimatedReveal>
                                    {!checkoutPaymentOpen ? selectedPaymentSummary : null}
                                    {!checkoutPaymentOpen && selectedPayment !== null ? (
                                        <button
                                            {...editableTextPart("continueButton")}
                                            type="button"
                                            className="justify-self-start rounded-full bg-[var(--site-primary)] px-5 py-3 text-sm font-black text-[var(--site-on-primary)] disabled:bg-[color-mix(in_srgb,var(--site-text)_25%,transparent)]"
                                            style={partStyle(block, "continueButton")}
                                            disabled={!canContinue}
                                            onClick={() => void saveSelection("contact")}
                                        >
                                            {saving ? message("ui.common.saving") : richTextContent(block, "continueButtonLabel", "Pokračovat na informace o vás")}
                                        </button>
                                    ) : null}
                                </>
                            ) : null}
                        </div>
                    ) : null}
                    {step === "contact" ? (
                        <div className="grid gap-4">
                            <ContactFormPreview
                                block={block}
                                form={checkoutForm}
                                countryOptions={countryOptions}
                                submitting={checkoutSubmitting}
                                submitDisabled={!canContinue}
                                error={checkoutError}
                                onChange={changeContactForm}
                                onSubmit={(event) => void submitCheckout(event)}
                            />
                            <button
                                {...editableTextPart("changeDeliveryPaymentButton")}
                                type="button"
                                className="justify-self-start rounded-full border border-[var(--site-border)] px-5 py-3 text-sm font-bold"
                                style={partStyle(block, "changeDeliveryPaymentButton")}
                                onClick={() => {
                                    onCheckoutRouteStepChange("deliveryPayment");
                                    setStep("payment");
                                }}
                            >
                                {richTextContent(block, "changeDeliveryPaymentButtonLabel", "Změnit dopravu a platbu")}
                            </button>
                        </div>
                    ) : null}
                </div>
                <CheckoutOrderColumn
                    cart={cart}
                    delivery={selectedDelivery}
                    payment={selectedPayment}
                    pickupPoint={selectedPickupPoint}
                />
            </div>
        </section>
    );
}

function SearchFormPreview({
    block,
    context,
    onCatalogNavigate,
}: {
    block?: ShopTemplateBlock | null;
    context: ShopPageContext;
    onCatalogNavigate?: CatalogNavigateHandler;
}) {
    const message = useShopStorefrontMessage();
    const storefrontHref = useShopStorefrontHref();
    const catalogSort = catalogSortFromContext(context);
    const catalogFilters = catalogFiltersFromContext(context);
    const searchData = nestedRecord(contextData(context), "search");
    const query = stringOrNull(searchData.query) ?? "";
    const baseHref = context.canonicalHref ?? storefrontHref(context.slug ? `/${context.slug}` : "/");
    const categoryValue = searchData.categories;
    const categories = Array.isArray(categoryValue) ? categoryValue.flatMap((item): Array<{ uuid: string; name: string; slug: string }> => {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
            return [];
        }

        const data = item as Record<string, unknown>;
        const uuid = stringOrNull(data.uuid);
        const name = stringOrNull(data.name);
        const slug = stringOrNull(data.slug);

        return uuid && name && slug ? [{ uuid, name, slug }] : [];
    }) : [];

    return (
        <section className="px-5 py-10 sm:px-6" style={blockVisualStyle(block)}>
            <div className="mx-auto max-w-[var(--site-container)]">
                <h1 {...editableTextPart("title")} className="text-4xl font-black" style={partStyle(block, "title")}>{richTextContent(block, "title", "Vyhledavani")}</h1>
                <div className="mt-6">
                    <div {...editablePart("search")} style={partStyle(block, "search")}>
                        <CatalogTextFilter
                            baseHref={baseHref}
                            currentSort={catalogSort}
                            filters={catalogFilters}
                            query={query}
                            onNavigate={onCatalogNavigate}
                            label={blockText(block, "searchLabel", "Upravit hledání")}
                            placeholder={blockText(block, "placeholder", message("block.systemSearchForm.placeholder"))}
                        />
                    </div>
                </div>
                {categories.length > 0 ? (
                    <div className="mt-6 flex flex-wrap gap-2">
                        {categories.map((category) => (
                            <a key={category.uuid} href={storefrontHref(`/${category.slug}`)} className="rounded-full bg-[var(--site-surface)] px-4 py-2 text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_70%,transparent)] hover:text-[var(--site-primary)]">
                                {category.name}
                            </a>
                        ))}
                    </div>
                ) : null}
            </div>
        </section>
    );
}

function orderStatusLabel(status: string, message: ShopStorefrontMessage): string {
    const normalized = status.trim().toLowerCase();
    const statusCode = normalized.includes("_") ? normalized.split("_").at(-1) ?? normalized : normalized;
    const labels: Record<string, string> = {
        pending: message("ui.order.status.pending"),
        new: message("ui.order.status.new"),
        processing: message("ui.order.status.processing"),
        paid: message("ui.order.status.paid"),
        shipped: message("ui.order.status.shipped"),
        delivered: message("ui.order.status.delivered"),
        completed: message("ui.order.status.completed"),
        done: message("ui.order.status.completed"),
        cancelled: message("ui.order.status.cancelled"),
        canceled: message("ui.order.status.cancelled"),
    };

    return labels[normalized] ?? labels[statusCode] ?? status;
}

function normalizedOrderStatus(status: string): string {
    const normalized = status.trim().toLowerCase();

    return normalized.includes("_") ? normalized.split("_").at(-1) ?? normalized : normalized;
}

function orderStatusSentence(status: string, message: ShopStorefrontMessage): string {
    return message("ui.order.statusSentence", { status: orderStatusLabel(status, message) });
}

function isHistoricalOrder(order: PublicOrderListItem): boolean {
    return ["completed", "done", "delivered", "cancelled", "canceled"].includes(normalizedOrderStatus(order.status));
}

function canBuyOrderAgain(order: PublicOrderListItem | PublicOrderDetail): boolean {
    return ["completed", "done", "delivered"].includes(normalizedOrderStatus(order.status));
}

function orderPrimaryProduct(order: PublicOrderListItem | PublicOrderDetail): string {
    const firstItem = order.items?.[0];

    if (!firstItem) {
        return "-";
    }

    const rest = (order.items?.length ?? 0) - 1;

    return rest > 0 ? `${firstItem.productName} +${rest}` : firstItem.productName;
}

function customerAddressTypeLabel(type: PublicCustomerAddressType, message: ShopStorefrontMessage): string {
    const option = CUSTOMER_ADDRESS_TYPES.find((addressType) => addressType.value === type);

    return option ? message(option.messageKey) : message("ui.address.other");
}

function customerAddressTypeIcon(type: PublicCustomerAddressType): IconName {
    return CUSTOMER_ADDRESS_TYPES.find((option) => option.value === type)?.icon ?? "mapPin";
}

function customerAddressLine(address: PublicCustomerAddress): string {
    return [
        [address.address1, address.houseNumber].filter(Boolean).join(" "),
        [address.postcode, address.city].filter(Boolean).join(" "),
        address.countryCode,
    ].filter((part) => typeof part === "string" && part.trim() !== "").join(", ");
}

function emptyCustomerAddressForm(customer: PublicCustomer, firstAddress: boolean): PublicCustomerAddressPayload {
    return {
        firstname: customer.firstname,
        lastname: customer.lastname,
        company: customer.company,
        email: customer.email,
        phone: null,
        address1: "",
        houseNumber: null,
        address2: null,
        city: "",
        postcode: "",
        countryCode: "CZ",
        addressType: "home",
        useAsBillingAddress: firstAddress,
        useAsDeliveryAddress: firstAddress,
    };
}

function customerAddressPayloadFromAddress(address: PublicCustomerAddress): PublicCustomerAddressPayload {
    return {
        firstname: address.firstname,
        lastname: address.lastname,
        company: address.company,
        email: address.email,
        phone: address.phone,
        address1: address.address1,
        houseNumber: address.houseNumber,
        address2: address.address2,
        city: address.city,
        postcode: address.postcode,
        countryCode: address.countryCode,
        addressType: address.addressType,
        useAsBillingAddress: address.billingForCustomer,
        useAsDeliveryAddress: address.deliveryForCustomer,
    };
}

function cleanCustomerAddressPayload(payload: PublicCustomerAddressPayload): PublicCustomerAddressPayload {
    const optional = (value: string | null): string | null => {
        const trimmed = (value ?? "").trim();

        return trimmed === "" ? null : trimmed;
    };

    return {
        firstname: payload.firstname.trim(),
        lastname: payload.lastname.trim(),
        company: optional(payload.company),
        email: optional(payload.email),
        phone: optional(payload.phone),
        address1: payload.address1.trim(),
        houseNumber: optional(payload.houseNumber),
        address2: optional(payload.address2),
        city: payload.city.trim(),
        postcode: payload.postcode.trim(),
        countryCode: (payload.countryCode.trim() || "CZ").toUpperCase(),
        addressType: payload.addressType,
        useAsBillingAddress: payload.useAsBillingAddress,
        useAsDeliveryAddress: payload.useAsDeliveryAddress,
    };
}

function statusTimeline(order: PublicOrderDetail): Array<{ id: string; status: string; date: string; note: string | null }> {
    const changes = order.statusChanges.length > 0
        ? [...order.statusChanges].reverse().map((change) => ({
            id: change.uuid,
            status: change.toStatus,
            date: change.createdAt,
            note: change.note,
        }))
        : [];

    if (changes.length === 0 || normalizedOrderStatus(changes[0]?.status ?? "") !== "new") {
        changes.unshift({
            id: `${order.uuid}-created`,
            status: "new",
            date: order.createdAt,
            note: null,
        });
    }

    if (!changes.some((change) => normalizedOrderStatus(change.status) === normalizedOrderStatus(order.status))) {
        changes.push({
            id: `${order.uuid}-current`,
            status: order.status,
            date: order.updatedAt,
            note: null,
        });
    }

    return changes.filter((change, index, timeline) => (
        index === 0 || normalizedOrderStatus(change.status) !== normalizedOrderStatus(timeline[index - 1]?.status ?? "")
    ));
}

function deliverySummary(order: PublicOrderDetail): string {
    if (order.pickupPointName) {
        return order.pickupPointName;
    }

    if (order.pickupPointId) {
        return order.pickupPointId;
    }

    const delivery = addressLine(order.deliveryAddress);

    return delivery === "" ? "-" : delivery;
}

function AccountNavigationPreview({
    block,
    customerSession,
    customerLoading,
    customerError,
    customerApiBasePath,
    orderApiBasePath,
    customerAddresses,
    favoriteProducts,
    favoriteCount,
    onLogin,
    onRegister,
    onLogout,
    onUpdateProfile,
    onChangePassword,
    onRequestPasswordReset,
    onConfirmPasswordReset,
    onReorder,
    onCustomerAddressesChange,
    onToggleFavorite,
}: {
    block?: ShopTemplateBlock | null;
    customerSession: PublicCustomerSession;
    customerLoading: boolean;
    customerError: string | null;
    customerApiBasePath: string | null;
    orderApiBasePath: string | null;
    customerAddresses: PublicCustomerAddress[];
    favoriteProducts: FavoriteProductPreview[];
    favoriteCount: number;
    onLogin: (email: string, password: string) => Promise<boolean>;
    onRegister: (
        email: string,
        password: string,
        passwordConfirmation: string,
        newsletterOptOut: boolean,
    ) => Promise<boolean>;
    onLogout: () => Promise<void>;
    onUpdateProfile: (profile: PublicCustomerProfileUpdate) => Promise<boolean>;
    onChangePassword: (
        currentPassword: string,
        newPassword: string,
        newPasswordConfirmation: string,
    ) => Promise<boolean>;
    onRequestPasswordReset: (email: string) => Promise<boolean>;
    onConfirmPasswordReset: (
        token: string,
        newPassword: string,
        newPasswordConfirmation: string,
    ) => Promise<boolean>;
    onReorder: (orderUuid: string) => Promise<PublicOrderReorderResponse | null>;
    onCustomerAddressesChange: (addresses: PublicCustomerAddress[]) => void;
    onToggleFavorite: (productId: string) => void;
}) {
    const message = useShopStorefrontMessage();
    const { formatDate, formatMoney, locale } = useShopStorefrontFormatting();
    const accountRootRef = useRef<HTMLElement | null>(null);
    const [mode, setMode] = useState<AccountMode>("login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [passwordConfirmation, setPasswordConfirmation] = useState("");
    const [newsletterOptOut, setNewsletterOptOut] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [registrationMessage, setRegistrationMessage] = useState<string | null>(null);
    const [activeSection, setActiveSection] = useState<AccountSection>("overview");
    const [orders, setOrders] = useState<PublicOrderListItem[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersError, setOrdersError] = useState<string | null>(null);
    const [selectedOrderUuid, setSelectedOrderUuid] = useState<string | null>(null);
    const [selectedOrder, setSelectedOrder] = useState<PublicOrderDetail | null>(null);
    const [orderDetailLoading, setOrderDetailLoading] = useState(false);
    const [orderDetailError, setOrderDetailError] = useState<string | null>(null);
    const [orderTimelineOpen, setOrderTimelineOpen] = useState(false);
    const [openOrderItemMenuUuid, setOpenOrderItemMenuUuid] = useState<string | null>(null);
    const [reorderingOrderUuid, setReorderingOrderUuid] = useState<string | null>(null);
    const [reorderMessage, setReorderMessage] = useState<string | null>(null);
    const [reorderError, setReorderError] = useState<string | null>(null);
    const [profileForm, setProfileForm] = useState<PublicCustomerProfileUpdate>({
        firstname: "",
        lastname: "",
        email: "",
        company: null,
        ic: null,
        vat: null,
        newsletterSubscribed: true,
    });
    const [profileCustomerType, setProfileCustomerType] = useState<AccountCustomerType>("personal");
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [emailDraft, setEmailDraft] = useState("");
    const [profileSubmitting, setProfileSubmitting] = useState(false);
    const [profileMessage, setProfileMessage] = useState<string | null>(null);
    const [profileError, setProfileError] = useState<string | null>(null);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("");
    const [passwordSubmitting, setPasswordSubmitting] = useState(false);
    const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [passwordResetMode, setPasswordResetMode] = useState<"request" | "confirm" | null>(null);
    const [passwordResetEmail, setPasswordResetEmail] = useState("");
    const [passwordResetToken, setPasswordResetToken] = useState("");
    const [passwordResetNewPassword, setPasswordResetNewPassword] = useState("");
    const [passwordResetNewPasswordConfirmation, setPasswordResetNewPasswordConfirmation] = useState("");
    const [passwordResetSubmitting, setPasswordResetSubmitting] = useState(false);
    const [passwordResetMessage, setPasswordResetMessage] = useState<string | null>(null);
    const [passwordResetError, setPasswordResetError] = useState<string | null>(null);
    const [addresses, setAddresses] = useState<PublicCustomerAddress[]>([]);
    const [addressesLoading, setAddressesLoading] = useState(false);
    const [addressesError, setAddressesError] = useState<string | null>(null);
    const [addressFormOpen, setAddressFormOpen] = useState(false);
    const [addressForm, setAddressForm] = useState<PublicCustomerAddressPayload | null>(null);
    const [editingAddressUuid, setEditingAddressUuid] = useState<string | null>(null);
    const [addressSubmitting, setAddressSubmitting] = useState(false);
    const [addressMessage, setAddressMessage] = useState<string | null>(null);
    const links: Array<{ id: AccountSection; label: string }> = [
        { id: "overview", label: message("ui.account.overview") },
        { id: "orders", label: message("ui.account.orders") },
        { id: "addresses", label: message("ui.account.addresses") },
        { id: "favorites", label: message("ui.account.favorites") },
        { id: "settings", label: message("ui.account.settings") },
        { id: "connections", label: message("ui.account.connections") },
    ];
    const ongoingOrders = orders.filter((order) => !isHistoricalOrder(order));
    const historyOrders = orders.filter(isHistoricalOrder);
    const orderSections: Array<{ title: string; orders: PublicOrderListItem[] }> = [
        { title: message("ui.order.ongoing"), orders: ongoingOrders },
        { title: message("ui.order.history"), orders: historyOrders },
    ];
    const customerDisplayName = [
        customerSession.customer?.firstname,
        customerSession.customer?.lastname,
    ].filter(Boolean).join(" ") || customerSession.customer?.email || message("ui.account.customer");
    const customerInitials = customerDisplayName
        .split(" ")
        .map((part) => part.charAt(0))
        .join("")
        .slice(0, 2)
        .toUpperCase();
    const latestOrder = orders.reduce<PublicOrderListItem | null>((latest, order) => {
        if (latest === null) {
            return order;
        }

        return new Date(order.createdAt).getTime() > new Date(latest.createdAt).getTime() ? order : latest;
    }, null);
    const selectedOrderSummary = selectedOrderUuid !== null
        ? orders.find((order) => order.uuid === selectedOrderUuid) ?? null
        : null;
    const defaultDeliveryAddress = addresses.find((address) => address.deliveryForCustomer) ?? addresses[0] ?? null;
    const defaultBillingAddress = addresses.find((address) => address.billingForCustomer) ?? defaultDeliveryAddress;
    const profileDefaultAddress = defaultDeliveryAddress ?? defaultBillingAddress;
    const profileInputClass = "mt-2 w-full rounded-2xl border border-transparent bg-[var(--site-surface-elevated)] px-4 py-3 font-normal outline-none transition focus:border-[var(--site-primary)] focus:bg-[var(--site-surface)] disabled:cursor-not-allowed disabled:text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]";
    const passwordInputClass = "mt-2 w-full rounded-2xl border border-[color-mix(in_srgb,var(--site-text)_15%,transparent)] bg-[var(--site-surface)] px-4 py-3 font-normal text-[var(--site-text)] shadow-sm outline-none transition placeholder:text-[color-mix(in_srgb,var(--site-text)_35%,transparent)] focus:border-[var(--site-primary)] focus:ring-4 focus:ring-[var(--site-primary)]/12";
    const profilePanelClass = "rounded-3xl bg-[var(--site-surface)] p-5 shadow-[0_18px_48px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]";

    function selectAccountSection(section: AccountSection): void {
        setActiveSection(section);
        accountRootRef.current?.ownerDocument.defaultView?.history.replaceState(null, "", `#${section}`);
    }

    useEffect(() => {
        const customer = customerSession.customer;

        if (!customerSession.authenticated || customer === null) {
            return;
        }

        setProfileForm({
            firstname: customer.firstname,
            lastname: customer.lastname,
            email: customer.email,
            company: customer.company,
            ic: customer.ic,
            vat: customer.vat,
            newsletterSubscribed: customer.newsletterSubscribed,
        });
        setProfileCustomerType([customer.company, customer.ic, customer.vat].some((value) => (value ?? "").trim() !== "") ? "company" : "personal");
        setEmailDraft(customer.email);
        setEmailModalOpen(false);
    }, [customerSession.authenticated, customerSession.customer]);

    useEffect(() => {
        if (!customerSession.authenticated || customerSession.customer === null) {
            return;
        }

        setAddresses(customerAddresses);
    }, [customerAddresses, customerSession.authenticated, customerSession.customer]);

    useEffect(() => {
        const view = accountRootRef.current?.ownerDocument.defaultView;

        if (!view) {
            return undefined;
        }

        const syncSectionFromHash = () => {
            const section = accountSectionFromHash(view.location.hash);

            if (section !== null) {
                setActiveSection(section);
            }
        };

        syncSectionFromHash();
        view.addEventListener("hashchange", syncSectionFromHash);

        return () => view.removeEventListener("hashchange", syncSectionFromHash);
    }, []);

    useEffect(() => {
        if (!customerSession.authenticated || customerSession.customer === null || orderApiBasePath === null) {
            setOrders([]);
            setSelectedOrderUuid(null);
            setSelectedOrder(null);
            setOrdersError(null);

            return undefined;
        }

        let cancelled = false;
        const apiPath = orderApiBasePath;

        async function loadOrders() {
            setOrdersLoading(true);
            setOrdersError(null);

            try {
                const response = await fetch(apiPath, {
                    credentials: "same-origin",
                    cache: "no-store",
                });

                if (!response.ok) {
                    throw new Error(message("ui.error.ordersLoad"));
                }

                const payload = await response.json() as PublicOrderListResponse;

                if (!cancelled) {
                    setOrders(payload.orders ?? []);
                }
            } catch {
                if (!cancelled) {
                    setOrders([]);
                    setOrdersError(message("ui.error.ordersLoad"));
                }
            } finally {
                if (!cancelled) {
                    setOrdersLoading(false);
                }
            }
        }

        void loadOrders();

        return () => {
            cancelled = true;
        };
    }, [customerSession.authenticated, customerSession.customer?.uuid, orderApiBasePath]);

    useEffect(() => {
        if (!customerSession.authenticated || customerSession.customer === null || customerApiBasePath === null) {
            setAddresses([]);
            setAddressesError(null);
            setAddressFormOpen(false);
            setAddressForm(null);
            setEditingAddressUuid(null);

            return undefined;
        }

        let cancelled = false;
        const apiPath = customerApiBasePath;

        async function loadAddresses() {
            setAddressesLoading(true);
            setAddressesError(null);
            let requestError = message("ui.error.addressesLoad");

            try {
                const response = await fetch(`${apiPath}/addresses`, {
                    credentials: "same-origin",
                    cache: "no-store",
                });

                if (!response.ok) {
                    requestError = await customerErrorTextFromResponse(response, requestError, message);
                    throw new Error(requestError);
                }

                const payload = await response.json() as PublicCustomerAddressListResponse;

                if (!cancelled) {
                    setAddresses(payload.addresses ?? []);
                    onCustomerAddressesChange(payload.addresses ?? []);
                }
            } catch {
                if (!cancelled) {
                    setAddresses([]);
                    setAddressesError(requestError);
                }
            } finally {
                if (!cancelled) {
                    setAddressesLoading(false);
                }
            }
        }

        void loadAddresses();

        return () => {
            cancelled = true;
        };
    }, [customerApiBasePath, customerSession.authenticated, customerSession.customer?.uuid]);

    useEffect(() => {
        if (selectedOrderUuid === null || orderApiBasePath === null) {
            setSelectedOrder(null);
            setOrderDetailError(null);

            return undefined;
        }

        let cancelled = false;
        const apiPath = orderApiBasePath;
        const orderUuid = selectedOrderUuid;

        async function loadOrderDetail() {
            setOrderDetailLoading(true);
            setOrderDetailError(null);

            try {
                const response = await fetch(`${apiPath}/${encodeURIComponent(orderUuid)}`, {
                    credentials: "same-origin",
                    cache: "no-store",
                });

                if (!response.ok) {
                    throw new Error(message("ui.error.orderDetailLoad"));
                }

                const payload = await response.json() as PublicOrderDetail;

                if (!cancelled) {
                    setSelectedOrder(payload);
                }
            } catch {
                if (!cancelled) {
                    setSelectedOrder(null);
                    setOrderDetailError(message("ui.error.orderDetailLoad"));
                }
            } finally {
                if (!cancelled) {
                    setOrderDetailLoading(false);
                }
            }
        }

        void loadOrderDetail();

        return () => {
            cancelled = true;
        };
    }, [orderApiBasePath, selectedOrderUuid]);

    useEffect(() => {
        const view = accountRootRef.current?.ownerDocument.defaultView;

        if (!view) {
            return;
        }

        const token = new URLSearchParams(view.location.search).get("resetToken");

        if (token && token.trim() !== "") {
            setPasswordResetToken(token.trim());
            setPasswordResetMode("confirm");
        }
    }, []);

    async function submit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setSubmitting(true);
        setRegistrationMessage(null);

        try {
            const success = mode === "login"
                ? await onLogin(email, password)
                : await onRegister(email, password, passwordConfirmation, newsletterOptOut);

            if (success) {
                setPassword("");
                setPasswordConfirmation("");

                if (mode === "register") {
                    setRegistrationMessage(message("ui.account.registrationCheckEmail"));
                }
            }
        } finally {
            setSubmitting(false);
        }
    }

    function optionalProfileText(value: string | null): string | null {
        const trimmed = (value ?? "").trim();

        return trimmed === "" ? null : trimmed;
    }

    function openNewAddressForm(): void {
        const customer = customerSession.customer;

        if (customer === null) {
            return;
        }

        setEditingAddressUuid(null);
        setAddressForm(emptyCustomerAddressForm(customer, addresses.length === 0));
        setAddressFormOpen(true);
        setAddressMessage(null);
        setAddressesError(null);
    }

    function openEditAddressForm(address: PublicCustomerAddress): void {
        setEditingAddressUuid(address.uuid);
        setAddressForm(customerAddressPayloadFromAddress(address));
        setAddressFormOpen(true);
        setAddressMessage(null);
        setAddressesError(null);
    }

    function updateAddressForm(patch: Partial<PublicCustomerAddressPayload>): void {
        setAddressForm((current) => current === null ? current : { ...current, ...patch });
    }

    async function submitAddress(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        if (addressForm === null || customerApiBasePath === null) {
            return;
        }

        setAddressSubmitting(true);
        setAddressMessage(null);
        setAddressesError(null);
        let requestError = message("ui.error.addressSave");

        try {
            const payload = cleanCustomerAddressPayload(addressForm);
            const response = await fetch(
                editingAddressUuid === null
                    ? `${customerApiBasePath}/addresses`
                    : `${customerApiBasePath}/addresses/${encodeURIComponent(editingAddressUuid)}`,
                {
                    method: editingAddressUuid === null ? "POST" : "PATCH",
                    headers: { "Content-Type": "application/json" },
                    credentials: "same-origin",
                    body: JSON.stringify(payload),
                },
            );

            if (!response.ok) {
                requestError = await customerErrorTextFromResponse(response, requestError, message);
                throw new Error(requestError);
            }

            const data = await response.json() as PublicCustomerAddressListResponse;
            setAddresses(data.addresses ?? []);
            onCustomerAddressesChange(data.addresses ?? []);
            setAddressFormOpen(false);
            setAddressForm(null);
            setEditingAddressUuid(null);
            setAddressMessage(editingAddressUuid === null ? message("ui.account.addressAdded") : message("ui.account.addressSaved"));
        } catch {
            setAddressesError(requestError);
        } finally {
            setAddressSubmitting(false);
        }
    }

    async function deleteAddress(addressUuid: string) {
        if (customerApiBasePath === null || addressSubmitting) {
            return;
        }

        setAddressSubmitting(true);
        setAddressMessage(null);
        setAddressesError(null);
        let requestError = message("ui.error.addressDelete");

        try {
            const response = await fetch(`${customerApiBasePath}/addresses/${encodeURIComponent(addressUuid)}`, {
                method: "DELETE",
                credentials: "same-origin",
            });

            if (!response.ok) {
                requestError = await customerErrorTextFromResponse(response, requestError, message);
                throw new Error(requestError);
            }

            const data = await response.json() as PublicCustomerAddressListResponse;
            setAddresses(data.addresses ?? []);
            onCustomerAddressesChange(data.addresses ?? []);

            if (editingAddressUuid === addressUuid) {
                setAddressFormOpen(false);
                setAddressForm(null);
                setEditingAddressUuid(null);
            }

            setAddressMessage(message("ui.account.addressDeleted"));
        } catch {
            setAddressesError(requestError);
        } finally {
            setAddressSubmitting(false);
        }
    }

    async function submitProfile(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setProfileSubmitting(true);
        setProfileMessage(null);
        setProfileError(null);

        try {
            const success = await onUpdateProfile({
                firstname: profileForm.firstname.trim(),
                lastname: profileForm.lastname.trim(),
                email: profileForm.email.trim(),
                company: profileCustomerType === "company" ? optionalProfileText(profileForm.company) : null,
                ic: profileCustomerType === "company" ? optionalProfileText(profileForm.ic) : null,
                vat: profileCustomerType === "company" ? optionalProfileText(profileForm.vat) : null,
                newsletterSubscribed: profileForm.newsletterSubscribed,
            });

            if (success) {
                setProfileMessage(message("ui.account.profileSaved"));
            } else {
                setProfileError(message("ui.error.profileSave"));
            }
        } finally {
            setProfileSubmitting(false);
        }
    }

    function submitEmailChange(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        setProfileForm((current) => ({ ...current, email: emailDraft.trim() }));
        setEmailModalOpen(false);
    }

    async function submitPasswordChange(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setPasswordSubmitting(true);
        setPasswordMessage(null);
        setPasswordError(null);

        try {
            const success = await onChangePassword(currentPassword, newPassword, newPasswordConfirmation);

            if (success) {
                setCurrentPassword("");
                setNewPassword("");
                setNewPasswordConfirmation("");
                setPasswordMessage(message("ui.account.passwordChanged"));
            } else {
                setPasswordError(message("ui.error.passwordChange"));
            }
        } finally {
            setPasswordSubmitting(false);
        }
    }

    async function submitPasswordResetRequest(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setPasswordResetSubmitting(true);
        setPasswordResetMessage(null);
        setPasswordResetError(null);

        try {
            const success = await onRequestPasswordReset((passwordResetEmail || email).trim());

            if (success) {
                setPasswordResetMessage(message("ui.account.resetSent"));
            } else {
                setPasswordResetError(message("ui.error.passwordResetRequest"));
            }
        } finally {
            setPasswordResetSubmitting(false);
        }
    }

    async function submitPasswordResetConfirm(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setPasswordResetSubmitting(true);
        setPasswordResetMessage(null);
        setPasswordResetError(null);

        try {
            const success = await onConfirmPasswordReset(
                passwordResetToken.trim(),
                passwordResetNewPassword,
                passwordResetNewPasswordConfirmation,
            );

            if (success) {
                setPasswordResetNewPassword("");
                setPasswordResetNewPasswordConfirmation("");
                setPasswordResetMode(null);
                setPasswordResetMessage(message("ui.account.passwordReset"));
            } else {
                setPasswordResetError(message("ui.error.resetLinkInvalid"));
            }
        } finally {
            setPasswordResetSubmitting(false);
        }
    }

    async function reorderSelectedOrder(orderUuid: string) {
        setReorderingOrderUuid(orderUuid);
        setReorderMessage(null);
        setReorderError(null);

        try {
            const response = await onReorder(orderUuid);

            if (response === null) {
                setReorderError(message("ui.error.orderReorder"));

                return;
            }

            if (response.skippedItems.length > 0) {
                setReorderMessage(message("ui.account.reorderPartial", { count: response.skippedItems.length }));
            } else {
                setReorderMessage(message("ui.account.reorderDone"));
            }
        } finally {
            setReorderingOrderUuid(null);
        }
    }

    if (customerSession.authenticated && customerSession.customer !== null) {
        return (
            <section ref={accountRootRef} className="bg-[var(--site-surface)] px-5 py-10 sm:px-6" style={blockVisualStyle(block)}>
                <div className="mx-auto max-w-[var(--site-container)]">
                    <div className="grid gap-10 lg:grid-cols-[300px_minmax(0,1fr)]">
                        <aside className="lg:sticky lg:top-6 lg:self-start">
                            <div className="rounded-[28px] bg-[var(--site-surface)] p-4">
                                <div className="rounded-3xl bg-[var(--site-surface)] p-4 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                                    <div className="flex items-center gap-3">
                                        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[var(--site-primary)] text-sm font-black text-[var(--site-on-primary)]">
                                            {customerInitials}
                                        </span>
                                        <span className="min-w-0">
                                            <span className="block truncate text-sm font-black text-[var(--site-text)]">{customerDisplayName}</span>
                                            <span className="mt-0.5 block truncate text-xs font-bold text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">{customerSession.customer.email}</span>
                                        </span>
                                    </div>
                                </div>
                                <nav className="mt-4 space-y-1" aria-label={message("ui.account.sectionNavigation")}>
                                    {links.map((link) => (
                                        <button
                                            key={link.id}
                                            {...editablePart(`${link.id}Link`)}
                                            type="button"
                                            style={partStyle(block, `${link.id}Link`)}
                                            className={classNames(
                                                "block w-full rounded-2xl px-4 py-3 text-left text-sm font-black transition",
                                                activeSection === link.id
                                                    ? "bg-[var(--site-primary)] text-[var(--site-on-primary)] shadow-sm"
                                                    : "text-[color-mix(in_srgb,var(--site-text)_65%,transparent)]",
                                            )}
                                            onClick={() => selectAccountSection(link.id)}
                                        >
                                            {blockText(block, `${link.id}LinkLabel`, link.label)}
                                        </button>
                                    ))}
                                </nav>
                                <button
                                    {...editableTextPart("logoutButton")}
                                    type="button"
                                    className="mt-4 w-full rounded-full border border-[color-mix(in_srgb,var(--site-text)_15%,transparent)] bg-[var(--site-surface)] px-5 py-3 text-sm font-black text-[color-mix(in_srgb,var(--site-text)_65%,transparent)] transition disabled:opacity-60"
                                    style={partStyle(block, "logoutButton")}
                                    disabled={customerLoading}
                                    onClick={() => {
                                        setSelectedOrderUuid(null);
                                        void onLogout();
                                    }}
                                >
                                    {richTextContent(block, "logoutButtonLabel", "Odhlásit se")}
                                </button>
                            </div>
                        </aside>
                        <div>
                        {activeSection === "overview" ? (
                            <div className="mt-6 grid gap-5">
                                <div className="rounded-3xl bg-[var(--site-background)] p-5 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)] sm:p-6">
                                    <div className="grid gap-5 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center">
                                        <div className="grid h-16 w-16 place-items-center rounded-3xl bg-[var(--site-surface)] text-xl font-black text-[var(--site-primary)] shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                                            {customerInitials}
                                        </div>
                                        <div className="min-w-0">
                                            <p {...editableTextPart("overviewEyebrow")} className="text-xs font-black uppercase tracking-[0.16em] text-[var(--site-primary)]" style={partStyle(block, "overviewEyebrow")}>{richTextContent(block, "overviewEyebrow", "Zákaznický profil")}</p>
                                            <h2 className="mt-2 truncate text-2xl font-black">{customerDisplayName}</h2>
                                        </div>
                                        <button
                                            {...editableTextPart("profileButton")}
                                            type="button"
                                            className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--site-primary)] px-5 py-3 text-sm font-black text-[var(--site-on-primary)] transition"
                                            style={partStyle(block, "profileButton")}
                                            onClick={() => setActiveSection("settings")}
                                        >
                                            <Icon name="user" className="h-4 w-4" />
                                            {richTextContent(block, "profileButtonLabel", "Upravit profil")}
                                        </button>
                                    </div>
                                    <div className="mt-5 grid gap-3 sm:grid-cols-2">
                                        {[
                                            ["E-mail", customerSession.customer.email],
                                            ["Newsletter", customerSession.customer.newsletterSubscribed ? message("ui.account.newsletterSubscribed") : message("ui.account.newsletterUnsubscribed")],
                                        ].map(([title, value]) => (
                                            <div key={title} className="rounded-2xl bg-[var(--site-surface)] p-4 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                                                <p className="text-xs font-black uppercase tracking-[0.14em] text-[color-mix(in_srgb,var(--site-text)_35%,transparent)]">{title}</p>
                                                <p className="mt-2 truncate text-sm font-black text-[color-mix(in_srgb,var(--site-text)_75%,transparent)]">{value}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
                                    <button
                                        type="button"
                                        className="rounded-3xl bg-[var(--site-surface-elevated)] p-5 text-left shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)] transition hover:bg-[var(--site-surface)] hover:ring-[var(--site-primary)]/35"
                                        onClick={() => setActiveSection("orders")}
                                    >
                                        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-[var(--site-surface)] text-[var(--site-primary)] shadow-sm">
                                            <Icon name="bag" className="h-5 w-5" />
                                        </span>
                                        <span {...editableTextPart("ordersCardTitle")} className="mt-4 block text-sm font-black text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]" style={partStyle(block, "ordersCardTitle")}>{richTextContent(block, "ordersCardTitle", "Objednávky")}</span>
                                        <span className="mt-1 block text-3xl font-black text-[var(--site-text)]">{orders.length}</span>
                                        <span {...editableTextPart("ordersCardButton")} className="mt-2 inline-flex items-center gap-2 text-sm font-black text-[var(--site-primary)]" style={partStyle(block, "ordersCardButton")}>
                                            {richTextContent(block, "ordersCardButtonLabel", "Zobrazit objednávky")}
                                            <Icon name="chevronRight" className="h-4 w-4" />
                                        </span>
                                    </button>

                                    <div className="grid gap-3 rounded-3xl bg-[var(--site-surface)] p-4 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)] sm:grid-cols-2 lg:grid-cols-1">
                                        {[
                                            [message("ui.account.ordersOngoing"), String(ongoingOrders.length)],
                                            [message("ui.account.ordersCompleted"), String(historyOrders.length)],
                                        ].map(([title, value]) => (
                                            <div
                                                key={title}
                                                className="rounded-2xl bg-[var(--site-surface-elevated)] p-4"
                                            >
                                                <span className="block text-xs font-black uppercase tracking-[0.14em] text-[color-mix(in_srgb,var(--site-text)_35%,transparent)]">{title}</span>
                                                <span className="mt-1 block text-2xl font-black text-[var(--site-text)]">{value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="rounded-3xl bg-[var(--site-background)] p-5 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                        <div>
                                            <p {...editableTextPart("activityEyebrow")} className="text-xs font-black uppercase tracking-[0.16em] text-[var(--site-primary)]" style={partStyle(block, "activityEyebrow")}>{richTextContent(block, "activityEyebrow", "Poslední aktivita")}</p>
                                            <h2 {...editableTextPart("activityTitle")} className="mt-1 text-xl font-black" style={partStyle(block, "activityTitle")}>{richTextContent(block, "activityTitle", "Poslední objednávka")}</h2>
                                        </div>
                                    </div>
                                    {latestOrder !== null ? (
                                        <button
                                            type="button"
                                            className="mt-5 grid w-full gap-4 rounded-3xl bg-[var(--site-surface)] p-4 text-left shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)] transition hover:ring-[var(--site-primary)]/40 sm:grid-cols-[minmax(0,1fr)_130px_120px]"
                                            onClick={() => {
                                                setActiveSection("orders");
                                                setSelectedOrder(null);
                                                setOrderDetailError(null);
                                                setOrderTimelineOpen(false);
                                                setOpenOrderItemMenuUuid(null);
                                                setSelectedOrderUuid(latestOrder.uuid);
                                            }}
                                        >
                                            <span className="min-w-0">
                                                <span className="block text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_35%,transparent)]">{message("ui.order.code")}</span>
                                                <span className="mt-1 block truncate font-mono text-sm font-black text-[var(--site-primary)]">{latestOrder.reference}</span>
                                                <span className="mt-2 block truncate text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">{orderPrimaryProduct(latestOrder)}</span>
                                            </span>
                                            <span>
                                                <span className="block text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_35%,transparent)]">{message("ui.order.status")}</span>
                                                <span className="mt-1 inline-flex rounded-full bg-[var(--site-surface-elevated)] px-3 py-1 text-xs font-black text-[var(--site-primary)]">
                                                    {orderStatusLabel(latestOrder.status, message)}
                                                </span>
                                            </span>
                                            <span>
                                                <span className="block text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_35%,transparent)]">{message("ui.order.price")}</span>
                                                <span className="mt-1 block font-black">{formatMoney(latestOrder.totalPrice, latestOrder.currencyCode)}</span>
                                            </span>
                                        </button>
                                    ) : (
                                        <div className="mt-5 rounded-3xl bg-[var(--site-surface)] p-5 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)] shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                                            {message("ui.order.noneOverview")}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : null}

                        {activeSection === "orders" ? (
                            <div className="mt-6 grid gap-6">
                                {selectedOrderUuid === null ? (
                                    <>
                                        {ordersLoading ? (
                                            <p className="border border-[var(--site-border)] bg-[var(--site-surface)] p-4 text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]">{message("ui.order.loading")}</p>
                                        ) : null}
                                        {ordersError ? (
                                            <p className="border border-[var(--site-primary)] bg-[var(--site-primary-soft)] p-4 text-sm font-bold text-[var(--site-primary)]">{ordersError}</p>
                                        ) : null}
                                        {!ordersLoading && !ordersError && orders.length === 0 ? (
                                            <p className="border border-[var(--site-border)] bg-[var(--site-surface)] p-5 text-sm text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]">
                                                {message("ui.order.none")}
                                            </p>
                                        ) : null}
                                        {orderSections.map(({ title, orders: sectionOrders }) => (
                                            sectionOrders.length > 0 ? (
                                                <div key={title}>
                                                    <h2 className="text-xl font-black">{title}</h2>
                                                    <div className="mt-4 divide-y divide-[var(--site-border)] overflow-hidden rounded-2xl border border-[var(--site-border)]">
                                                        {sectionOrders.map((order) => (
                                                            <button
                                                                key={order.uuid}
                                                                type="button"
                                                                className="grid w-full gap-3 bg-[var(--site-surface)] p-4 text-left transition hover:bg-[var(--site-surface)] lg:grid-cols-[176px_108px_minmax(0,1fr)_104px_120px_34px] lg:items-center"
                                                                onClick={() => {
                                                                    setActiveSection("orders");
                                                                    setSelectedOrder(null);
                                                                    setOrderDetailError(null);
                                                                    setOrderTimelineOpen(false);
                                                                    setOpenOrderItemMenuUuid(null);
                                                                    setSelectedOrderUuid(order.uuid);
                                                                }}
                                                            >
                                                                <span className="min-w-0">
                                                                    <span className="block text-xs font-bold uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.order.code")}</span>
                                                                    <span className="mt-1 block break-all font-mono text-[11px] font-black leading-4 text-[var(--site-primary)]" title={order.reference}>{order.reference}</span>
                                                                </span>
                                                                <span>
                                                                    <span className="block text-xs font-bold uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.order.date")}</span>
                                                                    <span className="mt-1 block text-xs font-bold">{formatDate(order.createdAt)}</span>
                                                                </span>
                                                                <span className="min-w-0">
                                                                    <span className="block text-xs font-bold uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.order.product")}</span>
                                                                    <span className="mt-1 block truncate text-sm font-bold">{orderPrimaryProduct(order)}</span>
                                                                </span>
                                                                <span>
                                                                    <span className="block text-xs font-bold uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.order.price")}</span>
                                                                    <span className="mt-1 block text-xs font-bold">{formatMoney(order.totalPrice, order.currencyCode)}</span>
                                                                </span>
                                                                <span>
                                                                    <span className="block text-xs font-bold uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.order.status")}</span>
                                                                    <span className="mt-1 inline-flex rounded-full bg-[var(--site-surface)] px-2.5 py-1 text-xs font-black text-[var(--site-primary)]">
                                                                        {orderStatusLabel(order.status, message)}
                                                                    </span>
                                                                </span>
                                                                <span className="hidden h-8 w-8 place-items-center rounded-full border border-[var(--site-border)] text-[color-mix(in_srgb,var(--site-text)_45%,transparent)] lg:grid">
                                                                    <Icon name="chevronRight" className="h-4 w-4" />
                                                                </span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : null
                                        ))}
                                    </>
                                ) : (
                                    <div className="overflow-hidden rounded-3xl bg-[var(--site-surface)] shadow-[0_18px_54px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                                        <div className="bg-[var(--site-background)] p-5 sm:p-6">
                                            <button
                                                type="button"
                                                className="inline-flex items-center gap-2 rounded-full border border-[var(--site-border)] bg-[var(--site-surface)] px-4 py-2 text-sm font-bold hover:border-[var(--site-primary)] hover:text-[var(--site-primary)]"
                                                onClick={() => {
                                                    setSelectedOrderUuid(null);
                                                    setSelectedOrder(null);
                                                    setOrderDetailError(null);
                                                    setOrderTimelineOpen(false);
                                                    setOpenOrderItemMenuUuid(null);
                                                }}
                                            >
                                                <Icon name="chevronLeft" className="h-4 w-4" />
                                                {message("ui.order.backToOrders")}
                                            </button>
                                            <div className="mt-5">
                                                <p className="text-xs font-bold uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.order.detail")}</p>
                                                <h2 className="mt-1 break-all font-mono text-xl font-black leading-tight text-[var(--site-primary)] sm:text-2xl">{selectedOrder?.reference ?? selectedOrderSummary?.reference ?? message("ui.order.fallback")}</h2>
                                                <p className="mt-2 text-sm text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">{message("ui.common.created", { date: formatDate(selectedOrder?.createdAt ?? selectedOrderSummary?.createdAt) })}</p>
                                            </div>
                                        </div>

                                        <div className="p-5 sm:p-6">
                                            {orderDetailLoading ? (
                                                <div className="grid gap-3">
                                                    <div className="h-16 animate-pulse rounded-2xl bg-[var(--site-surface)]" />
                                                    <div className="h-32 animate-pulse rounded-2xl bg-[var(--site-surface)]" />
                                                </div>
                                            ) : null}
                                            {orderDetailError ? (
                                                <p className="border border-[var(--site-primary)] bg-[var(--site-primary-soft)] p-4 text-sm font-bold text-[var(--site-primary)]">{orderDetailError}</p>
                                            ) : null}
                                            {selectedOrder !== null ? (
                                                <div className="grid gap-5">
                                                    <div className="rounded-3xl bg-[var(--site-surface-elevated)] p-5">
                                                        <div className="flex flex-col gap-4">
                                                            <div>
                                                                <p className="text-lg font-black">{orderStatusSentence(selectedOrder.status, message)}</p>
                                                                <p className="mt-1 text-sm text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">{message("ui.order.currentStatus", { status: orderStatusLabel(selectedOrder.status, message) })}</p>
                                                            </div>
                                                            <div className="flex flex-wrap gap-3">
                                                                {canBuyOrderAgain(selectedOrder) ? (
                                                                    <button
                                                                        {...editablePart("orderBuyAgainButton")}
                                                                        type="button"
                                                                        className="rounded-full bg-[var(--site-primary)] px-5 py-3 text-sm font-black text-[var(--site-on-primary)] shadow-sm transition disabled:cursor-not-allowed disabled:bg-[color-mix(in_srgb,var(--site-text)_25%,transparent)]"
                                                                        style={partStyle(block, "orderBuyAgainButton")}
                                                                        disabled={reorderingOrderUuid === selectedOrder.uuid}
                                                                        onClick={() => {
                                                                            void reorderSelectedOrder(selectedOrder.uuid);
                                                                        }}
                                                                    >
                                                                        {reorderingOrderUuid === selectedOrder.uuid ? message("ui.account.reordering") : message("ui.account.buyAgain")}
                                                                    </button>
                                                                ) : null}
                                                                <button
                                                                    type="button"
                                                                    className="rounded-full bg-[var(--site-surface)] px-5 py-3 text-sm font-black text-[color-mix(in_srgb,var(--site-text)_70%,transparent)] shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)] transition hover:bg-[var(--site-inverse)] hover:text-[var(--site-on-inverse)]"
                                                                >
                                                                    {message("ui.order.complain")}
                                                                </button>
                                                            </div>
                                                            {reorderMessage ? (
                                                                <p className="rounded-2xl bg-[var(--site-accent-soft)] px-4 py-3 text-sm font-bold text-[var(--site-accent)] ring-1 ring-[var(--site-accent)]">
                                                                    {reorderMessage}
                                                                </p>
                                                            ) : null}
                                                            {reorderError ? (
                                                                <p className="rounded-2xl bg-[var(--site-primary-soft)] px-4 py-3 text-sm font-bold text-[var(--site-primary)] ring-1 ring-[var(--site-primary)]">
                                                                    {reorderError}
                                                                </p>
                                                            ) : null}
                                                            <div>
                                                                <button
                                                                    type="button"
                                                                    className="inline-flex items-center gap-2 text-sm font-black text-[var(--site-primary)] underline decoration-[var(--site-primary)]/35 underline-offset-4 transition hover:text-[var(--site-text)] hover:decoration-[var(--site-text)]"
                                                                    onClick={() => setOrderTimelineOpen((current) => !current)}
                                                                >
                                                                    {message("ui.order.timeline")}
                                                                    <Icon name="chevronDown" className={classNames("h-4 w-4 transition-transform", orderTimelineOpen && "rotate-180")} />
                                                                </button>
                                                                {orderTimelineOpen ? (
                                                                    <div className="mt-4 grid gap-0 rounded-3xl bg-[var(--site-background)] p-5">
                                                                        {statusTimeline(selectedOrder).map((change, index, timeline) => (
                                                                            <div key={change.id} className="grid grid-cols-[32px_minmax(0,1fr)] gap-3">
                                                                                <div className="relative flex justify-center">
                                                                                    <span className="grid h-8 w-8 place-items-center rounded-full bg-[var(--site-primary)] text-[var(--site-on-primary)]">
                                                                                        <Icon name="check" className="h-4 w-4" />
                                                                                    </span>
                                                                                    {index < timeline.length - 1 ? <span className="absolute top-8 h-[calc(100%-0.5rem)] w-px bg-[var(--site-primary)]/30" /> : null}
                                                                                </div>
                                                                                <div className="pb-5">
                                                                                    <p className="font-black">{orderStatusLabel(change.status, message)}</p>
                                                                                    <p className="mt-1 text-sm text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">{formatDate(change.date)}</p>
                                                                                    {change.note ? <p className="mt-2 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]">{change.note}</p> : null}
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="overflow-visible rounded-3xl border border-[color-mix(in_srgb,var(--site-text)_5%,transparent)] bg-[var(--site-surface)] shadow-sm">
                                                        <div className="border-b border-[color-mix(in_srgb,var(--site-text)_5%,transparent)] px-5 py-4">
                                                            <h3 className="font-black">{message("ui.order.items")}</h3>
                                                        </div>
                                                        <div className="divide-y divide-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                                                            {selectedOrder.items.map((item) => (
                                                                <div key={item.uuid} className="relative grid gap-3 p-5 sm:grid-cols-[64px_minmax(0,1fr)_120px_80px_120px_44px] sm:items-center">
                                                                    <span className="grid h-16 w-16 place-items-center overflow-hidden rounded-2xl bg-[var(--site-surface-elevated)] text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_35%,transparent)]">
                                                                        {item.primaryImage ? (
                                                                            <img
                                                                                alt={item.primaryImage.alt || item.productName}
                                                                                className="h-full w-full object-cover"
                                                                                src={item.primaryImage.thumbnailUrl ?? item.primaryImage.url}
                                                                            />
                                                                        ) : item.productName.slice(0, 2)}
                                                                    </span>
                                                                    <div className="min-w-0">
                                                                        <p className="truncate font-black">{item.productName}</p>
                                                                        {item.productReference ? <p className="mt-1 text-xs text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">{item.productReference}</p> : null}
                                                                    </div>
                                                                    <p className="text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">{message("ui.order.inOrder")}</p>
                                                                    <p className="text-sm font-bold">{item.quantity} {message("ui.product.unit")}</p>
                                                                    <p className="font-black">{formatMoney(item.totalPrice, selectedOrder.currencyCode)}</p>
                                                                    <button
                                                                        type="button"
                                                                        className="grid h-10 w-10 place-items-center rounded-full bg-[var(--site-surface-elevated)] text-lg font-black hover:bg-[var(--site-inverse)] hover:text-[var(--site-on-inverse)]"
                                                                        aria-label={message("ui.common.actionsFor", { name: item.productName })}
                                                                        onClick={() => setOpenOrderItemMenuUuid((current) => current === item.uuid ? null : item.uuid)}
                                                                    >
                                                                        ...
                                                                    </button>
                                                                    {openOrderItemMenuUuid === item.uuid ? (
                                                                        <div className="absolute right-5 top-16 z-10 w-52 rounded-2xl bg-[var(--site-surface)] p-2 shadow-[0_18px_54px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                                                                            <button type="button" className="block w-full rounded-xl px-3 py-2 text-left text-sm font-bold hover:bg-[var(--site-surface-elevated)]">{message("ui.order.complainProduct")}</button>
                                                                            <button type="button" className="block w-full rounded-xl px-3 py-2 text-left text-sm font-bold hover:bg-[var(--site-surface-elevated)]">{message("ui.product.writeReview")}</button>
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="rounded-3xl border border-[color-mix(in_srgb,var(--site-text)_5%,transparent)] bg-[var(--site-surface)] p-5 shadow-sm">
                                                        <h3 className="font-black">{message("ui.order.delivery")}</h3>
                                                        <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_120px_80px_120px] sm:items-center">
                                                            <p className="font-bold">{selectedOrder.carrierName ?? message("ui.order.delivery")}</p>
                                                            <p className="text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">{orderStatusLabel(selectedOrder.status, message)}</p>
                                                            <p className="text-sm font-bold">1 {message("ui.product.unit")}</p>
                                                            <p className="font-black">{formatMoney(selectedOrder.carrierPrice, selectedOrder.currencyCode)}</p>
                                                        </div>
                                                    </div>

                                                    <div className="rounded-3xl border border-[color-mix(in_srgb,var(--site-text)_5%,transparent)] bg-[var(--site-surface)] p-5 shadow-sm">
                                                        <h3 className="font-black">{message("ui.order.payment")}</h3>
                                                        <div className="mt-4 grid gap-3">
                                                            <div className="flex justify-between gap-4 text-sm">
                                                                <span className="text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]">{selectedOrder.paymentName ?? message("ui.order.payment")}</span>
                                                                <strong>{formatMoney(selectedOrder.paymentSurcharge, selectedOrder.currencyCode)}</strong>
                                                            </div>
                                                            <div className="flex justify-between gap-4 text-sm">
                                                                <span className="text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]">{message("ui.order.goods")}</span>
                                                                <strong>{formatMoney(selectedOrder.itemsPrice, selectedOrder.currencyCode)}</strong>
                                                            </div>
                                                            <div className="flex justify-between gap-4 text-sm">
                                                                <span className="text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]">{message("ui.order.delivery")}</span>
                                                                <strong>{formatMoney(selectedOrder.carrierPrice, selectedOrder.currencyCode)}</strong>
                                                            </div>
                                                            <div className="flex justify-between gap-4 border-t border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] pt-3 text-sm">
                                                                <span className="text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]">{message("ui.order.totalWithoutVat")}</span>
                                                                <strong>{formatMoney(selectedOrder.totalNetPrice, selectedOrder.currencyCode)}</strong>
                                                            </div>
                                                            <div className="flex justify-between gap-4 text-lg">
                                                                <span className="font-black">{message("ui.order.totalWithVat")}</span>
                                                                <strong>{formatMoney(selectedOrder.totalPrice, selectedOrder.currencyCode)}</strong>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="rounded-3xl border border-[color-mix(in_srgb,var(--site-text)_5%,transparent)] bg-[var(--site-surface)] p-5 shadow-sm">
                                                        <h3 className="font-black">{message("ui.order.shippingAddress")}</h3>
                                                        <p className="mt-2 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]">{deliverySummary(selectedOrder)}</p>
                                                    </div>

                                                    <div className="rounded-3xl bg-[var(--site-surface-elevated)] p-5">
                                                        <h3 className="font-black">{message("ui.order.billingAddress")}</h3>
                                                        <div className="mt-4 grid gap-4 md:grid-cols-2">
                                                            <div>
                                                                <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.order.customer")}</p>
                                                                <p className="mt-1 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_65%,transparent)]">
                                                                    {[selectedOrder.customer.firstname, selectedOrder.customer.lastname].filter(Boolean).join(" ") || "-"}<br />
                                                                    {selectedOrder.customer.email ?? "-"}
                                                                    {selectedOrder.customer.phone ? <><br />{selectedOrder.customer.phone}</> : null}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.order.address")}</p>
                                                                <p className="mt-1 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_65%,transparent)]">{addressLine(selectedOrder.billingAddress) || "-"}</p>
                                                                {selectedOrder.billingAddress.ic || selectedOrder.billingAddress.vat ? (
                                                                    <p className="mt-2 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_65%,transparent)]">
                                                                        {selectedOrder.billingAddress.ic ? <>{message("ui.account.companyId")}: {selectedOrder.billingAddress.ic}<br /></> : null}
                                                                        {selectedOrder.billingAddress.vat ? <>{message("ui.account.vatId")}: {selectedOrder.billingAddress.vat}</> : null}
                                                                    </p>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : null}

                        {activeSection === "addresses" ? (
                            <div className="mt-6 grid gap-5">
                                <section className="overflow-hidden rounded-[2rem] bg-[var(--site-background)] shadow-inner shadow-[var(--site-shadow-color)]">
                                    <div className="flex flex-col gap-4 bg-[var(--site-surface)] p-5 ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)] sm:flex-row sm:items-center sm:justify-between">
                                        <div>
                                            <p className="text-xs font-black uppercase tracking-[0.16em] text-[var(--site-primary)]">{message("ui.account.deliveryAndBilling")}</p>
                                            <h2 className="mt-1 text-2xl font-black">{message("ui.account.savedAddresses")}</h2>
                                            <p className="mt-2 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">
                                                {message("ui.account.addressIntro")}
                                            </p>
                                        </div>
                                        <button
                                            {...editablePart("addAddressButton")}
                                            type="button"
                                            className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--site-primary)] px-5 py-3 text-sm font-black text-[var(--site-on-primary)] shadow-sm transition disabled:opacity-60"
                                            style={partStyle(block, "addAddressButton")}
                                            disabled={customerApiBasePath === null || addressSubmitting}
                                            onClick={openNewAddressForm}
                                        >
                                            <Icon name="mapPin" className="h-4 w-4" />
                                            {message("ui.account.addAddress")}
                                        </button>
                                    </div>

                                    {addressesLoading ? (
                                        <div className="grid gap-3 p-5 sm:grid-cols-2">
                                            <div className="h-36 animate-pulse rounded-3xl bg-[var(--site-surface)]" />
                                            <div className="h-36 animate-pulse rounded-3xl bg-[var(--site-surface)]" />
                                        </div>
                                    ) : null}

                                    {addressesError ? (
                                        <div className="p-5">
                                            <p className="rounded-2xl bg-[var(--site-primary-soft)] px-4 py-3 text-sm font-bold text-[var(--site-primary)] ring-1 ring-[var(--site-primary)]">{addressesError}</p>
                                        </div>
                                    ) : null}

                                    {addressMessage ? (
                                        <div className="px-5 pt-5">
                                            <p className="rounded-2xl bg-[var(--site-accent-soft)] px-4 py-3 text-sm font-bold text-[var(--site-accent)] ring-1 ring-[var(--site-accent)]">{addressMessage}</p>
                                        </div>
                                    ) : null}

                                    {!addressesLoading && addresses.length === 0 ? (
                                        <div className="grid min-h-72 place-items-center p-8 text-center">
                                            <div className="max-w-sm">
                                                <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[var(--site-surface)] text-[var(--site-primary)] shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                                                    <Icon name="mapPin" className="h-7 w-7" />
                                                </span>
                                                <h3 className="mt-5 text-xl font-black">{message("ui.account.noAddress")}</h3>
                                                <p className="mt-2 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">
                                                    {message("ui.account.firstAddressDefault")}
                                                </p>
                                            </div>
                                        </div>
                                    ) : null}

                                    {addresses.length > 0 ? (
                                        <div className="grid gap-4 p-4 sm:grid-cols-2 sm:p-5">
                                            {addresses.map((address) => (
                                                <article key={address.uuid} className="rounded-3xl bg-[var(--site-surface)] p-5 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div className="flex min-w-0 gap-3">
                                                            <span
                                                                className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[var(--site-surface-elevated)] text-[var(--site-primary)]"
                                                                title={customerAddressTypeLabel(address.addressType, message)}
                                                            >
                                                                <Icon name={customerAddressTypeIcon(address.addressType)} className="h-5 w-5" />
                                                            </span>
                                                            <div className="min-w-0">
                                                                <p className="truncate font-black">
                                                                    {[address.firstname, address.lastname].filter(Boolean).join(" ")}
                                                                </p>
                                                                {address.company ? <p className="mt-1 truncate text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">{address.company}</p> : null}
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <button
                                                                type="button"
                                                                className="grid h-10 w-10 place-items-center rounded-full bg-[var(--site-surface-elevated)] text-[color-mix(in_srgb,var(--site-text)_60%,transparent)] transition hover:bg-[var(--site-inverse)] hover:text-[var(--site-on-inverse)]"
                                                                title={message("ui.common.edit")}
                                                                onClick={() => openEditAddressForm(address)}
                                                            >
                                                                <Icon name="user" className="h-4 w-4" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="grid h-10 w-10 place-items-center rounded-full bg-[var(--site-surface-elevated)] text-[color-mix(in_srgb,var(--site-text)_60%,transparent)] transition hover:bg-[var(--site-primary)] hover:text-[var(--site-on-primary)] disabled:opacity-50"
                                                                title={message("ui.common.delete")}
                                                                disabled={addressSubmitting}
                                                                onClick={() => {
                                                                    void deleteAddress(address.uuid);
                                                                }}
                                                            >
                                                                <Icon name="trash" className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <p className="mt-4 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_65%,transparent)]">{customerAddressLine(address)}</p>
                                                    {address.address2 ? <p className="mt-1 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">{address.address2}</p> : null}
                                                    <div className="mt-4 flex flex-wrap gap-2">
                                                        {address.deliveryForCustomer ? (
                                                            <span className="rounded-full bg-[var(--site-primary)]/10 px-3 py-1 text-xs font-black text-[var(--site-primary)]">{message("ui.account.delivery")}</span>
                                                        ) : null}
                                                        {address.billingForCustomer ? (
                                                            <span className="rounded-full bg-[color-mix(in_srgb,var(--site-text)_5%,transparent)] px-3 py-1 text-xs font-black text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">{message("ui.account.billing")}</span>
                                                        ) : null}
                                                        <span className="rounded-full bg-[color-mix(in_srgb,var(--site-text)_5%,transparent)] px-3 py-1 text-xs font-black text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">
                                                            {customerAddressTypeLabel(address.addressType, message)}
                                                        </span>
                                                    </div>
                                                    <div className="mt-4 grid gap-1 text-sm text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">
                                                        {address.email ? <span>{address.email}</span> : null}
                                                        {address.phone ? <span>{address.phone}</span> : null}
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    ) : null}
                                </section>

                                {addressFormOpen && addressForm ? (
                                    <form className="rounded-[2rem] bg-[var(--site-surface)] p-5 shadow-[0_18px_48px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]" onSubmit={submitAddress}>
                                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                            <div>
                                                <p className="text-xs font-black uppercase tracking-[0.16em] text-[var(--site-primary)]">
                                                    {editingAddressUuid === null ? message("ui.account.newAddress") : message("ui.account.editAddress")}
                                                </p>
                                                <h3 className="mt-1 text-xl font-black">{message("ui.account.deliveryAddress")}</h3>
                                            </div>
                                            <div className="flex flex-col-reverse gap-2 sm:flex-row">
                                                <button
                                                    type="button"
                                                    className="rounded-full bg-[var(--site-surface-elevated)] px-5 py-3 text-sm font-black text-[color-mix(in_srgb,var(--site-text)_60%,transparent)] transition hover:bg-[var(--site-inverse)] hover:text-[var(--site-on-inverse)]"
                                                    onClick={() => {
                                                        setAddressFormOpen(false);
                                                        setAddressForm(null);
                                                        setEditingAddressUuid(null);
                                                    }}
                                                >
                                                    {message("ui.common.cancel")}
                                                </button>
                                                <button
                                                    {...editableTextPart("addressSaveButton")}
                                                    type="submit"
                                                    className="rounded-full bg-[var(--site-primary)] px-5 py-3 text-sm font-black text-[var(--site-on-primary)] shadow-sm transition disabled:opacity-60"
                                                    style={partStyle(block, "addressSaveButton")}
                                                    disabled={addressSubmitting}
                                                >
                                                    {addressSubmitting ? message("ui.common.saving") : richTextContent(block, "addressSaveButtonLabel", "Uložit adresu")}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="mt-5 grid gap-4 md:grid-cols-2">
                                            <label {...editablePart("firstnameInput")} className="block text-sm font-bold" style={partStyle(block, "firstnameInput")}>
                                                {blockText(block, "firstnameInput", "Jméno")} *
                                                <input className={profileInputClass} value={addressForm.firstname} onChange={(event) => updateAddressForm({ firstname: event.currentTarget.value })} required />
                                            </label>
                                            <label {...editablePart("lastnameInput")} className="block text-sm font-bold" style={partStyle(block, "lastnameInput")}>
                                                {blockText(block, "lastnameInput", "Příjmení")} *
                                                <input className={profileInputClass} value={addressForm.lastname} onChange={(event) => updateAddressForm({ lastname: event.currentTarget.value })} required />
                                            </label>
                                            <label {...editablePart("companyInput")} className="block text-sm font-bold md:col-span-2" style={partStyle(block, "companyInput")}>
                                                {blockText(block, "companyInput", "Název firmy")}
                                                <input className={profileInputClass} value={addressForm.company ?? ""} onChange={(event) => updateAddressForm({ company: event.currentTarget.value })} />
                                            </label>
                                            <label {...editablePart("address1Input")} className="block text-sm font-bold" style={partStyle(block, "address1Input")}>
                                                {blockText(block, "address1Input", "Ulice")} *
                                                <input className={profileInputClass} value={addressForm.address1} onChange={(event) => updateAddressForm({ address1: event.currentTarget.value })} required />
                                            </label>
                                            <label {...editablePart("houseNumberInput")} className="block text-sm font-bold" style={partStyle(block, "houseNumberInput")}>
                                                {blockText(block, "houseNumberInput", "Číslo popisné")}
                                                <input className={profileInputClass} value={addressForm.houseNumber ?? ""} onChange={(event) => updateAddressForm({ houseNumber: event.currentTarget.value })} />
                                            </label>
                                            <label {...editablePart("postcodeInput")} className="block text-sm font-bold" style={partStyle(block, "postcodeInput")}>
                                                {blockText(block, "postcodeInput", "PSČ")} *
                                                <input className={profileInputClass} value={addressForm.postcode} onChange={(event) => updateAddressForm({ postcode: event.currentTarget.value })} required />
                                            </label>
                                            <label {...editablePart("cityInput")} className="block text-sm font-bold" style={partStyle(block, "cityInput")}>
                                                {blockText(block, "cityInput", "Město")} *
                                                <input className={profileInputClass} value={addressForm.city} onChange={(event) => updateAddressForm({ city: event.currentTarget.value })} required />
                                            </label>
                                            <label {...editablePart("emailInput")} className="block text-sm font-bold" style={partStyle(block, "emailInput")}>
                                                {blockText(block, "emailInput", "E-mail")}
                                                <input className={profileInputClass} type="email" value={addressForm.email ?? ""} onChange={(event) => updateAddressForm({ email: event.currentTarget.value })} />
                                            </label>
                                            <label {...editablePart("phoneInput")} className="block text-sm font-bold" style={partStyle(block, "phoneInput")}>
                                                {blockText(block, "phoneInput", "Telefon")}
                                                <input className={profileInputClass} type="tel" value={addressForm.phone ?? ""} onChange={(event) => updateAddressForm({ phone: event.currentTarget.value })} />
                                            </label>
                                            <label {...editablePart("address2Input")} className="block text-sm font-bold md:col-span-2" style={partStyle(block, "address2Input")}>
                                                {blockText(block, "address2Input", "Upřesňující informace")}
                                                <input
                                                    className={profileInputClass}
                                                    value={addressForm.address2 ?? ""}
                                                    onChange={(event) => updateAddressForm({ address2: event.currentTarget.value })}
                                                    placeholder={message("ui.account.deliveryNotePlaceholder")}
                                                />
                                            </label>
                                        </div>

                                        <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                                            <div>
                                            <p className="text-sm font-black">{message("ui.account.addressType")}</p>
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    {CUSTOMER_ADDRESS_TYPES.map((option) => (
                                                        <button
                                                            key={option.value}
                                                            type="button"
                                                            className={classNames(
                                                                "grid h-12 w-12 place-items-center rounded-2xl transition",
                                                                addressForm.addressType === option.value
                                                                    ? "bg-[var(--site-primary)] text-[var(--site-on-primary)] shadow-sm"
                                                                    : "bg-[var(--site-surface-elevated)] text-[color-mix(in_srgb,var(--site-text)_55%,transparent)] hover:bg-[var(--site-inverse)] hover:text-[var(--site-on-inverse)]",
                                                            )}
                                                            title={message(option.messageKey)}
                                                            aria-label={message(option.messageKey)}
                                                            onClick={() => updateAddressForm({ addressType: option.value })}
                                                        >
                                                            <Icon name={option.icon} className="h-5 w-5" />
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="grid gap-2">
                                                <label className="flex items-start gap-3 rounded-2xl bg-[var(--site-surface-elevated)] p-4 text-sm font-bold">
                                                    <input
                                                        type="checkbox"
                                                        className={softCheckboxClass}
                                                        checked={addressForm.useAsDeliveryAddress}
                                                        onChange={(event) => updateAddressForm({ useAsDeliveryAddress: event.currentTarget.checked })}
                                                    />
                                                    <span>{message("ui.account.useForDelivery")}</span>
                                                </label>
                                                <label className="flex items-start gap-3 rounded-2xl bg-[var(--site-surface-elevated)] p-4 text-sm font-bold">
                                                    <input
                                                        type="checkbox"
                                                        className={softCheckboxClass}
                                                        checked={addressForm.useAsBillingAddress}
                                                        onChange={(event) => updateAddressForm({ useAsBillingAddress: event.currentTarget.checked })}
                                                    />
                                                    <span>{message("ui.account.useForBilling")}</span>
                                                </label>
                                            </div>
                                        </div>
                                    </form>
                                ) : null}
                            </div>
                        ) : null}

                        {activeSection === "settings" ? (
                            <div className="mt-6 grid gap-6">
                                <form className="rounded-[2rem] bg-[var(--site-background)] p-5 shadow-inner shadow-[var(--site-shadow-color)]" onSubmit={submitProfile}>
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                        <div>
                                            <h2 {...editableTextPart("profileTitle")} className="text-xl font-black" style={partStyle(block, "profileTitle")}>{richTextContent(block, "profileTitle", "Profilové údaje")}</h2>
                                            <p {...editableTextPart("profileText")} className="mt-1 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]" style={partStyle(block, "profileText")}>
                                                {richTextContent(block, "profileText", "Vyberte typ zákazníka a uložte údaje pro další objednávky.")}
                                            </p>
                                        </div>
                                        <button
                                            {...editableTextPart("profileSaveButton")}
                                            type="submit"
                                            className="rounded-full bg-[var(--site-primary)] px-5 py-3 text-sm font-black text-[var(--site-on-primary)] shadow-sm transition disabled:opacity-60"
                                            style={partStyle(block, "profileSaveButton")}
                                            disabled={profileSubmitting || customerLoading}
                                        >
                                            {profileSubmitting ? message("ui.common.saving") : richTextContent(block, "profileSaveButtonLabel", "Uložit údaje")}
                                        </button>
                                    </div>

                                    {profileMessage ? <p className="mt-4 border border-[var(--site-accent)] bg-[var(--site-accent-soft)] p-3 text-sm font-bold text-[var(--site-accent)]">{profileMessage}</p> : null}
                                    {profileError ? <p className="mt-4 border border-[var(--site-primary)] bg-[var(--site-primary-soft)] p-3 text-sm font-bold text-[var(--site-primary)]">{profileError}</p> : null}

                                    <div className="mt-6 grid gap-5">
                                        <div className="grid gap-3 rounded-3xl bg-[var(--site-surface)] p-2 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)] sm:grid-cols-2">
                                            {[
                                                ["personal", message("ui.account.personalCustomer"), message("ui.account.personalCustomerHint"), "user"],
                                                ["company", message("ui.account.companyCustomer"), message("ui.account.companyCustomerHint"), "store"],
                                            ].map(([type, title, text, icon]) => (
                                                <button
                                                    key={type}
                                                    type="button"
                                                    className={classNames(
                                                        "flex items-center gap-3 rounded-2xl p-4 text-left transition",
                                                        profileCustomerType === type
                                                            ? "bg-[var(--site-primary)] text-[var(--site-on-primary)] shadow-sm"
                                                            : "bg-[var(--site-surface-elevated)] text-[color-mix(in_srgb,var(--site-text)_70%,transparent)] hover:bg-[var(--site-surface)] hover:text-[var(--site-text)]",
                                                    )}
                                                    onClick={() => setProfileCustomerType(type as AccountCustomerType)}
                                                >
                                                    <span className={classNames(
                                                        "grid h-11 w-11 shrink-0 place-items-center rounded-2xl",
                                                        profileCustomerType === type ? "bg-[color-mix(in_srgb,var(--site-surface)_18%,transparent)]" : "bg-[var(--site-surface)]",
                                                    )}>
                                                        <Icon name={icon as IconName} className="h-5 w-5" />
                                                    </span>
                                                    <span>
                                                        <span className="block text-sm font-black">{title}</span>
                                                        <span className={classNames(
                                                            "mt-1 block text-xs font-bold",
                                                            profileCustomerType === type ? "text-[color-mix(in_srgb,var(--site-on-inverse)_70%,transparent)]" : "text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]",
                                                        )}>
                                                            {text}
                                                        </span>
                                                    </span>
                                                </button>
                                            ))}
                                        </div>

                                        <section className={profilePanelClass}>
                                            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                                                <div>
                                                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[var(--site-primary)]">{message("ui.account.contact")}</p>
                                                    <h3 className="mt-1 text-lg font-black">{message("ui.account.personalData")}</h3>
                                                </div>
                                                <p className="text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">
                                                    {profileCustomerType === "company" ? message("ui.account.companyContact") : message("ui.account.personalAccountBase")}
                                                </p>
                                            </div>
                                            <div className="mt-4 grid gap-4 md:grid-cols-2">
                                                <label {...editablePart("firstnameInput")} className="block text-sm font-bold" style={partStyle(block, "firstnameInput")}>
                                                    {blockText(block, "firstnameInput", "Jméno")}
                                                    <input
                                                        className={profileInputClass}
                                                        value={profileForm.firstname}
                                                        onChange={(event) => setProfileForm((current) => ({ ...current, firstname: event.currentTarget.value }))}
                                                        required
                                                    />
                                                </label>
                                                <label {...editablePart("lastnameInput")} className="block text-sm font-bold" style={partStyle(block, "lastnameInput")}>
                                                    {blockText(block, "lastnameInput", "Příjmení")}
                                                    <input
                                                        className={profileInputClass}
                                                        value={profileForm.lastname}
                                                        onChange={(event) => setProfileForm((current) => ({ ...current, lastname: event.currentTarget.value }))}
                                                        required
                                                    />
                                                </label>
                                                <div className="block text-sm font-bold md:col-span-2">
                                                    <label htmlFor="customer-profile-email">{message("ui.account.email")}</label>
                                                    <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                                                        <input
                                                            id="customer-profile-email"
                                                            className="w-full rounded-2xl border border-transparent bg-[var(--site-surface-elevated)] px-4 py-3 font-normal outline-none transition focus:border-[var(--site-primary)] focus:bg-[var(--site-surface)] disabled:cursor-not-allowed disabled:text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]"
                                                            type="email"
                                                            value={profileForm.email}
                                                            disabled
                                                            required
                                                        />
                                                        <button
                                                            type="button"
                                                            className="shrink-0 rounded-2xl bg-[var(--site-surface-elevated)] px-4 py-3 text-sm font-black text-[color-mix(in_srgb,var(--site-text)_60%,transparent)] transition hover:bg-[var(--site-inverse)] hover:text-[var(--site-on-inverse)]"
                                                            onClick={() => {
                                                                setEmailDraft(profileForm.email);
                                                                setEmailModalOpen(true);
                                                            }}
                                                        >
                                                            {message("ui.account.change")}
                                                        </button>
                                                    </div>
                                                </div>
                                                {profileCustomerType === "company" ? (
                                                    <>
                                                        <div className="md:col-span-2">
                                                            <div className="h-px bg-[color-mix(in_srgb,var(--site-text)_5%,transparent)]" />
                                                        </div>
                                                    <label {...editablePart("companyInput")} className="block text-sm font-bold md:col-span-2" style={partStyle(block, "companyInput")}>
                                                        {blockText(block, "companyInput", "Název firmy")}
                                                        <input
                                                                className={profileInputClass}
                                                                value={profileForm.company ?? ""}
                                                                onChange={(event) => setProfileForm((current) => ({ ...current, company: event.currentTarget.value }))}
                                                            />
                                                        </label>
                                                    <label {...editablePart("icInput")} className="block text-sm font-bold" style={partStyle(block, "icInput")}>
                                                        {blockText(block, "icInput", "IČ")}
                                                        <input
                                                                className={profileInputClass}
                                                                value={profileForm.ic ?? ""}
                                                                onChange={(event) => setProfileForm((current) => ({ ...current, ic: event.currentTarget.value }))}
                                                            />
                                                        </label>
                                                    <label {...editablePart("vatInput")} className="block text-sm font-bold" style={partStyle(block, "vatInput")}>
                                                        {blockText(block, "vatInput", "DIČ")}
                                                        <input
                                                                className={profileInputClass}
                                                                value={profileForm.vat ?? ""}
                                                                onChange={(event) => setProfileForm((current) => ({ ...current, vat: event.currentTarget.value }))}
                                                            />
                                                        </label>
                                                    </>
                                                ) : null}
                                            </div>
                                        </section>

                                        <section className={profilePanelClass}>
                                            <h3 className="font-black">{message("ui.account.communication")}</h3>
                                            <label className="mt-3 flex items-start gap-3 rounded-2xl bg-[var(--site-surface-elevated)] p-4 text-sm font-bold">
                                                <input
                                                    type="checkbox"
                                                    className={softCheckboxClass}
                                                    checked={profileForm.newsletterSubscribed}
                                                    onChange={(event) => setProfileForm((current) => ({ ...current, newsletterSubscribed: event.currentTarget.checked }))}
                                                />
                                                <span>
                                                    {message("ui.account.newsletterConsent")}
                                                </span>
                                            </label>
                                        </section>
                                    </div>
                                </form>

                                {emailModalOpen ? (
                                    <div className="fixed inset-0 z-50 grid place-items-center bg-[color-mix(in_srgb,var(--site-text)_45%,transparent)] px-4 py-8">
                                        <button
                                            type="button"
                                            className="absolute inset-0 cursor-default"
                                            aria-label={message("ui.account.closeEmailChange")}
                                            onClick={() => setEmailModalOpen(false)}
                                        />
                                        <form
                                            className="relative w-full max-w-[520px] rounded-[2rem] bg-[var(--site-surface)] p-6 shadow-[0_30px_90px_var(--site-shadow-color)]"
                                            onSubmit={submitEmailChange}
                                        >
                                            <div>
                                                <p className="text-xs font-black uppercase tracking-[0.16em] text-[var(--site-primary)]">{message("ui.account.email")}</p>
                                                <h2 className="mt-2 text-2xl font-black">{message("ui.account.emailChange")}</h2>
                                                <p className="mt-3 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]">
                                                    {message("ui.account.emailExplanation")}
                                                </p>
                                            </div>
                                            <label className="mt-6 block text-sm font-black">
                                                {message("ui.account.newEmail")}
                                                <span className="mt-2 flex items-center rounded-2xl bg-[var(--site-surface-elevated)] px-4 focus-within:ring-2 focus-within:ring-[var(--site-primary)]/30">
                                                    <span className="text-lg font-black text-[color-mix(in_srgb,var(--site-text)_35%,transparent)]">@</span>
                                                    <input
                                                        className="w-full bg-transparent px-3 py-3 font-normal outline-none"
                                                        type="email"
                                                        value={emailDraft}
                                                        onChange={(event) => setEmailDraft(event.currentTarget.value)}
                                                        autoFocus
                                                        required
                                                    />
                                                </span>
                                            </label>
                                            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                                                <button
                                                    {...editablePart("emailCancelButton")}
                                                    type="button"
                                                    className="rounded-2xl bg-[var(--site-surface-elevated)] px-5 py-3 text-sm font-black text-[color-mix(in_srgb,var(--site-text)_60%,transparent)] transition"
                                                    style={partStyle(block, "emailCancelButton")}
                                                    onClick={() => setEmailModalOpen(false)}
                                                >
                                                    {message("ui.common.cancel")}
                                                </button>
                                                <button
                                                    {...editablePart("emailSubmitButton")}
                                                    type="submit"
                                                    className="rounded-2xl bg-[var(--site-primary)] px-5 py-3 text-sm font-black text-[var(--site-on-primary)] shadow-sm transition"
                                                    style={partStyle(block, "emailSubmitButton")}
                                                >
                                                    {message("ui.account.useEmail")}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                ) : null}

                                <section className="rounded-[2rem] bg-[var(--site-surface)] p-5 shadow-[0_18px_48px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                        <div>
                                            <p className="text-xs font-black uppercase tracking-[0.16em] text-[var(--site-primary)]">{message("ui.account.defaultAddress")}</p>
                                            <h2 {...editableTextPart("addressTitle")} className="mt-1 text-xl font-black" style={partStyle(block, "addressTitle")}>{richTextContent(block, "addressTitle", "Doručení a fakturace")}</h2>
                                            <p className="mt-1 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">
                                                {message("ui.account.addressPrefill")}
                                            </p>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                {...editablePart("defaultAddressButton")}
                                                type="button"
                                                className="rounded-full bg-[var(--site-primary)] px-5 py-3 text-sm font-black text-[var(--site-on-primary)] shadow-sm transition disabled:opacity-60"
                                                style={partStyle(block, "defaultAddressButton")}
                                                disabled={customerApiBasePath === null || addressSubmitting}
                                                onClick={() => {
                                                    if (profileDefaultAddress) {
                                                        openEditAddressForm(profileDefaultAddress);
                                                    } else {
                                                        openNewAddressForm();
                                                    }
                                                }}
                                            >
                                                {profileDefaultAddress ? message("ui.account.editDefaultAddress") : message("ui.account.fillAddress")}
                                            </button>
                                            <button
                                                type="button"
                                                className="rounded-full bg-[var(--site-surface-elevated)] px-5 py-3 text-sm font-black text-[color-mix(in_srgb,var(--site-text)_60%,transparent)] transition hover:bg-[var(--site-inverse)] hover:text-[var(--site-on-inverse)]"
                                                onClick={() => selectAccountSection("addresses")}
                                            >
                                                {message("ui.account.manageAddresses")}
                                            </button>
                                        </div>
                                    </div>

                                    {addressesError ? (
                                        <p className="mt-4 rounded-2xl bg-[var(--site-primary-soft)] px-4 py-3 text-sm font-bold text-[var(--site-primary)] ring-1 ring-[var(--site-primary)]">{addressesError}</p>
                                    ) : null}
                                    {addressMessage ? (
                                        <p className="mt-4 rounded-2xl bg-[var(--site-accent-soft)] px-4 py-3 text-sm font-bold text-[var(--site-accent)] ring-1 ring-[var(--site-accent)]">{addressMessage}</p>
                                    ) : null}

                                    {addressesLoading ? (
                                        <div className="mt-5 grid gap-3 md:grid-cols-2">
                                            <div className="h-32 animate-pulse rounded-3xl bg-[var(--site-surface-elevated)]" />
                                            <div className="h-32 animate-pulse rounded-3xl bg-[var(--site-surface-elevated)]" />
                                        </div>
                                    ) : profileDefaultAddress === null ? (
                                        <div className="mt-5 rounded-3xl bg-[var(--site-surface-elevated)] p-5 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]">
                                            {message("ui.account.noAddressDefault")}
                                        </div>
                                    ) : (
                                        <div className="mt-5 grid gap-3 md:grid-cols-2">
                                            {[
                                                { label: message("ui.account.delivery"), address: defaultDeliveryAddress },
                                                { label: message("ui.account.billing"), address: defaultBillingAddress },
                                            ].map((item) => (
                                                item.address ? (
                                                    <article key={item.label} className="rounded-3xl bg-[var(--site-surface-elevated)] p-4">
                                                        <div className="flex items-start justify-between gap-3">
                                                            <div className="min-w-0">
                                                                <p className="text-xs font-black uppercase tracking-[0.14em] text-[color-mix(in_srgb,var(--site-text)_35%,transparent)]">{item.label}</p>
                                                                <h3 className="mt-2 truncate font-black">
                                                                    {[item.address.firstname, item.address.lastname].filter(Boolean).join(" ")}
                                                                </h3>
                                                            </div>
                                                            <span
                                                                className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-[var(--site-surface)] text-[var(--site-primary)]"
                                                                title={customerAddressTypeLabel(item.address.addressType, message)}
                                                            >
                                                                <Icon name={customerAddressTypeIcon(item.address.addressType)} className="h-5 w-5" />
                                                            </span>
                                                        </div>
                                                        {item.address.company ? <p className="mt-2 truncate text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">{item.address.company}</p> : null}
                                                        <p className="mt-3 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]">{customerAddressLine(item.address)}</p>
                                                        <div className="mt-4 flex flex-wrap gap-2">
                                                            <button
                                                                type="button"
                                                                className="rounded-full bg-[var(--site-surface)] px-4 py-2 text-xs font-black text-[color-mix(in_srgb,var(--site-text)_60%,transparent)] transition hover:bg-[var(--site-inverse)] hover:text-[var(--site-on-inverse)]"
                                                                onClick={() => openEditAddressForm(item.address)}
                                                            >
                                                                {message("ui.common.edit")}
                                                            </button>
                                                        </div>
                                                    </article>
                                                ) : null
                                            ))}
                                        </div>
                                    )}

                                    {activeSection === "settings" && addressFormOpen && addressForm ? (
                                        <form className="mt-5 rounded-3xl bg-[var(--site-background)] p-5 ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]" onSubmit={submitAddress}>
                                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                                <div>
                                                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[var(--site-primary)]">
                                                        {editingAddressUuid === null ? message("ui.account.newAddress") : message("ui.account.editAddress")}
                                                    </p>
                                                    <h3 className="mt-1 text-lg font-black">{message("ui.account.customerAddress")}</h3>
                                                </div>
                                                <div className="flex flex-col-reverse gap-2 sm:flex-row">
                                                    <button
                                                        type="button"
                                                        className="rounded-full bg-[var(--site-surface-elevated)] px-5 py-3 text-sm font-black text-[color-mix(in_srgb,var(--site-text)_60%,transparent)] transition hover:bg-[var(--site-inverse)] hover:text-[var(--site-on-inverse)]"
                                                        onClick={() => {
                                                            setAddressFormOpen(false);
                                                            setAddressForm(null);
                                                            setEditingAddressUuid(null);
                                                        }}
                                                    >
                                                        {message("ui.common.cancel")}
                                                    </button>
                                                        <button
                                                            {...editableTextPart("addressSaveButton")}
                                                        type="submit"
                                                        className="rounded-full bg-[var(--site-primary)] px-5 py-3 text-sm font-black text-[var(--site-on-primary)] shadow-sm transition disabled:opacity-60"
                                                        style={partStyle(block, "addressSaveButton")}
                                                        disabled={addressSubmitting}
                                                    >
                                                        {addressSubmitting ? message("ui.common.saving") : richTextContent(block, "addressSaveButtonLabel", "Uložit adresu")}
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="mt-5 grid gap-4 md:grid-cols-2">
                                                <label {...editablePart("firstnameInput")} className="block text-sm font-bold" style={partStyle(block, "firstnameInput")}>
                                                    {blockText(block, "firstnameInput", "Jméno")} *
                                                    <input className={profileInputClass} value={addressForm.firstname} onChange={(event) => updateAddressForm({ firstname: event.currentTarget.value })} required />
                                                </label>
                                                <label {...editablePart("lastnameInput")} className="block text-sm font-bold" style={partStyle(block, "lastnameInput")}>
                                                    {blockText(block, "lastnameInput", "Příjmení")} *
                                                    <input className={profileInputClass} value={addressForm.lastname} onChange={(event) => updateAddressForm({ lastname: event.currentTarget.value })} required />
                                                </label>
                                                <label {...editablePart("companyInput")} className="block text-sm font-bold md:col-span-2" style={partStyle(block, "companyInput")}>
                                                    {blockText(block, "companyInput", "Název firmy")}
                                                    <input className={profileInputClass} value={addressForm.company ?? ""} onChange={(event) => updateAddressForm({ company: event.currentTarget.value })} />
                                                </label>
                                                <label {...editablePart("address1Input")} className="block text-sm font-bold" style={partStyle(block, "address1Input")}>
                                                    {blockText(block, "address1Input", "Ulice")} *
                                                    <input className={profileInputClass} value={addressForm.address1} onChange={(event) => updateAddressForm({ address1: event.currentTarget.value })} required />
                                                </label>
                                                <label {...editablePart("houseNumberInput")} className="block text-sm font-bold" style={partStyle(block, "houseNumberInput")}>
                                                    {blockText(block, "houseNumberInput", "Číslo popisné")}
                                                    <input className={profileInputClass} value={addressForm.houseNumber ?? ""} onChange={(event) => updateAddressForm({ houseNumber: event.currentTarget.value })} />
                                                </label>
                                                <label {...editablePart("postcodeInput")} className="block text-sm font-bold" style={partStyle(block, "postcodeInput")}>
                                                    {blockText(block, "postcodeInput", "PSČ")} *
                                                    <input className={profileInputClass} value={addressForm.postcode} onChange={(event) => updateAddressForm({ postcode: event.currentTarget.value })} required />
                                                </label>
                                                <label {...editablePart("cityInput")} className="block text-sm font-bold" style={partStyle(block, "cityInput")}>
                                                    {blockText(block, "cityInput", "Město")} *
                                                    <input className={profileInputClass} value={addressForm.city} onChange={(event) => updateAddressForm({ city: event.currentTarget.value })} required />
                                                </label>
                                                <label {...editablePart("emailInput")} className="block text-sm font-bold" style={partStyle(block, "emailInput")}>
                                                    {blockText(block, "emailInput", "E-mail")}
                                                    <input className={profileInputClass} type="email" value={addressForm.email ?? ""} onChange={(event) => updateAddressForm({ email: event.currentTarget.value })} />
                                                </label>
                                                <label {...editablePart("phoneInput")} className="block text-sm font-bold" style={partStyle(block, "phoneInput")}>
                                                    {blockText(block, "phoneInput", "Telefon")}
                                                    <input className={profileInputClass} type="tel" value={addressForm.phone ?? ""} onChange={(event) => updateAddressForm({ phone: event.currentTarget.value })} />
                                                </label>
                                                <label {...editablePart("address2Input")} className="block text-sm font-bold md:col-span-2" style={partStyle(block, "address2Input")}>
                                                    {blockText(block, "address2Input", "Upřesňující informace")}
                                                    <input
                                                        className={profileInputClass}
                                                        value={addressForm.address2 ?? ""}
                                                        onChange={(event) => updateAddressForm({ address2: event.currentTarget.value })}
                                                        placeholder={message("ui.account.deliveryNotePlaceholder")}
                                                    />
                                                </label>
                                            </div>

                                            <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                                                <div>
                                                    <p className="text-sm font-black">{message("ui.account.addressType")}</p>
                                                    <div className="mt-2 flex flex-wrap gap-2">
                                                        {CUSTOMER_ADDRESS_TYPES.map((option) => (
                                                            <button
                                                                key={option.value}
                                                                type="button"
                                                                className={classNames(
                                                                    "grid h-12 w-12 place-items-center rounded-2xl transition",
                                                                    addressForm.addressType === option.value
                                                                        ? "bg-[var(--site-primary)] text-[var(--site-on-primary)] shadow-sm"
                                                                        : "bg-[var(--site-surface-elevated)] text-[color-mix(in_srgb,var(--site-text)_55%,transparent)] hover:bg-[var(--site-inverse)] hover:text-[var(--site-on-inverse)]",
                                                                )}
                                                                title={message(option.messageKey)}
                                                                aria-label={message(option.messageKey)}
                                                                onClick={() => updateAddressForm({ addressType: option.value })}
                                                            >
                                                                <Icon name={option.icon} className="h-5 w-5" />
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="grid gap-2">
                                                    <label className="flex items-start gap-3 rounded-2xl bg-[var(--site-surface)] p-4 text-sm font-bold">
                                                        <input
                                                            type="checkbox"
                                                            className={softCheckboxClass}
                                                            checked={addressForm.useAsDeliveryAddress}
                                                            onChange={(event) => updateAddressForm({ useAsDeliveryAddress: event.currentTarget.checked })}
                                                        />
                                                        <span>{message("ui.account.useForDelivery")}</span>
                                                    </label>
                                                    <label className="flex items-start gap-3 rounded-2xl bg-[var(--site-surface)] p-4 text-sm font-bold">
                                                        <input
                                                            type="checkbox"
                                                            className={softCheckboxClass}
                                                            checked={addressForm.useAsBillingAddress}
                                                            onChange={(event) => updateAddressForm({ useAsBillingAddress: event.currentTarget.checked })}
                                                        />
                                                        <span>{message("ui.account.useForBilling")}</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </form>
                                    ) : null}
                                </section>

                                <form className="rounded-[2rem] bg-[var(--site-surface)] p-5 shadow-[0_18px_48px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]" onSubmit={submitPasswordChange}>
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                        <div>
                                            <p className="text-xs font-black uppercase tracking-[0.16em] text-[var(--site-primary)]">{message("ui.account.security")}</p>
                                            <h2 {...editableTextPart("passwordTitle")} className="mt-1 text-xl font-black" style={partStyle(block, "passwordTitle")}>{richTextContent(block, "passwordTitle", "Změna hesla")}</h2>
                                            <p {...editableTextPart("passwordText")} className="mt-1 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]" style={partStyle(block, "passwordText")}>
                                                {richTextContent(block, "passwordText", "Pro uložení zadejte aktuální heslo a nové heslo alespoň na 8 znaků.")}
                                            </p>
                                        </div>
                                        <button
                                            {...editableTextPart("passwordSaveButton")}
                                            type="submit"
                                            className="rounded-full bg-[var(--site-inverse)] px-5 py-3 text-sm font-black text-[var(--site-on-inverse)] shadow-sm disabled:opacity-60"
                                            style={partStyle(block, "passwordSaveButton")}
                                            disabled={passwordSubmitting || customerLoading}
                                        >
                                            {passwordSubmitting ? message("ui.common.changing") : richTextContent(block, "passwordSaveButtonLabel", "Změnit heslo")}
                                        </button>
                                    </div>

                                    {passwordMessage ? <p className="mt-4 border border-[var(--site-accent)] bg-[var(--site-accent-soft)] p-3 text-sm font-bold text-[var(--site-accent)]">{passwordMessage}</p> : null}
                                    {passwordError ? <p className="mt-4 border border-[var(--site-primary)] bg-[var(--site-primary-soft)] p-3 text-sm font-bold text-[var(--site-primary)]">{passwordError}</p> : null}

                                    <div className="mt-5 rounded-3xl bg-[var(--site-surface-elevated)] p-4">
                                        <div className="grid gap-4 md:grid-cols-3">
                                            <label {...editablePart("currentPasswordInput")} className="block text-sm font-bold" style={partStyle(block, "currentPasswordInput")}>
                                                {blockText(block, "currentPasswordInput", "Aktuální heslo")}
                                                <input
                                                    className={passwordInputClass}
                                                    type="password"
                                                    value={currentPassword}
                                                    onChange={(event) => setCurrentPassword(event.currentTarget.value)}
                                                    required
                                                />
                                            </label>
                                            <label {...editablePart("newPasswordInput")} className="block text-sm font-bold" style={partStyle(block, "newPasswordInput")}>
                                                {blockText(block, "newPasswordInput", "Nové heslo")}
                                                <input
                                                    className={passwordInputClass}
                                                    type="password"
                                                    minLength={8}
                                                    value={newPassword}
                                                    onChange={(event) => setNewPassword(event.currentTarget.value)}
                                                    required
                                                />
                                            </label>
                                            <label {...editablePart("newPasswordConfirmationInput")} className="block text-sm font-bold" style={partStyle(block, "newPasswordConfirmationInput")}>
                                                {blockText(block, "newPasswordConfirmationInput", "Zopakovat heslo")}
                                                <input
                                                    className={passwordInputClass}
                                                    type="password"
                                                    minLength={8}
                                                    value={newPasswordConfirmation}
                                                    onChange={(event) => setNewPasswordConfirmation(event.currentTarget.value)}
                                                    required
                                                />
                                            </label>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        ) : null}

                        {activeSection === "favorites" ? (
                            <div className="mt-6 grid gap-5">
                                <section className="overflow-hidden rounded-[2rem] bg-[var(--site-background)] shadow-inner shadow-[var(--site-shadow-color)]">
                                    <div className="flex flex-col gap-4 bg-[var(--site-surface)] p-5 ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)] sm:flex-row sm:items-center sm:justify-between">
                                        <div>
                                            <p className="text-xs font-black uppercase tracking-[0.16em] text-[var(--site-primary)]">{message("ui.account.favoritesEyebrow")}</p>
                                            <h2 className="mt-1 text-2xl font-black">{message("ui.account.favoriteProducts")}</h2>
                                            <p className="mt-2 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">
                                                {message("ui.account.favoritesIntro")}
                                            </p>
                                        </div>
                                        <div className="grid h-20 w-20 place-items-center rounded-[28px] bg-[var(--site-surface-elevated)] text-center">
                                            <span className="block px-2 text-sm font-black text-[var(--site-primary)]">
                                                {storefrontPluralMessage(
                                                    favoriteCount,
                                                    locale,
                                                    ACCOUNT_ITEM_COUNT_MESSAGE_KEYS,
                                                    message,
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    {favoriteProducts.length === 0 ? (
                                        <div className="grid min-h-72 place-items-center p-8 text-center">
                                            <div className="max-w-sm">
                                                <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[var(--site-surface)] text-[var(--site-primary)] shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                                                    <Icon name="heart" className="h-7 w-7" />
                                                </span>
                                                <h3 className="mt-5 text-xl font-black">{message("ui.account.noFavorites")}</h3>
                                                <p className="mt-2 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">
                                                    {message("ui.account.favoriteHint")}
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="grid gap-3 p-4 sm:p-5">
                                            {favoriteProducts.map((product, favoriteIndex) => {
                                                const imagePart = `favorite${favoriteIndex + 1}Image`;
                                                const image = blockText(block, `${imagePart}Url`, product.image ?? "");
                                                const imageAlt = blockText(block, `${imagePart}Alt`, product.alt);
                                                const imageTitle = blockText(block, `${imagePart}Title`, "");

                                                return (
                                                <article
                                                    key={product.id}
                                                    {...{ ...itemHoverAttributes(block), ...imageHoverFrameAttributes(block, imagePart) }}
                                                    className="grid gap-4 rounded-[28px] bg-[var(--site-surface)] p-4 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)] sm:grid-cols-[84px_minmax(0,1fr)_auto] sm:items-center"
                                                >
                                                    <a
                                                        href={functionalStorefrontHref(product.href)}
                                                        className="relative h-[84px] w-[84px] overflow-hidden rounded-[26px] bg-[var(--site-surface-elevated)]"
                                                        style={imageFrameStyle(block, imagePart)}
                                                        aria-label={product.name}
                                                    >
                                                        {image ? (
                                                            <img
                                                                {...editablePart(imagePart)}
                                                                data-shop-image-hover-target="true"
                                                                src={image}
                                                                alt={imageAlt}
                                                                title={imageTitle || undefined}
                                                                className="h-full w-full object-cover"
                                                                style={imageInteractivePartStyle(block, imagePart)}
                                                            />
                                                        ) : (
                                                            <span className="grid h-full w-full place-items-center text-[var(--site-primary)]">
                                                                <Icon name="heart" className="h-7 w-7" />
                                                            </span>
                                                        )}
                                                    </a>
                                                    <div className="min-w-0">
                                                        {product.brand ? <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_35%,transparent)]">{product.brand}</p> : null}
                                                        <a href={functionalStorefrontHref(product.href)} className="mt-1 block text-base font-black leading-snug hover:text-[var(--site-primary)]">
                                                            {product.name}
                                                        </a>
                                                        <div className="mt-2 flex flex-wrap items-center gap-2">
                                                            {product.price ? <span className="font-black text-[var(--site-primary)]">{product.price}</span> : null}
                                                            {product.unavailable ? (
                                                                <span className="rounded-full bg-[var(--site-accent-soft)] px-3 py-1 text-xs font-black text-[var(--site-accent)]">
                                                                    {message("ui.account.productUnavailable")}
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap gap-2 sm:justify-end">
                                                        <a {...editableTextPart("favoriteViewButton")} href={functionalStorefrontHref(product.href)} className="inline-flex items-center justify-center rounded-full bg-[var(--site-primary)] px-4 py-2.5 text-sm font-black text-[var(--site-on-primary)]" style={partStyle(block, "favoriteViewButton")}>
                                                            {richTextContent(block, "favoriteViewButtonLabel", "Zobrazit")}
                                                        </a>
                                                        <button
                                                            {...editableTextPart("favoriteRemoveButton")}
                                                            type="button"
                                                            className="inline-flex items-center justify-center rounded-full bg-[var(--site-surface-elevated)] px-4 py-2.5 text-sm font-black text-[color-mix(in_srgb,var(--site-text)_60%,transparent)] transition"
                                                            style={partStyle(block, "favoriteRemoveButton")}
                                                            onClick={() => onToggleFavorite(product.id)}
                                                        >
                                                            {richTextContent(block, "favoriteRemoveButtonLabel", "Odebrat")}
                                                        </button>
                                                    </div>
                                                </article>
                                                );
                                            })}
                                        </div>
                                    )}
                                </section>
                            </div>
                        ) : null}

                        {activeSection === "connections" ? (
                            <div className="mt-6 grid gap-5">
                                <section className={profilePanelClass}>
                                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[var(--site-primary)]">{message("ui.account.signInEyebrow")}</p>
                                    <h2 className="mt-1 text-xl font-black">{message("ui.account.connectedAccounts")}</h2>
                                    <p className="mt-2 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">
                                        {message("ui.account.connectionsIntro")}
                                    </p>
                                    <div className="mt-5 grid gap-3">
                                        {[
                                            [message("ui.account.emailAndPassword"), customerSession.customer.email, true],
                                            ["Google", message("ui.account.connectionReady"), false],
                                            ["Facebook", message("ui.account.connectionReady"), false],
                                        ].map(([title, description, connected]) => (
                                            <div key={String(title)} className="flex flex-col gap-3 rounded-2xl bg-[var(--site-surface-elevated)] p-4 sm:flex-row sm:items-center sm:justify-between">
                                                <div>
                                                    <p className="font-black">{title}</p>
                                                    <p className="mt-1 text-sm text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">{description}</p>
                                                </div>
                                                <span className={classNames(
                                                    "inline-flex w-fit rounded-full px-3 py-1 text-xs font-black",
                                                    connected ? "bg-[var(--site-accent-soft)] text-[var(--site-accent)]" : "bg-[var(--site-surface)] text-[color-mix(in_srgb,var(--site-text)_45%,transparent)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]",
                                                )}>
                                                    {connected ? message("ui.account.connected") : message("ui.account.comingSoon")}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            </div>
                        ) : null}

                        {activeSection !== "overview" && activeSection !== "orders" && activeSection !== "addresses" && activeSection !== "favorites" && activeSection !== "settings" && activeSection !== "connections" ? (
                            <div className="mt-6 border border-[var(--site-border)] bg-[var(--site-surface)] p-5">
                                <h2 className="font-black">{links.find((link) => link.id === activeSection)?.label}</h2>
                                <p className="mt-2 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">{message("ui.account.sectionComingSoon")}</p>
                            </div>
                        ) : null}

                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section ref={accountRootRef} className="px-5 py-10 sm:px-6" style={blockVisualStyle(block)}>
            <div className="mx-auto grid max-w-[var(--site-container)] gap-8 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                <div>
                    <p {...editableTextPart("eyebrow")} className="text-xs font-bold uppercase text-[var(--site-primary)]" style={partStyle(block, "eyebrow")}>{richTextContent(block, "eyebrow", "Zákaznický účet")}</p>
                    <h1 {...editableTextPart("title")} className="mt-3 text-4xl font-black leading-tight" style={partStyle(block, "title")}>{richTextContent(block, "title", "Přihlášení a registrace")}</h1>
                    <p {...editableTextPart("text")} className="mt-4 max-w-xl text-sm leading-7 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]" style={partStyle(block, "text")}>
                        {richTextContent(block, "text", "Vytvořte si účet pro rychlejší objednávky. Registrace vyžaduje pouze e-mail a heslo.")}
                    </p>
                    <div className="mt-7 grid gap-3 text-sm text-[color-mix(in_srgb,var(--site-text)_65%,transparent)]">
                        <div {...editableTextPart("benefit1Text")} className="flex items-center gap-3" style={partStyle(block, "benefit1Text")}><Icon name="check" className="h-5 w-5 text-[var(--site-primary)]" /> {richTextContent(block, "benefit1Text", "Košík se po přihlášení propojí s účtem.")}</div>
                        <div {...editableTextPart("benefit2Text")} className="flex items-center gap-3" style={partStyle(block, "benefit2Text")}><Icon name="shield" className="h-5 w-5 text-[var(--site-primary)]" /> {richTextContent(block, "benefit2Text", "Heslo ukládáme bezpečně jako hash.")}</div>
                        <div {...editableTextPart("benefit3Text")} className="flex items-center gap-3" style={partStyle(block, "benefit3Text")}><Icon name="truck" className="h-5 w-5 text-[var(--site-primary)]" /> {richTextContent(block, "benefit3Text", "Objednávka se spáruje se zákaznickým účtem.")}</div>
                    </div>
                </div>
                <div className="border border-[var(--site-border)] bg-[var(--site-surface)] p-5 shadow-sm sm:p-7">
                    <div className="grid grid-cols-2 gap-2 rounded-full bg-[var(--site-surface)] p-1">
                        {[
                            ["login", message("ui.account.login")],
                            ["register", message("ui.account.register")],
                        ].map(([value, label]) => (
                            <button
                                key={value}
                                {...editablePart(`${value}Button`)}
                                type="button"
                                className={classNames(
                                    "rounded-full px-4 py-3 text-sm font-black transition",
                                    mode === value ? "bg-[var(--site-surface)] text-[var(--site-primary)] shadow-sm" : "text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]",
                                )}
                                style={partStyle(block, `${value}Button`)}
                                onClick={() => {
                                    setMode(value as AccountMode);
                                    setRegistrationMessage(null);
                                }}
                            >
                                {blockText(block, `${value}ButtonLabel`, label)}
                            </button>
                        ))}
                    </div>
                    {passwordResetMode === "request" ? (
                        <form className="mt-6 grid gap-4 rounded-3xl bg-[var(--site-surface-elevated)] p-4" onSubmit={submitPasswordResetRequest}>
                            <div>
                                <h2 {...editableTextPart("resetTitle")} className="text-xl font-black" style={partStyle(block, "resetTitle")}>{richTextContent(block, "resetTitle", "Obnova hesla")}</h2>
                                <p {...editableTextPart("resetText")} className="mt-2 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]" style={partStyle(block, "resetText")}>
                                    {richTextContent(block, "resetText", "Zadejte e-mail k účtu a pošleme vám odkaz pro nastavení nového hesla.")}
                                </p>
                            </div>
                            <label {...editablePart("resetEmailInput")} className="grid gap-2 text-sm font-bold" style={partStyle(block, "resetEmailInput")}>
                                {blockText(block, "resetEmailInput", "E-mail")}
                                <input
                                    className="rounded-2xl border border-[var(--site-border)] bg-[var(--site-surface)] px-4 py-3 text-base font-normal outline-none focus:border-[var(--site-primary)]"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={passwordResetEmail || email}
                                    onChange={(event) => setPasswordResetEmail(event.target.value)}
                                />
                            </label>
                            {passwordResetMessage ? <p className="rounded-2xl bg-[var(--site-accent-soft)] px-4 py-3 text-sm font-bold text-[var(--site-accent)] ring-1 ring-[var(--site-accent)]">{passwordResetMessage}</p> : null}
                            {passwordResetError ? <p className="rounded-2xl bg-[var(--site-primary-soft)] px-4 py-3 text-sm font-bold text-[var(--site-primary)] ring-1 ring-[var(--site-primary)]">{passwordResetError}</p> : null}
                            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                                <button
                                    {...editableTextPart("resetBackButton")}
                                    type="button"
                                    className="rounded-full border border-[color-mix(in_srgb,var(--site-text)_15%,transparent)] px-5 py-3 text-sm font-black"
                                    style={partStyle(block, "resetBackButton")}
                                    onClick={() => setPasswordResetMode(null)}
                                >
                                    {richTextContent(block, "resetBackButtonLabel", "Zpět na přihlášení")}
                                </button>
                                <button
                                    {...editableTextPart("resetSubmitButton")}
                                    type="submit"
                                    className="rounded-full bg-[var(--site-primary)] px-5 py-3 text-sm font-black text-[var(--site-on-primary)] disabled:cursor-not-allowed disabled:bg-[color-mix(in_srgb,var(--site-text)_25%,transparent)]"
                                    style={partStyle(block, "resetSubmitButton")}
                                    disabled={passwordResetSubmitting || customerLoading}
                                >
                                    {passwordResetSubmitting ? message("ui.common.sending") : richTextContent(block, "resetSubmitButtonLabel", "Odeslat odkaz")}
                                </button>
                            </div>
                        </form>
                    ) : passwordResetMode === "confirm" ? (
                        <form className="mt-6 grid gap-4 rounded-3xl bg-[var(--site-surface-elevated)] p-4" onSubmit={submitPasswordResetConfirm}>
                            <div>
                                <h2 {...editableTextPart("newPasswordTitle")} className="text-xl font-black" style={partStyle(block, "newPasswordTitle")}>{richTextContent(block, "newPasswordTitle", "Nastavení nového hesla")}</h2>
                                <p {...editableTextPart("newPasswordText")} className="mt-2 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]" style={partStyle(block, "newPasswordText")}>
                                    {richTextContent(block, "newPasswordText", "Zadejte nové heslo alespoň na 8 znaků.")}
                                </p>
                            </div>
                            <label {...editablePart("newPasswordInput")} className="grid gap-2 text-sm font-bold" style={partStyle(block, "newPasswordInput")}>
                                {blockText(block, "newPasswordInput", "Nové heslo")}
                                <input
                                    className="rounded-2xl border border-[var(--site-border)] bg-[var(--site-surface)] px-4 py-3 text-base font-normal outline-none focus:border-[var(--site-primary)]"
                                    type="password"
                                    autoComplete="new-password"
                                    minLength={8}
                                    required
                                    value={passwordResetNewPassword}
                                    onChange={(event) => setPasswordResetNewPassword(event.target.value)}
                                />
                            </label>
                            <label {...editablePart("newPasswordConfirmationInput")} className="grid gap-2 text-sm font-bold" style={partStyle(block, "newPasswordConfirmationInput")}>
                                {blockText(block, "newPasswordConfirmationInput", "Heslo znovu")}
                                <input
                                    className="rounded-2xl border border-[var(--site-border)] bg-[var(--site-surface)] px-4 py-3 text-base font-normal outline-none focus:border-[var(--site-primary)]"
                                    type="password"
                                    autoComplete="new-password"
                                    minLength={8}
                                    required
                                    value={passwordResetNewPasswordConfirmation}
                                    onChange={(event) => setPasswordResetNewPasswordConfirmation(event.target.value)}
                                />
                            </label>
                            {passwordResetMessage ? <p className="rounded-2xl bg-[var(--site-accent-soft)] px-4 py-3 text-sm font-bold text-[var(--site-accent)] ring-1 ring-[var(--site-accent)]">{passwordResetMessage}</p> : null}
                            {passwordResetError ? <p className="rounded-2xl bg-[var(--site-primary-soft)] px-4 py-3 text-sm font-bold text-[var(--site-primary)] ring-1 ring-[var(--site-primary)]">{passwordResetError}</p> : null}
                            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                                <button
                                    {...editableTextPart("resetBackButton")}
                                    type="button"
                                    className="rounded-full border border-[color-mix(in_srgb,var(--site-text)_15%,transparent)] px-5 py-3 text-sm font-black"
                                    style={partStyle(block, "resetBackButton")}
                                    onClick={() => setPasswordResetMode(null)}
                                >
                                    {richTextContent(block, "resetBackButtonLabel", "Zpět na přihlášení")}
                                </button>
                                <button
                                    {...editableTextPart("newPasswordSubmitButton")}
                                    type="submit"
                                    className="rounded-full bg-[var(--site-primary)] px-5 py-3 text-sm font-black text-[var(--site-on-primary)] disabled:cursor-not-allowed disabled:bg-[color-mix(in_srgb,var(--site-text)_25%,transparent)]"
                                    style={partStyle(block, "newPasswordSubmitButton")}
                                    disabled={passwordResetSubmitting || customerLoading}
                                >
                                    {passwordResetSubmitting ? message("ui.common.saving") : richTextContent(block, "newPasswordSubmitButtonLabel", "Nastavit heslo")}
                                </button>
                            </div>
                        </form>
                    ) : (
                    <form className="mt-6 grid gap-4" onSubmit={submit}>
                        <label {...editablePart("emailInput")} className="grid gap-2 text-sm font-bold" style={partStyle(block, "emailInput")}>
                            {blockText(block, "emailInput", "E-mail")}
                            <input
                                className="border border-[var(--site-border)] px-4 py-3 text-base font-normal outline-none focus:border-[var(--site-primary)]"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                            />
                        </label>
                        <label {...editablePart("passwordInput")} className="grid gap-2 text-sm font-bold" style={partStyle(block, "passwordInput")}>
                            {blockText(block, "passwordInput", "Heslo")}
                            <input
                                className="border border-[var(--site-border)] px-4 py-3 text-base font-normal outline-none focus:border-[var(--site-primary)]"
                                type="password"
                                autoComplete={mode === "login" ? "current-password" : "new-password"}
                                minLength={mode === "register" ? 8 : undefined}
                                required
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                            />
                        </label>
                        {mode === "login" ? (
                            <button
                                {...editableTextPart("forgotPasswordButton")}
                                type="button"
                                className="w-fit text-sm font-black text-[var(--site-primary)] underline underline-offset-4"
                                style={partStyle(block, "forgotPasswordButton")}
                                onClick={() => {
                                    setPasswordResetEmail(email);
                                    setPasswordResetMode("request");
                                    setPasswordResetMessage(null);
                                    setPasswordResetError(null);
                                }}
                            >
                                {richTextContent(block, "forgotPasswordButtonLabel", "Zapomenuté heslo?")}
                            </button>
                        ) : null}
                        {mode === "register" ? (
                            <>
                                <label {...editablePart("passwordConfirmationInput")} className="grid gap-2 text-sm font-bold" style={partStyle(block, "passwordConfirmationInput")}>
                                    {blockText(block, "passwordConfirmationInput", "Heslo znovu")}
                                    <input
                                        className="border border-[var(--site-border)] px-4 py-3 text-base font-normal outline-none focus:border-[var(--site-primary)]"
                                        type="password"
                                        autoComplete="new-password"
                                        minLength={8}
                                        required
                                        value={passwordConfirmation}
                                        onChange={(event) => setPasswordConfirmation(event.target.value)}
                                    />
                                </label>
                                <label className="inline-flex items-start gap-3 text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_65%,transparent)]">
                                    <input
                                        type="checkbox"
                                        className={softCheckboxClass}
                                        checked={newsletterOptOut}
                                        onChange={(event) => setNewsletterOptOut(event.target.checked)}
                                    />
                                    <span {...editableTextPart("newsletterOptOutText")} style={partStyle(block, "newsletterOptOutText")}>{richTextContent(block, "newsletterOptOutText", "Nepřeji si odebírat newslettery.")}</span>
                                </label>
                            </>
                        ) : null}
                        {customerError ? (
                            <p className="border border-[var(--site-primary)] bg-[var(--site-primary-soft)] px-4 py-3 text-sm font-bold text-[var(--site-primary)]">
                                {customerError}
                            </p>
                        ) : null}
                        {registrationMessage ? (
                            <p className="border border-[var(--site-accent)] bg-[var(--site-accent-soft)] px-4 py-3 text-sm font-bold text-[var(--site-accent)]" role="status">
                                {registrationMessage}
                            </p>
                        ) : null}
                        <button
                            {...editableTextPart("submitButton")}
                            type="submit"
                            className="rounded-full bg-[var(--site-primary)] px-6 py-4 text-sm font-black text-[var(--site-on-primary)] disabled:cursor-not-allowed disabled:bg-[color-mix(in_srgb,var(--site-text)_25%,transparent)]"
                            style={partStyle(block, "submitButton")}
                            disabled={submitting || customerLoading}
                        >
                            {submitting || customerLoading ? message("ui.common.sending") : mode === "login" ? richTextContent(block, "submitLoginButtonLabel", "Přihlásit se") : richTextContent(block, "submitRegisterButtonLabel", "Registrovat se")}
                        </button>
                    </form>
                    )}
                </div>
            </div>
        </section>
    );
}

type ColumnsShowcaseVariant =
    | "alternatingRows"
    | "bentoGrid"
    | "comparison"
    | "editorialFashion"
    | "floatingProductCards"
    | "fourIcons"
    | "imageText"
    | "magazineCollage"
    | "stats"
    | "stickyStack"
    | "threeCards"
    | "timeline";

type ColumnsCard = {
    imagePart: string;
    imageUrl: string;
    index: number;
    label: string;
    statValue?: string;
    text: string;
    title: string;
    url?: string;
};

function columnsShowcaseVariant(block: ShopTemplateBlock): ColumnsShowcaseVariant {
    const value = blockOptionText(block, "columnsVariant");

    if (
        value === "alternatingRows"
        || value === "bentoGrid"
        || value === "comparison"
        || value === "editorialFashion"
        || value === "floatingProductCards"
        || value === "fourIcons"
        || value === "imageText"
        || value === "magazineCollage"
        || value === "stats"
        || value === "stickyStack"
        || value === "threeCards"
        || value === "timeline"
    ) {
        return value;
    }

    return "threeCards";
}

function columnsCard(block: ShopTemplateBlock, index: number): ColumnsCard {
    const imagePart = `card${index}Image`;

    return {
        imagePart,
        imageUrl: blockText(block, `${imagePart}Url`, ""),
        index,
        label: blockText(block, `card${index}Label`, ""),
        statValue: blockText(block, `stat${index}Value`, ""),
        text: blockText(block, `card${index}Text`, ""),
        title: blockText(block, `card${index}Title`, ""),
        url: functionalStorefrontHref(blockText(block, `card${index}Url`, "")),
    };
}

function columnsCards(block: ShopTemplateBlock, count: number): ColumnsCard[] {
    return Array.from({ length: count }, (_, index) => columnsCard(block, index + 1));
}

function slotCard(block: ShopTemplateBlock, index: number): ColumnsCard {
    return {
        imagePart: "image",
        imageUrl: blockText(block, "imageUrl", ""),
        index,
        label: blockText(block, "label", blockText(block, "buttonLabel", "")),
        statValue: blockText(block, "statValue", ""),
        text: blockText(block, "text", ""),
        title: blockText(block, "title", ""),
        url: functionalStorefrontHref(blockText(block, "href", blockText(block, "url", ""))),
    };
}

function materializedRenderer(block: ShopTemplateBlock | null | undefined): string {
    return blockOptionText(block, "materializedRenderer");
}

function SlotItemsWithFallback({
    block,
    className,
    emptyClassName,
    itemClassName,
    children,
    renderItem,
}: {
    block: ShopTemplateBlock;
    className: string;
    emptyClassName?: string;
    itemClassName?: SlotItemClassName;
    children: ReactNode;
    renderItem: (params: {
        card: ColumnsCard;
        childBlock: ShopTemplateBlock;
        index: number;
        itemPart: string;
    }) => ReactNode;
}) {
    const editorMode = useContext(ShopEditorModeContext);
    const items = slotChildren(block, "items");
    const itemsReplaceFallback = useSlotReplacesFallback(block, "items");

    if (items.length > 0 || itemsReplaceFallback) {
        return (
            <SlotBlockGroup
                block={block}
                slotKey="items"
                className={className}
                itemClassName={itemClassName}
                renderItem={({ childBlock, index, itemPart }) => (
                    materializedRenderer(childBlock)
                        ? <AtomicBlock block={childBlock} partPrefix={itemPart} slotParentType={block.type} />
                        : renderItem({
                            card: slotCard(childBlock, index + 1),
                            childBlock,
                            index,
                            itemPart,
                        })
                )}
            />
        );
    }

    return (
        <>
            {children}
            {editorMode ? (
                <SlotBlockGroup
                    block={block}
                    slotKey="items"
                    className={emptyClassName ?? "mt-6 grid gap-3"}
                    itemClassName={itemClassName}
                />
            ) : null}
        </>
    );
}

function SlotCardInnerContent({
    block,
    itemPart,
    className = "mt-4 grid gap-3",
}: {
    block: ShopTemplateBlock;
    itemPart: string;
    className?: string;
}) {
    return (
        <SlotBlockGroup
            block={block}
            slotKey="content"
            partPrefix={itemPart}
            className={className}
        />
    );
}

function CardContentSlot({
    block,
    children,
    className = "grid gap-3",
    itemPart,
    renderItem,
}: {
    block: ShopTemplateBlock | undefined;
    children: ReactNode;
    className?: string;
    itemPart: string | undefined;
    renderItem: SlotRenderItem;
}) {
    const contentItems = slotChildren(block, "content");
    const contentReplacesFallback = useSlotReplacesFallback(block, "content");

    if (!block || (contentItems.length === 0 && !contentReplacesFallback)) {
        return <>{keyedSlotRenderedItem(children, itemPart ? `${itemPart}:content` : "card-content")}</>;
    }

    return (
        <SlotBlockGroup
            block={block}
            slotKey="content"
            partPrefix={itemPart}
            className={className}
            renderItem={renderItem}
        />
    );
}

type CardContentTag = "code" | "div" | "h2" | "h3" | "h4" | "p" | "span";

function StyledCardContentElement({
    children,
    className,
    part,
    style,
    tag,
}: {
    children: ReactNode;
    className: string;
    part: string;
    style?: CSSProperties;
    tag: CardContentTag;
}) {
    const renderedChildren = keyedSlotRenderedItem(children, `styled-card-content:${part}`);
    const props = {
        ...editableTextPart(part),
        className,
        style,
    };

    if (tag === "code") {
        return <code {...props}>{renderedChildren}</code>;
    }

    if (tag === "div") {
        return <div {...props}>{renderedChildren}</div>;
    }

    if (tag === "h2") {
        return <h2 {...props}>{renderedChildren}</h2>;
    }

    if (tag === "h4") {
        return <h4 {...props}>{renderedChildren}</h4>;
    }

    if (tag === "p") {
        return <p {...props}>{renderedChildren}</p>;
    }

    if (tag === "span") {
        return <span {...props}>{renderedChildren}</span>;
    }

    return <h3 {...props}>{renderedChildren}</h3>;
}

function StyledCardContentSlot({
    block,
    buttonClassName,
    children,
    className = "grid gap-0",
    itemPart,
    statClassName = "text-4xl font-semibold text-[var(--site-text)]",
    statTag = "div",
    textClassName = "mt-3 text-base font-light leading-relaxed text-[var(--site-muted)]",
    textTag = "p",
    titleClassName = "text-2xl font-semibold text-[var(--site-text)]",
    titleTag = "h3",
}: {
    block: ShopTemplateBlock | undefined;
    buttonClassName?: string;
    children: ReactNode;
    className?: string;
    itemPart: string | undefined;
    statClassName?: string;
    statTag?: CardContentTag;
    textClassName?: string;
    textTag?: CardContentTag;
    titleClassName?: string;
    titleTag?: CardContentTag;
}) {
    const renderItem: SlotRenderItem = ({ childBlock, index, itemPart }) => {
        const role = blockOptionText(childBlock, "slotRole");
        const contentKey = itemPart || `styled-card-content:${childBlock.type}:${index}`;

        if (role === "statValue") {
            return (
                <StyledCardContentElement
                    key={contentKey}
                    className={statClassName}
                    part="text"
                    style={partStyle(childBlock, "text")}
                    tag={statTag}
                >
                    {richTextContent(childBlock, "text", "")}
                </StyledCardContentElement>
            );
        }

        if (role === "title" || childBlock.type === "heading") {
            return (
                <StyledCardContentElement
                    key={contentKey}
                    className={titleClassName}
                    part="title"
                    style={partStyle(childBlock, "title")}
                    tag={titleTag}
                >
                    {richTextContent(childBlock, "text", blockText(childBlock, "title", ""))}
                </StyledCardContentElement>
            );
        }

        if (role === "text" || childBlock.type === "paragraph" || childBlock.type === "text") {
            return (
                <StyledCardContentElement
                    key={contentKey}
                    className={textClassName}
                    part="text"
                    style={partStyle(childBlock, "text")}
                    tag={textTag}
                >
                    {richTextContent(childBlock, "text", "")}
                </StyledCardContentElement>
            );
        }

        if (role === "button" || childBlock.type === "button") {
            return (
                <a
                    key={contentKey}
                    {...editablePart("primaryButton")}
href={functionalStorefrontHref(blockText(childBlock, "href", ""))}
                    className={buttonClassName ?? "mt-6 inline-flex rounded-full bg-[var(--site-text)] px-6 py-3 text-sm font-medium text-[var(--site-on-inverse)]"}
                    style={partStyle(childBlock, "primaryButton")}
                >
                    <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
                </a>
            );
        }

        return <AtomicBlock key={contentKey} block={childBlock} partPrefix={itemPart} />;
    };

    return (
        <CardContentSlot block={block} itemPart={itemPart} className={className} renderItem={renderItem}>
            {children}
        </CardContentSlot>
    );
}

function ColumnsSectionShell({
    block,
    children,
    className = "bg-[var(--site-surface)] px-5 py-[120px] sm:px-6",
    innerClassName = "mx-auto max-w-[var(--site-container)]",
}: {
    block: ShopTemplateBlock;
    children: ReactNode;
    className?: string;
    innerClassName?: string;
}) {
    return (
        <section
            {...blockInteractionAttributes(block)}
            className={classNames("border-y border-[var(--site-border)]", className)}
            style={mergeStyles(blockStyle(block), interactionStyle(block))}
        >
            <div className={innerClassName}>
                {children}
            </div>
        </section>
    );
}

function isColumnsOrientationLabel(value: string): boolean {
    return /^columns\s+\d+$/i.test(value.trim());
}

function ColumnsEyebrow({ block, centered }: { block: ShopTemplateBlock; centered?: boolean }) {
    if (blockOptionText(block, "showSectionLabel") !== "true") {
        return null;
    }

    const rawBadge = blockText(block, "badge", "");
    const rawEyebrow = blockText(block, "eyebrow", "");
    const badge = isColumnsOrientationLabel(rawBadge) ? "" : rawBadge;
    const eyebrowValue = isColumnsOrientationLabel(rawEyebrow) ? "" : rawEyebrow;
    const eyebrow = badge || eyebrowValue;
    const part = badge ? "badge" : "eyebrow";

    if (!eyebrow) {
        return null;
    }

    return (
        <span
            {...editableTextPart(part)}
            className={classNames("mb-12 block text-xs font-semibold uppercase text-[var(--site-muted)]", centered && "text-center")}
            style={partStyle(block, part)}
        >
            {richTextContent(block, part, eyebrow)}
        </span>
    );
}

function ColumnsImage({
    block,
    part = "image",
    className,
    imgClassName = "object-cover",
}: {
    block: ShopTemplateBlock;
    part?: string;
    className?: string;
    imgClassName?: string;
}) {
    const prefix = part === "image" ? "image" : part;
    const image = blockText(block, `${prefix}Url`, "");

    if (!image) {
        return <div className={classNames("bg-[var(--site-surface)]", className)} />;
    }

    return (
        <div
            {...imageHoverFrameAttributes(block, part, "scale-sm")}
            className={classNames("overflow-hidden bg-[var(--site-surface)]", className)}
            style={imageFrameStyle(block, part)}
        >
            <img
                {...editablePart(part)}
                data-shop-image-hover-target="true"
                src={image}
                alt={blockText(block, `${prefix}Alt`, blockText(block, "alt", ""))}
                title={blockText(block, `${prefix}Title`, "") || undefined}
                className={classNames("h-full w-full", imgClassName)}
                style={imageInteractivePartStyle(block, part, "scale-sm")}
            />
        </div>
    );
}

function ColumnsButtons({ block }: { block: ShopTemplateBlock }) {
    const primaryLabel = blockText(block, "primaryLabel", "");
    const secondaryLabel = blockText(block, "secondaryLabel", "");
    const renderActionItem: SlotRenderItem = ({ childBlock, index, itemPart }) => {
        if (childBlock.type !== "button") {
            return <AtomicBlock block={childBlock} partPrefix={itemPart} />;
        }

        const primary = index === 0;

        return (
            <a
                {...editablePart("primaryButton")}
href={functionalStorefrontHref(blockText(childBlock, "href", ""))}
                className={classNames(
                    "inline-flex min-h-14 items-center justify-center rounded-full px-8 text-sm font-semibold transition",
                    primary
                        ? "bg-[var(--site-primary)] text-[var(--site-on-primary)] shadow-[0_10px_24px_var(--site-shadow-color)] hover:bg-[var(--site-primary)]/90"
                        : "border border-[var(--site-border)] bg-[var(--site-surface)] text-[var(--site-text)] hover:bg-[var(--site-surface)]",
                )}
                style={partStyle(childBlock, "primaryButton")}
            >
                <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
            </a>
        );
    };

    return (
        <SlotWithFallback
            block={block}
            slotKey="actions"
            className="mt-10 flex flex-col gap-4 sm:flex-row"
            emptyClassName="mt-4 flex flex-col gap-3 sm:flex-row"
            itemClassName="w-fit max-w-full"
            renderItem={renderActionItem}
        >
            {primaryLabel || secondaryLabel ? (
                <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                    {primaryLabel ? (
                        <a {...editablePart("primaryButton")} href={functionalStorefrontHref(blockText(block, "primaryUrl", ""))} className="inline-flex min-h-14 items-center justify-center rounded-full bg-[var(--site-primary)] px-8 text-sm font-semibold text-[var(--site-on-primary)] shadow-[0_10px_24px_var(--site-shadow-color)] transition hover:bg-[var(--site-primary)]/90" style={partStyle(block, "primaryButton")}>
                            <span {...editableTextPart("primaryButton")}>{richTextContent(block, "primaryLabel", primaryLabel)}</span>
                        </a>
                    ) : null}
                    {secondaryLabel ? (
                        <a {...editablePart("secondaryButton")} href={functionalStorefrontHref(blockText(block, "secondaryUrl", ""))} className="inline-flex min-h-14 items-center justify-center rounded-full border border-[var(--site-border)] bg-[var(--site-surface)] px-8 text-sm font-semibold text-[var(--site-text)] transition hover:bg-[var(--site-surface)]" style={partStyle(block, "secondaryButton")}>
                            <span {...editableTextPart("secondaryButton")}>{richTextContent(block, "secondaryLabel", secondaryLabel)}</span>
                        </a>
                    ) : null}
                </div>
            ) : null}
        </SlotWithFallback>
    );
}

function ShowcaseCopySlot({
    block,
    slotClassName,
    emptyClassName,
    headingClassName,
    paragraphClassName,
    children,
}: {
    block: ShopTemplateBlock;
    slotClassName: string;
    emptyClassName?: string;
    headingClassName: string;
    paragraphClassName?: string;
    children: ReactNode;
}) {
    const renderCopyItem: SlotRenderItem = ({ childBlock, itemPart }) => {
        if (childBlock.type === "heading") {
            const eyebrow = blockText(childBlock, "eyebrow", "");

            return (
                <>
                    {eyebrow ? (
                        <p {...editableTextPart("eyebrow")} className="mb-4 text-sm font-semibold uppercase text-[var(--site-muted)]" style={partStyle(childBlock, "eyebrow")}>
                            {richTextContent(childBlock, "eyebrow", eyebrow)}
                        </p>
                    ) : null}
                    <h2 {...editableTextPart("title")} className={headingClassName} style={partStyle(childBlock, "title")}>
                        {richTextContent(childBlock, "text", blockText(childBlock, "title", ""))}
                    </h2>
                </>
            );
        }

        if (childBlock.type === "paragraph" || childBlock.type === "text") {
            return (
                <p {...editableTextPart("text")} className={paragraphClassName ?? "text-base leading-relaxed text-[var(--site-muted)]"} style={partStyle(childBlock, "text")}>
                    {richTextContent(childBlock, "text", "")}
                </p>
            );
        }

        return <AtomicBlock block={childBlock} partPrefix={itemPart} />;
    };

    return (
        <SlotWithFallback
            block={block}
            slotKey="content"
            className={slotClassName}
            emptyClassName={emptyClassName ?? slotClassName}
            renderItem={renderCopyItem}
        >
            {children}
        </SlotWithFallback>
    );
}

function ShowcaseMediaSlot({
    block,
    className,
    children,
}: {
    block: ShopTemplateBlock;
    className: string;
    children: ReactNode;
}) {
    return (
        <SlotWithFallback
            block={block}
            slotKey="media"
            className="grid"
            emptyClassName="grid"
            renderItem={({ childBlock, itemPart }) => (
                childBlock.type === "image" || childBlock.type === "card"
                    ? <ColumnsImage block={childBlock} className={className} />
                    : <AtomicBlock block={childBlock} partPrefix={itemPart} />
            )}
        >
            {children}
        </SlotWithFallback>
    );
}

function ColumnsThreeCards({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 3);

    const renderCard = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <article key={card.index} className="group flex flex-col gap-6">
            {childBlock ? (
                <ColumnsImage block={childBlock} className="aspect-[4/5] rounded-[20px]" />
            ) : (
                <ColumnsImage block={block} part={card.imagePart} className="aspect-[4/5] rounded-[20px]" />
            )}
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                titleClassName="text-2xl font-semibold leading-tight text-[var(--site-text)]"
                textClassName="mt-3 text-base font-light leading-relaxed text-[var(--site-muted)]"
                buttonClassName="mt-6 inline-flex border-b border-[var(--site-text)] pb-1 text-sm font-medium text-[var(--site-text)] transition-colors hover:border-[var(--site-primary)] hover:text-[var(--site-primary)]"
            >
                <div>
                    <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="text-2xl font-semibold leading-tight text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}
                    </h3>
                    <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className="mt-3 text-base font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}
                    </p>
                    {card.label ? (
                        <a {...editablePart(childBlock ? "primaryButton" : `card${card.index}Label`)} href={card.url} className="mt-6 inline-flex border-b border-[var(--site-text)] pb-1 text-sm font-medium text-[var(--site-text)] transition-colors hover:border-[var(--site-primary)] hover:text-[var(--site-primary)]" style={partStyle(childBlock ?? block, childBlock ? "primaryButton" : `card${card.index}Label`)}>
                            <span {...editableTextPart(childBlock ? "primaryButton" : `card${card.index}Label`)}>
                                {richTextContent(childBlock ?? block, childBlock ? "label" : `card${card.index}Label`, card.label)}
                            </span>
                        </a>
                    ) : null}
                </div>
            </StyledCardContentSlot>
        </article>
    );

    return (
        <ColumnsSectionShell block={block}>
            <ColumnsEyebrow block={block} />
            <SlotItemsWithFallback
                block={block}
                className="grid grid-cols-1 gap-8 md:grid-cols-3 lg:gap-12"
                renderItem={({ card, childBlock, itemPart }) => renderCard(card, childBlock, itemPart)}
            >
                <div className="grid grid-cols-1 gap-8 md:grid-cols-3 lg:gap-12">
                    {cards.map((card) => renderCard(card))}
                </div>
            </SlotItemsWithFallback>
        </ColumnsSectionShell>
    );
}

function ColumnsStickyStack({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 3);

    const renderCard = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <article key={card.index} className="flex flex-col items-center gap-6 rounded-[24px] p-4 transition-colors duration-300 hover:bg-[var(--site-surface)] sm:flex-row">
            {childBlock ? (
                <ColumnsImage block={childBlock} className="h-40 w-full shrink-0 rounded-[16px] sm:w-40" />
            ) : (
                <ColumnsImage block={block} part={card.imagePart} className="h-40 w-full shrink-0 rounded-[16px] sm:w-40" />
            )}
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                titleClassName="text-2xl font-semibold leading-tight text-[var(--site-text)]"
                textClassName="mt-2 text-base font-light leading-relaxed text-[var(--site-muted)]"
            >
                <div>
                    <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="text-2xl font-semibold leading-tight text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}
                    </h3>
                    <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className="mt-2 text-base font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}
                    </p>
                </div>
            </StyledCardContentSlot>
        </article>
    );

    return (
        <ColumnsSectionShell block={block}>
            <ColumnsEyebrow block={block} />
            <div className="flex flex-col gap-16 lg:flex-row lg:gap-24">
                <div className="lg:w-2/5">
                    <div className="lg:sticky lg:top-24">
                        <ShowcaseCopySlot
                            block={block}
                            slotClassName="grid gap-0"
                            headingClassName="text-4xl font-semibold leading-[1.1] text-[var(--site-text)] md:text-5xl lg:text-[56px]"
                            paragraphClassName="mt-6 max-w-md text-lg font-light leading-relaxed text-[var(--site-muted)]"
                        >
                            <h2 {...editableTextPart("title")} className="text-4xl font-semibold leading-[1.1] text-[var(--site-text)] md:text-5xl lg:text-[56px]" style={partStyle(block, "title")}>
                                {richTextContent(block, "title", "Crafted for the modern aesthete.")}
                            </h2>
                            <p {...editableTextPart("text")} className="mt-6 max-w-md text-lg font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(block, "text")}>
                                {richTextContent(block, "text", "Our design philosophy centers on reducing noise, prioritizing quality, and celebrating natural textures.")}
                            </p>
                        </ShowcaseCopySlot>
                        <ColumnsButtons block={block} />
                    </div>
                </div>
                <SlotItemsWithFallback
                    block={block}
                    className="flex flex-col gap-8 lg:w-3/5"
                    renderItem={({ card, childBlock, itemPart }) => renderCard(card, childBlock, itemPart)}
                >
                    <div className="flex flex-col gap-8 lg:w-3/5">
                        {cards.map((card) => renderCard(card))}
                    </div>
                </SlotItemsWithFallback>
            </div>
        </ColumnsSectionShell>
    );
}

function ColumnsStats({ block }: { block: ShopTemplateBlock }) {
    const stats = [1, 2, 3].map((index) => ({
        index,
        label: blockText(block, `stat${index}Label`, ""),
        text: blockText(block, `stat${index}Text`, ""),
        value: blockText(block, `stat${index}Value`, ""),
    }));

    const renderStat = (stat: { index: number; label: string; text: string; value: string }, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <StyledCardContentSlot
            key={stat.index}
            block={childBlock}
            itemPart={itemPart}
            statClassName="text-[80px] font-light leading-none text-[var(--site-text)] lg:text-[100px]"
            titleClassName="mt-6 text-lg font-medium text-[var(--site-text)]"
            titleTag="p"
            textClassName="mx-auto mt-2 max-w-xs text-base font-light text-[var(--site-muted)]"
        >
            <div key={stat.index}>
                <div {...editableTextPart(childBlock ? "statValue" : `stat${stat.index}Value`)} className="text-[80px] font-light leading-none text-[var(--site-text)] lg:text-[100px]" style={partStyle(childBlock ?? block, childBlock ? "statValue" : `stat${stat.index}Value`)}>
                    {richTextContent(childBlock ?? block, childBlock ? "statValue" : `stat${stat.index}Value`, stat.value)}
                </div>
                <p {...editableTextPart(childBlock ? "title" : `stat${stat.index}Label`)} className="mt-6 text-lg font-medium text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `stat${stat.index}Label`)}>
                    {richTextContent(childBlock ?? block, childBlock ? "title" : `stat${stat.index}Label`, stat.label)}
                </p>
                <p {...editableTextPart(childBlock ? "text" : `stat${stat.index}Text`)} className="mx-auto mt-2 max-w-xs text-base font-light text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `stat${stat.index}Text`)}>
                    {richTextContent(childBlock ?? block, childBlock ? "text" : `stat${stat.index}Text`, stat.text)}
                </p>
            </div>
        </StyledCardContentSlot>
    );

    return (
        <ColumnsSectionShell block={block} className="bg-[var(--site-surface)] px-5 py-[140px] text-center sm:px-6">
            <ColumnsEyebrow block={block} centered />
            <ShowcaseCopySlot
                block={block}
                slotClassName="grid justify-items-center gap-0 text-center"
                headingClassName="mx-auto max-w-3xl text-4xl font-semibold leading-tight text-[var(--site-text)] md:text-5xl lg:text-[56px]"
            >
                <h2 {...editableTextPart("title")} className="mx-auto max-w-3xl text-4xl font-semibold leading-tight text-[var(--site-text)] md:text-5xl lg:text-[56px]" style={partStyle(block, "title")}>
                    {richTextContent(block, "title", "Uncompromising quality, by the numbers.")}
                </h2>
            </ShowcaseCopySlot>
            <SlotItemsWithFallback
                block={block}
                className="mt-24 grid grid-cols-1 gap-16 border-t border-[var(--site-border)] pt-16 md:grid-cols-3 lg:gap-8"
                renderItem={({ card, childBlock, itemPart }) => renderStat({
                    index: card.index,
                    label: card.title,
                    text: card.text,
                    value: card.statValue ?? "",
                }, childBlock, itemPart)}
            >
                <div className="mt-24 grid grid-cols-1 gap-16 border-t border-[var(--site-border)] pt-16 md:grid-cols-3 lg:gap-8">
                    {stats.map((stat) => renderStat(stat))}
                </div>
            </SlotItemsWithFallback>
        </ColumnsSectionShell>
    );
}

function ColumnsImageText({ block }: { block: ShopTemplateBlock }) {
    const items = slotChildren(block, "items");
    const itemsReplaceFallback = useSlotReplacesFallback(block, "items");
    const renderTextColumn = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => {
        const secondaryLabel = blockText(childBlock ?? block, childBlock ? "secondaryLabel" : "secondaryLabel", "");
        const secondaryHref = functionalStorefrontHref(blockText(childBlock ?? block, childBlock ? "secondaryHref" : "secondaryUrl", ""));

        return (
            <div className="flex flex-col items-start">
                <p {...editableTextPart("eyebrow")} className="mb-6 text-sm font-semibold uppercase text-[var(--site-muted)]" style={partStyle(childBlock ?? block, "eyebrow")}>
                    {richTextContent(childBlock ?? block, "eyebrow", "The process")}
                </p>
                <StyledCardContentSlot
                    block={childBlock}
                    itemPart={itemPart}
                    titleTag="h2"
                    titleClassName="text-4xl font-semibold leading-[1.1] text-[var(--site-text)] md:text-5xl lg:text-[56px]"
                    textClassName="mt-8 text-lg font-light leading-relaxed text-[var(--site-muted)]"
                    buttonClassName="mt-10 inline-flex min-h-14 items-center justify-center rounded-full bg-[var(--site-primary)] px-8 text-sm font-semibold text-[var(--site-on-primary)] shadow-[0_10px_24px_var(--site-shadow-color)] transition hover:bg-[var(--site-primary)]/90"
                >
                    <>
                        <h2 {...editableTextPart("title")} className="text-4xl font-semibold leading-[1.1] text-[var(--site-text)] md:text-5xl lg:text-[56px]" style={partStyle(childBlock ?? block, "title")}>
                            {richTextContent(childBlock ?? block, "title", card.title)}
                        </h2>
                        <p {...editableTextPart("text")} className="mt-8 text-lg font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, "text")}>
                            {richTextContent(childBlock ?? block, "text", card.text)}
                        </p>
                        {card.label || secondaryLabel ? (
                            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                                {card.label ? (
                        <a {...editablePart(childBlock ? "primaryButton" : "primaryButton")} href={card.url} className="inline-flex min-h-14 items-center justify-center rounded-full bg-[var(--site-primary)] px-8 text-sm font-semibold text-[var(--site-on-primary)] shadow-[0_10px_24px_var(--site-shadow-color)] transition hover:bg-[var(--site-primary)]/90" style={partStyle(childBlock ?? block, childBlock ? "primaryButton" : "primaryButton")}>
                                        <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock ?? block, childBlock ? "label" : "primaryLabel", card.label)}</span>
                                    </a>
                                ) : null}
                                {secondaryLabel ? (
                                    <a {...editablePart(childBlock ? "secondaryButton" : "secondaryButton")} href={secondaryHref} className="inline-flex min-h-14 items-center justify-center rounded-full border border-[var(--site-border)] bg-[var(--site-surface)] px-8 text-sm font-semibold text-[var(--site-text)] transition hover:bg-[var(--site-surface)]" style={partStyle(childBlock ?? block, childBlock ? "secondaryButton" : "secondaryButton")}>
                                        <span {...editableTextPart("secondaryButton")}>{richTextContent(childBlock ?? block, childBlock ? "secondaryLabel" : "secondaryLabel", secondaryLabel)}</span>
                                    </a>
                                ) : null}
                            </div>
                        ) : null}
                    </>
                </StyledCardContentSlot>
            </div>
        );
    };

    if (items.length > 0 || itemsReplaceFallback) {
        return (
            <ColumnsSectionShell block={block}>
                <ColumnsEyebrow block={block} />
                <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2 lg:gap-24" data-shop-slot-container="items" data-shop-slot-item-count={items.length}>
                    {items.map((childBlock, index) => {
                        const itemPart = slotPart("items", index);

                        return (
                            <div key={`${childBlock.type}:${index}`} data-shop-block-part={itemPart} data-shop-outline-disabled={siteBuilderBlockDelegatesOutlineToColumn(childBlock) ? "true" : undefined} style={childBlockWrapperStyle(childBlock)}>
                                {index === 0 ? (
                                    <ColumnsImage block={childBlock} className="aspect-[4/5] rounded-[24px] shadow-[0_24px_48px_-12px_var(--site-shadow-color)] lg:aspect-[3/4]" />
                                ) : (
                                    renderTextColumn(slotCard(childBlock, index + 1), childBlock, itemPart)
                                )}
                            </div>
                        );
                    })}
                </div>
            </ColumnsSectionShell>
        );
    }

    return (
        <ColumnsSectionShell block={block}>
            <ColumnsEyebrow block={block} />
            <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2 lg:gap-24">
                <ColumnsImage block={block} className="aspect-[4/5] rounded-[24px] shadow-[0_24px_48px_-12px_var(--site-shadow-color)] lg:aspect-[3/4]" />
                <SlotItemsWithFallback
                    block={block}
                    className="grid gap-6"
                    renderItem={({ card, childBlock, itemPart }) => renderTextColumn(card, childBlock, itemPart)}
                >
                    {renderTextColumn({
                        imagePart: "image",
                        imageUrl: "",
                        index: 1,
                        label: blockText(block, "primaryLabel", ""),
                        text: blockText(block, "text", ""),
                        title: blockText(block, "title", "Redefining elegance through simplicity."),
                        url: functionalStorefrontHref(blockText(block, "primaryUrl", "")),
                    })}
                </SlotItemsWithFallback>
            </div>
        </ColumnsSectionShell>
    );
}

function ColumnsFourIcons({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 4);
    const icons: IconName[] = ["check", "creditCard", "shield", "rotate"];

    const renderCard = (card: ColumnsCard, itemIndex: number, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <article key={card.index}>
            <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--site-surface)] text-[var(--site-text)]">
                <Icon name={icons[itemIndex] ?? "check"} className="h-6 w-6" />
            </div>
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                titleClassName="text-xl font-semibold leading-tight text-[var(--site-text)]"
                textClassName="mt-3 text-base font-light leading-relaxed text-[var(--site-muted)]"
            >
                <>
                    <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="text-xl font-semibold leading-tight text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}
                    </h3>
                    <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className="mt-3 text-base font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}
                    </p>
                </>
            </StyledCardContentSlot>
        </article>
    );

    return (
        <ColumnsSectionShell block={block}>
            <ColumnsEyebrow block={block} />
            <div className="mb-16 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
                <ShowcaseCopySlot
                    block={block}
                    slotClassName="grid max-w-xl gap-0"
                    headingClassName="max-w-xl text-4xl font-semibold leading-[1.1] text-[var(--site-text)] lg:text-[48px]"
                >
                    <h2 {...editableTextPart("title")} className="max-w-xl text-4xl font-semibold leading-[1.1] text-[var(--site-text)] lg:text-[48px]" style={partStyle(block, "title")}>
                        {richTextContent(block, "title", "Everything you need for a complete wardrobe.")}
                    </h2>
                </ShowcaseCopySlot>
                <SlotWithFallback
                    block={block}
                    slotKey="actions"
                    className="flex flex-wrap gap-3"
                    emptyClassName="flex flex-wrap gap-3"
                    itemClassName="w-fit max-w-full"
                    renderItem={({ childBlock }) => (
                        <a {...editablePart("primaryButton")} href={functionalStorefrontHref(blockText(childBlock, "href", ""))} className="border-b border-[var(--site-text)] pb-1 text-sm font-medium text-[var(--site-text)] transition-colors hover:border-[var(--site-primary)] hover:text-[var(--site-primary)]" style={partStyle(childBlock, "primaryButton")}>
                            <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
                        </a>
                    )}
                >
                    {blockText(block, "primaryLabel", "") ? (
                        <a {...editablePart("primaryButton")} href={functionalStorefrontHref(blockText(block, "primaryUrl", ""))} className="border-b border-[var(--site-text)] pb-1 text-sm font-medium text-[var(--site-text)] transition-colors hover:border-[var(--site-primary)] hover:text-[var(--site-primary)]" style={partStyle(block, "primaryButton")}>
                            <span {...editableTextPart("primaryButton")}>{richTextContent(block, "primaryLabel", "View all features")}</span>
                        </a>
                    ) : null}
                </SlotWithFallback>
            </div>
            <SlotItemsWithFallback
                block={block}
                className="grid grid-cols-1 gap-8 border-t border-[var(--site-border)] pt-12 sm:grid-cols-2 lg:grid-cols-4 lg:gap-12"
                renderItem={({ card, childBlock, index, itemPart }) => renderCard(card, index, childBlock, itemPart)}
            >
                <div className="grid grid-cols-1 gap-8 border-t border-[var(--site-border)] pt-12 sm:grid-cols-2 lg:grid-cols-4 lg:gap-12">
                    {cards.map((card, itemIndex) => renderCard(card, itemIndex))}
                </div>
            </SlotItemsWithFallback>
        </ColumnsSectionShell>
    );
}

function ColumnsAlternatingRows({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 2);

    const renderRow = (card: ColumnsCard, itemIndex: number, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <div key={card.index} className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2 lg:gap-24">
            {childBlock ? (
                <ColumnsImage block={childBlock} className={classNames("aspect-square rounded-[24px] shadow-lg", itemIndex === 1 && "lg:order-2")} />
            ) : (
                <ColumnsImage block={block} part={card.imagePart} className={classNames("aspect-square rounded-[24px] shadow-lg", itemIndex === 1 && "lg:order-2")} />
            )}
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                className={classNames("grid gap-0", itemIndex === 1 && "lg:order-1")}
                titleClassName="text-3xl font-semibold leading-tight text-[var(--site-text)] lg:text-4xl"
                textClassName="mb-8 mt-6 text-lg font-light leading-relaxed text-[var(--site-muted)]"
                buttonClassName="inline-flex rounded-full border border-[var(--site-border)] px-6 py-3 font-medium text-[var(--site-text)] transition-colors hover:bg-[var(--site-surface)]"
            >
                <div className={classNames(itemIndex === 1 && "lg:order-1")}>
                    <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="text-3xl font-semibold leading-tight text-[var(--site-text)] lg:text-4xl" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}
                    </h3>
                    <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className="mb-8 mt-6 text-lg font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}
                    </p>
                    {card.label ? (
                        <a {...editablePart(childBlock ? "primaryButton" : `card${card.index}Label`)} href={card.url} className="inline-flex rounded-full border border-[var(--site-border)] px-6 py-3 font-medium text-[var(--site-text)] transition-colors hover:bg-[var(--site-surface)]" style={partStyle(childBlock ?? block, childBlock ? "primaryButton" : `card${card.index}Label`)}>
                            <span {...editableTextPart(childBlock ? "primaryButton" : `card${card.index}Label`)}>{richTextContent(childBlock ?? block, childBlock ? "label" : `card${card.index}Label`, card.label)}</span>
                        </a>
                    ) : null}
                </div>
            </StyledCardContentSlot>
        </div>
    );

    return (
        <ColumnsSectionShell block={block}>
            <ColumnsEyebrow block={block} />
            <SlotItemsWithFallback
                block={block}
                className="space-y-32"
                renderItem={({ card, childBlock, index, itemPart }) => renderRow(card, index, childBlock, itemPart)}
            >
                <div className="space-y-32">
                    {cards.map((card, itemIndex) => renderRow(card, itemIndex))}
                </div>
            </SlotItemsWithFallback>
        </ColumnsSectionShell>
    );
}

function ColumnsBentoGrid({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 3);
    const bentoItemClassName = ({ index }: { index: number }) => (
        index === 0 ? "md:col-span-2 md:row-span-2" : undefined
    );
    const renderBentoCard = (card: ColumnsCard, index: number, childBlock?: ShopTemplateBlock, itemPart?: string) => {
        if (index === 0) {
            return (
                <article key={card.index} className="group relative flex h-full min-h-[420px] flex-col overflow-hidden rounded-[32px] bg-[var(--site-surface)] p-10 md:min-h-0">
                    <StyledCardContentSlot
                        block={childBlock}
                        itemPart={itemPart}
                        className="relative z-10 grid max-w-md gap-0"
                        titleClassName="text-4xl font-semibold leading-tight text-[var(--site-text)]"
                        textClassName="mt-4 text-lg font-light text-[var(--site-muted)]"
                        buttonClassName="mt-8 inline-flex rounded-full bg-[var(--site-text)] px-6 py-3 font-medium text-[var(--site-on-inverse)]"
                    >
                        <div className="relative z-10 max-w-md">
                            <h3 {...editableTextPart(childBlock ? "title" : "card1Title")} className="text-4xl font-semibold leading-tight text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : "card1Title")}>
                                {richTextContent(childBlock ?? block, childBlock ? "title" : "card1Title", card.title)}
                            </h3>
                            <p {...editableTextPart(childBlock ? "text" : "card1Text")} className="mt-4 text-lg font-light text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : "card1Text")}>
                                {richTextContent(childBlock ?? block, childBlock ? "text" : "card1Text", card.text)}
                            </p>
                            {card.label ? (
                                <a {...editablePart(childBlock ? "primaryButton" : "card1Label")} href={card.url} className="mt-8 inline-flex rounded-full bg-[var(--site-text)] px-6 py-3 font-medium text-[var(--site-on-inverse)]" style={partStyle(childBlock ?? block, childBlock ? "primaryButton" : "card1Label")}>
                                    <span {...editableTextPart(childBlock ? "primaryButton" : "card1Label")}>{richTextContent(childBlock ?? block, childBlock ? "label" : "card1Label", card.label)}</span>
                                </a>
                            ) : null}
                        </div>
                    </StyledCardContentSlot>
                    {childBlock ? (
                        <ColumnsImage block={childBlock} className="absolute inset-0 h-full w-full rounded-none opacity-40 mix-blend-multiply" />
                    ) : (
                        <ColumnsImage block={block} part="card1Image" className="absolute inset-0 h-full w-full rounded-none opacity-40 mix-blend-multiply" />
                    )}
                </article>
            );
        }

        if (index === 1) {
            return (
                <article key={card.index} className="h-full rounded-[32px] bg-[var(--site-surface)] p-8">
                    <StyledCardContentSlot
                        block={childBlock}
                        itemPart={itemPart}
                        titleClassName="text-2xl font-semibold text-[var(--site-text)]"
                        textClassName="mt-2 font-light text-[var(--site-muted)]"
                    >
                        <>
                            <h3 {...editableTextPart(childBlock ? "title" : "card2Title")} className="text-2xl font-semibold text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : "card2Title")}>
                                {richTextContent(childBlock ?? block, childBlock ? "title" : "card2Title", card.title)}
                            </h3>
                            <p {...editableTextPart(childBlock ? "text" : "card2Text")} className="mt-2 font-light text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : "card2Text")}>
                                {richTextContent(childBlock ?? block, childBlock ? "text" : "card2Text", card.text)}
                            </p>
                        </>
                    </StyledCardContentSlot>
                    <div className="mt-8 flex h-32 items-center justify-center rounded-2xl bg-[var(--site-surface)] text-[var(--site-muted)] shadow-sm">
                        <Icon name="briefcase" className="h-12 w-12" />
                    </div>
                </article>
            );
        }

        return (
            <article key={card.index} className="flex h-full flex-col justify-between rounded-[32px] bg-[var(--site-text)] p-8 text-[var(--site-on-inverse)]">
                <StyledCardContentSlot
                    block={childBlock}
                    itemPart={itemPart}
                    titleClassName="text-2xl font-semibold"
                    textClassName="mt-2 font-light text-[color-mix(in_srgb,var(--site-on-inverse)_55%,transparent)]"
                >
                    <div>
                        <h3 {...editableTextPart(childBlock ? "title" : "card3Title")} className="text-2xl font-semibold" style={partStyle(childBlock ?? block, childBlock ? "title" : "card3Title")}>
                            {richTextContent(childBlock ?? block, childBlock ? "title" : "card3Title", card.title)}
                        </h3>
                        <p {...editableTextPart(childBlock ? "text" : "card3Text")} className="mt-2 font-light text-[color-mix(in_srgb,var(--site-on-inverse)_55%,transparent)]" style={partStyle(childBlock ?? block, childBlock ? "text" : "card3Text")}>
                            {richTextContent(childBlock ?? block, childBlock ? "text" : "card3Text", card.text)}
                        </p>
                    </div>
                </StyledCardContentSlot>
                <div className="mt-8 flex justify-end">
                    <a href={card.url} className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--site-surface)] text-[var(--site-text)] transition-transform hover:scale-110">
                        <Icon name="chevronRight" className="h-5 w-5" />
                    </a>
                </div>
            </article>
        );
    };

    return (
        <ColumnsSectionShell block={block}>
            <ColumnsEyebrow block={block} />
            <SlotItemsWithFallback
                block={block}
                className="grid grid-cols-1 gap-6 md:h-[700px] md:grid-cols-3 md:grid-rows-2"
                itemClassName={bentoItemClassName}
                renderItem={({ card, childBlock, index, itemPart }) => renderBentoCard(card, index, childBlock, itemPart)}
            >
                <div className="grid grid-cols-1 gap-6 md:h-[700px] md:grid-cols-3 md:grid-rows-2">
                    {cards.map((card, index) => (
                        <div key={card.index} className={bentoItemClassName({ index })}>
                            {renderBentoCard(card, index)}
                        </div>
                    ))}
                </div>
            </SlotItemsWithFallback>
        </ColumnsSectionShell>
    );
}

function ColumnsMagazineCollage({ block }: { block: ShopTemplateBlock }) {
    const items = slotChildren(block, "items");
    const itemsReplaceFallback = useSlotReplacesFallback(block, "items");
    const renderMediaImage: SlotRenderItem = ({ childBlock, itemPart }) => (
        childBlock.type === "image" || childBlock.type === "card"
            ? <ColumnsImage block={childBlock} className="h-[250px] rounded-[24px]" />
            : <AtomicBlock block={childBlock} partPrefix={itemPart} />
    );

    const renderOverlayCard = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <div className="relative h-[600px] overflow-hidden rounded-[24px] lg:h-[800px]">
            {childBlock ? (
                <ColumnsImage block={childBlock} className="h-full w-full rounded-none" />
            ) : (
                <ColumnsImage block={block} className="h-full w-full rounded-none" />
            )}
            <div className="absolute bottom-10 left-10 rounded-2xl bg-[color-mix(in_srgb,var(--site-text)_30%,transparent)] px-6 py-4 text-[var(--site-on-inverse)] backdrop-blur-md">
                <StyledCardContentSlot
                    block={childBlock}
                    itemPart={itemPart}
                    className="grid gap-0"
                    titleClassName="text-2xl font-semibold"
                    textClassName="mt-1 text-sm font-light text-[color-mix(in_srgb,var(--site-on-inverse)_80%,transparent)]"
                    buttonClassName="mt-3 inline-flex self-start border-b border-[var(--site-on-inverse)] pb-1 text-sm font-medium text-[var(--site-on-inverse)]"
                >
                    <>
                        <h3 {...editableTextPart(childBlock ? "title" : "card1Title")} className="text-2xl font-semibold" style={partStyle(childBlock ?? block, childBlock ? "title" : "card1Title")}>{richTextContent(childBlock ?? block, childBlock ? "title" : "card1Title", card.title)}</h3>
                        <p {...editableTextPart(childBlock ? "text" : "card1Text")} className="mt-1 text-sm font-light text-[color-mix(in_srgb,var(--site-on-inverse)_80%,transparent)]" style={partStyle(childBlock ?? block, childBlock ? "text" : "card1Text")}>{richTextContent(childBlock ?? block, childBlock ? "text" : "card1Text", card.text)}</p>
                    </>
                </StyledCardContentSlot>
            </div>
        </div>
    );
    const renderJournalCard = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => {
        const eyebrow = blockText(childBlock ?? block, childBlock ? "eyebrow" : "eyebrow", "Editorial");

        return (
        <article className="flex h-[250px] flex-col justify-center rounded-[24px] border border-[var(--site-border)] bg-[var(--site-surface)] p-8 transition-shadow hover:shadow-lg">
            <span {...editableTextPart("eyebrow")} className="mb-2 text-xs font-semibold uppercase text-[var(--site-muted)]" style={partStyle(childBlock ?? block, "eyebrow")}>{richTextContent(childBlock ?? block, "eyebrow", eyebrow)}</span>
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                titleClassName="text-xl font-semibold text-[var(--site-text)]"
                textClassName="mt-2 text-sm font-light text-[var(--site-muted)]"
                buttonClassName="mt-4 inline-flex self-start border-b border-[var(--site-text)] pb-1 text-sm font-medium text-[var(--site-text)] transition-colors hover:border-[var(--site-primary)] hover:text-[var(--site-primary)]"
            >
                <>
                    <h3 {...editableTextPart("title")} className="text-xl font-semibold text-[var(--site-text)]" style={partStyle(childBlock ?? block, "title")}>
                        {richTextContent(childBlock ?? block, "title", card.title)}
                    </h3>
                    {card.label ? (
                        <a {...editablePart(childBlock ? "primaryButton" : "card1Label")} href={card.url} className="mt-4 inline-flex self-start border-b border-[var(--site-text)] pb-1 text-sm font-medium text-[var(--site-text)] transition-colors hover:border-[var(--site-primary)] hover:text-[var(--site-primary)]" style={partStyle(childBlock ?? block, childBlock ? "primaryButton" : "card1Label")}>
                            <span {...editableTextPart(childBlock ? "primaryButton" : "card1Label")}>{richTextContent(childBlock ?? block, childBlock ? "label" : "card1Label", card.label)}</span>
                        </a>
                    ) : null}
                </>
            </StyledCardContentSlot>
        </article>
        );
    };

    if (items.length > 0 || itemsReplaceFallback) {
        if (items.length === 0) {
            return (
                <ColumnsSectionShell block={block}>
                    <ColumnsEyebrow block={block} />
                    <SlotBlockGroup block={block} slotKey="items" className="grid min-h-24 gap-6" />
                </ColumnsSectionShell>
            );
        }

        const firstPart = slotPart("items", 0);
        const secondPart = slotPart("items", 1);
        const firstCard = slotCard(items[0], 1);
        const secondCard = items[1] ? slotCard(items[1], 2) : null;

        return (
            <ColumnsSectionShell block={block}>
                <ColumnsEyebrow block={block} />
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-12" data-shop-slot-container="items" data-shop-slot-item-count={items.length}>
                    <div data-shop-block-part={firstPart} className="lg:col-span-8" style={childBlockWrapperStyle(items[0])}>
                        {renderOverlayCard(firstCard, items[0], firstPart)}
                    </div>
                    <div className="flex h-full flex-col gap-6 lg:col-span-4">
                        <SlotWithFallback
                            block={block}
                            slotKey="media"
                            className="grid gap-6"
                            emptyClassName="grid gap-6"
                            renderItem={renderMediaImage}
                        >
                            <>
                                <ColumnsImage block={block} part="image2" className="h-[250px] rounded-[24px]" />
                                <ColumnsImage block={block} part="image3" className="h-[250px] rounded-[24px]" />
                            </>
                        </SlotWithFallback>
                        {items[1] && secondCard ? (
                            <div data-shop-block-part={secondPart} style={childBlockWrapperStyle(items[1])}>
                                {renderJournalCard(secondCard, items[1], secondPart)}
                            </div>
                        ) : null}
                        {items.slice(2).map((childBlock, index) => {
                            const itemPart = slotPart("items", index + 2);

                            return (
                                <div key={`${childBlock.type}:${index + 2}`} data-shop-block-part={itemPart} data-shop-outline-disabled={siteBuilderBlockDelegatesOutlineToColumn(childBlock) ? "true" : undefined} style={childBlockWrapperStyle(childBlock)}>
                                    {renderJournalCard(slotCard(childBlock, index + 3), childBlock, itemPart)}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </ColumnsSectionShell>
        );
    }

    return (
        <ColumnsSectionShell block={block}>
            <ColumnsEyebrow block={block} />
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-12">
                <div className="relative h-[600px] overflow-hidden rounded-[24px] lg:col-span-8 lg:h-[800px]">
                    <ColumnsImage block={block} className="h-full w-full rounded-none" />
                    <div className="absolute bottom-10 left-10 rounded-2xl bg-[color-mix(in_srgb,var(--site-text)_30%,transparent)] px-6 py-4 text-[var(--site-on-inverse)] backdrop-blur-md">
                        <h3 {...editableTextPart("card1Title")} className="text-2xl font-semibold" style={partStyle(block, "card1Title")}>{richTextContent(block, "card1Title", "The core collection")}</h3>
                        <p {...editableTextPart("card1Text")} className="mt-1 text-sm font-light text-[color-mix(in_srgb,var(--site-on-inverse)_80%,transparent)]" style={partStyle(block, "card1Text")}>{richTextContent(block, "card1Text", "")}</p>
                    </div>
                </div>
                <div className="flex h-full flex-col gap-6 lg:col-span-4">
                    <SlotWithFallback
                        block={block}
                        slotKey="media"
                        className="grid gap-6"
                        emptyClassName="grid gap-6"
                        renderItem={renderMediaImage}
                    >
                        <>
                            <ColumnsImage block={block} part="image2" className="h-[250px] rounded-[24px]" />
                            <ColumnsImage block={block} part="image3" className="h-[250px] rounded-[24px]" />
                        </>
                    </SlotWithFallback>
                    <article className="flex h-[250px] flex-col justify-center rounded-[24px] border border-[var(--site-border)] bg-[var(--site-surface)] p-8 transition-shadow hover:shadow-lg">
                        <span {...editableTextPart("eyebrow")} className="mb-2 text-xs font-semibold uppercase text-[var(--site-muted)]" style={partStyle(block, "eyebrow")}>{richTextContent(block, "eyebrow", "Editorial")}</span>
                        <h3 {...editableTextPart("title")} className="text-xl font-semibold text-[var(--site-text)]" style={partStyle(block, "title")}>
                            {richTextContent(block, "title", "Read the latest journal")}
                        </h3>
                        {blockText(block, "card1Label", "") ? (
                            <a {...editablePart("card1Label")} href={functionalStorefrontHref(blockText(block, "card1Url", ""))} className="mt-4 inline-flex self-start border-b border-[var(--site-text)] pb-1 text-sm font-medium text-[var(--site-text)] transition-colors hover:border-[var(--site-primary)] hover:text-[var(--site-primary)]" style={partStyle(block, "card1Label")}>
                                <span {...editableTextPart("card1Label")}>{richTextContent(block, "card1Label", "Read article")}</span>
                            </a>
                        ) : null}
                    </article>
                </div>
            </div>
        </ColumnsSectionShell>
    );
}

function ColumnsTimeline({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 3);

    const renderCard = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <article key={card.index} className="relative pt-12 md:pt-16">
            <div className={classNames("mb-6 inline-flex h-12 w-12 items-center justify-center rounded-full border shadow-sm md:absolute md:left-0 md:top-0 md:z-10 md:mb-0", card.index === 3 ? "border-[var(--site-text)] bg-[var(--site-text)] text-[var(--site-on-inverse)]" : "border-[var(--site-border)] bg-[var(--site-surface)] text-[var(--site-text)]")}>
                {String(card.index).padStart(2, "0")}
            </div>
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                titleClassName="text-2xl font-semibold leading-tight text-[var(--site-text)]"
                textClassName="mt-4 font-light leading-relaxed text-[var(--site-muted)]"
            >
                <>
                    <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="text-2xl font-semibold leading-tight text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}
                    </h3>
                    <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className="mt-4 font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}
                    </p>
                </>
            </StyledCardContentSlot>
        </article>
    );

    return (
        <ColumnsSectionShell block={block}>
            <ColumnsEyebrow block={block} />
            <SlotItemsWithFallback
                block={block}
                className="relative grid grid-cols-1 gap-12 before:absolute before:left-0 before:right-0 before:top-6 before:hidden before:h-px before:bg-[var(--site-border)] md:grid-cols-3 md:before:block lg:gap-24"
                renderItem={({ card, childBlock, itemPart }) => renderCard(card, childBlock, itemPart)}
            >
                <div className="relative grid grid-cols-1 gap-12 md:grid-cols-3 lg:gap-24">
                    <div className="absolute left-0 right-0 top-6 hidden h-px bg-[var(--site-border)] md:block" />
                    {cards.map((card) => renderCard(card))}
                </div>
            </SlotItemsWithFallback>
        </ColumnsSectionShell>
    );
}

function ColumnsComparison({ block }: { block: ShopTemplateBlock }) {
    const message = useShopStorefrontMessage();
    const cards = columnsCards(block, 2);
    const items = slotChildren(block, "items");
    const itemsReplaceFallback = useSlotReplacesFallback(block, "items");

    const renderCard = (card: ColumnsCard, highlighted: boolean, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <article key={card.index} className={classNames("rounded-[32px] p-8 text-center lg:p-12", highlighted ? "bg-[var(--site-text)] text-[var(--site-on-inverse)] shadow-2xl" : "border border-[var(--site-border)] bg-[var(--site-surface)] text-[var(--site-text)]")}>
            {childBlock ? (
                <ColumnsImage block={childBlock} className={classNames("mx-auto mb-8 h-48 w-48 rounded-full", highlighted ? "shadow-xl" : "shadow-md")} />
            ) : (
                <ColumnsImage block={block} part={card.imagePart} className={classNames("mx-auto mb-8 h-48 w-48 rounded-full", highlighted ? "shadow-xl" : "shadow-md")} />
            )}
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                titleClassName="text-2xl font-semibold"
                textClassName={classNames("mt-2", highlighted ? "text-[color-mix(in_srgb,var(--site-on-inverse)_55%,transparent)]" : "text-[var(--site-muted)]")}
                buttonClassName={classNames("mt-8 inline-flex w-full justify-center rounded-full py-3 font-medium", highlighted ? "bg-[var(--site-surface)] text-[var(--site-text)] hover:bg-[color-mix(in_srgb,var(--site-surface)_90%,transparent)]" : "border border-[var(--site-border)] bg-[var(--site-surface)] hover:bg-[var(--site-surface)]")}
            >
                <>
                    <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="text-2xl font-semibold" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>{richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}</h3>
                    <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className={classNames("mt-2", highlighted ? "text-[color-mix(in_srgb,var(--site-on-inverse)_55%,transparent)]" : "text-[var(--site-muted)]")} style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>{richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}</p>
                    {card.label ? <a {...editablePart(childBlock ? "primaryButton" : `card${card.index}Label`)} href={card.url} className={classNames("mt-8 inline-flex w-full justify-center rounded-full py-3 font-medium", highlighted ? "bg-[var(--site-surface)] text-[var(--site-text)] hover:bg-[color-mix(in_srgb,var(--site-surface)_90%,transparent)]" : "border border-[var(--site-border)] bg-[var(--site-surface)] hover:bg-[var(--site-surface)]")} style={partStyle(childBlock ?? block, childBlock ? "primaryButton" : `card${card.index}Label`)}><span {...editableTextPart(childBlock ? "primaryButton" : `card${card.index}Label`)}>{richTextContent(childBlock ?? block, childBlock ? "label" : `card${card.index}Label`, card.label)}</span></a> : null}
                </>
            </StyledCardContentSlot>
        </article>
    );
    const renderComparisonLabels = (childBlock?: ShopTemplateBlock) => {
        const labels = multilineItems(blockText(childBlock, "text", "Material\nFit profile\nDurability\nBest for"), ["Material", "Fit profile", "Durability", "Best for"]);

        return (
            <div className="hidden flex-col gap-12 text-center text-sm font-semibold uppercase text-[var(--site-muted)] lg:flex">
                {labels.map((label, index) => (
                    <div key={`${label}-${index}`} {...(childBlock ? editablePart("text") : {})} style={childBlock ? partStyle(childBlock, "text") : undefined}>{label}</div>
                ))}
            </div>
        );
    };

    if (items.length > 0 || itemsReplaceFallback) {
        return (
            <ColumnsSectionShell block={block} className="bg-[var(--site-surface)] px-5 py-[140px] text-center sm:px-6">
                <ColumnsEyebrow block={block} centered />
                <ShowcaseCopySlot
                    block={block}
                    slotClassName="mb-20 grid justify-items-center gap-0 text-center"
                    emptyClassName="mb-6 grid justify-items-center gap-3 text-center"
                    headingClassName="text-center text-4xl font-semibold text-[var(--site-text)] lg:text-[48px]"
                >
                    <h2 {...editableTextPart("title")} className="mb-20 text-center text-4xl font-semibold text-[var(--site-text)] lg:text-[48px]" style={partStyle(block, "title")}>
                        {richTextContent(block, "title", "Find your perfect fit.")}
                    </h2>
                </ShowcaseCopySlot>
                <div className="mx-auto grid max-w-5xl grid-cols-1 items-center gap-8 lg:grid-cols-[1fr_200px_1fr]" data-shop-slot-container="items" data-shop-slot-item-count={items.length}>
                    {items.map((childBlock, index) => {
                        const itemPart = slotPart("items", index);
                        const isLabels = childBlock.type === "paragraph" || childBlock.type === "text";
                        const highlighted = index >= 2;

                        return (
                            <div key={`${childBlock.type}:${index}`} data-shop-block-part={itemPart} data-shop-outline-disabled={siteBuilderBlockDelegatesOutlineToColumn(childBlock) ? "true" : undefined} style={childBlockWrapperStyle(childBlock)}>
                                {isLabels
                                    ? renderComparisonLabels(childBlock)
                                    : renderCard(slotCard(childBlock, index + 1), highlighted, childBlock, itemPart)}
                            </div>
                        );
                    })}
                </div>
            </ColumnsSectionShell>
        );
    }

    return (
        <ColumnsSectionShell block={block} className="bg-[var(--site-surface)] px-5 py-[140px] text-center sm:px-6">
            <ColumnsEyebrow block={block} centered />
            <ShowcaseCopySlot
                block={block}
                slotClassName="mb-20 grid justify-items-center gap-0 text-center"
                emptyClassName="mb-6 grid justify-items-center gap-3 text-center"
                headingClassName="text-center text-4xl font-semibold text-[var(--site-text)] lg:text-[48px]"
            >
                <h2 {...editableTextPart("title")} className="mb-20 text-center text-4xl font-semibold text-[var(--site-text)] lg:text-[48px]" style={partStyle(block, "title")}>
                    {richTextContent(block, "title", "Find your perfect fit.")}
                </h2>
            </ShowcaseCopySlot>
            <SlotItemsWithFallback
                block={block}
                className="mx-auto grid max-w-5xl grid-cols-1 items-center gap-8 lg:grid-cols-[1fr_200px_1fr]"
                renderItem={({ card, childBlock, index, itemPart }) => (
                    <>
                        {index === 1 ? (
                            <div className="hidden flex-col gap-12 text-center text-sm font-semibold uppercase text-[var(--site-muted)] lg:flex">
                                <div>{message("ui.comparison.material")}</div>
                                <div>{message("ui.comparison.fitProfile")}</div>
                                <div>{message("ui.comparison.durability")}</div>
                                <div>{message("ui.comparison.bestFor")}</div>
                            </div>
                        ) : null}
                        {renderCard(card, index === 1, childBlock, itemPart)}
                    </>
                )}
            >
                <div className="mx-auto grid max-w-5xl grid-cols-1 items-center gap-8 lg:grid-cols-[1fr_200px_1fr]">
                    {cards[0] ? renderCard(cards[0], false) : null}
                    <div className="hidden flex-col gap-12 text-center text-sm font-semibold uppercase text-[var(--site-muted)] lg:flex">
                        <div>{message("ui.comparison.material")}</div>
                        <div>{message("ui.comparison.fitProfile")}</div>
                        <div>{message("ui.comparison.durability")}</div>
                        <div>{message("ui.comparison.bestFor")}</div>
                    </div>
                    {cards[1] ? renderCard(cards[1], true) : null}
                </div>
            </SlotItemsWithFallback>
        </ColumnsSectionShell>
    );
}

function ColumnsEditorialFashion({ block }: { block: ShopTemplateBlock }) {
    const editorMode = useContext(ShopEditorModeContext);
    const items = slotChildren(block, "items");
    const itemsReplaceFallback = useSlotReplacesFallback(block, "items");
    const renderLeftColumn = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <div className="flex flex-col gap-6 md:col-span-3">
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                titleClassName="text-3xl font-semibold leading-tight text-[var(--site-text)]"
                textClassName="text-sm font-light leading-relaxed text-[var(--site-muted)]"
            >
                <>
                    <h3 {...editableTextPart(childBlock ? "title" : "card1Title")} className="text-3xl font-semibold leading-tight text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : "card1Title")}>
                        {richTextContent(childBlock ?? block, childBlock ? "title" : "card1Title", card.title)}
                    </h3>
                    <p {...editableTextPart(childBlock ? "text" : "card1Text")} className="text-sm font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : "card1Text")}>
                        {richTextContent(childBlock ?? block, childBlock ? "text" : "card1Text", card.text)}
                    </p>
                </>
            </StyledCardContentSlot>
            {childBlock ? (
                <ColumnsImage block={childBlock} className="mt-4 aspect-[3/4] rounded-[16px]" />
            ) : (
                <ColumnsImage block={block} part="card1Image" className="mt-4 aspect-[3/4] rounded-[16px]" />
            )}
        </div>
    );
    const renderRightColumn = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <div className="flex h-full flex-col justify-end gap-6 pt-12 md:col-span-3 md:pt-0">
            {childBlock ? (
                <ColumnsImage block={childBlock} className="mb-4 aspect-square rounded-[16px]" />
            ) : (
                <ColumnsImage block={block} part="card2Image" className="mb-4 aspect-square rounded-[16px]" />
            )}
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                textClassName="text-sm font-light leading-relaxed text-[var(--site-muted)]"
                buttonClassName="mt-2 inline-flex self-start border-b border-[var(--site-text)] pb-1 text-sm font-medium text-[var(--site-text)] transition-colors hover:border-[var(--site-primary)] hover:text-[var(--site-primary)]"
            >
                <>
                    <p {...editableTextPart(childBlock ? "text" : "card2Text")} className="text-sm font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : "card2Text")}>
                        {richTextContent(childBlock ?? block, childBlock ? "text" : "card2Text", card.text)}
                    </p>
                    {card.label ? (
                        <a {...editablePart(childBlock ? "primaryButton" : "primaryButton")} href={card.url} className="mt-2 inline-flex self-start border-b border-[var(--site-text)] pb-1 text-sm font-medium text-[var(--site-text)] transition-colors hover:border-[var(--site-primary)] hover:text-[var(--site-primary)]" style={partStyle(childBlock ?? block, childBlock ? "primaryButton" : "primaryButton")}>
                            <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock ?? block, childBlock ? "label" : "primaryLabel", card.label)}</span>
                        </a>
                    ) : null}
                </>
            </StyledCardContentSlot>
        </div>
    );

    if (items.length > 0 || itemsReplaceFallback) {
        const firstPart = slotPart("items", 0);
        const secondPart = slotPart("items", 1);

        return (
            <ColumnsSectionShell block={block}>
                <ColumnsEyebrow block={block} />
                <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-12 lg:gap-16" data-shop-slot-container="items" data-shop-slot-item-count={items.length}>
                    {items[0] ? (
                        <div data-shop-block-part={firstPart} className="md:col-span-3" style={childBlockWrapperStyle(items[0])}>
                            {renderLeftColumn(slotCard(items[0], 1), items[0], firstPart)}
                        </div>
                    ) : null}
                    <div className="md:col-span-6">
                        <ShowcaseMediaSlot block={block} className="aspect-[4/5] rounded-[24px] shadow-lg">
                            <ColumnsImage block={block} className="aspect-[4/5] rounded-[24px] shadow-lg" />
                        </ShowcaseMediaSlot>
                    </div>
                    {items[1] ? (
                        <div data-shop-block-part={secondPart} className="md:col-span-3" style={childBlockWrapperStyle(items[1])}>
                            {renderRightColumn(slotCard(items[1], 2), items[1], secondPart)}
                        </div>
                    ) : null}
                </div>
            </ColumnsSectionShell>
        );
    }

    return (
        <ColumnsSectionShell block={block}>
            <ColumnsEyebrow block={block} />
            <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-12 lg:gap-16">
                <div className="flex flex-col gap-6 md:col-span-3">
                    <h3 {...editableTextPart("card1Title")} className="text-3xl font-semibold leading-tight text-[var(--site-text)]" style={partStyle(block, "card1Title")}>
                        {richTextContent(block, "card1Title", "Effortless elegance")}
                    </h3>
                    <p {...editableTextPart("card1Text")} className="text-sm font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(block, "card1Text")}>
                        {richTextContent(block, "card1Text", "")}
                    </p>
                    <ColumnsImage block={block} part="card1Image" className="mt-4 aspect-[3/4] rounded-[16px]" />
                </div>
                <div className="md:col-span-6">
                    <ShowcaseMediaSlot block={block} className="aspect-[4/5] rounded-[24px] shadow-lg">
                        <ColumnsImage block={block} className="aspect-[4/5] rounded-[24px] shadow-lg" />
                    </ShowcaseMediaSlot>
                </div>
                <div className="flex h-full flex-col justify-end gap-6 pt-12 md:col-span-3 md:pt-0">
                    <ColumnsImage block={block} part="card2Image" className="mb-4 aspect-square rounded-[16px]" />
                    <p {...editableTextPart("card2Text")} className="text-sm font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(block, "card2Text")}>
                        {richTextContent(block, "card2Text", "")}
                    </p>
                    {blockText(block, "primaryLabel", "") ? (
                        <a {...editablePart("primaryButton")} href={functionalStorefrontHref(blockText(block, "primaryUrl", ""))} className="mt-2 inline-flex self-start border-b border-[var(--site-text)] pb-1 text-sm font-medium text-[var(--site-text)] transition-colors hover:border-[var(--site-primary)] hover:text-[var(--site-primary)]" style={partStyle(block, "primaryButton")}>
                            <span {...editableTextPart("primaryButton")}>{richTextContent(block, "primaryLabel", "View gallery")}</span>
                        </a>
                    ) : null}
                </div>
            </div>
            {editorMode ? (
                <SlotBlockGroup
                    block={block}
                    slotKey="items"
                    className="mt-6 grid gap-3"
                />
            ) : null}
        </ColumnsSectionShell>
    );
}

function ColumnsFloatingProductCards({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 3);

    const renderCard = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <article key={card.index} className="group rounded-[24px] bg-[var(--site-surface)] p-2 shadow-[0_20px_40px_-12px_var(--site-shadow-color)] transition-transform duration-500 hover:-translate-y-2">
            {childBlock ? (
                <ColumnsImage block={childBlock} className="aspect-square rounded-[20px]" />
            ) : (
                <ColumnsImage block={block} part={card.imagePart} className="aspect-square rounded-[20px]" />
            )}
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                className="grid gap-0 p-8"
                titleClassName="text-xl font-semibold leading-tight text-[var(--site-text)]"
                textClassName="mb-8 mt-3 text-sm font-light leading-relaxed text-[var(--site-muted)]"
                buttonClassName="inline-flex w-full justify-center rounded-full bg-[var(--site-text)] py-3 text-sm font-medium text-[var(--site-on-inverse)] transition-colors hover:bg-[var(--site-primary)]"
            >
                <div className="p-8">
                    <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="text-xl font-semibold leading-tight text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}
                    </h3>
                    <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className="mb-8 mt-3 text-sm font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}
                    </p>
                    {card.label ? (
                        <a {...editablePart(childBlock ? "primaryButton" : `card${card.index}Label`)} href={card.url} className="inline-flex w-full justify-center rounded-full bg-[var(--site-text)] py-3 text-sm font-medium text-[var(--site-on-inverse)] transition-colors hover:bg-[var(--site-primary)]" style={partStyle(childBlock ?? block, childBlock ? "primaryButton" : `card${card.index}Label`)}>
                            <span {...editableTextPart(childBlock ? "primaryButton" : `card${card.index}Label`)}>{richTextContent(childBlock ?? block, childBlock ? "label" : `card${card.index}Label`, card.label)}</span>
                        </a>
                    ) : null}
                </div>
            </StyledCardContentSlot>
        </article>
    );

    return (
        <ColumnsSectionShell block={block} className="bg-[var(--site-surface)] px-5 py-[140px] sm:px-6">
            <ColumnsEyebrow block={block} centered />
            <ShowcaseCopySlot
                block={block}
                slotClassName="mx-auto mb-20 grid max-w-2xl justify-items-center gap-0 text-center"
                headingClassName="mx-auto max-w-2xl text-center text-4xl font-semibold leading-tight text-[var(--site-text)] lg:text-[48px]"
            >
                <h2 {...editableTextPart("title")} className="mx-auto max-w-2xl text-center text-4xl font-semibold leading-tight text-[var(--site-text)] lg:text-[48px]" style={partStyle(block, "title")}>
                    {richTextContent(block, "title", "Elevate your daily routine with premium essentials.")}
                </h2>
            </ShowcaseCopySlot>
            <SlotItemsWithFallback
                block={block}
                className="grid grid-cols-1 gap-8 md:grid-cols-3 lg:gap-12"
                renderItem={({ card, childBlock, itemPart }) => renderCard(card, childBlock, itemPart)}
            >
                <div className="grid grid-cols-1 gap-8 md:grid-cols-3 lg:gap-12">
                    {cards.map((card) => renderCard(card))}
                </div>
            </SlotItemsWithFallback>
        </ColumnsSectionShell>
    );
}

function ColumnsShowcasePreview({ block }: { block: ShopTemplateBlock }) {
    const variant = columnsShowcaseVariant(block);

    if (variant === "stickyStack") return <ColumnsStickyStack block={block} />;
    if (variant === "stats") return <ColumnsStats block={block} />;
    if (variant === "imageText") return <ColumnsImageText block={block} />;
    if (variant === "fourIcons") return <ColumnsFourIcons block={block} />;
    if (variant === "alternatingRows") return <ColumnsAlternatingRows block={block} />;
    if (variant === "bentoGrid") return <ColumnsBentoGrid block={block} />;
    if (variant === "magazineCollage") return <ColumnsMagazineCollage block={block} />;
    if (variant === "timeline") return <ColumnsTimeline block={block} />;
    if (variant === "comparison") return <ColumnsComparison block={block} />;
    if (variant === "editorialFashion") return <ColumnsEditorialFashion block={block} />;
    if (variant === "floatingProductCards") return <ColumnsFloatingProductCards block={block} />;

    return <ColumnsThreeCards block={block} />;
}

type ContentShowcaseVariant =
    | "alternatingFeatureGrid"
    | "alternatingMediaRows"
    | "asymmetricEditorial"
    | "carouselPreview"
    | "checklistMedia"
    | "circularKpis"
    | "codeSnippet"
    | "companyIntro"
    | "companyValues"
    | "darkStats"
    | "editorialCard"
    | "faqMedia"
    | "featureImageCards"
    | "featureRows"
    | "fourStepProcess"
    | "iconFeatureGrid"
    | "metricDashboard"
    | "numberedProcess"
    | "premiumBento"
    | "pricingComparison"
    | "statsCards"
    | "stickyCardStack"
    | "tabbedPreview"
    | "verticalTimeline";

function contentShowcaseVariant(block: ShopTemplateBlock): ContentShowcaseVariant {
    const value = blockOptionText(block, "contentVariant");

    if (
        value === "alternatingFeatureGrid"
        || value === "alternatingMediaRows"
        || value === "asymmetricEditorial"
        || value === "carouselPreview"
        || value === "checklistMedia"
        || value === "circularKpis"
        || value === "codeSnippet"
        || value === "companyIntro"
        || value === "companyValues"
        || value === "darkStats"
        || value === "editorialCard"
        || value === "faqMedia"
        || value === "featureImageCards"
        || value === "featureRows"
        || value === "fourStepProcess"
        || value === "iconFeatureGrid"
        || value === "metricDashboard"
        || value === "numberedProcess"
        || value === "premiumBento"
        || value === "pricingComparison"
        || value === "statsCards"
        || value === "stickyCardStack"
        || value === "tabbedPreview"
        || value === "verticalTimeline"
    ) {
        return value;
    }

    return "featureImageCards";
}

function ContentSectionShell({
    block,
    children,
    className = "bg-[var(--site-background)] px-5 py-[120px] sm:px-6",
    innerClassName = "mx-auto max-w-[var(--site-container)]",
}: {
    block: ShopTemplateBlock;
    children: ReactNode;
    className?: string;
    innerClassName?: string;
}) {
    return (
        <section
            {...blockInteractionAttributes(block)}
            className={className}
            style={mergeStyles(blockStyle(block), interactionStyle(block))}
        >
            <div className={innerClassName}>
                {children}
            </div>
        </section>
    );
}

function ContentActions({ block, centered }: { block: ShopTemplateBlock; centered?: boolean }) {
    const primaryLabel = blockText(block, "primaryLabel", "");
    const secondaryLabel = blockText(block, "secondaryLabel", "");
    const renderActionItem: SlotRenderItem = ({ childBlock, index, itemPart }) => {
        if (childBlock.type !== "button") {
            return <AtomicBlock block={childBlock} partPrefix={itemPart} />;
        }

        const primary = index === 0;

        return (
            <a
                {...editablePart("primaryButton")}
href={functionalStorefrontHref(blockText(childBlock, "href", ""))}
                className={classNames(
                    "inline-flex min-h-14 items-center justify-center rounded-full px-8 text-sm font-semibold transition",
                    primary
                        ? "bg-[var(--site-primary)] text-[var(--site-on-primary)] shadow-[0_12px_28px_var(--site-shadow-color)] hover:bg-[var(--site-primary)]/90"
                        : "border border-[var(--site-border)] bg-[var(--site-background)] text-[var(--site-text)] hover:bg-[var(--site-surface)]",
                )}
                style={partStyle(childBlock, "primaryButton")}
            >
                <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
            </a>
        );
    };

    return (
        <SlotWithFallback
            block={block}
            slotKey="actions"
            className={classNames("mt-10 flex flex-col gap-4 sm:flex-row", centered && "justify-center")}
            emptyClassName={classNames("mt-4 flex flex-col gap-3 sm:flex-row", centered && "justify-center")}
            itemClassName="w-fit max-w-full"
            renderItem={renderActionItem}
        >
            {primaryLabel || secondaryLabel ? (
                <div className={classNames("mt-10 flex flex-col gap-4 sm:flex-row", centered && "justify-center")}>
                    {primaryLabel ? (
                        <a {...editablePart("primaryButton")} href={functionalStorefrontHref(blockText(block, "primaryUrl", ""))} className="inline-flex min-h-14 items-center justify-center rounded-full bg-[var(--site-primary)] px-8 text-sm font-semibold text-[var(--site-on-primary)] shadow-[0_12px_28px_var(--site-shadow-color)] transition hover:bg-[var(--site-primary)]/90" style={partStyle(block, "primaryButton")}>
                            <span {...editableTextPart("primaryButton")}>{richTextContent(block, "primaryLabel", primaryLabel)}</span>
                        </a>
                    ) : null}
                    {secondaryLabel ? (
                        <a {...editablePart("secondaryButton")} href={functionalStorefrontHref(blockText(block, "secondaryUrl", ""))} className="inline-flex min-h-14 items-center justify-center rounded-full border border-[var(--site-border)] bg-[var(--site-background)] px-8 text-sm font-semibold text-[var(--site-text)] transition hover:bg-[var(--site-surface)]" style={partStyle(block, "secondaryButton")}>
                            <span {...editableTextPart("secondaryButton")}>{richTextContent(block, "secondaryLabel", secondaryLabel)}</span>
                        </a>
                    ) : null}
                </div>
            ) : null}
        </SlotWithFallback>
    );
}

function contentStats(block: ShopTemplateBlock, count = 4) {
    return Array.from({ length: count }, (_, index) => {
        const number = index + 1;

        return {
            index: number,
            label: blockText(block, `stat${number}Label`, ""),
            text: blockText(block, `stat${number}Text`, ""),
            value: blockText(block, `stat${number}Value`, ""),
        };
    }).filter((item) => item.label || item.text || item.value);
}

function ContentFeatureImageCards({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 3);
    const renderCardContent: SlotRenderItem = ({ childBlock, itemPart }) => {
        if (childBlock.type === "heading") {
            return (
                <h3 {...editableTextPart("title")} className="text-lg font-semibold text-[var(--site-text)]" style={partStyle(childBlock, "title")}>
                    {richTextContent(childBlock, "text", blockText(childBlock, "title", ""))}
                </h3>
            );
        }

        if (childBlock.type === "paragraph" || childBlock.type === "text") {
            return (
                <p {...editableTextPart("text")} className="mt-2 text-sm leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock, "text")}>
                    {richTextContent(childBlock, "text", "")}
                </p>
            );
        }

        if (childBlock.type === "button") {
            return (
                <a {...editablePart("primaryButton")} href={functionalStorefrontHref(blockText(childBlock, "href", ""))} className="mt-4 inline-flex rounded-full bg-[var(--site-primary)] px-5 py-2.5 text-sm font-semibold text-[var(--site-on-primary)]" style={partStyle(childBlock, "primaryButton")}>
                    <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
                </a>
            );
        }

        return <AtomicBlock block={childBlock} partPrefix={itemPart} />;
    };

    const renderCard = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <div key={card.index}>
            <CardContentSlot block={childBlock} itemPart={itemPart} className="grid gap-0" renderItem={renderCardContent}>
                <>
                    <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="text-lg font-semibold text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>{richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}</h3>
                    <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className="mt-2 text-sm leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>{richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}</p>
                    {childBlock && itemPart ? <SlotCardInnerContent block={childBlock} itemPart={itemPart} /> : null}
                </>
            </CardContentSlot>
        </div>
    );

    return (
        <ContentSectionShell block={block}>
            <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
                <div>
                    <ShowcaseCopySlot
                        block={block}
                        slotClassName="grid gap-0"
                        headingClassName="text-4xl font-semibold leading-[1.1] text-[var(--site-text)] md:text-[48px]"
                        paragraphClassName="mt-6 max-w-md text-lg font-light leading-relaxed text-[var(--site-muted)]"
                    >
                        <h2 {...editableTextPart("title")} className="text-4xl font-semibold leading-[1.1] text-[var(--site-text)] md:text-[48px]" style={partStyle(block, "title")}>
                            {richTextContent(block, "title", "A new standard in digital craftsmanship.")}
                        </h2>
                        <p {...editableTextPart("text")} className="mt-6 max-w-md text-lg font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(block, "text")}>
                            {richTextContent(block, "text", "")}
                        </p>
                    </ShowcaseCopySlot>
                    <ContentActions block={block} />
                    <SlotItemsWithFallback
                        block={block}
                        className="mt-16 grid grid-cols-1 gap-6 border-t border-[var(--site-border)] pt-10 sm:grid-cols-3"
                        renderItem={({ card, childBlock, itemPart }) => renderCard(card, childBlock, itemPart)}
                    >
                        <div className="mt-16 grid grid-cols-1 gap-6 border-t border-[var(--site-border)] pt-10 sm:grid-cols-3">
                            {cards.map((card) => renderCard(card))}
                        </div>
                    </SlotItemsWithFallback>
                </div>
                <ShowcaseMediaSlot block={block} className="aspect-[4/3] rounded-[24px] shadow-[0_24px_60px_-12px_var(--site-shadow-color)]">
                    <ColumnsImage block={block} className="aspect-[4/3] rounded-[24px] shadow-[0_24px_60px_-12px_var(--site-shadow-color)]" />
                </ShowcaseMediaSlot>
            </div>
        </ContentSectionShell>
    );
}

function ContentNumberedProcess({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 3);

    const renderCard = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <article key={card.index} className="relative">
            <div aria-hidden="true" className="pointer-events-none absolute -left-4 -top-14 text-[120px] font-bold leading-none text-[var(--site-border)] opacity-70">
                {card.index}
            </div>
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                titleClassName="relative text-2xl font-semibold text-[var(--site-text)]"
                textClassName="relative mt-4 font-light leading-relaxed text-[var(--site-muted)]"
            >
                <>
                    <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="relative text-2xl font-semibold text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}
                    </h3>
                    <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className="relative mt-4 font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}
                    </p>
                </>
            </StyledCardContentSlot>
        </article>
    );

    return (
        <ContentSectionShell block={block} className="border-y border-[var(--site-border)] bg-[var(--site-surface)] px-5 py-[120px] sm:px-6">
            <ShowcaseCopySlot
                block={block}
                slotClassName="grid justify-items-center gap-0 text-center"
                headingClassName="mx-auto max-w-3xl text-center text-4xl font-semibold leading-[1.1] text-[var(--site-text)] md:text-[56px]"
            >
                <h2 {...editableTextPart("title")} className="mx-auto max-w-3xl text-center text-4xl font-semibold leading-[1.1] text-[var(--site-text)] md:text-[56px]" style={partStyle(block, "title")}>
                    {richTextContent(block, "title", "Transforming complexity into effortless experiences.")}
                </h2>
            </ShowcaseCopySlot>
            <SlotItemsWithFallback
                block={block}
                className="mt-24 grid grid-cols-1 gap-12 md:grid-cols-3 lg:gap-16"
                renderItem={({ card, childBlock, itemPart }) => renderCard(card, childBlock, itemPart)}
            >
                <div className="mt-24 grid grid-cols-1 gap-12 md:grid-cols-3 lg:gap-16">
                    {cards.map((card) => renderCard(card))}
                </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function ContentStickyCardStack({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 3);
    const renderStackContent: SlotRenderItem = ({ childBlock, itemPart }) => {
        if (childBlock.type === "heading") {
            return (
                <h3 {...editableTextPart("title")} className="text-2xl font-semibold text-[var(--site-text)]" style={partStyle(childBlock, "title")}>
                    {richTextContent(childBlock, "text", blockText(childBlock, "title", ""))}
                </h3>
            );
        }

        if (childBlock.type === "paragraph" || childBlock.type === "text") {
            return (
                <p {...editableTextPart("text")} className="mt-4 font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock, "text")}>
                    {richTextContent(childBlock, "text", "")}
                </p>
            );
        }

        if (childBlock.type === "button") {
            return (
                <a {...editablePart("primaryButton")} href={functionalStorefrontHref(blockText(childBlock, "href", ""))} className="mt-5 inline-flex rounded-full bg-[var(--site-primary)] px-5 py-2.5 text-sm font-semibold text-[var(--site-on-primary)]" style={partStyle(childBlock, "primaryButton")}>
                    <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
                </a>
            );
        }

        return <AtomicBlock block={childBlock} partPrefix={itemPart} />;
    };

    const renderCard = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <article key={card.index} className="flex flex-col items-center gap-8 rounded-[24px] border border-[var(--site-border)] bg-[var(--site-background)] p-8 shadow-[0_8px_30px_var(--site-shadow-color)] transition-shadow hover:shadow-xl sm:flex-row">
            {childBlock ? (
                <ColumnsImage block={childBlock} className="h-48 w-full shrink-0 rounded-[16px] sm:w-48" />
            ) : (
                <ColumnsImage block={block} part={card.imagePart} className="h-48 w-full shrink-0 rounded-[16px] sm:w-48" />
            )}
            <div>
                <CardContentSlot block={childBlock} itemPart={itemPart} className="grid gap-0" renderItem={renderStackContent}>
                    <>
                        <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="text-2xl font-semibold text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>{richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}</h3>
                        <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className="mt-4 font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>{richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}</p>
                        {childBlock && itemPart ? <SlotCardInnerContent block={childBlock} itemPart={itemPart} /> : null}
                    </>
                </CardContentSlot>
            </div>
        </article>
    );

    return (
        <ContentSectionShell block={block}>
            <div className="flex flex-col gap-16 lg:flex-row lg:gap-24">
                <div className="lg:w-1/3">
                    <div className="lg:sticky lg:top-12">
                        <ShowcaseCopySlot
                            block={block}
                            slotClassName="grid gap-0"
                            headingClassName="text-4xl font-semibold leading-[1.1] text-[var(--site-text)] md:text-[48px]"
                            paragraphClassName="mt-6 text-lg font-light leading-relaxed text-[var(--site-muted)]"
                        >
                            <h2 {...editableTextPart("title")} className="text-4xl font-semibold leading-[1.1] text-[var(--site-text)] md:text-[48px]" style={partStyle(block, "title")}>
                                {richTextContent(block, "title", "Curated for the modern enterprise.")}
                            </h2>
                            <p {...editableTextPart("text")} className="mt-6 text-lg font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(block, "text")}>
                                {richTextContent(block, "text", "")}
                            </p>
                        </ShowcaseCopySlot>
                    </div>
                </div>
                <SlotItemsWithFallback
                    block={block}
                    className="flex flex-col gap-8 lg:w-2/3"
                    renderItem={({ card, childBlock, itemPart }) => renderCard(card, childBlock, itemPart)}
                >
                    <div className="flex flex-col gap-8 lg:w-2/3">
                        {cards.map((card) => renderCard(card))}
                    </div>
                </SlotItemsWithFallback>
            </div>
        </ContentSectionShell>
    );
}

function ContentIconFeatureGrid({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 4);
    const icons: IconName[] = ["navigation", "shield", "rotate", "briefcase"];

    const renderCard = (card: ColumnsCard, index: number, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <article key={card.index} className="flex gap-6">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[var(--site-border)] bg-[var(--site-background)] text-[var(--site-text)] shadow-sm">
                <Icon name={icons[index] ?? "check"} className="h-5 w-5" />
            </span>
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                className="grid gap-0"
                titleClassName="text-xl font-semibold text-[var(--site-text)]"
                textClassName="mt-3 font-light leading-relaxed text-[var(--site-muted)]"
            >
                <span>
                    <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="text-xl font-semibold text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>{richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}</h3>
                    <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className="mt-3 font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>{richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}</p>
                </span>
            </StyledCardContentSlot>
        </article>
    );

    return (
        <ContentSectionShell block={block} className="border-y border-[var(--site-border)] bg-[var(--site-surface)] px-5 py-[120px] sm:px-6">
            <div className="mx-auto mb-20 max-w-2xl text-center">
                <ShowcaseCopySlot
                    block={block}
                    slotClassName="grid justify-items-center gap-0 text-center"
                    headingClassName="text-4xl font-semibold leading-[1.1] text-[var(--site-text)] md:text-[48px]"
                    paragraphClassName="mt-6 text-lg font-light text-[var(--site-muted)]"
                >
                    <h2 {...editableTextPart("title")} className="text-4xl font-semibold leading-[1.1] text-[var(--site-text)] md:text-[48px]" style={partStyle(block, "title")}>{richTextContent(block, "title", "Built for performance.")}</h2>
                    <p {...editableTextPart("text")} className="mt-6 text-lg font-light text-[var(--site-muted)]" style={partStyle(block, "text")}>{richTextContent(block, "text", "")}</p>
                </ShowcaseCopySlot>
            </div>
            <SlotItemsWithFallback
                block={block}
                className="mx-auto grid max-w-4xl grid-cols-1 gap-x-16 gap-y-16 md:grid-cols-2"
                renderItem={({ card, childBlock, index, itemPart }) => renderCard(card, index, childBlock, itemPart)}
            >
                <div className="mx-auto grid max-w-4xl grid-cols-1 gap-x-16 gap-y-16 md:grid-cols-2">
                    {cards.map((card, index) => renderCard(card, index))}
                </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function ContentEditorialCard({ block }: { block: ShopTemplateBlock }) {
    const renderEditorialCard = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => {
        const eyebrow = childBlock ? blockText(childBlock, "eyebrow", "") : blockText(block, "eyebrow", "The vision");

        return (
            <div className="relative flex min-h-[600px] w-full items-end overflow-hidden rounded-[24px] bg-[var(--site-surface)] shadow-xl">
                {childBlock ? (
                    <ColumnsImage block={childBlock} className="absolute inset-0 h-full w-full rounded-none" />
                ) : (
                    <ColumnsImage block={block} className="absolute inset-0 h-full w-full rounded-none" />
                )}
                <div className="relative z-10 m-8 max-w-xl rounded-[20px] bg-[var(--site-background)] p-8 shadow-[0_32px_64px_var(--site-shadow-color)] md:m-12 md:p-12">
                    <span {...editablePart(childBlock ? "eyebrow" : "eyebrow")} className="mb-4 block text-sm font-semibold uppercase text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "eyebrow" : "eyebrow")}>{eyebrow}</span>
                    <StyledCardContentSlot
                        block={childBlock}
                        itemPart={itemPart}
                        titleTag="h2"
                        titleClassName="text-3xl font-semibold leading-tight text-[var(--site-text)] md:text-4xl"
                        textClassName="mt-6 font-light leading-relaxed text-[var(--site-muted)]"
                        buttonClassName="mt-8 inline-flex border-b border-[var(--site-text)] pb-1 text-sm font-medium text-[var(--site-text)] transition-colors hover:border-[var(--site-primary)] hover:text-[var(--site-primary)]"
                    >
                        <>
                            <h2 {...editablePart(childBlock ? "title" : "title")} className="text-3xl font-semibold leading-tight text-[var(--site-text)] md:text-4xl" style={partStyle(childBlock ?? block, childBlock ? "title" : "title")}>{card.title}</h2>
                            <p {...editablePart(childBlock ? "text" : "text")} className="mt-6 font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : "text")}>{card.text}</p>
                            {card.label ? (
                                <a {...editablePart(childBlock ? "primaryButton" : "primaryButton")} href={card.url} className="mt-8 inline-flex border-b border-[var(--site-text)] pb-1 text-sm font-medium text-[var(--site-text)] transition-colors hover:border-[var(--site-primary)] hover:text-[var(--site-primary)]" style={partStyle(childBlock ?? block, childBlock ? "primaryButton" : "primaryButton")}>
                                    {card.label}
                                </a>
                            ) : null}
                        </>
                    </StyledCardContentSlot>
                </div>
            </div>
        );
    };

    return (
        <ContentSectionShell block={block}>
            <SlotItemsWithFallback
                block={block}
                className="grid"
                renderItem={({ card, childBlock, itemPart }) => renderEditorialCard(card, childBlock, itemPart)}
            >
                <div>
                    {renderEditorialCard({
                        imagePart: "image",
                        imageUrl: blockText(block, "imageUrl", ""),
                        index: 1,
                        label: blockText(block, "primaryLabel", ""),
                        text: blockText(block, "text", ""),
                        title: blockText(block, "title", ""),
                        url: functionalStorefrontHref(blockText(block, "primaryUrl", "")),
                    })}
                </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function ContentAlternatingMediaRows({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 2);

    const renderRow = (card: ColumnsCard, index: number, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <div key={card.index} className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2 lg:gap-24">
            {childBlock ? (
                <ColumnsImage block={childBlock} className={classNames("aspect-[4/3] rounded-[24px] shadow-lg", index === 1 && "lg:order-2")} />
            ) : (
                <ColumnsImage block={block} part={card.imagePart} className={classNames("aspect-[4/3] rounded-[24px] shadow-lg", index === 1 && "lg:order-2")} />
            )}
            <div className={classNames(index === 1 && "lg:order-1")}>
                <h2 {...editablePart(childBlock ? "title" : `card${card.index}Title`)} className="text-3xl font-semibold leading-[1.1] text-[var(--site-text)] md:text-[40px]" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>{card.title}</h2>
                <p {...editablePart(childBlock ? "text" : `card${card.index}Text`)} className="mt-6 text-lg font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>{card.text}</p>
                {card.label ? <a {...editablePart(childBlock ? "primaryButton" : `card${card.index}Label`)} href={card.url} className="mt-8 inline-flex rounded-full border border-[var(--site-border)] px-6 py-3 text-sm font-medium text-[var(--site-text)] transition-colors hover:bg-[var(--site-background)]" style={partStyle(childBlock ?? block, childBlock ? "primaryButton" : `card${card.index}Label`)}>{card.label}</a> : null}
                {childBlock && itemPart ? <SlotCardInnerContent block={childBlock} itemPart={itemPart} /> : null}
            </div>
        </div>
    );

    return (
        <ContentSectionShell block={block} className="border-y border-[var(--site-border)] bg-[var(--site-surface)] px-5 py-[120px] sm:px-6">
            <SlotItemsWithFallback
                block={block}
                className="space-y-32"
                renderItem={({ card, childBlock, index, itemPart }) => renderRow(card, index, childBlock, itemPart)}
            >
                <div className="space-y-32">
                    {cards.map((card, index) => renderRow(card, index))}
                </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function ContentAsymmetricEditorial({ block }: { block: ShopTemplateBlock }) {
    const renderAsymmetric = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => {
        const eyebrow = childBlock ? blockText(childBlock, "eyebrow", "") : blockText(block, "eyebrow", "");

        return (
            <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-12">
                <div className="pt-12 lg:col-span-4 lg:pr-12">
                    <StyledCardContentSlot
                        block={childBlock}
                        itemPart={itemPart}
                        titleTag="h2"
                        titleClassName="text-4xl font-semibold leading-tight text-[var(--site-text)] md:text-[48px]"
                        textClassName="mt-6 text-lg font-light leading-relaxed text-[var(--site-muted)]"
                    >
                        <>
                            <h2 {...editablePart(childBlock ? "title" : "title")} className="text-4xl font-semibold leading-tight text-[var(--site-text)] md:text-[48px]" style={partStyle(childBlock ?? block, childBlock ? "title" : "title")}>{card.title}</h2>
                            <p {...editablePart(childBlock ? "text" : "text")} className="mt-6 text-lg font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : "text")}>{card.text}</p>
                        </>
                    </StyledCardContentSlot>
                    <div className="mt-12 h-px w-16 bg-[var(--site-border)]" />
                    <p {...editablePart(childBlock ? "eyebrow" : "eyebrow")} className="mt-12 text-sm font-light uppercase text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "eyebrow" : "eyebrow")}>{eyebrow}</p>
                </div>
                <div className="relative lg:col-span-8">
                    {childBlock ? (
                        <>
                            <ColumnsImage block={childBlock} className="aspect-[3/4] rounded-[24px] shadow-xl lg:h-[800px] lg:aspect-auto" />
                            <ColumnsImage block={childBlock} part="image2" className="absolute -left-20 bottom-24 hidden aspect-[4/5] w-64 rounded-[16px] border-4 border-[var(--site-background)] shadow-2xl lg:block" />
                        </>
                    ) : (
                        <>
                            <ColumnsImage block={block} className="aspect-[3/4] rounded-[24px] shadow-xl lg:h-[800px] lg:aspect-auto" />
                            <ColumnsImage block={block} part="image2" className="absolute -left-20 bottom-24 hidden aspect-[4/5] w-64 rounded-[16px] border-4 border-[var(--site-background)] shadow-2xl lg:block" />
                        </>
                    )}
                </div>
            </div>
        );
    };

    return (
        <ContentSectionShell block={block}>
            <SlotItemsWithFallback
                block={block}
                className="grid"
                renderItem={({ card, childBlock, itemPart }) => renderAsymmetric(card, childBlock, itemPart)}
            >
                <div>
                    {renderAsymmetric({
                        imagePart: "image",
                        imageUrl: blockText(block, "imageUrl", ""),
                        index: 1,
                        label: "",
                        text: blockText(block, "text", ""),
                        title: blockText(block, "title", ""),
                        url: undefined,
                    })}
                </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function ContentCompanyIntro({ block }: { block: ShopTemplateBlock }) {
    const renderCompanyIntro = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <div className="text-center">
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                className="grid justify-items-center gap-0 text-center"
                titleTag="h2"
                titleClassName="mx-auto max-w-4xl text-4xl font-semibold leading-[1.05] text-[var(--site-text)] md:text-[56px] lg:text-[64px]"
                textClassName="mx-auto mt-8 max-w-3xl text-xl font-light leading-relaxed text-[var(--site-muted)]"
            >
                <>
                    <h2 {...editablePart(childBlock ? "title" : "title")} className="mx-auto max-w-4xl text-4xl font-semibold leading-[1.05] text-[var(--site-text)] md:text-[56px] lg:text-[64px]" style={partStyle(childBlock ?? block, childBlock ? "title" : "title")}>{card.title}</h2>
                    <p {...editablePart(childBlock ? "text" : "text")} className="mx-auto mt-8 max-w-3xl text-xl font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : "text")}>{card.text}</p>
                </>
            </StyledCardContentSlot>
            {childBlock ? (
                <ColumnsImage block={childBlock} className="mx-auto mt-20 aspect-video max-w-5xl rounded-[32px] border border-[var(--site-border)] shadow-2xl" />
            ) : (
                <ColumnsImage block={block} className="mx-auto mt-20 aspect-video max-w-5xl rounded-[32px] border border-[var(--site-border)] shadow-2xl" />
            )}
        </div>
    );

    return (
        <ContentSectionShell block={block} className="border-y border-[var(--site-border)] bg-[var(--site-surface)] px-5 py-[120px] text-center sm:px-6">
            <SlotItemsWithFallback
                block={block}
                className="grid"
                renderItem={({ card, childBlock, itemPart }) => renderCompanyIntro(card, childBlock, itemPart)}
            >
                <div>
                    {renderCompanyIntro({
                        imagePart: "image",
                        imageUrl: blockText(block, "imageUrl", ""),
                        index: 1,
                        label: "",
                        text: blockText(block, "text", ""),
                        title: blockText(block, "title", ""),
                        url: undefined,
                    })}
                </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function ContentStatsCards({ block }: { block: ShopTemplateBlock }) {
    const stats = contentStats(block, 4);
    const renderStatContent: SlotRenderItem = ({ childBlock, itemPart }) => {
        const role = blockOptionText(childBlock, "slotRole");

        if (role === "statValue") {
            return (
                <div {...editableTextPart("text")} className="text-[56px] font-light leading-none text-[var(--site-text)]" style={partStyle(childBlock, "text")}>
                    {richTextContent(childBlock, "text", "")}
                </div>
            );
        }

        if (childBlock.type === "heading") {
            return (
                <p {...editableTextPart("title")} className="mt-4 font-medium text-[var(--site-text)]" style={partStyle(childBlock, "title")}>
                    {richTextContent(childBlock, "text", blockText(childBlock, "title", ""))}
                </p>
            );
        }

        if (childBlock.type === "paragraph" || childBlock.type === "text") {
            return (
                <p {...editableTextPart("text")} className="mt-2 text-sm text-[var(--site-muted)]" style={partStyle(childBlock, "text")}>
                    {richTextContent(childBlock, "text", "")}
                </p>
            );
        }

        return <AtomicBlock block={childBlock} partPrefix={itemPart} />;
    };

    const renderStat = (stat: { index: number; label: string; text: string; value: string }, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <article key={stat.index} className="rounded-[24px] border border-[var(--site-border)] bg-[var(--site-surface)] p-10 text-center transition-transform hover:-translate-y-1">
            <CardContentSlot block={childBlock} itemPart={itemPart} className="grid gap-0" renderItem={renderStatContent}>
                <>
                    <div {...editableTextPart(childBlock ? "statValue" : `stat${stat.index}Value`)} className="text-[56px] font-light leading-none text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "statValue" : `stat${stat.index}Value`)}>{richTextContent(childBlock ?? block, childBlock ? "statValue" : `stat${stat.index}Value`, stat.value)}</div>
                    <p {...editableTextPart(childBlock ? "title" : `stat${stat.index}Label`)} className="mt-4 font-medium text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `stat${stat.index}Label`)}>{richTextContent(childBlock ?? block, childBlock ? "title" : `stat${stat.index}Label`, stat.label)}</p>
                    <p {...editableTextPart(childBlock ? "text" : `stat${stat.index}Text`)} className="mt-2 text-sm text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `stat${stat.index}Text`)}>{richTextContent(childBlock ?? block, childBlock ? "text" : `stat${stat.index}Text`, stat.text)}</p>
                    {childBlock && itemPart ? <SlotCardInnerContent block={childBlock} itemPart={itemPart} /> : null}
                </>
            </CardContentSlot>
        </article>
    );

    return (
        <ContentSectionShell block={block} className="bg-[var(--site-background)] px-5 py-[140px] sm:px-6">
            <ShowcaseCopySlot
                block={block}
                slotClassName="mb-20 grid justify-items-center gap-4 text-center"
                emptyClassName="mb-20 mt-4 grid justify-items-center gap-3 text-center"
                headingClassName="text-4xl font-semibold text-[var(--site-text)] md:text-[48px]"
                paragraphClassName="mt-4 text-lg font-light text-[var(--site-muted)]"
            >
                <div className="mb-20 text-center">
                    <h2 {...editableTextPart("title")} className="text-4xl font-semibold text-[var(--site-text)] md:text-[48px]" style={partStyle(block, "title")}>{richTextContent(block, "title", "Proven at scale.")}</h2>
                    <p {...editableTextPart("text")} className="mt-4 text-lg font-light text-[var(--site-muted)]" style={partStyle(block, "text")}>{richTextContent(block, "text", "")}</p>
                </div>
            </ShowcaseCopySlot>
            <SlotItemsWithFallback
                block={block}
                className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4"
                renderItem={({ card, childBlock, itemPart }) => renderStat({
                    index: card.index,
                    label: card.title,
                    text: card.text,
                    value: card.statValue ?? "",
                }, childBlock, itemPart)}
            >
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                    {stats.map((stat) => renderStat(stat))}
                </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function ContentCircularKpis({ block }: { block: ShopTemplateBlock }) {
    const stats = contentStats(block, 4);

    const renderKpi = (stat: { index: number; label: string; text: string; value: string }, childBlock?: ShopTemplateBlock, itemPart?: string) => {
        const percent = Math.max(0, Math.min(100, Number.parseFloat(stat.value.replace(/[^\d.]/g, "")) || 75));
        const dashOffset = 351 - (351 * percent) / 100;

        return (
            <article key={stat.index} className="flex flex-col items-center text-center">
                <div className="relative flex h-32 w-32 items-center justify-center">
                    <svg className="h-full w-full -rotate-90" viewBox="0 0 128 128" aria-hidden="true">
                        <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="6" fill="transparent" className="text-[var(--site-border)]" />
                        <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="6" fill="transparent" strokeDasharray="351" strokeDashoffset={dashOffset} className="text-[var(--site-primary)] transition-all duration-1000" />
                    </svg>
                    <span {...editableTextPart(childBlock ? "statValue" : `stat${stat.index}Value`)} className="absolute text-2xl font-semibold text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "statValue" : `stat${stat.index}Value`)}>{richTextContent(childBlock ?? block, childBlock ? "statValue" : `stat${stat.index}Value`, stat.value)}</span>
                </div>
                <h3 {...editableTextPart(childBlock ? "title" : `stat${stat.index}Label`)} className="mt-6 text-lg font-semibold text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `stat${stat.index}Label`)}>{richTextContent(childBlock ?? block, childBlock ? "title" : `stat${stat.index}Label`, stat.label)}</h3>
                <p {...editableTextPart(childBlock ? "text" : `stat${stat.index}Text`)} className="mt-2 text-sm text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `stat${stat.index}Text`)}>{richTextContent(childBlock ?? block, childBlock ? "text" : `stat${stat.index}Text`, stat.text)}</p>
                {childBlock && itemPart ? <SlotCardInnerContent block={childBlock} itemPart={itemPart} /> : null}
            </article>
        );
    };

    return (
        <ContentSectionShell block={block} className="border-y border-[var(--site-border)] bg-[var(--site-surface)] px-5 py-[120px] sm:px-6">
            <SlotItemsWithFallback
                block={block}
                className="mx-auto grid max-w-5xl grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4"
                renderItem={({ card, childBlock, itemPart }) => renderKpi({
                    index: card.index,
                    label: card.title,
                    text: card.text,
                    value: card.statValue ?? "",
                }, childBlock, itemPart)}
            >
                <div className="mx-auto grid max-w-5xl grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4">
                    {stats.map((stat) => renderKpi(stat))}
                </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function ContentMetricDashboard({ block }: { block: ShopTemplateBlock }) {
    const bars = ["40%", "60%", "55%", "80%", "70%", "95%"];
    const schemaVersion = useContext(ShopBuilderSchemaVersionContext);

    const renderMetricCard = (card: ColumnsCard, index: number, childBlock?: ShopTemplateBlock, itemPart?: string) => {
        const childHasContentSlot = slotChildren(childBlock, "content").length > 0 || slotReplacesFallbackAtVersion(childBlock, "content", schemaVersion);

        if (index === 0) {
            return (
                <article key={card.index} className="h-full rounded-[24px] border border-[var(--site-border)] bg-[var(--site-background)] p-8 shadow-sm">
                    <StyledCardContentSlot
                        block={childBlock}
                        itemPart={itemPart}
                        className="mb-8 grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4 gap-y-1"
                        titleClassName="text-lg font-semibold text-[var(--site-text)]"
                        textClassName="col-start-1 text-sm text-[var(--site-muted)]"
                        statClassName="col-start-2 row-span-2 row-start-1 rounded-full bg-[var(--site-primary)]/10 px-3 py-1 text-xs font-semibold text-[var(--site-primary)]"
                        statTag="span"
                    >
                        <div className="mb-8 flex items-center justify-between">
                            <div>
                                <h3 {...editableTextPart(childBlock ? "title" : "stat1Label")} className="text-lg font-semibold text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : "stat1Label")}>{richTextContent(childBlock ?? block, childBlock ? "title" : "stat1Label", card.title)}</h3>
                                <p {...editableTextPart(childBlock ? "text" : "stat1Text")} className="text-sm text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : "stat1Text")}>{richTextContent(childBlock ?? block, childBlock ? "text" : "stat1Text", card.text)}</p>
                            </div>
                            <span {...editableTextPart(childBlock ? "statValue" : "stat1Value")} className="rounded-full bg-[var(--site-primary)]/10 px-3 py-1 text-xs font-semibold text-[var(--site-primary)]" style={partStyle(childBlock ?? block, childBlock ? "statValue" : "stat1Value")}>{richTextContent(childBlock ?? block, childBlock ? "statValue" : "stat1Value", card.statValue ?? "")}</span>
                        </div>
                    </StyledCardContentSlot>
                    <div className="flex h-64 items-end gap-2 sm:gap-4">
                        {bars.map((height, barIndex) => <div key={`${height}-${barIndex}`} className={classNames("w-full rounded-t-md", barIndex === bars.length - 1 ? "bg-[var(--site-primary)] shadow-lg" : "bg-[var(--site-surface)] transition-colors hover:bg-[var(--site-border)]")} style={{ height }} />)}
                    </div>
                </article>
            );
        }

        const progressWidth = index === 1 ? "70%" : "48%";
        const statPart = `stat${card.index}`;

        return (
            <article key={card.index} className="flex h-full flex-col justify-center rounded-[24px] border border-[var(--site-border)] bg-[var(--site-background)] p-8 shadow-sm">
                <StyledCardContentSlot
                    block={childBlock}
                    itemPart={itemPart}
                    titleTag="h4"
                    titleClassName="mb-2 text-sm font-medium text-[var(--site-muted)]"
                    statClassName="text-4xl font-semibold text-[var(--site-text)]"
                    textClassName="mt-2 text-xs text-[var(--site-muted)]"
                >
                    <>
                        <h4 {...editableTextPart(childBlock ? "title" : `${statPart}Label`)} className="mb-2 text-sm font-medium text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `${statPart}Label`)}>{richTextContent(childBlock ?? block, childBlock ? "title" : `${statPart}Label`, card.title)}</h4>
                        <div {...editableTextPart(childBlock ? "statValue" : `${statPart}Value`)} className="text-4xl font-semibold text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "statValue" : `${statPart}Value`)}>{richTextContent(childBlock ?? block, childBlock ? "statValue" : `${statPart}Value`, card.statValue ?? "")}</div>
                    </>
                </StyledCardContentSlot>
                <div className="mt-4 h-2 w-full rounded-full bg-[var(--site-surface)]"><div className="h-2 rounded-full bg-[var(--site-primary)]" style={{ width: progressWidth }} /></div>
                {childHasContentSlot ? null : (
                    <p {...editableTextPart(childBlock ? "text" : `${statPart}Text`)} className="mt-2 text-xs text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `${statPart}Text`)}>{richTextContent(childBlock ?? block, childBlock ? "text" : `${statPart}Text`, card.text)}</p>
                )}
            </article>
        );
    };

    return (
        <ContentSectionShell block={block}>
            <ShowcaseCopySlot
                block={block}
                slotClassName="mb-16 grid justify-items-center gap-4 text-center"
                emptyClassName="mb-16 mt-4 grid justify-items-center gap-3 text-center"
                headingClassName="text-4xl font-semibold text-[var(--site-text)] md:text-[48px]"
            >
                <div className="mb-16 text-center">
                    <h2 {...editableTextPart("title")} className="text-4xl font-semibold text-[var(--site-text)] md:text-[48px]" style={partStyle(block, "title")}>{richTextContent(block, "title", "Real-time performance.")}</h2>
                </div>
            </ShowcaseCopySlot>
            <SlotItemsWithFallback
                block={block}
                className="mx-auto grid max-w-6xl grid-cols-1 gap-6 lg:grid-cols-3 lg:grid-rows-2"
                itemClassName={({ index }) => index === 0 ? "lg:col-span-2 lg:row-span-2" : undefined}
                renderItem={({ card, childBlock, index, itemPart }) => renderMetricCard(card, index, childBlock, itemPart)}
            >
                <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 lg:grid-cols-3">
                <article className="rounded-[24px] border border-[var(--site-border)] bg-[var(--site-background)] p-8 shadow-sm lg:col-span-2">
                    <div className="mb-8 flex items-center justify-between">
                        <div>
                            <h3 {...editableTextPart("stat1Label")} className="text-lg font-semibold text-[var(--site-text)]" style={partStyle(block, "stat1Label")}>{richTextContent(block, "stat1Label", "Revenue overview")}</h3>
                            <p {...editableTextPart("stat1Text")} className="text-sm text-[var(--site-muted)]" style={partStyle(block, "stat1Text")}>{richTextContent(block, "stat1Text", "Last 30 days")}</p>
                        </div>
                        <span {...editableTextPart("stat1Value")} className="rounded-full bg-[var(--site-primary)]/10 px-3 py-1 text-xs font-semibold text-[var(--site-primary)]" style={partStyle(block, "stat1Value")}>{richTextContent(block, "stat1Value", "+14.5%")}</span>
                    </div>
                    <div className="flex h-64 items-end gap-2 sm:gap-4">
                        {bars.map((height, index) => <div key={`${height}-${index}`} className={classNames("w-full rounded-t-md", index === bars.length - 1 ? "bg-[var(--site-primary)] shadow-lg" : "bg-[var(--site-surface)] transition-colors hover:bg-[var(--site-border)]")} style={{ height }} />)}
                    </div>
                </article>
                <div className="flex flex-col gap-6">
                    {[2, 3].map((index) => (
                        <article key={index} className="flex flex-1 flex-col justify-center rounded-[24px] border border-[var(--site-border)] bg-[var(--site-background)] p-8 shadow-sm">
                            <h4 {...editableTextPart(`stat${index}Label`)} className="mb-2 text-sm font-medium text-[var(--site-muted)]" style={partStyle(block, `stat${index}Label`)}>{richTextContent(block, `stat${index}Label`, "")}</h4>
                            <div {...editableTextPart(`stat${index}Value`)} className="text-4xl font-semibold text-[var(--site-text)]" style={partStyle(block, `stat${index}Value`)}>{richTextContent(block, `stat${index}Value`, "")}</div>
                            <div className="mt-4 h-2 w-full rounded-full bg-[var(--site-surface)]"><div className="h-2 rounded-full bg-[var(--site-primary)]" style={{ width: index === 2 ? "70%" : "48%" }} /></div>
                            <p {...editableTextPart(`stat${index}Text`)} className="mt-2 text-xs text-[var(--site-muted)]" style={partStyle(block, `stat${index}Text`)}>{richTextContent(block, `stat${index}Text`, "")}</p>
                        </article>
                    ))}
                </div>
            </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function ContentFeatureRows({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 3);
    const icons: IconName[] = ["briefcase", "shield", "navigation"];

    const renderRow = (card: ColumnsCard, index: number, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <article key={card.index} className="flex flex-col items-start gap-6 rounded-[24px] border border-[var(--site-border)] bg-[var(--site-background)] p-8 shadow-sm md:flex-row md:items-center">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[16px] border border-[var(--site-border)] bg-[var(--site-surface)] text-[var(--site-text)]">
                <Icon name={icons[index] ?? "check"} className="h-8 w-8" />
            </span>
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                className="grid flex-1 gap-0"
                titleClassName="text-xl font-semibold text-[var(--site-text)]"
                textClassName="mt-2 font-light leading-relaxed text-[var(--site-muted)]"
            >
                <span className="flex-1">
                    <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="text-xl font-semibold text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>{richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}</h3>
                    <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className="mt-2 font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>{richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}</p>
                </span>
            </StyledCardContentSlot>
        </article>
    );

    return (
        <ContentSectionShell block={block} className="border-y border-[var(--site-border)] bg-[var(--site-surface)] px-5 py-[120px] sm:px-6">
            <SlotItemsWithFallback
                block={block}
                className="mx-auto max-w-4xl space-y-8"
                renderItem={({ card, childBlock, index, itemPart }) => renderRow(card, index, childBlock, itemPart)}
            >
                <div className="mx-auto max-w-4xl space-y-8">
                    {cards.map((card, index) => renderRow(card, index))}
                </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function pricingCardHighlighted(block: ShopTemplateBlock | undefined, index: number): boolean {
    const highlighted = blockOptionText(block, "pricingHighlighted");

    if (highlighted === "true") {
        return true;
    }

    if (highlighted === "false") {
        return false;
    }

    return index === 1;
}

function PricingComparisonCard({
    card,
    childBlock,
    fallbackBlock,
    index,
    itemPart,
}: {
    card: ColumnsCard;
    childBlock?: ShopTemplateBlock;
    fallbackBlock?: ShopTemplateBlock;
    index: number;
    itemPart?: string;
}) {
    const message = useShopStorefrontMessage();
    const highlighted = pricingCardHighlighted(childBlock, index);
    const styleBlock = childBlock ?? fallbackBlock;
    const articleAttributes = childBlock ? blockInteractionAttributes(childBlock) : {};
    const articleStyle = childBlock ? mergeStyles(blockStyle(childBlock), interactionStyle(childBlock), gridFootprintMinHeightStyle(childBlock)) : undefined;
    const schemaVersion = useContext(ShopBuilderSchemaVersionContext);
    const childContentItems = slotChildren(childBlock, "content");
    const hasContentSlot = childBlock ? childContentItems.length > 0 || slotReplacesFallbackAtVersion(childBlock, "content", schemaVersion) : false;
    const renderPricingContent: SlotRenderItem = ({ childBlock: contentBlock, index: contentIndex, itemPart: contentItemPart }) => {
        const role = blockOptionText(contentBlock, "slotRole");
        const contentKey = contentItemPart || `pricing-content:${contentBlock.type}:${contentIndex}`;

        if (contentBlock.type === "heading" || role === "title") {
            return (
                <h3 key={contentKey} {...editableTextPart("title")} className="text-lg font-medium" style={partStyle(contentBlock, "title")}>
                    {richTextContent(contentBlock, "text", blockText(contentBlock, "title", ""))}
                </h3>
            );
        }

        if (role === "statValue") {
            return (
                <div key={contentKey} {...editablePart("text")} className="mt-4 text-4xl font-semibold" style={partStyle(contentBlock, "text")}>
                    <span {...editableTextPart("text")}>{richTextContent(contentBlock, "text", "")}</span>
                    <span key="pricing-period" className={classNames("ml-1 text-lg font-normal", highlighted ? "text-[color-mix(in_srgb,var(--site-on-primary)_60%,transparent)]" : "text-[var(--site-muted)]")}>{message("ui.pricing.perMonth")}</span>
                </div>
            );
        }

        if (contentBlock.type === "paragraph" || contentBlock.type === "text") {
            return (
                <p key={contentKey} {...editableTextPart("text")} className={classNames("mt-4 h-10 text-sm", highlighted ? "text-[color-mix(in_srgb,var(--site-on-primary)_60%,transparent)]" : "text-[var(--site-muted)]")} style={partStyle(contentBlock, "text")}>
                    {richTextContent(contentBlock, "text", "")}
                </p>
            );
        }

        if (contentBlock.type === "button") {
            return (
                <a key={contentKey} {...editablePart("primaryButton")} href={functionalStorefrontHref(blockText(contentBlock, "href", ""))} className={classNames("mt-8 inline-flex w-full justify-center rounded-full py-3 font-medium transition-colors", highlighted ? "bg-[var(--site-background)] text-[var(--site-text)] hover:bg-[var(--site-surface)]" : "border border-[var(--site-border)] bg-[var(--site-surface)] text-[var(--site-text)] hover:bg-[var(--site-background)]")} style={mergeStyles(atomicButtonBlockStyle(contentBlock), partStyle(contentBlock, "primaryButton"))}>
                    <span {...editableTextPart("primaryButton")}>{richTextContent(contentBlock, "label", "")}</span>
                </a>
            );
        }

        return <AtomicBlock key={contentKey} block={contentBlock} />;
    };
    return (
        <article
            key={card.index}
            {...articleAttributes}
            className={classNames("relative rounded-[24px] border p-8", highlighted ? "border-[var(--site-primary)] bg-[var(--site-primary)] text-[var(--site-on-primary)] shadow-2xl md:-translate-y-4" : "border-[var(--site-border)] bg-[var(--site-background)] text-[var(--site-text)]")}
            style={articleStyle}
        >
            {highlighted ? <div key="pricing-highlight" className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--site-primary)] px-3 py-1 text-xs font-bold uppercase text-[var(--site-on-primary)]">{message("ui.pricing.popular")}</div> : null}
            {hasContentSlot ? (
                <SlotBlockGroup
                    key="pricing-content-slot"
                    block={childBlock}
                    slotKey="content"
                    partPrefix={itemPart}
                    className="grid gap-0"
                    renderItem={renderPricingContent}
                />
            ) : (
                <div key="pricing-fallback-content" className="contents">
                    <h3 key="pricing-title" {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="text-lg font-medium" style={partStyle(styleBlock, childBlock ? "title" : `card${card.index}Title`)}>{richTextContent(styleBlock, childBlock ? "title" : `card${card.index}Title`, card.title)}</h3>
                    <div key="pricing-value" {...editablePart(childBlock ? "statValue" : `stat${card.index}Value`)} className="mt-4 text-4xl font-semibold" style={partStyle(styleBlock, childBlock ? "statValue" : `stat${card.index}Value`)}><span {...editableTextPart(childBlock ? "statValue" : `stat${card.index}Value`)}>{richTextContent(styleBlock, childBlock ? "statValue" : `stat${card.index}Value`, card.statValue ?? "")}</span><span key="pricing-period" className={classNames("ml-1 text-lg font-normal", highlighted ? "text-[color-mix(in_srgb,var(--site-on-primary)_60%,transparent)]" : "text-[var(--site-muted)]")}>{message("ui.pricing.perMonth")}</span></div>
                    <p key="pricing-text" {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className={classNames("mt-4 h-10 text-sm", highlighted ? "text-[color-mix(in_srgb,var(--site-on-primary)_60%,transparent)]" : "text-[var(--site-muted)]")} style={partStyle(styleBlock, childBlock ? "text" : `card${card.index}Text`)}>{richTextContent(styleBlock, childBlock ? "text" : `card${card.index}Text`, card.text)}</p>
                    {card.label ? <a key="pricing-button" {...editablePart(childBlock ? "primaryButton" : `card${card.index}Label`)} href={card.url} className={classNames("mt-8 inline-flex w-full justify-center rounded-full py-3 font-medium transition-colors", highlighted ? "bg-[var(--site-background)] text-[var(--site-text)] hover:bg-[var(--site-surface)]" : "border border-[var(--site-border)] bg-[var(--site-surface)] text-[var(--site-text)] hover:bg-[var(--site-background)]")} style={mergeStyles(childBlock ? atomicButtonBlockStyle(childBlock) : undefined, partStyle(styleBlock, childBlock ? "primaryButton" : `card${card.index}Label`))}><span {...editableTextPart(childBlock ? "primaryButton" : `card${card.index}Label`)}>{richTextContent(styleBlock, childBlock ? "label" : `card${card.index}Label`, card.label)}</span></a> : null}
                    {childBlock && itemPart ? <SlotCardInnerContent key="pricing-content" block={childBlock} itemPart={itemPart} /> : null}
                </div>
            )}
        </article>
    );
}

function ContentPricingComparison({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 3);

    return (
        <ContentSectionShell block={block} className="bg-[var(--site-background)] px-5 py-[140px] sm:px-6">
            <ShowcaseCopySlot
                block={block}
                slotClassName="mb-20 grid justify-items-center gap-4 text-center"
                emptyClassName="mb-20 mt-4 grid justify-items-center gap-3 text-center"
                headingClassName="text-4xl font-semibold text-[var(--site-text)] md:text-[48px]"
            >
                <div className="mb-20 text-center">
                    <h2 {...editableTextPart("title")} className="text-4xl font-semibold text-[var(--site-text)] md:text-[48px]" style={partStyle(block, "title")}>{richTextContent(block, "title", "")}</h2>
                </div>
            </ShowcaseCopySlot>
            <SlotItemsWithFallback
                block={block}
                className="mx-auto grid max-w-5xl grid-cols-1 items-center gap-8 md:grid-cols-3"
                renderItem={({ card, childBlock, index, itemPart }) => (
                    <PricingComparisonCard
                        key={`pricing-card:${childBlock.type}:${index}`}
                        card={card}
                        childBlock={childBlock}
                        fallbackBlock={block}
                        index={index}
                        itemPart={itemPart}
                    />
                )}
            >
                <div className="mx-auto grid max-w-5xl grid-cols-1 items-center gap-8 md:grid-cols-3">
                    {cards.map((card, index) => (
                        <PricingComparisonCard
                            key={card.index}
                            card={{ ...card, statValue: blockText(block, `stat${card.index}Value`, "") }}
                            fallbackBlock={block}
                            index={index}
                        />
                    ))}
                </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function ContentChecklistMedia({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 4);

    const renderItem = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <div key={card.index} className="flex items-start gap-4">
            <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--site-primary)] text-[var(--site-on-primary)]"><Icon name="check" className="h-4 w-4" /></span>
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                titleClassName="text-lg font-medium text-[var(--site-text)]"
                textClassName="mt-1 font-light text-[var(--site-muted)]"
            >
                <span>
                    <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="text-lg font-medium text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>{richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}</h3>
                    <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className="mt-1 font-light text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>{richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}</p>
                </span>
            </StyledCardContentSlot>
        </div>
    );

    return (
        <ContentSectionShell block={block} className="border-y border-[var(--site-border)] bg-[var(--site-surface)] px-5 py-[120px] sm:px-6">
            <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
                <ShowcaseMediaSlot block={block} className="aspect-square rounded-[24px] shadow-xl">
                    <ColumnsImage block={block} className="aspect-square rounded-[24px] shadow-xl" />
                </ShowcaseMediaSlot>
                <div>
                    <ShowcaseCopySlot
                        block={block}
                        slotClassName="mb-10 grid gap-0"
                        headingClassName="text-4xl font-semibold leading-[1.1] text-[var(--site-text)] md:text-[48px]"
                    >
                        <h2 {...editableTextPart("title")} className="text-4xl font-semibold leading-[1.1] text-[var(--site-text)] md:text-[48px]" style={partStyle(block, "title")}>{richTextContent(block, "title", "")}</h2>
                    </ShowcaseCopySlot>
                    <SlotItemsWithFallback
                        block={block}
                        className="space-y-6"
                        renderItem={({ card, childBlock, itemPart }) => renderItem(card, childBlock, itemPart)}
                    >
                        <div className="space-y-6">
                            {cards.map((card) => renderItem(card))}
                        </div>
                    </SlotItemsWithFallback>
                </div>
            </div>
        </ContentSectionShell>
    );
}

function ContentTabbedPreview({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 4);
    const tabs = cards.map((card) => card.label).filter(Boolean);
    const renderTab = (card: ColumnsCard, index: number, childBlock?: ShopTemplateBlock) => (
        <span
            key={`${card.label}-${index}`}
            {...editableTextPart(childBlock ? "label" : `card${card.index}Label`)}
            className={classNames("rounded-full px-6 py-3 text-sm font-medium", index === 0 ? "bg-[var(--site-primary)] text-[var(--site-on-primary)] shadow-md" : "border border-[var(--site-border)] bg-[var(--site-background)] text-[var(--site-muted)]")}
            style={partStyle(childBlock ?? block, childBlock ? "label" : `card${card.index}Label`)}
        >
            {richTextContent(childBlock ?? block, childBlock ? "label" : `card${card.index}Label`, card.label)}
        </span>
    );

    return (
        <ContentSectionShell block={block}>
            <SlotItemsWithFallback
                block={block}
                className="mb-12 flex flex-wrap justify-center gap-4"
                itemClassName="w-fit max-w-full"
                renderItem={({ card, childBlock, index }) => renderTab(card, index, childBlock)}
            >
                <div className="mb-12 flex flex-wrap justify-center gap-4">
                    {tabs.map((tab, index) => renderTab({ imagePart: "image", imageUrl: "", index: index + 1, label: tab, text: "", title: "", url: undefined }, index))}
                </div>
            </SlotItemsWithFallback>
            <div className="rounded-[32px] border border-[var(--site-border)] bg-[var(--site-surface)] p-4 md:p-8">
                <ShowcaseMediaSlot block={block} className="aspect-video rounded-[24px] border border-[var(--site-border)] bg-[var(--site-background)] shadow-sm">
                    <ColumnsImage block={block} className="aspect-video rounded-[24px] border border-[var(--site-border)] bg-[var(--site-background)] shadow-sm" />
                </ShowcaseMediaSlot>
                <div className="mx-auto mt-8 max-w-2xl text-center">
                    <ShowcaseCopySlot
                        block={block}
                        slotClassName="grid justify-items-center gap-0 text-center"
                        headingClassName="text-2xl font-semibold text-[var(--site-text)]"
                        paragraphClassName="mt-3 font-light text-[var(--site-muted)]"
                    >
                        <h3 {...editableTextPart("title")} className="text-2xl font-semibold text-[var(--site-text)]" style={partStyle(block, "title")}>{richTextContent(block, "title", "")}</h3>
                        <p {...editableTextPart("text")} className="mt-3 font-light text-[var(--site-muted)]" style={partStyle(block, "text")}>{richTextContent(block, "text", "")}</p>
                    </ShowcaseCopySlot>
                </div>
            </div>
        </ContentSectionShell>
    );
}

function ContentVerticalTimeline({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 3);

    const renderTimelineItem = (card: ColumnsCard, index: number, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <article key={card.index} className={classNames("relative flex w-full flex-col items-start md:flex-row md:items-center md:justify-between", index === 1 && "md:flex-row-reverse")}>
            <div className={classNames("hidden w-5/12 md:block", index === 1 ? "pl-12 text-left" : "pr-12 text-right")}>
                <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="text-2xl font-semibold text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>{richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}</h3>
                <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className="mt-3 font-light text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>{richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}</p>
                {childBlock && itemPart ? <SlotCardInnerContent block={childBlock} itemPart={itemPart} /> : null}
            </div>
            <div className="absolute left-0 mt-1 h-4 w-4 rounded-full border-4 border-[var(--site-surface)] bg-[var(--site-primary)] md:left-1/2 md:mt-0 md:-translate-x-1/2" />
            <div className={classNames("w-full pl-12 md:w-5/12", index === 1 ? "md:pl-0 md:pr-12 md:text-right" : "md:pl-12")}>
                <div {...editableTextPart(childBlock ? "statValue" : `stat${card.index}Value`)} className="mb-2 text-sm font-bold text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "statValue" : `stat${card.index}Value`)}>{richTextContent(childBlock ?? block, childBlock ? "statValue" : `stat${card.index}Value`, card.statValue ?? "")}</div>
                <div className="md:hidden">
                    <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="text-2xl font-semibold text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>{richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}</h3>
                    <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className="mt-2 font-light text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>{richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}</p>
                </div>
                {childBlock ? (
                    <ColumnsImage block={childBlock} className="mt-4 aspect-[4/3] rounded-[16px] shadow-sm md:mt-0" />
                ) : (
                    <ColumnsImage block={block} part={card.imagePart} className="mt-4 aspect-[4/3] rounded-[16px] shadow-sm md:mt-0" />
                )}
            </div>
        </article>
    );

    return (
        <ContentSectionShell block={block} className="border-y border-[var(--site-border)] bg-[var(--site-surface)] px-5 py-[120px] sm:px-6" innerClassName="relative mx-auto max-w-[1000px]">
            <div className="absolute bottom-0 left-6 top-0 w-px bg-[var(--site-border)] md:left-1/2 md:-translate-x-1/2" />
            <SlotItemsWithFallback
                block={block}
                className="relative space-y-24"
                renderItem={({ card, childBlock, index, itemPart }) => renderTimelineItem(card, index, childBlock, itemPart)}
            >
                <div className="relative space-y-24">
                    {cards.map((card, index) => renderTimelineItem(card, index))}
                </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function ContentFourStepProcess({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 4);

    const renderStep = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <article key={card.index} className="relative">
            <div className={classNames("mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full border text-xl font-semibold shadow-sm", card.index === 4 ? "border-[var(--site-primary)] bg-[var(--site-primary)] text-[var(--site-on-primary)]" : "border-[var(--site-border)] bg-[var(--site-background)] text-[var(--site-text)]")}>{card.index}</div>
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                titleClassName="text-xl font-semibold text-[var(--site-text)]"
                textClassName="mt-3 text-sm font-light text-[var(--site-muted)]"
            >
                <>
                    <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="text-xl font-semibold text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>{richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}</h3>
                    <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className="mt-3 text-sm font-light text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>{richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}</p>
                </>
            </StyledCardContentSlot>
        </article>
    );

    return (
        <ContentSectionShell block={block}>
            <ShowcaseCopySlot
                block={block}
                slotClassName="mb-24 grid justify-items-center gap-0 text-center"
                headingClassName="text-4xl font-semibold text-[var(--site-text)] md:text-[48px]"
            >
                <div className="text-center">
                    <h2 {...editableTextPart("title")} className="text-4xl font-semibold text-[var(--site-text)] md:text-[48px]" style={partStyle(block, "title")}>{richTextContent(block, "title", "How it works.")}</h2>
                </div>
            </ShowcaseCopySlot>
            <SlotItemsWithFallback
                block={block}
                className="relative grid grid-cols-1 gap-12 text-center before:absolute before:left-[10%] before:right-[10%] before:top-10 before:hidden before:h-px before:bg-[var(--site-border)] md:grid-cols-4 md:before:block"
                renderItem={({ card, childBlock, itemPart }) => renderStep(card, childBlock, itemPart)}
            >
                <div className="relative grid grid-cols-1 gap-12 text-center md:grid-cols-4">
                <div className="absolute left-[10%] right-[10%] top-10 hidden h-px bg-[var(--site-border)] md:block" />
                    {cards.map((card) => renderStep(card))}
                </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function ContentFaqMedia({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 4);

    const renderFaq = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => {
        const renderFaqContent: SlotRenderItem = ({ childBlock: contentBlock, itemPart }) => {
            const role = blockOptionText(contentBlock, "slotRole");

            if (role === "title" || contentBlock.type === "heading") {
                return (
                    <h3 {...editablePart("title")} className="flex items-center justify-between text-lg font-medium text-[var(--site-text)]" style={partStyle(contentBlock, "title")}>
                        <span {...editableTextPart("title")}>{richTextContent(contentBlock, "text", blockText(contentBlock, "title", ""))}</span>
                        <Icon name="chevronDown" className={classNames("h-5 w-5 text-[var(--site-muted)]", card.index === 1 && "rotate-180")} />
                    </h3>
                );
            }

            if (role === "text" || contentBlock.type === "paragraph" || contentBlock.type === "text") {
                if (card.index !== 1) {
                    return null;
                }

                return (
                    <p {...editableTextPart("text")} className="mt-4 font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(contentBlock, "text")}>
                        {richTextContent(contentBlock, "text", "")}
                    </p>
                );
            }

            return <AtomicBlock block={contentBlock} partPrefix={itemPart} />;
        };

        return (
            <article key={card.index} className={classNames("rounded-[20px] border bg-[var(--site-background)] p-6", card.index === 1 ? "border-[var(--site-border)] shadow-sm" : "border-transparent transition-colors hover:border-[var(--site-border)]")}>
                <CardContentSlot block={childBlock} itemPart={itemPart} className="grid gap-0" renderItem={renderFaqContent}>
                    <>
                        <h3 {...editablePart(childBlock ? "title" : `card${card.index}Title`)} className="flex items-center justify-between text-lg font-medium text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>
                            <span {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)}>{richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}</span>
                            <Icon name="chevronDown" className={classNames("h-5 w-5 text-[var(--site-muted)]", card.index === 1 && "rotate-180")} />
                        </h3>
                        {card.index === 1 ? <p {...editableTextPart(childBlock ? "text" : "card1Text")} className="mt-4 font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : "card1Text")}>{richTextContent(childBlock ?? block, childBlock ? "text" : "card1Text", card.text)}</p> : null}
                    </>
                </CardContentSlot>
            </article>
        );
    };

    return (
        <ContentSectionShell block={block} className="border-y border-[var(--site-border)] bg-[var(--site-surface)] px-5 py-[120px] sm:px-6">
            <div className="grid grid-cols-1 items-start gap-16 lg:grid-cols-2 lg:gap-24">
                <ShowcaseMediaSlot block={block} className="sticky top-12 hidden aspect-[4/5] rounded-[24px] shadow-lg lg:block">
                    <ColumnsImage block={block} className="sticky top-12 hidden aspect-[4/5] rounded-[24px] shadow-lg lg:block" />
                </ShowcaseMediaSlot>
                <div>
                    <ShowcaseCopySlot
                        block={block}
                        slotClassName="mb-12 grid gap-0"
                        headingClassName="text-4xl font-semibold text-[var(--site-text)]"
                    >
                        <h2 {...editableTextPart("title")} className="text-4xl font-semibold text-[var(--site-text)]" style={partStyle(block, "title")}>{richTextContent(block, "title", "Frequently asked.")}</h2>
                    </ShowcaseCopySlot>
                    <SlotItemsWithFallback
                        block={block}
                        className="space-y-4"
                        renderItem={({ card, childBlock, itemPart }) => renderFaq(card, childBlock, itemPart)}
                    >
                        <div className="space-y-4">
                            {cards.map((card) => renderFaq(card))}
                        </div>
                    </SlotItemsWithFallback>
                </div>
            </div>
        </ContentSectionShell>
    );
}

function ContentDarkStats({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 3);

    const renderCard = (card: ColumnsCard, index: number, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <article key={card.index} className={classNames("rounded-[24px] border border-[color-mix(in_srgb,var(--site-on-primary)_15%,transparent)] bg-[color-mix(in_srgb,var(--site-on-primary)_10%,transparent)] p-10 backdrop-blur-sm", index === 1 && "lg:-translate-y-6")}>
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                statClassName="text-[72px] font-light leading-none text-[var(--site-on-primary)]"
                titleClassName="mt-4 text-xl font-medium text-[color-mix(in_srgb,var(--site-on-primary)_85%,transparent)]"
                textClassName="mt-2 font-light text-[color-mix(in_srgb,var(--site-on-primary)_55%,transparent)]"
            >
                <>
                    <div {...editableTextPart(childBlock ? "statValue" : `stat${card.index}Value`)} className="text-[72px] font-light leading-none text-[var(--site-on-primary)]" style={partStyle(childBlock ?? block, childBlock ? "statValue" : `stat${card.index}Value`)}>{richTextContent(childBlock ?? block, childBlock ? "statValue" : `stat${card.index}Value`, card.statValue ?? "")}</div>
                    <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="mt-4 text-xl font-medium text-[color-mix(in_srgb,var(--site-on-primary)_85%,transparent)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>{richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}</h3>
                    <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className="mt-2 font-light text-[color-mix(in_srgb,var(--site-on-primary)_55%,transparent)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>{richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}</p>
                </>
            </StyledCardContentSlot>
        </article>
    );

    return (
        <ContentSectionShell block={block} className="bg-[var(--site-primary)] px-5 py-[140px] text-[var(--site-on-primary)] sm:px-6">
            <SlotWithFallback
                block={block}
                slotKey="content"
                className="mb-24 grid justify-items-center gap-4 text-center"
                emptyClassName="mb-24 mt-4 grid justify-items-center gap-3 text-center"
                renderItem={({ childBlock, itemPart }) => (
                    childBlock.type === "heading" ? (
                        <h2 {...editableTextPart("title")} className="text-4xl font-semibold md:text-[56px]" style={partStyle(childBlock, "title")}>{richTextContent(childBlock, "text", blockText(childBlock, "title", ""))}</h2>
                    ) : childBlock.type === "paragraph" || childBlock.type === "text" ? (
                        <p {...editableTextPart("text")} className="text-lg font-light text-[color-mix(in_srgb,var(--site-on-primary)_70%,transparent)]" style={partStyle(childBlock, "text")}>{richTextContent(childBlock, "text", "")}</p>
                    ) : <AtomicBlock block={childBlock} partPrefix={itemPart} />
                )}
            >
                <div className="mb-24 text-center">
                    <h2 {...editableTextPart("title")} className="text-4xl font-semibold md:text-[56px]" style={partStyle(block, "title")}>{richTextContent(block, "title", "Scale without limits.")}</h2>
                </div>
            </SlotWithFallback>
            <SlotItemsWithFallback
                block={block}
                className="grid grid-cols-1 gap-8 lg:grid-cols-3"
                renderItem={({ card, childBlock, index, itemPart }) => renderCard(card, index, childBlock, itemPart)}
            >
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
                    {cards.map((card, index) => renderCard({ ...card, statValue: blockText(block, `stat${card.index}Value`, "") }, index))}
                </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function ContentCodeSnippet({ block }: { block: ShopTemplateBlock }) {
    const code = blockText(block, "code", "const data = await client.records.list();");
    const renderCodeSnippet = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => {
        const secondaryLabel = blockText(childBlock ?? block, childBlock ? "secondaryLabel" : "secondaryLabel", "");
        const secondaryHref = functionalStorefrontHref(blockText(childBlock ?? block, childBlock ? "secondaryHref" : "secondaryUrl", ""));

        return (
            <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2 lg:gap-24">
                <div>
                    <h2 {...editableTextPart(childBlock ? "title" : "title")} className="text-4xl font-semibold leading-[1.1] text-[var(--site-text)] md:text-[48px]" style={partStyle(childBlock ?? block, childBlock ? "title" : "title")}>{richTextContent(childBlock ?? block, childBlock ? "title" : "title", card.title)}</h2>
                    <p {...editableTextPart(childBlock ? "text" : "text")} className="mt-6 text-lg font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : "text")}>{richTextContent(childBlock ?? block, childBlock ? "text" : "text", card.text)}</p>
                    {card.label || secondaryLabel ? (
                        <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                            {card.label ? (
                                <a {...editablePart(childBlock ? "primaryButton" : "primaryButton")} href={card.url} className="inline-flex min-h-14 items-center justify-center rounded-full bg-[var(--site-primary)] px-8 text-sm font-semibold text-[var(--site-on-primary)] shadow-[0_12px_28px_var(--site-shadow-color)] transition hover:bg-[var(--site-primary)]/90" style={partStyle(childBlock ?? block, childBlock ? "primaryButton" : "primaryButton")}>
                                    <span {...editableTextPart(childBlock ? "primaryButton" : "primaryButton")}>{richTextContent(childBlock ?? block, childBlock ? "label" : "primaryLabel", card.label)}</span>
                                </a>
                            ) : null}
                            {secondaryLabel ? (
                                <a {...editablePart(childBlock ? "secondaryButton" : "secondaryButton")} href={secondaryHref} className="inline-flex min-h-14 items-center justify-center rounded-full border border-[var(--site-border)] bg-[var(--site-background)] px-8 text-sm font-semibold text-[var(--site-text)] transition hover:bg-[var(--site-surface)]" style={partStyle(childBlock ?? block, childBlock ? "secondaryButton" : "secondaryButton")}>
                                    <span {...editableTextPart(childBlock ? "secondaryButton" : "secondaryButton")}>{richTextContent(childBlock ?? block, "secondaryLabel", secondaryLabel)}</span>
                                </a>
                            ) : null}
                        </div>
                    ) : null}
                    {childBlock && itemPart ? <SlotCardInnerContent block={childBlock} itemPart={itemPart} /> : null}
                </div>
                <div className="overflow-x-auto rounded-[24px] border border-[var(--site-border)] bg-[var(--site-inverse)] p-8 text-[var(--site-on-inverse)] shadow-2xl">
                    <div className="mb-6 flex gap-2"><span className="h-3 w-3 rounded-full bg-[var(--site-primary)]" /><span className="h-3 w-3 rounded-full bg-[var(--site-accent)]" /><span className="h-3 w-3 rounded-full bg-[var(--site-muted)]" /></div>
                    <pre {...editableTextPart(childBlock ? "statValue" : "code")} className="whitespace-pre-wrap font-mono text-sm leading-relaxed text-[color-mix(in_srgb,var(--site-on-inverse)_80%,transparent)]" style={partStyle(childBlock ?? block, childBlock ? "statValue" : "code")}>{richTextContent(childBlock ?? block, childBlock ? "statValue" : "code", childBlock ? card.statValue ?? "" : code)}</pre>
                </div>
            </div>
        );
    };

    return (
        <ContentSectionShell block={block}>
            <SlotItemsWithFallback
                block={block}
                className="grid"
                renderItem={({ card, childBlock, itemPart }) => renderCodeSnippet(card, childBlock, itemPart)}
            >
                <div>
                    {renderCodeSnippet({
                        imagePart: "image",
                        imageUrl: "",
                        index: 1,
                        label: blockText(block, "primaryLabel", ""),
                        statValue: code,
                        text: blockText(block, "text", ""),
                        title: blockText(block, "title", "Developer friendly."),
                        url: functionalStorefrontHref(blockText(block, "primaryUrl", "")),
                    })}
                </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function ContentCarouselPreview({ block }: { block: ShopTemplateBlock }) {
    const renderCarouselCard = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <div className="mx-auto flex max-w-5xl flex-col overflow-hidden rounded-[32px] border border-[var(--site-border)] bg-[var(--site-background)] shadow-xl md:flex-row">
            <div className="aspect-square md:w-1/2 md:aspect-auto">
                {childBlock ? (
                    <ColumnsImage block={childBlock} className="h-full w-full rounded-none" />
                ) : (
                    <ColumnsImage block={block} className="h-full w-full rounded-none" />
                )}
            </div>
            <div className="flex flex-col justify-center p-10 md:w-1/2 lg:p-16">
                <span {...editableTextPart("eyebrow")} className="mb-4 text-xs font-bold uppercase text-[var(--site-muted)]" style={partStyle(childBlock ?? block, "eyebrow")}>{richTextContent(childBlock ?? block, "eyebrow", "Case study 01")}</span>
                <StyledCardContentSlot
                    block={childBlock}
                    itemPart={itemPart}
                    className="grid gap-0"
                    titleClassName="mb-6 text-3xl font-semibold text-[var(--site-text)]"
                    textClassName="mb-12 font-light leading-relaxed text-[var(--site-muted)]"
                >
                    <>
                        <h3 {...editableTextPart(childBlock ? "title" : "title")} className="mb-6 text-3xl font-semibold text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : "title")}>{richTextContent(childBlock ?? block, childBlock ? "title" : "title", card.title)}</h3>
                        <p {...editableTextPart(childBlock ? "text" : "text")} className="mb-12 font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : "text")}>{richTextContent(childBlock ?? block, childBlock ? "text" : "text", card.text)}</p>
                    </>
                </StyledCardContentSlot>
                <div className="mt-auto flex items-center justify-between">
                    <div className="flex gap-2"><span className="h-2 w-10 rounded-full bg-[var(--site-primary)]" /><span className="h-2 w-2 rounded-full bg-[var(--site-border)]" /><span className="h-2 w-2 rounded-full bg-[var(--site-border)]" /></div>
                    <div className="flex gap-2"><button className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--site-border)] text-[var(--site-muted)]"><Icon name="chevronLeft" className="h-4 w-4" /></button><button className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--site-border)] text-[var(--site-text)]"><Icon name="chevronRight" className="h-4 w-4" /></button></div>
                </div>
            </div>
        </div>
    );

    return (
        <ContentSectionShell block={block} className="border-y border-[var(--site-border)] bg-[var(--site-surface)] px-5 py-[120px] sm:px-6">
            <SlotItemsWithFallback
                block={block}
                className="grid"
                renderItem={({ card, childBlock, itemPart }) => renderCarouselCard(card, childBlock, itemPart)}
            >
                <div>
                    {renderCarouselCard({
                        imagePart: "image",
                        imageUrl: blockText(block, "imageUrl", ""),
                        index: 1,
                        label: "",
                        text: blockText(block, "text", ""),
                        title: blockText(block, "title", ""),
                        url: undefined,
                    })}
                </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function ContentAlternatingFeatureGrid({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 4);

    const renderFeatureCard = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => {
        const dark = card.index === 2;
        const imageFirst = card.index > 2;
        const renderFeatureContent: SlotRenderItem = ({ childBlock: contentBlock, itemPart }) => {
            if (contentBlock.type === "heading") {
                return (
                    <h3 {...editableTextPart("title")} className="text-2xl font-semibold" style={partStyle(contentBlock, "title")}>
                        {richTextContent(contentBlock, "text", blockText(contentBlock, "title", ""))}
                    </h3>
                );
            }

            if (contentBlock.type === "paragraph" || contentBlock.type === "text") {
                return (
                    <p {...editableTextPart("text")} className={classNames("mt-2 font-light", dark ? "text-[var(--site-background)]/55" : "text-[var(--site-muted)]")} style={partStyle(contentBlock, "text")}>
                        {richTextContent(contentBlock, "text", "")}
                    </p>
                );
            }

            if (contentBlock.type === "button") {
                return (
                    <a {...editablePart("primaryButton")} href={functionalStorefrontHref(blockText(contentBlock, "href", ""))} className={classNames("mt-4 inline-flex rounded-full px-5 py-2.5 text-sm font-semibold", dark ? "bg-[var(--site-background)] text-[var(--site-text)]" : "bg-[var(--site-text)] text-[var(--site-background)]")} style={partStyle(contentBlock, "primaryButton")}>
                        <span {...editableTextPart("primaryButton")}>{richTextContent(contentBlock, "label", "")}</span>
                    </a>
                );
            }

            return <AtomicBlock block={contentBlock} partPrefix={itemPart} />;
        };
        const textContent = (
            <CardContentSlot block={childBlock} itemPart={itemPart} className="grid gap-0" renderItem={renderFeatureContent}>
                <>
                    <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="text-2xl font-semibold" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>{richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}</h3>
                    <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className={classNames("mt-2 font-light", dark ? "text-[var(--site-background)]/55" : "text-[var(--site-muted)]")} style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>{richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}</p>
                    {childBlock && itemPart ? <SlotCardInnerContent block={childBlock} itemPart={itemPart} /> : null}
                </>
            </CardContentSlot>
        );

        return (
            <article key={card.index} className={classNames("group flex h-[500px] flex-col justify-between rounded-[32px] border p-10", dark ? "border-[var(--site-primary)] bg-[var(--site-primary)] text-[var(--site-on-primary)]" : "border-[var(--site-border)] bg-[var(--site-surface)] text-[var(--site-text)]")}>
                {!imageFirst ? (
                    <div>{textContent}</div>
                ) : null}
                {childBlock ? (
                    <ColumnsImage block={childBlock} className={classNames("h-48 w-full rounded-[16px] border shadow-sm transition-transform duration-500 group-hover:translate-y-0", imageFirst ? "mb-8 -translate-y-4" : "mt-8 translate-y-4", dark ? "border-[var(--site-background)]/10 opacity-70" : "border-[var(--site-border)]")} />
                ) : (
                    <ColumnsImage block={block} part={card.imagePart} className={classNames("h-48 w-full rounded-[16px] border shadow-sm transition-transform duration-500 group-hover:translate-y-0", imageFirst ? "mb-8 -translate-y-4" : "mt-8 translate-y-4", dark ? "border-[var(--site-background)]/10 opacity-70" : "border-[var(--site-border)]")} />
                )}
                {imageFirst ? (
                    <div>{textContent}</div>
                ) : null}
            </article>
        );
    };

    return (
        <ContentSectionShell block={block}>
            <SlotItemsWithFallback
                block={block}
                className="grid grid-cols-1 gap-8 md:grid-cols-2"
                renderItem={({ card, childBlock, itemPart }) => renderFeatureCard(card, childBlock, itemPart)}
            >
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                    {cards.map((card) => renderFeatureCard(card))}
                </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function ContentCompanyValues({ block }: { block: ShopTemplateBlock }) {
    const cards = columnsCards(block, 3);
    const renderValueContent: SlotRenderItem = ({ childBlock, itemPart }) => {
        if (childBlock.type === "heading") {
            return (
                <h3 {...editableTextPart("title")} className="text-xl font-semibold text-[var(--site-text)]" style={partStyle(childBlock, "title")}>
                    {richTextContent(childBlock, "text", blockText(childBlock, "title", ""))}
                </h3>
            );
        }

        if (childBlock.type === "paragraph" || childBlock.type === "text") {
            return (
                <p {...editableTextPart("text")} className="mt-4 font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock, "text")}>
                    {richTextContent(childBlock, "text", "")}
                </p>
            );
        }

        if (childBlock.type === "button") {
            return (
                <a {...editablePart("primaryButton")} href={functionalStorefrontHref(blockText(childBlock, "href", ""))} className="mt-5 inline-flex rounded-full bg-[var(--site-text)] px-5 py-2.5 text-sm font-semibold text-[var(--site-background)]" style={partStyle(childBlock, "primaryButton")}>
                    <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
                </a>
            );
        }

        return <AtomicBlock block={childBlock} partPrefix={itemPart} />;
    };

    const renderValue = (card: ColumnsCard, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <article key={card.index} className="rounded-[24px] border border-transparent bg-[var(--site-surface)]/60 p-8 transition-colors hover:border-[var(--site-border)] hover:bg-[var(--site-surface)]">
            <div className="mb-8 flex h-16 w-16 items-center justify-center rounded-[16px] border border-[var(--site-border)] bg-[var(--site-background)] shadow-sm">
                {card.index === 1 ? <div className="h-6 w-6 rounded-full bg-[var(--site-primary)]" /> : card.index === 2 ? <div className="h-6 w-6 rotate-45 bg-[var(--site-primary)]" /> : <div className="h-0 w-0 border-b-[20px] border-l-[12px] border-r-[12px] border-b-[var(--site-primary)] border-l-transparent border-r-transparent" />}
            </div>
            <CardContentSlot block={childBlock} itemPart={itemPart} className="grid gap-0" renderItem={renderValueContent}>
                <>
                    <h3 {...editableTextPart(childBlock ? "title" : `card${card.index}Title`)} className="text-xl font-semibold text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`)}>{richTextContent(childBlock ?? block, childBlock ? "title" : `card${card.index}Title`, card.title)}</h3>
                    <p {...editableTextPart(childBlock ? "text" : `card${card.index}Text`)} className="mt-4 font-light leading-relaxed text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`)}>{richTextContent(childBlock ?? block, childBlock ? "text" : `card${card.index}Text`, card.text)}</p>
                    {childBlock && itemPart ? <SlotCardInnerContent block={childBlock} itemPart={itemPart} /> : null}
                </>
            </CardContentSlot>
        </article>
    );

    return (
        <ContentSectionShell block={block} className="border-y border-[var(--site-border)] bg-[var(--site-background)] px-5 py-[120px] sm:px-6">
            <div className="mx-auto mb-20 max-w-2xl text-center">
                <ShowcaseCopySlot
                    block={block}
                    slotClassName="grid justify-items-center gap-0 text-center"
                    headingClassName="text-4xl font-semibold leading-[1.1] text-[var(--site-text)] md:text-[48px]"
                >
                    <h2 {...editableTextPart("title")} className="text-4xl font-semibold leading-[1.1] text-[var(--site-text)] md:text-[48px]" style={partStyle(block, "title")}>{richTextContent(block, "title", "")}</h2>
                </ShowcaseCopySlot>
            </div>
            <SlotItemsWithFallback
                block={block}
                className="grid grid-cols-1 gap-12 md:grid-cols-3"
                renderItem={({ card, childBlock, itemPart }) => renderValue(card, childBlock, itemPart)}
            >
                <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
                    {cards.map((card) => renderValue(card))}
                </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function ContentPremiumBento({ block }: { block: ShopTemplateBlock }) {
    const cards = [
        {
            imagePart: "image",
            imageUrl: blockText(block, "imageUrl", ""),
            index: 1,
            label: "",
            text: blockText(block, "card1Text", ""),
            title: blockText(block, "card1Title", ""),
            url: undefined,
        },
        {
            imagePart: "image",
            imageUrl: "",
            index: 2,
            label: "",
            statValue: blockText(block, "stat1Value", ""),
            text: "",
            title: blockText(block, "card2Title", "Uptime"),
            url: undefined,
        },
        {
            imagePart: "image",
            imageUrl: "",
            index: 3,
            label: "",
            text: "",
            title: blockText(block, "card3Title", ""),
            url: undefined,
        },
        {
            imagePart: "image",
            imageUrl: "",
            index: 4,
            label: "",
            statValue: blockText(block, "text", "POST /v1/checkout"),
            text: blockText(block, "card4Text", ""),
            title: blockText(block, "card4Title", ""),
            url: undefined,
        },
    ] satisfies ColumnsCard[];
    const bentoItemClassName = ({ index }: { index: number }) => (
        index === 0 ? "md:col-span-2 md:row-span-2"
            : index === 3 ? "md:col-span-2" : undefined
    );
    const renderBentoCard = (card: ColumnsCard, index: number, childBlock?: ShopTemplateBlock, itemPart?: string) => {
        const renderBentoContent: SlotRenderItem = ({ childBlock: contentBlock, itemPart }) => {
            const role = blockOptionText(contentBlock, "slotRole");

            if (contentBlock.type === "heading" || role === "title") {
                if (index === 0) {
                    return (
                        <h3 {...editableTextPart("title")} className="text-3xl font-semibold text-[var(--site-on-inverse)]" style={partStyle(contentBlock, "title")}>
                            {richTextContent(contentBlock, "text", blockText(contentBlock, "title", ""))}
                        </h3>
                    );
                }

                if (index === 1) {
                    return (
                        <h4 {...editableTextPart("title")} className="text-sm font-medium text-[color-mix(in_srgb,var(--site-on-primary)_55%,transparent)]" style={partStyle(contentBlock, "title")}>
                            {richTextContent(contentBlock, "text", blockText(contentBlock, "title", ""))}
                        </h4>
                    );
                }

                if (index === 2) {
                    return (
                        <h4 {...editableTextPart("title")} className="mt-auto text-lg font-semibold text-[var(--site-text)]" style={partStyle(contentBlock, "title")}>
                            {richTextContent(contentBlock, "text", blockText(contentBlock, "title", ""))}
                        </h4>
                    );
                }

                return (
                    <h3 {...editableTextPart("title")} className="text-2xl font-semibold text-[var(--site-text)]" style={partStyle(contentBlock, "title")}>
                        {richTextContent(contentBlock, "text", blockText(contentBlock, "title", ""))}
                    </h3>
                );
            }

            if (role === "statValue") {
                if (index === 1) {
                    return (
                        <div {...editableTextPart("text")} className="mt-auto text-5xl font-light text-[var(--site-on-primary)]" style={partStyle(contentBlock, "text")}>
                            {richTextContent(contentBlock, "text", "")}
                        </div>
                    );
                }

                return (
                    <code {...editableTextPart("text")} className="shrink-0 rounded-[16px] border border-[var(--site-border)] bg-[var(--site-surface)] p-4 font-mono text-xs text-[var(--site-muted)]" style={partStyle(contentBlock, "text")}>
                        {richTextContent(contentBlock, "text", "")}
                    </code>
                );
            }

            if (contentBlock.type === "paragraph" || contentBlock.type === "text") {
                return (
                    <p {...editableTextPart("text")} className={classNames(index === 0 ? "mt-2 max-w-sm font-light text-[color-mix(in_srgb,var(--site-on-inverse)_75%,transparent)]" : "mt-2 font-light text-[var(--site-muted)]")} style={partStyle(contentBlock, "text")}>
                        {richTextContent(contentBlock, "text", "")}
                    </p>
                );
            }

            return <AtomicBlock block={contentBlock} partPrefix={itemPart} />;
        };

        if (index === 0) {
            return (
                <article key={card.index} className="group relative min-h-[460px] overflow-hidden rounded-[32px] shadow-md md:h-full md:min-h-0">
                    {childBlock ? (
                        <ColumnsImage block={childBlock} className="absolute inset-0 h-full w-full rounded-none" />
                    ) : (
                        <ColumnsImage block={block} className="absolute inset-0 h-full w-full rounded-none" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[color-mix(in_srgb,var(--site-text)_80%,transparent)] via-[color-mix(in_srgb,var(--site-text)_20%,transparent)] to-transparent" />
                    <div className="absolute bottom-0 left-0 p-10">
                        <CardContentSlot block={childBlock} itemPart={itemPart} className="grid gap-0" renderItem={renderBentoContent}>
                            <>
                                <h3 {...editableTextPart(childBlock ? "title" : "card1Title")} className="text-3xl font-semibold text-[var(--site-on-inverse)]" style={partStyle(childBlock ?? block, childBlock ? "title" : "card1Title")}>{richTextContent(childBlock ?? block, childBlock ? "title" : "card1Title", card.title)}</h3>
                                <p {...editableTextPart(childBlock ? "text" : "card1Text")} className="mt-2 max-w-sm font-light text-[color-mix(in_srgb,var(--site-on-inverse)_75%,transparent)]" style={partStyle(childBlock ?? block, childBlock ? "text" : "card1Text")}>{richTextContent(childBlock ?? block, childBlock ? "text" : "card1Text", card.text)}</p>
                                {childBlock && itemPart ? <SlotCardInnerContent block={childBlock} itemPart={itemPart} /> : null}
                            </>
                        </CardContentSlot>
                    </div>
                </article>
            );
        }

        if (index === 1) {
            return (
                <article key={card.index} className="flex h-full flex-col justify-between rounded-[32px] bg-[var(--site-primary)] p-8 text-[var(--site-on-primary)] shadow-md">
                    <CardContentSlot block={childBlock} itemPart={itemPart} className="grid h-full gap-4" renderItem={renderBentoContent}>
                        <>
                            <h4 {...editableTextPart(childBlock ? "title" : "card2Title")} className="text-sm font-medium text-[color-mix(in_srgb,var(--site-on-primary)_55%,transparent)]" style={partStyle(childBlock ?? block, childBlock ? "title" : "card2Title")}>{richTextContent(childBlock ?? block, childBlock ? "title" : "card2Title", card.title)}</h4>
                            <div {...editableTextPart(childBlock ? "statValue" : "stat1Value")} className="mt-auto text-5xl font-light text-[var(--site-on-primary)]" style={partStyle(childBlock ?? block, childBlock ? "statValue" : "stat1Value")}>{richTextContent(childBlock ?? block, childBlock ? "statValue" : "stat1Value", card.statValue ?? "")}</div>
                            {childBlock && itemPart ? <SlotCardInnerContent block={childBlock} itemPart={itemPart} /> : null}
                        </>
                    </CardContentSlot>
                </article>
            );
        }

        if (index === 2) {
            return (
                <article key={card.index} className="flex h-full flex-col justify-between rounded-[32px] border border-[var(--site-border)] bg-[var(--site-surface)] p-8 shadow-sm">
                    <span className="mb-4 flex h-10 w-10 items-center justify-center rounded-full border border-[var(--site-border)] bg-[var(--site-background)] shadow-sm"><Icon name="shield" className="h-5 w-5" /></span>
                    <CardContentSlot block={childBlock} itemPart={itemPart} className="grid gap-0" renderItem={renderBentoContent}>
                        <>
                            <h4 {...editableTextPart(childBlock ? "title" : "card3Title")} className="mt-auto text-lg font-semibold text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : "card3Title")}>{richTextContent(childBlock ?? block, childBlock ? "title" : "card3Title", card.title)}</h4>
                            {childBlock && itemPart ? <SlotCardInnerContent block={childBlock} itemPart={itemPart} /> : null}
                        </>
                    </CardContentSlot>
                </article>
            );
        }

        return (
            <article key={card.index} className="flex h-full flex-col items-start justify-between gap-8 rounded-[32px] border border-[var(--site-border)] bg-[var(--site-background)] p-8 shadow-sm sm:flex-row sm:items-center">
                <CardContentSlot block={childBlock} itemPart={itemPart} className="contents" renderItem={renderBentoContent}>
                    <>
                        <div>
                            <h3 {...editableTextPart(childBlock ? "title" : "card4Title")} className="text-2xl font-semibold text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : "card4Title")}>{richTextContent(childBlock ?? block, childBlock ? "title" : "card4Title", card.title)}</h3>
                            <p {...editableTextPart(childBlock ? "text" : "card4Text")} className="mt-2 font-light text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "text" : "card4Text")}>{richTextContent(childBlock ?? block, childBlock ? "text" : "card4Text", card.text)}</p>
                            {childBlock && itemPart ? <SlotCardInnerContent block={childBlock} itemPart={itemPart} /> : null}
                        </div>
                        <code {...editableTextPart(childBlock ? "statValue" : "text")} className="shrink-0 rounded-[16px] border border-[var(--site-border)] bg-[var(--site-surface)] p-4 font-mono text-xs text-[var(--site-muted)]" style={partStyle(childBlock ?? block, childBlock ? "statValue" : "text")}>{richTextContent(childBlock ?? block, childBlock ? "statValue" : "text", card.statValue ?? "")}</code>
                    </>
                </CardContentSlot>
            </article>
        );
    };

    return (
        <ContentSectionShell block={block}>
            <SlotItemsWithFallback
                block={block}
                className="grid grid-cols-1 gap-6 md:h-[600px] md:grid-cols-4 md:grid-rows-2"
                itemClassName={bentoItemClassName}
                renderItem={({ card, childBlock, index, itemPart }) => renderBentoCard(card, index, childBlock, itemPart)}
            >
                <div className="grid grid-cols-1 gap-6 md:h-[600px] md:grid-cols-4 md:grid-rows-2">
                    {cards.map((card, index) => (
                        <div key={card.index} className={bentoItemClassName({ index })}>
                            {renderBentoCard(card, index)}
                        </div>
                    ))}
                </div>
            </SlotItemsWithFallback>
        </ContentSectionShell>
    );
}

function ContentShowcasePreview({ block }: { block: ShopTemplateBlock }) {
    const variant = contentShowcaseVariant(block);

    if (variant === "numberedProcess") return <ContentNumberedProcess block={block} />;
    if (variant === "stickyCardStack") return <ContentStickyCardStack block={block} />;
    if (variant === "iconFeatureGrid") return <ContentIconFeatureGrid block={block} />;
    if (variant === "editorialCard") return <ContentEditorialCard block={block} />;
    if (variant === "alternatingMediaRows") return <ContentAlternatingMediaRows block={block} />;
    if (variant === "asymmetricEditorial") return <ContentAsymmetricEditorial block={block} />;
    if (variant === "companyIntro") return <ContentCompanyIntro block={block} />;
    if (variant === "statsCards") return <ContentStatsCards block={block} />;
    if (variant === "circularKpis") return <ContentCircularKpis block={block} />;
    if (variant === "metricDashboard") return <ContentMetricDashboard block={block} />;
    if (variant === "featureRows") return <ContentFeatureRows block={block} />;
    if (variant === "pricingComparison") return <ContentPricingComparison block={block} />;
    if (variant === "checklistMedia") return <ContentChecklistMedia block={block} />;
    if (variant === "tabbedPreview") return <ContentTabbedPreview block={block} />;
    if (variant === "verticalTimeline") return <ContentVerticalTimeline block={block} />;
    if (variant === "fourStepProcess") return <ContentFourStepProcess block={block} />;
    if (variant === "faqMedia") return <ContentFaqMedia block={block} />;
    if (variant === "darkStats") return <ContentDarkStats block={block} />;
    if (variant === "codeSnippet") return <ContentCodeSnippet block={block} />;
    if (variant === "carouselPreview") return <ContentCarouselPreview block={block} />;
    if (variant === "alternatingFeatureGrid") return <ContentAlternatingFeatureGrid block={block} />;
    if (variant === "companyValues") return <ContentCompanyValues block={block} />;
    if (variant === "premiumBento") return <ContentPremiumBento block={block} />;

    return <ContentFeatureImageCards block={block} />;
}

type IntroHeroVariant = "centered" | "collage" | "editorial" | "features" | "fullscreen" | "gradient" | "minimal" | "parallax" | "product" | "split" | "video";

function introHeroVariant(block: ShopTemplateBlock): IntroHeroVariant {
    const value = blockOptionText(block, "introVariant");

    if (
        value === "centered"
        || value === "collage"
        || value === "editorial"
        || value === "features"
        || value === "fullscreen"
        || value === "gradient"
        || value === "minimal"
        || value === "parallax"
        || value === "product"
        || value === "split"
        || value === "video"
    ) {
        return value;
    }

    return "centered";
}

function introHeroIsDark(variant: IntroHeroVariant): boolean {
    return variant === "fullscreen" || variant === "gradient" || variant === "parallax" || variant === "video";
}

function introHeroFrameStyle(block: ShopTemplateBlock): CSSProperties | undefined {
    const backgroundColor = blockOptionText(block, "backgroundColor");
    const backgroundImage = blockOptionText(block, "backgroundImage");
    const opacity = blockOptionText(block, "opacity");

    return mergeStyles(
        {
            ...(backgroundColor ? { backgroundColor } : {}),
            ...(backgroundImage ? { backgroundImage } : {}),
            ...(opacity ? { opacity } : {}),
        },
        interactionStyle(block),
    );
}

function IntroHeroBadge({ block, centered, dark }: { block: ShopTemplateBlock; centered?: boolean; dark?: boolean }) {
    const badge = blockText(block, "badge", "");

    if (!badge) {
        return null;
    }

    return (
        <p
            {...editablePart("badge")}
            className={classNames(
                "inline-flex w-fit items-center gap-2 rounded-full px-4 py-2 text-xs font-black uppercase",
                centered && "mx-auto",
                dark ? "bg-[color-mix(in_srgb,var(--site-surface)_10%,transparent)] text-[var(--site-on-inverse)] ring-1 ring-[color-mix(in_srgb,var(--site-on-inverse)_20%,transparent)]" : "bg-[var(--site-surface)] text-[var(--site-text)] shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_10%,transparent)]",
            )}
            style={partStyle(block, "badge")}
        >
            <span className={classNames("h-2 w-2 rounded-full", dark ? "bg-[var(--site-accent)]" : "bg-[var(--site-primary)]")} />
            <span {...editableTextPart("badge")}>{richTextContent(block, "badge", badge)}</span>
        </p>
    );
}

function IntroHeroTitle({ block, centered, dark }: { block: ShopTemplateBlock; centered?: boolean; dark?: boolean }) {
    return (
        <h1
            {...editableTextPart("title")}
            className={classNames(
                "mt-5 font-bold leading-[1.05] tracking-tight",
                centered ? "mx-auto max-w-4xl text-center text-[40px] sm:text-[56px] lg:text-[72px]" : "max-w-3xl text-[40px] sm:text-[52px] lg:text-[64px]",
                dark ? "text-[var(--site-on-inverse)]" : "text-[var(--site-text)]",
            )}
            style={partStyle(block, "title")}
        >
            {richTextContent(block, "title", "Moderní storefront, který působí čistě a profesionálně.")}
        </h1>
    );
}

function IntroHeroText({ block, centered, dark }: { block: ShopTemplateBlock; centered?: boolean; dark?: boolean }) {
    return (
        <p
            {...editableTextPart("text")}
            className={classNames(
                "mt-6 text-[16px] leading-relaxed md:text-[18px]",
                centered ? "mx-auto max-w-2xl text-center" : "max-w-2xl",
                dark ? "text-[color-mix(in_srgb,var(--site-on-inverse)_90%,transparent)]" : "text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]",
            )}
            style={partStyle(block, "text")}
        >
            {richTextContent(block, "text", "Vytvořte profesionální landing page s jasným sdělením, dominantním vizuálem a CTA, které vede zákazníka dál.")}
        </p>
    );
}

type IntroHeroColumnActionStyle = "campaign" | "default" | "editorial" | "parallax";

function introHeroColumnActionStyle(variant: IntroHeroVariant): IntroHeroColumnActionStyle {
    if (variant === "editorial") {
        return "editorial";
    }

    if (variant === "parallax") {
        return "parallax";
    }

    if (variant === "fullscreen") {
        return "campaign";
    }

    return "default";
}

function IntroHeroColumnBlock({
    block,
    partPrefix,
    actionStyle = "default",
    centered,
    dark,
    showBadge = true,
}: {
    block: ShopTemplateBlock;
    partPrefix?: string;
    actionStyle?: IntroHeroColumnActionStyle;
    centered?: boolean;
    dark?: boolean;
    showBadge?: boolean;
}) {
    const renderColumnItem: SlotRenderItem = ({ childBlock, itemPart }) => {
        if ((childBlock.type === "badge" || childBlock.type === "ctaBadge") && showBadge) {
            if (actionStyle === "campaign") {
                return (
                    <span {...editablePart("primaryButton")} className="mb-4 text-sm font-semibold uppercase text-[color-mix(in_srgb,var(--site-on-inverse)_80%,transparent)]" style={partStyle(childBlock, "primaryButton")}>
                        <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
                    </span>
                );
            }

            if (actionStyle === "editorial") {
                return (
                    <span {...editablePart("primaryButton")} className="inline-flex rounded-full border border-[var(--site-border)] px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-[var(--site-muted)]" style={partStyle(childBlock, "primaryButton")}>
                        <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
                    </span>
                );
            }

            return (
                <p
                    {...editablePart("primaryButton")}
                    className={classNames(
                        "inline-flex w-fit items-center gap-2 rounded-full px-4 py-2 text-xs font-black uppercase",
                        centered && "mx-auto",
                        dark ? "bg-[color-mix(in_srgb,var(--site-surface)_10%,transparent)] text-[var(--site-on-inverse)] ring-1 ring-[color-mix(in_srgb,var(--site-on-inverse)_20%,transparent)]" : "bg-[var(--site-surface)] text-[var(--site-text)] shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_10%,transparent)]",
                    )}
                    style={partStyle(childBlock, "primaryButton")}
                >
                    <span className={classNames("h-2 w-2 rounded-full", dark ? "bg-[var(--site-accent)]" : "bg-[var(--site-primary)]")} />
                    <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
                </p>
            );
        }

        if (childBlock.type === "heading") {
            if (actionStyle === "campaign") {
                return (
                    <h1 {...editableTextPart("title")} className="text-[48px] font-light leading-[1.05] text-[var(--site-on-inverse)] md:text-[64px] lg:text-[78px]" style={partStyle(childBlock, "title")}>
                        {richTextContent(childBlock, "text", blockText(childBlock, "title", ""))}
                    </h1>
                );
            }

            if (actionStyle === "parallax") {
                return (
                    <h1 {...editableTextPart("title")} className="text-[48px] font-light leading-[1.05] text-[var(--site-background)] md:text-[64px] lg:text-[78px]" style={partStyle(childBlock, "title")}>
                        {richTextContent(childBlock, "text", blockText(childBlock, "title", ""))}
                    </h1>
                );
            }

            if (actionStyle === "editorial") {
                return (
                    <h1 {...editableTextPart("title")} className="mt-7 max-w-3xl text-5xl font-medium leading-[0.96] text-[var(--site-text)] sm:text-7xl lg:text-[88px]" style={partStyle(childBlock, "title")}>
                        {richTextContent(childBlock, "text", blockText(childBlock, "title", ""))}
                    </h1>
                );
            }

            return (
                <h1
                    {...editableTextPart("title")}
                    className={classNames(
                        "mt-5 font-bold leading-[1.05] tracking-tight",
                        centered ? "mx-auto max-w-4xl text-center text-[40px] sm:text-[56px] lg:text-[72px]" : "max-w-3xl text-[40px] sm:text-[52px] lg:text-[64px]",
                        dark ? "text-[var(--site-on-inverse)]" : "text-[var(--site-text)]",
                    )}
                    style={partStyle(childBlock, "title")}
                >
                    {richTextContent(childBlock, "text", blockText(childBlock, "title", ""))}
                </h1>
            );
        }

        if (childBlock.type === "paragraph" || childBlock.type === "text") {
            if (actionStyle === "campaign") {
                return (
                    <p {...editableTextPart("text")} className="mt-6 max-w-[650px] text-[16px] font-light leading-relaxed text-[color-mix(in_srgb,var(--site-on-inverse)_90%,transparent)] md:text-[18px]" style={partStyle(childBlock, "text")}>
                        {richTextContent(childBlock, "text", "")}
                    </p>
                );
            }

            if (actionStyle === "parallax") {
                return (
                    <p {...editableTextPart("text")} className="mt-6 max-w-[650px] text-[16px] font-light leading-relaxed text-[var(--site-background)] opacity-90 md:text-[18px]" style={partStyle(childBlock, "text")}>
                        {richTextContent(childBlock, "text", "")}
                    </p>
                );
            }

            if (actionStyle === "editorial") {
                return (
                    <p {...editableTextPart("text")} className="mt-6 max-w-2xl text-[16px] leading-relaxed text-[color-mix(in_srgb,var(--site-text)_55%,transparent)] md:text-[18px]" style={partStyle(childBlock, "text")}>
                        {richTextContent(childBlock, "text", "")}
                    </p>
                );
            }

            return (
                <p
                    {...editableTextPart("text")}
                    className={classNames(
                        "mt-6 text-[16px] leading-relaxed md:text-[18px]",
                        centered ? "mx-auto max-w-2xl text-center" : "max-w-2xl",
                        dark ? "text-[color-mix(in_srgb,var(--site-on-inverse)_90%,transparent)]" : "text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]",
                    )}
                    style={partStyle(childBlock, "text")}
                >
                    {richTextContent(childBlock, "text", "")}
                </p>
            );
        }

        if (childBlock.type === "button") {
            const secondaryButton = blockOptionText(childBlock, "buttonRole") === "secondary";

            if (actionStyle === "editorial") {
                return (
                    <a {...editablePart("primaryButton")} href={functionalStorefrontHref(blockText(childBlock, "href", ""))} className="group mt-10 inline-flex items-center gap-3 text-base font-black text-[var(--site-text)] transition hover:text-[var(--site-primary)]" style={partStyle(childBlock, "primaryButton")}>
                        <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
                        <span aria-hidden="true" className="transition-transform group-hover:translate-x-1">-&gt;</span>
                    </a>
                );
            }

            if (actionStyle === "campaign" || actionStyle === "parallax") {
                return (
                    <a
                        {...editablePart("primaryButton")}
href={functionalStorefrontHref(blockText(childBlock, "href", ""))}
                        className={classNames(
                            "inline-flex min-h-[48px] w-full items-center justify-center rounded-md px-5 text-[16px] font-medium transition duration-200 hover:opacity-90 sm:w-auto",
                            !secondaryButton
                                ? actionStyle === "parallax" ? "bg-[var(--site-background)] text-[var(--site-text)]" : "bg-[var(--site-surface)] text-[var(--site-text)]"
                                : actionStyle === "parallax" ? "border border-[var(--site-background)] bg-transparent text-[var(--site-background)]" : "border border-[var(--site-on-inverse)] bg-transparent text-[var(--site-on-inverse)]",
                        )}
                        style={partStyle(childBlock, "primaryButton")}
                    >
                        <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
                    </a>
                );
            }

            return (
                <a
                    {...editablePart("primaryButton")}
href={functionalStorefrontHref(blockText(childBlock, "href", ""))}
                    className={classNames(
                        "inline-flex h-14 items-center justify-center rounded-full px-8 text-[15px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25",
                        !secondaryButton
                            ? "bg-[var(--site-primary)] text-[var(--site-on-primary)] shadow-[0_10px_24px_var(--site-shadow-color)]"
                            : "border border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] bg-[var(--site-surface)] text-[var(--site-text)]",
                    )}
                    style={partStyle(childBlock, "primaryButton")}
                >
                    <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
                </a>
            );
        }

        return <AtomicBlock block={childBlock} partPrefix={itemPart} />;
    };
    const itemClassName: SlotItemClassName = ({ childBlock }) => (
        atomicButtonLikeBlockTypes.has(childBlock.type) ? "w-fit max-w-full" : "basis-full max-w-full"
    );

    return (
        <div
            {...atomicBlockInteractionAttributes(block)}
            className={classNames("grid min-h-full content-start", centered && "justify-items-center text-center")}
            data-shop-column-root="true"
            data-shop-column-part={partPrefix}
            style={atomicBlockStyle(block)}
        >
            <SlotBlockGroup
                block={block}
                slotKey="content"
                partPrefix={partPrefix}
                className={classNames(
                    "flex flex-wrap gap-3",
                    centered ? "justify-center text-center" : "justify-start",
                )}
                itemClassName={itemClassName}
                lockedChildren
                renderItem={renderColumnItem}
            />
        </div>
    );
}

function IntroHeroCopy({
    block,
    centered,
    dark,
    showBadge = true,
}: {
    block: ShopTemplateBlock;
    centered?: boolean;
    dark?: boolean;
    showBadge?: boolean;
}) {
    const renderCopyItem: SlotRenderItem = ({ childBlock, itemPart }) => {
        if (childBlock.type === "column") {
            return (
                <IntroHeroColumnBlock
                    block={childBlock}
                    partPrefix={itemPart}
                    centered={centered}
                    dark={dark}
                    showBadge={showBadge}
                />
            );
        }

        if ((childBlock.type === "badge" || childBlock.type === "ctaBadge") && showBadge) {
            return (
                <p
                    {...editablePart("primaryButton")}
                    className={classNames(
                        "inline-flex w-fit items-center gap-2 rounded-full px-4 py-2 text-xs font-black uppercase",
                        centered && "mx-auto",
                        dark ? "bg-[color-mix(in_srgb,var(--site-surface)_10%,transparent)] text-[var(--site-on-inverse)] ring-1 ring-[color-mix(in_srgb,var(--site-on-inverse)_20%,transparent)]" : "bg-[var(--site-surface)] text-[var(--site-text)] shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_10%,transparent)]",
                    )}
                    style={partStyle(childBlock, "primaryButton")}
                >
                    <span className={classNames("h-2 w-2 rounded-full", dark ? "bg-[var(--site-accent)]" : "bg-[var(--site-primary)]")} />
                    <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
                </p>
            );
        }

        if (childBlock.type === "heading") {
            return (
                <h1
                    {...editableTextPart("title")}
                    className={classNames(
                        "mt-5 font-bold leading-[1.05] tracking-tight",
                        centered ? "mx-auto max-w-4xl text-center text-[40px] sm:text-[56px] lg:text-[72px]" : "max-w-3xl text-[40px] sm:text-[52px] lg:text-[64px]",
                        dark ? "text-[var(--site-on-inverse)]" : "text-[var(--site-text)]",
                    )}
                    style={partStyle(childBlock, "title")}
                >
                    {richTextContent(childBlock, "text", blockText(childBlock, "title", ""))}
                </h1>
            );
        }

        if (childBlock.type === "paragraph" || childBlock.type === "text") {
            return (
                <p
                    {...editableTextPart("text")}
                    className={classNames(
                        "mt-6 text-[16px] leading-relaxed md:text-[18px]",
                        centered ? "mx-auto max-w-2xl text-center" : "max-w-2xl",
                        dark ? "text-[color-mix(in_srgb,var(--site-on-inverse)_90%,transparent)]" : "text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]",
                    )}
                    style={partStyle(childBlock, "text")}
                >
                    {richTextContent(childBlock, "text", "")}
                </p>
            );
        }

        return <AtomicBlock block={childBlock} partPrefix={itemPart} />;
    };

    return (
        <SlotWithFallback
            block={block}
            slotKey="content"
            className={classNames("grid gap-0", centered && "justify-items-center")}
            emptyClassName={classNames("mt-4 grid gap-3", centered && "justify-items-center")}
            renderItem={renderCopyItem}
        >
            {showBadge ? <IntroHeroBadge block={block} centered={centered} dark={dark} /> : null}
            <IntroHeroTitle block={block} centered={centered} dark={dark} />
            <IntroHeroText block={block} centered={centered} dark={dark} />
        </SlotWithFallback>
    );
}

function introHeroContentHasColumnActions(block: ShopTemplateBlock): boolean {
    const blockHasColumnActions = (childBlock: ShopTemplateBlock): boolean => {
        if (
            childBlock.type === "column"
            && slotChildren(childBlock, "content").some((contentBlock) => atomicButtonLikeBlockTypes.has(contentBlock.type))
        ) {
            return true;
        }

        return Object.values(childBlock.slots ?? {}).some((slotItems) => (
            Array.isArray(slotItems) && slotItems.some(blockHasColumnActions)
        ));
    };

    return Object.values(block.slots ?? {}).some((slotItems) => (
        Array.isArray(slotItems) && slotItems.some(blockHasColumnActions)
    ));
}

function IntroHeroActions({ block, centered }: { block: ShopTemplateBlock; centered?: boolean }) {
    const primaryLabel = blockText(block, "primaryLabel", "");
    const secondaryLabel = blockText(block, "secondaryLabel", "");
    const actionBlocks = slotChildren(block, "actions");

    if (actionBlocks.length === 0 && introHeroContentHasColumnActions(block)) {
        return null;
    }

    const renderActionItem: SlotRenderItem = ({ childBlock, index, itemPart }) => {
        if (childBlock.type !== "button") {
            return <AtomicBlock block={childBlock} partPrefix={itemPart} />;
        }

        const primary = index === 0;

        return (
            <a
                {...editablePart("primaryButton")}
href={functionalStorefrontHref(blockText(childBlock, "href", ""))}
                className={classNames(
                    "inline-flex h-14 items-center justify-center rounded-full px-8 text-[15px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25",
                    primary
                        ? "bg-[var(--site-primary)] text-[var(--site-on-primary)] shadow-[0_10px_24px_var(--site-shadow-color)]"
                        : "border border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] bg-[var(--site-surface)] text-[var(--site-text)]",
                )}
                style={partStyle(childBlock, "primaryButton")}
            >
                <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
            </a>
        );
    };

    return (
        <SlotWithFallback
            block={block}
            slotKey="actions"
            className={classNames("mt-10 flex flex-col gap-4 sm:flex-row sm:items-center", centered && "sm:justify-center")}
            emptyClassName={classNames("mt-4 flex flex-col gap-3 sm:flex-row sm:items-center", centered && "sm:justify-center")}
            itemClassName="w-fit max-w-full"
            renderItem={renderActionItem}
        >
            {primaryLabel || secondaryLabel ? (
                <div className={classNames("mt-10 flex flex-col gap-4 sm:flex-row sm:items-center", centered && "sm:justify-center")}>
                    {primaryLabel ? (
                        <a
                            {...editablePart("primaryButton")}
                            href={functionalStorefrontHref(blockText(block, "primaryUrl", ""))}
                            className="inline-flex h-14 items-center justify-center rounded-full bg-[var(--site-primary)] px-8 text-[15px] font-semibold text-[var(--site-on-primary)] shadow-[0_10px_24px_var(--site-shadow-color)] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25"
                            style={partStyle(block, "primaryButton")}
                        >
                            <span {...editableTextPart("primaryButton")}>{richTextContent(block, "primaryLabel", primaryLabel)}</span>
                        </a>
                    ) : null}
                    {secondaryLabel ? (
                        <a
                            {...editablePart("secondaryButton")}
                            href={functionalStorefrontHref(blockText(block, "secondaryUrl", ""))}
                            className="inline-flex h-14 items-center justify-center rounded-full border border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] bg-[var(--site-surface)] px-8 text-[15px] font-semibold text-[var(--site-text)] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25"
                            style={partStyle(block, "secondaryButton")}
                        >
                            <span {...editableTextPart("secondaryButton")}>{richTextContent(block, "secondaryLabel", secondaryLabel)}</span>
                        </a>
                    ) : null}
                </div>
            ) : null}
        </SlotWithFallback>
    );
}

function IntroHeroImage({
    block,
    className,
    imageClassName,
    part = "image",
}: {
    block: ShopTemplateBlock;
    className?: string;
    imageClassName?: string;
    part?: "image" | "image2" | "image3";
}) {
    const imagePrefix = part === "image" ? "image" : part;
    const image = blockText(block, `${imagePrefix}Url`, "");

    if (!image) {
        return null;
    }

    return (
        <div
            {...imageHoverFrameAttributes(block, part, "scale-sm")}
            className={classNames("overflow-hidden rounded-[28px] bg-[var(--site-surface)] shadow-[0_28px_76px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]", className)}
            style={imageFrameStyle(block, part)}
        >
            <img
                {...editablePart(part)}
                data-shop-image-hover-target="true"
                src={image}
                alt={blockText(block, `${imagePrefix}Alt`, blockText(block, "alt", ""))}
                title={blockText(block, `${imagePrefix}Title`, "") || undefined}
                className={classNames("h-full w-full object-contain", imageClassName)}
                style={imageInteractivePartStyle(block, part, "scale-sm")}
            />
        </div>
    );
}

function IntroHeroMediaSlot({
    block,
    className,
    imageClassName,
    children,
}: {
    block: ShopTemplateBlock;
    className: string;
    imageClassName?: string;
    children: ReactNode;
}) {
    return (
        <SlotWithFallback
            block={block}
            slotKey="media"
            className="grid"
            emptyClassName="grid"
            renderItem={({ childBlock, itemPart }) => (
                childBlock.type === "image" || childBlock.type === "card"
                    ? <IntroHeroImage block={childBlock} className={className} imageClassName={imageClassName} />
                    : <AtomicBlock block={childBlock} partPrefix={itemPart} />
            )}
        >
            {children}
        </SlotWithFallback>
    );
}

function IntroHeroStats({ block }: { block: ShopTemplateBlock }) {
    const stats = [1, 2, 3].map((index) => ({
        index,
        label: blockText(block, `stat${index}Label`, ""),
        value: blockText(block, `stat${index}Value`, ""),
    })).filter((item) => item.label || item.value);

    const renderStat = (item: { index: number; label: string; value: string }, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <div key={`${item.value}-${item.label}`} className="rounded-[22px] bg-[var(--site-surface)] p-4 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                statTag="p"
                statClassName="text-2xl font-black text-[var(--site-text)] sm:text-3xl"
                titleTag="p"
                titleClassName="mt-1 text-xs font-bold uppercase text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]"
            >
                <>
                    <p {...editableTextPart(childBlock ? "statValue" : `stat${item.index}Value`)} className="text-2xl font-black text-[var(--site-text)] sm:text-3xl" style={partStyle(childBlock ?? block, childBlock ? "statValue" : `stat${item.index}Value`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "statValue" : `stat${item.index}Value`, item.value)}
                    </p>
                    <p {...editableTextPart(childBlock ? "title" : `stat${item.index}Label`)} className="mt-1 text-xs font-bold uppercase text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `stat${item.index}Label`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "title" : `stat${item.index}Label`, item.label)}
                    </p>
                </>
            </StyledCardContentSlot>
        </div>
    );

    return (
        <SlotItemsWithFallback
            block={block}
            className="mt-10 grid gap-3 sm:grid-cols-3"
            renderItem={({ card, childBlock, itemPart }) => renderStat({
                index: card.index,
                label: card.title,
                value: card.statValue ?? "",
            }, childBlock, itemPart)}
        >
            {stats.length > 0 ? (
            <div className="mt-10 grid gap-3 sm:grid-cols-3">
                {stats.map((item) => renderStat(item))}
            </div>
            ) : null}
        </SlotItemsWithFallback>
    );
}

function IntroHeroFeatureCards({ block }: { block: ShopTemplateBlock }) {
    const icons: IconName[] = ["check", "rotate", "shield"];
    const features = [1, 2, 3].map((index) => ({
        icon: icons[index - 1] ?? "check",
        index,
        text: blockText(block, `feature${index}Text`, ""),
        title: blockText(block, `feature${index}Title`, ""),
    })).filter((item) => item.title || item.text);

    const renderFeature = (item: { icon: IconName; index: number; text: string; title: string }, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <article key={`${item.title}-${item.index}`} className="rounded-[24px] bg-[var(--site-surface)] p-6 shadow-[0_18px_48px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-[var(--site-primary)] text-[var(--site-on-primary)]">
                <Icon name={item.icon} className="h-5 w-5" />
            </span>
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                titleClassName="mt-5 text-lg font-black text-[var(--site-text)]"
                textClassName="mt-3 text-sm leading-7 text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]"
            >
                <>
                    <h3 {...editableTextPart(childBlock ? "title" : `feature${item.index}Title`)} className="mt-5 text-lg font-black text-[var(--site-text)]" style={partStyle(childBlock ?? block, childBlock ? "title" : `feature${item.index}Title`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "title" : `feature${item.index}Title`, item.title)}
                    </h3>
                    <p {...editableTextPart(childBlock ? "text" : `feature${item.index}Text`)} className="mt-3 text-sm leading-7 text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `feature${item.index}Text`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "text" : `feature${item.index}Text`, item.text)}
                    </p>
                </>
            </StyledCardContentSlot>
        </article>
    );

    return (
        <SlotItemsWithFallback
            block={block}
            className="mt-12 grid gap-4 md:grid-cols-3"
            renderItem={({ card, childBlock, index, itemPart }) => renderFeature({
                icon: icons[index] ?? "check",
                index: card.index,
                text: card.text,
                title: card.title,
            }, childBlock, itemPart)}
        >
            {features.length > 0 ? (
            <div className="mt-12 grid gap-4 md:grid-cols-3">
                {features.map((item) => renderFeature(item))}
            </div>
            ) : null}
        </SlotItemsWithFallback>
    );
}

function IntroHeroTrustBadges({ block }: { block: ShopTemplateBlock }) {
    const items = [1, 2, 3].map((index) => blockText(block, `trust${index}Label`, "")).filter(Boolean);
    const renderTrustBadge: SlotRenderItem = ({ childBlock, index }) => {
        const accent = blockOptionText(childBlock, "floatingRole") === "trustAccent" || index === 2;

        return (
            <span
                {...editablePart("primaryButton")}
                className={classNames(
                    "inline-flex items-center rounded-full bg-[var(--site-surface)] px-4 py-2 text-xs font-black text-[var(--site-text)] shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_10%,transparent)]",
                    accent && "bg-[var(--site-accent)]",
                )}
                style={partStyle(childBlock, "primaryButton")}
            >
                <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
            </span>
        );
    };

    if (items.length === 0) {
        return (
            <SlotWithFallback
                block={block}
                slotKey="items"
                className="mt-8 flex flex-wrap gap-3"
                emptyClassName="mt-4 flex flex-wrap gap-3"
                itemClassName="w-fit max-w-full"
                renderItem={renderTrustBadge}
            >
                {null}
            </SlotWithFallback>
        );
    }

    return (
        <SlotWithFallback
            block={block}
            slotKey="items"
            className="mt-8 flex flex-wrap gap-3"
            emptyClassName="mt-4 flex flex-wrap gap-3"
            itemClassName="w-fit max-w-full"
            renderItem={renderTrustBadge}
        >
            <div className="mt-8 flex flex-wrap gap-3">
                {items.map((item, index) => (
                    <span
                        key={`${item}-${index}`}
                        {...editableTextPart(`trust${index + 1}Label`)}
                        className={classNames(
                            "inline-flex items-center rounded-full bg-[var(--site-surface)] px-4 py-2 text-xs font-black text-[var(--site-text)] shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_10%,transparent)]",
                            index === 2 && "bg-[var(--site-accent)]",
                        )}
                        style={partStyle(block, `trust${index + 1}Label`)}
                    >
                        {richTextContent(block, `trust${index + 1}Label`, item)}
                    </span>
                ))}
            </div>
        </SlotWithFallback>
    );
}

function IntroHeroProductFloatingLabels({ block }: { block: ShopTemplateBlock }) {
    const firstLabel = blockText(block, "feature1Title", "");
    const secondLabel = blockText(block, "feature2Title", "");
    const itemClassName: SlotItemClassName = ({ childBlock, index }) => {
        const role = blockOptionText(childBlock, "floatingRole");

        return role === "productLabelAccent" || index === 1
            ? "absolute bottom-10 right-6 z-20 w-fit max-w-[calc(100%-3rem)]"
            : "absolute left-6 top-8 z-20 w-fit max-w-[calc(100%-3rem)]";
    };
    const renderLabel: SlotRenderItem = ({ childBlock, index }) => {
        const role = blockOptionText(childBlock, "floatingRole");
        const accent = role === "productLabelAccent" || index === 1;

        return (
            <span
                {...editablePart("primaryButton")}
                className="inline-flex items-center gap-2 rounded-full bg-[var(--site-surface)] px-4 py-2 text-sm font-black text-[var(--site-text)] shadow-lg ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]"
                style={partStyle(childBlock, "primaryButton")}
            >
                <span className={classNames("h-2 w-2 rounded-full", accent ? "bg-[var(--site-accent)]" : "bg-[var(--site-primary)]")} />
                <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
            </span>
        );
    };

    return (
        <SlotWithFallback
            block={block}
            slotKey="floatingCards"
            className="absolute inset-0 z-20"
            emptyClassName="absolute inset-0 z-20"
            itemClassName={itemClassName}
            renderItem={renderLabel}
        >
            <>
                {firstLabel ? (
                    <span {...editablePart("feature1Title")} className="absolute left-6 top-8 z-20 inline-flex items-center gap-2 rounded-full bg-[var(--site-surface)] px-4 py-2 text-sm font-black text-[var(--site-text)] shadow-lg ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]" style={partStyle(block, "feature1Title")}>
                        <span className="h-2 w-2 rounded-full bg-[var(--site-primary)]" />
                        <span {...editableTextPart("feature1Title")}>{richTextContent(block, "feature1Title", firstLabel)}</span>
                    </span>
                ) : null}
                {secondLabel ? (
                    <span {...editablePart("feature2Title")} className="absolute bottom-10 right-6 z-20 inline-flex items-center gap-2 rounded-full bg-[var(--site-surface)] px-4 py-2 text-sm font-black text-[var(--site-text)] shadow-lg ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]" style={partStyle(block, "feature2Title")}>
                        <span className="h-2 w-2 rounded-full bg-[var(--site-accent)]" />
                        <span {...editableTextPart("feature2Title")}>{richTextContent(block, "feature2Title", secondLabel)}</span>
                    </span>
                ) : null}
            </>
        </SlotWithFallback>
    );
}

function IntroHeroGlassCards({ block }: { block: ShopTemplateBlock }) {
    const cards = [1, 2, 3].map((index) => ({
        index,
        text: blockText(block, `glass${index}Text`, ""),
        title: blockText(block, `glass${index}Title`, ""),
    })).filter((item) => item.title || item.text);

    const renderGlassCard = (item: { index: number; text: string; title: string }, childBlock?: ShopTemplateBlock, itemPart?: string) => (
        <article key={`${item.title}-${item.index}`} className="rounded-[24px] border border-[color-mix(in_srgb,var(--site-on-inverse)_25%,transparent)] bg-[color-mix(in_srgb,var(--site-surface)_10%,transparent)] p-5 text-[var(--site-on-inverse)] shadow-[0_20px_54px_var(--site-shadow-color)] backdrop-blur-xl">
            <StyledCardContentSlot
                block={childBlock}
                itemPart={itemPart}
                titleClassName="text-base font-black"
                textClassName="mt-2 text-sm leading-6 text-[color-mix(in_srgb,var(--site-on-inverse)_70%,transparent)]"
            >
                <>
                    <h3 {...editableTextPart(childBlock ? "title" : `glass${item.index}Title`)} className="text-base font-black" style={partStyle(childBlock ?? block, childBlock ? "title" : `glass${item.index}Title`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "title" : `glass${item.index}Title`, item.title)}
                    </h3>
                    <p {...editableTextPart(childBlock ? "text" : `glass${item.index}Text`)} className="mt-2 text-sm leading-6 text-[color-mix(in_srgb,var(--site-on-inverse)_70%,transparent)]" style={partStyle(childBlock ?? block, childBlock ? "text" : `glass${item.index}Text`)}>
                        {richTextContent(childBlock ?? block, childBlock ? "text" : `glass${item.index}Text`, item.text)}
                    </p>
                </>
            </StyledCardContentSlot>
        </article>
    );

    return (
        <SlotItemsWithFallback
            block={block}
            className="grid gap-4"
            renderItem={({ card, childBlock, itemPart }) => renderGlassCard({
                index: card.index,
                text: card.text,
                title: card.title,
            }, childBlock, itemPart)}
        >
            {cards.length > 0 ? (
            <div className="grid gap-4">
                {cards.map((item) => renderGlassCard(item))}
            </div>
            ) : null}
        </SlotItemsWithFallback>
    );
}

function IntroHeroSplitVisual({ block }: { block: ShopTemplateBlock }) {
    const metricValue = blockText(block, "stat1Value", "");
    const metricLabel = blockText(block, "stat1Label", "");
    const noteTitle = blockText(block, "glass1Title", "");
    const noteText = blockText(block, "glass1Text", "");
    const floatingItemClassName: SlotItemClassName = ({ childBlock, index }) => {
        const role = blockOptionText(childBlock, "floatingRole");

        return role === "splitNote" || index === 1
            ? "absolute right-4 top-4 z-20 max-w-[17rem] sm:-right-5 sm:-top-5"
            : "absolute bottom-4 left-4 z-20 w-[min(17rem,calc(100%-2rem))] sm:-bottom-6 sm:-left-6";
    };
    const renderFloatingItem: SlotRenderItem = ({ childBlock, index, itemPart }) => {
        const role = blockOptionText(childBlock, "floatingRole");
        const note = role === "splitNote" || index === 1;

        if (note) {
            return (
                <div className="rounded-[22px] bg-[var(--site-surface)] p-4 shadow-[0_22px_54px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]">
                    <p {...editableTextPart("title")} className="text-sm font-black text-[var(--site-text)]" style={partStyle(childBlock, "title")}>{richTextContent(childBlock, "title", "")}</p>
                    <p {...editableTextPart("text")} className="mt-1 text-xs font-bold text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]" style={partStyle(childBlock, "text")}>{richTextContent(childBlock, "text", "")}</p>
                    <SlotCardInnerContent block={childBlock} itemPart={itemPart} />
                </div>
            );
        }

        return (
            <div className="flex items-center gap-4 rounded-[24px] bg-[color-mix(in_srgb,var(--site-surface)_90%,transparent)] p-4 shadow-[0_26px_70px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)] backdrop-blur-xl">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[var(--site-primary)]/10 text-[var(--site-primary)]">
                    <Icon name="navigation" className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                    <span {...editableTextPart("title")} className="block text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]" style={partStyle(childBlock, "title")}>{richTextContent(childBlock, "title", "")}</span>
                    <span {...editableTextPart("statValue")} className="mt-1 block text-xl font-black text-[var(--site-text)]" style={partStyle(childBlock, "statValue")}>{richTextContent(childBlock, "statValue", "")}</span>
                </span>
            </div>
        );
    };

    return (
        <div className="relative mx-auto w-full max-w-2xl lg:max-w-none">
            <IntroHeroMediaSlot block={block} className="aspect-[4/3] bg-[var(--site-surface)] p-0" imageClassName="object-cover">
                <IntroHeroImage block={block} className="aspect-[4/3] bg-[var(--site-surface)] p-0" imageClassName="object-cover" />
            </IntroHeroMediaSlot>
            <SlotWithFallback
                block={block}
                slotKey="floatingCards"
                className="absolute inset-0 z-20"
                emptyClassName="absolute inset-0 z-20"
                itemClassName={floatingItemClassName}
                renderItem={renderFloatingItem}
            >
                <>
                    {metricValue || metricLabel ? (
                        <div className="absolute bottom-4 left-4 z-20 flex w-[min(17rem,calc(100%-2rem))] items-center gap-4 rounded-[24px] bg-[color-mix(in_srgb,var(--site-surface)_90%,transparent)] p-4 shadow-[0_26px_70px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)] backdrop-blur-xl sm:-bottom-6 sm:-left-6">
                            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[var(--site-primary)]/10 text-[var(--site-primary)]">
                                <Icon name="navigation" className="h-5 w-5" />
                            </span>
                            <span className="min-w-0">
                                <span {...editableTextPart("stat1Label")} className="block text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]" style={partStyle(block, "stat1Label")}>{richTextContent(block, "stat1Label", metricLabel)}</span>
                                <span {...editableTextPart("stat1Value")} className="mt-1 block text-xl font-black text-[var(--site-text)]" style={partStyle(block, "stat1Value")}>{richTextContent(block, "stat1Value", metricValue)}</span>
                            </span>
                        </div>
                    ) : null}
                    {noteTitle || noteText ? (
                        <div className="absolute right-4 top-4 z-20 max-w-[17rem] rounded-[22px] bg-[var(--site-surface)] p-4 shadow-[0_22px_54px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)] sm:-right-5 sm:-top-5">
                            <p {...editableTextPart("glass1Title")} className="text-sm font-black text-[var(--site-text)]" style={partStyle(block, "glass1Title")}>{richTextContent(block, "glass1Title", noteTitle)}</p>
                            <p {...editableTextPart("glass1Text")} className="mt-1 text-xs font-bold text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]" style={partStyle(block, "glass1Text")}>{richTextContent(block, "glass1Text", noteText)}</p>
                        </div>
                    ) : null}
                </>
            </SlotWithFallback>
        </div>
    );
}

function IntroHeroEditorial({ block }: { block: ShopTemplateBlock }) {
    const primaryLabel = blockText(block, "primaryLabel", "");
    const contentReplacesFallback = useSlotReplacesFallback(block, "content");
    const hasContentSlot = slotChildren(block, "content").length > 0 || contentReplacesFallback;
    const hasColumnActions = introHeroContentHasColumnActions(block);
    const renderEditorialCopy: SlotRenderItem = ({ childBlock, itemPart }) => {
        if (childBlock.type === "column") {
            return (
                <IntroHeroColumnBlock
                    block={childBlock}
                    partPrefix={itemPart}
                    actionStyle="editorial"
                />
            );
        }

        if (childBlock.type === "badge" || childBlock.type === "ctaBadge") {
            return (
                <span
                    {...editablePart("primaryButton")}
                    className="inline-flex rounded-full border border-[var(--site-border)] px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-[var(--site-muted)]"
                    style={partStyle(childBlock, "primaryButton")}
                >
                    <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
                </span>
            );
        }

        if (childBlock.type === "heading") {
            return (
                <h1
                    {...editableTextPart("title")}
                    className="mt-7 max-w-3xl text-5xl font-medium leading-[0.96] text-[var(--site-text)] sm:text-7xl lg:text-[88px]"
                    style={partStyle(childBlock, "title")}
                >
                    {richTextContent(childBlock, "text", blockText(childBlock, "title", ""))}
                </h1>
            );
        }

        if (childBlock.type === "paragraph" || childBlock.type === "text") {
            return (
                <p
                    {...editableTextPart("text")}
                    className="mt-6 max-w-2xl text-[16px] leading-relaxed text-[color-mix(in_srgb,var(--site-text)_55%,transparent)] md:text-[18px]"
                    style={partStyle(childBlock, "text")}
                >
                    {richTextContent(childBlock, "text", "")}
                </p>
            );
        }

        return <AtomicBlock block={childBlock} partPrefix={itemPart} />;
    };
    const renderEditorialAction: SlotRenderItem = ({ childBlock, itemPart }) => (
        childBlock.type === "button" ? (
            <a
                {...editablePart("primaryButton")}
href={functionalStorefrontHref(blockText(childBlock, "href", ""))}
                className="group mt-10 inline-flex items-center gap-3 text-base font-black text-[var(--site-text)] transition hover:text-[var(--site-primary)]"
                style={partStyle(childBlock, "primaryButton")}
            >
                <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
                <span aria-hidden="true" className="transition-transform group-hover:translate-x-1">-&gt;</span>
            </a>
        ) : <AtomicBlock block={childBlock} partPrefix={itemPart} />
    );

    return (
        <section {...blockInteractionAttributes(block)} className="px-5 py-[120px] sm:px-6" style={mergeStyles(blockStyle(block), interactionStyle(block))}>
            <div className="mx-auto grid max-w-[var(--site-container)] gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(360px,1fr)] lg:items-center">
                <div>
                    {hasContentSlot ? null : <IntroHeroBadge block={block} />}
                    <SlotWithFallback
                        block={block}
                        slotKey="content"
                        className="grid gap-0"
                        emptyClassName="mt-4 grid gap-3"
                        renderItem={renderEditorialCopy}
                    >
                        <h1
                            {...editableTextPart("title")}
                            className="mt-7 max-w-3xl text-5xl font-medium leading-[0.96] text-[var(--site-text)] sm:text-7xl lg:text-[88px]"
                            style={partStyle(block, "title")}
                        >
                            {richTextContent(block, "title", "Minimalismus, který nechá vyniknout produkt.")}
                        </h1>
                        <IntroHeroText block={block} />
                    </SlotWithFallback>
                    {hasColumnActions ? null : (
                        <SlotWithFallback
                            block={block}
                            slotKey="actions"
                            className="flex flex-col items-start"
                            emptyClassName="mt-4 flex flex-col items-start gap-3"
                            itemClassName="w-fit max-w-full"
                            renderItem={renderEditorialAction}
                        >
                            {primaryLabel ? (
                                <a
                                    {...editablePart("primaryButton")}
                            href={functionalStorefrontHref(blockText(block, "primaryUrl", ""))}
                                    className="group mt-10 inline-flex items-center gap-3 text-base font-black text-[var(--site-text)] transition hover:text-[var(--site-primary)]"
                                    style={partStyle(block, "primaryButton")}
                                >
                                    <span {...editableTextPart("primaryButton")}>{richTextContent(block, "primaryLabel", primaryLabel)}</span>
                                    <span aria-hidden="true" className="transition-transform group-hover:translate-x-1">-&gt;</span>
                                </a>
                            ) : null}
                        </SlotWithFallback>
                    )}
                </div>
                <IntroHeroMediaSlot block={block} className="aspect-[3/4] bg-[var(--site-surface)] p-0 shadow-[0_24px_64px_var(--site-shadow-color)]" imageClassName="object-cover">
                    <IntroHeroImage block={block} className="aspect-[3/4] bg-[var(--site-surface)] p-0 shadow-[0_24px_64px_var(--site-shadow-color)]" imageClassName="object-cover" />
                </IntroHeroMediaSlot>
            </div>
        </section>
    );
}

function IntroHeroFullscreen({ block }: { block: ShopTemplateBlock }) {
    return <IntroHeroBackgroundCampaign block={block} />;
}

function IntroHeroParallax({ block }: { block: ShopTemplateBlock }) {
    const editorMode = useContext(ShopEditorModeContext);
    const image = blockText(block, "imageUrl", editorMode ? "https://images.unsplash.com/photo-1525507119028-ed4c629a60a3?auto=format&fit=crop&w=1800&q=88" : "");
    const hasMediaSlot = slotChildren(block, "media").length > 0;
    const primaryLabel = blockText(block, "primaryLabel", "");
    const secondaryLabel = blockText(block, "secondaryLabel", "");
    const hasColumnActions = introHeroContentHasColumnActions(block);
    const backgroundPosition = imageOptionText(block, "image", "objectPosition") || "center";
    const sectionStyle = mergeStyles(
        introHeroFrameStyle(block),
        {
            backgroundImage: hasMediaSlot || !image ? undefined : `url("${image}")`,
            backgroundPosition,
            backgroundSize: "cover",
        },
    );
    const renderCopy: SlotRenderItem = ({ childBlock, itemPart }) => {
        if (childBlock.type === "column") {
            return (
                <IntroHeroColumnBlock
                    block={childBlock}
                    partPrefix={itemPart}
                    actionStyle="parallax"
                    centered
                />
            );
        }

        if (childBlock.type === "heading") {
            return (
                <h1
                    {...editableTextPart("title")}
                    className="text-[48px] font-light leading-[1.05] text-[var(--site-background)] md:text-[64px] lg:text-[78px]"
                    style={partStyle(childBlock, "title")}
                >
                    {richTextContent(childBlock, "text", blockText(childBlock, "title", ""))}
                </h1>
            );
        }

        if (childBlock.type === "paragraph" || childBlock.type === "text") {
            return (
                <p
                    {...editableTextPart("text")}
                    className="mt-6 max-w-[650px] text-[16px] font-light leading-relaxed text-[var(--site-background)] opacity-90 md:text-[18px]"
                    style={partStyle(childBlock, "text")}
                >
                    {richTextContent(childBlock, "text", "")}
                </p>
            );
        }

        return <AtomicBlock block={childBlock} partPrefix={itemPart} />;
    };
    const renderAction: SlotRenderItem = ({ childBlock, index, itemPart }) => {
        if (childBlock.type !== "button") {
            return <AtomicBlock block={childBlock} partPrefix={itemPart} />;
        }

        return (
            <a
                {...editablePart("primaryButton")}
href={functionalStorefrontHref(blockText(childBlock, "href", ""))}
                className={classNames(
                    "inline-flex min-h-[48px] w-full items-center justify-center rounded-md px-5 text-[16px] font-medium transition duration-200 hover:opacity-90 sm:w-auto",
                    index === 0
                        ? "bg-[var(--site-background)] text-[var(--site-text)]"
                        : "border border-[var(--site-background)] bg-transparent text-[var(--site-background)]",
                )}
                style={partStyle(childBlock, "primaryButton")}
            >
                <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
            </a>
        );
    };

    return (
        <section
            {...blockInteractionAttributes(block)}
            {...imageHoverFrameAttributes(block, "image", "scale-sm")}
            className="relative isolate flex h-[650px] min-h-[650px] w-full items-center justify-center overflow-hidden bg-[var(--site-text)] text-center"
            style={sectionStyle}
        >
            <SlotWithFallback
                block={block}
                slotKey="media"
                className="absolute inset-0 z-0"
                emptyClassName="absolute inset-0 z-0"
                itemClassName="absolute inset-0"
                renderItem={({ childBlock, itemPart }) => (
                    childBlock.type === "image" || childBlock.type === "card"
                        ? <IntroHeroImage block={childBlock} className="h-full w-full rounded-none bg-transparent p-0 shadow-none ring-0" imageClassName="object-cover" />
                        : <AtomicBlock block={childBlock} partPrefix={itemPart} />
                )}
            >
                {image ? <img
                    {...editablePart("image")}
                    data-shop-image-hover-target="true"
                    src={image}
                    alt={blockText(block, "imageAlt", blockText(block, "alt", ""))}
                    title={blockText(block, "imageTitle", "") || undefined}
                    className="absolute inset-0 z-0 h-full w-full object-cover"
                    style={imageInteractivePartStyle(block, "image", "scale-sm")}
                /> : null}
            </SlotWithFallback>
            <div className="pointer-events-none absolute inset-0 z-10 bg-[var(--site-text)]/45" />
            <div className="relative z-20 flex w-full max-w-[900px] flex-col items-center justify-center px-5 text-center">
                <SlotWithFallback
                    block={block}
                    slotKey="content"
                    className="grid justify-items-center gap-0 text-center"
                    emptyClassName="mt-4 grid justify-items-center gap-3 text-center"
                    renderItem={renderCopy}
                >
                    <h1
                        {...editableTextPart("title")}
                        className="text-[48px] font-light leading-[1.05] text-[var(--site-background)] md:text-[64px] lg:text-[78px]"
                        style={partStyle(block, "title")}
                    >
                        {richTextContent(block, "title", editorMode ? "Color Matters" : "")}
                    </h1>
                    <p
                        {...editableTextPart("text")}
                        className="mt-6 max-w-[650px] text-[16px] font-light leading-relaxed text-[var(--site-background)] opacity-90 md:text-[18px]"
                        style={partStyle(block, "text")}
                    >
                        {richTextContent(block, "text", editorMode ? "Color trends are already waiting to spring into action for the next summer." : "")}
                    </p>
                </SlotWithFallback>
                {hasColumnActions ? null : (
                    <SlotWithFallback
                        block={block}
                        slotKey="actions"
                        className="mt-14 flex w-full flex-col items-center justify-center gap-2 sm:w-auto sm:flex-row"
                        emptyClassName="mt-4 flex w-full flex-col items-center justify-center gap-2 sm:w-auto sm:flex-row"
                        itemClassName="w-full max-w-full sm:w-auto"
                        renderItem={renderAction}
                    >
                        {primaryLabel || secondaryLabel ? (
                            <>
                            {primaryLabel ? (
                                <a
                                    {...editablePart("primaryButton")}
                            href={functionalStorefrontHref(blockText(block, "primaryUrl", ""))}
                                    className="inline-flex min-h-[48px] w-full items-center justify-center rounded-md bg-[var(--site-background)] px-5 text-[16px] font-medium text-[var(--site-text)] transition duration-200 hover:opacity-90 sm:w-auto"
                                    style={partStyle(block, "primaryButton")}
                                >
                                    <span {...editableTextPart("primaryButton")}>{richTextContent(block, "primaryLabel", primaryLabel)}</span>
                                </a>
                            ) : null}
                            {secondaryLabel ? (
                                <a
                                    {...editablePart("secondaryButton")}
                                    href={functionalStorefrontHref(blockText(block, "secondaryUrl", ""))}
                                    className="inline-flex min-h-[48px] w-full items-center justify-center rounded-md border border-[var(--site-background)] bg-transparent px-5 text-[16px] font-medium text-[var(--site-background)] transition duration-200 hover:opacity-90 sm:w-auto"
                                    style={partStyle(block, "secondaryButton")}
                                >
                                    <span {...editableTextPart("secondaryButton")}>{richTextContent(block, "secondaryLabel", secondaryLabel)}</span>
                                </a>
                            ) : null}
                            </>
                        ) : null}
                    </SlotWithFallback>
                )}
            </div>
        </section>
    );
}

function IntroHeroBackgroundCampaign({ block }: { block: ShopTemplateBlock }) {
    const editorMode = useContext(ShopEditorModeContext);
    const image = blockText(block, "imageUrl", editorMode ? "https://images.unsplash.com/photo-1525507119028-ed4c629a60a3?auto=format&fit=crop&w=1800&q=88" : "");
    const sectionStyle = introHeroFrameStyle(block);
    const primaryLabel = blockText(block, "primaryLabel", "");
    const secondaryLabel = blockText(block, "secondaryLabel", "");
    const badge = blockText(block, "badge", "");
    const hasColumnActions = introHeroContentHasColumnActions(block);
    const renderCopy: SlotRenderItem = ({ childBlock, itemPart }) => {
        if (childBlock.type === "column") {
            return (
                <IntroHeroColumnBlock
                    block={childBlock}
                    partPrefix={itemPart}
                    actionStyle="campaign"
                    centered
                />
            );
        }

        if (childBlock.type === "badge" || childBlock.type === "ctaBadge") {
            return (
                <span
                    {...editablePart("primaryButton")}
                className="mb-4 text-sm font-semibold uppercase text-[color-mix(in_srgb,var(--site-on-inverse)_80%,transparent)]"
                style={partStyle(childBlock, "primaryButton")}
            >
                    <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
                </span>
            );
        }

        if (childBlock.type === "heading") {
            return (
                <h1
                    {...editableTextPart("title")}
                    className="text-[48px] font-light leading-[1.05] text-[var(--site-on-inverse)] md:text-[64px] lg:text-[78px]"
                    style={partStyle(childBlock, "title")}
                >
                    {richTextContent(childBlock, "text", blockText(childBlock, "title", ""))}
                </h1>
            );
        }

        if (childBlock.type === "paragraph" || childBlock.type === "text") {
            return (
                <p
                    {...editableTextPart("text")}
                    className="mt-6 max-w-[650px] text-[16px] font-light leading-relaxed text-[color-mix(in_srgb,var(--site-on-inverse)_90%,transparent)] md:text-[18px]"
                    style={partStyle(childBlock, "text")}
                >
                    {richTextContent(childBlock, "text", "")}
                </p>
            );
        }

        return <AtomicBlock block={childBlock} partPrefix={itemPart} />;
    };
    const renderAction: SlotRenderItem = ({ childBlock, index, itemPart }) => {
        if (childBlock.type !== "button") {
            return <AtomicBlock block={childBlock} partPrefix={itemPart} />;
        }

        return (
            <a
                {...editablePart("primaryButton")}
href={functionalStorefrontHref(blockText(childBlock, "href", ""))}
                className={classNames(
                    "inline-flex min-h-[48px] w-full items-center justify-center rounded-md px-5 text-[16px] font-medium transition-colors duration-200 sm:w-auto",
                    index === 0
                        ? "bg-[var(--site-surface)] text-[var(--site-text)] hover:bg-[color-mix(in_srgb,var(--site-surface)_90%,transparent)]"
                        : "border border-[var(--site-on-inverse)] bg-transparent text-[var(--site-on-inverse)] hover:bg-[color-mix(in_srgb,var(--site-surface)_10%,transparent)]",
                )}
                style={partStyle(childBlock, "primaryButton")}
            >
                <span {...editableTextPart("primaryButton")}>{richTextContent(childBlock, "label", "")}</span>
            </a>
        );
    };

    return (
        <section
            {...blockInteractionAttributes(block)}
            {...imageHoverFrameAttributes(block, "image", "scale-sm")}
            className="relative isolate flex h-[650px] min-h-[560px] w-full items-center justify-center overflow-hidden bg-[var(--site-inverse)] px-5 py-24 text-center md:h-[665px]"
            style={sectionStyle}
        >
            <SlotWithFallback
                block={block}
                slotKey="media"
                className="absolute inset-0 z-0"
                emptyClassName="absolute inset-0 z-0"
                itemClassName="absolute inset-0"
                renderItem={({ childBlock, itemPart }) => (
                    childBlock.type === "image" || childBlock.type === "card"
                        ? <IntroHeroImage block={childBlock} className="h-full w-full rounded-none bg-transparent p-0 shadow-none ring-0" imageClassName="object-cover" />
                        : <AtomicBlock block={childBlock} partPrefix={itemPart} />
                )}
            >
                {image ? <img
                    {...editablePart("image")}
                    data-shop-image-hover-target="true"
                    src={image}
                    alt={blockText(block, "imageAlt", blockText(block, "alt", ""))}
                    title={blockText(block, "imageTitle", "") || undefined}
                    className="absolute inset-0 z-0 h-full w-full object-cover"
                    style={imageInteractivePartStyle(block, "image", "scale-sm")}
                /> : null}
            </SlotWithFallback>
            <div className="pointer-events-none absolute inset-0 z-10 bg-[color-mix(in_srgb,var(--site-text)_45%,transparent)]" />
            <div className="relative z-20 flex w-full max-w-[920px] flex-col items-center justify-center px-1 text-center">
                <SlotWithFallback
                    block={block}
                    slotKey="content"
                    className="grid justify-items-center gap-0 text-center"
                    emptyClassName="mt-4 grid justify-items-center gap-3 text-center"
                    renderItem={renderCopy}
                >
                    {badge ? (
                        <span
                            {...editableTextPart("badge")}
                            className="mb-4 text-sm font-semibold uppercase text-[color-mix(in_srgb,var(--site-on-inverse)_80%,transparent)]"
                            style={partStyle(block, "badge")}
                        >
                            {richTextContent(block, "badge", badge)}
                        </span>
                    ) : null}
                    <h1
                        {...editableTextPart("title")}
                        className="text-[48px] font-light leading-[1.05] text-[var(--site-on-inverse)] md:text-[64px] lg:text-[78px]"
                        style={partStyle(block, "title")}
                    >
                        {richTextContent(block, "title", editorMode ? "Color Matters" : "")}
                    </h1>
                    <p
                        {...editableTextPart("text")}
                        className="mt-6 max-w-[650px] text-[16px] font-light leading-relaxed text-[color-mix(in_srgb,var(--site-on-inverse)_90%,transparent)] md:text-[18px]"
                        style={partStyle(block, "text")}
                    >
                        {richTextContent(block, "text", editorMode ? "Color trends are already waiting to spring into action for the next summer." : "")}
                    </p>
                </SlotWithFallback>
                {hasColumnActions ? null : (
                    <SlotWithFallback
                        block={block}
                        slotKey="actions"
                        className="mt-14 flex w-full flex-col items-center justify-center gap-2 sm:w-auto sm:flex-row"
                        emptyClassName="mt-4 flex w-full flex-col items-center justify-center gap-2 sm:w-auto sm:flex-row"
                        itemClassName="w-full max-w-full sm:w-auto"
                        renderItem={renderAction}
                    >
                        {primaryLabel || secondaryLabel ? (
                            <>
                            {primaryLabel ? (
                                <a
                                    {...editablePart("primaryButton")}
                            href={functionalStorefrontHref(blockText(block, "primaryUrl", ""))}
                                    className="inline-flex min-h-[48px] w-full items-center justify-center rounded-md bg-[var(--site-surface)] px-5 text-[16px] font-medium text-[var(--site-text)] transition-colors duration-200 hover:bg-[color-mix(in_srgb,var(--site-surface)_90%,transparent)] sm:w-auto"
                                    style={partStyle(block, "primaryButton")}
                                >
                                    <span {...editableTextPart("primaryButton")}>{richTextContent(block, "primaryLabel", primaryLabel)}</span>
                                </a>
                            ) : null}
                            {secondaryLabel ? (
                                <a
                                    {...editablePart("secondaryButton")}
                                    href={functionalStorefrontHref(blockText(block, "secondaryUrl", ""))}
                                    className="inline-flex min-h-[48px] w-full items-center justify-center rounded-md border border-[var(--site-on-inverse)] bg-transparent px-5 text-[16px] font-medium text-[var(--site-on-inverse)] transition-colors duration-200 hover:bg-[color-mix(in_srgb,var(--site-surface)_10%,transparent)] sm:w-auto"
                                    style={partStyle(block, "secondaryButton")}
                                >
                                    <span {...editableTextPart("secondaryButton")}>{richTextContent(block, "secondaryLabel", secondaryLabel)}</span>
                                </a>
                            ) : null}
                            </>
                        ) : null}
                    </SlotWithFallback>
                )}
            </div>
        </section>
    );
}

function IntroHeroCollage({ block }: { block: ShopTemplateBlock }) {
    const quote = blockText(block, "glass1Text", "");
    const author = blockText(block, "glass1Title", "");
    const authorMeta = blockText(block, "trust1Label", "");
    const quoteItemClassName = "absolute bottom-[8%] right-[4%] z-30 w-[min(19rem,80%)]";
    const collageMediaItemClassName: SlotItemClassName = ({ index }) => (
        index === 1
            ? "absolute bottom-0 left-0 z-20 aspect-square w-[52%]"
            : index === 2
              ? "absolute left-[10%] top-[18%] z-0 aspect-[3/4] w-[36%]"
              : "absolute right-0 top-0 z-10 aspect-[4/5] w-[58%]"
    );
    const collageMediaClassName = (index: number) => (
        index === 2
            ? "h-full w-full bg-[var(--site-surface)] p-0 shadow-[0_20px_54px_var(--site-shadow-color)]"
            : "h-full w-full bg-[var(--site-surface)] p-0 shadow-[0_28px_76px_var(--site-shadow-color)]"
    );
    const renderCollageMedia: SlotRenderItem = ({ childBlock, index, itemPart }) => (
        childBlock.type === "image" || childBlock.type === "card"
            ? <IntroHeroImage block={childBlock} className={collageMediaClassName(index)} imageClassName="object-cover" />
            : <AtomicBlock block={childBlock} partPrefix={itemPart} />
    );
    const renderQuote: SlotRenderItem = ({ childBlock, itemPart }) => (
        <article className="rounded-[24px] bg-[color-mix(in_srgb,var(--site-surface)_92%,transparent)] p-5 shadow-[0_32px_74px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)] backdrop-blur-xl">
            <div className="flex gap-1 text-[var(--site-accent)]" aria-hidden="true">
                <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
            </div>
            <p {...editableTextPart("text")} className="mt-3 text-sm font-semibold leading-6 text-[color-mix(in_srgb,var(--site-text)_75%,transparent)]" style={partStyle(childBlock, "text")}>{richTextContent(childBlock, "text", "")}</p>
            <div className="mt-4 flex items-center gap-3">
                <span className="h-9 w-9 rounded-full bg-[var(--site-primary)]/12 ring-1 ring-[var(--site-primary)]/15" />
                <span>
                    <span {...editableTextPart("title")} className="block text-xs font-black text-[var(--site-text)]" style={partStyle(childBlock, "title")}>{richTextContent(childBlock, "title", "")}</span>
                    <span {...editableTextPart("label")} className="mt-0.5 block text-[10px] font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]" style={partStyle(childBlock, "label")}>{richTextContent(childBlock, "label", "")}</span>
                </span>
            </div>
            <SlotCardInnerContent block={childBlock} itemPart={itemPart} />
        </article>
    );

    return (
        <section {...blockInteractionAttributes(block)} className="overflow-hidden px-5 py-[120px] sm:px-6" style={mergeStyles(blockStyle(block), interactionStyle(block))}>
            <div className="mx-auto grid max-w-[var(--site-container)] gap-12 lg:grid-cols-[minmax(0,0.72fr)_minmax(520px,1fr)] lg:items-center">
                <div>
                    <IntroHeroCopy block={block} showBadge={false} />
                    <IntroHeroActions block={block} />
                </div>
                <div className="relative h-[560px] sm:h-[620px]">
                    <SlotWithFallback
                        block={block}
                        slotKey="media"
                        className="absolute inset-0"
                        emptyClassName="absolute inset-0"
                        itemClassName={collageMediaItemClassName}
                        renderItem={renderCollageMedia}
                    >
                        <>
                            <IntroHeroImage
                                block={block}
                                className="absolute right-0 top-0 z-10 aspect-[4/5] w-[58%] bg-[var(--site-surface)] p-0 shadow-[0_28px_76px_var(--site-shadow-color)]"
                                imageClassName="object-cover"
                            />
                            <IntroHeroImage
                                block={block}
                                part="image2"
                                className="absolute bottom-0 left-0 z-20 aspect-square w-[52%] bg-[var(--site-surface)] p-0 shadow-[0_28px_76px_var(--site-shadow-color)]"
                                imageClassName="object-cover"
                            />
                            <IntroHeroImage
                                block={block}
                                part="image3"
                                className="absolute left-[10%] top-[18%] z-0 aspect-[3/4] w-[36%] bg-[var(--site-surface)] p-0 shadow-[0_20px_54px_var(--site-shadow-color)]"
                                imageClassName="object-cover"
                            />
                        </>
                    </SlotWithFallback>
                    <SlotWithFallback
                        block={block}
                        slotKey="floatingCards"
                        className="absolute inset-0 z-30"
                        emptyClassName="absolute inset-0 z-30"
                        itemClassName={quoteItemClassName}
                        renderItem={renderQuote}
                    >
                        {quote || author ? (
                            <article className={`${quoteItemClassName} rounded-[24px] bg-[color-mix(in_srgb,var(--site-surface)_92%,transparent)] p-5 shadow-[0_32px_74px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)] backdrop-blur-xl`}>
                                <div className="flex gap-1 text-[var(--site-accent)]" aria-hidden="true">
                                    <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                                </div>
                                <p {...editableTextPart("glass1Text")} className="mt-3 text-sm font-semibold leading-6 text-[color-mix(in_srgb,var(--site-text)_75%,transparent)]" style={partStyle(block, "glass1Text")}>{richTextContent(block, "glass1Text", quote)}</p>
                                <div className="mt-4 flex items-center gap-3">
                                    <span className="h-9 w-9 rounded-full bg-[var(--site-primary)]/12 ring-1 ring-[var(--site-primary)]/15" />
                                    <span>
                                        <span {...editableTextPart("glass1Title")} className="block text-xs font-black text-[var(--site-text)]" style={partStyle(block, "glass1Title")}>{richTextContent(block, "glass1Title", author)}</span>
                                        <span {...editableTextPart("trust1Label")} className="mt-0.5 block text-[10px] font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]" style={partStyle(block, "trust1Label")}>{richTextContent(block, "trust1Label", authorMeta)}</span>
                                    </span>
                                </div>
                            </article>
                        ) : null}
                    </SlotWithFallback>
                </div>
            </div>
        </section>
    );
}

function IntroHeroVideo({ block }: { block: ShopTemplateBlock }) {
    const message = useShopStorefrontMessage();
    const image = blockText(block, "imageUrl", "");
    const videoUrl = blockText(block, "videoUrl", blockText(block, "primaryUrl", "#video"));
    const renderPlayButton: SlotRenderItem = ({ childBlock }) => (
        <a
            {...editablePart("primaryButton")}
            href={blockText(childBlock, "href", videoUrl)}
            className="grid h-20 w-20 place-items-center rounded-full bg-[var(--site-surface)] text-[var(--site-primary)] shadow-[0_22px_58px_var(--site-shadow-color)] transition"
            style={partStyle(childBlock, "primaryButton")}
            aria-label={blockText(childBlock, "label", message("ui.video.watch"))}
        >
            <span className="ml-1 h-0 w-0 border-y-[10px] border-y-transparent border-l-[16px] border-l-current" />
        </a>
    );

    return (
        <div className="mx-auto max-w-[var(--site-container)]">
            <div className="relative grid min-h-[560px] overflow-hidden rounded-[32px] bg-[var(--site-inverse)] p-6 shadow-[0_30px_86px_var(--site-shadow-color)] sm:p-10 lg:min-h-[640px] lg:p-14">
                <SlotWithFallback
                    block={block}
                    slotKey="media"
                    className="absolute inset-0"
                    emptyClassName="absolute inset-0"
                    itemClassName="absolute inset-0"
                    renderItem={({ childBlock, itemPart }) => (
                        childBlock.type === "image" || childBlock.type === "card"
                            ? <IntroHeroImage block={childBlock} className="h-full w-full rounded-none bg-transparent p-0 shadow-none ring-0" imageClassName="object-cover" />
                            : <AtomicBlock block={childBlock} partPrefix={itemPart} />
                    )}
                >
                    {image ? (
                        <img
                            {...editablePart("image")}
                            data-shop-image-hover-target="true"
                            src={image}
                            alt={blockText(block, "imageAlt", blockText(block, "alt", ""))}
                            title={blockText(block, "imageTitle", "") || undefined}
                            className="absolute inset-0 h-full w-full object-cover"
                            style={imageInteractivePartStyle(block, "image", "scale-sm")}
                        />
                    ) : null}
                </SlotWithFallback>
                <div className="absolute inset-0 bg-gradient-to-r from-[color-mix(in_srgb,var(--site-text)_75%,transparent)] via-[color-mix(in_srgb,var(--site-text)_40%,transparent)] to-[color-mix(in_srgb,var(--site-text)_15%,transparent)]" />
                <div className="relative z-10 flex h-full flex-col justify-center">
                    <div className="max-w-3xl">
                        <IntroHeroCopy block={block} dark />
                        <IntroHeroActions block={block} />
                    </div>
                    <SlotWithFallback
                        block={block}
                        slotKey="floatingCards"
                        className="mt-10 w-fit lg:absolute lg:right-14 lg:top-1/2 lg:mt-0 lg:-translate-y-1/2"
                        emptyClassName="mt-10 w-fit lg:absolute lg:right-14 lg:top-1/2 lg:mt-0 lg:-translate-y-1/2"
                        itemClassName="w-fit"
                        renderItem={renderPlayButton}
                    >
                        <a
                            {...editablePart("playButton")}
                            href={videoUrl}
                            className="grid h-20 w-20 place-items-center rounded-full bg-[var(--site-surface)] text-[var(--site-primary)] shadow-[0_22px_58px_var(--site-shadow-color)] transition"
                            style={partStyle(block, "playButton")}
                            aria-label={blockText(block, "primaryLabel", message("ui.video.watch"))}
                        >
                            <span className="ml-1 h-0 w-0 border-y-[10px] border-y-transparent border-l-[16px] border-l-current" />
                        </a>
                    </SlotWithFallback>
                </div>
            </div>
        </div>
    );
}

function IntroHeroPreview({ block }: { block: ShopTemplateBlock }) {
    const variant = introHeroVariant(block);
    const dark = introHeroIsDark(variant);
    const sectionStyle = mergeStyles(blockStyle(block), interactionStyle(block));

    if (variant === "fullscreen") {
        return <IntroHeroFullscreen block={block} />;
    }

    if (variant === "parallax") {
        return <IntroHeroParallax block={block} />;
    }

    if (variant === "editorial") {
        return <IntroHeroEditorial block={block} />;
    }

    if (variant === "collage") {
        return <IntroHeroCollage block={block} />;
    }

    if (variant === "video") {
        return (
            <section {...blockInteractionAttributes(block)} className="px-5 py-12 sm:px-6" style={sectionStyle}>
                <IntroHeroVideo block={block} />
            </section>
        );
    }

    if (variant === "minimal") {
        return (
            <section {...blockInteractionAttributes(block)} className="px-5 py-[200px] sm:px-6" style={sectionStyle}>
                <div className="mx-auto max-w-[var(--site-container)]">
                    <div className="mx-auto max-w-5xl text-center">
                        <IntroHeroCopy block={block} centered showBadge={false} />
                        <IntroHeroActions block={block} centered />
                    </div>
                </div>
            </section>
        );
    }

    if (variant === "features") {
        return (
            <section {...blockInteractionAttributes(block)} className="px-5 py-16 sm:px-6 lg:py-24" style={sectionStyle}>
                <div className="mx-auto max-w-[var(--site-container)]">
                    <div className="mx-auto max-w-4xl text-center">
                        <IntroHeroCopy block={block} centered />
                        <IntroHeroActions block={block} centered />
                    </div>
                    <IntroHeroFeatureCards block={block} />
                </div>
            </section>
        );
    }

    if (variant === "gradient") {
        return (
            <section {...blockInteractionAttributes(block)} className="px-5 py-16 text-[var(--site-on-inverse)] sm:px-6 lg:py-24" style={sectionStyle}>
                <div className="mx-auto grid max-w-[var(--site-container)] gap-12 lg:grid-cols-[minmax(0,0.95fr)_minmax(360px,0.62fr)] lg:items-center">
                    <div>
                        <IntroHeroCopy block={block} dark />
                        <IntroHeroActions block={block} />
                    </div>
                    <IntroHeroGlassCards block={block} />
                </div>
            </section>
        );
    }

    if (variant === "product") {
        return (
            <section {...blockInteractionAttributes(block)} className="px-5 py-[120px] sm:px-6" style={sectionStyle}>
                <div className="mx-auto grid max-w-[var(--site-container)] gap-[72px] lg:grid-cols-[minmax(0,1fr)_minmax(420px,1fr)] lg:items-center">
                    <div>
                        <IntroHeroCopy block={block} showBadge={false} />
                        <IntroHeroActions block={block} />
                        <IntroHeroTrustBadges block={block} />
                    </div>
                    <div className="relative flex min-h-[600px] items-center justify-center rounded-[40px] bg-[var(--site-surface)] p-6 shadow-[0_28px_76px_var(--site-shadow-color)] ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)] sm:p-10">
                        <div className="relative z-10 mx-auto w-full max-w-[88%]">
                            <IntroHeroMediaSlot block={block} className="aspect-[4/3] bg-transparent p-0 shadow-none ring-0" imageClassName="rounded-[28px] object-cover">
                                <IntroHeroImage block={block} className="aspect-[4/3] bg-transparent p-0 shadow-none ring-0" imageClassName="rounded-[28px] object-cover" />
                            </IntroHeroMediaSlot>
                        </div>
                        <IntroHeroProductFloatingLabels block={block} />
                    </div>
                </div>
            </section>
        );
    }

    if (variant === "split") {
        return (
            <section {...blockInteractionAttributes(block)} className="px-5 py-[120px] sm:px-6" style={sectionStyle}>
                <div className="mx-auto grid max-w-[var(--site-container)] gap-[72px] lg:grid-cols-[minmax(0,0.82fr)_minmax(420px,1fr)] lg:items-center">
                    <div>
                        <IntroHeroCopy block={block} />
                        <IntroHeroActions block={block} />
                        <IntroHeroStats block={block} />
                    </div>
                    <IntroHeroSplitVisual block={block} />
                </div>
            </section>
        );
    }

    return (
        <section {...blockInteractionAttributes(block)} className="px-5 py-[120px] sm:px-6" style={sectionStyle}>
            <div className="mx-auto max-w-[var(--site-container)]">
                <div className="mx-auto max-w-5xl text-center">
                    <IntroHeroCopy block={block} centered dark={dark} />
                    <IntroHeroActions block={block} centered />
                </div>
                <div className="mx-auto mt-24 max-w-[1040px]">
                    <IntroHeroMediaSlot block={block} className="aspect-[16/9] rounded-[32px] bg-[var(--site-surface)] p-0" imageClassName="object-cover">
                        <IntroHeroImage block={block} className="aspect-[16/9] rounded-[32px] bg-[var(--site-surface)] p-0" imageClassName="object-cover" />
                    </IntroHeroMediaSlot>
                </div>
            </div>
        </section>
    );
}

function RichContentPreview({ block, context }: { block: ShopTemplateBlock; context?: ShopPageContext | null }) {
    const message = useShopStorefrontMessage();
    const editorMode = useContext(ShopEditorModeContext);
    const hasGridComposition = slotChildren(block, SHOP_GRID_SLOT_KEY).length > 0;
    const contentItems = slotChildren(block, "content");
    const contentReplacesFallback = useSlotReplacesFallback(block, "content");

    if (hasGridComposition) {
        return null;
    }

    return (
        <section className="px-5 py-12 sm:px-6" style={blockVisualStyle(block)}>
            <div className="mx-auto max-w-[var(--site-container)] border border-[var(--site-border)] bg-[var(--site-surface)] p-8">
                {contentItems.length > 0 || contentReplacesFallback || editorMode ? (
                    <SlotBlockGroup
                        block={block}
                        slotKey="content"
                        className="grid gap-4"
                    />
                ) : (
                    <>
                        <h2 {...editableTextPart("title")} className="text-3xl font-black" style={partStyle(block, "title")}>{richTextContent(block, "title", context?.title ?? message("ui.block.systemPageFallback"))}</h2>
                        <p {...editableTextPart("text")} className="mt-4 max-w-3xl text-sm leading-7 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]" style={partStyle(block, "text")}>
                            {richTextContent(block, "text", context?.seoDescription ?? message("ui.block.systemPageFallback"))}
                        </p>
                    </>
                )}
            </div>
        </section>
    );
}

function lagunaProductPageFacets(
    facets: PublicCatalogFacet[],
    config: CatalogProductPageConfig,
): PublicCatalogFacet[] {
    return facets.filter((facet) => {
        const code = facet.code.trim().toLocaleLowerCase();
        const name = facet.attributeName?.trim().toLocaleLowerCase() ?? "";

        if (!config.showPriceFilter && code === "price") return false;
        if (!config.showTagsFilter && (code === "tag" || code === "tags" || name === "tag" || name === "tags")) return false;

        return true;
    });
}

function LagunaCatalogCategories({
    activeItemUuid,
    activePathUuids,
    collapsed,
    imageUrls = [],
    items,
    onNavigate,
    sidebar = false,
    style,
}: {
    activeItemUuid?: string | null;
    activePathUuids?: ReadonlySet<string>;
    collapsed: boolean;
    imageUrls?: readonly (string | null)[];
    items: readonly PublicShopMenuItem[];
    onNavigate?: CatalogNavigateHandler;
    sidebar?: boolean;
    style: CatalogCategoryStyle;
}) {
    const editorMode = useContext(ShopEditorModeContext);
    const message = useShopStorefrontMessage();
    const [expanded, setExpanded] = useState(!collapsed);
    const [expandedCategoryUuids, setExpandedCategoryUuids] = useState<Set<string>>(
        () => new Set(activePathUuids ?? (activeItemUuid ? [activeItemUuid] : [])),
    );
    const imageIndex = style === "imageIndex";
    const playfulIndex = style === "playfulIndex";
    const categoryIndex = imageIndex || playfulIndex;
    const hasNavigableDescendant = (item: PublicShopMenuItem): boolean => (
        functionalStorefrontHref(menuItemHref(item)) !== undefined
        || item.children.some(hasNavigableDescendant)
    );
    const linkedItems = items.filter((item) => (
        categoryIndex && sidebar
            ? hasNavigableDescendant(item)
            : functionalStorefrontHref(menuItemHref(item)) !== undefined
    ));

    useEffect(() => {
        setExpanded(!collapsed);
    }, [collapsed]);

    useEffect(() => {
        const nextActiveUuids = activePathUuids ?? (activeItemUuid ? new Set([activeItemUuid]) : null);

        if (!nextActiveUuids || nextActiveUuids.size === 0) return;

        setExpandedCategoryUuids((current) => {
            const hasEveryActiveUuid = Array.from(nextActiveUuids).every((uuid) => current.has(uuid));

            if (hasEveryActiveUuid) return current;

            return new Set([...current, ...nextActiveUuids]);
        });
    }, [activeItemUuid, activePathUuids]);

    if (linkedItems.length === 0) return null;

    const canCollapse = collapsed && linkedItems.length > 6;
    const visibleItems = canCollapse && !expanded ? linkedItems.slice(0, 6) : linkedItems;
    const visualCategories = style === "images"
        || style === "grid"
        || style === "largeImages"
        || (categoryIndex && !sidebar);
    const renderSidebarChildren = (children: readonly PublicShopMenuItem[], depth = 1): ReactNode => {
        const visibleChildren = children.filter(hasNavigableDescendant);

        if (visibleChildren.length === 0) return null;

        return (
            <div className={classNames(depth === 1 && "border-b border-[#dadada] py-2 pl-4", depth > 1 && "pl-3")}>
                {visibleChildren.map((child) => {
                    const childHref = functionalStorefrontHref(menuItemHref(child));
                    const childLabelClassName = classNames(
                        "block py-1.5 text-xs font-medium text-[color-mix(in_srgb,var(--site-text)_68%,transparent)] hover:text-black",
                        child.uuid === activeItemUuid && "font-black text-black",
                    );

                    return (
                        <div key={child.uuid} data-catalog-category-active={child.uuid === activeItemUuid ? "true" : undefined}>
                            {childHref ? (
                                <ShopStorefrontLink
                                    href={childHref}
                                    className={childLabelClassName}
                                    onClick={(event) => {
                                        if (editorMode) {
                                            event.preventDefault();
                                        } else {
                                            handleCatalogNavigateClick(event, childHref, onNavigate);
                                        }
                                    }}
                                >
                                    {child.label}
                                </ShopStorefrontLink>
                            ) : (
                                <span className={childLabelClassName}>{child.label}</span>
                            )}
                            {renderSidebarChildren(child.children, depth + 1)}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <nav
            aria-label={message("ui.search.categories")}
            className={classNames(
                "min-w-0",
                style === "tabs" && "border-b border-[var(--site-border)]",
                sidebar && !categoryIndex && "rounded-[24px] border border-[var(--site-border)] bg-[var(--site-surface)] p-3",
                sidebar && categoryIndex && "border-t border-[#dadada] bg-transparent",
            )}
            data-catalog-category-style={style}
        >
            {sidebar && categoryIndex ? (
                <h2 className="border-b border-[#dadada] py-4 text-lg font-black text-black">
                    {message("ui.search.categories")}
                </h2>
            ) : null}
            <div className={classNames(
                sidebar || style === "default" || style === "bordered" || style === "tabs" || style === "pills"
                    ? sidebar ? "grid grid-cols-1 gap-2" : "flex flex-wrap gap-2"
                    : "grid gap-3",
                !sidebar && style === "images" && "grid-cols-2 md:grid-cols-4 xl:grid-cols-6",
                !sidebar && style === "grid" && "grid-cols-2 md:grid-cols-4",
                !sidebar && style === "largeImages" && "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
                !sidebar && categoryIndex && "grid-cols-2 gap-5 md:grid-cols-4",
                sidebar && categoryIndex && "gap-0",
            )}>
                {visibleItems.map((item, index) => {
                    const href = functionalStorefrontHref(menuItemHref(item));
                    const initial = Array.from(item.label.trim())[0]?.toLocaleUpperCase() ?? "";
                    const imageUrl = imageUrls[index] ?? item.imageUrl ?? null;

                    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
                        if (editorMode) {
                            event.preventDefault();

                            return;
                        }

                        if (href) handleCatalogNavigateClick(event, href, onNavigate);
                    };
                    const categoryLinkClassName = classNames(
                                "group min-w-0 text-sm font-black transition",
                                style === "default" && "px-3 py-2 hover:text-[var(--site-primary)]",
                                style === "bordered" && "rounded-xl border border-[var(--site-border)] px-4 py-2.5 hover:border-[var(--site-primary)]",
                                style === "tabs" && "border-b-2 border-transparent px-3 py-3 hover:border-[var(--site-primary)] hover:text-[var(--site-primary)]",
                                style === "pills" && "rounded-full border border-[var(--site-border)] bg-[var(--site-surface)] px-4 py-2.5 hover:border-[var(--site-primary)]",
                                visualCategories && !categoryIndex && "grid overflow-hidden rounded-[18px] border border-[var(--site-border)] bg-[var(--site-surface)] p-2 shadow-sm",
                                visualCategories && categoryIndex && "grid overflow-hidden bg-white p-0 text-center",
                                sidebar && categoryIndex && "flex min-h-12 flex-1 items-center justify-between py-3 font-semibold hover:text-black",
                                item.uuid === activeItemUuid && "text-black",
                            );
                    const categoryLinkContent = (
                        <>
                            {visualCategories ? (
                                <span className={classNames(
                                    "grid place-items-center bg-[color-mix(in_srgb,var(--site-primary)_12%,var(--site-surface))] text-2xl font-black text-[var(--site-primary)]",
                                    !categoryIndex && "rounded-[14px]",
                                    style === "largeImages" ? "aspect-[16/7] text-4xl" : "aspect-[4/3]",
                                    index % 3 === 1 && "bg-[color-mix(in_srgb,var(--site-accent)_18%,var(--site-surface))]",
                                    index % 3 === 2 && "bg-[var(--site-primary-soft)]",
                                )} aria-hidden="true">
                                    {imageUrl ? (
                                        <img src={imageUrl} alt="" className="h-full w-full bg-white object-contain p-3" />
                                    ) : initial}
                                </span>
                            ) : null}
                            <span className={classNames("block truncate", visualCategories && (categoryIndex ? "py-3" : "px-2 py-2"))}>{item.label}</span>
                        </>
                    );
                    const categoryLink = href ? (
                        <ShopStorefrontLink
                            href={href}
                            onClick={handleClick}
                            className={categoryLinkClassName}
                        >
                            {categoryLinkContent}
                        </ShopStorefrontLink>
                    ) : (
                        <div className={categoryLinkClassName}>{categoryLinkContent}</div>
                    );

                    if (!sidebar || !categoryIndex) {
                        return <Fragment key={item.uuid}>{categoryLink}</Fragment>;
                    }

                    const categoryExpanded = expandedCategoryUuids.has(item.uuid);
                    const categoryRow = (
                        <div
                            className="flex min-w-0 items-stretch border-b border-[#dadada]"
                            data-catalog-category-active={item.uuid === activeItemUuid ? "true" : undefined}
                        >
                            {categoryLink}
                            {item.children.length > 0 ? (
                                <button
                                    type="button"
                                    className="grid min-h-12 w-10 shrink-0 place-items-center text-[#666] hover:text-black"
                                    aria-expanded={categoryExpanded}
                                    aria-label={item.label}
                                    onClick={() => setExpandedCategoryUuids((current) => {
                                        const next = new Set(current);

                                        if (next.has(item.uuid)) {
                                            next.delete(item.uuid);
                                        } else {
                                            next.add(item.uuid);
                                        }

                                        return next;
                                    })}
                                >
                                    <Icon name={categoryExpanded ? "chevronDown" : "chevronRight"} className="h-4 w-4" />
                                </button>
                            ) : null}
                        </div>
                    );

                    if (item.children.length === 0) {
                        return <Fragment key={item.uuid}>{categoryRow}</Fragment>;
                    }

                    return (
                        <div key={item.uuid} data-catalog-category-active={item.uuid === activeItemUuid ? "true" : undefined}>
                            {categoryRow}
                            {categoryExpanded ? renderSidebarChildren(item.children) : null}
                        </div>
                    );
                })}
            </div>
            {canCollapse ? (
                <button
                    type="button"
                    className={classNames(
                        "mt-3 px-3 py-2 text-xs font-black text-[var(--site-primary)] hover:bg-[var(--site-primary-soft)]",
                        categoryIndex ? "rounded-none" : "rounded-full",
                    )}
                    onClick={() => setExpanded((current) => !current)}
                >
                    {expanded
                        ? message("ui.catalog.showLess")
                        : message("ui.catalog.showMore", { count: linkedItems.length - visibleItems.length })}
                </button>
            ) : null}
        </nav>
    );
}

function LagunaCatalogProductToolbar({
    activeFilterCount,
    baseHref,
    config,
    filters,
    onNavigate,
    onOpenFilters,
    query,
    saleLocked,
    selectedOptions,
    showCategoryMenu,
    showPlayfulSearch,
    sort,
    total,
}: {
    activeFilterCount: number;
    baseHref: string;
    config: CatalogProductPageConfig;
    filters: CatalogFilterState;
    onNavigate?: CatalogNavigateHandler;
    onOpenFilters: () => void;
    query: string | null;
    saleLocked: boolean;
    selectedOptions: Array<{ facet: PublicCatalogFacet; option: PublicCatalogFacetOption }>;
    showCategoryMenu: boolean;
    showPlayfulSearch?: boolean;
    sort: CatalogSortValue;
    total: number;
}) {
    const message = useShopStorefrontMessage();
    const { locale } = useShopStorefrontFormatting();
    const showFilterButton = showCategoryMenu || config.filtersMode !== "hidden";
    const filterButtonLabel = config.filtersMode === "hidden"
        ? message("ui.search.categories")
        : message("ui.catalog.filters");
    const selectedFilterSummary = activeFilterCount > 0 ? (
        <div className="mt-4 border-t border-[var(--site-border)] pt-4">
            <SelectedCatalogFilters
                baseHref={baseHref}
                currentSort={sort}
                filters={filters}
                onNavigate={onNavigate}
                query={query}
                saleLocked={saleLocked}
                selectedOptions={selectedOptions}
            />
        </div>
    ) : null;

    if (config.filterStyle === "editorialExpand") {
        return (
            <div className="catalog-product-toolbar mb-8" data-catalog-toolbar-anatomy="editorial">
                {config.toolbarSort ? (
                    <div className="flex min-w-0 max-w-full flex-wrap items-center justify-end gap-3">
                        <span className="min-w-0 max-w-full text-xs font-medium [overflow-wrap:anywhere]">{message("ui.catalog.sortLabel")}:</span>
                        <CatalogSortTabs
                            baseHref={baseHref}
                            currentSort={sort}
                            filters={filters}
                            onNavigate={onNavigate}
                            presentationStyle={config.filterStyle}
                            query={query}
                        />
                    </div>
                ) : null}
                {showFilterButton ? (
                    <div className="mt-12 flex min-w-0 max-w-full justify-center">
                        <button
                            type="button"
                            className="inline-flex min-h-[42px] min-w-0 max-w-full flex-wrap items-center justify-center border border-[var(--site-text)] bg-white px-5 text-center text-xs font-black uppercase whitespace-normal [overflow-wrap:anywhere]"
                            onClick={onOpenFilters}
                        >
                            {filterButtonLabel}
                            {activeFilterCount > 0 ? <span className="ml-2">({activeFilterCount})</span> : null}
                        </button>
                    </div>
                ) : null}
                {selectedFilterSummary}
            </div>
        );
    }

    if (config.filterStyle === "boutiqueSidebar") {
        return (
            <div className="catalog-product-toolbar mb-8" data-catalog-toolbar-anatomy="boutique">
                {config.toolbarSort ? (
                    <CatalogSortTabs
                        baseHref={baseHref}
                        currentSort={sort}
                        filters={filters}
                        onNavigate={onNavigate}
                        presentationStyle={config.filterStyle}
                        query={query}
                    />
                ) : null}
                {selectedFilterSummary}
            </div>
        );
    }

    if (config.filterStyle === "playfulSidebar") {
        return (
            <div className="catalog-product-toolbar mb-8" data-catalog-toolbar-anatomy="playful">
                {showPlayfulSearch ? (
                    <div className="mb-5 min-w-0 max-w-full" data-catalog-playful-toolbar-search>
                        <CatalogTextFilter
                            baseHref={baseHref}
                            currentSort={sort}
                            filters={filters}
                            onNavigate={onNavigate}
                            query={query}
                            variant="inline"
                        />
                    </div>
                ) : null}
                <div className="flex min-w-0 max-w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="min-w-0 max-w-full text-sm text-[color-mix(in_srgb,var(--site-text)_55%,transparent)] [overflow-wrap:anywhere]">
                        {query
                            ? storefrontPluralMessage(total, locale, CATALOG_QUERY_SUMMARY_MESSAGE_KEYS, message, { query })
                            : storefrontPluralMessage(total, locale, CATALOG_PRODUCT_COUNT_MESSAGE_KEYS, message)}
                    </p>
                    {config.toolbarSort ? (
                        <CatalogSortTabs
                            baseHref={baseHref}
                            currentSort={sort}
                            filters={filters}
                            onNavigate={onNavigate}
                            presentationStyle="default"
                            query={query}
                        />
                    ) : null}
                    {showFilterButton && config.filtersMode !== "sidebar" && config.filtersMode !== "hidden" ? (
                        <button
                            type="button"
                            className="inline-flex min-h-11 min-w-0 max-w-full items-center justify-center border-2 border-dotted border-[var(--playful-coral-strong,var(--site-primary))] bg-[var(--playful-blush,var(--site-accent-soft))] px-5 text-center text-xs font-black text-[var(--playful-coral-strong,var(--site-primary))] whitespace-normal [overflow-wrap:anywhere]"
                            onClick={onOpenFilters}
                        >
                            {filterButtonLabel}
                            {activeFilterCount > 0 ? <span className="ml-2">({activeFilterCount})</span> : null}
                        </button>
                    ) : null}
                </div>
                {selectedFilterSummary}
            </div>
        );
    }

    return (
        <div className="catalog-product-toolbar mb-6 rounded-[24px] border border-[var(--site-border)] bg-[var(--site-surface)] p-4 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                    <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_40%,transparent)]">{message("ui.catalog.results")}</p>
                    <p className="mt-1 max-w-full text-sm text-[color-mix(in_srgb,var(--site-text)_55%,transparent)] [overflow-wrap:anywhere]">
                        {query
                            ? storefrontPluralMessage(total, locale, CATALOG_QUERY_SUMMARY_MESSAGE_KEYS, message, { query })
                            : storefrontPluralMessage(total, locale, CATALOG_PRODUCT_COUNT_MESSAGE_KEYS, message)}
                    </p>
                </div>
                <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                    {config.toolbarSearch ? (
                        <div className="min-w-0 flex-1 lg:w-[320px]">
                            <CatalogTextFilter
                                baseHref={baseHref}
                                currentSort={sort}
                                filters={filters}
                                onNavigate={onNavigate}
                                query={query}
                                variant="inline"
                            />
                        </div>
                    ) : null}
                    {showFilterButton ? (
                        <button
                            type="button"
                            className={classNames(
                                "inline-flex min-h-11 min-w-0 max-w-full flex-wrap items-center justify-center gap-2 rounded-full bg-[var(--site-surface-elevated)] px-5 text-center text-sm font-black whitespace-normal [overflow-wrap:anywhere]",
                                config.filtersMode !== "offscreen" && (config.filtersMode === "sidebar" || config.filtersMode === "inline" || showCategoryMenu) && "lg:hidden",
                            )}
                            onClick={onOpenFilters}
                        >
                            {filterButtonLabel}
                            {config.filtersMode !== "hidden" && activeFilterCount > 0 ? (
                                <span className="rounded-full bg-[var(--site-primary)] px-2 py-0.5 text-[10px] text-[var(--site-on-primary)]">{activeFilterCount}</span>
                            ) : null}
                        </button>
                    ) : null}
                    {config.toolbarSort ? (
                        <CatalogSortTabs
                            baseHref={baseHref}
                            currentSort={sort}
                            filters={filters}
                            onNavigate={onNavigate}
                            presentationStyle={config.filterStyle}
                            query={query}
                        />
                    ) : null}
                </div>
            </div>
            {selectedFilterSummary}
        </div>
    );
}

function lagunaCatalogPageNumbers(currentPage: number, totalPages: number): number[] {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);

    return Array.from(new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]))
        .filter((page) => page >= 1 && page <= totalPages)
        .sort((first, second) => first - second);
}

function LagunaCatalogPagination({
    baseHref,
    context,
    filters,
    onNavigate,
    query,
    sort,
}: {
    baseHref: string;
    context: ShopPageContext;
    filters: CatalogFilterState;
    onNavigate?: CatalogNavigateHandler;
    query: string | null;
    sort: CatalogSortValue;
}) {
    const editorMode = useContext(ShopEditorModeContext);
    const message = useShopStorefrontMessage();
    const pagination = catalogPaginationFromContext(context);

    if (pagination.totalPages <= 1) return null;

    const pages = lagunaCatalogPageNumbers(pagination.page, pagination.totalPages);
    const hrefForPage = (page: number) => catalogPaginationHref(
        baseHref,
        sort,
        filters,
        query,
        page,
        pagination.limit,
    );
    const handleClick = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
        if (editorMode) {
            event.preventDefault();

            return;
        }

        handleCatalogNavigateClick(event, href, onNavigate);
    };

    return (
        <nav aria-label={message("ui.catalog.catalog")} className="catalog-pagination mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-[var(--site-border)] pt-6">
            {pagination.hasPreviousPage ? (
                <a className="rounded-full border border-[var(--site-border)] px-4 py-2 text-sm font-black" href={hrefForPage(pagination.page - 1)} onClick={(event) => handleClick(event, hrefForPage(pagination.page - 1))}>
                    {message("ui.common.previous")}
                </a>
            ) : <span />}
            <div className="flex flex-wrap items-center justify-center gap-1">
                {pages.map((page, index) => {
                    const previous = pages[index - 1];
                    const href = hrefForPage(page);

                    return (
                        <Fragment key={page}>
                            {previous && page - previous > 1 ? <span className="px-1">…</span> : null}
                            <a
                                aria-current={page === pagination.page ? "page" : undefined}
                                className={classNames(
                                    "grid h-10 min-w-10 place-items-center rounded-full px-2 text-sm font-black",
                                    page === pagination.page ? "bg-[var(--site-primary)] text-[var(--site-on-primary)]" : "hover:bg-[var(--site-surface)]",
                                )}
                                href={href}
                                onClick={(event) => handleClick(event, href)}
                            >
                                {page}
                            </a>
                        </Fragment>
                    );
                })}
            </div>
            {pagination.hasNextPage ? (
                <a className="rounded-full border border-[var(--site-border)] px-4 py-2 text-sm font-black" href={hrefForPage(pagination.page + 1)} onClick={(event) => handleClick(event, hrefForPage(pagination.page + 1))}>
                    {message("ui.common.next")}
                </a>
            ) : <span />}
        </nav>
    );
}

const CATALOG_EDITORIAL_PRESENTATION_CSS = `
.shop-catalog-presentation:is([data-catalog-product-design="editorialGrid"],[data-catalog-product-design="utilityGrid"],[data-catalog-product-design="boutiqueGrid"],[data-catalog-product-design="classicBordered"],[data-catalog-product-design="playfulCards"]) :is([data-catalog-product-card],[data-catalog-product-content],[data-catalog-product-actions]){min-width:0;max-width:100%}.shop-catalog-presentation:is([data-catalog-product-design="editorialGrid"],[data-catalog-product-design="utilityGrid"],[data-catalog-product-design="boutiqueGrid"],[data-catalog-product-design="classicBordered"],[data-catalog-product-design="playfulCards"]) [data-catalog-product-content] :is(a,p,button,span){max-width:100%;overflow-wrap:anywhere}
.shop-catalog-presentation[data-catalog-product-design="editorialGrid"] [data-catalog-product-card]{overflow:visible;border:0!important;border-right:1px solid var(--site-text)!important;border-bottom:1px solid var(--site-text)!important;border-radius:0!important;background:transparent!important;padding:0!important;text-align:center;box-shadow:none!important}.shop-catalog-presentation[data-catalog-product-design="editorialGrid"] [data-catalog-product-media] a{border:0!important;border-radius:0!important;background:#fff!important;box-shadow:none!important}.shop-catalog-presentation[data-catalog-product-design="editorialGrid"] [data-catalog-product-content]{align-items:center}.shop-catalog-presentation[data-catalog-product-design="editorialGrid"] [data-catalog-product-content]>div{width:100%}.shop-catalog-presentation[data-catalog-product-design="editorialGrid"] [data-catalog-product-content] a{font-family:var(--site-heading-font);font-weight:650;letter-spacing:-.02em}.shop-catalog-presentation[data-catalog-product-design="editorialGrid"] [data-catalog-product-actions]>div{justify-content:center}.shop-catalog-presentation[data-catalog-product-design="editorialGrid"] [data-catalog-product-actions] button,.shop-catalog-presentation[data-catalog-product-design="editorialGrid"] [data-catalog-product-actions]>a{border-radius:0!important;letter-spacing:.08em;text-transform:uppercase}
.shop-catalog-presentation[data-catalog-product-design="utilityGrid"] [data-catalog-product-card]{overflow:visible;border:0!important;border-radius:0!important;background:transparent!important;padding:0!important;box-shadow:none!important}.shop-catalog-presentation[data-catalog-product-design="utilityGrid"] [data-catalog-product-media] a{border:0!important;border-radius:0!important;box-shadow:none!important;transition:opacity .2s}.shop-catalog-presentation[data-catalog-product-design="utilityGrid"] [data-catalog-product-card]:hover [data-catalog-product-media] a{opacity:.9}.shop-catalog-presentation[data-catalog-product-design="utilityGrid"] [data-catalog-product-actions] button,.shop-catalog-presentation[data-catalog-product-design="utilityGrid"] [data-catalog-product-actions]>a{border:0!important;border-radius:0!important;background:var(--site-accent)!important;color:var(--site-text)!important;box-shadow:none!important;text-transform:uppercase}
.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-product-card]{overflow:hidden;border:0!important;border-radius:0!important;background:transparent!important;padding:0!important;text-align:center;box-shadow:none!important}.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-product-media] a{border:0!important;border-radius:8px!important;box-shadow:none!important}.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-product-content]{align-items:center}.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-product-content]>div{width:100%}.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-boutique-swatches]{justify-content:center}.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-product-actions]{width:100%}.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-product-actions]>div{justify-content:center}.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-product-actions] button,.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-product-actions]>a{border-radius:8px!important;background:var(--site-primary)!important;box-shadow:none!important}
.shop-catalog-presentation[data-catalog-product-design="classicBordered"] [data-catalog-product-card]{border:1px solid #dadada!important;border-radius:0!important;background:#fff!important;box-shadow:none!important}.shop-catalog-presentation[data-catalog-product-design="classicBordered"] [data-catalog-product-media] a{border:0!important;border-radius:0!important;background:#fff!important;box-shadow:none!important}.shop-catalog-presentation[data-catalog-product-design="classicBordered"] [data-catalog-product-content]>div{width:100%}.shop-catalog-presentation[data-catalog-product-design="classicBordered"] [data-catalog-product-actions]{width:100%}.shop-catalog-presentation[data-catalog-product-design="classicBordered"] [data-catalog-product-actions]>div{justify-content:center}.shop-catalog-presentation[data-catalog-product-design="classicBordered"] [data-catalog-product-actions] button,.shop-catalog-presentation[data-catalog-product-design="classicBordered"] [data-catalog-product-actions]>a{border-radius:0!important;background:#000!important;color:#fff!important;box-shadow:none!important}.shop-catalog-presentation[data-catalog-product-design="classicBordered"] [data-catalog-product-card]>div:first-child>button{border-radius:0!important;box-shadow:none!important}
.shop-catalog-presentation[data-catalog-filter-style="editorialExpand"] .catalog-product-toolbar{border:0;border-block:1px solid var(--site-text);border-radius:0;background:transparent;box-shadow:none;padding-inline:0}.shop-catalog-presentation[data-catalog-filter-style="editorialExpand"] .catalog-product-toolbar button,.shop-catalog-presentation[data-catalog-filter-style="editorialExpand"] .catalog-product-toolbar select{border-radius:0!important}.shop-catalog-presentation[data-catalog-filter-style="editorialExpand"] .catalog-editorial-inline-panel .catalog-filter-panel-header{border-color:var(--site-text);padding-inline:0}.shop-catalog-presentation[data-catalog-filter-style="editorialExpand"] .catalog-editorial-inline-panel .catalog-filter-facet{margin:0;border-color:var(--site-text);border-radius:0;background:transparent}
.shop-catalog-presentation[data-catalog-filter-style="boutiqueSidebar"] aside{background:#f9f9f9;padding:24px}.shop-catalog-presentation[data-catalog-filter-style="boutiqueSidebar"] aside .catalog-filter-panel{overflow:hidden;border:0;border-radius:0;background:#f9f9f9;box-shadow:none}.shop-catalog-presentation[data-catalog-filter-style="boutiqueSidebar"] aside nav{border:0!important;border-radius:0!important;background:#f9f9f9!important;padding:0!important}.shop-catalog-presentation[data-catalog-filter-style="boutiqueSidebar"] .catalog-filter-facet{margin:0;border-color:#dedede;border-radius:0!important;background:transparent}.shop-catalog-presentation[data-catalog-filter-style="boutiqueSidebar"] .catalog-product-toolbar{border-radius:8px;box-shadow:none}
.shop-catalog-presentation[data-catalog-filter-style="structuredSidebar"] aside{background:#fff;padding:0}.shop-catalog-presentation[data-catalog-filter-style="structuredSidebar"] aside .catalog-filter-panel{border:0;border-radius:0;background:transparent;box-shadow:none}.shop-catalog-presentation[data-catalog-filter-style="structuredSidebar"] .catalog-filter-panel-header{padding:20px 0;border-color:#dadada}.shop-catalog-presentation[data-catalog-filter-style="structuredSidebar"] .catalog-filter-panel-header span,.shop-catalog-presentation[data-catalog-filter-style="structuredSidebar"] .catalog-filter-facet button span span{border-radius:0!important;background:transparent!important;color:#111!important}.shop-catalog-presentation[data-catalog-filter-style="structuredSidebar"] .catalog-filter-facet{border-color:#dadada;border-radius:0!important;background:transparent}.shop-catalog-presentation[data-catalog-filter-style="structuredSidebar"] .catalog-filter-facet>button{padding:18px 0}.shop-catalog-presentation[data-catalog-filter-style="structuredSidebar"] .catalog-filter-facet>div{padding:0 0 18px}.shop-catalog-presentation[data-catalog-filter-style="structuredSidebar"] .catalog-filter-facet a{border-radius:0!important;background:transparent!important}.shop-catalog-presentation[data-catalog-filter-style="structuredSidebar"] [data-catalog-range-track]{display:block!important}.shop-catalog-presentation[data-catalog-filter-style="structuredSidebar"] .catalog-product-toolbar{border:0;border-block:1px solid #dadada;border-radius:0;background:#fff;box-shadow:none;padding-inline:0}.shop-catalog-presentation[data-catalog-filter-style="structuredSidebar"] .catalog-product-toolbar button,.shop-catalog-presentation[data-catalog-filter-style="structuredSidebar"] .catalog-product-toolbar select{border-radius:0!important}.shop-catalog-presentation[data-catalog-filter-style="structuredSidebar"] nav[aria-label]{border-radius:0}.shop-catalog-presentation[data-catalog-filter-style="structuredSidebar"] .catalog-pagination [aria-current="page"]{border-radius:0!important;background:#000!important;color:#fff!important}
@media(min-width:1024px){.shop-catalog-presentation[data-catalog-filter-style="utilityInline"] .catalog-inline-filters .catalog-filter-panel{display:grid;grid-auto-columns:minmax(190px,1fr);grid-auto-flow:column;grid-template-columns:minmax(180px,.72fr);overflow-x:auto;border:0;border-block:1px solid var(--site-border);border-radius:0;box-shadow:none}.shop-catalog-presentation[data-catalog-filter-style="utilityInline"] .catalog-inline-filters .catalog-filter-panel-header{position:sticky;left:0;z-index:1;border-right:1px solid var(--site-border);border-bottom:0;background:var(--site-background)}.shop-catalog-presentation[data-catalog-filter-style="utilityInline"] .catalog-inline-filters .catalog-filter-facet{min-width:190px;border-right:1px solid var(--site-border);border-bottom:0}.shop-catalog-presentation[data-catalog-filter-style="utilityInline"] .catalog-inline-filters .catalog-filter-facet>div{padding:1rem}.shop-catalog-presentation[data-catalog-filter-style="utilityInline"] .catalog-product-toolbar{border:0;background:transparent;box-shadow:none;padding-inline:0}}
@media(max-width:767px){.shop-catalog-presentation[data-catalog-product-design="editorialGrid"] [data-laguna-product-design="editorialGrid"]{grid-template-columns:minmax(0,1fr)!important}.shop-catalog-presentation[data-catalog-product-design="utilityGrid"] [data-laguna-product-design="utilityGrid"]{gap:1rem}.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-product-content]{padding-inline:.25rem!important}.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-product-actions]>a,.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-product-actions]>button{box-sizing:border-box!important;width:100%!important;max-width:100%!important;min-width:0!important;padding-inline:clamp(6px,3vw,12px)!important;overflow-wrap:anywhere!important;white-space:normal!important}}

/* Portable playful catalog controls. These selectors belong to the selected
   component variants and intentionally do not require a storefront profile. */
.shop-catalog-presentation[data-catalog-playful-presentation="true"]{
  --playful-cream:var(--kids-cream,var(--site-background,#f6f1e4));
  --playful-paper:var(--kids-paper,var(--site-surface,#faf7ef));
  --playful-aqua:var(--kids-aqua,var(--site-accent,#c3e1db));
  --playful-coral:var(--kids-coral,var(--site-primary,#ec6830));
  --playful-coral-strong:var(--kids-coral-strong,var(--site-primary,#c94b1f));
  --playful-blush:var(--kids-blush,var(--site-accent-soft,#ffd7cd));
  --playful-ink:var(--kids-ink,var(--site-text,#444));
  --playful-muted:var(--kids-muted,var(--site-muted,#77736b));
  --playful-line:var(--kids-line,var(--site-border,#e2d9c7));
  color:var(--playful-ink)
}
.shop-catalog-presentation[data-catalog-integrated-layout="playful-index"]{background:#fff!important;padding:0 24px 108px!important}
.shop-catalog-presentation[data-catalog-integrated-layout="playful-index"]>[data-catalog-content-width]{width:min(100%,1300px)!important;max-width:1300px!important}
.shop-catalog-presentation[data-catalog-integrated-layout="playful-index"] [data-catalog-integrated-intro]{display:flex!important;min-height:110px!important;align-items:center!important;margin:0 0 90px!important;background:#f7f7f7!important;box-shadow:0 0 0 100vmax #f7f7f7!important;clip-path:inset(0 -100vmax)!important;text-align:left!important}
.shop-catalog-presentation[data-catalog-integrated-layout="playful-index"] [data-catalog-integrated-intro]>[data-shop-slot-container]{width:100%!important}
.shop-catalog-presentation[data-catalog-integrated-layout="playful-index"] [data-catalog-integrated-intro] :is(h1,h2){background:none!important;color:var(--playful-ink)!important;font-size:23px!important;font-weight:900!important;letter-spacing:-.025em!important;line-height:1.1!important;-webkit-text-fill-color:var(--playful-ink)!important}
.shop-catalog-presentation[data-catalog-integrated-layout="playful-index"] [data-catalog-integrated-intro] p{display:none!important}
.shop-catalog-presentation[data-catalog-integrated-layout="playful-index"]>[data-catalog-content-width]>div:last-child{grid-template-columns:280px minmax(0,1fr)!important;gap:50px!important}

.shop-catalog-presentation [data-catalog-top-categories] nav[data-catalog-category-style="playfulIndex"]{border:0!important;background:transparent!important;padding:0!important}
.shop-catalog-presentation [data-catalog-top-categories] nav[data-catalog-category-style="playfulIndex"]>div{display:grid!important;grid-template-columns:repeat(4,minmax(0,1fr))!important;gap:20px!important}
.shop-catalog-presentation [data-catalog-top-categories] nav[data-catalog-category-style="playfulIndex"] a{display:grid!important;width:auto!important;min-width:0!important;min-height:0!important;grid-template-columns:minmax(0,1fr)!important;overflow:hidden!important;border:0!important;border-radius:28px!important;background:#fff!important;padding:10px 10px 16px!important;box-shadow:0 10px 30px color-mix(in srgb,var(--playful-ink) 7%,transparent)!important;text-align:center!important}
.shop-catalog-presentation [data-catalog-top-categories] nav[data-catalog-category-style="playfulIndex"] a::after{display:none!important;content:none!important}
.shop-catalog-presentation [data-catalog-top-categories] nav[data-catalog-category-style="playfulIndex"] a>span:first-child{display:block!important;width:100%!important;aspect-ratio:4/3!important;overflow:hidden!important;border-radius:22px!important;background:var(--playful-cream)!important}
.shop-catalog-presentation [data-catalog-top-categories] nav[data-catalog-category-style="playfulIndex"] a>span:first-child img{width:100%!important;height:100%!important;padding:0!important;object-fit:cover!important}
.shop-catalog-presentation [data-catalog-top-categories] nav[data-catalog-category-style="playfulIndex"] a>span:last-child{padding:13px 8px 0!important;color:var(--playful-ink)!important;font-size:15px!important;font-weight:850!important;line-height:1.25!important}

.shop-catalog-presentation aside nav[data-catalog-category-style="playfulIndex"],.shop-catalog-presentation .catalog-mobile-playful-filters nav[data-catalog-category-style="playfulIndex"]{margin:0 0 38px!important;overflow:visible!important;border:0!important;border-radius:0!important;background:#fff!important;padding:0!important;text-align:left!important;box-shadow:none!important}
.shop-catalog-presentation aside nav[data-catalog-category-style="playfulIndex"] h2,.shop-catalog-presentation .catalog-mobile-playful-filters nav[data-catalog-category-style="playfulIndex"] h2{margin:0 0 16px!important;border:0!important;padding:0!important;color:var(--playful-ink)!important;font-size:23px!important;font-weight:900!important;line-height:1.15!important}
.shop-catalog-presentation aside nav[data-catalog-category-style="playfulIndex"]>div,.shop-catalog-presentation .catalog-mobile-playful-filters nav[data-catalog-category-style="playfulIndex"]>div{display:flex!important;flex-wrap:wrap!important;align-items:center!important;justify-content:flex-start!important;gap:4px 7px!important;text-align:left!important}
.shop-catalog-presentation :is(aside,.catalog-mobile-playful-filters) nav[data-catalog-category-style="playfulIndex"]>div>div,.shop-catalog-presentation :is(aside,.catalog-mobile-playful-filters) nav[data-catalog-category-style="playfulIndex"]>div>div>div{display:contents!important}
.shop-catalog-presentation :is(aside,.catalog-mobile-playful-filters) nav[data-catalog-category-style="playfulIndex"] a{display:inline-flex!important;min-height:32px!important;align-items:center!important;justify-content:flex-start!important;flex:none!important;border:0!important;background:transparent!important;padding:4px 0!important;color:color-mix(in srgb,var(--playful-muted) 75%,#fff)!important;font-size:11px!important;font-weight:500!important;line-height:1.55!important;text-align:left!important;text-transform:uppercase!important;white-space:normal!important}
.shop-catalog-presentation :is(aside,.catalog-mobile-playful-filters) nav[data-catalog-category-style="playfulIndex"] a::after{margin-left:7px;color:color-mix(in srgb,var(--playful-muted) 55%,#fff);content:"/"}
.shop-catalog-presentation :is(aside,.catalog-mobile-playful-filters) nav[data-catalog-category-style="playfulIndex"] button{display:none!important}

.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] aside{overflow:visible!important;border:0!important;border-radius:0!important;background:#fff!important;padding:0!important;box-shadow:none!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] [data-catalog-playful-search],.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] [data-catalog-playful-toolbar-search]{margin-bottom:38px!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] :is([data-catalog-playful-search],[data-catalog-playful-toolbar-search]) form{border:0!important;background:transparent!important;padding:0!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] :is([data-catalog-playful-search],[data-catalog-playful-toolbar-search]) form>label>span:first-child{display:none!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] :is([data-catalog-playful-search],[data-catalog-playful-toolbar-search]) form>label>span:last-child{position:relative!important;min-height:50px!important;border:0!important;border-radius:4px!important;background:#f7f7f7!important;padding:0 48px 0 12px!important;box-shadow:none!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] :is([data-catalog-playful-search],[data-catalog-playful-toolbar-search]) form>label>span:last-child>svg{display:none!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] :is([data-catalog-playful-search],[data-catalog-playful-toolbar-search]) form>label>span:last-child::after{position:absolute;top:50%;right:12px;display:grid;width:24px;height:24px;border-radius:999px;background:var(--playful-aqua);color:var(--playful-ink);content:"›";font-size:20px;font-weight:900;line-height:1;place-items:center;transform:translateY(-50%)}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] [data-catalog-search-input]{height:50px!important;padding:0!important;color:var(--playful-muted)!important;font-size:13px!important;font-weight:400!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] :is(aside,.catalog-mobile-playful-filters) :is(.catalog-filter-panel,nav){overflow:visible!important;border:0!important;border-radius:0!important;background:#fff!important;padding:0!important;box-shadow:none!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] .catalog-filter-panel-header{display:none!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] .catalog-filter-facet{margin:0!important;overflow:visible!important;border:0!important;border-radius:0!important;background:transparent!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] .catalog-filter-facet+.catalog-filter-facet{margin-top:34px!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] .catalog-filter-facet>button,.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] .catalog-filter-facet>div:first-child{min-height:0!important;border:0!important;border-radius:0!important;background:transparent!important;padding:0 0 13px!important;color:var(--playful-ink)!important;font-size:23px!important;font-weight:900!important;line-height:1.15!important;box-shadow:none!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] .catalog-filter-facet>button svg{width:14px!important;height:14px!important;color:var(--playful-muted)!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] .catalog-filter-facet>div{padding:0!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] .catalog-filter-facet a{min-height:32px!important;border:0!important;border-radius:0!important;background:transparent!important;padding:5px 0!important;color:var(--playful-muted)!important;font-size:11px!important;font-weight:500!important;line-height:1.55!important;text-transform:uppercase!important;white-space:normal!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] .catalog-filter-facet a>span:first-child>span:first-child{display:none!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] .catalog-filter-facet a>span:last-child{background:transparent!important;padding:0!important;color:var(--playful-muted)!important;font-size:10px!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] [data-catalog-range-track]{display:block!important;height:32px!important;min-height:32px!important;margin-bottom:4px!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] [data-catalog-range-track] input[type="range"]{height:32px!important;min-height:32px!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] [data-catalog-range-track]>span{top:8px!important;height:2px!important;background:var(--playful-aqua)!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] [data-catalog-range-track] input::-webkit-slider-thumb{width:15px!important;height:15px!important;border-radius:999px!important;background:var(--playful-aqua)!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] [data-catalog-range-track] input::-moz-range-thumb{width:15px!important;height:15px!important;border:0!important;border-radius:999px!important;background:var(--playful-aqua)!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] .catalog-filter-facet form>div.grid{gap:7px!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] .catalog-filter-facet form input[type="number"]{height:32px!important;border:0!important;background:#f7f7f7!important;padding:5px 7px!important;color:var(--playful-muted)!important;font-size:10px!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] .catalog-filter-facet form>button[type="submit"]{width:112px!important;min-height:40px!important;margin-left:auto!important;border:2px dotted var(--playful-coral-strong)!important;border-radius:0!important;background:var(--playful-blush)!important;padding:7px 12px!important;color:var(--playful-coral-strong)!important;font-size:11px!important;font-weight:900!important;text-transform:uppercase!important;box-shadow:none!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] [data-catalog-toolbar-anatomy="playful"]{min-height:50px!important;margin:0 0 24px!important;border:0!important;border-radius:0!important;background:transparent!important;padding:0!important;box-shadow:none!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] [data-catalog-toolbar-anatomy="playful"]>div{min-height:50px!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] [data-catalog-toolbar-anatomy="playful"] p{color:var(--playful-muted)!important;font-size:12px!important;font-weight:400!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] [data-catalog-toolbar-anatomy="playful"] .relative{width:230px!important;min-width:230px!important}
.shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] [data-catalog-toolbar-anatomy="playful"] .relative>button{width:230px!important;min-height:50px!important;border:1px solid var(--playful-line)!important;border-radius:3px!important;background:#fff!important;padding:0 18px!important;color:var(--playful-muted)!important;font-size:12px!important;font-weight:400!important;box-shadow:none!important}

.shop-catalog-presentation[data-catalog-product-design="playfulCards"] [data-catalog-product-card]{overflow:visible!important;border:0!important;border-radius:0!important;background:transparent!important;padding:0!important;text-align:center!important;box-shadow:none!important}
.shop-catalog-presentation[data-catalog-product-design="playfulCards"] [data-catalog-product-media]{overflow:hidden!important;border-radius:8px!important}
.shop-catalog-presentation[data-catalog-product-design="playfulCards"] [data-catalog-product-media] a:not([data-catalog-playful-overlay-action]){aspect-ratio:1!important;overflow:hidden!important;border:0!important;border-radius:8px!important;background:#f7f7f7!important;box-shadow:none!important}
.shop-catalog-presentation[data-catalog-product-design="playfulCards"] [data-catalog-product-media] img{box-sizing:border-box!important;width:100%!important;height:100%!important;max-height:100%!important;min-height:0!important;padding:12px!important;object-fit:contain!important}
.shop-catalog-presentation[data-catalog-product-design="playfulCards"] [data-catalog-product-media] a>span{top:14px!important;right:14px!important;left:auto!important;border-radius:48% 52% 45% 55%/58% 44% 56% 42%!important;background:#fff!important;color:var(--playful-ink)!important;box-shadow:none!important}
.shop-catalog-presentation[data-catalog-product-design="playfulCards"] [data-catalog-product-media]>button{display:none!important}
.shop-catalog-presentation[data-catalog-product-design="playfulCards"] [data-catalog-product-content]{min-width:0!important;align-items:center!important;padding:14px 0 0!important}
.shop-catalog-presentation[data-catalog-product-design="playfulCards"] [data-catalog-product-content]>div:first-child{width:100%!important;text-align:center!important}
.shop-catalog-presentation[data-catalog-product-design="playfulCards"] [data-catalog-product-content] a{display:flex!important;min-height:32px!important;align-items:center!important;justify-content:center!important;color:var(--playful-ink)!important;font-size:20px!important;font-weight:900!important;line-height:1.25!important}
.shop-catalog-presentation[data-catalog-product-design="playfulCards"] :is([data-catalog-product-reviews],[data-catalog-stock-label],[data-catalog-price-label],[data-catalog-playful-swatches]){display:none!important}
.shop-catalog-presentation[data-catalog-product-design="playfulCards"] [data-catalog-product-actions]{width:100%!important;min-width:0!important}
.shop-catalog-presentation[data-catalog-product-design="playfulCards"] [data-catalog-product-actions]>div{justify-content:center!important}
.shop-catalog-presentation[data-catalog-product-design="playfulCards"] [data-catalog-product-actions] p{color:var(--playful-muted)!important;font-size:16px!important;font-weight:400!important;line-height:1.3!important}
.shop-catalog-presentation[data-catalog-product-design="playfulCards"] [data-catalog-playful-overlay-action]{border:2px dotted var(--playful-coral-strong)!important;border-radius:0!important;background:var(--playful-blush)!important;color:var(--playful-coral-strong)!important;opacity:0!important;box-shadow:none!important;transition:opacity .18s ease!important}
.shop-catalog-presentation[data-catalog-product-design="playfulCards"] [data-catalog-product-card]:is(:hover,:focus-within) [data-catalog-playful-overlay-action]{opacity:1!important}
.shop-catalog-presentation[data-catalog-playful-presentation="true"] .catalog-pagination{justify-content:center!important;margin-top:68px!important;border:0!important;padding-top:0!important}
.shop-catalog-presentation[data-catalog-playful-presentation="true"] .catalog-pagination :is(a,[aria-current="page"]){width:28px!important;min-width:28px!important;height:28px!important;min-height:28px!important;border:0!important;border-radius:999px!important;background:transparent!important;padding:0!important;color:var(--playful-ink)!important;font-size:11px!important;box-shadow:none!important}
.shop-catalog-presentation[data-catalog-playful-presentation="true"] .catalog-pagination [aria-current="page"]{background:var(--playful-blush)!important;color:var(--playful-ink)!important}

@media(max-width:1023px){
  .shop-catalog-presentation .catalog-mobile-playful-filters{display:block!important;margin:58px 0 0!important}
  .shop-catalog-presentation[data-catalog-integrated-layout="playful-index"]>[data-catalog-content-width]>div:last-child{display:block!important}
}
@media(max-width:767px){
  .shop-catalog-presentation[data-catalog-integrated-layout="playful-index"]{padding:0 16px 70px!important}
  .shop-catalog-presentation[data-catalog-integrated-layout="playful-index"]>[data-catalog-content-width]{width:300px!important;max-width:100%!important}
  .shop-catalog-presentation[data-catalog-integrated-layout="playful-index"] [data-catalog-integrated-intro]{min-height:64px!important;margin-bottom:48px!important}
  .shop-catalog-presentation[data-catalog-integrated-layout="playful-index"] [data-catalog-integrated-intro] :is(h1,h2){font-size:20px!important}
  .shop-catalog-presentation [data-catalog-top-categories] nav[data-catalog-category-style="playfulIndex"]>div{grid-template-columns:minmax(0,1fr)!important;gap:12px!important}
  .shop-catalog-presentation [data-catalog-top-categories] nav[data-catalog-category-style="playfulIndex"] a{grid-template-columns:76px minmax(0,1fr)!important;align-items:center!important;border-radius:22px!important;padding:8px!important;text-align:left!important}
  .shop-catalog-presentation [data-catalog-top-categories] nav[data-catalog-category-style="playfulIndex"] a>span:first-child{width:76px!important;aspect-ratio:1!important;border-radius:18px!important}
  .shop-catalog-presentation [data-catalog-top-categories] nav[data-catalog-category-style="playfulIndex"] a>span:last-child{padding:8px 12px!important}
  .shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] [data-catalog-toolbar-anatomy="playful"]{margin-bottom:28px!important}
  .shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] [data-catalog-toolbar-anatomy="playful"] .relative{width:140px!important;min-width:140px!important}
  .shop-catalog-presentation[data-catalog-filter-style="playfulSidebar"] [data-catalog-toolbar-anatomy="playful"] .relative>button{width:140px!important;min-height:40px!important;padding-inline:11px!important;font-size:10px!important}
  .shop-catalog-presentation[data-catalog-product-design="playfulCards"] [data-catalog-product-content]{padding:12px 0 0!important}
  .shop-catalog-presentation[data-catalog-product-design="playfulCards"] [data-catalog-product-actions]{padding-top:8px!important}
  .shop-catalog-presentation[data-catalog-product-design="playfulCards"] [data-catalog-playful-overlay-action]{display:none!important}
  .shop-catalog-presentation[data-catalog-playful-presentation="true"] .catalog-pagination{margin-top:54px!important}
}

/* Exact reference-family catalog anatomies. */
.shop-catalog-presentation[data-catalog-product-design="editorialGrid"] [data-laguna-product-design="editorialGrid"]{border-color:#dadada!important}
.shop-catalog-presentation[data-catalog-product-design="editorialGrid"] [data-catalog-product-card]{border-right-color:#dadada!important;border-bottom-color:#dadada!important}
.shop-catalog-presentation[data-catalog-product-design="editorialGrid"] [data-catalog-product-media]{overflow:hidden!important}
.shop-catalog-presentation[data-catalog-product-design="editorialGrid"] [data-catalog-product-media] img{box-sizing:border-box!important;width:100%!important;height:100%!important;max-height:100%!important;min-height:0!important;padding:20px!important;object-fit:contain!important}
.shop-catalog-presentation[data-catalog-product-design="editorialGrid"] [data-catalog-product-content]{min-height:150px;padding:22px 18px 24px!important}
.shop-catalog-presentation[data-catalog-product-design="editorialGrid"] [data-catalog-price-label]{display:none}
.shop-catalog-presentation[data-catalog-product-design="editorialGrid"] [data-catalog-product-actions]>a,.shop-catalog-presentation[data-catalog-product-design="editorialGrid"] [data-catalog-product-actions]>button{display:none!important}
.shop-catalog-presentation[data-catalog-product-design="editorialGrid"] [data-catalog-product-actions] p{font-size:16px;font-weight:700;color:var(--site-text)}
.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-product-media] a{border-radius:0!important;background:#fff!important}
.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-product-media]{overflow:hidden!important}
.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-product-media] img{width:100%!important;height:100%!important;max-height:100%!important;min-height:0!important;padding:12px!important;object-fit:contain!important}
.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-price-label]{display:none}
.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-product-actions]>a,.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-product-actions]>button{display:inline-flex!important;min-height:50px;width:auto!important;align-items:center;justify-content:center;padding:0 24px!important}
@media(max-width:767px){.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-product-actions]>a,.shop-catalog-presentation[data-catalog-product-design="boutiqueGrid"] [data-catalog-product-actions]>button{box-sizing:border-box!important;width:100%!important;max-width:100%!important;min-width:0!important;padding-inline:clamp(6px,3vw,12px)!important;white-space:normal!important;overflow-wrap:anywhere!important}}

.shop-catalog-presentation[data-catalog-integrated-layout="editorial-index"] [data-catalog-editorial-title-rail]{padding-top:2px}
.shop-catalog-presentation[data-catalog-integrated-layout="editorial-index"] [data-catalog-integrated-intro]{margin:0}
.shop-catalog-presentation[data-catalog-integrated-layout="editorial-index"] [data-catalog-integrated-intro] :is(h1,h2){font-family:var(--site-heading-font);font-size:30px;font-weight:400;line-height:1.18;text-transform:uppercase}
.shop-catalog-presentation[data-catalog-integrated-layout="editorial-index"] [data-catalog-integrated-intro] p{display:none}
.shop-catalog-presentation[data-catalog-integrated-layout="editorial-index"] [data-catalog-top-categories]{min-height:132px;margin:0 0 24px}
.shop-catalog-presentation[data-catalog-integrated-layout="editorial-index"] [data-catalog-top-categories] nav>div{display:grid;grid-template-columns:repeat(auto-fit,minmax(116px,150px));justify-content:start;gap:22px}
.shop-catalog-presentation[data-catalog-integrated-layout="editorial-index"] [data-catalog-top-categories] a{display:grid!important;border:0!important;border-radius:0!important;background:#fff!important;padding:0!important;box-shadow:none!important;text-align:center}
.shop-catalog-presentation[data-catalog-integrated-layout="editorial-index"] [data-catalog-top-categories] a>span:first-child{aspect-ratio:3/2!important;border-radius:0!important;background:#fff!important}
.shop-catalog-presentation[data-catalog-integrated-layout="editorial-index"] [data-catalog-top-categories] a>span:last-child{padding:10px 0!important;font-size:12px;letter-spacing:.03em;text-transform:uppercase}
.shop-catalog-presentation[data-catalog-integrated-layout="editorial-index"] [data-catalog-toolbar-anatomy="editorial"]{min-height:154px}
.shop-catalog-presentation[data-catalog-integrated-layout="editorial-index"] [data-catalog-toolbar-anatomy="editorial"] .relative{min-width:190px;width:auto}
.shop-catalog-presentation[data-catalog-integrated-layout="editorial-index"] [data-catalog-toolbar-anatomy="editorial"] .relative>button{min-height:32px;width:auto;background:transparent;padding:0;font-size:12px}

.shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-integrated-intro]{margin:0 auto 30px;max-width:1580px;text-align:center}
.shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-integrated-intro] :is(h1,h2){font-family:var(--site-heading-font);font-size:48px;font-weight:700;line-height:1.15;letter-spacing:-.03em}
.shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-integrated-intro] p{margin:24px auto 0;max-width:1560px;font-size:16px;line-height:1.5}
.shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-top-categories]{margin:0 0 40px;border-bottom:1px solid #dadada;padding-bottom:40px}
.shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-top-categories] nav>div{display:flex;flex-wrap:wrap;justify-content:center;gap:24px}
.shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-top-categories] a{display:grid!important;grid-template-columns:40px minmax(0,1fr) 22px!important;align-items:center;width:256px;min-height:56px;border:1px solid #dadada!important;border-radius:8px!important;background:#fff!important;padding:0 14px 0 8px!important;box-shadow:none!important;text-align:left}
.shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-top-categories] a::after{content:"›";font-size:24px;font-weight:400;text-align:right}
.shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-top-categories] a>span:first-child{width:40px;aspect-ratio:1!important;border-radius:0!important;background:#fff!important;font-size:13px}
.shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-top-categories] a>span:first-child img{padding:4px!important}
.shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-top-categories] a>span:last-child{padding:0 8px!important;font-size:15px}
.shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] aside{border:1px solid #dadada;border-radius:8px!important;padding:24px!important}
.shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-toolbar-anatomy="boutique"]{border:0!important;border-radius:0!important;background:transparent!important;padding:0!important}
.shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-sort-presentation="flat-tabs"]{width:100%;border:0!important}
.shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-sort-presentation="flat-tabs"]>a{flex:1;justify-content:center;border:0!important}
.shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] .catalog-mobile-boutique-filters .catalog-filter-panel{border:1px solid #dadada;border-radius:8px;background:#f9f9f9;box-shadow:none}

@media(min-width:1024px){
  .shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"]{padding-top:32px!important}
  .shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"]>div{max-width:1694px!important}
  .shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-integrated-intro]{margin-bottom:38px}
  .shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-integrated-intro] p{min-height:48px}
  .shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-top-categories]{padding-bottom:43px}
}
@media(min-width:1024px) and (max-width:1279px){
  .shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-sort-presentation="flat-tabs"]{flex-wrap:wrap;overflow:visible}
  .shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-sort-presentation="flat-tabs"]>a{flex:1 0 26%;min-width:0;padding-inline:10px;font-size:12px}
}
@media(max-width:1023px){
  .shop-catalog-presentation[data-catalog-integrated-layout="editorial-index"] [data-catalog-editorial-title-rail]{padding:0}
  .shop-catalog-presentation[data-catalog-integrated-layout="editorial-index"] [data-catalog-top-categories]{min-height:0;margin-top:22px}
  .shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-integrated-intro] :is(h1,h2){font-size:32px}
  .shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-integrated-intro] p{font-size:15px;line-height:1.35}
  .shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-top-categories] nav>div{display:grid;grid-template-columns:1fr;gap:8px}
  .shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-top-categories] a{width:100%}
  .shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-sort-presentation="flat-tabs"]{flex-wrap:wrap;overflow:visible}
  .shop-catalog-presentation[data-catalog-integrated-layout="boutique-index"] [data-catalog-sort-presentation="flat-tabs"]>a{flex:1 0 30%;justify-content:center}
}
@media(max-width:767px){
  .shop-catalog-presentation[data-catalog-integrated-layout="editorial-index"] [data-catalog-toolbar-anatomy="editorial"]{min-height:132px}
  .shop-catalog-presentation[data-catalog-integrated-layout="editorial-index"] [data-catalog-toolbar-anatomy="editorial"]>div:nth-child(2)>button{width:100%}
  .shop-catalog-presentation[data-catalog-integrated-layout="editorial-index"] [data-catalog-top-categories] nav>div{grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
}
`;

function SystemProductListPreview({
    block,
    context,
    favorites,
    catalogUpdating,
    addingProductUuid,
    integratedCategoryIntro,
    integratedCategoryRail,
    onAddToCart,
    onCatalogNavigate,
    onToggleFavorite,
}: {
    block?: ShopTemplateBlock | null;
    context: ShopPageContext;
    favorites: Set<string>;
    catalogUpdating: boolean;
    addingProductUuid?: string | null;
    integratedCategoryIntro?: ReactNode;
    integratedCategoryRail?: ReactNode;
    onAddToCart?: (productUuid: string, productAttributeUuid?: string | null, quantity?: number, surchargeValueUuids?: string[]) => void;
    onCatalogNavigate?: CatalogNavigateHandler;
    onToggleFavorite: (productId: string) => void;
}) {
    const message = useShopStorefrontMessage();
    const storefrontHref = useShopStorefrontHref();
    const commerce = useContext(ShopCommerceContentContext);
    const productPageReference = lagunaCatalogProductPageReference(block);
    const config = catalogProductPageConfig(productPageReference?.block);
    const catalogProducts = catalogProductsFromContext(context);
    const catalogTotal = catalogTotalFromContext(context, catalogProducts.length);
    const catalogSort = catalogSortFromContext(context);
    const catalogFilters = catalogFiltersFromContext(context);
    const catalogQuery = catalogQueryFromContext(context);
    const baseHref = context.canonicalHref ?? storefrontHref(context.slug ? `/${context.slug}` : "/");
    const saleLocked = context.routeType === "sale";
    const allFacets = catalogFacetsWithSale(
        catalogFacetsFromContext(context),
        catalogFilters,
        !saleLocked,
        message("ui.catalog.saleFacet"),
        message("ui.catalog.saleOption"),
    );
    const catalogFacets = lagunaProductPageFacets(allFacets, config);
    const selectedFacetOptions = selectedCatalogFacetOptions(catalogFacets);
    const activeFilterCount = activeCatalogFilterCount(catalogFilters, !saleLocked);
    const topCategorySource = commerce?.activeSectionItems.length
        ? commerce.activeSectionItems
        : commerce?.headerMenuItems.flatMap((item) => item.children.length > 0 ? item.children : [item]) ?? [];
    const topCategoriesItems = Array.from(new Map(topCategorySource.map((item) => [item.uuid, item])).values());
    const sidebarCategorySource = (
        config.categoryStyle === "imageIndex"
        || config.categoryStyle === "playfulIndex"
    ) && commerce?.headerMenuItems.length
        ? commerce.headerMenuItems
        : topCategoriesItems;
    const sidebarCategories = Array.from(new Map(sidebarCategorySource.map((item) => [item.uuid, item])).values());
    const activeCatalogItemData = nestedRecord(
        contextData(context),
        context.routeType === "section" ? "section" : "category",
    );
    const activeCatalogItemUuid = stringOrNull(activeCatalogItemData.uuid)
        ?? commerce?.activeSection?.uuid
        ?? null;
    const activeCatalogPathUuids = useMemo(() => {
        const uuids = activeMenuUuidsFromContext(context);

        if (commerce?.activeSection?.uuid) uuids.add(commerce.activeSection.uuid);

        return uuids;
    }, [commerce?.activeSection?.uuid, context]);
    const authoredCategoryImageUrls = catalogCategoryImageUrls(productPageReference?.block);
    const categoryImageUrls = topCategoriesItems.map((_, index) => authoredCategoryImageUrls[index] ?? null);
    const integratedEditorialLayout = Boolean(
        integratedCategoryIntro
        && config.filterStyle === "editorialExpand"
        && config.productDesign === "editorialGrid",
    );
    const integratedBoutiqueLayout = Boolean(
        integratedCategoryIntro
        && config.filterStyle === "boutiqueSidebar"
        && config.productDesign === "boutiqueGrid",
    );
    const playfulSidebarLayout = config.filterStyle === "playfulSidebar";
    const playfulCatalogPresentation = config.categoryStyle === "playfulIndex"
        || playfulSidebarLayout
        || config.productDesign === "playfulCards";
    const integratedPlayfulLayout = Boolean(
        integratedCategoryIntro
        && playfulSidebarLayout,
    );
    const categoriesInSidebar = !integratedEditorialLayout
        && (config.categoriesPlacement === "sidebar" || config.categoriesPlacement === "both")
        && sidebarCategories.length > 0;
    const filtersInSidebar = config.filtersMode === "sidebar";
    const hasSidebar = categoriesInSidebar || filtersInSidebar;
    const playfulAfterResultsFilters = playfulSidebarLayout
        && (categoriesInSidebar || filtersInSidebar);
    const playfulDrawerNeeded = playfulSidebarLayout
        && config.filtersMode !== "hidden"
        && config.filtersMode !== "sidebar";
    const hasMobileDrawer = !integratedBoutiqueLayout
        && (!playfulAfterResultsFilters || playfulDrawerNeeded)
        && (categoriesInSidebar || config.filtersMode !== "hidden");
    const [filtersOpen, setFiltersOpen] = useState(false);
    const editorialInlinePanel = config.filterStyle === "editorialExpand"
        && config.filtersMode === "offscreen";
    const integratedStructuredLayout = Boolean(
        integratedCategoryIntro
        && config.categoriesPlacement === "both"
        && config.categoryStyle === "imageIndex"
        && config.filterStyle === "structuredSidebar"
        && config.filtersMode === "sidebar",
    );
    const integratedReferenceLayout = integratedEditorialLayout
        || integratedBoutiqueLayout
        || integratedPlayfulLayout
        || integratedStructuredLayout;
    const whiteCommerceCatalog = config.filterStyle === "structuredSidebar"
        || config.productDesign === "classicBordered";

    useEffect(() => {
        if (config.filtersMode === "hidden") setFiltersOpen(false);
    }, [config.filtersMode]);

    const topCategories = config.categoriesPlacement === "top" || config.categoriesPlacement === "both" ? (
        <div className="mb-6" data-catalog-top-categories>
            <LagunaCatalogCategories
                activeItemUuid={activeCatalogItemUuid}
                activePathUuids={activeCatalogPathUuids}
                collapsed={config.collapseCategories}
                imageUrls={categoryImageUrls}
                items={topCategoriesItems}
                onNavigate={onCatalogNavigate}
                style={config.categoryStyle}
            />
        </div>
    ) : null;
    const toolbar = (
        <LagunaCatalogProductToolbar
            activeFilterCount={activeFilterCount}
            baseHref={baseHref}
            config={config}
            filters={catalogFilters}
            onNavigate={onCatalogNavigate}
            onOpenFilters={() => setFiltersOpen((current) => editorialInlinePanel ? !current : true)}
            query={catalogQuery}
            saleLocked={saleLocked}
            selectedOptions={selectedFacetOptions}
            showCategoryMenu={categoriesInSidebar}
            showPlayfulSearch={playfulSidebarLayout && config.toolbarSearch && !hasSidebar}
            sort={catalogSort}
            total={catalogTotal}
        />
    );
    const expandedEditorialFilters = editorialInlinePanel && filtersOpen ? (
        <div
            className="catalog-editorial-inline-panel mb-10 hidden border-y border-[var(--site-text)] py-2 lg:block"
            data-catalog-inline-expanded="true"
        >
            <CatalogFilterPanel
                baseHref={baseHref}
                catalogFacets={catalogFacets}
                catalogTotal={catalogTotal}
                collapseSections
                currentSort={catalogSort}
                filters={catalogFilters}
                onNavigate={onCatalogNavigate}
                presentationStyle={config.filterStyle}
                query={catalogQuery}
                saleLocked={saleLocked}
            />
        </div>
    ) : null;
    const inlineFilters = config.filtersMode === "inline" ? (
        <div className="catalog-inline-filters mb-8 hidden lg:block">
            <CatalogFilterPanel
                baseHref={baseHref}
                catalogFacets={catalogFacets}
                catalogTotal={catalogTotal}
                collapseSections={false}
                currentSort={catalogSort}
                filters={catalogFilters}
                onNavigate={onCatalogNavigate}
                presentationStyle={config.filterStyle}
                query={catalogQuery}
                saleLocked={saleLocked}
            />
        </div>
    ) : null;
    const mobileBoutiqueFilters = integratedBoutiqueLayout && filtersInSidebar ? (
        <div className="catalog-mobile-boutique-filters mb-8 lg:hidden">
            <CatalogFilterPanel
                baseHref={baseHref}
                catalogFacets={catalogFacets}
                catalogTotal={catalogTotal}
                collapseSections={config.collapseSidebarSections}
                currentSort={catalogSort}
                filters={catalogFilters}
                onNavigate={onCatalogNavigate}
                presentationStyle={config.filterStyle}
                query={catalogQuery}
                saleLocked={saleLocked}
            />
        </div>
    ) : null;
    const mobilePlayfulFilters = playfulAfterResultsFilters ? (
        <div className="catalog-mobile-playful-filters mb-8 lg:hidden">
            {config.toolbarSearch ? (
                <div className="mb-7" data-catalog-playful-search>
                    <CatalogTextFilter
                        baseHref={baseHref}
                        currentSort={catalogSort}
                        filters={catalogFilters}
                        onNavigate={onCatalogNavigate}
                        query={catalogQuery}
                    />
                </div>
            ) : null}
            {categoriesInSidebar ? (
                <div className="mb-7">
                    <LagunaCatalogCategories
                        activeItemUuid={activeCatalogItemUuid}
                        activePathUuids={activeCatalogPathUuids}
                        collapsed={config.collapseCategories}
                        items={sidebarCategories}
                        onNavigate={onCatalogNavigate}
                        sidebar
                        style={config.categoryStyle}
                    />
                </div>
            ) : null}
            {filtersInSidebar ? (
                <CatalogFilterPanel
                    baseHref={baseHref}
                    catalogFacets={catalogFacets}
                    catalogTotal={catalogTotal}
                    collapseSections={config.collapseSidebarSections}
                    currentSort={catalogSort}
                    filters={catalogFilters}
                    onNavigate={onCatalogNavigate}
                    presentationStyle={config.filterStyle}
                    query={catalogQuery}
                    saleLocked={saleLocked}
                />
            ) : null}
        </div>
    ) : null;

    return (
        <section
            className="shop-catalog-presentation bg-[var(--site-background)] px-5 py-10 sm:px-6"
            data-catalog-category-style={config.categoryStyle}
            data-catalog-filter-mode={config.filtersMode}
            data-catalog-filter-style={config.filterStyle}
            data-catalog-integrated-layout={
                integratedEditorialLayout
                    ? "editorial-index"
                    : integratedBoutiqueLayout
                        ? "boutique-index"
                        : integratedPlayfulLayout
                            ? "playful-index"
                            : integratedStructuredLayout ? "category-index" : undefined
            }
            data-catalog-playful-presentation={playfulCatalogPresentation ? "true" : undefined}
            data-catalog-product-design={config.productDesign}
            style={whiteCommerceCatalog
                ? mergeStyles(blockVisualStyle(block), WHITE_COMMERCE_PRESENTATION_STYLE)
                : blockVisualStyle(block)}
        >
            <style>{CATALOG_EDITORIAL_PRESENTATION_CSS}</style>
            {hasMobileDrawer ? (
                <CatalogMobileFilterDrawer
                    open={filtersOpen}
                    baseHref={baseHref}
                    categoryMenu={categoriesInSidebar ? {
                        activeItemUuid: activeCatalogItemUuid,
                        activePathUuids: activeCatalogPathUuids,
                        collapsed: config.collapseCategories,
                        items: sidebarCategories,
                        style: config.categoryStyle,
                    } : undefined}
                    catalogFacets={catalogFacets}
                    catalogFilters={catalogFilters}
                    catalogQuery={catalogQuery}
                    catalogSort={catalogSort}
                    catalogTotal={catalogTotal}
                    collapseSections={config.collapseSidebarSections}
                    desktop={config.filtersMode === "offscreen"}
                    onClose={() => setFiltersOpen(false)}
                    onNavigate={onCatalogNavigate}
                    presentationStyle={config.filterStyle}
                    saleLocked={saleLocked}
                    showFilters={config.filtersMode !== "hidden"}
                    showSearch={false}
                />
            ) : null}
            <div
                className={classNames(
                    "mx-auto min-w-0",
                    integratedBoutiqueLayout || integratedPlayfulLayout
                        ? "max-w-[1680px]"
                        : integratedEditorialLayout || config.contentWidth === "regular"
                            ? "max-w-[var(--site-container)]"
                            : "max-w-none",
                )}
                data-catalog-content-width={config.contentWidth}
                data-shop-block-part={productPageReference?.part}
                style={productPageReference ? blockVisualStyle(productPageReference.block) : undefined}
            >
                {integratedBoutiqueLayout ? integratedCategoryIntro : null}
                {integratedBoutiqueLayout ? topCategories : null}
                {integratedPlayfulLayout ? integratedCategoryIntro : null}
                {integratedPlayfulLayout ? topCategories : null}
                {!integratedReferenceLayout ? topCategories : null}
                {!integratedReferenceLayout ? toolbar : null}
                {!integratedReferenceLayout ? expandedEditorialFilters : null}
                {!integratedReferenceLayout ? inlineFilters : null}
                <div className={classNames(
                    "min-w-0",
                    (hasSidebar || integratedEditorialLayout) && "grid gap-8",
                    integratedEditorialLayout && "lg:grid-cols-[340px_minmax(0,1fr)] lg:gap-8",
                    hasSidebar && config.filterStyle === "boutiqueSidebar" && "lg:grid-cols-[358px_minmax(0,1fr)]",
                    hasSidebar && config.filterStyle === "playfulSidebar" && "lg:grid-cols-[280px_minmax(0,1fr)]",
                    hasSidebar && config.filterStyle === "structuredSidebar" && "lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-10",
                    hasSidebar
                        && config.filterStyle !== "boutiqueSidebar"
                        && config.filterStyle !== "playfulSidebar"
                        && config.filterStyle !== "structuredSidebar"
                        && "lg:grid-cols-[280px_minmax(0,1fr)]",
                    catalogUpdating && "pointer-events-none opacity-60",
                )}>
                    {integratedEditorialLayout ? (
                        <aside className="min-w-0" data-catalog-editorial-title-rail>
                            {integratedCategoryIntro}
                        </aside>
                    ) : hasSidebar ? (
                        <aside className="hidden min-w-0 space-y-4 lg:sticky lg:top-5 lg:block lg:self-start">
                            {playfulSidebarLayout && config.toolbarSearch ? (
                                <div className="mb-7" data-catalog-playful-search>
                                    <CatalogTextFilter
                                        baseHref={baseHref}
                                        currentSort={catalogSort}
                                        filters={catalogFilters}
                                        onNavigate={onCatalogNavigate}
                                        query={catalogQuery}
                                    />
                                </div>
                            ) : null}
                            {categoriesInSidebar ? (
                                <LagunaCatalogCategories
                                    activeItemUuid={activeCatalogItemUuid}
                                    activePathUuids={activeCatalogPathUuids}
                                    collapsed={config.collapseCategories}
                                    items={sidebarCategories}
                                    onNavigate={onCatalogNavigate}
                                    sidebar
                                    style={integratedBoutiqueLayout ? "imageIndex" : config.categoryStyle}
                                />
                            ) : null}
                            {filtersInSidebar ? (
                                <CatalogFilterPanel
                                    baseHref={baseHref}
                                    catalogFacets={catalogFacets}
                                    catalogTotal={catalogTotal}
                                    collapseSections={config.collapseSidebarSections}
                                    currentSort={catalogSort}
                                    filters={catalogFilters}
                                    onNavigate={onCatalogNavigate}
                                    presentationStyle={config.filterStyle}
                                    query={catalogQuery}
                                    saleLocked={saleLocked}
                                />
                            ) : null}
                        </aside>
                    ) : null}
                    <div className="min-w-0">
                        {integratedStructuredLayout ? integratedCategoryIntro : null}
                        {integratedStructuredLayout ? topCategories : null}
                        {integratedStructuredLayout ? integratedCategoryRail : null}
                        {integratedStructuredLayout ? toolbar : null}
                        {integratedStructuredLayout ? expandedEditorialFilters : null}
                        {integratedStructuredLayout ? inlineFilters : null}
                        {integratedEditorialLayout ? topCategories : null}
                        {integratedEditorialLayout ? toolbar : null}
                        {integratedEditorialLayout ? expandedEditorialFilters : null}
                        {integratedEditorialLayout ? inlineFilters : null}
                        {integratedBoutiqueLayout ? toolbar : null}
                        {integratedBoutiqueLayout ? mobileBoutiqueFilters : null}
                        {integratedPlayfulLayout ? toolbar : null}
                        <ProductGrid
                            addingProductUuid={addingProductUuid}
                            favorites={favorites}
                            onAddToCart={onAddToCart}
                            onToggleFavorite={onToggleFavorite}
                            productPageReference={productPageReference}
                            publicProducts={catalogProducts}
                        />
                        <LagunaCatalogPagination
                            baseHref={baseHref}
                            context={context}
                            filters={catalogFilters}
                            onNavigate={onCatalogNavigate}
                            query={catalogQuery}
                            sort={catalogSort}
                        />
                        {mobilePlayfulFilters}
                    </div>
                </div>
            </div>
        </section>
    );
}

function currentStoreFromContext(context: ShopPageContext | null | undefined): PublicStoreLink | null {
    const store = storeFromRecord(contextData(context).store);

    return store ?? storesFromContext(context)[0] ?? null;
}

function storeMapSrc(store: PublicStoreLink): string {
    if (store.latitude !== null && store.longitude !== null) {
        const lat = store.latitude;
        const lng = store.longitude;
        const bbox = [
            (lng - 0.01).toFixed(6),
            (lat - 0.006).toFixed(6),
            (lng + 0.01).toFixed(6),
            (lat + 0.006).toFixed(6),
        ].join(",");

        return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat.toFixed(6)},${lng.toFixed(6)}`;
    }

    const query = encodeURIComponent(`${store.name} ${store.addressLine}`);

    return `https://maps.google.com/maps?q=${query}&output=embed`;
}

function storeDirectionsHref(store: PublicStoreLink): string {
    const query = store.latitude !== null && store.longitude !== null
        ? `${store.latitude},${store.longitude}`
        : `${store.name} ${store.addressLine}`;

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function StoreOpeningHours({ store }: { store: PublicStoreLink }) {
    const message = useShopStorefrontMessage();
    const openingHours = store.openingHours;

    if (!openingHours || Object.keys(openingHours).length === 0) {
        return <p className="text-sm text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">{message("ui.store.openingHoursMissing")}</p>;
    }

    const dayLabels: Record<string, string> = {
        monday: message("ui.store.day.monday"),
        tuesday: message("ui.store.day.tuesday"),
        wednesday: message("ui.store.day.wednesday"),
        thursday: message("ui.store.day.thursday"),
        friday: message("ui.store.day.friday"),
        saturday: message("ui.store.day.saturday"),
        sunday: message("ui.store.day.sunday"),
    };

    return (
        <dl className="grid gap-x-10 gap-y-3 text-sm sm:grid-cols-2">
            {Object.entries(openingHours).map(([day, hours]) => (
                <div key={day} className="flex items-center justify-between gap-4 border-b border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] pb-3">
                    <dt className="font-bold text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">{dayLabels[day] ?? day}</dt>
                    <dd className="text-right font-black">{typeof hours === "string" ? hours : JSON.stringify(hours)}</dd>
                </div>
            ))}
        </dl>
    );
}

function StoreHorizontalNavigation({
    context,
    currentStoreUuid,
}: {
    context: ShopPageContext;
    currentStoreUuid: string;
}) {
    const message = useShopStorefrontMessage();
    const languageCode = useContext(ShopStorefrontLanguageContext);
    const storesHref = storefrontSpecialRouteHref("stores", languageCode);
    const cityStores = storeCitiesFromContext(context).flatMap((city) => city.stores.map((store) => ({
        city: city.name,
        store,
    })));

    if (cityStores.length === 0) {
        return null;
    }

    return (
        <nav className="border-b border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] pb-5" aria-label={message("ui.store.selection")}>
            <div className="flex gap-3 overflow-x-auto pb-2">
                <a
                    href={storesHref}
                    className="inline-flex h-12 shrink-0 items-center rounded-full border border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] px-5 text-sm font-black text-[color-mix(in_srgb,var(--site-text)_70%,transparent)] transition hover:border-[var(--site-primary)]/25 hover:text-[var(--site-primary)]"
                >
                    {message("ui.store.all")}
                </a>
                {cityStores.map(({ city, store }) => {
                    const active = store.uuid === currentStoreUuid;

                    return (
                        <a
                            key={store.uuid}
                            href={store.href}
                            className={classNames(
                                "inline-flex h-12 shrink-0 items-center gap-3 rounded-full px-5 text-sm font-black transition",
                                active
                                    ? "bg-[var(--site-primary)] text-[var(--site-on-primary)] shadow-[0_12px_30px_var(--site-shadow-color)]"
                                    : "bg-[var(--site-surface)] text-[color-mix(in_srgb,var(--site-text)_70%,transparent)] hover:bg-[var(--site-surface)] hover:text-[var(--site-primary)] hover:shadow-sm",
                            )}
                            >
                                <span>{city}</span>
                                <span className={classNames("h-1.5 w-1.5 rounded-full", active ? "bg-[color-mix(in_srgb,var(--site-surface)_60%,transparent)]" : "bg-[color-mix(in_srgb,var(--site-text)_20%,transparent)]")} />
                                <span>{store.name}</span>
                            </a>
                        );
                })}
            </div>
        </nav>
    );
}

function StoreInfoItem({
    icon,
    label,
    children,
}: {
    icon: IconName;
    label: string;
    children: ReactNode;
}) {
    return (
        <div className="flex min-w-0 gap-4">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[var(--site-surface)] text-[var(--site-primary)]">
                <Icon name={icon} className="h-5 w-5" />
            </span>
            <div className="min-w-0">
                <p className="text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_35%,transparent)]">{label}</p>
                <div className="mt-2 text-sm font-bold leading-6 text-[color-mix(in_srgb,var(--site-text)_75%,transparent)]">{children}</div>
            </div>
        </div>
    );
}

function storeCoordinatesText(store: PublicStoreLink, message: ShopStorefrontMessage): string {
    if (store.latitude === null || store.longitude === null) {
        return message("ui.store.coordinatesMissing");
    }

    return `${store.latitude.toFixed(5)}, ${store.longitude.toFixed(5)}`;
}

function storeCityAnchorId(cityName: string): string {
    const slug = cityName
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    let hash = 2166136261;

    for (let index = 0; index < cityName.length; index += 1) {
        hash ^= cityName.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }

    return `store-city-${slug || "city"}-${(hash >>> 0).toString(16)}`;
}

function storeCountText(count: number, languageCode: string | null, message: ShopStorefrontMessage): string {
    switch (storefrontPluralCategory(count, languageCode)) {
        case "one":
            return message("ui.store.count.one", { count });
        case "few":
            return message("ui.store.count.few", { count });
        case "many":
            return message("ui.store.count.many", { count });
        default:
            return message("ui.store.count.other", { count });
    }
}

function activeStoreCountLabel(count: number, languageCode: string | null, message: ShopStorefrontMessage): string {
    switch (storefrontPluralCategory(count, languageCode)) {
        case "one":
            return message("ui.store.active.one");
        case "few":
            return message("ui.store.active.few");
        case "many":
            return message("ui.store.active.many");
        default:
            return message("ui.store.active.other");
    }
}

function StoreListLayout({ context }: { context: ShopPageContext }) {
    const message = useShopStorefrontMessage();
    const languageCode = useContext(ShopStorefrontLanguageContext);
    const cities = storeCitiesFromContext(context);
    const stores = cities.flatMap((city) => city.stores.map((store) => ({ city: city.name, store })));

    return (
        <section className="bg-[var(--site-surface)] px-5 py-10 sm:px-6">
            <div className="mx-auto max-w-[var(--site-container)]">
                <div className="border-b border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] pb-8">
                    <p className="text-xs font-black uppercase text-[var(--site-primary)]">{message("ui.store.visitEyebrow")}</p>
                    <h1 className="mt-3 max-w-4xl text-4xl font-black tracking-normal text-[var(--site-text)] sm:text-6xl">{context.title}</h1>
                    <p className="mt-5 max-w-3xl text-base leading-7 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]">
                        {message("ui.store.listDescription")}
                    </p>
                </div>

                <nav className="mt-6 flex gap-3 overflow-x-auto pb-2" aria-label={message("ui.store.cityNavigation")}>
                    {cities.map((city) => (
                        <a
                            key={city.name}
                            href={`#${storeCityAnchorId(city.name)}`}
                            className="inline-flex h-12 shrink-0 items-center gap-3 rounded-full bg-[var(--site-surface)] px-5 text-sm font-black text-[color-mix(in_srgb,var(--site-text)_70%,transparent)] transition hover:bg-[var(--site-surface)] hover:text-[var(--site-primary)] hover:shadow-sm"
                        >
                            <span>{city.name}</span>
                            <span className="rounded-full bg-[var(--site-surface)] px-2 py-0.5 text-xs text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">{city.stores.length}</span>
                        </a>
                    ))}
                </nav>

                <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_340px]">
                    <div>
                        <div className="space-y-12">
                            {cities.map((city) => (
                                <section key={city.name} id={storeCityAnchorId(city.name)}>
                                    <div className="mb-5 flex items-end justify-between gap-4">
                                        <div>
                                            <p className="text-xs font-black uppercase text-[var(--site-primary)]">{message("ui.store.city")}</p>
                                            <h2 className="mt-1 text-3xl font-black text-[var(--site-text)]">{city.name}</h2>
                                        </div>
                                        <p className="text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">{storeCountText(city.stores.length, languageCode, message)}</p>
                                    </div>
                                    <div className="grid gap-5">
                                        {city.stores.map((store) => (
                                            <article key={store.uuid} className="group border-b border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] pb-6">
                                                <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
                                                    <div>
                                                        <h3 className="text-2xl font-black text-[var(--site-text)]">
                                                            <a href={store.href} className="transition group-hover:text-[var(--site-primary)]">{store.name}</a>
                                                        </h3>
                                                        {store.description ? (
                                                            <p className="mt-3 max-w-2xl text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">{store.description}</p>
                                                        ) : null}
                                                        <div className="mt-5 grid gap-4 sm:grid-cols-3">
                                                            <StoreInfoItem icon="mapPin" label={message("ui.store.address")}>
                                                                <p>{store.addressLine}</p>
                                                            </StoreInfoItem>
                                                            <StoreInfoItem icon="phone" label={message("ui.store.contact")}>
                                                                <div className="space-y-1">
                                                                    {store.phone ? <a className="block hover:text-[var(--site-primary)]" href={`tel:${store.phone}`}>{store.phone}</a> : null}
                                                                    {store.email ? <a className="block hover:text-[var(--site-primary)]" href={`mailto:${store.email}`}>{store.email}</a> : null}
                                                                </div>
                                                            </StoreInfoItem>
                                                            <StoreInfoItem icon="navigation" label={message("ui.store.coordinates")}>
                                                                <p>{storeCoordinatesText(store, message)}</p>
                                                            </StoreInfoItem>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap gap-3 md:justify-end">
                                                        {store.pickupEnabled ? (
                                                            <span className="inline-flex h-10 items-center rounded-full bg-[var(--site-accent-soft)] px-4 text-xs font-black text-[var(--site-accent)]">
                                                                {message("ui.store.pickup")}
                                                            </span>
                                                        ) : null}
                                                        <a
                                                            href={store.href}
                                                            className="inline-flex h-10 items-center gap-2 rounded-full bg-[var(--site-primary)] px-4 text-xs font-black text-[var(--site-on-primary)] transition hover:-translate-y-0.5 hover:shadow-[0_12px_28px_var(--site-shadow-color)]"
                                                        >
                                                            {message("ui.store.details")}
                                                            <Icon name="chevronRight" className="h-4 w-4" />
                                                        </a>
                                                    </div>
                                                </div>
                                            </article>
                                        ))}
                                    </div>
                                </section>
                            ))}
                        </div>
                    </div>
                    <aside className="lg:sticky lg:top-6 lg:self-start">
                        <div className="rounded-[28px] bg-[var(--site-surface)] p-6">
                            <p className="text-xs font-black uppercase text-[var(--site-primary)]">{message("ui.store.quickOverview")}</p>
                            <p className="mt-2 text-3xl font-black text-[var(--site-text)]">{stores.length}</p>
                            <p className="mt-1 text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]">{activeStoreCountLabel(stores.length, languageCode, message)}</p>
                            <div className="mt-6 space-y-3">
                                {stores.map(({ city, store }) => (
                                    <a key={store.uuid} href={store.href} className="block rounded-2xl bg-[var(--site-surface)] px-4 py-3 transition hover:-translate-y-0.5 hover:text-[var(--site-primary)] hover:shadow-sm">
                                        <span className="block text-sm font-black">{store.name}</span>
                                        <span className="mt-1 block text-xs font-bold text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]">{city} • {store.addressLine}</span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </section>
    );
}

function StoreDetailLayout({ context }: { context: ShopPageContext }) {
    const message = useShopStorefrontMessage();
    const store = currentStoreFromContext(context);

    if (!store) {
        return <StoreListLayout context={context} />;
    }

    return (
        <section className="bg-[var(--site-surface)] px-5 py-10 sm:px-6">
            <div className="mx-auto max-w-[var(--site-container)]">
                <StoreHorizontalNavigation context={context} currentStoreUuid={store.uuid} />

                <div className="pt-10">
                    <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
                        <div>
                            <p className="text-xs font-black uppercase text-[var(--site-primary)]">{store.city}</p>
                            <h1 className="mt-3 max-w-4xl text-4xl font-black tracking-normal text-[var(--site-text)] sm:text-6xl">{store.name}</h1>
                            {store.description ? (
                                <p className="mt-5 max-w-3xl text-base leading-7 text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]">{store.description}</p>
                            ) : null}
                        </div>
                        <div className="flex flex-wrap gap-3">
                            {store.pickupEnabled ? (
                                <span className="inline-flex items-center rounded-full bg-[var(--site-accent-soft)] px-5 py-3 text-sm font-black text-[var(--site-accent)]">
                                    {message("ui.store.pickupAvailable")}
                                </span>
                            ) : null}
                            <a
                                href={storeDirectionsHref(store)}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 rounded-full bg-[var(--site-primary)] px-5 py-3 text-sm font-black text-[var(--site-on-primary)] transition hover:-translate-y-0.5 hover:shadow-[0_12px_28px_var(--site-shadow-color)]"
                            >
                                <Icon name="navigation" className="h-4 w-4" />
                                {message("ui.store.navigate")}
                            </a>
                        </div>
                    </div>

                    <div className="mt-10 grid gap-8 lg:grid-cols-3">
                        <StoreInfoItem icon="mapPin" label={message("ui.store.address")}>
                            <p>{store.addressLine}</p>
                            <a href={storeDirectionsHref(store)} target="_blank" rel="noreferrer" className="mt-2 inline-flex text-[var(--site-primary)] hover:underline">
                                {message("ui.store.openNavigation")}
                            </a>
                        </StoreInfoItem>
                        <StoreInfoItem icon="phone" label={message("ui.store.contact")}>
                            <div className="space-y-1">
                                {store.phone ? <a className="block hover:text-[var(--site-primary)]" href={`tel:${store.phone}`}>{store.phone}</a> : null}
                                {store.email ? (
                                    <a className="flex items-center gap-2 hover:text-[var(--site-primary)]" href={`mailto:${store.email}`}>
                                        <Icon name="mail" className="h-4 w-4" />
                                        {store.email}
                                    </a>
                                ) : null}
                                {!store.phone && !store.email ? <p>{message("ui.store.contactMissing")}</p> : null}
                            </div>
                        </StoreInfoItem>
                        <StoreInfoItem icon="navigation" label={message("ui.store.coordinates")}>
                            <p>{storeCoordinatesText(store, message)}</p>
                        </StoreInfoItem>
                    </div>

                    <hr className="mt-12 border-[color-mix(in_srgb,var(--site-text)_10%,transparent)]" />

                    <section className="mt-10">
                        <div>
                            <p className="text-xs font-black uppercase text-[var(--site-primary)]">{message("ui.store.openingHoursEyebrow")}</p>
                            <h2 className="mt-2 text-2xl font-black text-[var(--site-text)] sm:text-3xl">{message("ui.store.openingHours")}</h2>
                            <p className="mt-3 max-w-xl text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">
                                {message("ui.store.openingHoursDescription")}
                            </p>
                        </div>
                        <div className="mt-7 max-w-4xl">
                            <StoreOpeningHours store={store} />
                        </div>
                    </section>

                    <section className="mt-12">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                            <div>
                                <p className="text-xs font-black uppercase text-[var(--site-primary)]">{message("ui.store.map")}</p>
                                <h2 className="mt-2 text-2xl font-black text-[var(--site-text)] sm:text-3xl">{message("ui.store.where")}</h2>
                            </div>
                            <p className="max-w-xl text-sm leading-6 text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]">{store.addressLine}</p>
                        </div>
                        <div className="mt-7 overflow-hidden rounded-[28px] bg-[var(--site-surface)] shadow-[0_18px_60px_var(--site-shadow-color)]">
                            <iframe
                                title={message("ui.store.mapTitle", { name: store.name })}
                                src={storeMapSrc(store)}
                                className="h-[420px] w-full border-0 sm:h-[520px]"
                                loading="lazy"
                            />
                        </div>
                    </section>

                </div>
            </div>
        </section>
    );
}

function GenericSystemBlock({
    block,
    index,
    context,
    favorites,
    favoriteProducts,
    catalogUpdating,
    onToggleFavorite,
    onCatalogNavigate,
    cart,
    cartLoading,
    cartHref,
    deliveryPaymentHref,
    contactHref,
    checkoutHref,
    checkoutRouteStep,
    checkoutOptions,
    checkoutOptionsLoading,
    cartError,
    packetaApiKey,
    checkoutForm,
    checkoutSubmitting,
    checkoutError,
    createdOrder,
    customerSession,
    customerLoading,
    customerError,
    customerApiBasePath,
    orderApiBasePath,
    customerAddresses,
    onCheckoutRouteStepChange,
    onSelectDeliveryPayment,
    onCheckoutFormChange,
    onSubmitCheckout,
    onCustomerLogin,
    onCustomerRegister,
    onCustomerLogout,
    onCustomerProfileUpdate,
    onCustomerPasswordChange,
    onCustomerPasswordResetRequest,
    onCustomerPasswordResetConfirm,
    onCustomerReorder,
    onCustomerAddressesChange,
    onUpdateCartItem,
    onRemoveCartItem,
}: {
    block: ShopTemplateBlock;
    index: number;
    context: ShopPageContext;
    favorites: Set<string>;
    favoriteProducts: FavoriteProductPreview[];
    catalogUpdating: boolean;
    onToggleFavorite: (productId: string) => void;
    onCatalogNavigate?: CatalogNavigateHandler;
    cart: PublicCart;
    cartLoading: boolean;
    cartHref: string;
    deliveryPaymentHref: string;
    contactHref: string;
    checkoutHref: string;
    checkoutRouteStep: CheckoutRouteStep;
    checkoutOptions: PublicCheckoutOptions;
    checkoutOptionsLoading: boolean;
    cartError: string | null;
    packetaApiKey?: string;
    checkoutForm: CheckoutFormState;
    checkoutSubmitting: boolean;
    checkoutError: string | null;
    createdOrder: PublicOrderCreated | null;
    customerSession: PublicCustomerSession;
    customerLoading: boolean;
    customerError: string | null;
    customerApiBasePath: string | null;
    orderApiBasePath: string | null;
    customerAddresses: PublicCustomerAddress[];
    onCheckoutRouteStepChange: (step: CheckoutRouteStep) => void;
    onSelectDeliveryPayment: (
        carrierRateUuid: string,
        carrierPaymentOptionUuid: string,
        pickupPoint?: PickupPointSelection,
    ) => Promise<boolean>;
    onCheckoutFormChange: (patch: Partial<CheckoutFormState>) => void;
    onSubmitCheckout: (form: CheckoutFormState) => Promise<boolean>;
    onCustomerLogin: (email: string, password: string) => Promise<boolean>;
    onCustomerRegister: (
        email: string,
        password: string,
        passwordConfirmation: string,
        newsletterOptOut: boolean,
    ) => Promise<boolean>;
    onCustomerLogout: () => Promise<void>;
    onCustomerProfileUpdate: (profile: PublicCustomerProfileUpdate) => Promise<boolean>;
    onCustomerPasswordChange: (
        currentPassword: string,
        newPassword: string,
        newPasswordConfirmation: string,
    ) => Promise<boolean>;
    onCustomerPasswordResetRequest: (email: string) => Promise<boolean>;
    onCustomerPasswordResetConfirm: (
        token: string,
        newPassword: string,
        newPasswordConfirmation: string,
    ) => Promise<boolean>;
    onCustomerReorder: (orderUuid: string) => Promise<PublicOrderReorderResponse | null>;
    onCustomerAddressesChange: (addresses: PublicCustomerAddress[]) => void;
    onUpdateCartItem: (itemUuid: string, quantity: number) => void;
    onRemoveCartItem: (itemUuid: string) => void;
}) {
    const message = useShopStorefrontMessage();
    const { formatMoney } = useShopStorefrontFormatting();

    if (block.type === "richContent") {
        return <RichContentPreview block={block} context={context} />;
    }

    if (block.type === "systemCheckoutSteps") {
        return (
            <CheckoutStepsPreview
                block={block}
                cart={cart}
                cartHref={cartHref}
                deliveryPaymentHref={deliveryPaymentHref}
                contactHref={contactHref}
                checkoutRouteStep={checkoutRouteStep}
                checkoutOptions={checkoutOptions}
                checkoutOptionsLoading={checkoutOptionsLoading}
                cartError={cartError}
                packetaApiKey={packetaApiKey}
                checkoutForm={checkoutForm}
                checkoutSubmitting={checkoutSubmitting}
                checkoutError={checkoutError}
                createdOrder={createdOrder}
                onCheckoutRouteStepChange={onCheckoutRouteStepChange}
                onSelectDeliveryPayment={onSelectDeliveryPayment}
                onCheckoutFormChange={onCheckoutFormChange}
                onSubmitCheckout={onSubmitCheckout}
            />
        );
    }

    if (block.type === "systemCartItems") {
        return (
            <CartItemsPreview
                block={block}
                cart={cart}
                loading={cartLoading}
                error={cartError}
                cartHref={cartHref}
                onUpdateItem={onUpdateCartItem}
                onRemoveItem={onRemoveCartItem}
            />
        );
    }

    if (block.type === "systemCartSummary") {
        return (
            <CartSummaryPreview
                block={block}
                cart={cart}
                checkoutHref={checkoutHref}
                freeShippingMessage={cartFreeShippingMessage(cart, checkoutOptions, message, formatMoney)}
            />
        );
    }

    if (block.type === "systemSearchForm") {
        return (
            <SearchFormPreview
                block={block}
                context={context}
                onCatalogNavigate={onCatalogNavigate}
            />
        );
    }

    if (block.type === "systemProductList") {
        return (
            <SystemProductListPreview
                block={block}
                context={context}
                favorites={favorites}
                catalogUpdating={catalogUpdating}
                onCatalogNavigate={onCatalogNavigate}
                onToggleFavorite={onToggleFavorite}
            />
        );
    }

    if (block.type === "systemAccountNavigation") {
        return (
            <AccountNavigationPreview
                block={block}
                customerSession={customerSession}
                customerLoading={customerLoading}
                customerError={customerError}
                customerApiBasePath={customerApiBasePath}
                orderApiBasePath={orderApiBasePath}
                customerAddresses={customerAddresses}
                favoriteProducts={favoriteProducts}
                favoriteCount={favorites.size}
                onLogin={onCustomerLogin}
                onRegister={onCustomerRegister}
                onLogout={onCustomerLogout}
                onUpdateProfile={onCustomerProfileUpdate}
                onChangePassword={onCustomerPasswordChange}
                onRequestPasswordReset={onCustomerPasswordResetRequest}
                onConfirmPasswordReset={onCustomerPasswordResetConfirm}
                onReorder={onCustomerReorder}
                onCustomerAddressesChange={onCustomerAddressesChange}
                onToggleFavorite={onToggleFavorite}
            />
        );
    }

    if (block.type === "categoryRail") {
        return <CategorySection block={block} />;
    }

    if (block.type === "productRail") {
        return <ProductRail block={block} favorites={favorites} onToggleFavorite={onToggleFavorite} />;
    }

    if (block.type === "benefitStrip") {
        return <LoyaltyBlock block={block} />;
    }

    if (block.type === "campaignGrid") {
        return <CampaignBlocks block={block} />;
    }

    if (block.type === "brandGrid") {
        return <KidsAndBrands block={block} />;
    }

    if (block.type === "newsletter") {
        return <Newsletter block={block} onSubmit={(event) => event.preventDefault()} />;
    }

    if (block.type === "seoText") {
        return <EntitySeoText title={context.title} description={context.seoDescription} block={block} />;
    }

    return <BlockPlaceholder block={block} index={index} />;
}

function CartPageLayout({
    context,
    favorites,
    onToggleFavorite,
    cart,
    cartLoading,
    cartError,
    cartHref,
    checkoutHref,
    checkoutOptions,
    onUpdateCartItem,
    onRemoveCartItem,
    editorMode,
}: {
    context: ShopPageContext | null | undefined;
    favorites: Set<string>;
    onToggleFavorite: (productId: string) => void;
    cart: PublicCart;
    cartLoading: boolean;
    cartError: string | null;
    cartHref: string;
    checkoutHref: string;
    checkoutOptions: PublicCheckoutOptions;
    onUpdateCartItem: (itemUuid: string, quantity: number) => void;
    onRemoveCartItem: (itemUuid: string) => void;
    editorMode: boolean;
}) {
    const message = useShopStorefrontMessage();
    const { formatMoney } = useShopStorefrontFormatting();
    const blocks = blocksFor(context, [
        { type: "systemCartItems" },
        { type: "systemCartSummary" },
        { type: "benefitStrip" },
        { type: "productRail" },
    ]);

    return (
        <>
            {blocks.map((block, index) => {
                if (block.type === "systemCartItems") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <CartItemsPreview
                                block={block}
                                cart={cart}
                                loading={cartLoading}
                                error={cartError}
                                cartHref={cartHref}
                                checkoutHref={checkoutHref}
                                freeShippingMessage={cartFreeShippingMessage(cart, checkoutOptions, message, formatMoney)}
                                freeShippingProgress={cartFreeShippingProgress(cart, checkoutOptions, message, formatMoney)}
                                showSummary
                                onUpdateItem={onUpdateCartItem}
                                onRemoveItem={onRemoveCartItem}
                            />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "systemCartSummary") {
                    return null;
                }
                if (block.type === "benefitStrip") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <LoyaltyBlock block={block} />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "productRail") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <ProductRail block={block} favorites={favorites} onToggleFavorite={onToggleFavorite} />
                        </EditableBlockFrame>
                    );
                }

                return (
                    <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                        <BlockPlaceholder block={block} index={index} />
                    </EditableBlockFrame>
                );
            })}
        </>
    );
}

function SinglePageCartLayout({
    context,
    favorites,
    onToggleFavorite,
    cart,
    cartLoading,
    cartError,
    cartHref,
    deliveryPaymentHref,
    contactHref,
    checkoutRouteStep,
    checkoutOptions,
    checkoutOptionsLoading,
    packetaApiKey,
    checkoutForm,
    checkoutSubmitting,
    checkoutError,
    createdOrder,
    onCheckoutRouteStepChange,
    onSelectDeliveryPayment,
    onCheckoutFormChange,
    onSubmitCheckout,
    onUpdateCartItem,
    onRemoveCartItem,
    editorMode,
}: {
    context: ShopPageContext | null | undefined;
    favorites: Set<string>;
    onToggleFavorite: (productId: string) => void;
    cart: PublicCart;
    cartLoading: boolean;
    cartError: string | null;
    cartHref: string;
    deliveryPaymentHref: string;
    contactHref: string;
    checkoutRouteStep: CheckoutRouteStep;
    checkoutOptions: PublicCheckoutOptions;
    checkoutOptionsLoading: boolean;
    packetaApiKey?: string;
    checkoutForm: CheckoutFormState;
    checkoutSubmitting: boolean;
    checkoutError: string | null;
    createdOrder: PublicOrderCreated | null;
    onCheckoutRouteStepChange: (step: CheckoutRouteStep) => void;
    onSelectDeliveryPayment: (
        carrierRateUuid: string,
        carrierPaymentOptionUuid: string,
        pickupPoint?: PickupPointSelection,
    ) => Promise<boolean>;
    onCheckoutFormChange: (patch: Partial<CheckoutFormState>) => void;
    onSubmitCheckout: (form: CheckoutFormState) => Promise<boolean>;
    onUpdateCartItem: (itemUuid: string, quantity: number) => void;
    onRemoveCartItem: (itemUuid: string) => void;
    editorMode: boolean;
}) {
    const storedBlocks = blocksFor(context, [
        { type: "systemCartItems" },
        { type: "systemCheckoutSteps" },
        { type: "benefitStrip" },
        { type: "productRail" },
    ]);
    const blocks = storedBlocks.some((block) => block.type === "systemCheckoutSteps")
        ? storedBlocks
        : storedBlocks.flatMap((block) => (
            block.type === "systemCartItems"
                ? [block, { type: "systemCheckoutSteps" }]
                : [block]
        ));

    return (
        <>
            {blocks.map((block, index) => {
                if (block.type === "systemCartItems") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <CartItemsPreview
                                block={block}
                                cart={cart}
                                loading={cartLoading}
                                error={cartError}
                                cartHref={cartHref}
                                onUpdateItem={onUpdateCartItem}
                                onRemoveItem={onRemoveCartItem}
                            />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "systemCheckoutSteps") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <CheckoutStepsPreview
                                block={block}
                                cart={cart}
                                cartHref={cartHref}
                                deliveryPaymentHref={deliveryPaymentHref}
                                contactHref={contactHref}
                                checkoutRouteStep={checkoutRouteStep}
                                checkoutOptions={checkoutOptions}
                                checkoutOptionsLoading={checkoutOptionsLoading}
                                cartError={cartError}
                                packetaApiKey={packetaApiKey}
                                checkoutForm={checkoutForm}
                                checkoutSubmitting={checkoutSubmitting}
                                checkoutError={checkoutError}
                                createdOrder={createdOrder}
                                mode="singlePage"
                                onCheckoutRouteStepChange={onCheckoutRouteStepChange}
                                onSelectDeliveryPayment={onSelectDeliveryPayment}
                                onCheckoutFormChange={onCheckoutFormChange}
                                onSubmitCheckout={onSubmitCheckout}
                            />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "systemCartSummary") {
                    return null;
                }
                if (block.type === "benefitStrip") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <LoyaltyBlock block={block} />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "productRail") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <ProductRail block={block} favorites={favorites} onToggleFavorite={onToggleFavorite} />
                        </EditableBlockFrame>
                    );
                }

                return (
                    <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                        <BlockPlaceholder block={block} index={index} />
                    </EditableBlockFrame>
                );
            })}
        </>
    );
}

const LAGUNA_CONTACT_FALLBACK_FIELDS: SiteBuilderLeadFormField[] = [
    { key: "name", label: "Name", type: "text", required: true, placeholder: null, options: [] },
    { key: "email", label: "E-mail", type: "email", required: true, placeholder: null, options: [] },
    { key: "message", label: "Message", type: "textarea", required: true, placeholder: null, options: [], rows: 5 },
];

function LagunaContactForm({ block, editorMode }: { block: ShopTemplateBlock; editorMode: boolean }) {
    const message = useShopStorefrontMessage();
    const fieldIdPrefix = useId();
    const storefrontLocationHref = useContext(ShopStorefrontLocationHrefContext);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [startedAt] = useState(() => Math.floor(Date.now() / 1_000));
    const leadFormLookup = useSiteBuilderLeadForm({
        enabled: !editorMode,
        formKey: "contact",
    });
    const fields = leadFormLookup.form?.fields.length
        ? leadFormLookup.form.fields
        : LAGUNA_CONTACT_FALLBACK_FIELDS;
    const fieldClass = "rounded-2xl border border-[var(--site-border)] bg-[var(--site-surface)] px-4 py-3 text-sm text-[var(--site-text)] outline-none transition placeholder:text-[var(--site-muted)] focus-visible:border-[var(--site-primary)] focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/20";
    const fieldLabelClass = "grid min-w-0 gap-1.5 text-xs font-bold text-[color-mix(in_srgb,var(--site-text)_72%,transparent)]";
    const busy = submitting || leadFormLookup.loading;

    function fieldLabel(field: SiteBuilderLeadFormField): string {
        if (field.key === "name") return blockText(block, "nameInput", message("block.systemContactForm.nameInput"));
        if (field.key === "email") return blockText(block, "emailInput", message("block.systemContactForm.emailInput"));
        if (field.key === "message") return blockText(block, "messageInput", message("block.systemContactForm.messageInput"));

        return field.label;
    }

    async function submitContact(event: FormEvent<HTMLFormElement>): Promise<void> {
        event.preventDefault();

        if (editorMode || busy) {
            return;
        }

        const form = event.currentTarget;
        const values = siteBuilderLeadValuesFromFormData(new FormData(form));
        setError(null);
        setSuccess(null);
        setSubmitting(true);

        try {
            const result = await submitSiteBuilderLead({
                endpoint: "/api/lead/submit",
                fields: values,
                form: leadFormLookup.form,
                honeypotValue: String(values.honeypotValue ?? ""),
                landingUrl: storefrontLocationHref
                    ?? form.ownerDocument.defaultView?.location.href
                    ?? "",
                purpose: "contact",
                startedAt,
            });

            if (!result.ok) {
                setError(result.message ?? message("ui.common.retry"));

                return;
            }

            form.reset();
            setSuccess(result.message ?? message("block.systemContactForm.successMessage"));
        } catch {
            setError(message("ui.common.retry"));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <form
            aria-busy={busy}
            className="mx-auto grid max-w-3xl gap-4 rounded-[28px] border border-[var(--site-border)] bg-[var(--site-surface)] p-5 sm:grid-cols-2 sm:p-6"
            onSubmit={(event) => void submitContact(event)}
        >
            <input autoComplete="off" hidden name="honeypotValue" tabIndex={-1} type="text" />
            {error ? <p className="rounded-xl border border-[var(--site-primary)] bg-[var(--site-primary-soft)] px-4 py-3 text-sm font-bold text-[var(--site-primary)] sm:col-span-2" role="alert">{error}</p> : null}
            {success ? <p className="rounded-xl border border-[var(--site-accent)] bg-[color-mix(in_srgb,var(--site-accent)_12%,var(--site-surface))] px-4 py-3 text-sm font-bold text-[var(--site-text)] sm:col-span-2" role="status">{success}</p> : null}
            {fields.map((field, index) => {
                const label = fieldLabel(field);
                const placeholder = field.placeholder?.trim() || label;
                const fieldId = `${fieldIdPrefix}-${index}`;
                const fullWidth = field.type === "textarea";

                return (
                    <label className={classNames(fieldLabelClass, fullWidth ? "sm:col-span-2" : "")} htmlFor={fieldId} key={`${field.key}:${index}`}>
                        <span>{label} {field.required ? <span aria-hidden="true">*</span> : null}</span>
                        {field.type === "textarea" ? (
                            <textarea
                                className={classNames(fieldClass, "min-h-36 resize-y")}
                                id={fieldId}
                                name={field.key}
                                placeholder={placeholder}
                                readOnly={editorMode}
                                required={field.required}
                                rows={Math.max(3, field.rows ?? 5)}
                            />
                        ) : field.type === "select" ? (
                            <select className={fieldClass} defaultValue="" disabled={editorMode} id={fieldId} name={field.key} required={field.required}>
                                <option value="">{placeholder}</option>
                                {field.options.map((option) => <option key={option} value={option}>{option}</option>)}
                            </select>
                        ) : (
                            <input
                                autoComplete={field.type === "email" ? "email" : field.type === "tel" ? "tel" : field.key === "name" ? "name" : undefined}
                                className={fieldClass}
                                id={fieldId}
                                name={field.key}
                                placeholder={placeholder}
                                readOnly={editorMode}
                                required={field.required}
                                type={["email", "tel", "url"].includes(field.type) ? field.type : "text"}
                            />
                        )}
                    </label>
                );
            })}
            <button {...editablePart("button")} disabled={busy || editorMode} type="submit" className="rounded-full bg-[var(--site-primary)] px-5 py-3 text-sm font-black text-[var(--site-on-primary)] disabled:cursor-not-allowed disabled:opacity-60 sm:justify-self-start" style={partStyle(block, "button")}>
                <span {...editableTextPart("button")}>{submitting ? message("ui.common.sending") : richTextContent(block, "buttonLabel", message("block.systemContactForm.buttonLabel"))}</span>
            </button>
        </form>
    );
}

function ContactLayout({ context, editorMode }: { context: ShopPageContext; editorMode: boolean }) {
    const message = useShopStorefrontMessage();
    const blocks = blocksFor(context, [
        { type: "richContent" },
        { type: "contactInfo" },
        { type: "systemContactForm" },
    ]);

    return (
        <>
            {blocks.map((block, index) => {
                if (block.type === "richContent") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <PageIntro
                                title={context.title}
                                description={context.seoDescription ?? ""}
                                block={block}
                            />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "contactInfo") {
                    const phone = blockText(block, "phoneText", blockText(block, "phone", editorMode ? "530 514 600" : ""));
                    const email = blockText(block, "emailText", blockText(block, "email", editorMode ? "info@shoepoint.cz" : ""));

                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <section className="px-5 py-10 sm:px-6" style={blockVisualStyle(block)}>
                                <div className="mx-auto max-w-[var(--site-container)] space-y-4 text-sm leading-6">
                                    <h2 {...editableTextPart("title")} className="text-2xl font-black" style={partStyle(block, "title")}>{richTextContent(block, "title", message("block.contactInfo.title"))}</h2>
                                    <p {...editableTextPart("text")} style={partStyle(block, "text")}>{richTextContent(block, "text", message("block.contactInfo.text"))}</p>
                                    {phone ? <p {...editablePart("phoneText")} className="text-2xl font-black" style={partStyle(block, "phoneText")}>{phone}</p> : null}
                                    {email ? <p {...editablePart("emailText")} style={partStyle(block, "emailText")}>{email}</p> : null}
                                    <SlotBlockGroup
                                        block={block}
                                        slotKey="details"
                                        className="grid gap-4 pt-4 md:grid-cols-2"
                                    />
                                </div>
                            </section>
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "systemContactForm") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <section className="px-5 py-10 sm:px-6" style={blockVisualStyle(block)}>
                                <LagunaContactForm block={block} editorMode={editorMode} />
                            </section>
                        </EditableBlockFrame>
                    );
                }

                return (
                    <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                        <BlockPlaceholder block={block} index={index} />
                    </EditableBlockFrame>
                );
            })}
        </>
    );
}

function GenericSystemLayout({
    context,
    favorites,
    favoriteProducts,
    catalogUpdating,
    onToggleFavorite,
    onCatalogNavigate,
    cart,
    cartLoading,
    cartHref,
    deliveryPaymentHref,
    contactHref,
    checkoutHref,
    checkoutRouteStep,
    checkoutOptions,
    checkoutOptionsLoading,
    cartError,
    packetaApiKey,
    checkoutForm,
    checkoutSubmitting,
    checkoutError,
    createdOrder,
    customerSession,
    customerLoading,
    customerError,
    customerApiBasePath,
    orderApiBasePath,
    customerAddresses,
    onCheckoutRouteStepChange,
    onSelectDeliveryPayment,
    onCheckoutFormChange,
    onSubmitCheckout,
    onCustomerLogin,
    onCustomerRegister,
    onCustomerLogout,
    onCustomerProfileUpdate,
    onCustomerPasswordChange,
    onCustomerPasswordResetRequest,
    onCustomerPasswordResetConfirm,
    onCustomerReorder,
    onCustomerAddressesChange,
    onUpdateCartItem,
    onRemoveCartItem,
    editorMode,
}: {
    context: ShopPageContext;
    favorites: Set<string>;
    favoriteProducts: FavoriteProductPreview[];
    catalogUpdating: boolean;
    onToggleFavorite: (productId: string) => void;
    onCatalogNavigate?: CatalogNavigateHandler;
    cart: PublicCart;
    cartLoading: boolean;
    cartHref: string;
    deliveryPaymentHref: string;
    contactHref: string;
    checkoutHref: string;
    checkoutRouteStep: CheckoutRouteStep;
    checkoutOptions: PublicCheckoutOptions;
    checkoutOptionsLoading: boolean;
    cartError: string | null;
    packetaApiKey?: string;
    checkoutForm: CheckoutFormState;
    checkoutSubmitting: boolean;
    checkoutError: string | null;
    createdOrder: PublicOrderCreated | null;
    customerSession: PublicCustomerSession;
    customerLoading: boolean;
    customerError: string | null;
    customerApiBasePath: string | null;
    orderApiBasePath: string | null;
    customerAddresses: PublicCustomerAddress[];
    onCheckoutRouteStepChange: (step: CheckoutRouteStep) => void;
    onSelectDeliveryPayment: (
        carrierRateUuid: string,
        carrierPaymentOptionUuid: string,
        pickupPoint?: PickupPointSelection,
    ) => Promise<boolean>;
    onCheckoutFormChange: (patch: Partial<CheckoutFormState>) => void;
    onSubmitCheckout: (form: CheckoutFormState) => Promise<boolean>;
    onCustomerLogin: (email: string, password: string) => Promise<boolean>;
    onCustomerRegister: (
        email: string,
        password: string,
        passwordConfirmation: string,
        newsletterOptOut: boolean,
    ) => Promise<boolean>;
    onCustomerLogout: () => Promise<void>;
    onCustomerProfileUpdate: (profile: PublicCustomerProfileUpdate) => Promise<boolean>;
    onCustomerPasswordChange: (
        currentPassword: string,
        newPassword: string,
        newPasswordConfirmation: string,
    ) => Promise<boolean>;
    onCustomerPasswordResetRequest: (email: string) => Promise<boolean>;
    onCustomerPasswordResetConfirm: (
        token: string,
        newPassword: string,
        newPasswordConfirmation: string,
    ) => Promise<boolean>;
    onCustomerReorder: (orderUuid: string) => Promise<PublicOrderReorderResponse | null>;
    onCustomerAddressesChange: (addresses: PublicCustomerAddress[]) => void;
    onUpdateCartItem: (itemUuid: string, quantity: number) => void;
    onRemoveCartItem: (itemUuid: string) => void;
    editorMode: boolean;
}) {
    const blocks = blocksFor(context, context.pageType === "checkout"
        ? [
            { type: "systemCheckoutSteps" },
            { type: "systemCartSummary" },
            { type: "benefitStrip" },
        ]
        : [
            { type: "richContent" },
        ]);

    const renderedBlocks = context.pageType === "checkout"
        ? blocks.filter((block) => block.type !== "systemCartSummary")
        : blocks;

    return (
        <>
            {renderedBlocks.map((block, index) => (
                <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                    <GenericSystemBlock
                        block={block}
                        index={index}
                        context={context}
                        favorites={favorites}
                        favoriteProducts={favoriteProducts}
                        catalogUpdating={catalogUpdating}
                        onToggleFavorite={onToggleFavorite}
                        onCatalogNavigate={onCatalogNavigate}
                        cart={cart}
                        cartLoading={cartLoading}
                        cartHref={cartHref}
                        deliveryPaymentHref={deliveryPaymentHref}
                        contactHref={contactHref}
                        checkoutHref={checkoutHref}
                        checkoutRouteStep={checkoutRouteStep}
                        checkoutOptions={checkoutOptions}
                        checkoutOptionsLoading={checkoutOptionsLoading}
                        cartError={cartError}
                        packetaApiKey={packetaApiKey}
                        checkoutForm={checkoutForm}
                        checkoutSubmitting={checkoutSubmitting}
                        checkoutError={checkoutError}
                        createdOrder={createdOrder}
                        customerSession={customerSession}
                        customerLoading={customerLoading}
                        customerError={customerError}
                        customerApiBasePath={customerApiBasePath}
                        orderApiBasePath={orderApiBasePath}
                        customerAddresses={customerAddresses}
                        onCheckoutRouteStepChange={onCheckoutRouteStepChange}
                        onSelectDeliveryPayment={onSelectDeliveryPayment}
                        onCheckoutFormChange={onCheckoutFormChange}
                        onSubmitCheckout={onSubmitCheckout}
                        onCustomerLogin={onCustomerLogin}
                        onCustomerRegister={onCustomerRegister}
                        onCustomerLogout={onCustomerLogout}
                        onCustomerProfileUpdate={onCustomerProfileUpdate}
                        onCustomerPasswordChange={onCustomerPasswordChange}
                        onCustomerPasswordResetRequest={onCustomerPasswordResetRequest}
                        onCustomerPasswordResetConfirm={onCustomerPasswordResetConfirm}
                        onCustomerReorder={onCustomerReorder}
                        onCustomerAddressesChange={onCustomerAddressesChange}
                        onUpdateCartItem={onUpdateCartItem}
                        onRemoveCartItem={onRemoveCartItem}
                    />
                </EditableBlockFrame>
            ))}
        </>
    );
}

function HomeLayout({
    context,
    favorites,
    recentlyViewedProducts,
    recommendedProducts,
    onToggleFavorite,
    addingProductUuid,
    onAddToCart,
    onNewsletterSubmit,
    editorMode,
}: {
    context: ShopPageContext | null | undefined;
    favorites: Set<string>;
    recentlyViewedProducts: FavoriteProductPreview[];
    recommendedProducts: FavoriteProductPreview[];
    onToggleFavorite: (productId: string) => void;
    addingProductUuid: string | null;
    onAddToCart: (
        productUuid: string,
        productAttributeUuid?: string | null,
        quantity?: number,
        surchargeValueUuids?: string[],
        manufacturingSpecification?: PublicManufacturingSpecification,
    ) => void;
    onNewsletterSubmit: (event: FormEvent<HTMLFormElement>) => void;
    editorMode: boolean;
}) {
    const message = useShopStorefrontMessage();
    const homeProducts = homeProductsFromContext(context);
    const fallbackBlocks: ShopTemplateBlock[] = [
        { type: "heroSlider" },
        { type: "categoryRail" },
        { type: "productRail" },
        { type: "recentlyViewedProducts" },
        { type: "recommendedProducts" },
        { type: "campaignGrid" },
        { type: "brandGrid" },
        { type: "benefitStrip" },
        { type: "serviceBenefits" },
        { type: "newsletter" },
        { type: "seoText" },
    ];
    const blocks = blocksFor(context, editorMode ? fallbackBlocks : []);

    return (
        <>
            {blocks.map((block, index) => {
                if (block.type === "heroSlider") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <HeroSlider block={block} editorMode={editorMode} />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "categoryRail") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <CategorySection block={block} />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "productRail") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            {context ? (
                                <HomeCatalogProductRail
                                    addingProductUuid={addingProductUuid}
                                    block={block}
                                    context={context}
                                    fallbackProducts={homeProducts}
                                    favorites={favorites}
                                    onAddToCart={onAddToCart}
                                    onToggleFavorite={onToggleFavorite}
                                />
                            ) : (
                                <ProductRail
                                    addingProductUuid={addingProductUuid}
                                    block={block}
                                    favorites={favorites}
                                    onAddToCart={onAddToCart}
                                    onToggleFavorite={onToggleFavorite}
                                    publicProducts={homeProducts}
                                />
                            )}
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "recentlyViewedProducts") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <PersonalizedProductSection
                                block={block}
                                editorMode={editorMode}
                                products={recentlyViewedProducts.slice(0, 4)}
                                fallbackEyebrow={message("block.recentlyViewedProducts.eyebrow")}
                                fallbackTitle={message("block.recentlyViewedProducts.title")}
                                emptyText="Produkty se zobrazí podle historie prohlížení zákazníka."
                            />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "recommendedProducts") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <PersonalizedProductSection
                                block={block}
                                editorMode={editorMode}
                                products={recommendedProducts.slice(0, 4)}
                                fallbackEyebrow={message("block.recommendedProducts.eyebrow")}
                                fallbackTitle={message("block.recommendedProducts.title")}
                                emptyText="Doporučení se doplní podle oblíbených a prohlížených produktů."
                            />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "campaignGrid") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <CampaignBlocks block={block} />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "brandGrid") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <KidsAndBrands block={block} />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "benefitStrip") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <LoyaltyBlock block={block} />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "serviceBenefits") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <ServiceBenefits block={block} />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "newsletter") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <Newsletter block={block} onSubmit={onNewsletterSubmit} />
                        </EditableBlockFrame>
                    );
                }
                if (block.type === "seoText") {
                    return (
                        <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                            <TrustAndText block={block} />
                        </EditableBlockFrame>
                    );
                }

                return (
                    <EditableBlockFrame key={`${block.type}-${index}`} block={block} index={index} editorMode={editorMode}>
                        <BlockPlaceholder block={block} index={index} />
                    </EditableBlockFrame>
                );
            })}
        </>
    );
}

function PersonalizedProductSection({
    block,
    editorMode,
    products,
    fallbackEyebrow,
    fallbackTitle,
    emptyText,
}: {
    block?: ShopTemplateBlock;
    editorMode: boolean;
    products: FavoriteProductPreview[];
    fallbackEyebrow: string;
    fallbackTitle: string;
    emptyText: string;
}) {
    const contentReplacesFallback = useSlotReplacesFallback(block, "content");

    if (products.length === 0 && !editorMode) {
        return null;
    }

    const contentItems = slotChildren(block, "content");
    const hasContentSlot = contentItems.length > 0 || contentReplacesFallback;

    return (
        <section className="bg-[var(--site-background)] px-5 py-12 sm:px-6">
            <div className="mx-auto max-w-[var(--site-container)]">
                <div className="mb-6 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
                    {hasContentSlot ? (
                        <SlotBlockGroup
                            block={block}
                            slotKey="content"
                            className="grid flex-1 gap-3"
                        />
                    ) : (
                        <>
                            <div>
                                <p {...editableTextPart("eyebrow")} className="text-xs font-black uppercase text-[var(--site-primary)]" style={partStyle(block, "eyebrow")}>{richTextContent(block, "eyebrow", fallbackEyebrow)}</p>
                                <h2 {...editableTextPart("title")} className="mt-2 text-2xl font-black text-[var(--site-text)]" style={partStyle(block, "title")}>{richTextContent(block, "title", fallbackTitle)}</h2>
                            </div>
                            {blockText(block, "buttonLabel", "") ? (
                                <a {...editablePart("button")} href={functionalStorefrontHref(blockText(block, "buttonUrl", ""))} className="text-sm font-black underline underline-offset-4" style={partStyle(block, "button")}>
                                    <span {...editableTextPart("button")}>{richTextContent(block, "buttonLabel", "")}</span>
                                </a>
                            ) : null}
                        </>
                    )}
                </div>
                {products.length > 0 ? (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        {products.map((product, index) => {
                            const imagePart = `product${index + 1}Image`;
                            const image = editorMode
                                ? blockText(block, `${imagePart}Url`, product.image ?? "")
                                : product.image ?? "";
                            const imageAlt = editorMode
                                ? blockText(block, `${imagePart}Alt`, product.alt)
                                : product.alt;
                            const imageTitle = editorMode ? blockText(block, `${imagePart}Title`, "") : "";
                            const itemLayout = imageContentLayout(block, "left");
                            const compactLayoutClass = itemLayout === "left"
                                ? "grid-cols-[72px_minmax(0,1fr)] items-center"
                                : itemLayout === "right"
                                    ? "grid-cols-[minmax(0,1fr)_72px] items-center"
                                    : "";
                            const imageBoxClass = itemLayout === "left" || itemLayout === "right"
                                ? "h-[72px] w-[72px]"
                                : "h-32 w-full";

                            return (
                                <a
                                    key={product.id}
                                    {...{ ...itemHoverAttributes(block), ...imageHoverFrameAttributes(block, imagePart) }}
                                    href={functionalStorefrontHref(product.href)}
                                    className={classNames("grid gap-4 rounded-[28px] bg-[var(--site-surface)] p-4 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--site-text)_5%,transparent)]", compactLayoutClass)}
                                >
                                    <span
                                        className={classNames("overflow-hidden rounded-[24px] bg-[var(--site-surface-elevated)]", imageBoxClass, imageContentImageOrderClass(block, "left"))}
                                        style={imageFrameStyle(block, imagePart)}
                                    >
                                        {image ? (
                                            <img
                                                {...editablePart(imagePart)}
                                                data-shop-image-hover-target="true"
                                                src={image}
                                                alt={imageAlt}
                                                title={imageTitle || undefined}
                                                className="h-full w-full object-cover"
                                                style={imageInteractivePartStyle(block, imagePart)}
                                            />
                                        ) : null}
                                    </span>
                                    <span className={classNames("min-w-0", imageContentTextOrderClass(block, "left"))}>
                                        {product.brand ? <span className="block truncate text-xs font-black uppercase text-[color-mix(in_srgb,var(--site-text)_35%,transparent)]">{product.brand}</span> : null}
                                        <span className="mt-1 block truncate text-sm font-black">{product.name}</span>
                                        {product.price ? <span className="mt-2 block text-sm font-black text-[var(--site-primary)]">{product.price}</span> : null}
                                    </span>
                                </a>
                            );
                        })}
                    </div>
                ) : (
                    <div {...editableTextPart("emptyText")} className="rounded-[28px] border border-dashed border-[color-mix(in_srgb,var(--site-text)_20%,transparent)] bg-[var(--site-surface)] px-5 py-8 text-sm font-bold text-[color-mix(in_srgb,var(--site-text)_45%,transparent)]" style={partStyle(block, "emptyText")}>
                        {richTextContent(block, "emptyText", emptyText)}
                    </div>
                )}
                <SlotBlockGroup
                    block={block}
                    slotKey="afterRail"
                    className="mt-8 grid gap-3 md:grid-cols-3"
                />
            </div>
        </section>
    );
}

function PageBody({
    page,
    pageContext,
    favorites,
    favoriteProducts,
    recentlyViewedProducts,
    recommendedProducts,
    catalogUpdating,
    onToggleFavorite,
    onCatalogNavigate,
    onNewsletterSubmit,
    cart,
    cartLoading,
    cartHref,
    deliveryPaymentHref,
    contactHref,
    checkoutHref,
    checkoutRouteStep,
    checkoutOptions,
    checkoutOptionsLoading,
    cartError,
    packetaApiKey,
    cartFlow,
    addingProductUuid,
    checkoutForm,
    checkoutSubmitting,
    checkoutError,
    createdOrder,
    customerSession,
    customerLoading,
    customerError,
    customerApiBasePath,
    orderApiBasePath,
    customerAddresses,
    onCheckoutRouteStepChange,
    onSelectDeliveryPayment,
    onCheckoutFormChange,
    onSubmitCheckout,
    onCustomerLogin,
    onCustomerRegister,
    onCustomerLogout,
    onCustomerProfileUpdate,
    onCustomerPasswordChange,
    onCustomerPasswordResetRequest,
    onCustomerPasswordResetConfirm,
    onCustomerReorder,
    onCustomerAddressesChange,
    onAddToCart,
    onUpdateCartItem,
    onRemoveCartItem,
    editorMode,
}: {
    page?: SitePageContent | null;
    pageContext?: ShopPageContext | null;
    favorites: Set<string>;
    favoriteProducts: FavoriteProductPreview[];
    recentlyViewedProducts: FavoriteProductPreview[];
    recommendedProducts: FavoriteProductPreview[];
    catalogUpdating: boolean;
    onToggleFavorite: (productId: string) => void;
    onCatalogNavigate?: CatalogNavigateHandler;
    onNewsletterSubmit: (event: FormEvent<HTMLFormElement>) => void;
    cart: PublicCart;
    cartLoading: boolean;
    cartHref: string;
    deliveryPaymentHref: string;
    contactHref: string;
    checkoutHref: string;
    checkoutRouteStep: CheckoutRouteStep;
    checkoutOptions: PublicCheckoutOptions;
    checkoutOptionsLoading: boolean;
    cartError: string | null;
    packetaApiKey?: string;
    cartFlow: "multiStep" | "singlePage";
    addingProductUuid: string | null;
    checkoutForm: CheckoutFormState;
    checkoutSubmitting: boolean;
    checkoutError: string | null;
    createdOrder: PublicOrderCreated | null;
    customerSession: PublicCustomerSession;
    customerLoading: boolean;
    customerError: string | null;
    customerApiBasePath: string | null;
    orderApiBasePath: string | null;
    customerAddresses: PublicCustomerAddress[];
    onCheckoutRouteStepChange: (step: CheckoutRouteStep) => void;
    onSelectDeliveryPayment: (
        carrierRateUuid: string,
        carrierPaymentOptionUuid: string,
        pickupPoint?: PickupPointSelection,
    ) => Promise<boolean>;
    onCheckoutFormChange: (patch: Partial<CheckoutFormState>) => void;
    onSubmitCheckout: (form: CheckoutFormState) => Promise<boolean>;
    onCustomerLogin: (email: string, password: string) => Promise<boolean>;
    onCustomerRegister: (
        email: string,
        password: string,
        passwordConfirmation: string,
        newsletterOptOut: boolean,
    ) => Promise<boolean>;
    onCustomerLogout: () => Promise<void>;
    onCustomerProfileUpdate: (profile: PublicCustomerProfileUpdate) => Promise<boolean>;
    onCustomerPasswordChange: (
        currentPassword: string,
        newPassword: string,
        newPasswordConfirmation: string,
    ) => Promise<boolean>;
    onCustomerPasswordResetRequest: (email: string) => Promise<boolean>;
    onCustomerPasswordResetConfirm: (
        token: string,
        newPassword: string,
        newPasswordConfirmation: string,
    ) => Promise<boolean>;
    onCustomerReorder: (orderUuid: string) => Promise<PublicOrderReorderResponse | null>;
    onCustomerAddressesChange: (addresses: PublicCustomerAddress[]) => void;
    onAddToCart: (
        productUuid: string,
        productAttributeUuid?: string | null,
        quantity?: number,
        surchargeValueUuids?: string[],
        manufacturingSpecification?: PublicManufacturingSpecification,
    ) => void;
    onUpdateCartItem: (itemUuid: string, quantity: number) => void;
    onRemoveCartItem: (itemUuid: string) => void;
    editorMode: boolean;
}) {
    const context = pageContext ?? (page ? {
        pageType: "home",
        routeType: "system",
        slug: "",
        title: page.title,
        seoDescription: page.content ?? null,
        languageCode: page.languageCode ?? "cs",
        status: "published",
        contentJson: null,
        data: {},
    } satisfies ShopPageContext : null);
    const pageType = context?.pageType ?? "home";

    if (context && pageType === "storeList") {
        return <StoreListLayout context={context} />;
    }

    if (context && pageType === "storeDetail") {
        return <StoreDetailLayout context={context} />;
    }

    if (context && (pageType === "category" || pageType === "section")) {
        return (
            <CategoryLayout
                context={context}
                favorites={favorites}
                catalogUpdating={catalogUpdating}
                addingProductUuid={addingProductUuid}
                onAddToCart={onAddToCart}
                onCatalogNavigate={onCatalogNavigate}
                onToggleFavorite={onToggleFavorite}
                editorMode={editorMode}
            />
        );
    }

    if (context && pageType === "product") {
        return (
            <ProductLayout
                context={context}
                favorites={favorites}
                onToggleFavorite={onToggleFavorite}
                addingProductUuid={addingProductUuid}
                onAddToCart={onAddToCart}
                editorMode={editorMode}
            />
        );
    }

    if (pageType === "cart" || (pageType === "checkout" && checkoutRouteStep === "cart")) {
        if (cartFlow === "singlePage") {
            return (
                <SinglePageCartLayout
                    context={pageType === "checkout" ? null : context}
                    favorites={favorites}
                    onToggleFavorite={onToggleFavorite}
                    cart={cart}
                    cartLoading={cartLoading}
                    cartError={cartError}
                    cartHref={cartHref}
                    deliveryPaymentHref={deliveryPaymentHref}
                    contactHref={contactHref}
                    checkoutRouteStep={checkoutRouteStep}
                    checkoutOptions={checkoutOptions}
                    checkoutOptionsLoading={checkoutOptionsLoading}
                    packetaApiKey={packetaApiKey}
                    checkoutForm={checkoutForm}
                    checkoutSubmitting={checkoutSubmitting}
                    checkoutError={checkoutError}
                    createdOrder={createdOrder}
                    onCheckoutRouteStepChange={onCheckoutRouteStepChange}
                    onSelectDeliveryPayment={onSelectDeliveryPayment}
                    onCheckoutFormChange={onCheckoutFormChange}
                    onSubmitCheckout={onSubmitCheckout}
                    onUpdateCartItem={onUpdateCartItem}
                    onRemoveCartItem={onRemoveCartItem}
                    editorMode={editorMode}
                />
            );
        }

        return (
            <CartPageLayout
                context={pageType === "checkout" ? null : context}
                favorites={favorites}
                onToggleFavorite={onToggleFavorite}
                cart={cart}
                cartLoading={cartLoading}
                cartError={cartError}
                cartHref={cartHref}
                checkoutHref={deliveryPaymentHref}
                checkoutOptions={checkoutOptions}
                onUpdateCartItem={onUpdateCartItem}
                onRemoveCartItem={onRemoveCartItem}
                editorMode={editorMode}
            />
        );
    }

    if (context && pageType === "contact") {
        return <ContactLayout context={context} editorMode={editorMode} />;
    }

    if (pageType === "home") {
        return (
            <HomeLayout
                context={context}
                favorites={favorites}
                recentlyViewedProducts={recentlyViewedProducts}
                recommendedProducts={recommendedProducts}
                onToggleFavorite={onToggleFavorite}
                addingProductUuid={addingProductUuid}
                onAddToCart={onAddToCart}
                onNewsletterSubmit={onNewsletterSubmit}
                editorMode={editorMode}
            />
        );
    }

    return (
        <GenericSystemLayout
            context={context ?? {
                pageType: "notFound",
                routeType: "notFound",
                slug: "not-found",
                title: "Stranka nenalezena",
                languageCode: "cs",
                status: "notFound",
                contentJson: null,
                data: {},
            }}
            favorites={favorites}
            favoriteProducts={favoriteProducts}
            catalogUpdating={catalogUpdating}
            onToggleFavorite={onToggleFavorite}
            onCatalogNavigate={onCatalogNavigate}
            cart={cart}
            cartLoading={cartLoading}
            cartHref={cartHref}
            deliveryPaymentHref={deliveryPaymentHref}
            contactHref={contactHref}
            checkoutHref={checkoutHref}
            checkoutRouteStep={checkoutRouteStep}
            checkoutOptions={checkoutOptions}
            checkoutOptionsLoading={checkoutOptionsLoading}
            cartError={cartError}
            packetaApiKey={packetaApiKey}
            checkoutForm={checkoutForm}
            checkoutSubmitting={checkoutSubmitting}
            checkoutError={checkoutError}
            createdOrder={createdOrder}
            customerSession={customerSession}
            customerLoading={customerLoading}
            customerError={customerError}
            customerApiBasePath={customerApiBasePath ?? null}
            orderApiBasePath={orderApiBasePath}
            customerAddresses={customerAddresses}
            onCheckoutRouteStepChange={onCheckoutRouteStepChange}
            onSelectDeliveryPayment={onSelectDeliveryPayment}
            onCheckoutFormChange={onCheckoutFormChange}
            onSubmitCheckout={onSubmitCheckout}
            onCustomerLogin={onCustomerLogin}
            onCustomerRegister={onCustomerRegister}
            onCustomerLogout={onCustomerLogout}
            onCustomerProfileUpdate={onCustomerProfileUpdate}
            onCustomerPasswordChange={onCustomerPasswordChange}
            onCustomerPasswordResetRequest={onCustomerPasswordResetRequest}
            onCustomerPasswordResetConfirm={onCustomerPasswordResetConfirm}
            onCustomerReorder={onCustomerReorder}
            onCustomerAddressesChange={onCustomerAddressesChange}
            onUpdateCartItem={onUpdateCartItem}
            onRemoveCartItem={onRemoveCartItem}
            editorMode={editorMode}
        />
    );
}

function hasLagunaAuthoredHeaderGrid(block: ShopTemplateBlock): boolean {
    const roles = new Set(slotChildren(block, SHOP_GRID_SLOT_KEY).map((childBlock) => (
        shopHeaderRole(childBlock)
    )));

    return SHOP_HEADER_ROLES.some((role) => roles.has(role));
}

function lagunaHeaderVisibilityClassName(childBlock: ShopTemplateBlock): string {
    return classNames(
        !shopHeaderBlockVisibleOn(childBlock, "desktop") && "laguna-semantic-header-hide-desktop",
        !shopHeaderBlockVisibleOn(childBlock, "mobile") && "laguna-semantic-header-hide-mobile",
    );
}

function lagunaHeaderGridItemClassName(childBlock: ShopTemplateBlock): string {
    const role = shopHeaderRole(childBlock);
    const visibility = lagunaHeaderVisibilityClassName(childBlock);

    if (role === "headerLogo") return classNames("col-span-7 py-3 pl-11 sm:col-span-8 lg:col-span-2 lg:py-4 lg:pl-0", visibility);
    if (role === "headerSearch") return classNames("laguna-semantic-header-desktop-only py-3 lg:col-span-4 lg:block lg:py-4", visibility);
    if (role === "headerStoresButton" || role === "headerAccountButton") return classNames("py-3 lg:col-span-1 lg:py-4", visibility);
    if (role === "headerLanguageSelector" || role === "headerCurrencySelector") return classNames("py-3 lg:col-span-1 lg:py-4", visibility);
    if (role === "headerFavoritesButton" || role === "headerCartButton") return classNames("col-span-2 grid place-items-center py-3 lg:col-span-1 lg:py-4", visibility);
    if (role === "headerMenu") {
        return classNames("laguna-semantic-header-menu col-span-12", visibility);
    }

    return "col-span-12";
}

function lagunaHeaderUtilityItemClassName(
    block: ShopTemplateBlock,
    childBlock: ShopTemplateBlock,
): string | undefined {
    const display = resolveShopHeaderUtilityDisplay(block);
    const icon = childBlock.type === "icon";
    const utilityItems = slotChildren(block, "content");
    const hasIcon = utilityItems.some((item) => item.type === "icon");
    const hasLabel = utilityItems.some((item) => item.type !== "icon");

    if (display === "icon") {
        if (!hasIcon) {
            return icon ? "hidden" : "inline-flex min-w-0 max-w-full truncate";
        }

        return icon
            ? "grid h-10 w-10 shrink-0 place-items-center"
            : "hidden";
    }

    if (display === "label") {
        if (!hasLabel) {
            return icon ? "grid h-10 w-10 shrink-0 place-items-center" : "hidden";
        }

        return icon
            ? "hidden"
            : "inline-flex min-w-0 max-w-full truncate";
    }

    return icon
        ? "grid h-10 w-10 shrink-0 place-items-center"
        : "hidden min-w-0 max-w-full truncate xl:inline-flex";
}

function lagunaHeaderUtilityActionClassName(block: ShopTemplateBlock): string {
    return resolveShopHeaderUtilityDisplay(block) === "icon"
        ? headerIconActionClass
        : headerLabelActionClass;
}

function isLagunaHeaderLogoMarkBlock(block: ShopTemplateBlock): boolean {
    return block.type === "image" || block.type === "media" || block.type === "icon";
}

function hasLagunaHeaderLogoMarkContent(block: ShopTemplateBlock): boolean {
    if (block.type === "icon") {
        return true;
    }

    if (block.type !== "image" && block.type !== "media") {
        return false;
    }

    const presentation = siteBuilderMediaPresentation(block.content);

    return presentation.kind === "image" && presentation.source.trim() !== "";
}

function hasLagunaHeaderLogoLabelContent(block: ShopTemplateBlock): boolean {
    if (isLagunaHeaderLogoMarkBlock(block)) {
        return false;
    }

    if (blockOptionText(block, "runtimeText").trim() !== "") {
        return true;
    }

    return Object.values(block.content ?? {}).some((value) => (
        typeof value === "string" && value.trim() !== ""
    ));
}

function lagunaHeaderLogoItemClassName(
    display: "markText" | "mark" | "text",
    childBlock: ShopTemplateBlock,
    editorMode: boolean,
): string | undefined {
    const mark = isLagunaHeaderLogoMarkBlock(childBlock);
    const emptyMark = mark && !hasLagunaHeaderLogoMarkContent(childBlock);

    if (display === "mark") {
        if (!mark) {
            return "hidden";
        }

        return !editorMode && emptyMark ? "hidden" : "shrink-0";
    }

    if (display === "text") {
        if (mark) {
            return "hidden";
        }

        return !editorMode && !hasLagunaHeaderLogoLabelContent(childBlock) ? "hidden" : undefined;
    }

    if (mark) {
        return !editorMode && emptyMark ? "hidden" : "shrink-0";
    }

    return !editorMode && !hasLagunaHeaderLogoLabelContent(childBlock) ? "hidden" : undefined;
}

function LagunaHeaderMenuRows({
    activeSection,
    headerMenuItems,
    navigationVariant,
}: {
    activeSection: PublicShopMenuItem | null;
    headerMenuItems: PublicShopMenuItem[];
    navigationVariant: LagunaNavigationVariant;
}) {
    return (
        <LagunaDesktopNavigation
            activeSection={activeSection}
            headerMenuItems={headerMenuItems}
            variant={navigationVariant}
        />
    );
}

function usesSingleRowMobileHeader(variant: LagunaNavigationVariant): boolean {
    return variant === "borderedDropdown"
        || variant === "imageCategoryStrip"
        || variant === "monochromeDropdown"
        || variant === "playfulFrame";
}

function LagunaLegacyClassicRootNavigation({
    activeSection,
    headerMenuItems,
    onPreviewSectionChange,
}: {
    activeSection: PublicShopMenuItem | null;
    headerMenuItems: PublicShopMenuItem[];
    onPreviewSectionChange: (uuid: string | null) => void;
}) {
    const message = useShopStorefrontMessage();

    return (
        <nav className="ml-12 hidden items-center gap-8 text-sm font-bold lg:flex" aria-label={message("ui.header.customerSegments")}>
            {headerMenuItems.map((item) => {
                const active = activeSection?.uuid === item.uuid;

                return (
                    <ShopStorefrontLink
                        key={item.uuid}
                        href={functionalStorefrontHref(menuItemHref(item))}
                        onClick={() => onPreviewSectionChange(item.uuid)}
                        onMouseEnter={() => onPreviewSectionChange(item.uuid)}
                        className={classNames(
                            "py-4 hover:text-[var(--site-primary)]",
                            animatedUnderlineClass,
                            active ? "text-[var(--site-primary)] after:scale-x-100" : null,
                        )}
                        {...linkTargetProps(item)}
                    >
                        {item.label}
                    </ShopStorefrontLink>
                );
            })}
        </nav>
    );
}

function LagunaLegacyClassicSubNavigation({
    activeSectionItems,
}: {
    activeSectionItems: PublicShopMenuItem[];
}) {
    return (
        <div className="relative hidden border-b border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] lg:block">
            <div className="mx-auto flex min-h-[52px] max-w-[var(--site-container)] items-center gap-7 px-6 text-[13px] font-bold">
                {activeSectionItems.map((item) => (
                    item.children.length > 0 ? (
                        <div key={item.uuid} className="group py-4">
                            <ShopStorefrontLink
                                href={functionalStorefrontHref(menuItemHref(item))}
                                className={classNames(
                                    "flex items-center gap-1",
                                    animatedUnderlineClass,
                                    isSaleMenuItem(item) ? "text-[var(--site-primary)]" : "hover:text-[var(--site-primary)]",
                                )}
                                {...linkTargetProps(item)}
                            >
                                {item.label} <Icon name="chevronDown" className="h-3.5 w-3.5" />
                            </ShopStorefrontLink>
                            <MegaMenu item={item} />
                        </div>
                    ) : (
                        <ShopStorefrontLink
                            key={item.uuid}
                            href={functionalStorefrontHref(menuItemHref(item))}
                            className={classNames("py-4 hover:text-[var(--site-primary)]", animatedUnderlineClass, isSaleMenuItem(item) && "text-[var(--site-primary)]")}
                            {...linkTargetProps(item)}
                        >
                            {item.label}
                        </ShopStorefrontLink>
                    )
                ))}
            </div>
        </div>
    );
}

function LagunaAuthoredHeaderGridItem({
    activeSection,
    block,
    headerMenuItems,
    itemPart,
    mobileMenuId,
    mobileOpen,
    navigationVariant,
    onOpenMobileMenu,
    showDesktopOverflowMenu,
}: {
    activeSection: PublicShopMenuItem | null;
    block: ShopTemplateBlock;
    headerMenuItems: PublicShopMenuItem[];
    itemPart: string;
    mobileMenuId: string;
    mobileOpen: boolean;
    navigationVariant: LagunaNavigationVariant;
    onOpenMobileMenu: () => void;
    showDesktopOverflowMenu: boolean;
}) {
    const headerContext = useContext(ShopHeaderChromeContext);
    const editorMode = useContext(ShopEditorModeContext);
    const message = useShopStorefrontMessage();
    const role = shopHeaderRole(block) ?? blockOptionText(block, SHOP_DOCUMENT_ROLE_OPTION);
    const authoredGap = blockOptionText(block, "gap");
    const utilityRole = (SHOP_HEADER_UTILITY_ROLES as readonly string[]).includes(role);
    const logoItems = role === "headerLogo" ? slotChildren(block, "content") : [];
    const logoDisplay = role === "headerLogo" ? resolveShopHeaderLogoDisplay(block) : "markText";
    const logoOrientation = blockOptionText(block, "contentLayout") === "vertical" ? "vertical" : "horizontal";
    const hasAuthoredLogoMark = logoItems.some(hasLagunaHeaderLogoMarkContent);
    const hasAuthoredLogoLabel = logoItems.some(hasLagunaHeaderLogoLabelContent);
    const authoredInlineContent = (
        <InlineSlotBlockGroup
            block={block}
            slotKey="content"
            partPrefix={itemPart}
            childSlotParentType={role}
            className={utilityRole
                ? "w-full flex-col justify-center gap-1"
                : role === "headerLogo" && logoOrientation === "vertical"
                    ? "flex-col"
                    : undefined}
            itemClassName={utilityRole
                ? ({ childBlock }) => lagunaHeaderUtilityItemClassName(block, childBlock)
                : role === "headerLogo"
                    ? ({ childBlock }) => lagunaHeaderLogoItemClassName(logoDisplay, childBlock, editorMode)
                    : undefined}
            style={{
                ...(authoredGap ? { gap: authoredGap } : {}),
                ...(role === "headerLogo" && logoOrientation === "vertical"
                    ? {
                        alignItems: resolveShopHeaderAlignment(block) === "center"
                            ? "center"
                            : resolveShopHeaderAlignment(block) === "right"
                                ? "flex-end"
                                : "flex-start",
                    }
                    : {}),
            }}
        />
    );

    if (!headerContext) {
        return null;
    }

    if (role === "headerLogo") {
        return (
            <ShopLogo
                href={headerContext.homeHref}
                siteName={headerContext.displayName}
                brandContent={authoredInlineContent}
                hasAuthoredLabel={hasAuthoredLogoLabel}
                hasAuthoredMark={hasAuthoredLogoMark}
                alignment={resolveShopHeaderAlignment(block)}
                display={logoDisplay}
                orientation={logoOrientation}
                stretch
            />
        );
    }

    if (role === "headerSearch") {
        return (
            <SlotBlockGroup
                block={block}
                slotKey="content"
                partPrefix={itemPart}
                childSlotParentType="headerSearch"
                className="grid min-w-0 w-full grid-cols-[minmax(0,1fr)]"
            />
        );
    }

    if (role === "headerStoresButton") {
        return (
            <StoreHeaderDropdown
                cities={headerContext.storeCities}
                storesHref={headerContext.storesHref}
                onClose={() => undefined}
                presentation="semanticGrid"
                triggerContent={authoredInlineContent}
            />
        );
    }

    if (role === "headerAccountButton") {
        return (
            <AccountHeaderDropdown
                accountHref={headerContext.accountHref}
                customerSession={headerContext.customerSession}
                loading={headerContext.customerLoading}
                onLogout={headerContext.onLogoutCustomer}
                presentation="semanticGrid"
                triggerContent={authoredInlineContent}
            />
        );
    }

    if (role === "headerLanguageSelector" && headerContext.currentLanguage) {
        return (
            <HeaderSelectorDropdown
                currentCode={headerContext.currentLanguage.code}
                options={headerContext.languageOptions}
                ariaLabel={message("ui.header.selectLanguage")}
                onSelect={headerContext.onLanguageSelect}
                triggerContent={authoredInlineContent}
            />
        );
    }

    if (role === "headerCurrencySelector" && headerContext.currentCurrency) {
        return (
            <HeaderSelectorDropdown
                currentCode={headerContext.currentCurrency.code}
                options={headerContext.currencyOptions}
                ariaLabel={message("ui.header.selectCurrency")}
                onSelect={headerContext.onCurrencySelect}
                triggerContent={authoredInlineContent}
            />
        );
    }

    if (role === "headerFavoritesButton") {
        return (
            <a
                href={headerContext.customerUuid !== null ? accountSectionHref(headerContext.accountHref, "favorites") : headerContext.accountHref}
                className={lagunaHeaderUtilityActionClassName(block)}
                aria-label={message("ui.account.favorites")}
            >
                {authoredInlineContent}
                <span className={classNames("absolute right-0 top-0 h-5 min-w-5 place-items-center rounded-full bg-[var(--site-primary)] px-1 text-[10px] font-bold text-[var(--site-on-primary)]", headerContext.visibleFavoriteProductIds.size > 0 ? "grid" : "hidden")}>
                    {headerContext.visibleFavoriteProductIds.size}
                </span>
            </a>
        );
    }

    if (role === "headerCartButton") {
        return (
            <div className="group relative">
                <a href={headerContext.cartHref} className={lagunaHeaderUtilityActionClassName(block)} aria-label={message("ui.cart.title")}>
                    {authoredInlineContent}
                    <span className={classNames("absolute right-0 top-0 h-5 min-w-5 place-items-center rounded-full bg-[var(--site-primary)] px-1 text-[10px] font-bold text-[var(--site-on-primary)]", headerContext.cart.totalQuantity > 0 ? "grid" : "hidden")}>
                        {headerContext.cart.totalQuantity}
                    </span>
                </a>
                <CartHoverPreview
                    cart={headerContext.cart}
                    loading={headerContext.cartLoading}
                    error={headerContext.cartError}
                    cartHref={headerContext.cartHref}
                />
            </div>
        );
    }

    if (role === "headerMenu") {
        return (
            <>
                <button
                    type="button"
                    className="absolute left-2.5 top-3 grid h-9 w-9 place-items-center sm:left-6 sm:h-10 sm:w-10 lg:hidden"
                    aria-label={message("ui.header.openMenu")}
                    aria-controls={mobileMenuId}
                    aria-expanded={mobileOpen}
                    onClick={onOpenMobileMenu}
                >
                    {usesSingleRowMobileHeader(navigationVariant) ? (
                        <Icon name="menu" className="h-5 w-5 sm:h-6 sm:w-6" />
                    ) : (
                        <InlineSlotBlockGroup
                            block={block}
                            slotKey="content"
                            partPrefix={itemPart}
                            childSlotParentType={role}
                            itemClassName={({ childBlock }) => childBlock.type === "text" ? "sr-only" : undefined}
                        />
                    )}
                </button>
                <div className="relative w-full">
                    <LagunaHeaderMenuRows
                        activeSection={activeSection}
                        headerMenuItems={headerMenuItems}
                        navigationVariant={navigationVariant}
                    />
                    {showDesktopOverflowMenu ? (
                        <button
                            type="button"
                            className="hidden"
                            data-shop-editorial-overflow-menu="true"
                            aria-label={message("ui.header.openMenu")}
                            aria-controls={mobileMenuId}
                            aria-expanded={mobileOpen}
                            onClick={onOpenMobileMenu}
                        >
                            <Icon name="menu" className="h-5 w-5" />
                        </button>
                    ) : null}
                </div>
            </>
        );
    }

    return <ColumnBlock block={block} partPrefix={itemPart} slotParentType="header" />;
}

function HeaderChrome({
    activeSection,
    block,
    blockIndex,
    design,
    headerMenuItems,
    mobileMenuId,
    mobileOpen,
    navigationVariant,
    onOpenMobileMenu,
}: {
    activeSection: PublicShopMenuItem | null;
    block: ShopTemplateBlock;
    blockIndex: number;
    design: ResolvedTemplateDesign;
    headerMenuItems: PublicShopMenuItem[];
    mobileMenuId: string;
    mobileOpen: boolean;
    navigationVariant: LagunaNavigationVariant;
    onOpenMobileMenu: () => void;
}) {
    const headerContext = useContext(ShopHeaderChromeContext);
    const editorMode = useContext(ShopEditorModeContext);
    const message = useShopStorefrontMessage();
    const builderPath = siteBuilderEditorRootPath(block, blockIndex);
    const hasAuthoredHeaderMenu = findShopHeaderRoleBlock(block, "headerMenu") !== null;
    const singleRowMobileHeader = usesSingleRowMobileHeader(navigationVariant);

    if (hasLagunaAuthoredHeaderGrid(block)) {
        return (
            <ShopBuilderBlockPathContext.Provider value={builderPath}>
                <header
                    {...blockInteractionAttributes(block)}
                    {...siteBuilderEditorBlockAttributes({
                        block,
                        editorMode,
                        path: builderPath,
                    })}
                    className={classNames(
                        "relative z-40 bg-[var(--site-surface)] shadow-sm",
                        design.components.header === "mega" ? "laguna-semantic-header" : "",
                    )}
                    data-shop-block-index={blockIndex}
                    data-shop-block-type={block.type}
                    data-shop-mobile-header-contrast={navigationVariant === "monochromeDropdown" ? "high" : undefined}
                    data-shop-mobile-header-layout={singleRowMobileHeader ? "single-row" : undefined}
                    data-shop-semantic-chrome-grid="header"
                    style={mergeStyles(
                        blockStyle(block),
                        design.components.header === "mega"
                            ? {
                                "--shop-semantic-header-min-height": blockOptionText(block, "minHeight") || "0px",
                            } as CSSProperties
                            : { minHeight: "auto" },
                        interactionStyle(block),
                    )}
                >
                    <SlotBlockGroup
                        block={block}
                        slotKey={SHOP_GRID_SLOT_KEY}
                        compactGridChildren
                        className={classNames(
                            "relative mx-auto grid max-w-[var(--site-container)] grid-cols-12 items-center gap-x-1.5 px-2.5 sm:gap-x-2 sm:px-6",
                            design.components.header === "minimal" ? "[&_[data-shop-block-part^='slot:grid']]:lg:py-3" : "",
                        )}
                        itemClassName={({ childBlock }) => lagunaHeaderGridItemClassName(childBlock)}
                        renderItem={({ childBlock, itemPart }) => (
                            <LagunaAuthoredHeaderGridItem
                                activeSection={activeSection}
                                block={childBlock}
                                headerMenuItems={headerMenuItems}
                                itemPart={itemPart}
                                mobileMenuId={mobileMenuId}
                                mobileOpen={mobileOpen}
                                navigationVariant={navigationVariant}
                                onOpenMobileMenu={onOpenMobileMenu}
                                showDesktopOverflowMenu={blockOptionText(block, "shopPresentationProfile") === "editorial"}
                            />
                        )}
                        semanticGrid
                    />
                    {!hasAuthoredHeaderMenu ? (
                        <>
                            <button
                                type="button"
                                className="absolute left-2.5 top-3 z-10 grid h-9 w-9 place-items-center sm:left-6 sm:h-10 sm:w-10 lg:hidden"
                                aria-label={message("ui.header.openMenu")}
                                aria-controls={mobileMenuId}
                                aria-expanded={mobileOpen}
                                onClick={onOpenMobileMenu}
                            >
                                <Icon name="menu" className="h-5 w-5 sm:h-6 sm:w-6" />
                            </button>
                            <LagunaDesktopNavigation
                                activeSection={activeSection}
                                headerMenuItems={headerMenuItems}
                                variant={navigationVariant}
                            />
                        </>
                    ) : null}
                    {headerContext && !singleRowMobileHeader ? <MobileStoresLink storesHref={headerContext.storesHref} /> : null}
                </header>
            </ShopBuilderBlockPathContext.Provider>
        );
    }

    return (
        <ShopBuilderBlockPathContext.Provider value={builderPath}>
            <header
                {...blockInteractionAttributes(block)}
                {...siteBuilderEditorBlockAttributes({
                    block,
                    editorMode,
                    path: builderPath,
                })}
                className="relative z-40 bg-[var(--site-surface)] shadow-sm"
                data-shop-block-index={blockIndex}
                data-shop-block-type={block.type}
                data-shop-mobile-header-contrast={navigationVariant === "monochromeDropdown" ? "high" : undefined}
                data-shop-mobile-header-layout={singleRowMobileHeader ? "single-row" : undefined}
                style={mergeStyles(blockStyle(block), interactionStyle(block))}
            >
            <div className="border-b border-[color-mix(in_srgb,var(--site-text)_10%,transparent)]">
                <div className={classNames(
                    "mx-auto flex max-w-[var(--site-container)] items-center justify-between gap-1.5 px-2.5 py-3 sm:gap-2 sm:px-6",
                    design.components.header === "minimal" ? "lg:py-3" : "lg:py-4",
                )}>
                    <button
                        type="button"
                        className="grid h-9 w-9 shrink-0 place-items-center sm:h-10 sm:w-10 lg:hidden"
                        aria-label={message("ui.header.openMenu")}
                        aria-controls={mobileMenuId}
                        aria-expanded={mobileOpen}
                        onClick={onOpenMobileMenu}
                    >
                        <Icon name="menu" className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                    {headerContext ? <ShopLogo href={headerContext.homeHref} siteName={headerContext.displayName} /> : null}
                    {headerContext ? (
                        <button
                            type="button"
                            className={classNames(
                                "hidden min-w-0 max-w-xl flex-1 items-center justify-between rounded-full border border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] bg-[var(--site-surface)] px-5 py-3 text-sm text-[color-mix(in_srgb,var(--site-text)_45%,transparent)] shadow-sm transition hover:-translate-y-0.5 hover:border-[var(--site-primary)]/25 hover:bg-[var(--site-surface)] hover:text-[var(--site-text)] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25 lg:flex",
                                design.components.header === "mega" ? "mx-8" : "ml-8",
                            )}
                            onClick={headerContext.onOpenSearch}
                        >
                            <span>{message("block.headerSearch.placeholder")}</span>
                            <span className="grid h-8 w-8 place-items-center rounded-full bg-[var(--site-primary)] text-[var(--site-on-primary)]">
                                <Icon name="search" className="h-4 w-4" />
                            </span>
                        </button>
                    ) : null}
                    {headerContext ? (
                        <div className="flex shrink-0 items-center gap-0 sm:gap-3">
                            <button type="button" className={classNames(headerIconActionClass, "lg:hidden")} aria-label={message("ui.search.label")} onClick={headerContext.onOpenSearch}>
                                <Icon name="search" className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
                            </button>
                            <StoreHeaderDropdown
                                cities={headerContext.storeCities}
                                storesHref={headerContext.storesHref}
                                onClose={() => undefined}
                            />
                            <AccountHeaderDropdown
                                accountHref={headerContext.accountHref}
                                customerSession={headerContext.customerSession}
                                loading={headerContext.customerLoading}
                                onLogout={headerContext.onLogoutCustomer}
                            />
                            {headerContext.currentLanguage ? (
                                <HeaderSelectorDropdown
                                    currentCode={headerContext.currentLanguage.code}
                                    options={headerContext.languageOptions}
                                    ariaLabel={message("ui.header.selectLanguage")}
                                    onSelect={headerContext.onLanguageSelect}
                                />
                            ) : null}
                            {headerContext.currentCurrency ? (
                                <HeaderSelectorDropdown
                                    currentCode={headerContext.currentCurrency.code}
                                    options={headerContext.currencyOptions}
                                    ariaLabel={message("ui.header.selectCurrency")}
                                    onSelect={headerContext.onCurrencySelect}
                                />
                            ) : null}
                            <a href={headerContext.customerUuid !== null ? accountSectionHref(headerContext.accountHref, "favorites") : headerContext.accountHref} className={headerIconActionClass} aria-label={message("ui.account.favorites")}>
                                <Icon name="heart" className={classNames("h-7 w-7", headerContext.visibleFavoriteProductIds.size > 0 && "fill-current text-[var(--site-primary)]")} />
                                <span className={classNames("absolute right-0 top-0 h-5 min-w-5 place-items-center rounded-full bg-[var(--site-primary)] px-1 text-[10px] font-bold text-[var(--site-on-primary)]", headerContext.visibleFavoriteProductIds.size > 0 ? "grid" : "hidden")}>
                                    {headerContext.visibleFavoriteProductIds.size}
                                </span>
                            </a>
                            <div className="group relative">
                                <a href={headerContext.cartHref} className={headerIconActionClass} aria-label={message("ui.cart.title")}>
                                    <Icon name="bag" className="h-7 w-7" />
                                    <span className={classNames("absolute right-0 top-0 h-5 min-w-5 place-items-center rounded-full bg-[var(--site-primary)] px-1 text-[10px] font-bold text-[var(--site-on-primary)]", headerContext.cart.totalQuantity > 0 ? "grid" : "hidden")}>
                                        {headerContext.cart.totalQuantity}
                                    </span>
                                </a>
                                <CartHoverPreview
                                    cart={headerContext.cart}
                                    loading={headerContext.cartLoading}
                                    error={headerContext.cartError}
                                    cartHref={headerContext.cartHref}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
                {headerContext && !singleRowMobileHeader ? <MobileStoresLink storesHref={headerContext.storesHref} /> : null}
            </div>

            <LagunaDesktopNavigation
                activeSection={activeSection}
                headerMenuItems={headerMenuItems}
                variant={navigationVariant}
            />
            </header>
        </ShopBuilderBlockPathContext.Provider>
    );
}

function LagunaStorefrontView({
    runtime,
}: {
    runtime: ShopStorefrontRuntimeModel;
}) {
    const {
        accountHref,
        activeSection,
        activeSectionItems,
        addCartItem,
        addingProductUuid,
        cart,
        cartError,
        cartFlow,
        cartHref,
        cartLoading,
        cartOpen,
        catalogUpdating,
        changeCustomerPassword,
        checkoutContactHref,
        checkoutError,
        checkoutForm,
        checkoutHref,
        checkoutOptions,
        checkoutOptionsLoading,
        checkoutRouteStep,
        checkoutSubmitting,
        closeOverlay,
        confirmCustomerPasswordReset,
        contentLanguage,
        createdOrder,
        currencyOptions,
        currentCurrency,
        currentLanguage,
        customerAddresses,
        customerApiBasePath,
        customerError,
        customerLoading,
        customerSession,
        customerUuid,
        design,
        displayName,
        editorMode,
        handleNewsletterSubmit,
        headerBlock,
        headerBlockIndex,
        headerMenuItems,
        homeHref,
        languageOptions,
        livePageContext,
        localizedPageContext,
        loginCustomer,
        logoutCustomer,
        mobileMenuItems,
        mobileOpen,
        navigateCatalog,
        navigateCheckoutRouteStep,
        normalizedPageContentVersion,
        openSearch,
        orderApiBasePath,
        packetaApiKey,
        page,
        pages,
        promoBarBlock,
        promoBarBlockIndex,
        recentlyViewedProducts,
        recommendedProductPreviews,
        registerCustomer,
        removeCartItem,
        reorderCustomerOrder,
        requestCustomerPasswordReset,
        searchHref,
        searchOpen,
        selectStorefrontLanguage,
        selectDeliveryPayment,
        setCustomerAddresses,
        setMobileOpen,
        setPreviewSectionUuid,
        setSelectedCurrencyCode,
        shopResolveApiPath,
        shopSearchApiPath,
        shopSiteInfo,
        showLegacyFooterFallback,
        showLegacyHeaderFallback,
        showLegacyPromoBarFallback,
        storeCities,
        storefrontFormatting,
        storefrontHref,
        storefrontLanguageCode,
        storefrontMessage,
        storesHref,
        submitCheckout,
        toastVisible,
        toggleFavorite,
        updateCartItem,
        updateCheckoutForm,
        updateCustomerProfile,
        visibleFavoriteProductIds,
        visibleFavoriteProducts,
    } = runtime;
    const shopHeaderChrome: ShopHeaderChromeContextValue = {
        accountHref,
        cart,
        cartError,
        cartHref,
        cartLoading,
        currentCurrency,
        currentLanguage,
        customerLoading,
        customerSession,
        customerUuid,
        currencyOptions,
        displayName,
        homeHref,
        languageOptions,
        onCurrencySelect: setSelectedCurrencyCode,
        onLanguageSelect: selectStorefrontLanguage,
        onLogoutCustomer: logoutCustomer,
        onOpenSearch: openSearch,
        storeCities,
        storesHref,
        visibleFavoriteProductIds,
    };
    const navigationVariant = resolveLagunaNavigationVariant(headerBlock, design.components.header);
    const singleRowMobileHeader = usesSingleRowMobileHeader(navigationVariant);
    const navigationSettings = useMemo(() => resolveSiteNavigationSettings(headerBlock, {
        variant: navigationVariant,
    }), [headerBlock, navigationVariant]);
    const navigationRuntime = useMemo<LagunaNavigationRuntimeContextValue>(() => ({
        settings: navigationSettings,
        style: siteNavigationThemeStyle(resolveSiteNavigationThemeTokens(
            design,
            navigationSettings.theme,
        )) as CSSProperties,
    }), [design, navigationSettings]);
    const mobileMenuId = useId();
    const openMobileMenu = useCallback(() => setMobileOpen(true), [setMobileOpen]);
    const closeMobileMenu = useCallback(() => setMobileOpen(false), [setMobileOpen]);
    const mobileSearchBlock = findShopHeaderRoleBlock(headerBlock, "headerSearch");
    const showMobileSearch = showLegacyHeaderFallback
        || (headerBlock !== null && !hasLagunaAuthoredHeaderGrid(headerBlock))
        || (mobileSearchBlock !== null && shopHeaderBlockVisibleOn(mobileSearchBlock, "mobile"));
    const [pageScrolled, setPageScrolled] = useState(false);

    useEffect(() => {
        let animationFrame = 0;
        const updateScrollState = () => {
            animationFrame = 0;
            setPageScrolled((current) => {
                const next = window.scrollY > 48;
                return current === next ? current : next;
            });
        };
        const scheduleScrollState = () => {
            if (animationFrame !== 0) return;
            animationFrame = window.requestAnimationFrame(updateScrollState);
        };

        updateScrollState();
        window.addEventListener("scroll", scheduleScrollState, { passive: true });

        return () => {
            window.removeEventListener("scroll", scheduleScrollState);
            if (animationFrame !== 0) window.cancelAnimationFrame(animationFrame);
        };
    }, []);

    return (
        <ShopStorefrontLanguageContext.Provider value={storefrontLanguageCode}>
        <ShopStorefrontHrefContext.Provider value={storefrontHref}>
        <ShopStorefrontLocationHrefContext.Provider value={runtime.storefrontLocationHref}>
        <ShopStorefrontFormattingContext.Provider value={storefrontFormatting}>
        <ShopEditorModeContext.Provider value={editorMode}>
            <ShopBuilderSchemaVersionContext.Provider value={normalizedPageContentVersion}>
            <ShopSiteInfoContext.Provider value={shopSiteInfo}>
            <ShopSearchContext.Provider
                value={{
                    currencyCode: currentCurrency?.code ?? null,
                    languageCode: contentLanguage?.code ?? livePageContext?.languageCode ?? null,
                    pages,
                    searchHref,
                    shopSearchApiPath,
                }}
            >
            <LagunaNavigationRuntimeContext.Provider value={navigationRuntime}>
            <div
                className="min-w-0 max-w-full overflow-x-clip"
                lang={storefrontLocale(storefrontLanguageCode)}
                dir={storefrontLocale(storefrontLanguageCode) === "ar" ? "rtl" : "ltr"}
                data-shop-animation-preview={editorMode ? "true" : undefined}
                data-shop-page-type={localizedPageContext?.pageType ?? livePageContext?.pageType ?? "home"}
                data-shop-scroll-state={pageScrolled ? "compact" : "expanded"}
            >
            <ShopInteractionStyles />
            {promoBarBlock && promoBarBlockIndex >= 0 ? (
                <EditableBlockFrame block={promoBarBlock} index={promoBarBlockIndex} editorMode={editorMode}>
                    <PromoBar block={promoBarBlock} />
                </EditableBlockFrame>
            ) : showLegacyPromoBarFallback ? (
                <PromoBar />
            ) : null}

            {headerBlock && headerBlockIndex >= 0 ? (
                <ShopHeaderChromeContext.Provider value={shopHeaderChrome}>
                    <HeaderChrome
                        activeSection={activeSection}
                        block={headerBlock}
                        blockIndex={headerBlockIndex}
                        design={design}
                        headerMenuItems={headerMenuItems}
                        mobileMenuId={mobileMenuId}
                        mobileOpen={mobileOpen}
                        navigationVariant={navigationVariant}
                        onOpenMobileMenu={openMobileMenu}
                    />
                </ShopHeaderChromeContext.Provider>
            ) : showLegacyHeaderFallback ? (
            <header
                className="relative z-40 bg-[var(--site-surface)] shadow-sm"
                data-shop-mobile-header-contrast={navigationVariant === "monochromeDropdown" ? "high" : undefined}
                data-shop-mobile-header-layout={singleRowMobileHeader ? "single-row" : undefined}
                onMouseLeave={() => setPreviewSectionUuid(null)}
            >
                <div className="border-b border-[color-mix(in_srgb,var(--site-text)_10%,transparent)]">
                    <div className={classNames(
                        "mx-auto flex max-w-[var(--site-container)] items-center justify-between gap-1.5 px-2.5 py-3 sm:gap-2 sm:px-6",
                        design.components.header === "minimal" ? "lg:py-3" : "lg:py-4",
                    )}>
                        <button
                            type="button"
                            className="grid h-9 w-9 shrink-0 place-items-center sm:h-10 sm:w-10 lg:hidden"
                            aria-label={storefrontMessage("ui.header.openMenu")}
                            aria-controls={mobileMenuId}
                            aria-expanded={mobileOpen}
                            onClick={openMobileMenu}
                        >
                            <Icon name="menu" className="h-5 w-5 sm:h-6 sm:w-6" />
                        </button>
                        <ShopLogo href={homeHref} siteName={displayName} />
                        {design.components.header === "mega" && navigationVariant === "lagunaClassic" ? (
                            <LagunaLegacyClassicRootNavigation
                                activeSection={activeSection}
                                headerMenuItems={headerMenuItems}
                                onPreviewSectionChange={setPreviewSectionUuid}
                            />
                        ) : null}
                        <button
                            type="button"
                            className={classNames(
                                "hidden min-w-0 max-w-xl flex-1 items-center justify-between rounded-full border border-[color-mix(in_srgb,var(--site-text)_10%,transparent)] bg-[var(--site-surface)] px-5 py-3 text-sm text-[color-mix(in_srgb,var(--site-text)_45%,transparent)] shadow-sm transition hover:-translate-y-0.5 hover:border-[var(--site-primary)]/25 hover:bg-[var(--site-surface)] hover:text-[var(--site-text)] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary)]/25 lg:flex",
                                design.components.header === "mega" ? "mx-8" : "ml-8",
                            )}
                            onClick={openSearch}
                        >
                            <span>{storefrontMessage("block.headerSearch.placeholder")}</span>
                            <span className="grid h-8 w-8 place-items-center rounded-full bg-[var(--site-primary)] text-[var(--site-on-primary)]">
                                <Icon name="search" className="h-4 w-4" />
                            </span>
                        </button>
                        <div className="flex shrink-0 items-center gap-0 sm:gap-3">
                            <button type="button" className={classNames(headerIconActionClass, "lg:hidden")} aria-label={storefrontMessage("ui.search.label")} onClick={openSearch}>
                                <Icon name="search" className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
                            </button>
                            <StoreHeaderDropdown
                                cities={storeCities}
                                storesHref={storesHref}
                                onClose={() => undefined}
                            />
                            <AccountHeaderDropdown
                                accountHref={accountHref}
                                customerSession={customerSession}
                                loading={customerLoading}
                                onLogout={logoutCustomer}
                            />
                            {currentLanguage ? (
                                <HeaderSelectorDropdown
                                    currentCode={currentLanguage.code}
                                    options={languageOptions}
                                    ariaLabel={storefrontMessage("ui.header.selectLanguage")}
                                    onSelect={selectStorefrontLanguage}
                                />
                            ) : null}
                            {currentCurrency ? (
                                <HeaderSelectorDropdown
                                    currentCode={currentCurrency.code}
                                    options={currencyOptions}
                                    ariaLabel={storefrontMessage("ui.header.selectCurrency")}
                                    onSelect={(code) => setSelectedCurrencyCode(code)}
                                />
                            ) : null}
                            <a href={customerUuid !== null ? accountSectionHref(accountHref, "favorites") : accountHref} className={headerIconActionClass} aria-label={storefrontMessage("ui.account.favorites")}>
                                <Icon name="heart" className={classNames("h-7 w-7", visibleFavoriteProductIds.size > 0 && "fill-current text-[var(--site-primary)]")} />
                                <span className={classNames("absolute right-0 top-0 h-5 min-w-5 place-items-center rounded-full bg-[var(--site-primary)] px-1 text-[10px] font-bold text-[var(--site-on-primary)]", visibleFavoriteProductIds.size > 0 ? "grid" : "hidden")}>
                                    {visibleFavoriteProductIds.size}
                                </span>
                            </a>
                            <div className="group relative">
                                <a href={cartHref} className={headerIconActionClass} aria-label={storefrontMessage("ui.cart.title")}>
                                    <Icon name="bag" className="h-7 w-7" />
                                    <span className={classNames("absolute right-0 top-0 h-5 min-w-5 place-items-center rounded-full bg-[var(--site-primary)] px-1 text-[10px] font-bold text-[var(--site-on-primary)]", cart.totalQuantity > 0 ? "grid" : "hidden")}>
                                        {cart.totalQuantity}
                                    </span>
                                </a>
                                <CartHoverPreview
                                    cart={cart}
                                    loading={cartLoading}
                                    error={cartError}
                                    cartHref={cartHref}
                                />
                            </div>
                        </div>
                    </div>
                    {!singleRowMobileHeader ? <MobileStoresLink storesHref={storesHref} /> : null}
                </div>

                {design.components.header === "mega" ? (
                    navigationVariant === "lagunaClassic" ? (
                        <LagunaLegacyClassicSubNavigation activeSectionItems={activeSectionItems} />
                    ) : (
                        <LagunaDesktopNavigation
                            activeSection={activeSection}
                            headerMenuItems={headerMenuItems}
                            variant={navigationVariant}
                        />
                    )
                ) : (
                    <LagunaDesktopNavigation
                        activeSection={activeSection}
                        headerMenuItems={headerMenuItems}
                        variant={navigationVariant}
                    />
                )}

                {design.components.header === "mega" ? <BenefitStrip /> : null}
            </header>
            ) : null}

            <MobileMenu
                activeSection={activeSection}
                allowDesktop={blockOptionText(headerBlock, "shopPresentationProfile") === "editorial"}
                id={mobileMenuId}
                open={mobileOpen}
                onClose={closeMobileMenu}
                onOpenSearch={openSearch}
                homeHref={homeHref}
                siteName={displayName}
                items={mobileMenuItems}
                showSearch={showMobileSearch}
                variant={navigationVariant}
            />
            <SearchPanel
                open={searchOpen}
                onClose={closeOverlay}
                searchHref={searchHref}
                shopSearchApiPath={shopSearchApiPath}
                languageCode={contentLanguage?.code ?? livePageContext?.languageCode ?? null}
                currencyCode={currentCurrency?.code ?? null}
                recommendedProducts={recommendedProductPreviews}
            />
            <button
                type="button"
                aria-label={storefrontMessage("ui.header.closeOverlay")}
                className={classNames(
                    "fixed inset-0 z-[55] bg-[color-mix(in_srgb,var(--site-text)_45%,transparent)] transition duration-200",
                    searchOpen || cartOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
                )}
                onClick={closeOverlay}
            />
            <CartDrawer
                open={cartOpen}
                onClose={closeOverlay}
                cart={cart}
                loading={cartLoading}
                error={cartError}
                cartHref={cartHref}
                onRemoveItem={removeCartItem}
            />

            <ShopCommerceContentContext.Provider
                value={{
                    activeSection,
                    activeSectionItems,
                    addingProductUuid,
                    context: localizedPageContext,
                    currencyCode: currentCurrency?.code ?? null,
                    favorites: visibleFavoriteProductIds,
                    headerMenuItems,
                    onAddToCart: (productUuid) => {
                        void addCartItem(productUuid);
                    },
                    onRegisterCustomer: registerCustomer,
                    onToggleFavorite: toggleFavorite,
                    resolveApiPath: shopResolveApiPath,
                }}
            >
            <main>
                <PageBody
                    page={page}
                    pageContext={localizedPageContext}
                    favorites={visibleFavoriteProductIds}
                    favoriteProducts={visibleFavoriteProducts}
                    recentlyViewedProducts={recentlyViewedProducts}
                    recommendedProducts={recommendedProductPreviews}
                    catalogUpdating={catalogUpdating}
                    onToggleFavorite={toggleFavorite}
                    onCatalogNavigate={navigateCatalog}
                    onNewsletterSubmit={handleNewsletterSubmit}
                    cart={cart}
                    cartLoading={cartLoading}
                    cartHref={cartHref}
                    deliveryPaymentHref={checkoutHref}
                    contactHref={checkoutContactHref}
                    checkoutHref={checkoutHref}
                    checkoutRouteStep={checkoutRouteStep}
                    checkoutOptions={checkoutOptions}
                    checkoutOptionsLoading={checkoutOptionsLoading}
                    cartError={cartError}
                    packetaApiKey={packetaApiKey}
                    cartFlow={cartFlow}
                    addingProductUuid={addingProductUuid}
                    checkoutForm={checkoutForm}
                    checkoutSubmitting={checkoutSubmitting}
                    checkoutError={checkoutError}
                    createdOrder={createdOrder}
                    customerSession={customerSession}
                    customerLoading={customerLoading}
                    customerError={customerError}
                    customerApiBasePath={customerApiBasePath ?? null}
                    orderApiBasePath={orderApiBasePath}
                    customerAddresses={customerAddresses}
                    onCheckoutRouteStepChange={navigateCheckoutRouteStep}
                    onSelectDeliveryPayment={selectDeliveryPayment}
                    onCheckoutFormChange={updateCheckoutForm}
                    onSubmitCheckout={submitCheckout}
                    onCustomerLogin={loginCustomer}
                    onCustomerRegister={registerCustomer}
                    onCustomerLogout={logoutCustomer}
                    onCustomerProfileUpdate={updateCustomerProfile}
                    onCustomerPasswordChange={changeCustomerPassword}
                    onCustomerPasswordResetRequest={requestCustomerPasswordReset}
                    onCustomerPasswordResetConfirm={confirmCustomerPasswordReset}
                    onCustomerReorder={reorderCustomerOrder}
                    onCustomerAddressesChange={setCustomerAddresses}
                    onAddToCart={(productUuid, productAttributeUuid = null, quantity = 1, surchargeValueUuids = [], manufacturingSpecification) => {
                        void addCartItem(productUuid, productAttributeUuid, quantity, surchargeValueUuids, manufacturingSpecification);
                    }}
                    onUpdateCartItem={updateCartItem}
                    onRemoveCartItem={removeCartItem}
                    editorMode={editorMode}
                />
            </main>
            </ShopCommerceContentContext.Provider>

            {showLegacyFooterFallback ? (
                <Footer homeHref={homeHref} siteName={displayName} variant={design.components.footer} />
            ) : null}
            <div
                className={classNames(
                    "fixed bottom-5 left-1/2 z-[80] -translate-x-1/2 bg-[var(--site-inverse)] px-5 py-3 text-sm font-semibold text-[var(--site-on-inverse)] shadow-[0_6px_24px_var(--site-shadow-color)] transition duration-200",
                    toastVisible ? "opacity-100" : "pointer-events-none opacity-0",
                )}
            >
                {storefrontMessage("ui.newsletter.subscribed")}
            </div>
            </div>
            </LagunaNavigationRuntimeContext.Provider>
            </ShopSearchContext.Provider>
            </ShopSiteInfoContext.Provider>
            </ShopBuilderSchemaVersionContext.Provider>
        </ShopEditorModeContext.Provider>
        </ShopStorefrontFormattingContext.Provider>
        </ShopStorefrontLocationHrefContext.Provider>
        </ShopStorefrontHrefContext.Provider>
        </ShopStorefrontLanguageContext.Provider>
    );
}

export function LagunaTemplateClient({
    ...props
}: ShopStorefrontRuntimeProps) {
    return (
        <ShopStorefrontRuntime
            {...props}
            fallbackSiteName="Shop"
            previewAdapter={lagunaStorefrontPreviewAdapter}
        >
            {(runtime) => <LagunaStorefrontView runtime={runtime} />}
        </ShopStorefrontRuntime>
    );
}
